/**
 * design-tool-core.js — V1 Phase 2
 * @version 2.14.0 rev 14  (full changelog at end of file)
 *
 * Complete design tool: properties panel, resize slider, snap alignment,
 * color tint, SVG color editor, monogram (13 styles), clipart galleries
 * (SVG + PNG), mobile drawer system, touch optimizations.
 */
(function () {
  'use strict';

  /* Hoisted from the DOM REFS block (Aug 24 2026): a `const` is in its
     temporal dead zone until its line runs, so the DEBUG && ... guards
     above that block (the __DT_CONFIG__ check, the new-customer-accounts
     fallback, the fine-pointer detector) would throw a ReferenceError
     instead of logging. */
  const DEBUG = false;

  const CFG = window.__DT_CONFIG__;
  if (!CFG) { DEBUG && console.error('[dt] No __DT_CONFIG__ found.'); return; }

  /* ── New Customer Accounts fallback ──────────────────────────────
     Shopify's new customer accounts have a known bug where the Liquid
     `customer` object returns null even when the customer IS logged in
     (storefront session cookie expires while the account session persists).
     Fall back to ShopifyAnalytics if Liquid missed it. ─────────────── */
  if (!CFG.customerLoggedIn) {
    const saCid = window.ShopifyAnalytics?.meta?.page?.customerId;
    const stCid = window._st?.cid;
    const fallbackCid = saCid || stCid;
    if (fallbackCid) {
      CFG.customerLoggedIn = true;
      CFG.customerId = CFG.customerId || fallbackCid;
      DEBUG && console.info('[dt] Customer detected via JS fallback (Liquid customer was null). id:', fallbackCid);
    }
  }

  /* ── Self-detect return from login ──────────────────────────────
     With new customer accounts the redirect snippet in theme.liquid may
     never fire (the /account page is Shopify-hosted, not theme-rendered).
     If we land on the design tool page and dt_return_url is set, consume
     it here so the autosave auto-restore treats this as a login return. */
  try {
    const dtReturn = localStorage.getItem('dt_return_url');
    if (dtReturn && window.location.href.indexOf('view=design-tool') !== -1) {
      localStorage.removeItem('dt_return_url');
      sessionStorage.setItem('dt_just_redirected', 'true');
      DEBUG && console.info('[dt] Consumed dt_return_url (self-detected login return)');
    }
  } catch(e) {}

  /* ── Startup diagnostics ──────────────────────────────────────── */
  console.group('[dt] Design Tool v2 initializing');
  const imgCt = Object.keys(CFG.images).reduce((s, c) => s + Object.keys(CFG.images[c]).length, 0);

  console.groupEnd();

  /* ══════════════════════════════════════════════════════════════════
   * CONSTANTS
   * ══════════════════════════════════════════════════════════════════ */
  const MIN_DIM   = 10;
  const MAX_UNDO  = 50;
  const PROD_DPI  = 300;    /* production file resolution — all raster/vector output */
  /* EPS-only raster resolution (v2.9.20). 2x PROD_DPI = 600dpi target, bounded by a
     total-pixel budget so a large print area scales down instead of exhausting the
     canvas. Mirrors the traceSS budget pattern used for the vectorizer input.
     Single-item print EPS only — bulk stays at 300 (a fresh hi-res render per name
     inside the serial confirm loop would multiply frozen-UI time by the name count).

     REVERTED TO 1 (300dpi) at Brandon's request, v2.9.21 — the PRODUCTION PDF already
     carries text as outlined vector at infinite resolution with photos as 300dpi raster,
     so the EPS gains little for ~3.5x the bytes. Set back to 2 to re-enable 600dpi; the
     render path below is a no-op while this is 1. */
  const EPS_DPI_MULT  = 1;
  const EPS_PX_BUDGET = 24e6;
  const DRAG_THRESHOLD = 4; /* px — prevents tap-jitter micro-drags */
  const HANDLE_R  = 7;      /* visual handle radius (desktop only) */
  const HANDLE_HIT = 14;    /* hit detection radius for handles */

  /* ══════════════════════════════════════════════════════════════════
   * STATE
   * ══════════════════════════════════════════════════════════════════ */
  const S = {
    view:       CFG.sides[0],
    method:     CFG.defaultMethod,
    variantId:  CFG.variantId,
    colorHandle: CFG.currentColorHandle || '',

    byView:     {},
    sel:        null,
    iact:       null,

    zoom:       1,
    _panX:      0,
    _panY:      0,
    _quickZoom: null,
    showDims:   false,
    rscale:     1,
    rscaleByView: {},   /* per-side rscale cache for production code */
    pa:         { x:0, y:0, w:300, h:300 },
    paByView:   {},     /* per-side pa cache for production code */

    overlayImg: null,
    overlayStyle: '',

    history:    {},
    _lastPropsLayerId: null,
    _propsAdvancedOpen: false,
    _closeMobileDrawer: null,
    _mobileActivePanel: null,
    _lastStarter: -1,
    _secondSideNoticed: false,
    _uploadMode: null,
    _mobileActivePanelType: null,
    mode: 'view', /* 'view' or 'design' */
    snapEnabled: true,   /* Always start with snap ON — user can toggle off per session */
    _snapLines: [],

    textColor:  '#111111',
    textBold:   false,
    textItalic: false,
    textFont:   (function () { try { return (getAllFonts()[0] || {}).fontValue || 'Montserrat'; } catch (e) { return 'Montserrat'; } })(),
    textSize:   36,

    monoStyle:  0,
    monoColor:  '#2a1f18',
    monoColor2: '#F47E46',
  };

  CFG.sides.forEach(s => {
    S.byView[s] = [];
    S.history[s] = { stack: [], index: -1 };
  });

  const layers   = () => S.byView[S.view] || [];
  const selLayer = () => layers().find(l => l.id === S.sel) || null;
  /* Touch devices (phones + tablets, any size/orientation) use the mobile
     layout + gestures; narrow desktop windows (≤1024) fall back to it too.
     A hard width cutoff alone flipped iPads to desktop in landscape
     (1080–1366px wide), so detect a coarse pointer as well. Cached MQL —
     isMobile() is called on every render. */
  const _dtCoarse = window.matchMedia ? window.matchMedia('(pointer: coarse)') : { matches: false };
  const isMobile = () => _dtCoarse.matches || window.innerWidth <= 1024;

  /* ── Fine-pointer detection (Aug 24 2026) ─────────────────────────
     The "rotate your tablet" blocker is gated in CSS on (pointer:coarse)
     and suppressed by (any-pointer:fine). Both lie on 2-in-1 laptops:
     Chrome/Edge on Windows report ONLY a coarse pointer whenever Windows
     is in tablet/slate mode (trackpad attached or not), and Firefox
     reports pointer:coarse on any touchscreen laptop without an external
     mouse — so a 1920x1080 laptop in landscape got the prompt. Instead of
     trusting the OS, watch the input that actually arrives: a pointermove
     whose pointerType is 'mouse' (trackpads report 'mouse'; touch and pen
     never do) or a wheel event (touch scrolling never fires wheel). Two
     distinct positions are required so a single stray event can't flip
     it. Result: <html class="dt-fine-pointer">, remembered for the tab so
     the next load never flashes the prompt. The prompt's "Continue in
     landscape" button sets the same flag — nobody is ever hard-blocked.
     Layout gating (isMobile) is deliberately untouched. */
  const FINE_POINTER_KEY = 'dt_fine_pointer';
  function markFinePointer(src) {
    const root = document.documentElement;
    if (root.classList.contains('dt-fine-pointer')) return;
    root.classList.add('dt-fine-pointer');
    try { sessionStorage.setItem(FINE_POINTER_KEY, '1'); } catch (e) {}
    DEBUG && console.info('[dt] fine pointer detected via', src);
  }
  (function setupFinePointerDetect() {
    try { if (sessionStorage.getItem(FINE_POINTER_KEY) === '1') { markFinePointer('session'); return; } } catch (e) {}
    let seen = null;
    const onPointer = e => {
      if (e.pointerType !== 'mouse') return;
      if (seen && (Math.abs(e.clientX - seen.x) > 2 || Math.abs(e.clientY - seen.y) > 2)) {
        markFinePointer('pointermove');
        window.removeEventListener('pointermove', onPointer, true);
        window.removeEventListener('wheel', onWheel, true);
        return;
      }
      seen = { x: e.clientX, y: e.clientY };
    };
    const onWheel = () => {
      markFinePointer('wheel');
      window.removeEventListener('pointermove', onPointer, true);
      window.removeEventListener('wheel', onWheel, true);
    };
    if (window.PointerEvent) window.addEventListener('pointermove', onPointer, true);
    window.addEventListener('wheel', onWheel, { capture: true, passive: true });
    /* Escape hatch on the prompt itself. Inject the button if the section
       markup predates it, so CSS/JS can ship ahead of the liquid. */
    const prompt = document.getElementById('dt-rotate-prompt');
    if (prompt) {
      let btn = document.getElementById('dt-rotate-prompt-continue');
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button'; btn.id = 'dt-rotate-prompt-continue'; btn.className = 'dt-rotate-prompt__btn';
        btn.textContent = 'Continue in landscape';
        (prompt.querySelector('.dt-rotate-prompt__card') || prompt).appendChild(btn);
      }
      btn.addEventListener('click', () => markFinePointer('button'));
      /* The prompt is aria-modal but the button is the LAST focusable
         element in the document, so a keyboard-only user would have to Tab
         through the whole tool underneath to reach it. Put focus on it
         whenever the prompt is actually showing (initial paint + rotation). */
      const focusIfShown = () => {
        if (getComputedStyle(prompt).display !== 'none' && document.activeElement !== btn) {
          try { btn.focus({ preventScroll: true }); } catch (e) {}
        }
      };
      focusIfShown();
      window.addEventListener('resize', focusIfShown);
    }
  })();
  const hasModal = () => document.querySelector('.dt-rbg-overlay, .dt-imr-overlay, #dt-svg-editor, #dt-mono-overlay');

  /* ══════════════════════════════════════════════════════════════════
   * DOM REFS
   * ══════════════════════════════════════════════════════════════════ */
  /* DEBUG moved to the top of the IIFE (Aug 24 2026) — see note there. */
  const $ = id => document.getElementById(id);
  const $$ = sel => document.querySelectorAll(sel);

  /* ── Layout constants (match CSS) ── */
  const DRAWER_BOTTOM = 84;   /* CSS: .dt-mobile-drawer { bottom: 84px } */
  const TAB_BAR_H     = 68;   /* tab bar 56px + 12px bottom offset */
  const CART_BAR_H    = 64;   /* persistent mobile cart bar height */
  const DRAWER_RATIO  = 0.3;  /* CSS: --dt-drawer-h: 30vh → 0.3 of viewport */

  const app       = $('dt-app');
  const canvas    = $('dt-design-canvas');
  const ctx       = canvas?.getContext('2d');
  const mImg      = $('dt-mockup-img');
  const mWrap     = $('dt-canvas-wrap');
  const pOver     = $('dt-print-overlay');
  const cartBtn   = $('dt-add-cart');
  const zoomLbl   = $('dt-zoom-level');
  const sideTabs  = $('dt-side-tabs');
  const undoBtn   = $('dt-undo');
  const redoBtn   = $('dt-redo');

  if (!canvas || !ctx || !mImg) {
    DEBUG && console.error('[dt] Required DOM elements not found');
    return;
  }

  /* Prevent context menu and double-tap zoom on canvas */
  canvas.addEventListener('contextmenu', e => e.preventDefault());

  /* ══════════════════════════════════════════════════════════════════
   * PHYSICAL DIMENSIONS
   * ══════════════════════════════════════════════════════════════════ */
  function parseDims(dimStr) {
    const raw = (dimStr || '').toString().trim();
    if (!raw) return null;
    const parts = raw.split(/\s*x\s*/i);
    if (parts.length < 2) return null;
    const w = parseFloat(parts[0]), h = parseFloat(parts[1]);
    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return null;
    return { wInches: w, hInches: h };
  }

  const SIDE_PHYS = {};
  CFG.sides.forEach(s => { SIDE_PHYS[s] = parseDims(CFG.sideConfigs[s]?.dims); });

  function physDPI(slug) {
    const phys = SIDE_PHYS[slug];
    if (!phys) return 150;
    const pa = CFG.sideConfigs[slug]?.printArea || { w:300, h:300 };
    return ((pa.w / phys.wInches) + (pa.h / phys.hInches)) / 2;
  }

  function pxToInches(px, slug) { return px / physDPI(slug); }

  /* ══════════════════════════════════════════════════════════════════
   * INIT
   * ══════════════════════════════════════════════════════════════════ */
  /* Measure actual header height (includes mobile row 2) and set CSS variable */
  function syncHeaderHeight() {
    const hdr = $('dt-header');
    if (hdr) document.documentElement.style.setProperty('--dt-header-h-actual', hdr.offsetHeight + 'px');
  }
  /* UX-Aug15 (spec §2 "measured bars"): the mobile tab bar and cart bar were assumed to be
     56px / 64px constants in CSS calc() chains and in applyEditModeZoom. The action-mode bar is
     ~73px tall (Delete pill), which is exactly why the Accept FAB overlapped Delete. We now
     publish --dt-mobile-tabs-actual / --dt-cart-bar-h-actual (0 when hidden — spec A15) and every
     chained calc() reads var(--x-actual, var(--x)). The cart bar's safe-area padding is
     excluded so calc sites that add env(safe-area-inset-bottom) themselves don't double count. */
  let _barRO = null;
  function _safeAreaBottomPx() {
    let probe = $('dt-safe-probe');
    if (!probe) {
      probe = document.createElement('div'); probe.id = 'dt-safe-probe';
      probe.style.cssText = 'position:fixed;left:-9999px;bottom:0;width:0;height:0;padding-bottom:env(safe-area-inset-bottom,0px);pointer-events:none;visibility:hidden';
      document.body.appendChild(probe);
    }
    return parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  }
  function syncBarHeights() {
    const root = document.documentElement.style;
    const tabs = $('dt-mobile-tabs'), cart = $('dt-cart-bar');
    const vis = el => !!el && (typeof el.checkVisibility === 'function' ? el.checkVisibility() : el.offsetParent !== null || getComputedStyle(el).position === 'fixed') && getComputedStyle(el).display !== 'none';
    if (tabs && vis(tabs)) root.setProperty('--dt-mobile-tabs-actual', Math.round(tabs.offsetHeight) + 'px');
    else if (tabs) root.setProperty('--dt-mobile-tabs-actual', '0px');
    if (cart && vis(cart)) root.setProperty('--dt-cart-bar-h-actual', Math.max(0, Math.round(cart.offsetHeight - _safeAreaBottomPx())) + 'px');
    else root.setProperty('--dt-cart-bar-h-actual', '0px');
  }
  function setupBarObservers() {
    syncBarHeights();
    if (!('ResizeObserver' in window)) { window.addEventListener('resize', syncBarHeights); return; }
    _barRO = new ResizeObserver(() => syncBarHeights());
    ['dt-mobile-tabs', 'dt-cart-bar'].forEach(id => { const el = $(id); if (el) _barRO.observe(el); });
    /* class flips (dt-action-mode, dt-text-editing, dt-ctx-active) can toggle display without a
       resize event on the observed node itself */
    const mo = new MutationObserver(() => syncBarHeights());
    const tabs = $('dt-mobile-tabs'), cart = $('dt-cart-bar');
    if (tabs) mo.observe(tabs, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    if (cart) mo.observe(cart, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    syncHeaderHeight();
    /* Re-sync after fonts settle (header may reflow) */
    setTimeout(syncHeaderHeight, 300);
    document.fonts?.ready?.then(syncHeaderHeight);
    buildSideTabs();
    buildSiblingSelector();
    setupMethodToggle();
    setupLeftTabs();
    loadStarterRecipes();
    loadStateOutlines();
    loadInlineTemplates();
    setupStartCards();
    setupMobileTabs();
    setupTextTool();
    setupUpload();
    setupMonogram();
    setupClipart();
    setupInspirationButton();
    setupCanvasInteraction();
    setupCanvasDrop();        /* E1 */
    setupContextMenu();       /* E2 */
    setupIdleInspiration();   /* F4 */
    setupWheelZoom();
    setupToolbar();
    setupZoom();
    setupKeyboard();
    setupResize();
    setupCart();
    /* Inject mobile Remove-Color button + popover (dynamic — keeps Liquid template clean) */
    (function injectMobileRcElements() {
      var removebgBtn = $('dt-act-removebg');
      if (removebgBtn && !$('dt-act-rc')) {
        var rcBtn = document.createElement('button');
        rcBtn.className = 'dt-action-tab dt-action-tab--rc';
        rcBtn.id = 'dt-act-rc';
        rcBtn.setAttribute('aria-label', 'Remove a color');
        rcBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="1.5"/><path d="M7.5 10h5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg><span>Color</span>';
        removebgBtn.insertAdjacentElement('afterend', rcBtn);
      }
      /* v2.11.0 — engrave Invert tab (same injection pattern; CSS order rule
         .dt-action-tab--invert ships in design-tool.css) */
      if (removebgBtn && !$('dt-act-invert')) {
        var invBtn = document.createElement('button');
        invBtn.className = 'dt-action-tab dt-action-tab--invert';
        invBtn.id = 'dt-act-invert';
        invBtn.setAttribute('aria-label', 'Invert engrave style');
        invBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2.5" y="2.5" width="15" height="15" rx="2" stroke="currentColor" stroke-width="1.4"/><path d="M10 2.5v15" stroke="currentColor" stroke-width="1.4"/><path d="M4.5 10h3.5M13 7l2.5 3-2.5 3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Invert</span>';
        removebgBtn.insertAdjacentElement('afterend', invBtn);
        invBtn.addEventListener('click', function () { openEngraveInvertPicker(); });
      }
      var app = $('dt-app');
      if (app && !$('dt-rc-pop')) {
        var pop = document.createElement('div');
        pop.className = 'dt-textcolor-pop dt-rc-pop';
        pop.id = 'dt-rc-pop';
        pop.setAttribute('hidden', '');
        pop.innerHTML = '<div class="dt-textcolor-pop__title">Remove a Color</div><div class="dt-rc-pop__body" id="dt-rc-pop-body"></div>';
        app.appendChild(pop);
      }
    })();
    setupMobileLayerActions();
    setupDoneButton();
    setupViewportAria();

    /* Favicon: use the store/theme favicon (settings.favicon, via CFG.faviconUrl).
       Previously this forced a hardcoded "M" placeholder, which overrode the
       theme favicon the Liquid section sets on first paint. Now we only ever
       set the real theme favicon; if none is configured we leave whatever the
       page already has (never the placeholder). */
    if (CFG.faviconUrl) {
      let favicon = document.querySelector("link[rel~='icon']");
      if (!favicon) { favicon = document.createElement('link'); favicon.setAttribute('rel', 'icon'); document.head.appendChild(favicon); }
      favicon.href = CFG.faviconUrl;
    }

    /* Exit-intent: warn user if they have unsaved design work.
       S._allowUnload suppresses the prompt for the tool's OWN navigations
       (successful add-to-cart → /cart), where the work is already saved
       to the cart and the browser prompt would only read as an error. */
    window.addEventListener('beforeunload', (e) => {
      if (S._allowUnload) return;
      if (layers().length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    });


    /* Mobile round 2 — #1: the variant color pill replaces the logo in the
       header's top-right. The swatch strip it toggles stays at the canvas top
       (directly under the header), so the expansion still reads as connected. */
    if (isMobile()) {
      const _vp = $('dt-color-pill-mobile'), _hdr = document.querySelector('.dt-header');
      if (_vp && _hdr) {
        _hdr.appendChild(_vp);
        _hdr.classList.add('dt-hdr-has-variant');
        _vp.classList.add('dt-color-pill-mobile--header');
      }
    }
    setupQuickZoom();
    setupBarObservers(); /* UX-Aug15 (spec §2) measured bars */
    setupCanvasRow();    /* UX-Aug15 #2 canvas-top row (desktop) */
    setupInlineTextEdit();
    setupSavedTab();
    /* CRO #13/#16 — sync CTA labels + price to the (empty) initial state */
    updateCartBtn();
    /* Sync snap toggle buttons to match localStorage state */
    $$('.dt-snap-toggle').forEach(b => b.classList.toggle('active', S.snapEnabled));
    loadOverlay();
    preloadEngraveTextures();   /* CRO #22 */
    registerCustomFonts();
    loadMockup();
    /* Desktop: hide canvas swatches + side tabs (header row 2 replaces them).
       JS failsafe — CSS rule exists but Shopify CDN may cache old CSS. */
    if (!isMobile()) {
      $('dt-color-swatches')?.style.setProperty('display', 'none', 'important');
      $('dt-side-tabs')?.style.setProperty('display', 'none', 'important');
    }
    /* Desktop (wide only): the Start panel is active by default in markup, so the
       canvas already begins at its "with panel" width — no auto-click needed. The
       Start launchpad is the intended first view on desktop. */
    if (!isMobile()) {
      document.querySelector('.dt-sidebar__panels')?.classList.add('has-active-panel');
      document.querySelector('.dt-sidebar--left')?.classList.add('has-active-tab');
    }
    /* Check for saved draft after everything is initialized */
    setTimeout(checkAutosaveDraft, 500);
    /* Show the welcome action cards on first visit (after mockup loads).
       The launcher + persistent Start panel now own onboarding — no separate tip. */
    setTimeout(showWelcomeOverlay, 800);
    /* CRO #23 — first-run coach marks (waits out the welcome overlay) */
    setTimeout(function () { maybeStartTour(0); }, 1600);
  }

  /* ══════════════════════════════════════════════════════════════════
   * SIDE TABS
   * ══════════════════════════════════════════════════════════════════ */
  function getVisibleSides() {
    const ci = CFG.images[S.colorHandle];
    if (ci) { const v = CFG.sides.filter(s => ci[s]); if (v.length) return v; }
    const any = CFG.sides.filter(s => { for (const c in CFG.images) if (CFG.images[c][s]) return true; return false; });
    return any.length ? any : CFG.sides;
  }

  /* ── Context indicator ──────────────────────────────── */
  function updateContextIndicator() {
    const el = $('dt-context-side');
    if (!el) return;
    const label = CFG.sideConfigs[S.view]?.label || S.view;
    el.textContent = 'Customizing ' + label;
  }

  function buildSideTabs() {
    if (!sideTabs) return;
    const vis = getVisibleSides();
    if (!vis.includes(S.view) && vis.length) S.view = vis[0];
    /* Canvas area tabs (mobile) */
    sideTabs.innerHTML = vis.map(s => {
      const lbl = CFG.sideConfigs[s]?.label || s;
      return `<button class="dt-side-tab${s === S.view ? ' active' : ''}" data-side="${s}">${lbl}</button>`;
    }).join('');
    sideTabs.querySelectorAll('.dt-side-tab').forEach(b => b.addEventListener('click', () => switchToSide(b.dataset.side)));
    /* Desktop header Row 2 side tabs */
    const headerSides = $('dt-header-sides');
    if (headerSides) {
      headerSides.innerHTML = vis.length > 1 ? vis.map(s => {
        const lbl = CFG.sideConfigs[s]?.label || s;
        return `<button class="dt-side-tab${s === S.view ? ' active' : ''}" data-side="${s}">${lbl}</button>`;
      }).join('') : '<span class="dt-side-single" aria-label="Designing ' + (CFG.sideConfigs[vis[0]]?.label || vis[0]) + '"><span class="dt-side-single__pre">Designing:</span> ' + (CFG.sideConfigs[vis[0]]?.label || vis[0]) + '</span>';
      headerSides.querySelectorAll('.dt-side-tab').forEach(b => b.addEventListener('click', () => switchToSide(b.dataset.side)));
    }
    buildSideSegment(vis);
    updateContextIndicator();
  }

  /* UX-Aug15 · Mobile [FRONT ✓ | BACK +] segment — replaces the floating FLIP button (which is
     CSS-hidden; its snippet JS only clicks .dt-side-tab and stays inert). Same top-centre slot,
     same footprint, so applyEditModeZoom's available height is unchanged. Own id/class so the
     mobile hide rule for #dt-side-tabs / #dt-header-sides never applies. Rebuilt on every
     side/variant change (called from buildSideTabs). ✓ = side has visible layers; + = empty
     extra side (the back-side upsell in one glyph). */
  function buildSideSegment(vis) {
    try {
      const area = $('dt-canvas-area'); if (!area) return;
      let seg = $('dt-side-seg');
      if (!isMobile() || !vis || vis.length < 2) { if (seg) seg.hidden = true; return; }
      if (!seg) {
        seg = document.createElement('div'); seg.id = 'dt-side-seg'; seg.className = 'dt-side-seg';
        seg.setAttribute('role', 'tablist'); seg.setAttribute('aria-label', 'Side to design');
        area.appendChild(seg);
        seg.addEventListener('click', e => {
          const b = e.target.closest('.dt-side-seg__btn'); if (!b || b.dataset.side === S.view) return;
          switchToSide(b.dataset.side);
        });
      }
      seg.hidden = false;
      seg.innerHTML = vis.map(slug => {
        const lbl = CFG.sideConfigs[slug]?.label || slug;
        const has = (S.byView[slug] || []).some(l => l.visible !== false);
        const on = slug === S.view;
        const mark = has ? '<span class="dt-side-seg__mark" aria-hidden="true">✓</span>' : (!on ? '<span class="dt-side-seg__mark dt-side-seg__mark--add" aria-hidden="true">+</span>' : '');
        return `<button type="button" class="dt-side-seg__btn${on ? ' is-on' : ''}" role="tab" aria-selected="${on ? 'true' : 'false'}" data-side="${slug}">${lbl}${mark}</button>`;
      }).join('');
    } catch (e) { DEBUG && console.warn('[dt] buildSideSegment:', e && e.message); }
  }

  /* UX-Aug20: desktop canvas-top row — [method] pill on the LEFT, [sides] pill on the RIGHT.
     Re-parents (never clones) the existing #dt-header-sides and #dt-method nodes: buildSideTabs()
     rewrites #dt-header-sides' innerHTML by id (survives a move) and applyMethod's
     $$('.dt-method-btn') sync + the listeners bound in setupMethodToggle travel with the node.
     Idempotent. */
  function setupCanvasRow() {
    try {
      if (isMobile()) return;
      const row = $('dt-header-row2'); if (!row) return;
      /* LEFT pill: method toggle (ENGRAVE / PRINT) */
      let left = row.querySelector('.dt-canvas-row__left');
      if (!left) {
        left = document.createElement('div'); left.className = 'dt-canvas-row__left';
        row.insertBefore(left, row.firstChild);
      }
      const method = $('dt-method');   /* absent on single-method products (#dt-method-single stays put) */
      if (method && !left.contains(method)) {
        const wrap = document.createElement('div'); wrap.className = 'dt-canvas-row__method';
        wrap.appendChild(method); left.appendChild(wrap);
      }
      /* RIGHT group: side tabs (FRONT / BACK) + color selector */
      let right = row.querySelector('.dt-canvas-row__right');
      if (!right) {
        right = document.createElement('div'); right.className = 'dt-canvas-row__right';
        row.appendChild(right);
      }
      const sides = $('dt-header-sides');
      if (sides && sides.parentNode !== right) right.insertBefore(sides, right.firstChild);
      const colorSel = row.querySelector('.dt-header__color-select');
      if (colorSel && colorSel.parentNode !== right) right.appendChild(colorSel);
    } catch (e) { DEBUG && console.warn('[dt] setupCanvasRow:', e && e.message); }
  }

  /* Shared side switch (used by mobile canvas tabs + desktop header tabs) */
  function switchToSide(slug) {
    const _prev = S.view, _prevHadArt = (S.byView[_prev] || []).some(l => l.visible !== false);
    S.view = slug; S.sel = null; S.iact = null;
    dtAnalytics.sideSwitch(slug);
    buildSideTabs(); loadMockup(); updateLayersPanel(); forcePropsRebuild(); syncMobileSelection();
    updateContextIndicator();
    const _noticed = maybeNoticeSecondSide(slug);
    /* CRO #25 — reassure: switching sides never loses the other side's work.
       Once per side per session; the upcharge notice above takes priority. */
    if (!_noticed && slug !== _prev && _prevHadArt) {
      S._sideNarrated = S._sideNarrated || {};
      if (!S._sideNarrated[slug]) {
        S._sideNarrated[slug] = 1;
        toast('side_editing', { side: CFG.sideConfigs[slug]?.label || slug, prev: CFG.sideConfigs[_prev]?.label || _prev });
      }
    }
  }

  /* Up-front clarity: decorating a second side adds a separate cart item.
     Fires once per session, the moment they move to an undesigned extra side
     after already designing one — so it's never a checkout surprise. */
  function maybeNoticeSecondSide(slug) {
    if (S._secondSideNoticed) return false;
    const designed = getDesignedSides();
    const isUndesigned = !(S.byView[slug] && S.byView[slug].some(l => l.visible !== false));
    if (designed.length >= 1 && isUndesigned && designed.indexOf(slug) === -1) {
      const price = CFG.isCooler ? '$20' : '$6';
      toast('side_upcharge', { price: price }, 4500);
      S._secondSideNoticed = true;
      return true;
    }
    return false;
  }

  /* ══════════════════════════════════════════════════════════════════
   * SIBLING SELECTOR
   * ══════════════════════════════════════════════════════════════════ */
  function buildSiblingSelector() {
    const c = $('dt-sibling-selector');
    if (!c || !CFG.siblings.length) return;
    c.innerHTML = CFG.siblings.map(s =>
      `<button class="dt-sibling-btn${s.isCurrent ? ' active' : ''}" data-url="${s.url}">${s.size}</button>`
    ).join('');
    c.querySelectorAll('.dt-sibling-btn').forEach(b => b.addEventListener('click', () => {
      if (!b.classList.contains('active')) window.location.href = b.dataset.url;
    }));
  }

  /* ══════════════════════════════════════════════════════════════════
   * METHOD TOGGLE
   * ══════════════════════════════════════════════════════════════════ */
  function setupMethodToggle() {
    /* Apply a method change — single source of truth for the toggle.
       `anchorBtn` is the button the "best for…" bubble hangs off of. */
    function applyMethod(newMethod, anchorBtn) {
      if (newMethod !== 'engrave' && newMethod !== 'print') return;
      if (newMethod === S.method) return;
      /* CRO #9 — first manual toggle: explain the two methods once */
      if (!S._progMethodSwitch && !localStorage.getItem('dt_method_explained')) {
        try { localStorage.setItem('dt_method_explained', '1'); } catch (_) {}
        showMethodExplainer();
      }
      /* CRO #25 — narrate what just happened to the design */
      if (layers().length > 0) {
        toast(newMethod === 'engrave' ? 'method_engrave' : 'method_print');
        if (newMethod === 'engrave') setTimeout(runEtchSweep, 60);   /* F1 */
      }
      /* Sync active button(s) — desktop + mobile share data-method values */
      $$('.dt-method-btn').forEach(b => b.classList.toggle('active', b.dataset.method === newMethod));
      /* Sync wrapper data-pos (drives the sliding thumb) + gum-stretch pulse */
      $$('.dt-method').forEach(w => {
        w.dataset.pos = (newMethod === 'print') ? '1' : '0';
        w.classList.add('is-sliding');
        setTimeout(() => w.classList.remove('is-sliding'), 200);
      });
      S.method = newMethod;
      if (app) app.dataset.method = S.method;
      /* Toggle upload panel between standard (print) and engrave two-path */
      const stdUpload = $('dt-upload-standard'), engUpload = $('dt-upload-engrave');
      if (stdUpload && engUpload) {
        stdUpload.style.display = S.method === 'engrave' ? 'none' : '';
        engUpload.style.display = S.method === 'engrave' ? '' : 'none';
      }
      loadOverlay();
      reseedAutoTextColors('method:' + S.method); /* UX-Aug15 #1 — flagged layers only */
      forcePropsRebuild();
      render();
      showMethodBubble(S.method, anchorBtn);
      setTimeout(syncHeaderHeight, 50);
      /* (#clipart-engrave) Warn + offer to remove any fixed-colour clipart that can't engrave. */
      if (newMethod === 'engrave') setTimeout(maybeWarnEngraveFixedColor, 120);
      /* v2.11.0: an inverted plate is an ENGRAVE construct — in print it would
         ship as a giant dark rectangle through the print chain. toast(), not
         showTip() — showTip is globally disabled (review C3). */
      if (newMethod === 'print' && layers().some(l => l._engraveInverted)) {
        setTimeout(() => toast('Heads up: your inverted engrave layer prints as a solid dark shape in Print mode.', null, 5000), 200);
      }
    }

    /* The "other" method relative to current state */
    const other = () => (S.method === 'engrave' ? 'print' : 'engrave');

    /* Buttons: clicking the ACTIVE one flips too (real-toggle behavior). */
    $$('.dt-method-btn').forEach(btn => btn.addEventListener('click', () => {
      const target = (btn.dataset.method === S.method) ? other() : btn.dataset.method;
      const anchor = document.querySelector('.dt-method-btn[data-method="' + target + '"]');
      applyMethod(target, anchor || btn);
    }));

    /* Flanking labels: clicking selects that method (or flips if already active). */
    $$('.dt-method-label').forEach(lbl => lbl.addEventListener('click', () => {
      const want = lbl.dataset.method;
      const target = (want === S.method) ? other() : want;
      const anchor = document.querySelector('.dt-method-btn[data-method="' + target + '"]');
      applyMethod(target, anchor);
    }));
  }

  /* ══════════════════════════════════════════════════════════════════
   * LEFT SIDEBAR TABS
   * ══════════════════════════════════════════════════════════════════ */
  function setupLeftTabs() {
    const panelsWrap = document.querySelector('.dt-sidebar__panels');
    const tabsNav = document.querySelector('.dt-sidebar__tabs');

    /* ── Desktop: keep original DOM structure (tabs container + panels container)
       for the floating dock layout. No restructuring needed. ── */

    $$('.dt-sidebar__tab').forEach(btn => btn.addEventListener('click', () => {
      /* Clean up any unmodified default text layers when navigating away */
      cleanupDefaultText();

      /* The desktop Properties overlay floats over the panel slot (z-index 40).
         Switching tools must hide it, or the chosen panel opens *behind* it and
         nothing appears to change. Deselecting (same path as Escape) hides it. */
      if (!isMobile() && S.sel) {
        S.sel = null; S.iact = null;
        updateLayersPanel(); forcePropsRebuild(); render(); syncMobileSelection();
      }

      const isCollapsed = window.innerWidth <= 1280 && window.innerWidth > 1024;
      const wasActive = btn.classList.contains('active');

      $$('.dt-sidebar__tab').forEach(b => b.classList.remove('active'));
      $$('.dt-panel').forEach(p => p.classList.remove('active'));

      /* Sync panel container class for CSS :has() fallback */
      const panelsCtr = document.querySelector('.dt-sidebar__panels');
      if (panelsCtr) panelsCtr.classList.remove('has-active-panel');

      /* Sync sidebar class for drop-shadow :has() fallback */
      const sidebarEl = document.querySelector('.dt-sidebar--left');
      if (sidebarEl) { sidebarEl.classList.remove('has-active-tab'); sidebarEl.classList.remove('dt-clipart-open'); }

      if (wasActive) return; /* Toggle off — close panel */

      btn.classList.add('active');
      dtAnalytics.toolOpen(btn.dataset.panel);
      const p = $('dt-panel-' + btn.dataset.panel);
      if (p) p.classList.add('active');
      if (panelsCtr) panelsCtr.classList.add('has-active-panel');
      if (sidebarEl) sidebarEl.classList.add('has-active-tab');
      /* (#49) Desktop: float the library panels as a wider 3-column drawer OVER the canvas
         (canvas doesn't resize — the reserved slot stays; the drawer is position:fixed and
         overlays). Monogram joined Graphics/Templates so all libraries share one layout.
         Saved is synced from loadSavedDesignsGrid instead — it only widens when the
         customer actually has designs (signin/empty states keep the slot width). */
      if (sidebarEl && !isMobile() && (btn.dataset.panel === 'clipart' || btn.dataset.panel === 'templates' || btn.dataset.panel === 'monogram') && panelsCtr) {
        openLibraryDrawer();
      }

      if (btn.dataset.panel === 'monogram' && !isMobile()) buildMonogramInPanel();
      if (btn.dataset.panel === 'templates') setupTemplates();
      if (btn.dataset.panel === 'layers') updateLayersPanel();
      if (btn.dataset.panel === 'saved') loadSavedDesignsGrid();
    }));

    /* Click outside sidebar to close accordion + clean up default text */
    document.addEventListener('mousedown', e => {
      if (isMobile()) return;
      const sidebar = document.querySelector('.dt-sidebar--left');
      if (sidebar?.contains(e.target)) return;
      const rightPanel = document.querySelector('.dt-sidebar--right');
      if (rightPanel?.contains(e.target)) return;
      if (e.target.closest('.dt-rbg-overlay')) return;
      if (e.target.closest('.dt-imr-overlay, .dt-imr-zoom')) return;
      /* The review/inspiration zoom lightbox sits over everything (z-100000) and is
         appended to <body>, i.e. outside the sidebar. Without this, clicking its
         backdrop to close it would also deactivate the open Start panel. */
      if (e.target.closest('.dt-review-lightbox, .dt-inspo-pop')) return;
      cleanupDefaultText();
      /* Coach-mark tips, the cart-wait dialog, and the restore banner are
         body-appended; clicking them must not yank the open panel away. */
      if (e.target.closest('#dt-tour, .dt-wait, .dt-restore-banner')) return;
      /* Canvas-side view controls (header row2 cluster, quick-zoom satellite,
         dims toggle): adjusting the view isn't "clicking off" — keep the
         panel open. */
      if (e.target.closest('.dt-header__row2, #dt-quick-zoom, #dt-dims-toggle, #dt-ctx-bar, #dt-side-seg')) return; /* UX-Aug15: + actions bar, side segment */
      /* Clicking the canvas/mockup DOES close the open tool panel now. The old
         keep-open rule predates the wide drawers: a drawer (z:60) overlays the
         canvas and sits ABOVE the properties overlay (z:40), so selecting a
         layer with a drawer open left the drawer covering the props. The panel
         slot is a reserved fixed-width column, so closing never reflows the
         canvas mid-click; layer selection still runs via the canvas handlers. */
      $$('.dt-sidebar__tab').forEach(b => b.classList.remove('active'));
      $$('.dt-panel').forEach(p => p.classList.remove('active'));
      document.querySelector('.dt-sidebar__panels')?.classList.remove('has-active-panel');
      const _sb = document.querySelector('.dt-sidebar--left');
      if (_sb) { _sb.classList.remove('has-active-tab'); _sb.classList.remove('dt-clipart-open'); }
    });
  }

  /* (#49) Open the desktop library drawer: add the sidebar class and measure the
     reserved panel slot into --dt-clip-* so the fixed drawer lines up regardless
     of the exact sidebar width. Shared by the tab handler (Graphics / Templates /
     Monogram) and loadSavedDesignsGrid (Saved, only when designs exist). */
  function openLibraryDrawer() {
    const sidebarEl = document.querySelector('.dt-sidebar--left');
    const panelsCtr = document.querySelector('.dt-sidebar__panels');
    if (!sidebarEl || !panelsCtr) return;
    sidebarEl.classList.add('dt-clipart-open');
    const rb = panelsCtr.getBoundingClientRect();
    const rootS = document.documentElement.style;
    rootS.setProperty('--dt-clip-left', Math.round(rb.left) + 'px');
    rootS.setProperty('--dt-clip-top', Math.round(rb.top) + 'px');
    rootS.setProperty('--dt-clip-h', Math.round(rb.height) + 'px');
  }

  /* ══════════════════════════════════════════════════════════════════
   * START PANEL ROUTING
   * ══════════════════════════════════════════════════════════════════ */
  function routeToTool(panel, mode) {
    const mobile = isMobile();
    /* Pick the tab for the ACTIVE viewport. The opposite viewport's tabs still
       exist in the DOM (just display:none), so a viewport-agnostic query would
       click the wrong, hidden tab and nothing visible would happen. */
    const tabFor = (p) => mobile
      ? document.querySelector('.dt-mobile-tab[data-panel="' + p + '"]')
      : document.querySelector('.dt-sidebar__tab[data-panel="' + p + '"]');

    /* Special paths */
    if (panel === 'bulk') {
      /* Pure direct action — no tab click — so close the mobile modal ourselves. */
      if (mobile && S._closeMobileDrawer) S._closeMobileDrawer();
      openBulkOrderModal();
      return;
    }
    if (panel === 'photo') {
      /* Method (engrave vs print) only surfaces here — the one place finish
         changes the output. Everything else is method-agnostic. */
      startPhotoFlow();
      return;
    }
    if (panel === 'templates') {
      /* Open the Templates panel (its open-hook calls setupTemplates). If the
         product has no template galleries, setupTemplates shows an empty state
         that redirects to Graphics, so this is never a dead end. */
      const t = tabFor('templates');
      if (t) t.click();
      return;
    }
    /* Upload mode hint for the future Magic Upload flow (logo vs photo) */
    if (panel === 'upload' && mode) S._uploadMode = mode;
    /* Tab routes: on mobile, clicking the mobile tab triggers openMobilePanel,
       which closes any open modal itself — no explicit pre-close needed. */
    const tab = tabFor(panel);
    if (tab) tab.click();
  }

  /* ══ Roadmap 2 — START WIZARD: page-by-page launchpad in the Start panel.
     Renders over the static liquid markup at init (JS-only deploy). Pages:
     method (dual-method products only) → starting point → make-it-yours
     (popular templates). Other picks route straight into their tool. ══ */
  function _swSteps() { return (CFG.hasEngrave && CFG.hasPrint) ? ['method', 'start', 'make'] : ['start', 'make']; }

  function setupStartCards() { renderStartStep(0); }

  function renderStartStep(idx) {
    const host = $('dt-panel-start'); if (!host) return;
    const steps = _swSteps();
    idx = Math.max(0, Math.min(steps.length - 1, idx));
    const kind = steps[idx];
    const fg = '#F47E46', fo = '#344E41';
    const dots = '<div class="dt-sw-dots">' + steps.map((st, i) =>
      '<span class="dt-sw-dot' + (i <= idx ? ' on' : '') + '">' + (i < idx ? '\u2713' : (i + 1)) + '</span>').join('') +
      '<span class="dt-sw-count">step ' + (idx + 1) + ' of ' + steps.length + '</span></div>';
    const back = idx > 0 ? '<button class="dt-sw-back" type="button" data-sw-back>\u2039 Back</button>' : '';
    let body = '';

    if (kind === 'method') {
      const eng = S.method === 'engrave';
      body =
        '<div class="dt-sw-title">How should we make it?</div>' +
        '<div class="dt-sw-sub">You can switch anytime \u2014 colors adjust for the method.</div>' +
        '<button class="dt-sw-card' + (eng ? ' is-on' : '') + '" type="button" data-sw-method="engrave">' +
          '<span class="dt-sw-prev"><svg viewBox="0 0 48 48" width="44" height="44" aria-hidden="true"><rect x="2" y="2" width="44" height="44" rx="9" fill="#3c4248"/><rect x="2" y="2" width="44" height="44" rx="9" fill="none" stroke="#565e66" stroke-width="1.5"/><text x="24" y="31" text-anchor="middle" font-family="Georgia,serif" font-size="20" fill="none" stroke="#c8cdd2" stroke-width="0.9">Aa</text></svg></span>' +
          '<span class="dt-sw-card__tx"><strong>Laser engrave</strong><small>Etched into the surface \u2014 single color, never fades.</small></span>' +
        '</button>' +
        '<button class="dt-sw-card' + (!eng ? ' is-on' : '') + '" type="button" data-sw-method="print">' +
          '<span class="dt-sw-prev"><svg viewBox="0 0 48 48" width="44" height="44" aria-hidden="true"><rect x="2" y="2" width="44" height="44" rx="9" fill="#fff" stroke="#e2ded7" stroke-width="1.5"/><text x="24" y="28" text-anchor="middle" font-family="Georgia,serif" font-size="19" font-weight="bold" fill="' + fg + '">Aa</text><circle cx="15" cy="38" r="3" fill="#4A90D9"/><circle cx="24" cy="38" r="3" fill="#3A7D5C"/><circle cx="33" cy="38" r="3" fill="#F4C542"/></svg></span>' +
          '<span class="dt-sw-card__tx"><strong>Full-color print</strong><small>Photos, logos and bright colors \u2014 printed vivid.</small></span>' +
        '</button>';
    } else if (kind === 'start') {
      const ic = (paths) => '<span class="dt-sw-ico"><svg width="20" height="20" viewBox="0 0 20 20" fill="none">' + paths + '</svg></span>';
      const sw = (d) => d.replace(/STROKE/g, fo);
      const opts = [
        { goto: 'templates', badge: 'fastest', t: 'Start from a template', d: 'Fastest \u2014 edit a finished design',
          i: ic('<rect x="2.5" y="2.5" width="6" height="6" rx="1.5" stroke="' + fo + '" stroke-width="1.4"/><rect x="11.5" y="2.5" width="6" height="6" rx="1.5" stroke="' + fo + '" stroke-width="1.4"/><rect x="2.5" y="11.5" width="6" height="6" rx="1.5" stroke="' + fo + '" stroke-width="1.4"/><rect x="11.5" y="11.5" width="6" height="6" rx="1.5" stroke="' + fo + '" stroke-width="1.4"/>') },
        { goto: 'text', t: 'Add a name or text', d: 'The personal touch \u2014 names & dates',
          i: ic(sw('<path d="M4 5V4H16V5" stroke="STROKE" stroke-width="1.5" stroke-linecap="round"/><path d="M10 4V16" stroke="STROKE" stroke-width="1.5" stroke-linecap="round"/><path d="M7 16H13" stroke="STROKE" stroke-width="1.5" stroke-linecap="round"/>')) },
        { goto: 'upload', mode: 'logo', t: 'Upload a logo or art', d: 'Brand it \u2014 company gifts & team swag',
          i: ic(sw('<path d="M10 3V13M10 3L6 7M10 3L14 7" stroke="STROKE" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 13V15C3 16.1 3.9 17 5 17H15C16.1 17 17 16.1 17 15V13" stroke="STROKE" stroke-width="1.5" stroke-linecap="round"/>')) },
        { goto: 'photo', t: 'Add a photo', d: 'A favorite photo, your way',
          i: ic(sw('<rect x="2" y="5" width="16" height="12" rx="2.5" stroke="STROKE" stroke-width="1.4"/><path d="M6.5 5L7.6 3H12.4L13.5 5" stroke="STROKE" stroke-width="1.4" stroke-linejoin="round"/><circle cx="10" cy="11" r="3" stroke="STROKE" stroke-width="1.4"/>')) },
        { goto: 'monogram', t: 'Create a monogram', d: 'Timeless & elegant \u2014 classic initials',
          i: ic('<circle cx="10" cy="10" r="8.4" stroke="' + fo + '" stroke-width="1.1"/><text x="7.2" y="13.1" text-anchor="middle" font-size="8" font-weight="700" font-style="italic" font-family="Georgia,serif" fill="' + fo + '">A</text><text x="12.6" y="13.1" text-anchor="middle" font-size="8" font-weight="700" font-style="italic" font-family="Georgia,serif" fill="' + fo + '">B</text>') },
        { goto: 'bulk', t: 'Group order', d: 'One design, many names \u2014 teams & events',
          i: ic(sw('<circle cx="7" cy="7" r="2.5" stroke="STROKE" stroke-width="1.4"/><path d="M2.5 16C2.5 13.5 4.5 12 7 12C9.5 12 11.5 13.5 11.5 16" stroke="STROKE" stroke-width="1.4" stroke-linecap="round"/><circle cx="14" cy="7.5" r="2" stroke="STROKE" stroke-width="1.4"/><path d="M13 12.2C15.3 12.5 17 14 17 16" stroke="STROKE" stroke-width="1.4" stroke-linecap="round"/>')) },
      ];
      body =
        '<div class="dt-sw-title">Pick a starting point</div>' +
        '<div class="dt-sw-sub">Change anything later.</div>' +
        opts.map(o =>
          '<button class="dt-sw-card" type="button" data-sw-goto="' + o.goto + '"' + (o.mode ? ' data-sw-mode="' + o.mode + '"' : '') + '>' +
            o.i +
            '<span class="dt-sw-card__tx"><strong>' + o.t + '</strong><small>' + o.d + '</small></span>' +
            (o.badge ? '<span class="dt-sw-badge">' + o.badge + '</span>' : '') +
          '</button>').join('') +
        '<button class="dt-sw-link" type="button" data-sw-surprise>\u2728 Surprise me instead</button>';
    } else {
      const items = allTemplateItems().slice(0, 4);
      body =
        '<div class="dt-sw-title">Make it yours</div>' +
        '<div class="dt-sw-sub">Popular on this product \u2014 tap to apply.</div>' +
        '<div class="dt-sw-popgrid">' + items.map((it, i) => unifiedTemplateCardHtml(it, i)).join('') + '</div>' +
        '<button class="dt-btn dt-btn--primary dt-btn--full" type="button" data-sw-browse style="margin-top:10px">Browse all ' + allTemplateItems().length + ' templates</button>';
    }

    host.innerHTML = '<div class="dt-sw">' + dots + body + back + '</div>';

    host.querySelectorAll('[data-sw-method]').forEach(b => b.addEventListener('click', () => {
      setMethodVisibly(b.dataset.swMethod);
      renderStartStep(idx + 1);
    }));
    host.querySelector('[data-sw-back]')?.addEventListener('click', () => renderStartStep(idx - 1));
    host.querySelectorAll('[data-sw-goto]').forEach(b => b.addEventListener('click', () => {
      if (b.dataset.swGoto === 'templates') renderStartStep(idx + 1);
      else if (b.dataset.swGoto === 'photo') startPhotoFlow();
      else routeToTool(b.dataset.swGoto, b.dataset.swMode || undefined);
    }));
    host.querySelector('[data-sw-surprise]')?.addEventListener('click', surpriseMe);
    host.querySelector('[data-sw-browse]')?.addEventListener('click', () => routeToTool('templates'));
    if (kind === 'make') {
      const items = allTemplateItems().slice(0, 4);
      host.querySelectorAll('.dt-tpl-card').forEach(card => {
        attachTplGhost(card, () => items[parseInt(card.dataset.i, 10)]);
        card.addEventListener('click', () => {
          const it = items[parseInt(card.dataset.i, 10)]; if (!it) return;
          hideTplGhost();
          if (it.kind === 'inline') applyInlineTemplate(it.tpl);
          else if (it.kind === 'state') applyStateTemplate();
          else if (it.kind === 'meta') loadAndApplyTemplate(it.it.designUrl);
        });
      });
      host.querySelectorAll('.dt-tpl-thumb--tpl').forEach(img => {
        const it = items[parseInt(img.dataset.ti, 10)];
        if (it && it.kind === 'inline') renderTemplateThumb(it.tpl).then(url => { if (url) img.src = url; });
      });
    }
  }

  /* CRO #24 — desktop Start panel: 4 popular templates fill the dead space
     below the start cards. Reuses the unified card + the same tap-to-apply
     wiring as the Templates library. Hidden on touch via CSS. */
  function renderStartPopular() {
    if (isMobile()) return;
    var panel = $('dt-panel-start');
    if (!panel || panel.querySelector('.dt-start-popular')) return;
    var items = allTemplateItems().slice(0, 4);
    if (!items.length) return;
    var wrap = document.createElement('div');
    wrap.className = 'dt-start-popular';
    wrap.innerHTML = '<div class="dt-start-popular__label">Popular on this product</div>' +
      '<div class="dt-start-popular__grid">' +
      items.map(function (it, i) { return unifiedTemplateCardHtml(it, i); }).join('') +
      '</div>';
    panel.appendChild(wrap);
    wrap.querySelectorAll('.dt-tpl-card').forEach(function (card) {
      card.addEventListener('click', function () {
        var it = items[parseInt(card.dataset.i, 10)]; if (!it) return;
        if (it.kind === 'inline') applyInlineTemplate(it.tpl);
        else if (it.kind === 'state') applyStateTemplate();
        else if (it.kind === 'meta') loadAndApplyTemplate(it.it.designUrl);
      });
    });
    wrap.querySelectorAll('.dt-tpl-thumb--tpl').forEach(function (img) {
      var it = items[parseInt(img.dataset.ti, 10)];
      if (it && it.kind === 'inline') renderTemplateThumb(it.tpl).then(function (url) { if (url) img.src = url; });
    });
  }

  /* ── Inspiration: "See what others made" — customer photo reviews (Loox) ──
     Read-only Loox Storefront API: publicStoreId only (no secret key), CORS-locked
     to the store domain, returns no PII. Pages a few times collecting photo media,
     session-cached. Shared by the Start panel and the welcome overlay. Tap = zoom. */
  const DT_INSPO_CAP = 24;
  function fetchInspiration() {
    if (S._inspoPromise) return S._inspoPromise;
    const id = CFG.looxStoreId;
    if (!id) { S._inspoPromise = Promise.resolve([]); return S._inspoPromise; }
    S._inspoPromise = (async () => {
      const out = [];
      let url = 'https://storefront-api.loox.io/storefront/v1/store/' + encodeURIComponent(id) + '/product-reviews';
      try {
        for (let p = 0; p < 4 && out.length < DT_INSPO_CAP && url; p++) {
          const res = await fetch(url, { credentials: 'omit' });
          if (!res.ok) break;
          const data = await res.json();
          (data.reviews || []).forEach(r => {
            const ph = (r.media || []).find(m => m && m.type === 'photo' && m.url);
            if (ph) out.push({ url: ph.url, product: (r.product && r.product.name) || '' });
          });
          url = (data.pagination && data.pagination.hasMore && data.pagination.nextPageUrl) || null;
        }
      } catch (e) { DEBUG && console.warn('[dt] inspiration fetch failed', e); }
      return out.slice(0, DT_INSPO_CAP);
    })();
    return S._inspoPromise;
  }
  function inspirationSectionHtml() {
    /* Hidden until photos actually load, so a failed/empty fetch leaves no stray heading. */
    return '<div class="dt-inspo" hidden>' +
      '<div class="dt-inspo__head">See what others made</div>' +
      '<div class="dt-inspo__sub">Real photos from happy customers</div>' +
      '<div class="dt-inspo__grid" data-inspo-grid></div>' +
    '</div>';
  }
  function populateInspiration(scope, max) {
    if (!scope) return;
    const section = scope.querySelector('.dt-inspo');
    const grid = scope.querySelector('[data-inspo-grid]');
    if (!section || !grid) return;
    fetchInspiration().then(all => {
      const items = (all || []).slice(0, max || DT_INSPO_CAP);
      if (!items.length) { section.setAttribute('hidden', ''); return; }
      grid.innerHTML = items.map((it, i) =>
        '<button class="dt-inspo-card" type="button" data-inspo-i="' + i + '" title="' + escHtml(it.product || '') + '">' +
          '<img src="' + it.url + '" alt="' + escHtml(it.product || 'Customer photo') + '" loading="lazy" decoding="async">' +
        '</button>').join('');
      section.removeAttribute('hidden');
      grid.querySelectorAll('[data-inspo-i]').forEach(btn => btn.addEventListener('click', () => {
        const it = items[parseInt(btn.dataset.inspoI, 10)]; if (!it) return;
        openReviewLightbox(it.url, null, it.product ? ('Made by a customer · ' + it.product) : 'Made by a customer');
      }));
    });
  }

  /* Floating popover (over the whole tool) showing customer photos in the SAME card
     styling as the libraries. Appended to <body> — i.e. OUTSIDE #dt-app — so the
     engrave brightness(0) filter on `#dt-app .dt-clip-item img` can never reach these
     real photos. Tap a photo to zoom (reuses the review lightbox). */
  function openInspirationPopover() {
    document.getElementById('dt-inspo-pop')?.remove();
    const ov = document.createElement('div');
    ov.id = 'dt-inspo-pop'; ov.className = 'dt-inspo-pop';
    ov.innerHTML =
      '<div class="dt-inspo-pop__panel" role="dialog" aria-label="Customer inspiration">' +
        '<div class="dt-inspo-pop__head">' +
          '<span class="dt-inspo-pop__title">See what others made</span>' +
          '<button class="dt-inspo-pop__close" id="dt-inspo-pop-close" type="button" aria-label="Close">×</button>' +
        '</div>' +
        '<div class="dt-inspo-pop__sub">Real photos from happy customers — tap any to zoom.</div>' +
        '<div class="dt-clipart-grid dt-inspo-pop__grid" id="dt-inspo-pop-grid"><div class="dt-empty-state">Loading…</div></div>' +
      '</div>';
    document.body.appendChild(ov);
    /* A11y: full modal semantics (trap, Escape, focus restore) via shared helper */
    let _mTear = () => {};
    const close = () => { _mTear(); ov.remove(); };
    ov.querySelector('#dt-inspo-pop-close').addEventListener('click', close);
    ov.addEventListener('click', e => { if (e.target === ov) close(); });
    _mTear = wireModalA11y(ov, { onEscape: close, initial: () => ov.querySelector('#dt-inspo-pop-close') });
    const grid = ov.querySelector('#dt-inspo-pop-grid');
    fetchInspiration().then(items => {
      if (!grid.isConnected) return;
      if (!items || !items.length) { grid.innerHTML = '<div class="dt-empty-state">No customer photos yet.</div>'; return; }
      grid.innerHTML = items.map((it, i) =>
        '<div class="dt-clip-card">' +
          '<div class="dt-clip-item" data-inspo-i="' + i + '" title="' + escHtml(it.product || '') + '">' +
            '<img src="' + it.url + '" alt="' + escHtml(it.product || 'Customer photo') + '" loading="lazy" decoding="async"></div>' +
          (it.product ? '<div class="dt-clip-name">' + escHtml(it.product) + '</div>' : '') +
        '</div>').join('');
      grid.querySelectorAll('[data-inspo-i]').forEach(el => el.addEventListener('click', () => {
        const it = items[parseInt(el.dataset.inspoI, 10)]; if (!it) return;
        /* Caption: product title only — the "Made by a customer" prefix read poorly. */
        openReviewLightbox(it.url, null, it.product || '');
      }));
    });
  }

  /* Inject an "Inspiration" button right after Help — under it on the desktop tab
     rail, to the right of it in the mobile tab row. Help is injected async by its
     own snippet, so poll until it exists and insert after it. */
  function setupInspirationButton() {
    if (!CFG.looxStoreId) return;
    const ICON = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
      '<path d="M10 2.5a5 5 0 0 0-3 9c.4.3.6.7.6 1.2v.3h4.8v-.3c0-.5.2-.9.6-1.2a5 5 0 0 0-3-9Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
      '<path d="M8 16h4M8.8 18h2.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
    const make = tabClass => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = tabClass + ' dt-inspo-btn';
      b.setAttribute('aria-label', 'See what others made');
      b.title = 'Inspiration';
      b.innerHTML = ICON + '<span>Inspiration</span>';
      b.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); openInspirationPopover(); });
      return b;
    };
    let tries = 0;
    const iv = setInterval(() => {
      tries++;
      const dRail = document.querySelector('.dt-sidebar__tabs');
      if (dRail && !dRail.querySelector('.dt-inspo-btn')) {
        const h = dRail.querySelector('.dt-help-btn');
        if (h) h.insertAdjacentElement('afterend', make('dt-sidebar__tab'));
        else if (tries > 45) dRail.appendChild(make('dt-sidebar__tab'));
      }
      const mRail = document.querySelector('.dt-mobile-tabs');
      if (mRail && !mRail.querySelector('.dt-inspo-btn')) {
        const h = mRail.querySelector('.dt-help-btn');
        if (h) h.insertAdjacentElement('afterend', make('dt-mobile-tab'));
        else if (tries > 45) mRail.appendChild(make('dt-mobile-tab'));
      }
      const dDone = !document.querySelector('.dt-sidebar__tabs') || document.querySelector('.dt-sidebar__tabs .dt-inspo-btn');
      const mDone = !document.querySelector('.dt-mobile-tabs') || document.querySelector('.dt-mobile-tabs .dt-inspo-btn');
      if ((dDone && mDone) || tries > 60) clearInterval(iv);
    }, 120);
  }

  /* ── Photo flow: method surfaces ONLY here (Iteration 5) ── */

  /* Set engrave/print so the header toggle + uploads + canvas all stay in sync,
     by driving the real toggle button (single source of truth). */
  function setMethodVisibly(method) {
    if (S.method === method) return;
    var btn = document.querySelector('.dt-method-btn[data-method="' + method + '"]');
    S._progMethodSwitch = true;            /* CRO #9: not a manual toggle tap */
    try { if (btn) btn.click(); } finally { S._progMethodSwitch = false; }
  }

  function startPhotoFlow() {
    if (isMobile() && S._closeMobileDrawer) S._closeMobileDrawer();
    var dual = CFG.hasEngrave && CFG.hasPrint;
    if (!dual) { afterPhotoRoute(); return; }  /* single-method: nothing to ask */
    showPhotoFinishChoice();
  }

  /* Route into the uploader for a photo and leave a one-time hint so the
     hand-off isn't silent (the auto line-art conversion is a later step). */
  function afterPhotoRoute(method) {
    if (method) setMethodVisibly(method);
    routeToTool('upload', 'photo');
    var msg = (S.method === 'engrave')
      ? 'Upload your photo here \u2014 we\u2019ll set it up for an engraved look.'
      : 'Upload your photo here to add it to your design.';
    showTip('photo-upload', msg, null);
    /* Let the panel render, then land them on the right path. */
    setTimeout(applyUploadModeHint, 80);
  }

  /* Obsolete since the engrave uploader became a single auto-detecting zone:
     there's no separate Photo card to cue anymore. Kept as a safe no-op so
     existing callers (afterPhotoRoute) don't break; just clears the flag. */
  function applyUploadModeHint() {
    if (S._uploadMode !== 'photo') return;
    S._uploadMode = null;
  }

  function showPhotoFinishChoice() {
    document.getElementById('dt-photo-finish')?.remove();
    var ov = document.createElement('div');
    ov.id = 'dt-photo-finish';
    ov.className = 'dt-confirm-overlay';
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-modal', 'true');
    ov.setAttribute('aria-labelledby', 'dt-photo-finish-title');
    ov.innerHTML =
      '<div class="dt-photo-finish__box">' +
        '<div class="dt-photo-finish__title" id="dt-photo-finish-title">How should we finish your photo?</div>' +
        '<div class="dt-photo-finish__sub">Same price either way \u2014 just pick the look. You can change it later.</div>' +
        '<div class="dt-photo-finish__opts">' +
          '<button type="button" class="dt-photo-finish__opt" data-finish="print">' +
            '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="12" r="3.5" stroke="currentColor" stroke-width="1.5"/></svg>' +
            '<strong>Full color</strong><small>Printed in vivid color</small></button>' +
          '<button type="button" class="dt-photo-finish__opt" data-finish="engrave">' +
            '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M13 4l1.6 4.4L19 10l-4.4 1.6L13 16l-1.6-4.4L7 10l4.4-1.6L13 4Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M6 15l.8 2.2L9 18l-2.2.8L6 21l-.8-2.2L3 18l2.2-.8L6 15Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>' +
            '<strong>Engraved line art</strong><small>Best for a clean, etched look</small></button>' +
        '</div>' +
        '<button type="button" class="dt-photo-finish__cancel">Cancel</button>' +
      '</div>';
    (document.querySelector('.dt-body') || document.body).appendChild(ov);
    var prevFocus = document.activeElement;            /* R2: remember trigger */
    function close() {
      document.removeEventListener('keydown', onKey);  /* R1: always clean up */
      ov.remove();
      if (prevFocus && prevFocus.focus) { try { prevFocus.focus({ preventScroll: true }); } catch (e) {} }
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {                            /* R9: trap focus in the dialog */
        var f = ov.querySelectorAll('button');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    ov.querySelectorAll('.dt-photo-finish__opt').forEach(function (b) {
      b.addEventListener('click', function () {
        close();
        afterPhotoRoute(b.dataset.finish);
      });
    });
    ov.querySelector('.dt-photo-finish__cancel').addEventListener('click', close);
    ov.addEventListener('click', function (e) { if (e.target === ov) close(); });
    document.addEventListener('keydown', onKey);
    ov.querySelector('.dt-photo-finish__opt')?.focus({ preventScroll: true }); /* R7 */
  }

  /* Make any overlay keyboard-accessible the same way showPhotoFinishChoice is:
     aria-modal, Escape-to-close, Tab focus-trap, focus an initial control, and
     restore focus to the trigger on close. Returns a teardown() to call from the
     overlay's own close path. Reused by confirmDelete + the state picker so every
     dialog behaves consistently for keyboard / screen-reader users. */
  function wireModalA11y(ov, opts) {
    opts = opts || {};
    if (!ov) return function () {};
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-modal', 'true');
    var prevFocus = document.activeElement;
    function focusables() {
      var f = ov.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      return Array.prototype.filter.call(f, function (el) { return el.offsetParent !== null && !el.disabled; });
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); if (opts.onEscape) opts.onEscape(); return; }
      if (e.key === 'Tab') {
        var f = focusables(); if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    setTimeout(function () {
      try { ((opts.initial && opts.initial()) || focusables()[0]).focus({ preventScroll: true }); } catch (e) {}
    }, 20);
    return function teardown() {
      document.removeEventListener('keydown', onKey);
      if (prevFocus && prevFocus.focus) { try { prevFocus.focus({ preventScroll: true }); } catch (e) {} }
    };
  }

  /* ══════════════════════════════════════════════════════════════════
   * MOBILE DRAWER SYSTEM
   * ══════════════════════════════════════════════════════════════════ */
  function setupMobileTabs() {
    const drawer = $('dt-mobile-drawer');
    const content = $('dt-mobile-drawer-content');
    if (!drawer || !content) {
      $$('.dt-mobile-tab').forEach(b => b.addEventListener('click', () => {
        $$('.dt-mobile-tab').forEach(t => t.classList.remove('active'));
        b.classList.add('active');
      }));
      return;
    }

    const panelMap = {
      start:    'dt-panel-start',
      upload:   'dt-panel-upload',
      templates: 'dt-panel-templates',
      clipart:  'dt-panel-clipart',
      text:     'dt-panel-text',
      monogram: 'dt-panel-monogram',
      layers:   'dt-panel-layers',
      saved:    'dt-panel-saved',
      props:    'dt-props-panel',
    };

    const panelTitles = { start: 'Start here', templates: 'Templates', upload: 'Upload', clipart: 'Graphics', text: 'Add Text', monogram: 'Monogram', layers: 'Layers', saved: 'Saved Designs' };

    /* Persistent tool modal — created once, reused */
    let toolModal = null;
    let toolModalContent = null;
    function ensureToolModal() {
      if (toolModal) return;
      toolModal = document.createElement('div');
      toolModal.className = 'dt-tool-modal dt-kb-aware';
      toolModal.id = 'dt-tool-modal';
      toolModal.innerHTML = '<div class="dt-tool-modal__header">' +
        '<span class="dt-tool-modal__title" id="dt-tool-modal-title"></span>' +
        '<button class="dt-tool-modal__close" id="dt-tool-modal-close" aria-label="Close">' +
        '<svg width="14" height="14" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '</button></div>' +
        '<div class="dt-tool-modal__content dt-mobile-panel" id="dt-tool-modal-content"></div>';
      toolModal.style.display = 'none';
      app?.appendChild(toolModal);
      toolModalContent = toolModal.querySelector('#dt-tool-modal-content');
      toolModal.querySelector('#dt-tool-modal-close').addEventListener('click', closeMobilePanel);
    }

    /* ── ROUTER: open panel — props=drawer, tools=modal ── */
    function openMobilePanel(name) {
      if (!isMobile()) return;
      if (S._mobileActivePanel) closeMobilePanel();
      const el = $(panelMap[name]);
      if (!el) return;
      el._originalParent = el.parentElement;

      if (name === 'props') {
        /* Props → bottom drawer (canvas interaction stays possible) */
        content.appendChild(el);
        el.classList.add('active');
        drawer.classList.add('open');
        S._mobileActivePanelType = 'drawer';
        let xBtn = drawer.querySelector('.dt-drawer-close');
        if (!xBtn) {
          xBtn = document.createElement('button');
          xBtn.className = 'dt-drawer-close';
          xBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
          xBtn.addEventListener('click', () => closeMobilePanel());
          drawer.appendChild(xBtn);
        }
        xBtn.style.display = 'flex';
        const inlineBar = document.getElementById('dt-inline-bar');
        if (inlineBar) inlineBar.classList.add('above-drawer');
        requestAnimationFrame(() => {
          content.scrollTop = 0; /* always open a fresh panel at the top */
          updateDrawerScrollHints();
          if (S.mode === 'design') requestZoomToPA(50, true);
        });
      } else {
        /* Tool panels → full-height modal (above tab bar) */
        ensureToolModal();
        toolModalContent.appendChild(el);
        el.classList.add('active');
        toolModal.querySelector('#dt-tool-modal-title').textContent = panelTitles[name] || name;
        toolModal.style.display = 'flex';
        /* rev 11: the tab bar squares its top corners while a panel sits on it (no gap) */
        document.body.classList.add('dt-tool-modal-open');
        S._mobileActivePanelType = 'modal';
        /* The modal content scroller is reused across tool panels, so it keeps the
           previous panel's scroll position — opening a new tool would land at the
           bottom and look empty/broken. Reset to top on every open. */
        if (toolModalContent) { toolModalContent.scrollTop = 0; requestAnimationFrame(() => { toolModalContent.scrollTop = 0; }); }
      }

      S._mobileActivePanel = name;
      if (name !== 'props') dtAnalytics.toolOpen(name);
      $$('.dt-mobile-tab').forEach(t => t.classList.toggle('active', t.dataset.panel === name));
      if (name === 'templates') setupTemplates();
      if (name === 'layers') updateLayersPanel();
      if (name === 'saved') loadSavedDesignsGrid();
    }

    /* ── ROUTER: close whatever is open ── */
    function closeMobilePanel() {
      if (!S._mobileActivePanel) return;
      returnPanel(S._mobileActivePanel);

      if (S._mobileActivePanelType === 'drawer') {
        drawer.classList.remove('open');
        hideBackdrop();
        const inlineBar = document.getElementById('dt-inline-bar');
        if (inlineBar) inlineBar.classList.remove('above-drawer');
      } else if (S._mobileActivePanelType === 'modal') {
        if (toolModal) toolModal.style.display = 'none';
        document.body.classList.remove('dt-tool-modal-open');
      }

      S._mobileActivePanel = null;
      S._mobileActivePanelType = null;
      $$('.dt-mobile-tab').forEach(t => t.classList.remove('active'));
      if (isMobile() && S.mode === 'design' && S.sel) requestZoomToPA(100, true);
    }

    function returnPanel(name) {
      const el = $(panelMap[name]);
      if (el?._originalParent) el._originalParent.appendChild(el);
    }

    function showBackdrop() {
      let bd = document.querySelector('.dt-mobile-backdrop');
      if (!bd) {
        bd = document.createElement('div');
        bd.className = 'dt-mobile-backdrop';
        bd.addEventListener('click', closeMobilePanel);
        app?.appendChild(bd);
      }
      bd.classList.add('visible');
    }

    function hideBackdrop() {
      const bd = document.querySelector('.dt-mobile-backdrop');
      if (bd) { bd.classList.remove('visible'); bd.remove(); }
    }

    S._closeMobileDrawer = closeMobilePanel;
    S._openMobileDrawer = openMobilePanel;

    /* ── Keyboard-aware modal adjustment ── */
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (!isMobile()) return;
        const modal = document.querySelector('.dt-kb-aware');
        if (!modal || modal.style.display === 'none') return;
        const kbHeight = Math.max(0, window.innerHeight - window.visualViewport.height);
        const contentEl = modal.querySelector('.dt-tool-modal__content') || modal.querySelector('.dt-rbg-panel');
        if (contentEl) contentEl.style.paddingBottom = kbHeight > 50 ? kbHeight + 'px' : '';
      });
    }

    /* Scroll indicators — for props drawer */
    function updateDrawerScrollHints() {
      if (!content || !drawer) return;
      const { scrollTop, scrollHeight, clientHeight } = content;
      drawer.classList.toggle('has-scroll-top', scrollTop > 8);
      drawer.classList.toggle('has-scroll-bottom', scrollTop + clientHeight < scrollHeight - 8);
    }
    content?.addEventListener('scroll', updateDrawerScrollHints, { passive: true });

    $$('.dt-mobile-tab').forEach(btn => btn.addEventListener('click', () => {
      cleanupDefaultText();
      const p = btn.dataset.panel;
      if (S.sel && S._mobileActivePanel !== p) { S.sel = null; S.iact = null; syncMobileSelection(); render(); }
      /* Dismiss monogram overlay before switching tabs */
      document.getElementById('dt-mono-overlay')?.remove();
      if (p === 'monogram') {
        if (S._mobileActivePanel === p) { closeMobilePanel(); return; }
        if (S._mobileActivePanel) closeMobilePanel();
        /* Build monogram content into the panel, then open via tool modal */
        const monoPanel = document.getElementById('dt-panel-monogram');
        if (monoPanel) {
          monoPanel.innerHTML = '';
          openMonogramPopup(monoPanel);
        }
        openMobilePanel(p);
        return;
      }
      if (S._mobileActivePanel === p) closeMobilePanel();
      else openMobilePanel(p);
    }));

  }

  /* ══════════════════════════════════════════════════════════════════
   * MOBILE EDIT BAR — replaces tab bar when a layer is selected
   * ══════════════════════════════════════════════════════════════════ */

  function layerTypeLabel(l) {
    if (!l) return '';
    if (l.type === 'text') return 'Text';
    if (l.isMonogram) return 'Monogram';
    if (l.isSvg) return 'Graphic';
    return 'Image';
  }

  function syncMobileSelection() {
    if (!isMobile()) return;
    const l = selLayer();

    if (l) {
      morphTabBar('actions');
      updateActionBar();
      forcePropsRebuild();
      enterDesignMode();
      /* Selection pulse — overlay a pulsing box on the selected layer */
      const sl = selLayer();
      if (sl) {
        const b = bbox(sl);
        const wrap = $('dt-canvas-wrap');
        if (wrap) {
          const pulse = document.createElement('div');
          pulse.className = 'dt-layer-pulse';
          pulse.style.cssText = `left:${b.x}px;top:${b.y}px;width:${b.w}px;height:${b.h}px`;
          wrap.appendChild(pulse);
          pulse.addEventListener('animationend', () => pulse.remove());
        }
      }
    } else {
      /* If we're in design mode but no layer is selected (undo, Escape,
         layers-panel deletion, etc.) — exit design mode properly.
         Mirrors the safety guard already in deleteLayer / cleanupDefaultText. */
      if (S.mode === 'design') exitDesignMode();
      morphTabBar('tools');
      if (S._mobileActivePanel === 'props') {
        S._closeMobileDrawer();
      }
    }
  }

  function enterDesignMode() {
    if (!isMobile()) return;
    const done = $('dt-done-btn');
    const dims = $('dt-dims-toggle');
    const snap = $('dt-snap-fab');
    if (S.mode === 'design') {
      if (done) done.style.display = 'flex';
      if (dims) dims.style.display = 'flex';
      if (snap) { snap.style.display = 'flex'; snap.classList.toggle('active', S.snapEnabled); }
      return;
    }
    S.mode = 'design';
    document.activeElement?.blur();
    /* Visual mode indicator */
    $('dt-canvas-area')?.classList.add('dt-editing');
    /* Always zoom to frame print area above the drawer.
       Uses stored naturals if available, fresh measurement if not. */
    requestZoomToPA(0);
    if (done) done.style.display = 'flex';
    if (dims) dims.style.display = 'flex';
    if (snap) { snap.style.display = 'flex'; snap.classList.toggle('active', S.snapEnabled); }
  }

  function exitDesignMode() {
    if (!isMobile()) return;
    S.mode = 'view';
    S.sel = null; S.iact = null; S._snapLines = [];
    closeInlineTextEdit();
    closeTextColorPop();
    closeMorePop();
    closeFontPop();
    closeStylePop();
    const done = $('dt-done-btn'); if (done) done.style.display = 'none';
    const dims = $('dt-dims-toggle'); if (dims) dims.style.display = 'none';
    const snapFab = $('dt-snap-fab'); if (snapFab) snapFab.style.display = 'none';
    /* Also turn off dims display when exiting design mode */
    S.showDims = false;
    if (dims) dims.classList.remove('active');
    /* Visual mode indicator */
    $('dt-canvas-area')?.classList.remove('dt-editing');
    /* Close any open mobile panel (props drawer OR tool modal) */
    if (S._mobileActivePanel && S._closeMobileDrawer) S._closeMobileDrawer();
    morphTabBar('tools');
    updateLayersPanel(); forcePropsRebuild(); render();
    /* Zoom/pan stays exactly where it is — user controls their own view */
  }

  /* ── Mobile edit mode zoom — centered on print area ── */
  /* Stored natural-space measurements (before any transform) */
  

  function applyEditModeZoom(smooth) {
    if (!isMobile()) return;
    const wrap = $('dt-canvas-wrap');
    const ca   = $('dt-canvas-area');
    if (!wrap || !ca || !mImg.offsetWidth) return;

    const pa = S.pa;
    if (!pa.w || !pa.h) return;

    /* Always measure fresh — layout can shift (swatches hide, hint
       disappears, drawer opens) making cached positions stale. */
    const savedTransform = wrap.style.transform;
    const savedOrigin = wrap.style.transformOrigin;
    const savedTransition = wrap.style.transition;
    /* Kill transition so getBoundingClientRect reads the true rest
       position, not a mid-animation frame (causes zoom shoot-off). */
    wrap.style.transition = 'none';
    wrap.style.transform = 'none';
    wrap.style.transformOrigin = '0 0';
    void wrap.offsetHeight;
    const freshWrapRect = wrap.getBoundingClientRect();
    const freshCaRect = ca.getBoundingClientRect();
    const wrapLeft = freshWrapRect.left;
    const wrapTop = freshWrapRect.top;
    const caTop = freshCaRect.top;
    /* Restore previous transform + transition immediately */
    wrap.style.transform = savedTransform;
    wrap.style.transformOrigin = savedOrigin;
    void wrap.offsetHeight;
    wrap.style.transition = savedTransition;

    const vpH = window.visualViewport?.height || window.innerHeight;
    const vpW = window.innerWidth;

    /* Measure actual drawer height (fit-to-content) or use full canvas */
    const drawer = $('dt-mobile-drawer');
    let bottomBound;
    const _fontPop = $('dt-font-pop');
    if (drawer?.classList.contains('open')) {
      /* Measure real drawer position — content-fit means height varies */
      bottomBound = drawer.getBoundingClientRect().top - 8;
    } else if (_fontPop && !_fontPop.hasAttribute('hidden') && _fontPop.classList.contains('open') && !document.getElementById('dt-size-pop')) {
      /* UX-Aug15 M5: keep the selected layer visible above the open font sheet */
      bottomBound = _fontPop.getBoundingClientRect().top - 8;
    } else {
      /* UX-Aug15 (spec §2): measure the real top of whichever bottom chrome is highest
         (tab/action bar, cart bar) instead of the 56/64 constants. Falls back to the
         constants if neither is measurable. */
      const _tabsEl = $('dt-mobile-tabs'), _cartEl = $('dt-cart-bar');
      let _chromeTop = Infinity;
      [_tabsEl, _cartEl].forEach(el => {
        if (!el || getComputedStyle(el).display === 'none') return;
        const r = el.getBoundingClientRect(); if (r.height > 0 && r.top > 0) _chromeTop = Math.min(_chromeTop, r.top);
      });
      bottomBound = (isFinite(_chromeTop) ? _chromeTop : (vpH - TAB_BAR_H - CART_BAR_H)) - 8;
    }

    let topBound = caTop + 8;
    { const _seg = $('dt-side-seg'); if (_seg && !_seg.hidden && _seg.offsetParent) topBound = Math.max(topBound, _seg.getBoundingClientRect().bottom + 6); } /* UX-Aug15 */
    const availH = Math.max(80, bottomBound - topBound);
    const availW = vpW - 20;

    const z = Math.min((availW * 0.9) / pa.w, (availH * 0.9) / pa.h, 2.5);

    const paCX = pa.x + pa.w / 2;
    const paCY = pa.y + pa.h / 2;
    const targetX = vpW / 2;
    const targetY = topBound + availH / 2;

    const panX = targetX - wrapLeft - paCX * z;
    const panY = targetY - wrapTop  - paCY * z;

    /* Smooth mode: add CSS transition for a polished re-zoom */
    if (smooth && wrap) {
      wrap.classList.add('dt-zoom-transition');
      setZoom(z, panX, panY, true);
      setTimeout(() => wrap.classList.remove('dt-zoom-transition'), 350);
    } else {
      setZoom(z, panX, panY, true);
    }
  }

  /* Keyboard resize handler — recalculate zoom smoothly */
  if (window.visualViewport) {
    let _lastVPH = window.visualViewport.height;
    window.visualViewport.addEventListener('resize', () => {
      if (!isMobile() || S.mode !== 'design') return;
      const newH = window.visualViewport.height;
      const delta = Math.abs(newH - _lastVPH);
      if (delta > 80) {
        _lastVPH = newH;
        /* Don't reset natural measurements — just recalculate with new viewport */
        requestZoomToPA(100);
      }
    });
  }

  /* Unified zoom request — debounced, smart forceReset */
  let _zoomTimer = null;
  function requestZoomToPA(delay, smooth) {
    clearTimeout(_zoomTimer);
    const d = delay || 0;
    if (d === 0) { applyEditModeZoom(smooth); return; }
    _zoomTimer = setTimeout(() => applyEditModeZoom(smooth), d);
  }

  /* ── Snap toggle feedback ── */
  function showSnapFeedback(msg) {
    let toast = document.querySelector('.dt-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'dt-toast';
      toast.innerHTML = '<span class="dt-toast__text"></span>';
      document.querySelector('.dt-body')?.appendChild(toast);
    }
    toast.querySelector('.dt-toast__text').textContent = msg;
    toast.style.background = '';
    toast.classList.add('visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('visible'), 1500);
  }

  /* ── Loading overlay for async operations ── */
  function showLoading(msg) {
    let ov = document.querySelector('.dt-loading-overlay');
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'dt-loading-overlay';
      ov.innerHTML = '<div class="dt-loading-spinner"></div><span class="dt-loading-msg"></span>';
      document.querySelector('.dt-body')?.appendChild(ov);
    }
    ov.querySelector('.dt-loading-msg').textContent = msg || 'Loading...';
    ov.style.display = 'flex';
  }
  function hideLoading() {
    const ov = document.querySelector('.dt-loading-overlay');
    if (ov) ov.style.display = 'none';
  }

  /* ── Error feedback for async operations ── */
  function showError(msg) {
    dtAnalytics.error('user_facing', msg);
    let toast = document.querySelector('.dt-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'dt-toast';
      toast.innerHTML = '<span class="dt-toast__text"></span>';
      document.querySelector('.dt-body')?.appendChild(toast);
    }
    toast.querySelector('.dt-toast__text').textContent = msg;
    toast.style.background = 'var(--dt-danger)';
    toast.classList.add('visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => { toast.classList.remove('visible'); toast.style.background = ''; }, 4000);
  }
  function confirmDelete(id) {
    const l = layers().find(x => x.id === id);
    if (!l) return;
    const ov = document.createElement('div');
    ov.className = 'dt-confirm-overlay';
    ov.innerHTML = '<div class="dt-confirm-dialog">' +
      '<div class="dt-confirm-dialog__title">Delete ' + (l.type === 'text' ? 'Text' : l.type === 'image' ? 'Image' : 'Element') + '?</div>' +
      '<div class="dt-confirm-dialog__msg">"' + (l.name || 'Element').slice(0, 30) + '" will be removed.</div>' +
      '<div class="dt-confirm-dialog__btns">' +
      '<button class="dt-confirm-dialog__btn dt-confirm-dialog__btn--cancel">Cancel</button>' +
      '<button class="dt-confirm-dialog__btn dt-confirm-dialog__btn--danger">Delete</button>' +
      '</div></div>';
    document.querySelector('.dt-body')?.appendChild(ov);
    const teardown = wireModalA11y(ov, { onEscape: function () { close(); } });
    function close() { teardown(); ov.remove(); }
    ov.querySelector('.dt-confirm-dialog__btn--cancel').addEventListener('click', close);
    ov.querySelector('.dt-confirm-dialog__btn--danger').addEventListener('click', () => { close(); deleteLayer(id); });
    ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
  }

  /* CRO #12 — promise-based styled confirm; replaces native confirm() */
  function dtConfirm(msg, opts) {
    opts = opts || {};
    return new Promise((resolve) => {
      const ov = document.createElement('div');
      ov.className = 'dt-confirm-overlay';
      ov.innerHTML = '<div class="dt-confirm-dialog" role="dialog" aria-modal="true">' +
        '<div class="dt-confirm-dialog__title">' + (opts.title || 'Just checking') + '</div>' +
        '<div class="dt-confirm-dialog__msg">' + msg + '</div>' +
        '<div class="dt-confirm-dialog__btns">' +
        '<button class="dt-confirm-dialog__btn dt-confirm-dialog__btn--cancel" type="button">' + (opts.cancelLabel || 'Cancel') + '</button>' +
        '<button class="dt-confirm-dialog__btn dt-confirm-dialog__btn--primary" type="button">' + (opts.okLabel || 'Continue') + '</button>' +
        '</div></div>';
      (document.querySelector('.dt-body') || document.body).appendChild(ov);
      const teardown = wireModalA11y(ov, { onEscape: function () { close(false); } });
      function close(val) { teardown(); ov.remove(); resolve(val); }
      ov.querySelector('.dt-confirm-dialog__btn--cancel').addEventListener('click', () => close(false));
      ov.querySelector('.dt-confirm-dialog__btn--primary').addEventListener('click', () => close(true));
      ov.addEventListener('click', (e) => { if (e.target === ov) close(false); });
    });
  }

  /* When entering engrave, a fixed-colour (recolorable:false) clipart can't be engraved.
     It's hidden from the engrave library, but one may have been placed in print first, then
     carried into engrave. Prompt the user and offer to remove it (kept = renders as a flat
     silhouette / prints in colour). */
  function maybeWarnEngraveFixedColor() {
    if (S.method !== 'engrave') return;
    const offenders = [];
    CFG.sides.forEach(side => (S.byView[side] || []).forEach(l => {
      if (l.visible !== false && l.isSvg && l.recolorable === false) offenders.push({ side, layer: l });
    }));
    if (!offenders.length) return;
    const one = offenders.length === 1;
    const names = offenders.map(o => '“' + (o.layer.name || 'graphic') + '”').slice(0, 3).join(', ');
    dtConfirm(
      (one ? names + ' has' : names + ' have') + ' fixed colors and can’t be engraved. Remove ' +
      (one ? 'it' : 'them') + ', or keep ' + (one ? 'it' : 'them') + ' (won’t engrave cleanly).',
      { title: 'Can’t engrave this graphic', okLabel: one ? 'Remove it' : 'Remove them', cancelLabel: 'Keep' }
    ).then(remove => {
      if (!remove) return;
      offenders.forEach(o => {
        const arr = S.byView[o.side]; const i = arr ? arr.indexOf(o.layer) : -1;
        if (i >= 0) arr.splice(i, 1);
        if (S.sel === o.layer.id) { S.sel = null; S.iact = null; }
      });
      updateLayersPanel(); forcePropsRebuild(); updateCartBtn(); render(); pushHistory();
      if (typeof updateLayerBadge === 'function') updateLayerBadge();
      /* SAFETY: removing fixed-color graphics on the engrave switch can leave
         nothing selected. If we were in mobile design mode, return to view mode
         so gestures stay live (same strand fixed in deleteLayer()). */
      if (isMobile() && S.mode === 'design' && !S.sel) exitDesignMode();
    });
  }

  /* CRO #9 — one-time engrave/print explainer (first manual toggle tap) */
  function showMethodExplainer() {
    document.getElementById('dt-method-explain')?.remove();
    const ov = document.createElement('div');
    ov.id = 'dt-method-explain';
    ov.className = 'dt-confirm-overlay';
    ov.innerHTML = '<div class="dt-confirm-dialog" role="dialog" aria-modal="true">' +
      '<div class="dt-confirm-dialog__title">Engrave or print?</div>' +
      '<div class="dt-confirm-dialog__msg" style="text-align:left">' +
        '<strong>Laser engrave</strong> etches your design into the surface \u2014 always a single color, never fades.<br><br>' +
        '<strong>Print</strong> reproduces your design in full color.<br><br>' +
        'Switch anytime \u2014 colors adjust automatically for the method.' +
      '</div>' +
      '<div class="dt-confirm-dialog__btns">' +
      '<button class="dt-confirm-dialog__btn dt-confirm-dialog__btn--primary" type="button" style="flex:1">Got it</button>' +
      '</div></div>';
    (document.querySelector('.dt-body') || document.body).appendChild(ov);
    const teardown = wireModalA11y(ov, { onEscape: function () { close(); } });
    function close() { teardown(); ov.remove(); }
    ov.querySelector('.dt-confirm-dialog__btn--primary').addEventListener('click', close);
    ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
  }

  /* ── Desktop quick zoom (floating panel) ──────────── */
  function renderProductIcon() {
    const iconCanvas = $('dt-qz-icon');
    if (!iconCanvas || !mImg.naturalWidth || !mImg.naturalHeight) return;
    const ctx2 = iconCanvas.getContext('2d');
    const size = 22;
    iconCanvas.width = size; iconCanvas.height = size;
    /* Crop to square maintaining aspect ratio */
    const nat = mImg.naturalWidth / mImg.naturalHeight;
    let sx = 0, sy = 0, sw = mImg.naturalWidth, sh = mImg.naturalHeight;
    if (nat > 1) { sw = mImg.naturalHeight; sx = (mImg.naturalWidth - sw) / 2; }
    else if (nat < 1) { sh = mImg.naturalWidth; sy = (mImg.naturalHeight - sh) / 2; }
    ctx2.clearRect(0, 0, size, size);
    ctx2.drawImage(mImg, sx, sy, sw, sh, 0, 0, size, size);
    /* Dark blend overlay to create silhouette effect */
    ctx2.globalCompositeOperation = 'source-atop';
    ctx2.fillStyle = 'rgba(30, 25, 20, 0.70)';
    ctx2.fillRect(0, 0, size, size);
    ctx2.globalCompositeOperation = 'source-over';
  }

  function zoomToProduct() {
    const ca = $('dt-canvas-area'); if (!ca || !mImg.offsetWidth) return;
    if (S._quickZoom === 'product') {
      S._quickZoom = null; setZoom(1, 0, 0, true); updateQuickZoomBtns(); return;
    }
    S._quickZoom = 'product';
    const fitW = ca.offsetWidth  / mImg.offsetWidth;
    const fitH = ca.offsetHeight / mImg.offsetHeight;
    const targetZoom = Math.max(1.15, Math.min(fitW, fitH) * 1.0);
    setZoom(targetZoom, 0, 0, true);
    updateQuickZoomBtns();
  }

  /* UX-Aug15 #6: pan that keeps the print-area centre at the centre of the visible canvas
     for a given zoom (desktop). Mirrors zoomToPrintArea's measurement block. */
  function panForPACenter(z) {
    try {
      const ca = $('dt-canvas-area'); if (!ca || !S.pa.w || !mWrap) return null;
      const caRect0 = ca.getBoundingClientRect();
      let visTop = caRect0.top, visBottom = caRect0.bottom;
      const _bar = $('dt-ctx-bar'), _row = $('dt-header-row2');
      const _topEl = (_bar && _bar.offsetParent) ? _bar : ((_row && _row.offsetParent) ? _row : null);
      if (_topEl) visTop = Math.max(visTop, _topEl.getBoundingClientRect().bottom + 8);
      const _qz = $('dt-quick-zoom'); if (_qz && getComputedStyle(_qz).display !== 'none') visBottom = Math.min(visBottom, _qz.getBoundingClientRect().top - 8);
      const visH = Math.max(100, visBottom - visTop);
      const savedTransition = mWrap.style.transition;
      mWrap.style.transition = 'none'; mWrap.classList.remove('dt-zoom-transition');
      const savedT = mWrap.style.transform, savedO = mWrap.style.transformOrigin;
      mWrap.style.transform = ''; mWrap.style.transformOrigin = ''; void mWrap.offsetHeight;
      const wrapRect = mWrap.getBoundingClientRect(), caRect = ca.getBoundingClientRect();
      mWrap.style.transform = savedT; mWrap.style.transformOrigin = savedO; void mWrap.offsetHeight;
      mWrap.style.transition = savedTransition;
      const _inset = parseFloat(getComputedStyle(ca).getPropertyValue('--dt-row-inset')) || 0;
      const caCX = caRect.left + _inset + (caRect.width - _inset) / 2, caCY = visTop + visH / 2;
      const paCX = S.pa.x + S.pa.w / 2, paCY = S.pa.y + S.pa.h / 2;
      return { x: caCX - wrapRect.left - paCX * z, y: caCY - wrapRect.top - paCY * z };
    } catch (_) { return null; }
  }

  /* UX-Aug15 #6: opts.initial = the desktop landing zoom (gentle: factor 0.55, cap 1.5, never
     below 1) — it does NOT arm the Fit toggle, so the first Fit click still zooms IN to 85%.
     Desktop bounds now exclude the in-flow canvas-top row + actions bar at the top and the
     bottom-centre zoom strip, so a zoomed print area never slides under either. */
  function zoomToPrintArea(_ev, opts) {
    opts = opts || {};
    const ca = $('dt-canvas-area'); if (!ca || !S.pa.w || !mImg.offsetWidth) return;
    if (!opts.initial && S._quickZoom === 'printarea') {
      S._quickZoom = null; setZoom(1, 0, 0, true); updateQuickZoomBtns(); return;
    }
    if (!opts.initial) S._quickZoom = 'printarea';
    const factor = opts.factor || 0.85, cap = opts.cap || 2.5;

    /* Measure actual visible canvas area */
    const caRect0 = ca.getBoundingClientRect();
    const tabBar  = $('dt-mobile-tabs');
    let visTop = caRect0.top;
    let visBottom = (isMobile() && tabBar) ? tabBar.getBoundingClientRect().top : caRect0.bottom;
    if (!isMobile()) {
      const _bar = $('dt-ctx-bar'), _row = $('dt-header-row2');
      const _topEl = (_bar && _bar.offsetParent) ? _bar : ((_row && _row.offsetParent) ? _row : null);
      if (_topEl) visTop = Math.max(visTop, _topEl.getBoundingClientRect().bottom + 8);
      const _qz = $('dt-quick-zoom');
      if (_qz && getComputedStyle(_qz).display !== 'none') visBottom = Math.min(visBottom, _qz.getBoundingClientRect().top - 8);
    }
    const drawerH = (isMobile() && S.sel) ? (window.visualViewport?.height || window.innerHeight) * DRAWER_RATIO : 0; /* --dt-drawer-h: 30vh */
    const visH = Math.max(100, visBottom - visTop - drawerH);

    let targetZoom = Math.min(
      (ca.offsetWidth * factor) / S.pa.w,
      (visH * factor) / S.pa.h,
      cap
    );
    let _ctr = null; /* landing view centres on the product, not the print area */
    if (opts.initial) {
      /* Fix (post-deploy): the first landing zoomed to 150% and cropped the product top/bottom.
         Land so the WHOLE PRODUCT (its opaque bounds inside the mockup PNG) fits the visible
         canvas; only whitespace is allowed off-screen. Falls back to 100% when the mockup is
         opaque/tainted. */
      const bb = mockupContentBounds();
      if (bb) {
        const prodW = bb.w * mImg.offsetWidth, prodH = bb.h * mImg.offsetHeight;
        targetZoom = Math.min(targetZoom, (visH * 0.96) / prodH, (ca.offsetWidth * 0.96) / prodW);
        _ctr = { x: (bb.x + bb.w / 2) * mImg.offsetWidth, y: (bb.y + bb.h / 2) * mImg.offsetHeight };
      } else {
        targetZoom = 1;
      }
      targetZoom = Math.max(1, targetZoom);
      if (targetZoom <= 1.001) { setZoom(1, 0, 0, true); updateQuickZoomBtns(); return; }
    }

    /* Temporarily reset transform to measure natural position.
       Kill transition to prevent mid-animation reads (same fix as applyEditModeZoom). */
    const savedTransition = mWrap.style.transition;
    mWrap.style.transition = 'none';
    mWrap.classList.remove('dt-zoom-transition');
    mWrap.style.transform = '';
    mWrap.style.transformOrigin = '';
    void mWrap.offsetHeight;

    const wrapRect = mWrap.getBoundingClientRect();
    const caRect   = ca.getBoundingClientRect();
    const _inset = isMobile() ? 0 : (parseFloat(getComputedStyle(ca).getPropertyValue('--dt-row-inset')) || 0);
    const caCX = caRect.left + _inset + (caRect.width - _inset) / 2;
    const caCY = (isMobile() ? caRect.top : visTop) + visH / 2;
    const paCX = _ctr ? _ctr.x : S.pa.x + S.pa.w / 2;
    const paCY = _ctr ? _ctr.y : S.pa.y + S.pa.h / 2;
    const panX = caCX - wrapRect.left - paCX * targetZoom;
    const panY = caCY - wrapRect.top  - paCY * targetZoom;

    /* Restore transition before applying zoom (setZoom may add smooth class) */
    mWrap.style.transition = savedTransition;

    setZoom(targetZoom, panX, panY, true);
    updateQuickZoomBtns();
  }
  /* Opaque bounds of the mockup PNG (normalised 0..1), sampled at 96px; cached per src.
     null when the image has no alpha (opaque photo) or is CORS-tainted. */
  function mockupContentBounds() {
    try {
      if (!mImg || !mImg.complete || !mImg.naturalWidth) return null;
      const key = mImg.currentSrc || mImg.src;
      if (mockupContentBounds._k === key) return mockupContentBounds._v;
      const G = 96, c = document.createElement('canvas'); c.width = G; c.height = G;
      const x = c.getContext('2d', { willReadFrequently: true });
      x.drawImage(mImg, 0, 0, G, G);
      const d = x.getImageData(0, 0, G, G).data;
      let minX = G, minY = G, maxX = -1, maxY = -1, opaque = 0;
      for (let j = 0; j < G; j++) for (let i = 0; i < G; i++) {
        const a = d[(j * G + i) * 4 + 3];
        if (a > 16) { opaque++; if (i < minX) minX = i; if (i > maxX) maxX = i; if (j < minY) minY = j; if (j > maxY) maxY = j; }
      }
      let v = null;
      if (maxX >= 0 && opaque < G * G * 0.97) v = { x: minX / G, y: minY / G, w: (maxX - minX + 1) / G, h: (maxY - minY + 1) / G };
      mockupContentBounds._k = key; mockupContentBounds._v = v;
      return v;
    } catch (_) { return null; }
  }

  function updateQuickZoomBtns() {
    const a = $('dt-qz-print');
    if (a) a.classList.toggle('active', S._quickZoom === 'printarea');
  }

  function setupQuickZoom() {
    $('dt-qz-print')?.addEventListener('click', zoomToPrintArea);
    /* UX-Aug15 #6: − / + steppers (desktop strip). Zoom about the current view centre. */
    if (!isMobile()) {
      const _qzs = $('dt-quick-zoom');
      if (_qzs && !$('dt-qz-minus')) {
        const mk = (id, label, glyph, dir) => {
          const b = document.createElement('button'); b.type = 'button'; b.className = 'dt-qz-btn dt-qz-btn--step'; b.id = id;
          b.title = label; b.setAttribute('aria-label', label);
          b.innerHTML = dir > 0
            ? '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8M6 2v8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>'
            : '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
          b.addEventListener('click', () => {
            const z = Math.max(0.5, Math.min(3, +(S.zoom * (dir > 0 ? 1.25 : 0.8)).toFixed(3)));
            S._quickZoom = null;
            if (z <= 1.001) { setZoom(1, 0, 0, true); }
            else { const p = panForPACenter(z); if (p) setZoom(z, p.x, p.y, true); else setZoom(z, undefined, undefined, true); }
            updateQuickZoomBtns();
          });
          return b;
        };
        const lvl = $('dt-qz-level');
        _qzs.insertBefore(mk('dt-qz-minus', 'Zoom out', '−', -1), lvl || _qzs.firstChild);
        if (lvl) lvl.insertAdjacentElement('afterend', mk('dt-qz-plus', 'Zoom in', '+', 1));
        else _qzs.appendChild(mk('dt-qz-plus', 'Zoom in', '+', 1));
      }
    }
    /* polish: on DESKTOP, dims lives INSIDE the zoom card — one control rail, not
       satellites. On MOBILE it must stay a standalone fixed FAB: the zoom card is
       display:none on mobile, so reparenting the ruler into it hid the measurement
       button entirely (a fixed child of a display:none parent doesn't render). */
    if (!isMobile()) { const _qz0 = $('dt-quick-zoom'), _d0 = $('dt-dims-toggle');
      if (_qz0 && _d0 && _d0.parentElement !== _qz0) _qz0.appendChild(_d0); }
    /* F3 — download a hi-res preview of the current side (share it!) */
    const _qz = $('dt-quick-zoom');
    if (_qz && !$('dt-qz-download')) {
      const b = document.createElement('button');
      b.className = 'dt-qz-btn'; b.id = 'dt-qz-download'; b.type = 'button';
      b.title = 'Download a preview image'; b.setAttribute('aria-label', 'Download a preview image');
      b.innerHTML = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2.5V11M9 11L5.5 7.5M9 11L12.5 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 12.5V14a1.5 1.5 0 001.5 1.5h9A1.5 1.5 0 0015 14v-1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      _qz.appendChild(b);
      b.addEventListener('click', async () => {
        if (b.disabled) return; b.disabled = true; /* N3: reentrancy latch */
        try {
        await ensureSideLayerPaintSources(S.view); /* v2.12.3 */
        const png = mockupPng(S.view, 2);
        if (!png) { toast('preview_empty'); return; }
        const a = document.createElement('a');
        a.href = png;
        a.download = (CFG.productHandle || 'design') + '-' + S.view + '-preview.png';
        a.click();
        toast('preview_saved');
        } finally { b.disabled = false; }
      });
    }
    /* Dimensions toggle */
    const dimsBtn = $('dt-dims-toggle');
    if (dimsBtn) {
      dimsBtn.addEventListener('click', () => {
        S.showDims = !S.showDims;
        dimsBtn.classList.toggle('active', S.showDims);
        render();
      });
    }
  }








  function setupMobileLayerActions() {
    /* Floating undo/redo pill — always available */
    $('dt-pill-undo')?.addEventListener('click', undo);
    $('dt-pill-redo')?.addEventListener('click', redo);

    /* Action tab buttons (in morphed tab bar) */
    $('dt-act-edit')?.addEventListener('click', () => {
      const l = selLayer(); if (!l) return;
      /* Locked template → the single template edit panel */
      if (l._tplLockId) { openTemplateEditPanel(); return; }
      /* Edit = edit the words for text; for monograms it opens the full editor
         (letters + style + colours). Styling for text lives under Font. */
      if (l.type === 'text') openInlineTextEdit(l);
      else if (l.isMonogram) openMonogramEditor(l);
    });
    $('dt-act-fit')?.addEventListener('click', () => {
      const l = selLayer();
      /* Templates fit as one locked group; a reframing photo (lock suspended) and
         everything else fit per-layer via fitLayerToPA. */
      if (l && l._tplLockId && !tplReframing(l)) fitTemplateGroup(l); else fitLayerToPA();
    });
    $('dt-act-center')?.addEventListener('click', centerLayerInPA);
    $('dt-act-removebg')?.addEventListener('click', function() {
      const btn = this, nm = btn.querySelector('span');
      removeBgForLayer((st, msg) => {
        if (st === 'busy') { if (nm) nm.textContent = 'Removing…'; btn.disabled = true; }
        else if (st === 'error') { btn.disabled = false; if (nm) nm.textContent = 'Failed'; setTimeout(() => { if (nm) nm.textContent = 'Remove BG'; }, 2500); }
      });
    });
    $('dt-act-rc')?.addEventListener('click', toggleRcPop);
    $('dt-act-more')?.addEventListener('click', toggleMorePop);
    $('dt-more-fwd')?.addEventListener('click', () => { reorder(1); closeMorePop(); });
    $('dt-more-bwd')?.addEventListener('click', () => { reorder(-1); closeMorePop(); });
    $('dt-more-front')?.addEventListener('click', () => { reorderExtreme(1); closeMorePop(); });
    $('dt-more-back')?.addEventListener('click', () => { reorderExtreme(-1); closeMorePop(); });
    $('dt-more-reframe')?.addEventListener('click', () => { S._toggleLink(); closeMorePop(); });
    $('dt-more-bg')?.addEventListener('click', () => { const l = selLayer(); closeMorePop(); openBackdropLibrary(l); });
    $('dt-act-font')?.addEventListener('click', () => {
      const l = selLayer(); if (!l) return;
      if (l.isMonogram) { openMonogramEditor(l); return; }
      toggleFontPop();
    });
    $('dt-act-style')?.addEventListener('click', () => {
      const l = selLayer(); if (!l || l.type !== 'text') return;
      toggleStylePop();
    });
    $('dt-act-color')?.addEventListener('click', () => {
      const l = selLayer();
      if (l && l.isPhotoPlaceholder) { replacePhotoPlaceholder(l); return; }
      if (l && l.isSvg) { if (S.method === 'engrave') openSvgEngraveEditor(l); else openSvgEditor(l); return; }
      S._monoColorSlot = 1;
      toggleTextColorPop();
    });
    /* Second swatch (duotone monograms) — same colour popover, targeting tone 2. */
    $('dt-act-color2')?.addEventListener('click', () => {
      S._monoColorSlot = 2;
      const pop = $('dt-textcolor-pop');
      if (pop && pop.hasAttribute('hidden')) toggleTextColorPop();
      else renderTextColorPop();
    });
    /* Swap the photo in a filled slot / upload, keeping its position + mask. */
    $('dt-act-swap')?.addEventListener('click', () => {
      const l = selLayer(); if (!l) return;
      if (isClipartLayer(l)) startClipartSwap(l); else swapPhoto(l);
    });
    /* Reframe — doubles as Done while reframing (active state). */
    $('dt-act-reframe')?.addEventListener('click', () => { S._toggleLink(); updateActionBar(); });
    $('dt-act-del')?.addEventListener('click', () => { if (S.sel) confirmDelete(S.sel); });
    /* Floating Snap toggle (mobile column, above Done) */
    $('dt-snap-fab')?.addEventListener('click', () => {
      toggleSnap();
      $('dt-snap-fab')?.classList.toggle('active', S.snapEnabled);
      showSnapFeedback(S.snapEnabled ? 'Snap ON — elements align to guides' : 'Snap OFF');
    });
    /* Custom colour from the text-colour popover */
    $('dt-textcolor-custom-input')?.addEventListener('input', function () { applyTextColor(this.value, true); });
    $('dt-textcolor-custom-input')?.addEventListener('change', function () { applyTextColor(this.value); });
    /* Close the colour popover on outside tap */
    document.addEventListener('click', e => {
      const pop = $('dt-textcolor-pop');
      if (!pop || pop.hasAttribute('hidden')) return;
      if (pop.contains(e.target) || e.target.closest('#dt-act-color') || e.target.closest('#dt-act-color2')) return;
      closeTextColorPop();
    });
    /* Close the More popover on outside tap */
    document.addEventListener('click', e => {
      const pop = $('dt-more-pop');
      if (!pop || pop.hasAttribute('hidden')) return;
      if (pop.contains(e.target) || e.target.closest('#dt-act-more')) return;
      closeMorePop();
    });
    /* Close the Font popover on outside tap */
    document.addEventListener('click', e => {
      const pop = $('dt-font-pop');
      if (!pop || pop.hasAttribute('hidden')) return;
      if (pop.contains(e.target) || e.target.closest('#dt-act-font')) return;
      closeFontPop();
    });
    /* Close the Style popover on outside tap */
    document.addEventListener('click', e => {
      const pop = $('dt-style-pop');
      if (!pop || pop.hasAttribute('hidden')) return;
      /* #dt-more-style = the "Format text" row inside the MORE popover. It opens the
         style pop via toggleStylePop; without this exception the SAME click bubbles here
         and immediately closes the just-opened pop (it flashes and vanishes on mobile). */
      if (pop.contains(e.target) || e.target.closest('#dt-act-style') || e.target.closest('#dt-more-style')) return;
      closeStylePop();
    });
    /* Close the Remove Color popover on outside tap */
    document.addEventListener('click', e => {
      const pop = $('dt-rc-pop');
      if (!pop || pop.hasAttribute('hidden')) return;
      if (pop.contains(e.target) || e.target.closest('#dt-act-rc')) return;
      closeRcPop();
    });
  }

  /* ── Tab bar morph: tools ↔ actions ── */
  let _savedActiveTab = null;
  function morphTabBar(mode) {
    const tabs = $('dt-mobile-tabs');
    if (!tabs) return;
    if (mode === 'actions') {
      /* Save currently active tool tab before morphing */
      const active = tabs.querySelector('.dt-mobile-tab.active');
      if (active) _savedActiveTab = active.dataset.panel;
      tabs.classList.add('dt-action-mode');
      /* UX-Aug15 (spec §5): the action bar takes the cart bar's slot — body.dt-ctx-active hides
         #dt-cart-bar and drops the bar to bottom:0 (CSS). Bars never stack; the Accept FAB
         that used to overlap Delete is retired (Done lives in the bar's leading slot). */
      document.body.classList.add('dt-ctx-active');
      setupMobileDoneSlot();
      if (!localStorage.getItem('dt_tip_tap_done')) {
        try { localStorage.setItem('dt_tip_tap_done', '1'); } catch (_) {}
        setTimeout(() => toast('Tap outside your design to finish.', null, 3200), 700);
      }
      setTimeout(syncBarHeights, 0);
    } else {
      tabs.classList.remove('dt-action-mode');
      document.body.classList.remove('dt-ctx-active');
      setTimeout(syncBarHeights, 0);
      /* Restore previously active tool tab */
      if (_savedActiveTab) {
        $$('.dt-mobile-tab').forEach(t => t.classList.toggle('active', t.dataset.panel === _savedActiveTab));
      }
    }
  }

  /* Close any open drawer when a layer is added */
  function autoClosePanelOnAdd() {
    if (!isMobile()) return;
    if (S._mobileActivePanel) S._closeMobileDrawer?.();
  }

  /* ── Mobile action bar: live font card + colour swatch ── */
  function colorName(hex) {
    if (!hex) return 'Color';
    const h = ('' + hex).toLowerCase();
    const map = {
      '#111111': 'Black', '#000000': 'Black', '#1a1a1a': 'Black',
      '#ffffff': 'White', '#fff': 'White',
      '#f47e46': 'Coral', '#e06a38': 'Coral', '#344e41': 'Forest',
      '#c0392b': 'Red', '#2980b9': 'Blue', '#8e44ad': 'Purple',
      '#d4a373': 'Tan', '#27ae60': 'Green', '#f39c12': 'Orange'
    };
    return map[h] || h.toUpperCase();
  }
  function layerColor(l) {
    if (!l) return '#111111';
    if (l.isMonogram) return l.monoColor || '#111111';
    if (l.type === 'image' && !l.isSvg) return l.tintColor || '#ff0000';
    return l.color || '#111111';
  }
  function setLayerColor(l, hex) {
    if (!l) return;
    if (l.isMonogram) { l.monoColor = hex; l._autoColor = false; } else { l.color = hex; l._autoColor = false; } /* UX-Aug20: clear auto for monograms too */
  }
  /* Find a custom (metaobject) monogram style object by its display name, across all
     collections. The returned object carries _fontFamilies / borderImage set up when the
     monogram editor first built its cards, so it can be re-rendered with addCustomMonogramLayer. */
  /* Data-URL images decode asynchronously; trimToContent() returns an Image
     whose src was JUST set. Committing it and rendering immediately draws an
     incomplete image — nothing visibly changes until some LATER repaint
     (the "change only appears after clicking off the layer" bug). Run the
     commit only once the bitmap is actually ready. */
  function whenImgReady(im, cb) {
    if (!im || (im.complete && im.naturalWidth)) { cb(); return; }
    im.addEventListener('load', cb, { once: true });
    im.addEventListener('error', cb, { once: true });
  }
  function findCustomMonoStyle(name) {
    if (!name) return null;
    var found = null;
    (CFG.monogramCollections || []).forEach(function (coll) {
      (coll.styles || []).forEach(function (ms) { if (ms && ms.name === name) found = ms; });
    });
    return found;
  }
  /* Re-render a placed monogram into a new colour (colour is baked into the PNG, so it
     needs a rebuild, not just a re-render). Handles BOTH built-in styles and custom
     (metaobject) styles — previously custom styles silently ignored colour changes
     because MONO_STYLES[-1] is undefined. */
  /* UX-Aug20: generation counter + debounce for monogram color rebuilds.
     The native color picker fires `input` on every pixel movement (~60/s).
     Each rebuild creates a canvas (512 or 2048 px), PNG-encodes, loads an Image,
     then runs trimToContent (full pixel scan). Without debounce + generation
     tracking the async callbacks pile up → progressive slowdown → freeze.
     rev 2: debounce raised to 150 ms (from 60) and _monoGen passed through to
     addCustomMonogramLayer so its heavy async chain (2048 px canvas, border load,
     PNG encode, pixel scan) also bails on stale generations. */
  var _monoRebuildGen = 0;
  var _monoRebuildTimer = null;
  function rebuildMonogramColor(l, hex, slot) {
    if (slot === 2) l.monoColor2 = hex; else l.monoColor = hex;
    var gen = ++_monoRebuildGen;
    if (_monoRebuildTimer) clearTimeout(_monoRebuildTimer);
    _monoRebuildTimer = setTimeout(function () {
      _monoRebuildTimer = null;
      if (_monoRebuildGen !== gen) return;  /* superseded during the debounce wait */
      _execMonoRebuild(l, gen);
    }, 150);  /* 150 ms — ~6 fps, avoids piling up heavy canvas work */
  }
  function _execMonoRebuild(l, gen) {
    /* Custom (metaobject) monogram — re-render through the custom renderer so the new
       colour actually bakes in. silent:true skips history/props churn during live drag.
       _monoGen lets addCustomMonogramLayer's async chain bail when superseded. */
    if (l.monoStyle === -1 && l.customMonoStyleName) {
      var ms = findCustomMonoStyle(l.customMonoStyleName);
      if (ms) { addCustomMonogramLayer(l.monoText, ms, l.monoColor, l, { silent: true, _monoGen: gen }); return; }
      render(); return;
    }
    var style = MONO_STYLES[l.monoStyle]; if (!style) { render(); return; }
    var off = document.createElement('canvas'); off.width = 512; off.height = 512;
    try { style.render(off.getContext('2d'), l.monoText, 512, 512, l.monoColor, l.monoColor2); } catch (_) {}
    var b64 = off.toDataURL('image/png');
    var img = new Image();
    img.onload = function () {
      if (_monoRebuildGen !== gen) return;  /* stale — a newer rebuild superseded us */
      var trimmed = trimToContent(img);
      if (!trimmed) { render(); return; }
      whenImgReady(trimmed.img, function () {
        if (_monoRebuildGen !== gen) return;  /* stale check again after async wait */
        var cx2 = l.x + l.w / 2, cy2 = l.y + l.h / 2;
        l.img = trimmed.img; l.b64 = trimmed.b64; l.ar = trimmed.w / trimmed.h;
        l.w = l.h * l.ar; l.x = cx2 - l.w / 2; l.y = cy2 - l.h / 2;
        render();
      });
    };
    img.src = b64;
  }
  /* Switch a placed monogram to a different built-in style is handled by the
     full editor (openMonogramEditor) now. */
  /* ── Reusable placement + background actions (panel + action bar) ── */
  function fitLayerToPA() {
    syncCanvas(); /* UX-Aug15 (spec §2): stale-PA guard — S.pa can lag a layout change */
    const l2 = selLayer(); if (!l2) return;
    const pa = S.pa;
    /* Photo with a background/mask: the VISIBLE bounds are the linked backdrop, not
       the inner photo. syncGroupedBackdrops keeps the backdrop side = photo.w * relScale,
       so size the photo such that the backdrop fills ~0.9 of the print area, centre the
       group, and re-lock so the backdrop tracks. (Fixes Fit not filling the print area
       for a photo that has a background.) */
    const _bd = backdropOfGroup(l2);
    if (_bd && !l2.isBackdrop) {
      /* #1: photo + background act as ONE unit — fit their visible UNION
         (selectionBounds: mask → medallion frame; non-mask → photo ∪ backdrop
         content) into the print area and centre the union, scaling the photo;
         the linked backdrop follows proportionally via syncGroupedBackdrops. */
      syncGroupedBackdrops();
      const gb = selectionBounds(l2);
      if (gb.w > 1 && gb.h > 1) {
        const f = Math.min((pa.w * 0.9) / gb.w, (pa.h * 0.9) / gb.h);
        const pcx = l2.centered ? l2.x : l2.x + l2.w / 2;
        const pcy = l2.centered ? l2.y : l2.y + l2.h / 2;
        const gcx = gb.x + gb.w / 2, gcy = gb.y + gb.h / 2;
        l2.w = Math.max(4, l2.w * f); l2.h = Math.max(4, l2.h * f);
        const tx = (pa.x + pa.w / 2) - (gcx - pcx) * f;
        const ty = (pa.y + pa.h / 2) - (gcy - pcy) * f;
        if (l2.centered) { l2.x = tx; l2.y = ty; }
        else { l2.x = tx - l2.w / 2; l2.y = ty - l2.h / 2; }
        syncGroupedBackdrops();
        pushHistory(); render();
        return;
      }
    }
    /* rev 12: custom / Google fonts may not be canvas-ready yet — if measureText
       produces a wrong fit the user sees "nothing happened." Load the face first
       (resolves instantly when already cached) then run the actual fit. */
    if (l2.type === 'text' && l2.font && document.fonts && document.fonts.load) {
      var _fitFont = fontStr(l2);
      document.fonts.load(_fitFont).catch(function () {}).then(function () { _doFitText(l2, pa); });
    } else if (l2.type === 'text') {
      _doFitText(l2, pa);
    } else {
      const ar = l2.ar || (l2.w / (l2.h || 1));
      const paAR = pa.w / pa.h;
      if (ar > paAR) { l2.w = pa.w * 0.9; l2.h = l2.w / ar; }
      else { l2.h = pa.h * 0.9; l2.w = l2.h * ar; }
      if (l2.centered) { l2.x = pa.x + pa.w / 2; l2.y = pa.y + pa.h / 2; }
      else { l2.x = pa.x + (pa.w - l2.w) / 2; l2.y = pa.y + (pa.h - l2.h) / 2; }
      pushHistory(); render();
    }
  }
  function _doFitText(l2, pa) {
    measureText(l2);
    if (l2.w > 0) {
      const targetW = pa.w * 0.9;
      const scaleW = targetW / l2.w;
      const scaleH = l2.h > 0 ? (pa.h * 0.85) / l2.h : Infinity;
      const scale = Math.min(scaleW, scaleH);
      l2.fontSize = Math.round(l2.fontSize * scale);
      l2.fontSize = Math.max(10, Math.min(120, l2.fontSize));
      measureText(l2);
    }
    l2.x = pa.x + pa.w / 2; l2.y = pa.y + pa.h / 2 - (l2._inkOffY || 0);
    pushHistory(); render();
  }
  /* Fit an ENTIRE template (all _tplLockId members) into the print area as one unit.
     Scales every member about the group's union centre, re-centres the union in the PA,
     and keeps masked photo frames + text re-fit baselines consistent. */
  function fitTemplateGroup(l) {
    syncCanvas(); /* UX-Aug15 (spec §2) */
    const pa = S.pa; if (!pa || !l || !l._tplLockId) return;
    const gb = selectionBounds(l);
    /* Degenerate/single-member union → behave like a normal single-layer fit. */
    if (!(gb.w > 1 && gb.h > 1)) { fitLayerToPA(); return; }
    const f = Math.min((pa.w * 0.9) / gb.w, (pa.h * 0.9) / gb.h);
    if (!(f > 0) || !isFinite(f)) return;
    /* Snapshot positions BEFORE mutating, then scale about the union centre. */
    const ls = { members: lockScaleMembers(l), cx: gb.x + gb.w / 2, cy: gb.y + gb.h / 2 };
    applyLockScale(ls, f);
    /* Keep each text layer's re-fit baseline in step so a later edit doesn't snap it
       back to the pre-fit size (fitTemplateTextLayer resets fontSize to _tplBaseFs). */
    ls.members.forEach(s => { const m = s.l; if (m.type === 'text' && m._tplBaseFs != null) m._tplBaseFs = Math.max(6, Math.round(m._tplBaseFs * f)); });
    if (typeof syncGroupedBackdrops === 'function') syncGroupedBackdrops();
    /* Re-centre the scaled union (scaling about the centroid can leave it slightly off
       for asymmetric groups) — translate every non-backdrop member; masks follow via sync. */
    const gb2 = selectionBounds(l);
    const dx = (pa.x + pa.w / 2) - (gb2.x + gb2.w / 2);
    const dy = (pa.y + pa.h / 2) - (gb2.y + gb2.h / 2);
    (S.byView[S.view] || []).forEach(m => { if (m._tplLockId === l._tplLockId && !m.isBackdrop) { m.x += dx; m.y += dy; } });
    if (typeof syncGroupedBackdrops === 'function') syncGroupedBackdrops();
    pushHistory(); render();
  }
  /* Centre an ENTIRE template in the print area WITHOUT resizing it — the move-only
     half of fitTemplateGroup, for when the customer likes the size and just wants it
     square in the area. Same translate-every-member rule (masks follow via sync). */
  function centerTemplateGroup(l) {
    syncCanvas(); /* UX-Aug15 (spec §2) */
    const pa = S.pa; if (!pa || !l || !l._tplLockId) return;
    const gb = selectionBounds(l);
    if (!(gb.w > 0 && gb.h > 0)) { centerLayerInPA(); return; }
    const dx = (pa.x + pa.w / 2) - (gb.x + gb.w / 2);
    const dy = (pa.y + pa.h / 2) - (gb.y + gb.h / 2);
    (S.byView[S.view] || []).forEach(m => { if (m._tplLockId === l._tplLockId && !m.isBackdrop) { m.x += dx; m.y += dy; } });
    if (typeof syncGroupedBackdrops === 'function') syncGroupedBackdrops();
    pushHistory(); render();
  }
  function centerLayerInPA() {
    syncCanvas(); /* UX-Aug15 (spec §2) */
    const l2 = selLayer(); if (!l2) return;
    if (l2.centered) { l2.x = S.pa.x + S.pa.w / 2; l2.y = S.pa.y + S.pa.h / 2 - ((l2.type === 'text' && l2._inkOffY) || 0); }
    else { l2.x = S.pa.x + (S.pa.w - l2.w) / 2; l2.y = S.pa.y + (S.pa.h - l2.h) / 2; }
    pushHistory(); render();
  }
  async function removeBgForLayer(onStatus) {
    const l2 = selLayer(); if (!l2?.isRasterUpload || l2._bgRemoved) return;
    onStatus && onStatus('busy');
    try {
      const resp = await fetch(l2.b64);
      const blob = await resp.blob();
      const file = new File([blob], 'image.png', { type: blob.type || 'image/png' });
      const rbgB64 = await callRemoveBg(file);
      pushHistory();
      const img = new Image();
      img.onload = () => {
        l2._bgRemoved = true;
        const trimmed = trimToContent(img);
        const useImg = trimmed ? trimmed.img : img;
        const useB64 = trimmed ? trimmed.b64 : rbgB64;
        const tw = trimmed ? trimmed.w : (img.naturalWidth || l2.w);
        const th = trimmed ? trimmed.h : (img.naturalHeight || l2.h);
        /* Resize the layer to the trimmed crop (same rule as the upload path) so the
           bounds hug actual pixels and Fit-to-Print-Area behaves. Keep the centre fixed. */
        const ocx = l2.centered ? l2.x : l2.x + l2.w / 2;
        const ocy = l2.centered ? l2.y : l2.y + l2.h / 2;
        const newH = Math.min(S.pa.h * 0.65, th * S.rscale), newW = newH * (tw / th);
        l2.img = useImg; l2.b64 = useB64; l2.w = newW; l2.h = newH; l2.ar = tw / th;
        if (l2.centered) { l2.x = ocx; l2.y = ocy; } else { l2.x = ocx - newW / 2; l2.y = ocy - newH / 2; }
        /* If the photo is already in a backdrop group (e.g. removing BG again after a
           Swap), keep the backdrop tracking the resized cutout and don't re-prompt the
           library — the background simply reappears behind the new cutout. */
        var _bdExisting = backdropOfGroup(l2);
        if (_bdExisting && _bdExisting._linked !== false && typeof captureLinkTransform === 'function') captureLinkTransform(_bdExisting, l2);
        const apply = () => { render(); forcePropsRebuild(); updateLayersPanel(); updateActionBar(); if (!_bdExisting) openBackdropLibrary(l2); };
        if (useImg.complete) apply(); else { useImg.onload = apply; useImg.onerror = apply; }
      };
      img.src = rbgB64;
    } catch (err) {
      onStatus && onStatus('error', err.message);
      /* Leave the photo intact and tell the user they can retry (the button resets),
         so a transient remove.bg failure is never a dead end. */
      toast('removebg_fail');
      setTimeout(() => { forcePropsRebuild(); updateActionBar(); }, 3000);
    }
  }
  /* ── Layer-type eligibility (single source of truth for all contextual UI) ──
     A "cutout" is a raster upload whose background is gone (removed or already
     transparent). Remove-BG is offered only on opaque raster uploads; the backdrop
     library only behind a cutout (a backdrop behind an opaque photo is invisible);
     reframe only when grouped with a backdrop. Text, monograms, SVG, clipart and
     backdrops are never eligible. */
  function isRasterUploadLayer(l) {
    return !!(l && l.type === 'image' && l.isRasterUpload && !l.isSvg && !l.isMonogram && !l.isClipart && !l.isBackdrop);
  }
  function eligRemoveBg(l) { return isRasterUploadLayer(l) && !l._bgRemoved && !l._engraveInverted; }
  function isCutoutLayer(l) { return isRasterUploadLayer(l) && !!l._bgRemoved; }
  function eligBackdrop(l) { return !!(l && l.type === 'image' && !l.isSvg && !l.isMonogram) && isCutoutLayer(l); }
  /* ══ v2.11.0 ENGRAVE INVERT (DESIGN-engrave-invert-option.md; Brandon Jul 30) ══
     "Inverted" = engrave the ENCLOSED PAPER inside the artwork's hull; the
     drawn lines stay product-dark. Offered in the edit panel like Remove BG.
     Qualification gates (measured on two real orders — see the design doc):
     closed hull (largest component >= 90%), enclosed paper >= 15% of hull,
     ink share of hull 5–75% (25–75 = tonal band -> Inverted default),
     inverted area <= 75% + >= 2% of hull. Face detection is a BOOSTER only
     (FaceDetector ships unflagged nowhere and is human-faces-only — it would
     miss the antelope that motivated this feature); the gates carry
     qualification. All analysis is client-side on a <= 400px sample. */

  /* Scanline flood from the canvas edges across non-ink pixels. Returns
     typed-array masks. `ink` = alpha > 128 AND luminance <= 230 (so both
     stripped-transparent and legacy opaque-white uploads analyze alike). */
  function _invMasks(img, MAXD) {
    const nw = img.naturalWidth || img.width, nh = img.naturalHeight || img.height;
    if (!nw || !nh) return null;
    const sc = Math.min(1, (MAXD || 400) / Math.max(nw, nh));
    const w = Math.max(2, Math.round(nw * sc)), h = Math.max(2, Math.round(nh * sc));
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.drawImage(img, 0, 0, w, h);
    let d;
    try { d = cx.getImageData(0, 0, w, h).data; } catch (_) { return null; }
    const n = w * h;
    const ink = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      if (d[o + 3] > 128) {
        const L = 0.299 * d[o] + 0.587 * d[o + 1] + 0.114 * d[o + 2];
        if (L <= 230) ink[i] = 1;
      }
    }
    /* flood from every edge pixel over non-ink */
    const edge = new Uint8Array(n);
    const stack = [];
    const push = i => { if (!edge[i] && !ink[i]) { edge[i] = 1; stack.push(i); } };
    for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
    for (let y = 1; y < h - 1; y++) { push(y * w); push(y * w + w - 1); }
    while (stack.length) {
      const i = stack.pop(); const x = i % w, y = (i / w) | 0;
      if (x > 0) push(i - 1);
      if (x < w - 1) push(i + 1);
      if (y > 0) push(i - w);
      if (y < h - 1) push(i + w);
    }
    c.width = 0;
    return { w, h, n, ink, edge };
  }

  /* Gate analysis, cached on the layer (invalidated when the bitmap changes). */
  function _invGatesForLayer(l) {
    if (!l || !l.img) return null;
    const sig = (l.b64 ? l.b64.length : 0) + ':' + (l.img.naturalWidth || 0);
    if (l._invGates && l._invGates.sig === sig) return l._invGates;
    const m = _invMasks(l.img, 400);
    if (!m) return (l._invGates = { sig, ok: false });
    const { w, h, n, ink, edge } = m;
    let inkN = 0, hullN = 0, enclosedN = 0;
    for (let i = 0; i < n; i++) {
      if (ink[i]) inkN++;
      if (!edge[i]) { hullN++; if (!ink[i]) enclosedN++; }
    }
    if (!hullN || !inkN) return (l._invGates = { sig, ok: false });
    /* largest hull component share — flood inside hull */
    const seen = new Uint8Array(n); let largest = 0;
    const stack = [];
    for (let s = 0; s < n; s++) {
      if (edge[s] || seen[s]) continue;
      let size = 0; seen[s] = 1; stack.push(s);
      while (stack.length) {
        const i = stack.pop(); size++;
        const x = i % w, y = (i / w) | 0;
        if (x > 0 && !edge[i - 1] && !seen[i - 1]) { seen[i - 1] = 1; stack.push(i - 1); }
        if (x < w - 1 && !edge[i + 1] && !seen[i + 1]) { seen[i + 1] = 1; stack.push(i + 1); }
        if (y > 0 && !edge[i - w] && !seen[i - w]) { seen[i - w] = 1; stack.push(i - w); }
        if (y < h - 1 && !edge[i + w] && !seen[i + w]) { seen[i + w] = 1; stack.push(i + w); }
      }
      if (size > largest) largest = size;
    }
    const hullShare = largest / hullN;
    const enclosedShare = enclosedN / hullN;
    const inkShare = inkN / hullN;
    const invShare = enclosedShare; /* inverted engrave area = enclosed paper */
    const ok = hullShare >= 0.9 && enclosedShare >= 0.15 &&
               inkShare >= 0.05 && inkShare <= 0.75 &&
               invShare >= 0.02 && invShare <= 0.75;
    /* tonal band -> Inverted should be the pre-selected card on dark products */
    const tonal = inkShare >= 0.25 && inkShare <= 0.75;
    return (l._invGates = { sig, ok, tonal, hullShare, enclosedShare, inkShare });
  }

  function eligEngraveInvert(l) {
    if (S.method !== 'engrave' || !isRasterUploadLayer(l) || l._tplPhoto || l._tplMask) return false;
    /* Applied state: offer Un-invert ONLY while the original is stashed.
       Review C1: the plate alone cannot reconstruct the artwork (strokes
       connected to the hull boundary are unrecoverable), so a restored or
       undone design keeps its inverted state rather than offering a
       destructive toggle. */
    if (l._engraveInverted) return !!l._invertOrigB64;
    const g = _invGatesForLayer(l);
    return !!(g && g.ok);
  }

  /* Full-res inversion: alpha of result = hull & ~ink, painted solid black.
     Involution within the hull, so "restore" without the stashed original
     recomputes correctly (colors are lost — engraving is monochrome anyway). */
  function _invBuildInverted(img) {
    const MAXD = 2400; /* byte-size guard: keeps b64 under the restore-drop cap */
    const nw = img.naturalWidth || img.width, nh = img.naturalHeight || img.height;
    const sc = Math.min(1, MAXD / Math.max(nw, nh));
    const w = Math.max(2, Math.round(nw * sc)), h = Math.max(2, Math.round(nh * sc));
    const m = _invMasks(img, Math.max(w, h));
    if (!m || m.w !== w || m.h !== h) return null;
    const out = document.createElement('canvas'); out.width = w; out.height = h;
    const ox = out.getContext('2d');
    const id = ox.createImageData(w, h); const d = id.data;
    for (let i = 0; i < m.n; i++) {
      if (!m.edge[i] && !m.ink[i]) { const o = i * 4; d[o] = 0; d[o + 1] = 0; d[o + 2] = 0; d[o + 3] = 255; }
    }
    ox.putImageData(id, 0, 0);
    let b64 = out.toDataURL('image/png');
    /* review M3: the serializeDesign restore path drops layers whose b64 is
       >= 300k chars — a high-entropy plate can exceed it at 2400px. Downscale
       until it fits (three tries floor ~1000px; flat black compresses well,
       this loop almost never runs). */
    let tries = 0;
    while (b64.length >= 290000 && tries < 3) {
      tries++;
      const k = 0.72;
      const sw2 = Math.max(600, Math.round(out.width * k)), sh2 = Math.max(600, Math.round(out.height * k));
      const c2 = document.createElement('canvas'); c2.width = sw2; c2.height = sh2;
      c2.getContext('2d').drawImage(out, 0, 0, sw2, sh2);
      out.width = sw2; out.height = sh2;
      out.getContext('2d').drawImage(c2, 0, 0);
      c2.width = 0;
      b64 = out.toDataURL('image/png');
    }
    out.width = 0;
    return b64;
  }

  function applyEngraveInvert(l, invB64) {
    if (!l || !l.img) return;
    const done = (b64) => {
      const img = new Image();
      img.onload = () => {
        if (!l._invertOrigB64) l._invertOrigB64 = l.b64; /* in-session only (stripped from history + saves) */
        l.img = img; l.b64 = b64; l._engraveInverted = true;
        delete l._invGates; /* derived cache — bitmap changed */
        pushHistory(); /* AFTER the mutation so redo works (review flaw #8) */
        render(); forcePropsRebuild(); updateLayersPanel(); updateActionBar();
        toast('Inverted — the background engraves and your lines stay dark.');
      };
      img.src = b64;
    };
    const b64 = invB64 || _invBuildInverted(l.img);
    if (b64) done(b64);
  }

  function revertEngraveInvert(l) {
    /* Review C1: NO recompute path — the plate cannot reconstruct strokes
       connected to the hull boundary. Un-invert exists only while the
       in-session original is stashed (eligEngraveInvert gates the UI the
       same way; this guard covers direct calls). */
    if (!l || !l._invertOrigB64) { toast('Original artwork isn’t available — re-upload the image to change the engrave style.'); return; }
    const b64 = l._invertOrigB64;
    const img = new Image();
    img.onload = () => {
      l.img = img; l.b64 = b64;
      l._engraveInverted = false; delete l._invertOrigB64; delete l._invGates;
      pushHistory();
      render(); forcePropsRebuild(); updateLayersPanel(); updateActionBar();
    };
    img.src = b64;
  }

  /* Two-card picker (Normal / Inverted), rendered against the product's
     print-area color with the steel mark tone — polarity is only judgeable
     in product context. Reuses the remove-bg overlay chrome. */
  function openEngraveInvertPicker() {
    const l = selLayer(); if (!l) return;
    if (l._engraveInverted) { revertEngraveInvert(l); return; }
    const invB64 = _invBuildInverted(l.img);
    if (!invB64) { toast('img_open_fail'); return; }

    /* product-context background: mean color of the mockup under the PA.
       Coordinate basis (review M1): S.pa is in mImg OFFSET space — map to
       CURRENT natural pixels with kx/ky computed at call time (the hi-res
       mockup upgrade swaps naturalWidth while rscale keeps the base basis,
       so pa/rscale would sample the wrong rect after upgrade). */
    let bg = '#2b2e30';
    try {
      const pc = document.createElement('canvas'); pc.width = 8; pc.height = 8;
      const px = pc.getContext('2d');
      const paSrc = S.pa;
      const kx = (mImg.naturalWidth || 1) / Math.max(1, mImg.offsetWidth);
      const ky = (mImg.naturalHeight || 1) / Math.max(1, mImg.offsetHeight);
      px.drawImage(mImg, paSrc.x * kx, paSrc.y * ky, paSrc.w * kx, paSrc.h * ky, 0, 0, 8, 8);
      const dd = px.getImageData(0, 0, 8, 8).data;
      let r = 0, g = 0, b = 0;
      for (let i = 0; i < 64; i++) { r += dd[i * 4]; g += dd[i * 4 + 1]; b += dd[i * 4 + 2]; }
      bg = 'rgb(' + Math.round(r / 64) + ',' + Math.round(g / 64) + ',' + Math.round(b / 64) + ')';
      pc.width = 0;
    } catch (_) {}

    const card = (srcB64, cb) => {
      const im = new Image();
      im.onload = () => {
        const CW = 340, ar = im.naturalWidth / Math.max(1, im.naturalHeight);
        const cw2 = CW, ch2 = Math.round(CW / Math.max(0.2, ar));
        const c = document.createElement('canvas'); c.width = cw2; c.height = ch2;
        const cx = c.getContext('2d');
        cx.fillStyle = bg; cx.fillRect(0, 0, cw2, ch2);
        /* steel mark tone where the art's alpha is (the engraved look) */
        const mkc = document.createElement('canvas'); mkc.width = cw2; mkc.height = ch2;
        const mx = mkc.getContext('2d');
        mx.fillStyle = '#e8e2d5'; mx.fillRect(0, 0, cw2, ch2);
        mx.globalCompositeOperation = 'destination-in';
        mx.drawImage(im, 0, 0, cw2, ch2);
        cx.drawImage(mkc, 0, 0); mkc.width = 0;
        cb(c.toDataURL('image/png')); c.width = 0;
      };
      im.src = srcB64;
    };

    card(l.b64, normThumb => card(invB64, invThumb => {
      document.getElementById('dt-rbg-overlay')?.remove();
      const g = _invGatesForLayer(l);
      let selected = (g && g.tonal) ? 'inv' : 'orig';
      const overlay = document.createElement('div');
      overlay.id = 'dt-rbg-overlay'; overlay.className = 'dt-rbg-overlay';
      overlay.innerHTML = '<div class="dt-rbg-panel">' +
        '<div class="dt-rbg-header"><span class="dt-rbg-title">Engrave Style</span><button class="dt-rbg-close" id="dt-inv-close">&times;</button></div>' +
        '<div class="dt-rbg-subtitle" id="dt-inv-sub">On dark products the background can engrave instead — your lines stay dark, like the original artwork.</div>' +
        '<div class="dt-rbg-choices">' +
        '<div class="dt-rbg-choice" id="dt-inv-orig"><div class="dt-rbg-thumb"><img src="' + normThumb + '" alt="Normal"></div><div class="dt-rbg-label">Normal</div><div class="dt-rbg-sub">lines engrave</div></div>' +
        '<div class="dt-rbg-choice" id="dt-inv-res"><div class="dt-rbg-thumb"><img src="' + invThumb + '" alt="Inverted"></div><div class="dt-rbg-label">Inverted</div><div class="dt-rbg-sub">background engraves</div></div>' +
        '</div>' +
        '<button class="dt-rbg-use-btn" id="dt-inv-use">Use Selected Style</button></div>';
      document.body.appendChild(overlay);
      const oc = overlay.querySelector('#dt-inv-orig'), ic = overlay.querySelector('#dt-inv-res');
      const sel = v => { selected = v; oc.classList.toggle('selected', v === 'orig'); ic.classList.toggle('selected', v === 'inv'); };
      sel(selected);
      oc.addEventListener('click', () => sel('orig'));
      ic.addEventListener('click', () => sel('inv'));
      const close = () => overlay.remove();
      overlay.querySelector('#dt-inv-close').addEventListener('click', close);
      overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
      overlay.querySelector('#dt-inv-use').addEventListener('click', () => {
        close();
        if (selected === 'inv') applyEngraveInvert(l, invB64);
      });
      /* FACE BOOSTER — progressive enhancement only. Where FaceDetector
         exists (no browser ships it unflagged; human faces only), a hit
         strengthens the default + labeling. Gates carry qualification. */
      try {
        if (window.FaceDetector && !l._engraveInverted) {
          const fd = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
          Promise.race([
            fd.detect(l.img),
            new Promise(res => setTimeout(() => res(null), 800)),
          ]).then(faces => {
            if (faces && faces.length && document.body.contains(overlay)) {
              sel('inv');
              const sub = overlay.querySelector('#dt-inv-sub');
              if (sub) sub.textContent = 'Portrait detected — inverted is recommended on dark products so the face reads correctly.';
            }
          }).catch(() => {});
        }
      } catch (_) {}
    }));
  }

  function backdropOfGroup(l) {
    if (!l || l.isBackdrop || !l._groupId) return null;
    return (S.byView[S.view] || []).find(b => b.isBackdrop && b._groupId === l._groupId) || null;
  }
  function eligReframe(l) { return !!backdropOfGroup(l); }

  /* ── Remove Color — extract dominant colours & pixel-level removal ──
     Pure client-side canvas operation (no API call). Works on raster
     uploads only. The processed image replaces l.img and l.b64 so every
     render path (preview, production SVG/PNG, bulk, save) automatically
     uses the colour-removed version. */

  function eligRemoveColor(l) {
    /* v2.11.0 review M2: never on an inverted plate — its dominant color is
       the plate itself; one tap would delete the whole engrave field. */
    return isRasterUploadLayer(l) && !l.isBackdrop && !l._engraveInverted;
  }

  /**
   * Extract 2–4 dominant colours from an image via simple colour
   * quantisation on a downscaled canvas.  Returns an array of hex
   * strings sorted by pixel count (most common first).
   */
  function extractDominantColors(img, maxColors) {
    maxColors = maxColors || 4;
    var SZ = 64;   /* sample at 64×64 — fast and sufficient */
    try {
    var c = document.createElement('canvas'); c.width = SZ; c.height = SZ;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, SZ, SZ);
    var d = ctx.getImageData(0, 0, SZ, SZ).data;
    var buckets = {};  /* 'r,g,b' → count */
    var Q = 24;        /* quantisation step — groups similar shades */
    for (var i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 128) continue;  /* skip mostly-transparent pixels */
      var r = Math.round(d[i] / Q) * Q;
      var g = Math.round(d[i + 1] / Q) * Q;
      var b = Math.round(d[i + 2] / Q) * Q;
      r = Math.min(255, r); g = Math.min(255, g); b = Math.min(255, b);
      var key = r + ',' + g + ',' + b;
      buckets[key] = (buckets[key] || 0) + 1;
    }
    /* Sort by frequency, take top N */
    var sorted = Object.keys(buckets).sort(function (a, b) { return buckets[b] - buckets[a]; });

    /* Merge near-duplicates: if a candidate is very close to an already-
       picked colour, skip it so we get visually distinct swatches. */
    var result = [];
    var DIST = 50;  /* minimum Euclidean distance between final swatches */
    for (var s = 0; s < sorted.length && result.length < maxColors; s++) {
      var parts = sorted[s].split(',');
      var cr = +parts[0], cg = +parts[1], cb = +parts[2];
      var tooClose = false;
      for (var j = 0; j < result.length; j++) {
        var ep = result[j];
        var dr = cr - ep[0], dg = cg - ep[1], db = cb - ep[2];
        if (Math.sqrt(dr * dr + dg * dg + db * db) < DIST) { tooClose = true; break; }
      }
      if (!tooClose) result.push([cr, cg, cb]);
    }

    return result.map(function (rgb) {
      return '#' + ((1 << 24) | (rgb[0] << 16) | (rgb[1] << 8) | rgb[2]).toString(16).slice(1);
    });
    } catch (_) { return []; } /* CORS-tainted or unloaded image — swatches hidden */
  }

  /**
   * Remove a colour from an image (pixel-level alpha pass).
   * Returns { img: Image, b64: string } with matching pixels made transparent.
   * Uses tolerance for anti-aliased edges (gradual alpha fade).
   */
  function removeColorFromImage(srcImg, srcB64, hexColor, tolerance) {
    tolerance = tolerance != null ? tolerance : 60;
    return new Promise(function (resolve, reject) {
      /* Parse target colour */
      var tr = parseInt(hexColor.slice(1, 3), 16);
      var tg = parseInt(hexColor.slice(3, 5), 16);
      var tb = parseInt(hexColor.slice(5, 7), 16);

      /* Work at capped resolution to avoid mobile OOM on huge photos.
         16 MP (4000×4000) ≈ 64 MB canvas buffer — safe on all devices.
         The output replaces the layer image, so this sets the max
         resolution of the colour-removed version. */
      var w = srcImg.naturalWidth || srcImg.width;
      var h = srcImg.naturalHeight || srcImg.height;
      if (!w || !h) { reject(new Error('Invalid image dimensions')); return; }
      var MAX_PX = 16e6;
      if (w * h > MAX_PX) {
        var scale = Math.sqrt(MAX_PX / (w * h));
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      var c = document.createElement('canvas'); c.width = w; c.height = h;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(srcImg, 0, 0, w, h);
      var id = ctx.getImageData(0, 0, w, h);
      var d = id.data;

      for (var i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) continue;   /* already transparent */
        var dr = d[i] - tr, dg = d[i + 1] - tg, db = d[i + 2] - tb;
        var dist = Math.sqrt(dr * dr + dg * dg + db * db);
        if (dist <= tolerance) {
          /* Exact or near-exact match → fully transparent */
          d[i + 3] = 0;
        } else if (dist <= tolerance * 1.6) {
          /* Anti-alias fringe → gradual fade for smooth edges */
          var fade = (dist - tolerance) / (tolerance * 0.6);
          d[i + 3] = Math.round(d[i + 3] * Math.min(1, fade));
        }
      }

      ctx.putImageData(id, 0, 0);
      var b64 = c.toDataURL('image/png');
      var img = new Image();
      img.onload = function () { resolve({ img: img, b64: b64 }); };
      img.onerror = function () { reject(new Error('Failed to create processed image')); };
      img.src = b64;
    });
  }

  /**
   * Apply colour removal to the currently selected layer.
   * Stores originals for undo/restore, replaces l.img & l.b64 so all
   * render paths automatically use the processed image.
   */
  async function applyRemoveColor(hexColor) {
    var l = selLayer();
    if (!l || !eligRemoveColor(l)) return;

    /* Stash originals (only on first removal — subsequent re-picks
       always start from the original). */
    if (!l._originalB64) {
      l._originalB64 = l.b64;
      l._originalImg = l.img;
      /* Fresh stash = the current image IS the reference frame; any stale
         trim record (e.g. surviving a saved-design restore) is invalid. */
      l._rcTrim = null;
    }

    pushHistory();

    try {
      var result = await removeColorFromImage(l._originalImg, l._originalB64, hexColor, 60);
      /* v2.9.11 (#3, Corpsman audit): tighten the layer box to the ink that
         SURVIVES removal (same trim as the upload flow). Remove Color
         previously shrank visible ink but left the box at the full frame —
         overstating the measured logo size and the Fit target.

         The remap is RELATIVE: l._rcTrim records which region of the
         processed full frame the CURRENT l.img/box pair shows (null = full
         frame). The new box is derived from the current box and the old→new
         trim rects, so user moves/resizes between re-picks are preserved and
         nothing ever compound-shrinks. Rotation/flip-safe: the drawn point
         map is C + R·F·(p − C), so the box center moves by R·F·(Δsub·s).
         _rcTrim frame-consistency is guarded by the stored fw/fh. */
      var fw = result.img.naturalWidth || result.img.width;
      var fh = result.img.naturalHeight || result.img.height;
      var trimmed = null;
      try { trimmed = trimToContent(result.img); } catch (te) { trimmed = null; }
      /* Wait for the trimmed image to decode BEFORE swapping it in — a
         production render fired inside the decode window would silently
         drop the layer. On decode failure fall back to the untrimmed
         processed image (box then maps the full frame). */
      if (trimmed) {
        var ok = await new Promise(function (res) {
          if (trimmed.img.complete && trimmed.img.naturalWidth) { res(true); return; }
          trimmed.img.onload = function () { res(true); };
          trimmed.img.onerror = function () { res(false); };
        });
        if (!ok) trimmed = null;
      }
      var tbNew = trimmed ? { x: trimmed.x, y: trimmed.y, w: trimmed.w, h: trimmed.h }
                          : { x: 0, y: 0, w: fw, h: fh };
      var tbPrev = (l._rcTrim && l._rcTrim.fw === fw && l._rcTrim.fh === fh)
                 ? l._rcTrim : { x: 0, y: 0, w: fw, h: fh };
      var sxr = l.w / tbPrev.w, syr = l.h / tbPrev.h;
      var nw2 = tbNew.w * sxr, nh2 = tbNew.h * syr;
      var vx = ((tbNew.x + tbNew.w / 2) - (tbPrev.x + tbPrev.w / 2)) * sxr;
      var vy = ((tbNew.y + tbNew.h / 2) - (tbPrev.y + tbPrev.h / 2)) * syr;
      if (l.flipH) vx = -vx;
      if (l.flipV) vy = -vy;
      var rr = (l.rotation || 0) * Math.PI / 180;
      var cx0 = l.centered ? l.x : l.x + l.w / 2;
      var cy0 = l.centered ? l.y : l.y + l.h / 2;
      var fcx = cx0 + vx * Math.cos(rr) - vy * Math.sin(rr);
      var fcy = cy0 + vx * Math.sin(rr) + vy * Math.cos(rr);
      l.w = nw2; l.h = nh2; l.ar = nh2 ? nw2 / nh2 : 1;
      if (l.centered) { l.x = fcx; l.y = fcy; }
      else { l.x = fcx - nw2 / 2; l.y = fcy - nh2 / 2; }
      if (trimmed) {
        l.img = trimmed.img;
        l.b64 = trimmed.b64;
        l._rcTrim = { x: tbNew.x, y: tbNew.y, w: tbNew.w, h: tbNew.h, fw: fw, fh: fh };
      } else {
        l.img = result.img;
        l.b64 = result.b64;
        l._rcTrim = null;
      }
      l.removedColor = hexColor;
      render();
      forcePropsRebuild();
      updateLayersPanel();
    } catch (err) {
      toast('color_remove_fail');
    }
  }

  /**
   * Undo colour removal — restore original image.
   */
  function undoRemoveColor() {
    var l = selLayer();
    if (!l || !l.removedColor) return;
    pushHistory();
    if (l._originalImg && l._originalB64) {
      l.img = l._originalImg;
      l.b64 = l._originalB64;
    }
    /* v2.9.11: un-tighten RELATIVELY — expand the current box from the trim
       rect back to the full frame, keeping any moves/resizes the user made
       since removal (same math as applyRemoveColor with tbNew = full frame). */
    if (l._rcTrim && l._rcTrim.w && l._rcTrim.h) {
      var tp = l._rcTrim;
      var usxr = l.w / tp.w, usyr = l.h / tp.h;
      var uvx = ((tp.fw / 2) - (tp.x + tp.w / 2)) * usxr;
      var uvy = ((tp.fh / 2) - (tp.y + tp.h / 2)) * usyr;
      if (l.flipH) uvx = -uvx;
      if (l.flipV) uvy = -uvy;
      var urr = (l.rotation || 0) * Math.PI / 180;
      var ucx = (l.centered ? l.x : l.x + l.w / 2) + uvx * Math.cos(urr) - uvy * Math.sin(urr);
      var ucy = (l.centered ? l.y : l.y + l.h / 2) + uvx * Math.sin(urr) + uvy * Math.cos(urr);
      var unw = tp.fw * usxr, unh = tp.fh * usyr;
      l.w = unw; l.h = unh; l.ar = unh ? unw / unh : 1;
      if (l.centered) { l.x = ucx; l.y = ucy; }
      else { l.x = ucx - unw / 2; l.y = ucy - unh / 2; }
    }
    l.removedColor = null;
    l._originalB64 = null;
    l._originalImg = null;
    l._rcTrim = null;
    render();
    forcePropsRebuild();
    updateLayersPanel();
  }

  /* Gate the contextual rows in the mobile "More" popover. Called on open AND on every
     selection change so the rows can never linger on a now-incompatible layer (e.g. text). */
  /* MORE popover — the "Format text" row (Brandon, Jul 28 2026). Format left the
     quick-action bar so a text layer reads Font · Color · Edit · Fit · More · Delete;
     it lives in MORE now. Built here rather than in design-tool.liquid so the move
     ships in ONE file — if the row is ever added to the snippet this finds it and
     does not duplicate. Inserted as the popover's FIRST child so the existing
     "Layer order" title still labels only the order rows beneath it. */
  function ensureMoreStyleRow() {
    const pop = $('dt-more-pop'); if (!pop) return null;
    let row = $('dt-more-style-row');
    if (row) return row;
    row = document.createElement('div');
    row.className = 'dt-more-pop__row';
    row.id = 'dt-more-style-row';
    row.setAttribute('hidden', '');
    row.innerHTML = '<button class="dt-more-pop__btn" id="dt-more-style" type="button">' +
      '<svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M3 6h6M14 6h3M3 14h3M11 14h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="11" cy="6" r="2.2" stroke="currentColor" stroke-width="1.5"/><circle cx="8" cy="14" r="2.2" stroke="currentColor" stroke-width="1.5"/></svg>' +
      'Format text</button>';
    pop.insertBefore(row, pop.firstChild);
    /* toggleStylePop already closes MORE (and the other popovers) before opening,
       so one call is the whole handoff — same shape as the Background row. */
    row.querySelector('#dt-more-style').addEventListener('click', toggleStylePop);
    return row;
  }

  function gateMoreRows(l) {
    const maskRow = $('dt-more-mask-row'), lbl = $('dt-more-reframe-lbl'), bgRow = $('dt-more-bg-row');
    /* Format — text layers only. */
    const styleRow = ensureMoreStyleRow();
    if (styleRow) {
      if (l && l.type === 'text') styleRow.removeAttribute('hidden');
      else styleRow.setAttribute('hidden', '');
    }
    /* Photo-like layers only — text, monograms, clipart and placeholders must
       never show Reframe/Background even if they carry a stray _groupId. */
    const photoLike = !!(l && l.type === 'image' && !l.isSvg && !l.isMonogram && !l.isPhotoPlaceholder);
    const bd = photoLike ? backdropOfGroup(l) : null;
    /* #4: if the quick action bar already shows Reframe for this layer, don't
       duplicate it in More (same visibility rule as updateActionBar's reframeBtn). */
    const _inQuickBar = !!(l && (l.isRasterUpload || l._tplPhoto || l._tplMask) && !l.isPhotoPlaceholder && bd);
    if (maskRow) {
      if (bd && !_inQuickBar) { maskRow.removeAttribute('hidden'); if (lbl) lbl.textContent = bd._linked === false ? 'Done reframing' : 'Reframe photo'; }
      else maskRow.setAttribute('hidden', '');
    }
    if (bgRow) {
      if (photoLike && eligBackdrop(l)) bgRow.removeAttribute('hidden'); else bgRow.setAttribute('hidden', '');
    }
  }

  function updateActionBar() {
    if (!isMobile()) return;
    const l = selLayer(); if (!l) return;
    /* Changing selection closes any open transient popover, so a font/colour/style menu
       can never linger on a now-incompatible layer. (Deselecting closes them already.) */
    if (S.sel !== S._lastBarSel) {
      closeFontPop(); closeTextColorPop(); closeStylePop(); closeMorePop(); closeRcPop();
      S._lastBarSel = S.sel;
    }
    const fontBtn = $('dt-act-font'), colorBtn = $('dt-act-color');
    const isText = l.type === 'text';
    const isMono = !!l.isMonogram;
    const isSvg = !!l.isSvg;
    const isRaster = l.type === 'image' && !isSvg && !isMono;
    const isImageType = isMono || isSvg || isRaster;
    /* Edit = edit words (text only). Fit/Center for the image-like types.
       Remove BG for raster uploads. More (layer order) for everything. */
    const editBtn = $('dt-act-edit'), fitBtn = $('dt-act-fit'),
          centerBtn = $('dt-act-center'), rbgBtn = $('dt-act-removebg'), moreBtn = $('dt-act-more'),
          styleBtn = $('dt-act-style'), swapBtn = $('dt-act-swap'), reframeBtn = $('dt-act-reframe');
    const isPlaceholder = !!l.isPhotoPlaceholder;
    /* An actual photo image (uploaded, or a filled template slot) — never a placeholder,
       text, monogram, or graphic. Drives Swap. */
    const isPhoto = (l.isRasterUpload || l._tplPhoto || l._tplMask) && !isPlaceholder;
    const isClip = isClipartLayer(l);
    const bdReframe = isPhoto ? backdropOfGroup(l) : null; /* photo has a mask/backdrop → reframeable; never text/mono/clipart */
    const reframing = !!(bdReframe && bdReframe._linked === false);
    /* TEMPLATE LOCK: the template edits through ONE panel — action bar offers
       just Edit (opens it) and Delete (removes the whole template). */
    if (l._tplLockId && !reframing) {
      if (editBtn) editBtn.style.display = '';
      /* Fit to Print Area is offered for templates too — it fits the WHOLE locked
         group as one unit (the #dt-act-fit handler routes to fitTemplateGroup). */
      if (fitBtn) fitBtn.style.display = '';
      const delBtn = $('dt-act-del'); if (delBtn) delBtn.style.display = '';
      [fontBtn, colorBtn, $('dt-act-color2'), styleBtn, centerBtn, swapBtn, reframeBtn, rbgBtn, $('dt-act-rc'), $('dt-act-invert'), moreBtn].forEach(b => { if (b) b.style.display = 'none'; });
      return;
    }
    if (editBtn) editBtn.style.display = (isText || isMono) ? '' : 'none';
    /* Format moved into the MORE popover (Brandon, Jul 28 2026) so the text bar is
       Font · Color · Edit · Fit · More · Delete. The button stays in the DOM — the
       outside-click guard and its handler still reference it — it is just never
       shown; gateMoreRows surfaces #dt-more-style-row for text instead. */
    if (styleBtn) styleBtn.style.display = 'none';
    /* FIT + DELETE ALWAYS SHOW, for every type of design layer (Brandon,
       Jul 28 2026) — they are the two actions no panel offers on mobile any
       more, so the bar is the single home for both and may never withhold
       them. The old type test (isText || isImageType || isPlaceholder) covered
       everything selectable in practice, but "in practice" is not "always". */
    if (fitBtn) fitBtn.style.display = '';
    const _delBtn = $('dt-act-del'); if (_delBtn) _delBtn.style.display = '';
    /* Center is situational (Fit already sizes + centers) — keep the bar to the
       basics; Center still lives in the panel placement row. */
    if (centerBtn) centerBtn.style.display = 'none';
    /* Swap — photos swap via file picker; graphics swap via the clipart library. */
    if (swapBtn) swapBtn.style.display = (isPhoto || isClip) ? '' : 'none';
    /* Reframe — only photos with a mask/backdrop. Doubles as the Done control while
       reframing (active state) so it's obvious, and Swap stays reachable beside it. */
    if (reframeBtn) {
      reframeBtn.style.display = bdReframe ? '' : 'none';
      reframeBtn.classList.toggle('dt-action-tab--active', reframing);
      const _rlbl = reframeBtn.querySelector('span'); if (_rlbl) _rlbl.textContent = reframing ? 'Done' : 'Reframe';
    }
    /* Remove BG — raster uploads only (excludes template photos, text, monograms, graphics). */
    if (rbgBtn) rbgBtn.style.display = eligRemoveBg(l) ? '' : 'none';
    /* Remove Color — same eligibility as the desktop sidebar section. */
    const rcBtn = $('dt-act-rc');
    if (rcBtn) rcBtn.style.display = eligRemoveColor(l) ? '' : 'none';
    /* v2.11.0 Engrave Invert — gated raster uploads in engrave mode; the tab
       doubles as the restore control once applied. */
    const invTab = $('dt-act-invert');
    if (invTab) {
      const invOk = eligEngraveInvert(l);
      invTab.style.display = invOk ? '' : 'none';
      if (invOk) {
        invTab.classList.toggle('dt-action-tab--active', !!l._engraveInverted);
        const _il = invTab.querySelector('span'); if (_il) _il.textContent = l._engraveInverted ? 'Un-invert' : 'Invert';
      }
    }
    if (moreBtn) {
      /* MORE hides when its panel would be empty (Brandon, Jul 17 2026):
         the layer-order rows need 2+ layers — same dt-has-layers gate as
         the Layers tab (CSS hides them below that) — and the contextual
         Reframe/Background rows are per-layer. A lone photo with a
         backdrop keeps MORE for those rows; a lone text/graphic loses it. */
      gateMoreRows(l);
      const _hasOrder = layers().filter(x => !x.isBackdrop).length >= 2;
      const _hasCtx = !!(document.querySelector('#dt-more-mask-row:not([hidden])')
                      || document.querySelector('#dt-more-bg-row:not([hidden])')
                      /* Format now lives in here, so a lone text layer must keep MORE. */
                      || document.querySelector('#dt-more-style-row:not([hidden])'));
      moreBtn.style.display = (_hasOrder || _hasCtx) ? '' : 'none';
      if (!_hasOrder && !_hasCtx) closeMorePop();
    }
    if (fontBtn) {
      /* Monograms edit letters + style + colours through the full editor (the Edit
         button), so the separate Font slot is text-only to avoid two buttons that
         do the same thing. */
      fontBtn.style.display = isText ? '' : 'none';
      const prev = $('dt-act-font-preview'), nm = $('dt-act-font-name');
      if (isText) {
        const cur = getAllFonts().find(f => l.font === f.fontValue || l.font === f.name);
        if (prev) { prev.style.fontFamily = cur ? cur.css : ''; prev.textContent = 'Aa'; }
        if (nm) nm.textContent = cur ? cur.name : (l.font || 'Font');
      }
    }
    const _stl = (isMono && l.monoStyle >= 0) ? MONO_STYLES[l.monoStyle] : null;
    const isDuo = !!(_stl && _stl.twoTone) && S.method !== 'engrave';
    if (colorBtn) {
      /* rev 10 (Brandon, Aug 17): custom (metaobject) monograms — monoStyle === -1 — were
         excluded here, so single-colour custom monograms showed NO Color swatch on the mobile
         quick-actions bar (recolour was buried in Edit). The whole recolour path already
         supports them (applyTextColor → rebuildMonogramColor handles monoStyle === -1), so
         just show the swatch for every monogram. Two-tone still adds Color 2 below; custom
         styles are single-colour so they get one Color swatch. Engrave still hides it. */
      const monoColorOk = isMono;
      /* Tint shows for non-upload rasters, but NOT template photos — their bar is
         Fit / Swap / Reframe / Delete only (a customer's photo isn't a tint target). */
      /* Engrave: color is meaningless EXCEPT for SVGs, where the same button
         opens the per-element engrave/skip editor — keep it for those. */
      const showColor = (isText || monoColorOk || isSvg || (isRaster && !l.isRasterUpload && !l._tplPhoto && !l._tplMask)) && (S.method !== 'engrave' || isSvg);
      colorBtn.style.display = showColor ? '' : 'none';
      const chip = $('dt-act-color-chip'), cn = $('dt-act-color-name');
      colorBtn.dataset.mode = isSvg ? 'svg' : 'color';
      if (isSvg) {
        if (chip) { chip.style.background = ''; chip.classList.add('dt-act-color-chip--svg'); }
        /* A photo placeholder reuses this button to open the file picker (see the
           dt-act-color handler), so label it for that action on mobile, not "Edit
           Colors" — the add-photo step is the primary CRO action for the slot. */
        if (cn) cn.textContent = l.isPhotoPlaceholder ? 'Add Photo' : (S.method === 'engrave' ? 'Engraving' : 'Edit Colors');
      } else {
        const col = layerColor(l);
        if (chip) { chip.classList.remove('dt-act-color-chip--svg'); chip.style.background = (isRaster && !l.tintColor) ? '' : col; }
        if (cn) cn.textContent = isDuo ? 'Color 1' : ((isRaster && !l.tintColor) ? 'Tint' : colorName(col));
      }
    }
    /* Second swatch for two-tone (duotone) monograms — always shown beside Color 1. */
    const color2Btn = $('dt-act-color2');
    if (color2Btn) {
      color2Btn.style.display = isDuo ? '' : 'none';
      if (isDuo) { const ch2 = $('dt-act-color2-chip'); if (ch2) { ch2.classList.remove('dt-act-color-chip--svg'); ch2.style.background = l.monoColor2 || '#888888'; } }
    }
    /* Keep the More popover's contextual rows correct if it's open during a selection change */
    if (!$('dt-more-pop')?.hasAttribute('hidden')) gateMoreRows(l);
  }
  /* Text colour popover (opened by the action-bar swatch) */
  function applyTextColor(hex, live) {
    const l = selLayer(); if (!l) return;
    const clear = (hex === null || hex === 'none');
    if (l.isMonogram) {
      rebuildMonogramColor(l, hex, S._monoColorSlot || 1);
    } else if (l.type === 'image' && !l.isSvg) {
      l.tintColor = clear ? null : hex; render();
    } else {
      l.color = hex; l._autoColor = false; render();
    }
    /* live = mid-drag (picker spectrum / native dialog scrub): render only.
       History + rebuild land once on the final 'change' — a 2s hue scrub
       was writing dozens of history entries + full-design autosaves. */
    if (!live) { pushHistory(); updateActionBar(); forcePropsRebuild(); }
    const customChip = $('dt-textcolor-custom-chip'); if (customChip) customChip.style.background = clear ? 'transparent' : hex;
  }
  function renderTextColorPop() {
    const wrap = $('dt-textcolor-presets'); if (!wrap) return;
    const l = selLayer();
    const isRaster = l && l.type === 'image' && !l.isSvg && !l.isMonogram;
    const cur = (layerColor(l) || '').toLowerCase();
    const presets = CFG.colorPresets || ['#111111','#ffffff','#f47e46','#344e41','#c0392b','#2980b9','#8e44ad','#d4a373','#27ae60','#f39c12'];
    /* Raster tint can be cleared back to the photo's original colors. */
    let chips = '';
    if (isRaster) {
      const noneSel = (!l.tintColor) ? ' selected' : '';
      chips += '<div class="dt-color-preset dt-color-preset--none' + noneSel + '" data-preset="none" title="Original colors"></div>';
    }
    chips += presets.map(c => '<div class="dt-color-preset' + (cur && cur === c.toLowerCase() ? ' selected' : '') + '" data-preset="' + c + '" style="background:' + c + (c === '#ffffff' ? ';border-color:var(--dt-border)' : '') + '"></div>').join('');
    wrap.innerHTML = chips;
    const customChip = $('dt-textcolor-custom-chip'); if (customChip) customChip.style.background = (isRaster && !l.tintColor) ? 'transparent' : (cur || '#111111');
    const customInput = $('dt-textcolor-custom-input'); if (customInput) customInput.value = /^#[0-9a-f]{6}$/i.test(cur) ? cur : '#111111';
    wrap.querySelectorAll('.dt-color-preset').forEach(chip => chip.addEventListener('click', () => {
      applyTextColor(chip.dataset.preset);
      wrap.querySelectorAll('.dt-color-preset').forEach(c => c.classList.toggle('selected', c.dataset.preset === chip.dataset.preset));
    }));
  }
  /* Keep the floating column (ruler/snap/done) clear of the colour popover. */
  function setFloatingColumnVisible(show) {
    if (!isMobile() || S.mode !== 'design') return;
    ['dt-done-btn', 'dt-snap-fab', 'dt-dims-toggle'].forEach(id => {
      const el = $(id); if (!el) return;
      /* UX-Aug15: Done lives in the action bar now — never collapse its slot for a popover */
      if (id === 'dt-done-btn' && el.classList.contains('dt-action-tab--done')) return;
      el.style.display = show ? 'flex' : 'none';
    });
  }
  /* UX-Aug15 (spec §5 / A5 / A6): move the existing #dt-done-btn (all wiring stays on the node:
     enterDesignMode/exitDesignMode display toggles, stranded-mode guards, the click →
     exitDesignMode listener) into the action bar's LEADING slot as an outlined white pill.
     Idempotent; mobile only. */
  function setupMobileDoneSlot() {
    try {
      if (!isMobile()) return;
      const done = $('dt-done-btn'), tabs = $('dt-mobile-tabs');
      if (!done || !tabs || done.parentNode === tabs) return;
      done.classList.add('dt-action-tab', 'dt-action-tab--done');
      done.setAttribute('aria-label', 'Done editing');
      done.innerHTML = '<span class="dt-done-check" aria-hidden="true">✓</span><span>Done</span>';
      tabs.insertBefore(done, tabs.firstChild);
    } catch (e) { DEBUG && console.warn('[dt] setupMobileDoneSlot:', e && e.message); }
  }
  function closeTextColorPop() {
    const pop = $('dt-textcolor-pop');
    if (!pop || pop.hasAttribute('hidden')) return;
    pop.classList.remove('open');
    setFloatingColumnVisible(true);
    pop.addEventListener('transitionend', () => pop.setAttribute('hidden', ''), { once: true });
  }
  function closeMorePop() {
    const pop = $('dt-more-pop');
    if (!pop || pop.hasAttribute('hidden')) return;
    pop.classList.remove('open');
    setFloatingColumnVisible(true);
    pop.addEventListener('transitionend', () => pop.setAttribute('hidden', ''), { once: true });
  }
  /* ── Remove Color popover (mobile) — shows dominant-colour swatches
       so the user can tap one to make it transparent. Same logic as the
       desktop sidebar's dt-rc-section, surfaced as a bottom-sheet. */
  function closeRcPop() {
    const pop = $('dt-rc-pop');
    if (!pop || pop.hasAttribute('hidden')) return;
    pop.classList.remove('open');
    setFloatingColumnVisible(true);
    pop.addEventListener('transitionend', () => pop.setAttribute('hidden', ''), { once: true });
  }
  function renderRcPop() {
    const body = $('dt-rc-pop-body'); if (!body) return;
    const l = selLayer(); if (!l || !eligRemoveColor(l)) return;
    const colors = extractDominantColors(l._originalImg || l.img);
    if (!colors.length) { body.innerHTML = '<div style="font-size:var(--dt-text-sm);color:var(--dt-text-2);padding:6px 0">No colors detected yet</div>'; return; }
    let html = '<div class="dt-rc-swatches" style="justify-content:center">';
    colors.forEach(function (hex) {
      var sel = l.removedColor && l.removedColor.toLowerCase() === hex.toLowerCase() ? ' dt-rc-swatch--active' : '';
      html += '<button class="dt-rc-swatch' + sel + '" data-rc="' + hex + '" style="background:' + hex + '" title="Remove ' + hex + '" aria-label="Remove color ' + hex + '"></button>';
    });
    html += '</div>';
    if (l.removedColor && l._originalB64) {
      html += '<button class="dt-rc-undo" id="dt-rc-pop-undo" style="margin-top:8px">Restore original colors</button>';
    }
    body.innerHTML = html;
    /* Wire swatch clicks */
    body.querySelectorAll('.dt-rc-swatch').forEach(function (sw) {
      sw.addEventListener('click', function () {
        var hex = this.dataset.rc; if (!hex) return;
        applyRemoveColor(hex);
        closeRcPop();
      });
    });
    var undoBtn = body.querySelector('#dt-rc-pop-undo');
    if (undoBtn) undoBtn.addEventListener('click', function () { undoRemoveColor(); closeRcPop(); });
  }
  function toggleRcPop() {
    const pop = $('dt-rc-pop'); if (!pop) return;
    closeTextColorPop(); closeMorePop(); closeFontPop(); closeStylePop();
    if (pop.hasAttribute('hidden')) {
      renderRcPop();
      pop.removeAttribute('hidden');
      setFloatingColumnVisible(false);
      requestAnimationFrame(() => pop.classList.add('open'));
    } else {
      closeRcPop();
    }
  }
  function toggleMorePop() {
    const pop = $('dt-more-pop'); if (!pop) return;
    closeTextColorPop(); closeFontPop(); closeStylePop(); closeRcPop();
    if (pop.hasAttribute('hidden')) {
      gateMoreRows(selLayer());
      pop.removeAttribute('hidden');
      setFloatingColumnVisible(false);
      requestAnimationFrame(() => pop.classList.add('open'));
    } else {
      closeMorePop();
    }
  }
  function toggleTextColorPop() {
    const pop = $('dt-textcolor-pop'); if (!pop) return;
    closeMorePop(); closeFontPop(); closeStylePop(); closeRcPop();
    if (pop.hasAttribute('hidden')) {
      renderTextColorPop();
      pop.removeAttribute('hidden');
      setFloatingColumnVisible(false);
      requestAnimationFrame(() => pop.classList.add('open'));
    } else {
      closeTextColorPop();
    }
  }

  /* ── Focused FONT popover (mobile) — the font picker as its own surface,
        replacing the old mixed props drawer. Same data + selection logic as
        the desktop panel, but isolated IDs (#dt-fp-*) so the two never clash. */
  function renderFontPop() {
    const body = $('dt-font-pop-body'); if (!body) return;
    const l = selLayer(); if (!l || l.type !== 'text') return;
    const allFonts = getAllFonts();
    const fontCats = ['All'].concat(allFonts.map(f => (f.category || '').trim()).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i));
    if (!S._fontCat || fontCats.indexOf(S._fontCat) === -1) S._fontCat = 'All';
    /* UX-Aug15 M5: preview = the first WORD of the layer text (was an 8-char slice that read as
       clipping — "Best Mom Ever" → "Best Mom"); long single words fall back to 10 chars. */
    const _pw = ((l.text || '').trim().split(/\s+/)[0] || '');
    const preview = (l.text && l.text !== 'Your Text' ? (_pw.length > 10 ? _pw.slice(0, 10) : (_pw || 'Aa')) : 'Aa').replace(/'/g, '&#39;').replace(/"/g, '&quot;');
    let html = '';
    if (fontCats.length > 2) {
      html += '<div class="dt-tpl-cats dt-font-cats" id="dt-fp-cats" role="tablist" aria-label="Font categories">' +
        fontCats.map(c => '<button class="dt-tpl-cat' + (c === S._fontCat ? ' is-active' : '') + '" type="button" data-fcat="' + escHtml(c) + '">' + escHtml(c) + '</button>').join('') + '</div>';
    }
    html += '<div class="dt-font-picker" id="dt-fp-picker">';
    if (!allFonts.length) {
      html += '<div class="dt-font-empty" style="grid-column:1/-1;padding:14px;font-size:var(--dt-text-sm);color:var(--dt-muted);text-align:center;line-height:1.4">No fonts are set up yet.</div>';
    }
    html += allFonts.map(f => {
      const sel = (l.font === f.fontValue || l.font === f.name) ? ' selected' : '';
      const fcat = (f.category || '').trim();
      const hide = (S._fontCat !== 'All' && fcat !== S._fontCat) ? ' dt-font-card--hidden' : '';
      return '<div class="dt-font-card dt-card dt-card--type' + sel + hide + '" data-font="' + f.fontValue + '" data-source="' + f.source + '" data-fcat="' + escHtml(fcat) + '" role="button" tabindex="0" aria-label="' + escHtml(f.name) + '">' + CARD_CHECK +
        '<div class="dt-card__media"><div class="dt-font-card__preview dt-card__preview" style="font-family:' + f.css + '">' + preview + '</div></div>' +
        '<div class="dt-font-card__name dt-card__caption">' + f.name + '</div></div>';
    }).join('');
    html += '</div>';
    body.innerHTML = html;
    body.querySelectorAll('.dt-font-card').forEach(card => card.addEventListener('click', () => {
      const f = card.dataset.font, l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.font = f;
      /* UX-Aug15 W0: mobile font picks were never logged (desktop handler at the
         unified .dt-font-card site does) — GA4 showed 0 mobile dt_font_select. */
      dtAnalytics.fontSelect(fontDisplayName(f));
      document.fonts.load('36px "' + f + '"').then(() => {
        measureText(l2); l2.ar = l2.w / (l2.h || 1); render();
        if (S._inlineEditLayer === l2 && _inlineEditDiv) { _inlineEditDiv.style.fontFamily = "'" + l2.font + "', sans-serif"; syncInlineEdit(l2, _inlineEditDiv); }
      }).catch(() => { measureText(l2); render(); });
      body.querySelectorAll('.dt-font-card').forEach(c => c.classList.toggle('selected', c.dataset.font === f));
      updateActionBar(); pushHistory();
    }));
    body.querySelectorAll('#dt-fp-cats .dt-tpl-cat').forEach(chip => chip.addEventListener('click', () => {
      S._fontCat = chip.dataset.fcat;
      body.querySelectorAll('#dt-fp-cats .dt-tpl-cat').forEach(c => c.classList.toggle('is-active', c.dataset.fcat === S._fontCat));
      body.querySelectorAll('#dt-fp-picker .dt-font-card').forEach(c2 => c2.classList.toggle('dt-font-card--hidden', !(S._fontCat === 'All' || c2.dataset.fcat === S._fontCat)));
    }));
  }
  function closeFontPop() {
    const pop = $('dt-font-pop');
    if (!pop || pop.hasAttribute('hidden')) return;
    pop.classList.remove('open'); setFloatingColumnVisible(true);
    pop.addEventListener('transitionend', () => { pop.setAttribute('hidden', ''); _fontPopZoomRestore(); }, { once: true });
    /* transitionend can be skipped (no transition / hidden tab) — restore on a timer too */
    setTimeout(_fontPopZoomRestore, 320);
  }
  /* UX-Aug15 M5: the font sheet used to cover the print area, so the customer couldn't see the
     text change while auditioning fonts. While it's open, applyEditModeZoom reserves the space
     above the sheet (see its drawer branch); on close we re-zoom back. Symmetric; skipped when
     the (future, spec §6) #dt-size-pop exists. */
  function _fontPopZoomReserve() {
    if (!isMobile() || S.mode !== 'design' || document.getElementById('dt-size-pop')) return;
    requestZoomToPA(200, true);
  }
  function _fontPopZoomRestore() {
    if (!isMobile() || S.mode !== 'design' || document.getElementById('dt-size-pop')) return;
    if (_fontPopZoomRestore._t) clearTimeout(_fontPopZoomRestore._t);
    _fontPopZoomRestore._t = setTimeout(() => { if ($('dt-font-pop')?.hasAttribute('hidden')) applyEditModeZoom(true); }, 30);
  }
  function toggleFontPop() {
    const pop = $('dt-font-pop'); if (!pop) return;
    closeTextColorPop(); closeMorePop(); closeStylePop(); closeRcPop();
    if (pop.hasAttribute('hidden')) {
      renderFontPop();
      pop.removeAttribute('hidden');
      setFloatingColumnVisible(false);
      requestAnimationFrame(() => { pop.classList.add('open'); _fontPopZoomReserve(); });
    } else {
      closeFontPop();
    }
  }

  /* ── Focused STYLE popover (mobile) — bold/italic, alignment, line height,
        and placement. Self-contained wiring (mutate layer + render directly),
        so it never touches the shared desktop props panel. */
  function renderStylePop() {
    const body = $('dt-style-pop-body'); if (!body) return;
    const l = selLayer(); if (!l || l.type !== 'text') return;
    const align = l.textAlign || 'center';
    const lh = l.lineHeight || 1.2;
    let html = '<div class="dt-prop-toolbar dt-prop-style-row">';
    html += '<button class="dt-prop-toggle' + (l.bold ? ' active' : '') + '" id="dt-sp-bold"><strong>B</strong></button>';
    html += '<button class="dt-prop-toggle' + (l.italic ? ' active' : '') + '" id="dt-sp-italic"><em>I</em></button>';
    html += '<span class="dt-prop-style-sep"></span>';
    html += '<button class="dt-prop-toggle' + (align === 'left' ? ' active' : '') + '" id="dt-sp-align-l" data-align="left" title="Left"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M1 2h12M1 5h8M1 8h12M1 11h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></button>';
    html += '<button class="dt-prop-toggle' + (align === 'center' ? ' active' : '') + '" id="dt-sp-align-c" data-align="center" title="Align text center"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M1 2h12M3 5h8M1 8h12M4 11h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></button>';
    html += '<button class="dt-prop-toggle' + (align === 'right' ? ' active' : '') + '" id="dt-sp-align-r" data-align="right" title="Right"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M1 2h12M5 5h8M1 8h12M7 11h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></button>';
    html += '</div>';
    html += '<div class="dt-style-pop__lh"><span class="dt-style-pop__lh-label">Line height</span>';
    html += '<input type="range" id="dt-sp-lh" min="0.8" max="2.5" step="0.1" value="' + lh + '" class="dt-prop-slider" style="flex:1;accent-color:var(--dt-forest)">';
    html += '<span id="dt-sp-lh-val" class="dt-style-pop__lh-val">' + lh.toFixed(1) + '</span></div>';
    html += '<div class="dt-prop-place">';
    /* This popover is mobile-only, and on mobile Fit lives ONLY in the action bar
       (Brandon's rule, Jul 28 2026) — the Style sheet keeps Center, which the bar
       does not offer. The !isMobile() form (rather than deleting the line) keeps
       the rule readable and identical to the other two placement rows. */
    if (!isMobile()) html += '<button class="dt-prop-place-btn" id="dt-sp-fit"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3.5" width="12" height="9" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M5 6.5h6M5 9.5h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg><span>Fit to Print Area</span></button>';
    html += '<button class="dt-prop-place-btn" id="dt-sp-center" title="Center in print area"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2.5" width="12" height="11" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M8 5v6M5 8h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg><span>Center</span></button>';
    html += '</div>';
    body.innerHTML = html;
    $('dt-sp-bold')?.addEventListener('click', function() {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.bold = !l2.bold; this.classList.toggle('active', l2.bold);
      measureText(l2); l2.ar = l2.w / (l2.h || 1); render(); pushHistory();
      if (S._inlineEditLayer === l2 && _inlineEditDiv) { _inlineEditDiv.style.fontWeight = l2.bold ? 'bold' : 'normal'; syncInlineEdit(l2, _inlineEditDiv); }
    });
    $('dt-sp-italic')?.addEventListener('click', function() {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.italic = !l2.italic; this.classList.toggle('active', l2.italic);
      measureText(l2); l2.ar = l2.w / (l2.h || 1); render(); pushHistory();
      if (S._inlineEditLayer === l2 && _inlineEditDiv) { _inlineEditDiv.style.fontStyle = l2.italic ? 'italic' : 'normal'; syncInlineEdit(l2, _inlineEditDiv); }
    });
    ['l', 'c', 'r'].forEach(a => $('dt-sp-align-' + a)?.addEventListener('click', function() {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.textAlign = this.dataset.align;
      body.querySelectorAll('#dt-sp-align-l,#dt-sp-align-c,#dt-sp-align-r').forEach(b => b.classList.toggle('active', b.dataset.align === l2.textAlign));
      measureText(l2); pushHistory(); render();
    }));
    $('dt-sp-lh')?.addEventListener('input', function() {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.lineHeight = parseFloat(this.value);
      const v = $('dt-sp-lh-val'); if (v) v.textContent = l2.lineHeight.toFixed(1);
      measureText(l2); l2.ar = l2.w / (l2.h || 1); render();
    });
    $('dt-sp-lh')?.addEventListener('change', pushHistory);
    $('dt-sp-fit')?.addEventListener('click', fitLayerToPA);
    $('dt-sp-center')?.addEventListener('click', centerLayerInPA);
  }
  function closeStylePop() {
    const pop = $('dt-style-pop');
    if (!pop || pop.hasAttribute('hidden')) return;
    pop.classList.remove('open'); setFloatingColumnVisible(true);
    pop.addEventListener('transitionend', () => pop.setAttribute('hidden', ''), { once: true });
  }
  function toggleStylePop() {
    const pop = $('dt-style-pop'); if (!pop) return;
    closeTextColorPop(); closeMorePop(); closeFontPop(); closeRcPop();
    if (pop.hasAttribute('hidden')) {
      renderStylePop();
      pop.removeAttribute('hidden');
      setFloatingColumnVisible(false);
      requestAnimationFrame(() => pop.classList.add('open'));
    } else {
      closeStylePop();
    }
  }


  /* ══════════════════════════════════════════════════════════════════
   * LIBRARY CARD SYSTEM (UX-Aug15 rev 6, Brandon Aug 17) — ONE .dt-card anatomy for the
   * five libraries (monogram / graphics / templates / saved / fonts). Old class names stay
   * on the same element as aliases so every existing selector, handler and analytics hook
   * keeps working; only the visual system is new (design-tool.css "LIBRARY CARD SYSTEM").
   * ══════════════════════════════════════════════════════════════════ */
  const CARD_CHECK = '<span class="dt-card__check" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5.2l2.1 2.1L8 3.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
  const CARD_BADGE = '<span class="dt-card__badge" role="img" aria-label="Colours can be changed" title="Colours can be changed after adding"></span>';
  /* div-based cards (monogram / graphics / fonts) carry role=button + tabindex; make Enter/Space
     activate them like a real button (delegated once; native <button> cards are untouched). */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = e.target;
    if (t && t.classList && t.classList.contains('dt-card') && t.tagName !== 'BUTTON' && t.getAttribute('role') === 'button') { e.preventDefault(); t.click(); }
  });

  /* ══════════════════════════════════════════════════════════════════
   * VARIANT / COLOR SWITCHING
   * ══════════════════════════════════════════════════════════════════ */
  $$('.dt-color-swatch-btn').forEach(btn => btn.addEventListener('click', () => {
    $$('.dt-color-swatch-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    S.variantId = parseInt(btn.dataset.variantId);
    S.colorHandle = btn.dataset.colorHandle;
    history.replaceState({}, '', `${CFG.productUrl}?view=design-tool&variant=${S.variantId}`);
    const nameEl = $("dt-swatch-name"); if (nameEl) nameEl.textContent = btn.dataset.colorName || btn.dataset.colorHandle;
    /* Update desktop color pill + close popover */
    const pillName = $('dt-color-pill-name'); if (pillName) pillName.textContent = btn.dataset.colorName || btn.dataset.colorHandle;
    /* Update mobile color pill (name + dot) */
    const mPillName = $('dt-color-pill-name-m'); if (mPillName) mPillName.textContent = btn.dataset.colorName || btn.dataset.colorHandle;
    const mPillDot = $('dt-color-pill-dot');
    if (mPillDot) mPillDot.style.backgroundImage = `url('https://cdn.shopify.com/s/files/1/0537/7101/0220/files/${btn.dataset.colorHandle}.png')`;
    const dPillSwatch = $('dt-color-pill-swatch');
    if (dPillSwatch) { dPillSwatch.style.backgroundImage = `url('https://cdn.shopify.com/s/files/1/0537/7101/0220/files/${btn.dataset.colorHandle}.png')`; dPillSwatch.style.backgroundSize = 'cover'; dPillSwatch.style.backgroundPosition = 'center'; }
    const popover = $('dt-color-expand'); if (popover) popover.classList.remove('open');
    buildSideTabs(); loadOverlay(); loadMockup();
    /* Contrast check: after the mockup settles, warn if any text layer is
       near-illegible on the new product background. Runs once per switch. */
    setTimeout(() => { if (S.method === 'print') reseedAutoTextColors('variant'); checkDesignContrast(); updateCardSwatchBg(); /* UX-Aug20 */ }, 600);
  }));

  /* UX-Aug15 rev 5 (Brandon, Aug 17) — SOLD-OUT COLOURS ARE NOT OFFERED.
     The liquid renders one swatch per unique colour with no stock check. Shopify's variant JSON
     (CFG.variants) already carries `available`, which is exactly the rule wanted: false only when
     inventory is tracked, qty <= 0 AND policy = deny ("don't continue selling"); a continue-selling
     variant at 0 stays true. Per colour: hide the swatch (desktop popover + mobile list) when NO
     variant of that colour is available; if the swatch's mapped variant is out but a sibling of the
     same colour is in, retarget data-variant-id so the cart gets a buyable variant. Fail-open: no
     data, or every colour out → nothing hidden. If the landing variant (old ?variant= link, saved
     design) is sold out and another colour is buyable, hop to the first available colour with a
     toast — goes through the normal swatch click so mockup/overlay/side tabs/URL all update. */
  (function applySwatchAvailability() {
    try {
      const vs = Array.isArray(CFG.variants) ? CFG.variants : [];
      if (!vs.length || typeof vs[0].available !== 'boolean') return;
      const byId = new Map(vs.map(v => [String(v.id), v]));
      const optsOf = v => Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(x => x != null);
      const btns = $$('.dt-color-swatch-btn');
      if (!btns.length) return;
      /* colour option index: the option slot whose value equals the swatch's colour name */
      let colorIdx = -1;
      for (const b of btns) {
        const v = byId.get(String(b.dataset.variantId)); if (!v) continue;
        const i = optsOf(v).indexOf(b.dataset.colorName || '');
        if (i >= 0) { colorIdx = i; break; }
      }
      const colorOfVariant = v => colorIdx >= 0 ? (optsOf(v)[colorIdx] ?? '') : (v.title || '');
      /* colour name → { any: bool, first: available variant id } */
      const stock = new Map();
      vs.forEach(v => {
        const c = colorOfVariant(v); const e = stock.get(c) || { any: false, first: null };
        if (v.available) { e.any = true; if (e.first == null) e.first = v.id; }
        stock.set(c, e);
      });
      const names = [...new Set(btns.map(b => b.dataset.colorName || ''))];
      const availNames = names.filter(n => stock.get(n)?.any);
      if (!availNames.length) return;                       /* everything out → leave as-is (fail open) */
      let hidden = 0;
      btns.forEach(b => {
        const n = b.dataset.colorName || '', e = stock.get(n);
        if (!e || !e.any) { b.hidden = true; b.classList.add('dt-swatch--soldout'); b.setAttribute('aria-hidden', 'true'); hidden++; return; }
        const own = byId.get(String(b.dataset.variantId));
        if (own && !own.available && e.first != null) b.dataset.variantId = String(e.first);
      });
      DEBUG && console.log('[dt] swatch availability: hidden', hidden, 'of', btns.length, 'colours');
      /* Landing variant sold out → hop to the first buyable colour */
      const cur = byId.get(String(S.variantId));
      if (cur && !cur.available && !stock.get(colorOfVariant(cur))?.any) {
        const target = btns.find(b => !b.hidden && b.dataset.colorHandle !== S.colorHandle) || btns.find(b => !b.hidden);
        if (target) {
          const was = colorOfVariant(cur) || 'That colour';
          target.click();
          setTimeout(() => flashToast(was + ' is sold out \u2014 showing ' + (target.dataset.colorName || 'another colour') + ' instead.', 4000), 400);
        }
      }
    } catch (e) { DEBUG && console.warn('[dt] swatch availability skipped:', e); }
  })();

  /* ── UX-Aug15 #1: auto-contrast default text colour ─────────────────
     New text was hard-coded S.textColor (#111111) — black on the Black 20 oz that is
     the #1 product. Rules (agreed across three review rounds, zero engrave-output change):
       • Seeded ONLY at layer creation (addTextLayer / addTextLayerAndEdit), ONLY when
         S.method === 'print'. Starters, templates, monograms untouched.
       • The new layer gets an explicit l.color + a transient l._autoColor flag.
         serializeDesign() is an allowlist → the flag never reaches cart _design_data,
         S3 JSON, saved designs or bulk templates (byte-identical payloads).
       • print→engrave: flagged layers go back to #111111 (user picks untouched);
         engrave→print: flagged layers are re-seeded. So every engrave output for any
         design a user could previously produce is byte-identical.
       • Restore under engrave: a LIGHT text layer is darkened + flagged (closes the
         saved-print-design-loaded-under-engrave leak into the engrave PREPRESS PDF,
         whose live text uses l.color unforced).
       • Explicit user colour pick clears the flag (applyTextColor / setLayerColor /
         desktop preset path).
       • Variant switch (print only): flagged layers re-seeded next to checkDesignContrast.
     Luminance source: the mockup's print-area sample (same path as checkDesignContrast);
     falls back to the dark default if the mockup is missing/CORS-tainted. */
  function sampleMockupPALum() {
    try {
      if (!mImg || !mImg.complete || !mImg.naturalWidth) return null;
      const pa = S.pa; if (!pa || pa.w < 10) return null;
      const G = 16;
      const ow = mImg.offsetWidth || mImg.naturalWidth, oh = mImg.offsetHeight || mImg.naturalHeight;
      const kx = mImg.naturalWidth / ow, ky = mImg.naturalHeight / oh;
      const sc = document.createElement('canvas'); sc.width = G; sc.height = G;
      const sctx = sc.getContext('2d', { willReadFrequently: true });
      sctx.drawImage(mImg, pa.x * kx, pa.y * ky, pa.w * kx, pa.h * ky, 0, 0, G, G);
      const d = sctx.getImageData(0, 0, G, G).data;
      let rSum = 0, gSum = 0, bSum = 0;
      for (let i = 0; i < G * G; i++) { rSum += d[i*4]; gSum += d[i*4+1]; bSum += d[i*4+2]; }
      const n = G * G;
      return relLum(rSum/n, gSum/n, bSum/n);
    } catch (_) { return null; }
  }
  const AUTO_TEXT_DARK = '#111111', AUTO_TEXT_LIGHT = '#FFFFFF';
  /* Returns {color, auto}. auto=true only when a real sample drove the choice. */
  function seedTextColor() {
    if (S.method !== 'print') return { color: S.textColor, auto: false };
    const lum = sampleMockupPALum();
    if (lum === null) return { color: S.textColor, auto: false };
    return { color: lum < 0.35 ? AUTO_TEXT_LIGHT : AUTO_TEXT_DARK, auto: true };
  }
  function isLightHex(hex) {
    const rgb = hexToRgb(hex || ''); if (!rgb) return false;
    return relLum(rgb.r, rgb.g, rgb.b) > 0.5;
  }
  /* UX-Aug20: luminance-based check for template/starter default dark colours.
     Returns true for ANY dark hex (lum < 0.25) — handles #111111, #2C2825,
     #000000, and any other dark defaults from external JSON templates. */
  function _isDarkDefault(hex) {
    if (!hex) return true;
    var rgb = hexToRgb(hex);
    if (!rgb) return true;
    var lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return lum < 0.25;
  }
  /* Returns {color, auto} for monogram default colour, same logic as text. */
  function seedMonoColor() {
    if (S.method !== 'print') return { color: '#2a1f18', auto: false };
    const lum = sampleMockupPALum();
    if (lum === null) return { color: S.monoColor, auto: false };
    return { color: lum < 0.35 ? '#FFFFFF' : '#2a1f18', auto: true };
  }
  /* Re-seed every _autoColor text layer on every side (print) or park them dark (engrave).
     Only flagged layers are ever touched. */
  function reseedAutoTextColors(reason) {
    let changed = 0, monoRebuilds = 0;
    const seed = S.method === 'print' ? seedTextColor() : { color: AUTO_TEXT_DARK, auto: true };
    /* Final review A3: in print, only re-seed when a REAL mockup sample drove the choice —
       during a variant src swap the mockup isn't loaded yet and we must not write the dark
       fallback into flagged layers (black on the Black 20 oz). The next real sample re-seeds. */
    if (S.method === 'print' && !seed.auto) return 0;
    const monoSeed = S.method === 'print' ? seedMonoColor() : { color: '#2a1f18', auto: true };
    CFG.sides.forEach(side => (S.byView[side] || []).forEach(l => {
      if (!l._autoColor) return;
      /* Text layers (including template/starter text) */
      if (l.type === 'text') {
        const next = S.method === 'print' ? seed.color : AUTO_TEXT_DARK;
        if (l.color !== next) { l.color = next; changed++; }
      }
      /* UX-Aug20: Monogram layers — colour is baked into the PNG, so a full rebuild is needed */
      else if (l.isMonogram && monoSeed.auto) {
        const next = S.method === 'print' ? monoSeed.color : '#2a1f18';
        if (l.monoColor !== next) {
          rebuildMonogramColor(l, next, 1);  /* async re-render; rebuildMonogramColor calls render() on completion */
          monoRebuilds++; changed++;
        }
      }
      /* Under engrave the flag stays so a later flip back to print re-seeds. */
    }));
    /* UX-Aug20 race fix: if monogram rebuilds were started, skip the synchronous render() —
       the async rebuild callbacks will call render() when the new PNG is ready. Rendering now
       would show one frame with mismatched colours (text recolored, monogram still old PNG). */
    if (changed) {
      DEBUG && console.log('[dt] auto-contrast re-seed (' + reason + '):', changed, monoRebuilds ? '(+' + monoRebuilds + ' mono rebuilds)' : '');
      if (!monoRebuilds) render();
    }
    return changed;
  }
  /* Restore-time guard: light text arriving under engrave is darkened + flagged. */
  function guardRestoredTextColor(color) {
    if (S.method === 'engrave' && isLightHex(color)) return { color: AUTO_TEXT_DARK, auto: true };
    return { color: color, auto: false };
  }

  /* ── Contrast check after variant colour switch ─────────────────
     Sample the mockup under the print area to get a rough average luminance,
     then warn if any text layer's colour is too close (WCAG-ish ratio < 2). */
  function checkDesignContrast() {
    if (!mImg || !mImg.complete || !mImg.naturalWidth) return;
    const ls = layers(); if (!ls.length) return;
    /* Sample the mockup's print-area region at low res */
    try {
      const pa = S.pa; if (!pa || pa.w < 10) return;
      const G = 16;
      const ow = mImg.offsetWidth || mImg.naturalWidth, oh = mImg.offsetHeight || mImg.naturalHeight;
      const kx = mImg.naturalWidth / ow, ky = mImg.naturalHeight / oh;
      const sc = document.createElement('canvas'); sc.width = G; sc.height = G;
      const sctx = sc.getContext('2d', { willReadFrequently: true });
      sctx.drawImage(mImg, pa.x * kx, pa.y * ky, pa.w * kx, pa.h * ky, 0, 0, G, G);
      const d = sctx.getImageData(0, 0, G, G).data;
      let rSum = 0, gSum = 0, bSum = 0;
      for (let i = 0; i < G * G; i++) { rSum += d[i*4]; gSum += d[i*4+1]; bSum += d[i*4+2]; }
      const n = G * G;
      const bgLum = relLum(rSum/n, gSum/n, bSum/n);
      /* Check every text layer */
      let lowContrast = false;
      for (const l of ls) {
        if (l.type !== 'text' || l.visible === false) continue;
        const hex = l.color || '#111111';
        const rgb = hexToRgb(hex); if (!rgb) continue;
        const fgLum = relLum(rgb.r, rgb.g, rgb.b);
        const ratio = (Math.max(bgLum, fgLum) + 0.05) / (Math.min(bgLum, fgLum) + 0.05);
        if (ratio < 2) { lowContrast = true; break; }
      }
      if (lowContrast) {
        toast('text_contrast', null, 4500);
      }
    } catch (_) { /* CORS-tainted mockup — skip silently */ }
  }
  function relLum(r, g, b) {
    const s = [r,g,b].map(function(v){ v /= 255; return v <= 0.03928 ? v/12.92 : Math.pow((v+0.055)/1.055, 2.4); });
    return 0.2126*s[0] + 0.7152*s[1] + 0.0722*s[2];
  }
  function hexToRgb(hex) {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
    if (hex.length !== 6) return null;
    return { r: parseInt(hex.slice(0,2),16), g: parseInt(hex.slice(2,4),16), b: parseInt(hex.slice(4,6),16) };
  }

  /* Initialize the pill swatch/dot from the active variant on load */
  (function initPillSwatch() {
    const a = document.querySelector('.dt-color-swatch-btn.active') || document.querySelector('.dt-color-swatch-btn');
    if (!a) return;
    const url = `url('https://cdn.shopify.com/s/files/1/0537/7101/0220/files/${a.dataset.colorHandle}.png')`;
    const ds = $('dt-color-pill-swatch'); if (ds) { ds.style.backgroundImage = url; ds.style.backgroundSize = 'cover'; ds.style.backgroundPosition = 'center'; }
    const md = $('dt-color-pill-dot'); if (md) md.style.backgroundImage = url;
  })();

  /* Desktop: color pill popover toggle */
  $('dt-color-pill')?.addEventListener('click', (e) => {
    e.stopPropagation();
    const expand = $('dt-color-expand');
    if (!expand) return;
    expand.classList.toggle('open');
    /* Aug 28: the card is right-anchored under the pill and #dt-canvas-area is overflow:hidden,
       so on narrow canvases (open tool panel + 16-colour grid) its left edge was CLIPPED by the
       panel column. Nudge it right just enough to stay inside the canvas area (capped so it
       never spills past the right edge either). Reset each open so it re-measures. */
    expand.style.right = '';
    if (expand.classList.contains('open')) {
      try {
        const area = $('dt-canvas-area');
        if (area && !isMobile()) {
          const ar = area.getBoundingClientRect(), r = expand.getBoundingClientRect(), pad = 8;
          let shift = Math.max(0, (ar.left + pad) - r.left);
          shift = Math.min(shift, Math.max(0, (ar.right - pad) - r.right));
          if (shift > 0) expand.style.right = (-shift) + 'px';
        }
      } catch (_) {}
    }
  });
  document.addEventListener('click', (e) => {
    const expand = $('dt-color-expand');
    if (expand && expand.classList.contains('open') && !e.target.closest('.dt-header__color-select')) {
      expand.classList.remove('open');
    }
  });
  /* rev 5: Esc closes the colour dropdown too (it is a real popover on desktop now) */
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') $('dt-color-expand')?.classList.remove('open'); });

  /* ══════════════════════════════════════════════════════════════════
   * MOCKUP IMAGE
   * ══════════════════════════════════════════════════════════════════ */
  function mockupSrc() {
    const ci = CFG.images[S.colorHandle];
    if (ci?.[S.view]) return ci[S.view];
    for (const c in CFG.images) if (CFG.images[c][S.view]) return CFG.images[c][S.view];
    return null;
  }

  function loadMockup() {
    const src = mockupSrc();
    const sk = $('dt-mockup-skeleton');
    if (!src) { mImg.style.display = 'none'; if (sk) { sk.style.display = ''; sk.classList.remove('error'); } return; }
    mImg.style.display = 'block';
    /* CRO #21 — already showing the hi-res variant of this same base image */
    if (mImg._dtHiresFor === src) { mImg.classList.remove('loading'); onMockupLoad(); return; }
    mImg.classList.add('loading');
    if (sk) { sk.style.display = ''; sk.classList.remove('error'); sk.onclick = null; }
    if (mImg.getAttribute('src') !== src) {
      S._mockupBaseNW = 0; mImg._dtHiresFor = '';   /* CRO #21: new image — new coordinate basis */
      /* Use once:true so the handler fires exactly once and is removed */
      mImg.addEventListener('load', onMockupLoad, { once: true });
      mImg.onerror = () => {
        DEBUG && console.error('[dt] Mockup failed:', src);
        mImg.classList.remove('loading'); mImg.style.display = 'none';
        const s = $('dt-mockup-skeleton');
        if (s) { s.style.display = ''; s.classList.add('error'); s.onclick = () => loadMockup(); }
      };
      mImg.src = src;
    } else {
      onMockupLoad();
    }
  }

  function onMockupLoad() {
    mImg.classList.remove('loading');
    if (!S._mockupBaseNW) S._mockupBaseNW = mImg.naturalWidth || 1000;   /* CRO #21 */
    { const sk = $('dt-mockup-skeleton'); if (sk) { sk.style.display = 'none'; sk.classList.remove('error'); sk.onclick = null; } }
    if (typeof _lightCache === 'object') delete _lightCache[S.variantId + ':' + S.view];
    delete _paColorCache[S.variantId + ':' + S.view];   /* re-sample border contrast for new mockup */
    /* RAF ensures layout has settled before measuring offsetWidth for S.rscale */
    requestAnimationFrame(() => {
      syncCanvas(); syncPrintOverlay(); render();
      renderProductIcon();
      /* UX-Aug15 #1: the mockup for the current variant is now really loaded — re-seed
         flagged (auto-contrast) text layers against it (print only; flagged only). */
      try { if (S.method === 'print') reseedAutoTextColors('mockup-load'); } catch (_) {}
      /* Auto-zoom to print area on first load — MOBILE ONLY. Desktop used to
         land at up to 250%, cropping the product top/bottom so customers never
         saw what they were customizing. Desktop now lands at 100% (whole
         product); the quick-zoom preset jumps to the print area on demand. */
      if (!onMockupLoad._zoomed) {
        onMockupLoad._zoomed = true;
        if (isMobile()) setTimeout(zoomToPrintArea, 250);
        /* UX-Aug15 #6: desktop lands with the print area at ~55% of the visible height
           (cap 150%, never cropping like the old 250%); Fit still toggles 85% ↔ 100%. */
        else if (S.zoom === 1) setTimeout(() => { if (S.zoom === 1) zoomToPrintArea(null, { initial: true, factor: 0.6, cap: 1.6 }); }, 300);
      }
    });
  }

  /* CRO #21 — when zoom × devicePixelRatio outgrows the 1000px mockup
     variant, silently swap in the 2000px CDN variant of the SAME image.
     The coordinate space stays frozen to the base variant (S._mockupBaseNW),
     so S.pa and every layer keep their geometry — only the pixels sharpen.
     Sources smaller than the request are never upscaled by the CDN, so the
     probe's naturalWidth check skips products without bigger originals. */
  function maybeUpgradeMockup() {
    if (!mImg) return;
    const base = mockupSrc();
    if (!base || mImg._dtHiresFor === base || mImg._dtHiresLoading === base) return;
    if (!/[?&]width=1000(&|$)/.test(base)) return;
    const dpr = window.devicePixelRatio || 1;
    const need = (mImg.offsetWidth || 0) * S.zoom * dpr;
    if (need <= (S._mockupBaseNW || mImg.naturalWidth || 1000) * 1.2) return;
    const hi = base.replace(/([?&]width=)1000(&|$)/, '$12000$2');
    mImg._dtHiresLoading = base;
    const probe = new Image();
    probe.onload = () => {
      mImg._dtHiresLoading = '';
      if (mockupSrc() !== base) return;                       /* side/color changed mid-flight */
      if (probe.naturalWidth <= (S._mockupBaseNW || 0)) return; /* CDN had nothing bigger */
      mImg._dtHiresFor = base;
      mImg.src = hi;   /* layout + rscale basis unchanged — a pure pixel upgrade */
    };
    probe.onerror = () => { mImg._dtHiresLoading = ''; };
    probe.src = hi;
  }

  function getCanvasRenderScale() {
    const dpr = window.devicePixelRatio || 1;
    /* Match the actual on-screen magnification (zoom × device pixels) so text stays
       crisp when zoomed. The old min(zoom,2.5)×dpr cap under-sampled the buffer at the
       very zoom levels the print area is auto-fitted to on phones → blurry fonts. */
    let rs = Math.max(dpr, S.zoom * dpr);
    /* Clamp to a safe canvas budget (per-side + total area) to stay within iOS/Android
       canvas limits and bound memory — but never drop below native device pixels, or
       text renders sub-native-resolution and looks grainy on high-DPR phones. */
    const w = mImg?.offsetWidth || 1000, h = mImg?.offsetHeight || 1000;
    const cap = Math.min(4096 / Math.max(w, h), Math.sqrt(1.6e7 / Math.max(1, w * h)));
    if (rs > cap) rs = Math.max(dpr, cap);
    return rs;
  }

  /* Live (instant) view transform used DURING mobile gestures: the wrapper carries pan,
     the mockup raster + print overlay carry the zoom scale, and the canvas is temporarily
     CSS-scaled relative to its last re-rendered size for instant feedback. On settle,
     syncCanvas() re-renders the canvas at the true size with NO CSS scale. */
  let _renderedZoom = 1;
  function liveView(z, px, py) {
    if (mWrap) { mWrap.style.transformOrigin = '0 0'; mWrap.style.transform = (px || py) ? `translate(${px}px,${py}px)` : ''; }
    const sc = z !== 1 ? `scale(${z})` : '';
    if (mImg) { mImg.style.transformOrigin = '0 0'; mImg.style.transform = sc; }
    /* The print-area outline MUST use the exact same pivot and border rule as
       syncPrintOverlay (scale about the wrapper origin = -pa.x,-pa.y, border kept
       visually constant at 1.5/z). Driving it differently here than at rest is what
       made it drift during the pinch and snap into place on release. */
    if (pOver) {
      pOver.style.transformOrigin = `${-S.pa.x}px ${-S.pa.y}px`;
      pOver.style.transform = sc;
      pOver.style.borderWidth = Math.max(0.5, 1.5 / z) + 'px';
    }
    const cs = z / (_renderedZoom || 1);
    if (canvas) { canvas.style.transformOrigin = '0 0'; canvas.style.transform = Math.abs(cs - 1) > 1e-3 ? `scale(${cs})` : ''; }
  }

  function syncCanvas() {
    const w = mImg.offsetWidth, h = mImg.offsetHeight;
    if (!w || !h) return;
    const rs = getCanvasRenderScale();
    const bufW = Math.round(w * rs), bufH = Math.round(h * rs);
    if (canvas.width !== bufW || canvas.height !== bufH) {
      canvas.width = bufW; canvas.height = bufH;
    }
    const z = S.zoom;
    if (isMobile()) {
      /* iOS-safe: RE-RENDER the canvas at the true magnified size and display it with NO
         CSS scale — CSS-scaling a canvas is what iOS WebKit tiles into glitchy text. The
         zoom scale lives on the mockup raster; the wrapper carries pan only. */
      canvas.style.width = Math.round(w * z) + 'px'; canvas.style.height = Math.round(h * z) + 'px';
      canvas.style.transform = ''; canvas.style.transformOrigin = '0 0';
      _renderedZoom = z;
      const sc = z !== 1 ? `scale(${z})` : '';
      mImg.style.transformOrigin = '0 0'; mImg.style.transform = sc;
      const px = S._panX || 0, py = S._panY || 0;
      mWrap.style.transformOrigin = '0 0';
      mWrap.style.transform = (px || py) ? `translate(${px}px,${py}px)` : '';
      mWrap.style.willChange = '';
    } else {
      /* Desktop: canvas at natural size; the wrapper's transform magnifies it (desktop
         browsers rasterize the scaled canvas crisply). */
      canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
      canvas.style.transform = ''; canvas.style.transformOrigin = '';
      mImg.style.transform = ''; mImg.style.transformOrigin = '';
      _renderedZoom = 1;
    }
    ctx.setTransform(rs, 0, 0, rs, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    const _prevRscale = S.rscale;
    S.rscale = w / (S._mockupBaseNW || mImg.naturalWidth || 1000);
    S.rscaleByView[S.view] = S.rscale;
    computePA();

    /* ── Remap layer coordinates when rscale changes ──────────────────
       Layer x/y/w/h are in canvas-pixel space.  When the canvas resizes
       (viewport resize, panel open/close, orientation change), rscale
       changes but layer coordinates were frozen at the old value.  This
       caused text placed at the center of the print area to drift outside
       the production region, producing blank production files.  Scale all
       coordinates proportionally so layers stay pinned to the same
       position within the print area. */
    if (_prevRscale > 0 && Math.abs(S.rscale - _prevRscale) > 1e-6) {
      const _rr = S.rscale / _prevRscale;
      CFG.sides.forEach(side => {
        (S.byView[side] || []).forEach(l => {
          l.x *= _rr;  l.y *= _rr;
          l.w *= _rr;  l.h *= _rr;
          if (l.type === 'text') {
            l.fontSize = Math.max(1, l.fontSize * _rr);
            /* w/h will be corrected by measureText() on next render */
          }
          if (l.ar) l.ar = l.w / (l.h || 1);
        });
      });
      DEBUG && console.log('[dt] Layers remapped for rscale change:', _prevRscale.toFixed(4), '→', S.rscale.toFixed(4), '(×' + _rr.toFixed(4) + ')');
    }

    /* TEMP diagnostic (#44 blur): add ?dtdebug=1 to the URL to show canvas sampling on
       screen. samples/cssPx should be >= dpr for crisp text; if it's lower, the canvas is
       under-sampled. Remove after diagnosing. */
    if (location.search.indexOf('dtdebug') > -1) {
      let d = document.getElementById('dt-dbg');
      if (!d) { d = document.createElement('div'); d.id = 'dt-dbg'; d.style.cssText = 'position:fixed;top:64px;left:6px;z-index:99999;background:rgba(0,0,0,.82);color:#0f0;font:11px/1.35 monospace;padding:5px 7px;border-radius:5px;pointer-events:none;white-space:pre'; document.body.appendChild(d); }
      const rct = canvas.getBoundingClientRect();
      d.textContent = 'dpr=' + (window.devicePixelRatio || 1) + '  z=' + (S.zoom || 1).toFixed(2) + '  rs=' + rs.toFixed(2) +
        '\nbacking=' + canvas.width + 'x' + canvas.height +
        '\ncss=' + Math.round(rct.width) + 'x' + Math.round(rct.height) +
        '\nsamples/cssPx=' + (canvas.width / (rct.width || 1)).toFixed(2);
    }
  }

  function computePA() {
    const sc = S.rscale;
    const c = CFG.sideConfigs[S.view]?.printArea || { x:0, y:0, w:300, h:300 };
    S.pa = { x: c.x*sc, y: c.y*sc, w: c.w*sc, h: c.h*sc };
    S.paByView[S.view] = { x: c.x*sc, y: c.y*sc, w: c.w*sc, h: c.h*sc };
  }

  /* ── Print-overlay border contrast ────────────────────────────────
     Sample the mockup under the print area to determine average luminance.
     Dark products get a white border, light products keep the dark green.
     Cached per variant+view so we sample once per mockup, not per frame. */
  var _paColorCache = {};
  function _printOverlayBorderColor() {
    var key = S.variantId + ':' + S.view;
    if (key in _paColorCache) return _paColorCache[key];
    var fallback = 'rgba(52,78,65,0.65)';
    try {
      if (!mImg || !mImg.complete || !mImg.naturalWidth || !S.pa || S.pa.w < 10) return fallback;
      var pa = S.pa, G = 16;
      var ow = mImg.offsetWidth || mImg.naturalWidth, oh = mImg.offsetHeight || mImg.naturalHeight;
      var kx = mImg.naturalWidth / ow, ky = mImg.naturalHeight / oh;
      var sc = document.createElement('canvas'); sc.width = G; sc.height = G;
      var sctx = sc.getContext('2d', { willReadFrequently: true });
      sctx.drawImage(mImg, pa.x * kx, pa.y * ky, pa.w * kx, pa.h * ky, 0, 0, G, G);
      var d = sctx.getImageData(0, 0, G, G).data;
      var rS = 0, gS = 0, bS = 0;
      for (var i = 0; i < G * G; i++) { rS += d[i*4]; gS += d[i*4+1]; bS += d[i*4+2]; }
      var n = G * G;
      var lum = 0.299 * rS/n + 0.587 * gS/n + 0.114 * bS/n;
      var color = lum < 110 ? 'rgba(255,255,255,0.7)' : fallback;
      _paColorCache[key] = color;
      return color;
    } catch (_) { return fallback; }
  }

  function syncPrintOverlay() {
    computePA();
    if (pOver) {
      if (S.previewMode) { pOver.style.display = 'none'; return; }
      pOver.style.display = '';
      const z = S.zoom, bw = Math.max(0.5, (isMobile() ? 1.5 : 2) / z);   /* desktop: heavier outline for low vision */
      /* On mobile the overlay carries its own zoom scale (the wrapper no longer scales).
         It must scale about the SAME origin as the mockup image — the wrapper's 0,0 —
         not the overlay's own top-left, or it drifts off the print area as you zoom.
         The overlay sits at (pa.x, pa.y), so the wrapper origin in its local space is
         (-pa.x, -pa.y). On desktop the wrapper scales it, so no transform here. */
      const tf = (isMobile() && z !== 1)
        ? `;transform-origin:${-S.pa.x}px ${-S.pa.y}px;transform:scale(${z})`
        : '';
      var bc = _printOverlayBorderColor();
      pOver.style.cssText = `left:${S.pa.x}px;top:${S.pa.y}px;width:${S.pa.w}px;height:${S.pa.h}px;border-width:${bw}px;border-color:${bc}${tf}`;
    }
    syncClipWarnGeometry();
  }

  /* ══════════════════════════════════════════════════════════════════
   * CLIP WARNING — actual ink outside the print area
   * Red highlight on the crossed edge(s) + a tint over the outside strip +
   * a "Will Be Cropped" pill (viewport-clamped so it never renders off
   * screen), all persistent while any visible layer has non-transparent
   * pixels beyond the boundary. Detection is ink-based (alpha), NOT
   * bounding-box-based: transparent PNG padding or the empty corner of a
   * rotated layer never triggers it — matching production, which crops
   * pixels, not boxes.
   * ══════════════════════════════════════════════════════════════════ */
  let _clipWarnEl = null, _clipEdges = { top:false, right:false, bottom:false, left:false };
  let _clipCheckT = 0, _clipCheckTimer = 0, _clipInkCnv = null;

  function _clipWarnEls() {
    if (_clipWarnEl) return _clipWarnEl;
    const wrap = $('dt-canvas-wrap');
    if (!wrap) return null;
    const el = document.createElement('div');
    el.id = 'dt-clip-warn';
    el.className = 'dt-clip-warn';
    el.setAttribute('aria-hidden', 'true'); /* SR users get the live-region announcement instead */
    el.innerHTML =
      ['top','right','bottom','left'].map(s =>
        `<div class="dt-clip-warn__tint dt-clip-warn__tint--${s}"></div>` +
        `<div class="dt-clip-warn__edge dt-clip-warn__edge--${s}"></div>`
      ).join('') +
      '<div class="dt-clip-warn__pill">Will Be Cropped</div>';
    wrap.appendChild(el);
    _clipWarnEl = el;
    return el;
  }

  /* Mirror the print overlay's geometry (incl. the mobile zoom transform) */
  function syncClipWarnGeometry() {
    const el = _clipWarnEl;
    if (!el || !S.pa) return;
    const z = S.zoom;
    const tf = (isMobile() && z !== 1)
      ? `;transform-origin:${-S.pa.x}px ${-S.pa.y}px;transform:scale(${z})`
      : '';
    const on = el.classList.contains('is-on');
    el.style.cssText = `left:${S.pa.x}px;top:${S.pa.y}px;width:${S.pa.w}px;height:${S.pa.h}px${tf}`;
    el.classList.toggle('is-on', on);
    /* Tint patches are sized per-check from the actual clipped-ink extents
       (see _applyClipWarn) — container-relative, so they track zoom/pa moves. */
  }

  /* Corners of a layer's drawn rect after rotation — same pivot rules as
     applyTransform (centered layers pivot on (x,y), others on bbox center) */
  function _rotatedCorners(l) {
    const w = l.w || 0, h = l.h || 0;
    const dx = l.centered ? l.x - w / 2 : l.x, dy = l.centered ? l.y - h / 2 : l.y;
    const cx = l.centered ? l.x : l.x + w / 2, cy = l.centered ? l.y : l.y + h / 2;
    const pts = [[dx, dy], [dx + w, dy], [dx + w, dy + h], [dx, dy + h]];
    const r = (l.rotation || 0) * Math.PI / 180;
    if (!r) return pts;
    const cos = Math.cos(r), sin = Math.sin(r);
    return pts.map(([x, y]) => [cx + (x - cx) * cos - (y - cy) * sin, cy + (x - cx) * sin + (y - cy) * cos]);
  }

  function _computeInkOverflow() {
    const out = { top:false, right:false, bottom:false, left:false, _counts:null };
    if (!S.pa) return out;
    /* TOL: pre-filter slack (canvas px). SCAN_TOL: how far past the edge ink
       must reach before it counts — 1 px so any real leg/tail overlap warns
       while AA fringe of ink butted exactly at the line stays quiet. */
    const pa = S.pa, TOL = 2, SCAN_TOL = 1;
    const vis = layers().filter(l => l.visible !== false);
    if (!vis.length) return out;

    /* Stage 1 — rotated bbox pre-filter: fully-inside layers can't have ink outside.
       Text layers get an inflated bbox to account for descenders (g, y, p, q, j…)
       whose ink extends below the reported text-metric height, and for script-font
       swashes that overhang left/right of the measured advance width. 0.45em down
       and 0.15em per side cover script faces (the previous 0.3em missed them —
       order screenshot with a "y" tail crossing the edge undetected). Inflation
       only adds Stage-2 candidates; the pixel scan stays authoritative, so this
       can't cause false warnings. Shallow copy so the inflation survives the
       rotation math inside _rotatedCorners. */
    const cands = vis.filter(l => {
      let probe = l;
      if (l.type === 'text' && l.fontSize) {
        const desc = l.fontSize * 0.45;
        const side = l.fontSize * 0.15;
        probe = { ...l, h: (l.h || 0) + desc, w: (l.w || 0) + side * 2 };
        /* For centered text the extra height extends equally up and down from
           center, but descenders only hang below — shift the center down by
           half the inflation so the entire extra goes to the bottom. Width
           inflation is symmetric, which is correct for both pivot rules
           (centered pivots on x; top-left rects need the left shift). */
        if (l.centered) probe.y = l.y + desc / 2;
        else probe.x = l.x - side;
      }
      return _rotatedCorners(probe).some(([x, y]) =>
        x < pa.x - TOL || x > pa.x + pa.w + TOL || y < pa.y - TOL || y > pa.y + pa.h + TOL);
    });
    if (!cands.length) return out;

    /* Stage 2 — alpha test of the actual ink, using the same painters as the
       live canvas (applyTransform + paintLayer). 640 px scan (was 320): at 320
       one scan pixel ≈ 3 canvas px, so a thin script tail crossing the edge
       quantized to ≤1 px and slipped under the flicker threshold. */
    const cw = mImg?.offsetWidth || canvas.offsetWidth || 1000;
    const ch = mImg?.offsetHeight || canvas.offsetHeight || 1000;
    const sc = Math.min(1, 640 / Math.max(cw, ch, 1));
    const W = Math.max(1, Math.round(cw * sc)), H = Math.max(1, Math.round(ch * sc));
    if (!_clipInkCnv || _clipInkCnv.width !== W || _clipInkCnv.height !== H) {
      _clipInkCnv = document.createElement('canvas');
      _clipInkCnv.width = W; _clipInkCnv.height = H;
    }
    const c = _clipInkCnv.getContext('2d', { willReadFrequently: true });
    c.setTransform(1, 0, 0, 1, 0, 0); c.clearRect(0, 0, W, H);
    c.setTransform(sc, 0, 0, sc, 0, 0);
    cands.forEach(l => {
      c.save();
      try {
        const _clip = maskClipFor(l, layers(), b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
        if (_clip) c.clip(_clip);
        applyTransform(c, l);
        /* Tint stripped for the probe: paintLayer's tint path composites via
           main-canvas DPR coordinates (setTransform bypass), which would land
           at the wrong spot on this scaled offscreen. Alpha — the only thing
           we read — is identical with or without tint. */
        const probe = (l.tintColor && l.tintColor !== 'none') ? { ...l, tintColor: null } : l;
        paintLayer(c, probe, true);
      } catch (e) { /* non-fatal: a failed paint just skips this layer */ }
      c.restore();
    });
    let d;
    try {
      d = c.getImageData(0, 0, W, H).data;
    } catch (e) {
      /* CORS-tainted image — fall back to bbox-derived edges + boxes (conservative) */
      const boxes = {};
      cands.forEach(l => {
        const cs = _rotatedCorners(l);
        const bx1 = Math.min(...cs.map(p => p[0])), bx2 = Math.max(...cs.map(p => p[0]));
        const by1 = Math.min(...cs.map(p => p[1])), by2 = Math.max(...cs.map(p => p[1]));
        const grow = (s, b) => {
          const e = boxes[s];
          boxes[s] = e ? { x1:Math.min(e.x1,b.x1), y1:Math.min(e.y1,b.y1), x2:Math.max(e.x2,b.x2), y2:Math.max(e.y2,b.y2) } : b;
        };
        if (bx1 < pa.x - TOL)        { out.left = true;   grow('left',   { x1:bx1, y1:by1, x2:pa.x, y2:by2 }); }
        if (bx2 > pa.x + pa.w + TOL) { out.right = true;  grow('right',  { x1:pa.x+pa.w, y1:by1, x2:bx2, y2:by2 }); }
        if (by1 < pa.y - TOL)        { out.top = true;    grow('top',    { x1:bx1, y1:by1, x2:bx2, y2:pa.y }); }
        if (by2 > pa.y + pa.h + TOL) { out.bottom = true; grow('bottom', { x1:bx1, y1:pa.y+pa.h, x2:bx2, y2:by2 }); }
      });
      out._boxes = boxes;
      return out;
    }
    const x1 = (pa.x - SCAN_TOL) * sc, x2 = (pa.x + pa.w + SCAN_TOL) * sc;
    const y1 = (pa.y - SCAN_TOL) * sc, y2 = (pa.y + pa.h + SCAN_TOL) * sc;
    const counts = { top:0, right:0, bottom:0, left:0 };
    /* Per-side extents of the OUTSIDE ink (scan px) — the tint patches hug
       exactly this region, not the layer's bounding box */
    const ext = {};
    const acc = (s, px, py) => {
      const e = ext[s];
      if (!e) { ext[s] = { x1:px, y1:py, x2:px, y2:py }; return; }
      if (px < e.x1) e.x1 = px; if (px > e.x2) e.x2 = px;
      if (py < e.y1) e.y1 = py; if (py > e.y2) e.y2 = py;
    };
    for (let py = 0; py < H; py++) {
      const row = py * W;
      for (let px = 0; px < W; px++) {
        if (d[(row + px) * 4 + 3] <= 8) continue; /* same alpha threshold as the trim/engrave passes */
        if (px < x1) { counts.left++; acc('left', px, py); }
        else if (px > x2) { counts.right++; acc('right', px, py); }
        if (py < y1) { counts.top++; acc('top', px, py); }
        else if (py > y2) { counts.bottom++; acc('bottom', px, py); }
      }
    }
    /* >1 scan pixel required per side — keeps AA fringe from flickering the
       warning. At the 640 px scan a genuine 2 px tail overlap yields several
       pixels, so real text leg/tail crossings always clear this bar. */
    out.top = counts.top > 1; out.right = counts.right > 1;
    out.bottom = counts.bottom > 1; out.left = counts.left > 1;
    out._counts = counts;
    /* Scan-space extents → CSS px, padded one scan-pixel for quantization */
    const pad = Math.ceil(1 / sc);
    out._boxes = {};
    ['top', 'right', 'bottom', 'left'].forEach(s => {
      if (!out[s] || !ext[s]) return;
      out._boxes[s] = {
        x1: ext[s].x1 / sc - pad, y1: ext[s].y1 / sc - pad,
        x2: (ext[s].x2 + 1) / sc + pad, y2: (ext[s].y2 + 1) / sc + pad,
      };
    });

    /* Backgrounds count by GEOMETRY, not just painted alpha. A backdrop is the
       printed footprint of its group even where the probe reads no ink — its
       display raster may still be rasterizing right after placement, and thin
       slivers past the edge can quantize below the >1-px threshold at the 320px
       scan. Test the backdrop's VISIBLE content box (the same box selectionBounds
       and the Fit button use; masks use the full frame box) against the pa and
       force the crossed edges on. Rotated backdrops fall back to the full bbox
       via _rotatedCorners (conservative). */
    vis.forEach(l => {
      if (!l.isBackdrop) return;
      const bb = { x: l.centered ? l.x - l.w / 2 : l.x, y: l.centered ? l.y - l.h / 2 : l.y, w: l.w, h: l.h };
      const c0 = (!l._maskMarkup && !l.rotation && l._content) ? l._content : { x: 0, y: 0, w: 1, h: 1 };
      const probe = { ...l, centered: false, x: bb.x + c0.x * bb.w, y: bb.y + c0.y * bb.h, w: c0.w * bb.w, h: c0.h * bb.h };
      const cs = _rotatedCorners(probe);
      const bx1 = Math.min(...cs.map(p => p[0])), bx2 = Math.max(...cs.map(p => p[0]));
      const by1 = Math.min(...cs.map(p => p[1])), by2 = Math.max(...cs.map(p => p[1]));
      const grow = (s, b) => {
        const e = out._boxes[s];
        out._boxes[s] = e ? { x1: Math.min(e.x1, b.x1), y1: Math.min(e.y1, b.y1), x2: Math.max(e.x2, b.x2), y2: Math.max(e.y2, b.y2) } : b;
      };
      if (bx1 < pa.x - TOL)        { out.left = true;   grow('left',   { x1: bx1, y1: by1, x2: pa.x, y2: by2 }); }
      if (bx2 > pa.x + pa.w + TOL) { out.right = true;  grow('right',  { x1: pa.x + pa.w, y1: by1, x2: bx2, y2: by2 }); }
      if (by1 < pa.y - TOL)        { out.top = true;    grow('top',    { x1: bx1, y1: by1, x2: bx2, y2: pa.y }); }
      if (by2 > pa.y + pa.h + TOL) { out.bottom = true; grow('bottom', { x1: bx1, y1: pa.y + pa.h, x2: bx2, y2: by2 }); }
    });
    return out;
  }

  function _applyClipWarn(edges) {
    const el = _clipWarnEls();
    if (!el) return;
    const any = edges.top || edges.right || edges.bottom || edges.left;
    const prevAny = _clipEdges.top || _clipEdges.right || _clipEdges.bottom || _clipEdges.left;
    _clipEdges = edges;
    el.classList.toggle('is-on', !!any && !S.previewMode);
    const pa = S.pa, boxes = edges._boxes || {};
    /* Tint patches: rest flush against the OUTSIDE face of the crossed edge
       and cover only the clipped ink's extent — not the whole outside strip
       and not the layer's bounding box. All coords are container-relative
       (container == print-area rect), so left/top of 0 is the pa corner. */
    ['top', 'right', 'bottom', 'left'].forEach(s => {
      el.classList.toggle('is-' + s, !!edges[s]);
      const t = el.querySelector('.dt-clip-warn__tint--' + s);
      const b = edges[s] && boxes[s];
      if (!t) return;
      if (!b) { t.style.cssText = ''; return; }
      let css = '';
      if (s === 'right') {
        css = `left:${pa.w}px;width:${Math.max(2, b.x2 - (pa.x + pa.w))}px;` +
              `top:${b.y1 - pa.y}px;height:${Math.max(2, b.y2 - b.y1)}px`;
      } else if (s === 'left') {
        const w = Math.max(2, pa.x - b.x1);
        css = `left:${-w}px;width:${w}px;top:${b.y1 - pa.y}px;height:${Math.max(2, b.y2 - b.y1)}px`;
      } else if (s === 'top') {
        const h = Math.max(2, pa.y - b.y1);
        css = `top:${-h}px;height:${h}px;left:${b.x1 - pa.x}px;width:${Math.max(2, b.x2 - b.x1)}px`;
      } else {
        css = `top:${pa.h}px;height:${Math.max(2, b.y2 - (pa.y + pa.h))}px;` +
              `left:${b.x1 - pa.x}px;width:${Math.max(2, b.x2 - b.x1)}px`;
      }
      t.style.cssText = css;
    });
    /* Pill rides just beyond the tint patch on the most-overflowed edge */
    const pill = el.querySelector('.dt-clip-warn__pill');
    if (any && pill) {
      const cnt = edges._counts || {};
      const best = ['right', 'left', 'bottom', 'top']
        .filter(s => edges[s])
        .sort((a, b) => (cnt[b] || 0) - (cnt[a] || 0))[0];
      const b = boxes[best];
      let css = 'display:block;';
      if (b) {
        const midY = (b.y1 + b.y2) / 2 - pa.y, midX = (b.x1 + b.x2) / 2 - pa.x;
        if (best === 'right')  css += `left:${(b.x2 - pa.x) + 8}px;top:${midY}px;transform:translateY(-50%)`;
        if (best === 'left')   css += `right:${pa.w - (b.x1 - pa.x) + 8}px;top:${midY}px;transform:translateY(-50%)`;
        if (best === 'top')    css += `bottom:${pa.h - (b.y1 - pa.y) + 8}px;left:${midX}px;transform:translateX(-50%)`;
        if (best === 'bottom') css += `top:${(b.y2 - pa.y) + 8}px;left:${midX}px;transform:translateX(-50%)`;
      } else {
        css += `left:${pa.w + 8}px;top:50%;transform:translateY(-50%)`;
      }
      pill.style.cssText = css;
      /* Clamp within the viewport — a pill anchored near a screen-edge
         overflow could render partly off screen (unreadable). Measured
         after layout; the correction is applied as margins so it composes
         with the translate() centering. Inside the mobile zoom transform,
         1 container px == S.zoom screen px, so screen-space deltas are
         divided back by the zoom factor. */
      requestAnimationFrame(() => {
        if (!pill.style.cssText || !el.classList.contains('is-on')) return;
        const r = pill.getBoundingClientRect();
        if (!r.width) return;
        const vw = window.innerWidth, vh = window.innerHeight, PAD = 4;
        const z = (isMobile() && S.zoom !== 1) ? S.zoom : 1;
        let dx = 0, dy = 0;
        if (r.left < PAD) dx = PAD - r.left;
        else if (r.right > vw - PAD) dx = (vw - PAD) - r.right;
        if (r.top < PAD) dy = PAD - r.top;
        else if (r.bottom > vh - PAD) dy = (vh - PAD) - r.bottom;
        if (dx || dy) pill.style.cssText += `;margin-left:${(dx / z).toFixed(1)}px;margin-top:${(dy / z).toFixed(1)}px`;
      });
    } else if (pill) {
      pill.style.cssText = '';
    }
    if (any) syncClipWarnGeometry();
    /* Announce state transitions once via the polite live region (WCAG 4.1.3) */
    if (any && !prevAny) announceStatus('Part of your design is outside the print area and will be cropped.');
    else if (!any && prevAny) announceStatus('Design is back inside the print area.');
  }

  function _runClipCheck() {
    _clipCheckT = Date.now();
    if (S.previewMode) { if (_clipWarnEl) _clipWarnEl.classList.remove('is-on'); return; }
    _applyClipWarn(_computeInkOverflow());
  }

  /* Leading-edge throttle (live feedback mid-drag, ~150ms cadence) plus a
     trailing run so the final drop position is always evaluated exactly. */
  function scheduleClipCheck() {
    const now = Date.now();
    if (now - _clipCheckT > 150) { _runClipCheck(); return; }
    clearTimeout(_clipCheckTimer);
    _clipCheckTimer = setTimeout(_runClipCheck, 160);
  }

  /* ══════════════════════════════════════════════════════════════════
   * OVERLAY (Engrave texture)
   * ══════════════════════════════════════════════════════════════════ */
  /* CRO #22 — texture cache: every overlay loads through here exactly once.
     If the image is already decoded, the ready callback runs SYNCHRONOUSLY,
     so switching to engrave never flashes the flat-gradient fallback. */
  const DT_STEEL_FALLBACK = 'https://cdn.shopify.com/s/files/1/0537/7101/0220/files/stainless-steel-overlay.png?v=1772250505';
  const _texCache = {};
  function overlayImage(src) {
    let img = _texCache[src];
    if (!img) {
      img = _texCache[src] = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
    }
    return img;
  }

  /* Warm the textures the moment the tool boots (engrave products only) so
     they're decoded long before the first engrave render needs them. */
  function preloadEngraveTextures() {
    if (!CFG.hasEngrave) return;
    const ov = CFG.overlays?.[S.variantId];
    if (ov?.image) overlayImage(ov.image);
    overlayImage(DT_STEEL_FALLBACK);
  }

  function loadOverlay() {
    S.overlayImg = null; S.overlayStyle = '';
    if (S.method !== 'engrave') { render(); return; }
    const ov = CFG.overlays[S.variantId];
    const useTex = (src, style, onFail) => {
      const img = overlayImage(src);
      if (img.complete) {
        if (img.naturalWidth) { S.overlayImg = img; S.overlayStyle = style; render(); }
        else onFail();   /* cached as failed */
        return;
      }
      img.addEventListener('load', () => {
        if (S.method !== 'engrave') return;
        S.overlayImg = img; S.overlayStyle = style; render();
      }, { once: true });
      img.addEventListener('error', onFail, { once: true });
      render();   /* paint now with the gradient stand-in; texture pops in on load */
    };
    const fallback = () => useTex(DT_STEEL_FALLBACK, 'Stainless Steel', () => { S.overlayImg = null; render(); });
    if (ov?.image) useTex(ov.image, ov.style, fallback);
    else fallback();
  }

  /* ══════════════════════════════════════════════════════════════════
   * RENDER
   * ══════════════════════════════════════════════════════════════════ */
  function render() {
    if (!ctx) return;
    syncGroupedBackdrops(); /* keep linked backdrops locked to their photo (Phase 2b) */
    const w = mImg?.offsetWidth || canvas.width || 1000;
    const h = mImg?.offsetHeight || canvas.height || 1000;
    ctx.clearRect(0, 0, w, h);
    if (S.method === 'engrave') renderEngrave(); else renderPrint();
    drawSnapLines();
    repositionQuickZoom();
    scheduleCtxBarPlace(); /* UX-Aug15 rev 3: floating selection toolbar follows the layer */
    scheduleClipCheck(); /* crop warning: ink outside the print area */
  }

  /* ============================================================
     PRINT SIMULATION (preview-only; production export untouched)
     Sub-slice 3a — Surface light. Sample the product mockup's own
     luminance under the print area and modulate the artwork so the
     print catches the bottle's real highlights and shadows:
       diffuse = clamped MULTIPLY of low-freq luminance (shadows)
       specular = additive high-freq highlight (glints)
     All compositing is confined to the artwork's alpha shape.
     ============================================================ */
  let _lightCache = {};   /* key = variantId:view → {diffuse,specular} | null (CORS-tainted) */
  let _simTemp = null;    /* reused backing-size scratch canvas (light) */
  let _warpTemp = null;   /* reused backing-size scratch canvas (curvature snapshot) */
  /* UX-Aug20: when set, sim functions target this offscreen canvas/ctx/rs
     instead of the interactive canvas. Used by mockupPng to apply sim
     effects to review-modal captures without touching the live canvas. */
  var _simTarget = null;

  function printSimOn() {
    if (S._printSim === true) return true;    /* explicit override on */
    if (S._printSim === false) return false;  /* explicit override off */
    /* UX-Aug20: sim effects are now review-only — applied via mockupPng(withSim)
       for the item-review modal. Disabled during interactive editing on all
       devices (desktop + mobile) to keep the editing canvas clean/fast. */
    return false;
  }

  /* Small separable-ish box blur over a tiny luminance grid (runs once per
     variant/view, then cached — never per-frame). */
  function boxBlurGrid(src, w, h, r) {
    const out = new Float32Array(w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let acc = 0, n = 0;
      for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
        const xx = x + dx, yy = y + dy;
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
        acc += src[yy * w + xx]; n++;
      }
      out[y * w + x] = acc / n;
    }
    return out;
  }

  /* Build (and cache) the surface-light maps for the current variant+view by
     reading the mockup pixels under the print area. Returns null if the mockup
     image is cross-origin tainted (graceful no-op). */
  function getSurfaceLight() {
    const key = S.variantId + ':' + S.view;
    if (key in _lightCache) return _lightCache[key];
    let result = null;
    try {
      if (mImg && mImg.complete && mImg.naturalWidth && S.pa) {
        const pa = S.pa, G = 48;
        const ow = mImg.offsetWidth || mImg.naturalWidth, oh = mImg.offsetHeight || mImg.naturalHeight;
        const kx = mImg.naturalWidth / ow, ky = mImg.naturalHeight / oh;
        const grid = document.createElement('canvas'); grid.width = G; grid.height = G;
        const gctx = grid.getContext('2d', { willReadFrequently: true });
        gctx.drawImage(mImg, pa.x * kx, pa.y * ky, pa.w * kx, pa.h * ky, 0, 0, G, G);
        const d = gctx.getImageData(0, 0, G, G).data; /* throws if tainted */
        const lum = new Float32Array(G * G);
        let sum = 0;
        for (let i = 0; i < G * G; i++) {
          const L = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
          lum[i] = L; sum += L;
        }
        const mean = sum / (G * G) || 1;
        const low = boxBlurGrid(lum, G, G, Math.max(2, Math.round(G / 8)));
        const diff = document.createElement('canvas'); diff.width = G; diff.height = G;
        const spec = document.createElement('canvas'); spec.width = G; spec.height = G;
        const dctx = diff.getContext('2d'), sctx = spec.getContext('2d');
        const dimg = dctx.createImageData(G, G), simg = sctx.createImageData(G, G);
        for (let i = 0; i < G * G; i++) {
          /* diffuse: normalize low-freq to mean, clamp; multiply only darkens (cap 1.0) */
          let m = low[i] / mean; m = Math.max(0.80, Math.min(1.0, m));
          const v = Math.round(m * 255);
          dimg.data[i * 4] = v; dimg.data[i * 4 + 1] = v; dimg.data[i * 4 + 2] = v; dimg.data[i * 4 + 3] = 255;
          /* specular: sharp highlight = lum above the low-freq base */
          const s = Math.max(0, lum[i] - low[i]);
          const a = Math.min(255, Math.round(s * 1.4 * 0.30)); /* ~0.30 strength */
          simg.data[i * 4] = 255; simg.data[i * 4 + 1] = 255; simg.data[i * 4 + 2] = 255; simg.data[i * 4 + 3] = a;
        }
        dctx.putImageData(dimg, 0, 0); sctx.putImageData(simg, 0, 0);
        result = { diffuse: diff, specular: spec };
      }
    } catch (_) { result = null; } /* tainted mockup → no surface light */
    _lightCache[key] = result;
    return result;
  }

  /* Apply the cached surface-light maps to the artwork already on `ctx`.
     Works in IDENTITY (backing px) transform; masks every pass to the
     artwork's own alpha via destination-in so light never spills past it. */
  function applySurfaceLight(pa) {
    const light = getSurfaceLight();
    if (!light) return;
    const _cv = _simTarget ? _simTarget.canvas : canvas;  /* UX-Aug20 */
    const _cx = _simTarget ? _simTarget.ctx : ctx;
    const rs = _simTarget ? _simTarget.rs : getCanvasRenderScale();
    const W = _cv.width, H = _cv.height;
    const px = pa.x * rs, py = pa.y * rs, pw = pa.w * rs, ph = pa.h * rs;
    if (!_simTemp || _simTemp.width !== W || _simTemp.height !== H) {
      _simTemp = document.createElement('canvas'); _simTemp.width = W; _simTemp.height = H;
    }
    const t = _simTemp, tc = t.getContext('2d');
    const maskPass = (mapCanvas) => {
      tc.setTransform(1, 0, 0, 1, 0, 0);
      tc.globalCompositeOperation = 'source-over';
      tc.clearRect(0, 0, W, H);
      tc.imageSmoothingEnabled = true;
      tc.drawImage(mapCanvas, px, py, pw, ph);
      tc.globalCompositeOperation = 'destination-in';
      tc.drawImage(_cv, 0, 0);            /* confine to current artwork shape */
      tc.globalCompositeOperation = 'source-over';
    };
    _cx.save();
    _cx.setTransform(1, 0, 0, 1, 0, 0);
    maskPass(light.diffuse);
    _cx.globalCompositeOperation = 'multiply';
    _cx.drawImage(t, 0, 0);
    maskPass(light.specular);
    _cx.globalCompositeOperation = 'lighter';
    _cx.drawImage(t, 0, 0);
    _cx.globalCompositeOperation = 'source-over';
    _cx.restore();
  }

  /* ---- Sub-slice 3b: Curvature warp (cylindrical foreshortening) ----
     Reads the curvature_diameter metafield (inches). Blank / 0 / absent =
     flat product → no warp (light + finish still run). dtCurve(n) overrides
     for testing before the metafield exists. */
  function curvatureDiameter() {
    if (S._curveOverride != null) return S._curveOverride > 0 ? S._curveOverride : 0;
    const sc = CFG.sideConfigs[S.view] || {};
    const raw = sc.curvatureDiameter != null ? sc.curvatureDiameter : CFG.curvatureDiameter;
    const d = parseFloat(raw);
    return (isFinite(d) && d > 0) ? d : 0;
  }

  /* Warp the flat artwork on `ctx` into cylindrical foreshortening via vertical
     column slices (canvas displacement, not per-pixel JS). Geometry only — the
     surface-light pass supplies the cylinder shading, so we don't double it. */
  function applyCurvature(pa) {
    const diam = curvatureDiameter(); if (!diam) return;
    const phys = SIDE_PHYS[S.view]; if (!phys || !(phys.wInches > 0)) return;
    const _cv = _simTarget ? _simTarget.canvas : canvas;  /* UX-Aug20 */
    const _cx = _simTarget ? _simTarget.ctx : ctx;
    const rs = _simTarget ? _simTarget.rs : getCanvasRenderScale();
    const Pw = pa.w;
    const alpha = phys.wInches / diam;             /* half wrap angle (rad) */
    if (!(alpha > 0.03)) return;                   /* negligible curve → skip */
    const Rpx = (diam * Pw) / (2 * phys.wInches);  /* cylinder radius in CSS px */
    const Xmax = Rpx * Math.sin(alpha);            /* visible compressed half-width */
    const cx = pa.x + pa.w / 2;
    const W = _cv.width, H = _cv.height;
    if (!_warpTemp || _warpTemp.width !== W || _warpTemp.height !== H) {
      _warpTemp = document.createElement('canvas'); _warpTemp.width = W; _warpTemp.height = H;
    }
    const snap = _warpTemp, sctx = snap.getContext('2d');
    sctx.setTransform(1, 0, 0, 1, 0, 0); sctx.globalCompositeOperation = 'source-over';
    sctx.clearRect(0, 0, W, H);
    sctx.drawImage(_cv, 0, 0);                  /* snapshot the flat artwork (backing px) */
    _cx.save();
    _cx.beginPath(); _cx.rect(pa.x, pa.y, pa.w, pa.h); _cx.clip();
    _cx.clearRect(pa.x, pa.y, pa.w, pa.h);
    _cx.imageSmoothingEnabled = true;
    const sy = pa.y * rs, sh = pa.h * rs, step = 1;
    for (let xd = -Xmax; xd < Xmax; xd += step) {
      const ratio = xd / Rpx; if (ratio < -1 || ratio > 1) continue;
      const xsrc = cx + Rpx * Math.asin(ratio);    /* flat source column for this dest column */
      _cx.drawImage(snap, xsrc * rs, sy, step * rs, sh, cx + xd, pa.y, step + 0.6, pa.h);
    }
    _cx.restore();
  }

  /* ---- Sub-slice 3c: Finish (method-aware surface character) ----
     UV DTF → glossy diagonal clear-coat sheen (additive). Everything else
     (Flatbed Soft, Vinyl, Cylindrical) → matte fine grain. Masked to artwork. */
  let _grainTile = null;
  function getGrainTile() {
    if (_grainTile) return _grainTile;
    const N = 128, c = document.createElement('canvas'); c.width = N; c.height = N;
    const cx = c.getContext('2d'), img = cx.createImageData(N, N);
    for (let i = 0; i < N * N; i++) {
      const g = Math.max(0, Math.min(255, 128 + (Math.random() * 2 - 1) * 38));
      img.data[i * 4] = g; img.data[i * 4 + 1] = g; img.data[i * 4 + 2] = g; img.data[i * 4 + 3] = 255;
    }
    cx.putImageData(img, 0, 0); _grainTile = c; return c;
  }

  /* Determine preview finish type from the side's decoration method.
     'gradient' = diagonal gloss overlay (UV DTF, Cylindrical Print)
     'grain'    = subtle noise texture (Flatbed Print, Vinyl Print)
     Falls back to 'grain' for unknown methods. Engrave sides skip applyFinish. */
  function printFinishType(sideSlug) {
    const method = (CFG.sideConfigs[sideSlug]?.decorationMethod || '').toLowerCase();
    if (method.includes('uv dtf') || method.includes('cylindrical')) return 'gradient';
    if (method.includes('flatbed') || method.includes('vinyl')) return 'grain';
    return 'grain';
  }

  function applyFinish(pa) {
    const _cv = _simTarget ? _simTarget.canvas : canvas;  /* UX-Aug20 */
    const _cx = _simTarget ? _simTarget.ctx : ctx;
    const rs = _simTarget ? _simTarget.rs : getCanvasRenderScale();
    const W = _cv.width, H = _cv.height;
    const px = pa.x * rs, py = pa.y * rs, pw = pa.w * rs, ph = pa.h * rs;
    if (!_simTemp || _simTemp.width !== W || _simTemp.height !== H) {
      _simTemp = document.createElement('canvas'); _simTemp.width = W; _simTemp.height = H;
    }
    const t = _simTemp, tc = t.getContext('2d');
    const buildMasked = (paint) => {
      tc.setTransform(1, 0, 0, 1, 0, 0); tc.globalCompositeOperation = 'source-over';
      tc.clearRect(0, 0, W, H); paint(tc);
      tc.globalCompositeOperation = 'destination-in'; tc.drawImage(_cv, 0, 0);
      tc.globalCompositeOperation = 'source-over';
    };
    _cx.save(); _cx.setTransform(1, 0, 0, 1, 0, 0);
    if (printFinishType(S.view) === 'gradient') {
      buildMasked((c) => {
        const g = c.createLinearGradient(px, py, px + pw, py + ph);
        g.addColorStop(0, 'rgba(255,255,255,0)');
        g.addColorStop(0.44, 'rgba(255,255,255,0)');
        g.addColorStop(0.50, 'rgba(255,255,255,0.20)');
        g.addColorStop(0.56, 'rgba(255,255,255,0)');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        c.fillStyle = g; c.fillRect(px, py, pw, ph);
      });
      _cx.globalCompositeOperation = 'lighter'; _cx.drawImage(t, 0, 0);
    } else {
      buildMasked((c) => { c.fillStyle = c.createPattern(getGrainTile(), 'repeat'); c.fillRect(px, py, pw, ph); });
      _cx.globalCompositeOperation = 'overlay'; _cx.globalAlpha = 0.06; _cx.drawImage(t, 0, 0); _cx.globalAlpha = 1;
    }
    _cx.globalCompositeOperation = 'source-over'; _cx.restore();
  }

  /* ---- Sub-slice 3d: Engrave etched edge ----
     Derives an inner bevel from the artwork's own alpha: a dark rim on the
     top-left inner edge and a light rim on the bottom-right, reading as a
     recess cut into the surface. Confined to the artwork shape. */
  function applyEtchEdge(pa) {
    const _cv = _simTarget ? _simTarget.canvas : canvas;  /* UX-Aug20 */
    const _cx = _simTarget ? _simTarget.ctx : ctx;
    const rs = _simTarget ? _simTarget.rs : getCanvasRenderScale();
    const W = _cv.width, H = _cv.height;
    if (!_simTemp || _simTemp.width !== W || _simTemp.height !== H) {
      _simTemp = document.createElement('canvas'); _simTemp.width = W; _simTemp.height = H;
    }
    const t = _simTemp, tc = t.getContext('2d');
    const off = Math.max(1, Math.round(rs * 0.2));
    const rim = (dx, dy, color, gco, alpha) => {
      tc.setTransform(1, 0, 0, 1, 0, 0); tc.globalCompositeOperation = 'source-over';
      tc.clearRect(0, 0, W, H);
      tc.drawImage(_cv, 0, 0);                 /* artwork shape */
      tc.globalCompositeOperation = 'destination-out';
      tc.drawImage(_cv, dx, dy);               /* subtract shifted copy → edge band */
      tc.globalCompositeOperation = 'source-in';
      tc.fillStyle = color; tc.fillRect(0, 0, W, H);
      tc.globalCompositeOperation = 'source-over';
      _cx.save(); _cx.setTransform(1, 0, 0, 1, 0, 0);
      _cx.globalCompositeOperation = gco; _cx.globalAlpha = alpha; _cx.drawImage(t, 0, 0);
      _cx.globalAlpha = 1; _cx.globalCompositeOperation = 'source-over'; _cx.restore();
    };
    rim(off, off, '#000', 'multiply', 0.26);      /* shadow on top-left inner edge */
    rim(-off, -off, '#fff', 'lighter', 0.16);     /* highlight on bottom-right inner edge */
  }

  function renderPrint() {
    const pa = S.pa;
    /* Clip layer content to the print/decoration area */
    ctx.save();
    ctx.beginPath(); ctx.rect(pa.x, pa.y, pa.w, pa.h); ctx.clip();
    drawSwatchGlow(); /* UX-Aug20: swatch colour glow behind mono/template layers */
    layers().forEach(l => {
      if (l.visible === false) return;
      ctx.save();
      const _clip = maskClipFor(l, layers(), b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
      if (_clip) ctx.clip(_clip);
      applyTransform(ctx, l); paintLayer(ctx, l, false); ctx.restore();
    });
    ctx.restore();
    /* Surface-light sim — preview only, skipped mid-interaction for smooth drags.
       Curvature first (geometry), then light (samples the mockup at the curved
       screen positions and masks to the warped artwork). */
    if (printSimOn() && !S.iact && layers().some(l => l.visible !== false)) { applyCurvature(pa); applySurfaceLight(pa); applyFinish(pa); }
    /* Selection handles draw OUTSIDE the clip so they're always visible */
    if (!S.previewMode) {
      const sl = selLayer();
      if (sl) { ctx.save(); drawSelection(sl); ctx.restore(); }
      if (S.showDims) { ctx.save(); drawAreaDims(); ctx.restore(); }
    }
  }

  /* Reused engrave scratch canvases + downscaled-overlay cache. Allocating two
     full-backing-size canvases (and rescaling a full-resolution texture) on every
     drag frame made engrave interaction crawl on mobile; these persist instead. */
  let _engMask = null, _engOver = null, _ovScaled = null, _ovScaledKey = '';
  function scaledOverlay(tw, th) {
    if (!S.overlayImg?.naturalWidth || tw < 1 || th < 1) return null;
    const key = (S.overlayImg.src || '') + ':' + tw + 'x' + th;
    if (_ovScaled && _ovScaledKey === key) return _ovScaled;
    const cnv = document.createElement('canvas'); cnv.width = tw; cnv.height = th;
    cnv.getContext('2d').drawImage(S.overlayImg, 0, 0, tw, th);
    _ovScaled = cnv; _ovScaledKey = key;
    return cnv;
  }

  function renderEngrave() {
    const pa = S.pa;
    const rs = getCanvasRenderScale();
    const cw = mImg?.offsetWidth || 1000, ch = mImg?.offsetHeight || 1000;
    const W = canvas.width, H = canvas.height;
    const vis = layers().filter(l => l.visible !== false);

    /* While actively dragging/resizing, the steel mask + overlay + destination-in
       composite is several full-canvas passes per frame — the cause of the engrave
       drag lag on mobile. Skip it during interaction and draw the artwork directly;
       the full engraved look returns the moment the finger lifts (render() at rest). */
    if (S.iact) {
      ctx.save(); ctx.beginPath(); ctx.rect(pa.x, pa.y, pa.w, pa.h); ctx.clip();
      vis.forEach(l => {
        ctx.save();
        const _clip = maskClipFor(l, vis, b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
        if (_clip) ctx.clip(_clip);
        applyTransform(ctx, l); paintLayer(ctx, l, false); ctx.restore();
      });
      ctx.restore();
      if (!S.previewMode) {
        const sl = selLayer();
        if (sl) { ctx.save(); drawSelection(sl); ctx.restore(); }
        if (S.showDims) { ctx.save(); drawAreaDims(); ctx.restore(); }
      }
      return;
    }

    /* Mask canvas (reused) — layers rendered as solid black shapes, clipped to print area */
    if (!_engMask || _engMask.width !== W || _engMask.height !== H) { _engMask = document.createElement('canvas'); _engMask.width = W; _engMask.height = H; }
    const mc = _engMask, mctx = mc.getContext('2d');
    mctx.setTransform(1, 0, 0, 1, 0, 0); mctx.clearRect(0, 0, W, H); mctx.setTransform(rs, 0, 0, rs, 0, 0);
    mctx.save(); mctx.beginPath(); mctx.rect(pa.x, pa.y, pa.w, pa.h); mctx.clip();
    /* v2.11.0: inverted-plate layers composite with XOR so the preview
       matches the DXF's even-odd parity — a layer sitting ON the plate
       becomes an unengraved (dark) hole instead of vanishing into the
       steel union. Non-inverted layers keep source-over (matches the
       v2.9.30 production union). Plates paint LAST so XOR applies against
       the union of everything else. */
    const _invPlates = vis.filter(l => l._engraveInverted);
    vis.filter(l => !l._engraveInverted).forEach(l => {
      mctx.save();
      const _clip = maskClipFor(l, vis, b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
      if (_clip) mctx.clip(_clip);
      applyTransform(mctx, l); paintLayer(mctx, l, true); mctx.restore();
    });
    _invPlates.forEach(l => {
      mctx.save();
      mctx.globalCompositeOperation = 'xor';
      const _clip = maskClipFor(l, vis, b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
      if (_clip) mctx.clip(_clip);
      applyTransform(mctx, l); paintLayer(mctx, l, true); mctx.restore();
    });
    mctx.restore();

    /* Steel overlay canvas (reused) — downscaled texture clipped to print area */
    if (!_engOver || _engOver.width !== W || _engOver.height !== H) { _engOver = document.createElement('canvas'); _engOver.width = W; _engOver.height = H; }
    const oc = _engOver, octx = oc.getContext('2d');
    octx.setTransform(1, 0, 0, 1, 0, 0); octx.clearRect(0, 0, W, H); octx.setTransform(rs, 0, 0, rs, 0, 0);
    octx.save(); octx.beginPath(); octx.rect(pa.x, pa.y, pa.w, pa.h); octx.clip();
    const ovTex = scaledOverlay(Math.round(pa.w * rs), Math.round(pa.h * rs));
    if (ovTex) octx.drawImage(ovTex, pa.x, pa.y, pa.w, pa.h);
    else { const g = octx.createLinearGradient(pa.x, pa.y, pa.x+pa.w, pa.y+pa.h); g.addColorStop(0,'#d4d8dc'); g.addColorStop(0.5,'#c0c6cb'); g.addColorStop(1,'#c4c9cc'); octx.fillStyle = g; octx.fillRect(pa.x, pa.y, pa.w, pa.h); }
    octx.restore();

    /* Composite: keep steel only where mask has content */
    octx.globalCompositeOperation = 'destination-in'; octx.drawImage(mc, 0, 0, cw, ch); octx.globalCompositeOperation = 'source-over';
    ctx.drawImage(oc, 0, 0, cw, ch);

    /* Engrave sim — preview only, at rest: shared curvature + surface-light, then
       an etched-edge for recessed depth. No gloss for laser engraving. */
    if (printSimOn() && !S.iact && vis.length) { applyCurvature(pa); applySurfaceLight(pa); applyEtchEdge(pa); }

    const sl = selLayer(); if (sl) drawSelection(sl);
    if (S.showDims) drawAreaDims();
  }

  function applyTransform(c, l) {
    const cx = l.centered ? l.x : l.x + l.w/2, cy = l.centered ? l.y : l.y + l.h/2;
    /* F2 — entrance spring: scale eases 0.62→1 over 260ms (easeOutBack) */
    if (l._introT) {
      const tt = (performance.now() - l._introT) / 260;
      if (tt >= 1) { delete l._introT; }
      else {
        const k = 1.70158, e2 = tt - 1;
        const sc = 0.62 + 0.38 * (e2 * e2 * ((k + 1) * e2 + k) + 1);
        c.translate(cx, cy); c.scale(sc, sc); c.translate(-cx, -cy);
      }
    }
    c.translate(cx, cy);
    if (l.rotation) c.rotate((l.rotation * Math.PI) / 180);
    if (l.flipH) c.scale(-1, 1); if (l.flipV) c.scale(1, -1);
    c.translate(-cx, -cy);
  }

  function paintLayer(c, l, asMask) {
    if (l.type === 'text') {
      /* Hide text on canvas while inline editing is active. The live engrave
         mask (_engMask) must hide it too, or a steel-textured duplicate
         renders behind the contenteditable (offset further by the curvature
         sim). Export canvases (mockupPng/templateThumbPng) are unaffected. */
      if (l._inlineHidden && (c === ctx || (_engMask && c.canvas === _engMask))) return;
      c.font = fontStr(l); c.fillStyle = asMask ? '#000' : (l.color || '#111');
      c.textBaseline = 'middle'; c.textAlign = 'center';

      /* Multi-line support: split by newlines, draw each line */
      const lines = (l.text || '').split('\n');
      const lineH = (l.fontSize || 36) * (l.lineHeight || 1.25);
      const totalH = lines.length * lineH;
      const startY = l.y - totalH / 2 + lineH / 2;
      lines.forEach((line, i) => {
        c.fillText(line, l.x, startY + i * lineH);
      });

      /* #2: shared ink-accurate metrics (w, h, _inkOffY) */
      measureText(l);
    } else if (l.type === 'image' && !l.img && l._imgFailed) {
      /* Image failed to reload (undo/redo or network issue) — draw a visible placeholder */
      const dx = l.centered ? l.x - l.w/2 : l.x, dy = l.centered ? l.y - l.h/2 : l.y;
      c.save(); c.strokeStyle = '#cc3333'; c.lineWidth = 2; c.setLineDash([6,4]);
      c.strokeRect(dx, dy, l.w, l.h); c.setLineDash([]);
      c.fillStyle = '#cc3333'; c.font = '12px sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText('Image unavailable', dx + l.w/2, dy + l.h/2);
      c.restore();
    } else if (l.type === 'image' && l.img) {
      const dx = l.centered ? l.x - l.w/2 : l.x, dy = l.centered ? l.y - l.h/2 : l.y;
      if (!asMask && l.tintColor && l.tintColor !== 'none' && S.method === 'print') {
        /* Tint via DPR-aware offscreen canvas so the composited result
           doesn't blur when drawn onto the high-DPI main canvas. */
        const rs = getCanvasRenderScale();
        const tw = Math.max(1, Math.ceil(l.w * rs)), th = Math.max(1, Math.ceil(l.h * rs));
        const oc = document.createElement('canvas'); oc.width = tw; oc.height = th;
        const ox = oc.getContext('2d');
        ox.imageSmoothingQuality = 'high';
        ox.drawImage(l.img, 0, 0, tw, th);
        ox.globalCompositeOperation = 'source-atop';
        ox.globalAlpha = 0.75;
        ox.fillStyle = l.tintColor;
        ox.fillRect(0, 0, tw, th);
        /* Draw back at 1:1 pixel coords (bypass the main ctx transform) */
        c.save();
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.imageSmoothingQuality = 'high';
        c.drawImage(oc, Math.round(dx * rs), Math.round(dy * rs), tw, th);
        c.restore();
      } else {
        c.drawImage(l.img, dx, dy, l.w, l.h);
      }
    }
  }

  /* ── Selection outline + corner handles (desktop only) ─────── */
  function drawSelection(l) {
    /* While inline text editing, the contenteditable's coral frame is the
       single frame of truth — also drawing the dashed canvas box produced
       two slightly different rectangles around the same text (confusing). */
    if (l._inlineHidden) return;
    const { x, y, w, h } = selectionBounds(l);
    const p = 4;
    ctx.strokeStyle = '#344e41'; ctx.lineWidth = 1.5; ctx.setLineDash([5, 4]);
    ctx.strokeRect(x - p, y - p, w + p*2, h + p*2);
    ctx.setLineDash([]);

    /* While reframing (unlinked mask), show a faint outline of the photo's full extent
       so the user can see what they're panning/zooming behind the fixed frame. */
    if (!l.isBackdrop && l._groupId) {
      const _bdp = layers().find(b => b.isBackdrop && b._groupId === l._groupId);
      if (_bdp && _bdp._linked === false) {
        const pb = bbox(l);
        ctx.save();
        ctx.strokeStyle = 'rgba(52,78,65,0.30)'; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
        ctx.strokeRect(pb.x, pb.y, pb.w, pb.h);
        ctx.restore(); ctx.setLineDash([]);
      }
    }

    /* Corner handles only on desktop — mobile uses slider */
    if (!isMobile()) {
      const pts = cornerPoints(l);
      Object.values(pts).forEach(([hx, hy]) => {
        ctx.beginPath(); ctx.arc(hx, hy, HANDLE_R, 0, Math.PI * 2);
        ctx.fillStyle = '#fff'; ctx.fill();
        ctx.strokeStyle = '#344e41'; ctx.lineWidth = 2; ctx.stroke();
      });
    }

    /* DPI quality indicator for raster image layers */
    if (l.isRasterUpload && l.img?.naturalWidth && l.w > 0) {
      const effectiveDPI = l.img.naturalWidth / pxToInches(l.w / S.rscale, S.view);
      let badgeColor, badgeText;
      if (effectiveDPI >= 150) { badgeColor = '#3a7d5c'; badgeText = 'Good'; }
      else if (effectiveDPI >= 72) { badgeColor = '#c4870e'; badgeText = 'Low Res'; }
      else { badgeColor = '#c44133'; badgeText = 'Poor Quality'; }

      if (effectiveDPI < 150) {
        const badgeX = x + w + p + 4, badgeY = y - p - 2;
        ctx.font = '600 9px ui-sans-serif, system-ui, sans-serif';
        const bw = ctx.measureText(badgeText).width + 10;
        /* Badge background with white outline for visibility */
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.roundRect(badgeX - 1, badgeY - 12, bw + 2, 16, 4); ctx.fill();
        ctx.fillStyle = badgeColor;
        ctx.beginPath(); ctx.roundRect(badgeX, badgeY - 11, bw, 14, 3); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(badgeText, badgeX + 5, badgeY);
      }
    }

    /* Live physical dimensions label below selection (toggleable) */
    if (S.showDims) {
      const phys = SIDE_PHYS[S.view];
      if (phys) {
        const wIn = pxToInches(w / S.rscale, S.view).toFixed(2);
        const hIn = pxToInches(h / S.rscale, S.view).toFixed(2);
        const dimText = wIn + '" × ' + hIn + '"';
        ctx.font = '600 9px ui-sans-serif, system-ui, sans-serif';
        const tw = ctx.measureText(dimText).width + 10;
        const dx = x + (w - tw) / 2, dy = y + h + p + 14;
        ctx.fillStyle = 'rgba(0,0,0,0.65)';
        ctx.beginPath(); ctx.roundRect(dx, dy - 10, tw, 14, 3); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(dimText, dx + 5, dy);
      }
    }
  }

  /* Subtle print-area dimensions (shown with the measurement toggle) */
  function drawAreaDims() {
    const pa = S.pa; if (!pa) return;
    const phys = SIDE_PHYS[S.view]; if (!phys || !(phys.wInches > 0)) return;
    const fmt = (n) => { const r = Math.round(n * 10) / 10; return (r % 1 === 0 ? r.toFixed(0) : r.toFixed(1)) + '"'; };
    /* UX-Aug20: adaptive colour — white on dark products, forest green on light */
    const _lum = sampleMockupPALum();
    const _dark = _lum !== null && _lum < 0.35;
    const _stroke = _dark ? 'rgba(255,255,255,0.30)' : 'rgba(52,78,65,0.30)';
    const _fill   = _dark ? 'rgba(255,255,255,0.62)' : 'rgba(52,78,65,0.62)';
    ctx.save();
    ctx.strokeStyle = _stroke; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
    ctx.strokeRect(pa.x, pa.y, pa.w, pa.h);
    ctx.setLineDash([]);
    ctx.font = '600 9px ui-sans-serif, system-ui, sans-serif';
    ctx.fillStyle = _fill;
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    ctx.fillText(fmt(phys.wInches), pa.x + pa.w / 2, pa.y + pa.h + 5);
    ctx.save();
    ctx.translate(pa.x - 7, pa.y + pa.h / 2); ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(fmt(phys.hInches), 0, 0);
    ctx.restore();
    ctx.restore();
  }

  /* ── UX-Aug20: swatch-colour blurred glow behind monograms/templates ──
     Paints a soft radial gradient centred on each monogram or template-locked
     layer, tinted with the product's swatch colour. Print mode only.
     Radius = 0.8× the layer's bounding dimension. The gradient feathers from
     ~38% opacity at centre to 0 at the edge, producing a contextual backdrop
     without an actual blur filter (which is expensive on every canvas frame). */
  function drawSwatchGlow() {
    if (S.method !== 'print' || S.previewMode) return;
    const ls = layers();
    const swatchHex = getProductSwatchHex();
    const rgb = hexToRgb(swatchHex); if (!rgb) return;
    const pa = S.pa; if (!pa) return;

    ls.forEach(l => {
      if (l.visible === false) return;
      const isMono = l.isMonogram;
      const isTpl  = !!l._tplLockId && l.type === 'text';
      if (!isMono && !isTpl) return;

      const bx = l.centered ? l.x - l.w / 2 : l.x;
      const by = l.centered ? l.y - l.h / 2 : l.y;
      const cx = bx + l.w / 2;
      const cy = by + l.h / 2;
      const radius = Math.max(l.w, l.h) * 0.8;
      if (radius < 5) return;

      /* Clip to print area so the glow doesn't bleed outside */
      ctx.save();
      ctx.beginPath();
      ctx.rect(pa.x, pa.y, pa.w, pa.h);
      ctx.clip();

      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      grad.addColorStop(0,   `rgba(${rgb.r},${rgb.g},${rgb.b},0.38)`);
      grad.addColorStop(0.5, `rgba(${rgb.r},${rgb.g},${rgb.b},0.18)`);
      grad.addColorStop(1,   `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(pa.x, pa.y, pa.w, pa.h);
      ctx.restore();
    });
  }

  /* 4 corner handles only — proportional resize */
  function cornerPoints(l) {
    const { x, y, w, h } = selectionBounds(l), p = 6;
    return {
      tl: [x - p, y - p],
      tr: [x + w + p, y - p],
      bl: [x - p, y + h + p],
      br: [x + w + p, y + h + p],
    };
  }

  function hitHandle(l, px, py) {
    if (isMobile()) return null;
    if (l && l._noResize && !l._tplLockId) return null;   /* template photo slots are size-locked (group scale still allowed) */
    for (const [name, [hx, hy]] of Object.entries(cornerPoints(l))) {
      if (Math.hypot(px - hx, py - hy) <= HANDLE_HIT) return name;
    }
    return null;
  }

  const HANDLE_CURSORS = {
    tl: 'nwse-resize', tr: 'nesw-resize',
    bl: 'nesw-resize', br: 'nwse-resize',
  };

  function bbox(l) {
    /* #2: text boxes track the glyph ink (vertical offset from the line frame) */
    const oy = (l.type === 'text' && l._inkOffY) ? l._inkOffY : 0;
    return { x: l.centered ? l.x - l.w/2 : l.x, y: (l.centered ? l.y - l.h/2 : l.y) + oy, w: l.w, h: l.h };
  }

  /* Axis-aligned bounds of a box AFTER the layer's rotation, pivoting exactly
     where applyTransform paints (centered layers pivot on x/y, others on their
     rect centre). Returns the box untouched when rotation is 0, so every
     unrotated layer keeps its existing numbers byte-for-byte.
     Why (Brandon, Jul 28 2026): bbox() is rotation-blind, so a vertical
     template line (rotation ±90) drew a WIDE, SHORT selection box around
     text that paints TALL and NARROW — and typing grew the box left/right
     instead of up/down. rotAABB below does the same job for masks but pivots
     on the ink-offset bbox centre; selection has to pivot where the glyphs
     actually turn, hence the separate helper. */
  function rotBox(b, l) {
    const r = ((l && l.rotation) || 0) * Math.PI / 180;
    if (!r) return b;
    const cx = l.centered ? l.x : l.x + (l.w || 0) / 2;
    const cy = l.centered ? l.y : l.y + (l.h || 0) / 2;
    const cos = Math.cos(r), sin = Math.sin(r);
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
    [[b.x, b.y], [b.x + b.w, b.y], [b.x + b.w, b.y + b.h], [b.x, b.y + b.h]].forEach(p => {
      const nx = cx + (p[0] - cx) * cos - (p[1] - cy) * sin;
      const ny = cy + (p[0] - cx) * sin + (p[1] - cy) * cos;
      if (nx < x1) x1 = nx; if (nx > x2) x2 = nx;
      if (ny < y1) y1 = ny; if (ny > y2) y2 = ny;
    });
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  }

  /* For a grouped photo, the visible/anchoring element is the backdrop — masked
     (the medallion) or not (the disc/sunburst/etc.) — so the selection box + handles
     hug the backdrop's box. Falls back to the layer's own bbox for everything else. */
  function selectionBounds(l) {
    /* TEMPLATE LOCK: one selection box around the whole template (except
       while reframing a photo, which needs its own frame). */
    if (l && l._tplLockId && !tplReframing(l) && !l._lockDraggable) {
      const g = (S.byView[S.view] || []).filter(m => m._tplLockId === l._tplLockId && m.visible !== false);
      if (g.length > 1) {
        let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
        g.forEach(m => { const b = rotBox(bbox(m), m); x1 = Math.min(x1, b.x); y1 = Math.min(y1, b.y); x2 = Math.max(x2, b.x + b.w); y2 = Math.max(y2, b.y + b.h); });
        return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
      }
    }
    if (l && l._groupId && !l.isBackdrop && typeof layers === 'function') {
      const bd = layers().find(b => b.isBackdrop && b._groupId === l._groupId);
      if (bd) {
        if (bd._maskMarkup) return bbox(bd); /* mask: the medallion frame */
        if (bd._linked === false) return bbox(l); /* non-mask reframing: handles on the photo itself */
        /* non-mask: union of the photo and the backdrop's VISIBLE content (so full-bleed
           backdrops keep today's box, but disc_pop tightens to disc∪photo). */
        const pb = bbox(l), c = bd._content || { x: 0, y: 0, w: 1, h: 1 };
        const cb = { x: bd.x + c.x * bd.w, y: bd.y + c.y * bd.h, w: c.w * bd.w, h: c.h * bd.h };
        const x1 = Math.min(pb.x, cb.x), y1 = Math.min(pb.y, cb.y);
        const x2 = Math.max(pb.x + pb.w, cb.x + cb.w), y2 = Math.max(pb.y + pb.h, cb.y + cb.h);
        return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
      }
    }
    return rotBox(bbox(l), l);
  }

  /* Rotation-aware axis-aligned bounding box of a layer (for clamping against a mask
     that may be rotated). Returns plain bbox when rotation is 0. */
  function rotAABB(l) {
    const b = bbox(l), rot = (l.rotation || 0) * Math.PI / 180;
    if (!rot) return b;
    const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
    const c = Math.abs(Math.cos(rot)), s = Math.abs(Math.sin(rot));
    const ew = (b.w / 2) * c + (b.h / 2) * s, eh = (b.w / 2) * s + (b.h / 2) * c;
    return { x: cx - ew, y: cy - eh, w: ew * 2, h: eh * 2 };
  }

  /* While reframing (unlinked), keep the photo usable: allow it to shrink to 50% of
     full coverage (gaps allowed below that point per design choice), and keep its
     centre within the mask so it can't be dragged entirely off-frame. Axis-aligned. */
  function clampPhotoCoversMask(photo, bd) {
    if (!photo || !bd) return;
    const mb = rotAABB(bd);
    const minW = mb.w * 0.5, minH = mb.h * 0.5; /* floor: 50% below full coverage */
    let pw = photo.w, ph = photo.h;
    const fNeed = Math.max(minW / pw, minH / ph, 1);
    if (fNeed > 1) {
      const pcx = photo.centered ? photo.x : photo.x + pw / 2, pcy = photo.centered ? photo.y : photo.y + ph / 2;
      pw *= fNeed; ph *= fNeed; photo.w = pw; photo.h = ph;
      if (photo.centered) { photo.x = pcx; photo.y = pcy; } else { photo.x = pcx - pw / 2; photo.y = pcy - ph / 2; }
    }
    const pcx = photo.centered ? photo.x : photo.x + photo.w / 2;
    const pcy = photo.centered ? photo.y : photo.y + photo.h / 2;
    const ncx = Math.max(mb.x, Math.min(mb.x + mb.w, pcx));
    const ncy = Math.max(mb.y, Math.min(mb.y + mb.h, pcy));
    if (ncx !== pcx || ncy !== pcy) {
      if (photo.centered) { photo.x = ncx; photo.y = ncy; }
      else { photo.x = ncx - photo.w / 2; photo.y = ncy - photo.h / 2; }
    }
  }

  /* ══════════════════════════════════════════════════════════════════
   * LAYER HELPERS
   * ══════════════════════════════════════════════════════════════════ */
  let _id = 0;
  const uid = () => 'l' + (++_id) + '_' + Date.now();

  function fontStr(l) {
    return `${l.italic?'italic ':''}${l.bold?'bold ':''}${l.fontSize||36}px '${l.font||S.textFont||'Montserrat'}', sans-serif`;
  }

  function measureText(l) {
    /* #2: the box hugs the glyph INK (actualBoundingBox), not the line frame.
       Drawing is unchanged (lines are still laid out on lineH frames around
       l.y); l._inkOffY records where the ink's centre sits relative to l.y so
       bbox/selection/centering can follow the visible pixels. */
    ctx.save(); ctx.font = fontStr(l); ctx.textBaseline = 'middle';
    const lines = (l.text || '').split('\n');
    const lineH = (l.fontSize || 36) * (l.lineHeight || 1.25);
    const totalH = lines.length * lineH;
    let maxW = 0, inkTop = Infinity, inkBottom = -Infinity;
    lines.forEach((line, i) => {
      const m = ctx.measureText(line);
      const w = (m.actualBoundingBoxLeft !== undefined)
        ? m.actualBoundingBoxLeft + m.actualBoundingBoxRight : m.width;
      if (w > maxW) maxW = w;
      if (m.actualBoundingBoxAscent !== undefined && line.trim() !== '') {
        const yi = -totalH / 2 + lineH / 2 + i * lineH;   /* line anchor rel. to l.y */
        inkTop = Math.min(inkTop, yi - m.actualBoundingBoxAscent);
        inkBottom = Math.max(inkBottom, yi + m.actualBoundingBoxDescent);
      }
    });
    l.w = maxW || 10;
    if (inkTop < inkBottom) {
      l.h = Math.max(4, inkBottom - inkTop);
      l._inkOffY = (inkTop + inkBottom) / 2;
    } else {
      l.h = totalH || (l.fontSize || 36);
      l._inkOffY = 0;
    }
    ctx.restore();
  }

  function pushLayer(l) {
    layers().push(l); S.sel = l.id;
    /* F2 — new elements spring in (honors prefers-reduced-motion) */
    if (!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      l._introT = performance.now();
      const _spin = () => { if (l._introT) { render(); requestAnimationFrame(_spin); } };
      requestAnimationFrame(_spin);
    }
    /* Template slot fill: a pending intent claims the next image layer (set by
       replacePhotoPlaceholder; survives the engrave AI conversion pipeline). */
    if (S._slotIntent && l.type === 'image' && !l.isBackdrop) {
      var _si = S._slotIntent; S._slotIntent = null;
      if (Date.now() - _si.t < 300000) {
        try { settleIntoSlot(l, _si.slot); } catch (e) { DEBUG && console.warn('[dt] slot settle failed', e); }
      }
    }
    /* CRO #6 — one-time edit-affordance hint on the first text layer */
    if (l.type === 'text' && !S._textEditHinted && !localStorage.getItem('dt_hint_textedit')) {
      S._textEditHinted = true;
      try { localStorage.setItem('dt_hint_textedit', '1'); } catch (_) {}
      setTimeout(() => showTip('textedit', (isMobile() ? 'Double-tap' : 'Double-click') + ' your text any time to edit it.', null), 350);
    }
    autoClosePanelOnAdd();
    pushHistory(); updateLayersPanel(); forcePropsRebuild(); updateCartBtn(); render();
    syncMobileSelection();
    /* Zoom to frame print area after adding an element */
    if (isMobile()) requestZoomToPA(50, true);
    updateLayerBadge();
    if (l.img && !l.img.complete) {
      l.img.onload = () => { render(); forcePropsRebuild(); };
    }
  }

  function deleteLayer(id) {
    /* TEMPLATE LOCK: deleting any member removes the whole template. */
    const _t0 = layers().find(x => x.id === id);
    if (_t0 && _t0._tplLockId) {
      const arr = layers();
      for (let i = arr.length - 1; i >= 0; i--) if (arr[i]._tplLockId === _t0._tplLockId) arr.splice(i, 1);
      S.sel = null; S.iact = null;
      updateLayersPanel(); forcePropsRebuild(); updateActionBar();
      if (typeof syncMobileSelection === 'function') syncMobileSelection();
      if (typeof updateCartBtn === 'function') updateCartBtn();
      pushHistory(); render();
      /* Removing the whole template leaves the layer count stale otherwise — the
         normal delete path refreshes the badge, so this branch must too. */
      updateLayerBadge();
      /* Nothing selected after the cascade. If we were in design mode the Done
         button is now hidden, so the user can't tap it to leave — that strands
         them in design mode where single-finger gestures go idle instead of
         panning/selecting (looks like a frozen UI). Exit design mode for them,
         which hides the fabs AND restores view-mode gestures + the tool tabs. */
      if (isMobile() && !S.sel) {
        if (S.mode === 'design') exitDesignMode();
        else ['dt-done-btn', 'dt-dims-toggle', 'dt-snap-fab'].forEach(function (id) { var b = $(id); if (b) b.style.display = 'none'; });
      }
      return;
    }
    const arr = layers();
    const deleted = arr.find(l => l.id === id);
    const removeIds = [id];
    if (deleted && deleted._groupId) {
      if (deleted.isBackdrop) {
        /* Deleting the backdrop frees the photo from the group (no orphaned clip). */
        const photo = arr.find(l => !l.isBackdrop && l._groupId === deleted._groupId);
        if (photo) { photo._groupId = null; photo._maskKey = null; photo._linked = undefined; }
      } else {
        /* Deleting the photo also removes its backdrop (no orphaned backdrop). */
        arr.forEach(l => { if (l.isBackdrop && l._groupId === deleted._groupId) removeIds.push(l.id); });
      }
    }
    S.byView[S.view] = arr.filter(l => removeIds.indexOf(l.id) === -1);
    if (removeIds.indexOf(S.sel) !== -1) { S.sel = null; S.iact = null; }
    /* If the layer being edited in the monogram panel was just deleted, reset the panel
       out of "Edit / Save Changes" mode — otherwise it lingers with a Save Changes button
       bound to a layer that no longer exists. Rebuild it fresh in add mode. */
    if (_editingMonoLayer && removeIds.indexOf(_editingMonoLayer.id) !== -1) {
      _editingMonoLayer = null;
      var _mPanel = $('dt-panel-monogram');
      if (_mPanel && _mPanel.classList.contains('active')) { try { openMonogramPopup(_mPanel, null); } catch (e) {} }
    }
    pushHistory(); updateLayersPanel(); forcePropsRebuild(); updateCartBtn(); render();
    syncMobileSelection();
    updateLayerBadge();
    /* After a delete that leaves nothing selected on mobile, exit design mode.
       Hiding the fabs alone removed the Done button while S.mode stayed 'design',
       which strands the user: single-finger gestures go idle (no pan/select) and
       there's no Done to tap — the UI looks frozen. exitDesignMode() hides the
       fabs AND resets to view mode, restoring gestures + the tool tab bar. */
    if (isMobile() && !S.sel) {
      if (S.mode === 'design') exitDesignMode();
      else ['dt-done-btn', 'dt-dims-toggle', 'dt-snap-fab'].forEach(function (id) { var b = $(id); if (b) b.style.display = 'none'; });
    }
    /* Show undo toast */
    if (isMobile() && deleted) showUndoToast(deleted.name || 'Element');
  }

  /* ── Undo toast ── */
  function showUndoToast(name) {
    let toast = document.querySelector('.dt-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'dt-toast';
      toast.innerHTML = '<span class="dt-toast__text"></span>';
      document.querySelector('.dt-body')?.appendChild(toast);
    }
    toast.querySelector('.dt-toast__text').textContent = name + ' deleted';
    toast.style.background = ''; /* clear any bg left by showError */
    toast.classList.add('visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('visible'), 2500);
  }

  /* ── Layer count badge on Layers tab ── */
  function updateLayerBadge() {
    const tab = document.querySelector('.dt-mobile-tab[data-panel="layers"]');
    if (!tab) return;
    const count = layers().length;
    let badge = tab.querySelector('.dt-tab-badge');
    if (count > 0) {
      if (!badge) { badge = document.createElement('span'); badge.className = 'dt-tab-badge'; tab.appendChild(badge); }
      badge.textContent = count;
    } else if (badge) { badge.remove(); }
  }

  /* Remove any text layer that still has the default "Your Text" content.
     Called when user navigates away (switches tool, clicks canvas) without editing. */
  function cleanupDefaultText() {
    const defaults = layers().filter(l => l.type === 'text' && l.text === 'Your Text' && l !== S._inlineEditLayer);
    if (!defaults.length) return;
    defaults.forEach(l => {
      S.byView[S.view] = S.byView[S.view].filter(x => x.id !== l.id);
      if (S.sel === l.id) { S.sel = null; S.iact = null; }
    });
    if (defaults.length) {
      updateLayersPanel(); forcePropsRebuild(); updateCartBtn(); render();
    }
    /* SAFETY: if clearing abandoned default text left nothing selected while we
       were in mobile design mode, return to view mode. Otherwise S.mode stays
       'design' with single-finger gestures idle (no pan/select) — the same
       "frozen UI" strand fixed in deleteLayer(). */
    if (isMobile() && S.mode === 'design' && !S.sel) exitDesignMode();
  }

  function reorder(dir) {
    const arr = layers(), idx = arr.findIndex(l => l.id === S.sel);
    if (idx < 0) return;
    const ni = idx + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    pushHistory(); updateLayersPanel(); render();
  }

  /** Send the selected layer all the way to the front (dir > 0) or back
   *  (dir < 0). A photo with a linked backdrop moves as ONE unit — the
   *  backdrop stays directly beneath its photo (array order is z-order;
   *  splitting the pair would render the background over the photo). */
  function reorderExtreme(dir) {
    const arr = layers(), idx = arr.findIndex(l => l.id === S.sel);
    if (idx < 0) return;
    const l = arr[idx];
    let unit;
    if (l._tplLockId) {
      /* Template members move as ONE unit in their current relative order —
         moving a single member would reorder it against the template's own
         background (delete + drag already treat the lock as a unit). */
      unit = arr.filter(x => x._tplLockId === l._tplLockId);
    } else if (l.isBackdrop && l._groupId) {
      /* Backdrop selected from the Layers panel: bring its photo along
         (backdrop stays below) so the pair can never invert. */
      const photo = arr.find(x => !x.isBackdrop && x._groupId === l._groupId);
      unit = photo ? [l, photo] : [l];
    } else {
      unit = [l];
      if (l._groupId) {
        const bd = arr.find(b => b.isBackdrop && b._groupId === l._groupId);
        if (bd) unit.unshift(bd); /* backdrop below its photo, always */
      }
    }
    const rest = arr.filter(x => unit.indexOf(x) === -1);
    const next = dir > 0 ? rest.concat(unit) : unit.concat(rest);
    if (next.every((x, i) => x === arr[i])) return; /* already there */
    arr.length = 0;
    Array.prototype.push.apply(arr, next);
    pushHistory(); updateLayersPanel(); render();
  }

  /* ══════════════════════════════════════════════════════════════════
   * TEXT TOOL (left sidebar — sets defaults + adds new text)
   * ══════════════════════════════════════════════════════════════════ */
  let _addingText = false; /* Guard against rapid double-clicks */

  /* ── Add Text field: two-line start (Brandon, Aug 3 2026) ────────────
     design-tool.liquid ships #dt-text-input as <input type="text">, which
     can only ever paint ONE line — no CSS height changes that. A two-line
     start therefore needs a real <textarea>, and it is built HERE rather
     than in the snippet, per the v2.9.28 rule (HANDOFF-JUL26-28 §3.4): a UI
     change that CAN live in core.js does, so it cannot half-land across
     uploads. That rule is not theoretical here — an earlier attempt at this
     same field was lost exactly that way. It was never written back to the
     folder (both the Jul 24 liquid backup and the current liquid carry the
     identical single-line <input>, and their only diff is v2.9.33's line
     569), so the Jul 29 liquid re-paste took it out.
     Idempotent: no-ops if the element is already a textarea, so if the
     snippet is ever changed to carry one, the two coexist and neither
     duplicates. Every attribute setupTextTool depends on is carried over —
     .value, .focus() and .setSelectionRange() behave identically on a
     textarea, and maxlength applies to both. Presentation is inherited from
     .dt-input / .dt-panel textarea (width, font, padding, 12px radius); only
     the resize affordance is set here, inline, to keep this a ONE-file
     change. Enter now inserts a newline instead of doing nothing — there was
     no keydown handler on this field to preserve, and measureText has drawn
     multi-line text since well before this. */
  function ensureTextInputMultiline() {
    const el = $('dt-text-input');
    if (!el || el.tagName === 'TEXTAREA') return el;
    const ta = document.createElement('textarea');
    ta.id = 'dt-text-input';
    ta.className = el.className || 'dt-input';
    ta.rows = 2;
    if (el.placeholder) ta.placeholder = el.placeholder;
    const _ml = el.getAttribute('maxlength');
    if (_ml) ta.setAttribute('maxlength', _ml);
    ta.setAttribute('autocomplete', 'off');
    /* Same input hygiene the mobile inline edit bar already uses on its own
       textarea (#dt-inline-input): people type NAMES and short phrases here,
       and mobile autocorrect rewrites them ("Hadley" -> "Hadlee"). spellcheck
       off also stops red squiggles under every proper noun. */
    ta.setAttribute('autocorrect', 'off');
    ta.spellcheck = false;
    ta.style.resize = 'vertical';
    ta.value = el.value || '';
    el.replaceWith(ta);
    return ta;
  }

  function setupTextTool() {
    /* ORDER IS LOAD-BEARING: this MUST run before the $('dt-text-input')
       capture on the next line. The Add-Text click handler closes over that
       reference, so swapping the element afterwards leaves the handler holding
       a DETACHED node — every Add Text then reads '' and silently falls back to
       "Your Text", with no error and nothing in the console. Reproduced against
       the live build on Aug 3 2026; do not move this call below the capture. */
    ensureTextInputMultiline();
    const textInput = $('dt-text-input'), addBtn = $('dt-add-text-btn');
    addBtn?.addEventListener('click', () => {
      if (_addingText) return;

      if (isMobile()) {
        /* Mobile: keep current flow — type in sidebar, click button */
        const t = textInput?.value?.trim(); if (!t) { textInput?.focus(); return; }
        addTextLayer(t); textInput.value = '';
      } else {
        /* Desktop/tablet: create layer immediately and enter in-place edit */
        _addingText = true;
        const t = textInput?.value?.trim() || 'Your Text';
        addTextLayerAndEdit(t);
        if (textInput) textInput.value = '';
      }
    });
    /* Text template quick-add buttons */
    /* UX-Aug15 W0: Quick Ideas used to only fill the field (a second tap on Add Text was
       needed). Chips now add the layer directly via addTextLayer — fires dt_layer_add,
       keeps name hygiene + auto-fit; on mobile pushLayer auto-closes the tool modal and
       zooms to the print area. Respects the desktop _addingText latch. */
    $$('.dt-text-tpl').forEach(btn => btn.addEventListener('click', () => {
      const t = btn.dataset.text;
      if (!t) return;
      if (_addingText) return;
      addTextLayer(t);
      if (textInput) textInput.value = '';
    }));

    /* Group Names feature button — triggers the existing bulk order modal */
    $('dt-bulk-feature-btn')?.addEventListener('click', () => {
      const bulkBtn = $('dt-bulk-order-btn');
      if (bulkBtn) bulkBtn.click();
    });

    /* Dismissible announcement banner — disabled for now */
    /* if (!localStorage.getItem('dt_bulk_announcement_dismissed')) {
      const ann = $('dt-announcement');
      if (ann) ann.style.display = 'flex';
    }
    $('dt-announcement-close')?.addEventListener('click', () => {
      const ann = $('dt-announcement');
      if (ann) ann.style.display = 'none';
      try { localStorage.setItem('dt_bulk_announcement_dismissed', '1'); } catch (_) {}
    }); */
  }

  function addTextLayer(text) {
    dtAnalytics.layerAdd('text', text);
    const pa = S.pa;
    const _seed = seedTextColor(); /* UX-Aug15 #1 */
    const l = {
      id: uid(), type: 'text', name: text.replace(/\n/g, ' ').slice(0, 25), text,
      x: pa.x + pa.w/2, y: pa.y + pa.h/2,
      w: 0, h: S.textSize,
      font: S.textFont, fontSize: S.textSize, color: _seed.color, _autoColor: _seed.auto,
      bold: S.textBold, italic: S.textItalic, textAlign: 'center',
      rotation: 0, flipH: false, flipV: false, visible: true, centered: true,
      ar: null, isRasterUpload: false,
    };
    const fitToPA = () => {
      measureText(l);
      /* Auto-fit: scale fontSize so text fills ~90% of print area width */
      if (l.w > 0 && pa.w > 0) {
        const targetW = pa.w * 0.9;
        const scaleW = targetW / l.w;
        /* Also ensure height doesn't exceed PA height */
        const scaleH = l.h > 0 ? (pa.h * 0.85) / l.h : Infinity;
        const scale = Math.min(scaleW, scaleH);
        l.fontSize = Math.round(l.fontSize * scale);
        l.fontSize = Math.max(10, Math.min(120, l.fontSize));
        measureText(l);
      }
      l.ar = l.w / (l.h || 1);
      pushLayer(l);
    };
    document.fonts.load(l.fontSize + 'px "' + l.font + '"').then(fitToPA).catch(fitToPA);
  }

  /* ══════════════════════════════════════════════════════════════════
   * SURPRISE ME — curated, ready-made text starter designs
   *
   * The recipe DATA now lives in the `design-tool-surprise-recipes` snippet
   * (emitted as a JSON <script id="dt-starter-recipes"> in the page) so the
   * copy and roles can be edited without touching this file. loadStarterRecipes()
   * reads it on init; if the snippet is missing or malformed we fall back to
   * DEFAULT_STARTER_RECIPES below so the feature can never break.
   *
   * Fonts are NOT named here. Each line carries an abstract style `role`
   * (script | display | serif | sans | marker), resolved at runtime against the
   * `category` of your metaobject fonts. The editable schema is documented in
   * the snippet itself.
   * ══════════════════════════════════════════════════════════════════ */

  /* Keyword sets per role, matched (case-insensitive, substring) against each
     font's metaobject `category`. NOT font names — a small, stable vocabulary. */
  var ROLE_CATEGORY_KEYWORDS = {
    script:  ['script', 'hand', 'cursive', 'calligraph', 'brush'],
    display: ['display', 'bold', 'heavy', 'black', 'slab', 'condensed', 'marker'],
    sans:    ['sans', 'grotesk', 'geometric', 'round'],
    serif:   ['serif'],
    marker:  ['marker', 'brush', 'hand'],
  };

  function catMatchesRole(cat, role) {
    cat = String(cat || '').toLowerCase();
    if (role === 'sans')  return cat.indexOf('sans') !== -1;
    if (role === 'serif') return cat.indexOf('serif') !== -1 && cat.indexOf('sans') === -1;
    var kws = ROLE_CATEGORY_KEYWORDS[role] || [];
    return kws.some(function (k) { return cat.indexOf(k) !== -1; });
  }

  /* Fonts tagged as graphic/image-like (e.g. category "Graphic") aren't real
     text fonts, so they should never be auto-used in a Surprise me design. */
  function isGraphicCategory(cat) {
    cat = String(cat || '').toLowerCase();
    return cat.indexOf('graphic') !== -1 || cat.indexOf('image') !== -1 ||
           cat.indexOf('icon') !== -1 || cat.indexOf('dingbat') !== -1 ||
           cat.indexOf('symbol') !== -1;
  }

  /* Pick a renderable font family (fontValue) for a role from the fonts that
     actually exist. Graphic/image fonts are excluded; then category match
     first, then any text font, then a generic floor so it can never render
     nothing. */
  function pickStarterFont(role) {
    var all = (typeof getAllFonts === 'function') ? getAllFonts() : [];
    if (!all.length) return S.textFont || 'Montserrat';
    var textFonts = all.filter(function (f) { return !isGraphicCategory(f.category); });
    var usable = textFonts.length ? textFonts : all;   /* if everything is graphic, last-resort to all */
    var matches = usable.filter(function (f) { return catMatchesRole(f.category, role); });
    var pool = matches.length ? matches : usable;
    return pool[Math.floor(Math.random() * pool.length)].fontValue;
  }

  /* Minimal built-in fallback — ONLY used if the snippet didn't load or was
     malformed. The full, editable set lives in the snippet, not here. */
  var DEFAULT_STARTER_RECIPES = [
    { lines: [{ t: 'Cheers!', role: 'script', frac: 0.62 }] },
    { lines: [{ t: 'Adventure Awaits', role: 'display', frac: 0.36 }] },
  ];
  var STARTER_RECIPES = DEFAULT_STARTER_RECIPES;

  /* Read recipes from the rendered snippet, validate, and replace the default
     set. Defensive: any problem leaves the fallback in place. */
  function loadStarterRecipes() {
    try {
      var el = document.getElementById('dt-starter-recipes');
      if (!el) { DEBUG && console.warn('[dt] starter recipes snippet not found; using defaults'); return; }
      var data = JSON.parse(el.textContent);
      if (!Array.isArray(data)) return;
      var clean = data.filter(function (r) {
        return r && Array.isArray(r.lines) && r.lines.length &&
          r.lines.every(function (ln) {
            return ln && typeof ln.t === 'string' && ln.t.trim() &&
              typeof ln.frac === 'number' && ln.frac > 0 && ln.frac <= 1;
          });
      });
      if (clean.length) STARTER_RECIPES = clean;
      else DEBUG && console.warn('[dt] starter recipes snippet had no valid entries; using defaults');
    } catch (e) {
      DEBUG && console.warn('[dt] starter recipes parse failed; using defaults', e);
    }
  }



  function buildStarterLines(recipe, pa, sideAtCall, fonts) {
    /* #12 race guard: if the user switched sides during the async font load,
       abort rather than pushing layers onto the wrong side. */
    if (sideAtCall && S.view !== sideAtCall) return;
    /* #8/#11: remove any previously-seeded starter on this side so repeated
       taps cycle/replace cleanly instead of stacking overlapping designs. */
    const arr = layers();
    for (let i = arr.length - 1; i >= 0; i--) { if (arr[i]._starter) arr.splice(i, 1); }

    var _starterSeed = seedTextColor(); /* UX-Aug20: auto-contrast for starters */
    var built = [];
    recipe.lines.forEach(function (ln, i) {
      var fs = Math.round(pa.h * ln.frac);
      var l = {
        id: uid(), type: 'text', name: ln.t.slice(0, 25), text: ln.t,
        x: 0, y: 0, w: 0, h: fs, font: (fonts && fonts[i]) || pickStarterFont(ln.role), fontSize: fs,
        color: _starterSeed.color, _autoColor: _starterSeed.auto,
        bold: false, italic: false, textAlign: 'center', rotation: 0,
        flipH: false, flipV: false, visible: true, centered: true, ar: null, isRasterUpload: false,
        _starter: true,
      };
      measureText(l);
      /* Fit each line to ~88% of the print-area width */
      if (l.w > pa.w * 0.88 && l.w > 0) {
        var s = (pa.w * 0.88) / l.w;
        l.fontSize = Math.max(10, Math.round(l.fontSize * s));
        measureText(l);
      }
      l.ar = l.w / (l.h || 1);
      built.push(l);
    });
    /* Stack vertically, centered as a group within the print area */
    var gap = pa.h * 0.04;
    var totalH = built.reduce(function (a, l) { return a + l.h; }, 0) + gap * (built.length - 1);
    var cyTop = pa.y + (pa.h - totalH) / 2;
    built.forEach(function (l) {
      l.x = pa.x + pa.w / 2;     /* centered:true → x,y is the element center */
      l.y = cyTop + l.h / 2;
      cyTop += l.h + gap;
      arr.push(l);
    });
    /* One batched update (avoid per-layer pushLayer jank with multi-line recipes) */
    S.sel = built[built.length - 1].id;
    autoClosePanelOnAdd();
    pushHistory(); updateLayersPanel(); forcePropsRebuild(); updateCartBtn(); render();
    syncMobileSelection();
    if (typeof updateLayerBadge === 'function') updateLayerBadge();
    if (isMobile()) requestZoomToPA(50, true);
    /* #9/#10: toast only after a design has actually been placed */
    toast('recipe_added');
  }

  function addStarterDesign(recipe) {
    var pa = S.pa; if (!pa) return;
    var sideAtCall = S.view;
    /* Resolve each line's font ONCE (pickStarterFont is random) so the preload
       below and the actual build use the same family — no fallback flash. */
    var fonts = recipe.lines.map(function (ln) { return pickStarterFont(ln.role); });
    var loads = recipe.lines.map(function (ln, i) {
      var fs = Math.round(pa.h * ln.frac);
      return document.fonts.load(fs + 'px "' + fonts[i] + '"').catch(function () {});
    });
    Promise.all(loads).then(function () { buildStarterLines(recipe, pa, sideAtCall, fonts); })
      .catch(function () { buildStarterLines(recipe, pa, sideAtCall, fonts); });
  }

  function surpriseMe() {
    if (isMobile() && S._closeMobileDrawer) S._closeMobileDrawer();
    var n = STARTER_RECIPES.length;
    var idx = Math.floor(Math.random() * n);
    if (n > 1) { while (idx === S._lastStarter) idx = Math.floor(Math.random() * n); }
    S._lastStarter = idx;
    addStarterDesign(STARTER_RECIPES[idx]);   /* toast fires inside, only on success */
  }

  /* ══════════════════════════════════════════════════════════════════
   * TEMPLATE RECIPES — ready-made multi-line text layouts (Group/B2B set)
   *
   * Data lives in the `design-tool-template-recipes` snippet (emitted as
   * <script type="application/json" id="dt-template-recipes">) so the copy
   * is editable without touching this file. Same line schema as the starter
   * recipes — {t, role, frac} — plus a `name` and optional `category`. Built
   * through the proven buildStarterLines path, so fonts resolve by role and
   * the layout auto-fits the print area. Falls back to the defaults below if
   * the snippet is missing/malformed.
   * ══════════════════════════════════════════════════════════════════ */
  var DEFAULT_TEMPLATE_RECIPES = [
    { name: 'Name + Title', category: 'Professional', lines: [
      { t: 'Jordan Avery',  role: 'display', frac: 0.16 },
      { t: 'FOUNDER & CEO', role: 'sans',    frac: 0.065 },
    ] },
    { name: 'Realtor', category: 'Professional', lines: [
      { t: 'Jordan Avery',       role: 'display', frac: 0.15 },
      { t: 'REALTOR®',      role: 'sans',    frac: 0.06 },
      { t: 'Avery Group Realty', role: 'serif',   frac: 0.06 },
      { t: '555 · 123 · 4567', role: 'sans', frac: 0.05 },
    ] },
    { name: 'Team Roster', category: 'Team', lines: [
      { t: 'THE TEAM',                     role: 'display', frac: 0.10 },
      { t: 'Alex · Jordan · Sam',   role: 'sans', frac: 0.075 },
      { t: 'Taylor · Morgan · Riley', role: 'sans', frac: 0.075 },
    ] },
    { name: 'The Crew', category: 'Team', lines: [
      { t: 'THE CREW',  role: 'display', frac: 0.12 },
      { t: 'Est. 2024', role: 'script',  frac: 0.07 },
    ] },
  ];
  var TEMPLATE_RECIPES = DEFAULT_TEMPLATE_RECIPES;

  function loadTemplateRecipes() {
    try {
      var el = document.getElementById('dt-template-recipes');
      if (!el) { DEBUG && console.warn('[dt] template recipes snippet not found; using defaults'); return; }
      var data = JSON.parse(el.textContent);
      if (!Array.isArray(data)) return;
      var clean = data.filter(function (r) {
        return r && typeof r.name === 'string' && r.name.trim() &&
          Array.isArray(r.lines) && r.lines.length &&
          r.lines.every(function (ln) {
            return ln && typeof ln.t === 'string' && ln.t.trim() &&
              typeof ln.frac === 'number' && ln.frac > 0 && ln.frac <= 1;
          });
      }).map(function (r) {
        return {
          name: r.name.trim(),
          category: (typeof r.category === 'string' && r.category.trim()) || 'Templates',
          lines: r.lines.map(function (ln) { return { t: ln.t, role: ln.role || 'sans', frac: ln.frac }; }),
        };
      });
      if (clean.length) TEMPLATE_RECIPES = clean;
      else DEBUG && console.warn('[dt] template recipes snippet had no valid entries; using defaults');
    } catch (e) {
      DEBUG && console.warn('[dt] template recipes parse failed; using defaults', e);
    }
  }

  /* Apply a recipe: resolve fonts by role, preload, then build the centered
     multi-line text design on the CURRENT side (reuses buildStarterLines, so
     repeated taps replace the prior recipe rather than stacking). On desktop
     we then surface the "Make it yours" edit form. */
  function applyRecipeTemplate(recipe) {
    var pa = S.pa;
    if (!pa) { toast('pick_side'); return; }
    if (isMobile() && S._closeMobileDrawer) S._closeMobileDrawer();
    var sideAtCall = S.view;
    var fonts = recipe.lines.map(function (ln) { return pickStarterFont(ln.role); });
    var loads = recipe.lines.map(function (ln, i) {
      return document.fonts.load(Math.round(pa.h * ln.frac) + 'px "' + fonts[i] + '"').catch(function () {});
    });
    var finish = function () {
      buildStarterLines(recipe, pa, sideAtCall, fonts);
      if (!isMobile()) { try { showTemplatePersonalize(); } catch (e) {} }
    };
    Promise.all(loads).then(finish).catch(finish);
  }

  function recipeCardHtml(r, i) {
    var prev = r.lines.map(function (ln) {
      return '<span class="dt-recipe-line dt-recipe-line--' + escHtml(ln.role || 'sans') + '">' + escHtml(ln.t) + '</span>';
    }).join('');
    return '<button class="dt-recipe-card" data-ri="' + i + '" type="button" aria-label="Use template: ' + escHtml(r.name) + '">' +
      '<span class="dt-recipe-prev" aria-hidden="true">' + prev + '</span>' +
      '<span class="dt-recipe-name">' + escHtml(r.name) + '</span></button>';
  }

  /* (Removed: stateCardHtml / recipeSectionHtml / wireRecipeCards — the
     separate "Quick-start" recipe strip was folded into the unified
     categorized Templates library. State Outline is now a library item.) */

  /* ══════════════════════════════════════════════════════════════════
   * STATE OUTLINE template + searchable state picker
   *
   * 51 state/territory outlines are inlined as JSON in the
   * `design-tool-state-outlines` snippet (#dt-state-outlines). The template
   * drops a state outline (an SVG layer flagged isStateOutline) + a name line
   * + a draggable heart the customer positions on their town. Selecting the
   * outline and opening its editor shows a searchable library to swap states.
   * ══════════════════════════════════════════════════════════════════ */
  /* ══════════════════════════════════════════════════════════════════
   * BUILT-IN TEMPLATES — categorized parametric starter designs
   * Data inlined in the design-tool-templates snippet (#dt-templates): text
   * lines (real Google fonts + brand colours), an optional icon SVG, and an
   * optional photo placeholder. Rendered as items inside the categorized
   * Templates library, merged with the metaobject galleries.
   * ══════════════════════════════════════════════════════════════════ */
  var INLINE_TEMPLATES = [];

  function loadInlineTemplates() {
    try {
      var el = document.getElementById('dt-templates');
      if (!el) return;
      var data = JSON.parse(el.textContent);
      if (Array.isArray(data)) INLINE_TEMPLATES = data;
      /* Preload the fonts these templates use so the preview cards render in the
         real typefaces (and they become available/editable in the picker). */
      var fams = {};
      INLINE_TEMPLATES.forEach(function (t) { (t.lines || []).forEach(function (l) { if (l.font) fams[l.font] = 1; }); });
      Object.keys(fams).forEach(function (f) { try { ensureGoogleFont(f); } catch (e) {} });
    } catch (e) { DEBUG && console.warn('[dt] templates parse failed', e); }
  }

  var DT_PHOTO_PLACEHOLDER_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
    '<rect x="2" y="2" width="96" height="96" rx="8" fill="#F2F4F5" stroke="#8A8077" stroke-width="2" stroke-dasharray="6 5"/>' +
    '<circle cx="50" cy="40" r="11" fill="none" stroke="#8A8077" stroke-width="2.5"/>' +
    '<path d="M28 70 L44 52 L56 62 L66 50 L76 70 Z" fill="#8A8077"/>' +
    '<text x="50" y="90" text-anchor="middle" font-family="Inter,sans-serif" font-size="8" fill="#8A8077">Tap to add photo</text></svg>';

  /* Shape-aware empty slot: previews the final crop shape (circle / heart) so the
     customer understands the outcome BEFORE uploading (CRO clarity). Falls back to
     the square placeholder for un-masked photo slots. */
  function dtPhotoPlaceholderSvg(shape) {
    if (shape === 'circle') {
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
        '<circle cx="50" cy="50" r="47" fill="#F2F4F5" stroke="#8A8077" stroke-width="2" stroke-dasharray="6 5"/>' +
        '<circle cx="50" cy="42" r="10" fill="none" stroke="#8A8077" stroke-width="2.5"/>' +
        '<path d="M30 66 L44 50 L55 60 L64 49 L74 66 Z" fill="#8A8077"/>' +
        '<text x="50" y="84" text-anchor="middle" font-family="Inter,sans-serif" font-size="7.5" fill="#8A8077">Add photo</text></svg>';
    }
    if (shape === 'heart') {
      var hp = 'M50 86 C18 64 6 44 6 30 C6 18 16 10 27 10 C36 10 44 16 50 26 C56 16 64 10 73 10 C84 10 94 18 94 30 C94 44 82 64 50 86 Z';
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
        '<path d="' + hp + '" fill="#F2F4F5" stroke="#8A8077" stroke-width="2" stroke-dasharray="6 5"/>' +
        '<circle cx="50" cy="40" r="9" fill="none" stroke="#8A8077" stroke-width="2.3"/>' +
        '<path d="M33 58 L44 46 L53 55 L61 45 L69 58 Z" fill="#8A8077"/>' +
        '<text x="50" y="72" text-anchor="middle" font-family="Inter,sans-serif" font-size="7" fill="#8A8077">Add photo</text></svg>';
    }
    return DT_PHOTO_PLACEHOLDER_SVG;
  }

  /* Add an SVG layer at a precise centre + height (async; positions on load). */
  function addTplSvgAt(svg, cx, cy, hPx, extra) {
    var tag = 'tplsvg-' + uid();
    addSvgLayer(svg, (extra && extra.name) || 'Graphic', Object.assign({ _tplTag: tag }, extra || {}));
    var tries = 0;
    var place = function () {
      var arr = layers(), el = null;
      for (var i = arr.length - 1; i >= 0; i--) { if (arr[i]._tplTag === tag) { el = arr[i]; break; } }
      if (!el) { if (++tries < 80) setTimeout(place, 25); return; }
      var ar = el.ar || (el.w / (el.h || 1)) || 1;
      el.h = hPx; el.w = hPx * ar; el.centered = false;
      el.x = cx - el.w / 2; el.y = cy - el.h / 2;
      delete el._tplTag;
      render(); updateLayersPanel();
    };
    place();
  }

  /* ── TEMPLATE LOCK ─────────────────────────────────────────────
     Layers applied by a template share a _tplLockId and behave as ONE
     unit: one selection box, group move/scale, delete-together, and a
     single edit panel (the Templates panel's "Make it yours" form).
     The only per-element canvas interaction kept is photo reframing,
     entered from that panel. */
  function tplReframing(l) {
    if (!l || l.isBackdrop) return false;
    /* Maskless template photo (no backdrop group): a transient _reframing flag
       unlocks it for free drag/resize within the locked template, mirroring how an
       unlinked backdrop unlocks a masked photo. */
    if (l._reframing) return true;
    if (!l._groupId) return false;
    const bd = (S.byView[S.view] || []).find(b => b.isBackdrop && b._groupId === l._groupId);
    return !!(bd && bd._linked === false);
  }
  function fitTemplateTextLayer(l) {
    const pa = S.pa; if (!pa || !l || l.type !== 'text') return;
    if (l._tplBaseFs == null) l._tplBaseFs = l.fontSize;
    l.fontSize = l._tplBaseFs;
    measureText(l);
    /* A ±90 line (vertical templates) runs its ADVANCE down the print area, so
       the clamp axis is pa.h — clamping it against pa.w let "BRANDON" grow past
       the top and bottom edges and trip the crop warning. Mirrors the fitDim
       switch applyInlineTemplate already uses when it builds the layer. */
    const fitDim = (Math.abs(l.rotation || 0) === 90 ? pa.h : pa.w) * 0.92;
    if (l.w > fitDim && l.w > 0) {
      l.fontSize = Math.max(8, Math.floor(l.fontSize * fitDim / l.w));
      measureText(l);
    }
    l.ar = l.w / (l.h || 1);
  }
  function openTemplateEditPanel() {
    /* The template editor lives in the PROPERTIES panel. Desktop: it follows
       selection automatically; mobile: open the properties drawer. */
    if (isMobile()) { if (S._openMobileDrawer) S._openMobileDrawer('props'); }
    else forcePropsRebuild();
  }
  function attachLockDrag(iact, l) {
    if (!l || !l._tplLockId || tplReframing(l) || l._lockDraggable) return;
    const ccx = l.centered ? l.x : l.x + l.w / 2;
    const ccy = l.centered ? l.y : l.y + l.h / 2;
    iact.lockPeers = (S.byView[S.view] || [])
      .filter(m => m !== l && m._tplLockId === l._tplLockId && !m.isBackdrop)
      .map(m => ({ l: m, dx: (m.centered ? m.x : m.x + m.w / 2) - ccx, dy: (m.centered ? m.y : m.y + m.h / 2) - ccy }));
  }
  function lockScaleMembers(l) {
    return (S.byView[S.view] || [])
      .filter(m => m._tplLockId === l._tplLockId && !m.isBackdrop)
      .map(m => ({ l: m, x: m.x, y: m.y, w: m.w, h: m.h, fs: m.type === 'text' ? m.fontSize : null }));
  }
  function applyLockScale(ls, f) {
    f = Math.max(0.2, Math.min(8, f));
    ls.members.forEach(s => {
      const m = s.l;
      const ocx = m.centered ? s.x : s.x + s.w / 2;
      const ocy = m.centered ? s.y : s.y + s.h / 2;
      m.w = Math.max(4, s.w * f); m.h = Math.max(4, s.h * f);
      if (s.fs) { m.fontSize = Math.max(6, Math.round(s.fs * f)); measureText(m); }
      const ncx = ls.cx + (ocx - ls.cx) * f, ncy = ls.cy + (ocy - ls.cy) * f;
      if (m.centered) { m.x = ncx; m.y = ncy; } else { m.x = ncx - m.w / 2; m.y = ncy - m.h / 2; }
    });
  }

  /* Build a parametric template onto the current side. */
  async function applyInlineTemplate(tpl) {
    syncCanvas(); /* UX-Aug20: same stale-PA guard as applyTemplate */
    var pa = S.pa;
    if (!pa) { toast('pick_side'); return; }
    /* Replace what's on the current side (confirm first if it has real art),
       matching how the metaobject templates behave. */
    var hasArt = (S.byView[S.view] || []).some(function (l) { return l.visible !== false; });
    if (hasArt && !(await dtConfirm('It replaces the design on this side.', { title: 'Start this template?', okLabel: 'Use template' }))) return;
    if (isMobile() && S._closeMobileDrawer) S._closeMobileDrawer();
    S.byView[S.view] = []; S.sel = null; S.iact = null;

    var lines = (tpl.lines || []).filter(function (l) { return l && l.t; });
    var fams = {}; lines.forEach(function (l) { if (l.font) fams[l.font] = 1; });
    Object.keys(fams).forEach(function (f) { ensureGoogleFont(f); });
    var loads = Object.keys(fams).map(function (f) { return document.fonts.load('24px "' + f + '"').catch(function () {}); });
    /* engrave decorates in a single colour — ignore the template's print colours */
    var engrave = S.method === 'engrave';
    var lockId = uid();   /* TEMPLATE LOCK: shared by every layer this template creates */

    var build = function () {
      var GAP = 6;
      var iconRel = tpl.icon ? 64 : 0;
      var photoRel = tpl.photo ? 120 : 0;
      /* multi-line entries occupy height per LINE — budget accordingly */
      var textRel = lines.reduce(function (a, l) { return a + (l.size || 16) * ((String(l.t || '').match(/\n/g) || []).length + 1); }, 0);
      var nBlocks = lines.length + (iconRel ? 1 : 0) + (photoRel ? 1 : 0);
      var totalRel = textRel + iconRel + photoRel + GAP * Math.max(0, nBlocks - 1);
      var scale = (pa.h * 0.82) / (totalRel || (pa.h * 0.82));

      var _tplSeed = engrave ? { color: '#000000', auto: false } : seedTextColor(); /* UX-Aug20: auto-contrast for templates */
      var textLayers = lines.map(function (l) {
        var fs = Math.max(8, Math.round((l.size || 16) * scale));
        var rot = l.rotation || 0;
        /* UX-Aug20: if the template line has an explicit color AND it's not a dark default,
           honour the designer's pick; otherwise seed from the product's background luminance. */
        var _useColor = _isDarkDefault(l.color)
          ? _tplSeed.color : (engrave ? '#000000' : l.color);
        var _isAuto = _isDarkDefault(l.color)
          ? _tplSeed.auto : false;
        var layer = {
          id: uid(), type: 'text', name: (l.t || 'Text').slice(0, 25), text: l.t,
          x: 0, y: 0, w: 0, h: fs, font: l.font || 'Poppins', fontSize: fs,
          color: _useColor, _autoColor: _isAuto,
          bold: false, italic: false, textAlign: 'center', rotation: rot,
          flipH: false, flipV: false, visible: true, centered: true, ar: null, isRasterUpload: false, _tpl: true, _tplLockId: lockId,
        };
        measureText(layer);
        var fitDim = (Math.abs(rot) === 90) ? pa.h * 0.82 : pa.w * 0.9;
        if (layer.w > fitDim && layer.w > 0) {
          layer.fontSize = Math.max(8, Math.round(layer.fontSize * fitDim / layer.w));
          measureText(layer);
        }
        layer.ar = layer.w / (layer.h || 1);
        return layer;
      });

      var blocks = [];
      if (tpl.icon && tpl.iconPos !== 'bottom') blocks.push({ type: 'icon', h: iconRel * scale });
      if (tpl.photo) blocks.push({ type: 'photo', h: photoRel * scale });
      textLayers.forEach(function (l) {
        /* v2.9.22: stack on FONT-SIZE line frames (fontSize × line count), the
           same block heights renderTemplateThumb budgets — NOT the measured ink
           height (l.h). Ink-tight blocks collapsed big-number combos ("cheers
           to / 30 / MARIA"): Anton digits' ink is ~0.88em vs the 1em frame the
           card allocates, so the placed design crowded/overlapped the number
           against its neighbours and no longer matched the card. Ink metrics
           still drive bbox/selection; only the initial stacking budget changed. */
        var lc = String(l.text || '').split('\n').length;
        var bh = (Math.abs(l.rotation) === 90) ? l.w : l.fontSize * lc;
        blocks.push({ type: 'text', h: bh, layer: l });
      });
      if (tpl.icon && tpl.iconPos === 'bottom') blocks.push({ type: 'icon', h: iconRel * scale });

      var gap = pa.h * 0.03;
      var totalH = blocks.reduce(function (a, b) { return a + b.h; }, 0) + gap * Math.max(0, blocks.length - 1);
      var cyTop = pa.y + (pa.h - totalH) / 2;
      var arr = layers();
      var lastId = null;
      blocks.forEach(function (b) {
        var cy = cyTop + b.h / 2;
        if (b.type === 'text') { b.layer.x = pa.x + pa.w / 2; b.layer.y = cy; arr.push(b.layer); lastId = b.layer.id; }
        else if (b.type === 'icon') addTplSvgAt(tpl.icon, pa.x + pa.w / 2, cy, b.h, { name: 'Icon', _tplLockId: lockId });
        else if (b.type === 'photo') addTplSvgAt(dtPhotoPlaceholderSvg(tpl.mask), pa.x + pa.w / 2, cy, b.h, { name: 'Photo', isPhotoPlaceholder: true, _maskShape: tpl.mask || null, _tplLockId: lockId });
        cyTop += b.h + gap;
      });
      /* Don't auto-select a single element — open the template editor (the
         "Make it yours" form listing every element) instead, matching how the
         metaobject templates behave. */
      void lastId;
      S.sel = null; S.iact = null;
      render(); updateLayersPanel(); forcePropsRebuild();
      /* Re-fit text once the real fonts finish loading — fallback-font metrics
         caused overflow / "applied template looks different". */
      document.fonts.ready.then(function () {
        (S.byView[S.view] || []).forEach(function (tl) { if (tl.type === 'text' && tl._tplLockId === lockId) fitTemplateTextLayer(tl); });
        render();
      });
      if (typeof updateCartBtn === 'function') updateCartBtn();
      var hasText = false; try { hasText = showTemplatePersonalize(); } catch (e) {}
      if (isMobile()) {
        if (!hasText && S._closeMobileDrawer) S._closeMobileDrawer();
        requestZoomToPA(50, true);
      }
      setTimeout(function () { pushHistory(); }, 450);   /* re-capture after async icon/photo settle */
      /* #8 (mobile batch): bulk-intended template — tag the line that varies
         per person and float the Group-order coach as the access path. */
      if (tpl.bulk) {
        var _bts = (S.byView[S.view] || []).filter(function (l) { return l.type === 'text' && l._tplLockId === lockId; });
        var _bf = _bts[Math.min(tpl.bulkLine || 0, Math.max(0, _bts.length - 1))];
        if (_bf) _bf._bulkField = true;
        setTimeout(showBulkSeedCoach, 600);
      }
      toast(hasText ? 'tpl_added_text' : 'tpl_added');
    };
    Promise.all(loads).then(build).catch(build);
  }

  /* Photo placeholder → pick a file, drop it into the placeholder's box, and
     remove the placeholder. Used by template photo slots. */
  /* Fill a template photo slot with a freshly-added layer: size it to the
     slot (COVER when masked, CONTAIN otherwise), carry the template lock,
     remove the placeholder, and apply the shape mask. Shared by the direct
     print path AND the engrave AI/logo pipelines (via S._slotIntent, claimed
     in pushLayer). */
  function settleIntoSlot(nl, slot) {
    var arr = layers();
    var ph = arr.find(function (p) { return p.id === slot.phId; });
    var masked = !!slot.maskShape;
    var ar = nl.w / (nl.h || 1) || 1, w, h;
    if (masked) {
      /* COVER the slot so the mask shape is fully filled (crop overflow). */
      if (ar > slot.bw / slot.bh) { h = slot.bh; w = h * ar; } else { w = slot.bw; h = w / ar; }
    } else {
      /* CONTAIN inside the slot. */
      w = slot.bw; h = w / ar; if (h > slot.bh) { h = slot.bh; w = h * ar; }
    }
    nl.centered = false; nl.w = w; nl.h = h;
    nl.x = slot.bx + (slot.bw - w) / 2; nl.y = slot.by + (slot.bh - h) / 2;
    /* Template photo: lock size + no background removal, so its edit set
       stays minimal and distinct from a standard upload. */
    nl._tplPhoto = true; nl._noResize = true; nl.isRasterUpload = false;
    nl._tplLockId = slot.lockId || null;
    nl.name = 'Photo';
    var idx = ph ? arr.indexOf(ph) : -1; if (idx >= 0) arr.splice(idx, 1);
    S.sel = nl.id;
    /* Apply the shape mask via the existing backdrop-mask pipeline. */
    var mk = masked ? ({ circle: 'circle_mask', heart: 'heart_mask' })[slot.maskShape] : null;
    if (mk && typeof placeBackdrop === 'function') {
      nl._tplMask = true;
      placeBackdrop(nl, mk);   /* creates the mask group, renders, pushes history */
      /* Re-lock the mask box to the SLOT (smaller than the covering photo) so
         the shape sits fully inside the photo. */
      var bd = layers().find(function (l) { return l.isBackdrop && l._groupId === nl._groupId; });
      if (bd) {
        bd._tplLockId = nl._tplLockId;
        var mbox = Math.min(slot.bw, slot.bh);
        var pcx = nl.x + nl.w / 2, pcy = nl.y + nl.h / 2;
        bd.centered = false; bd.w = mbox; bd.h = mbox;
        bd.x = pcx - mbox / 2; bd.y = pcy - mbox / 2;
        if (typeof captureLinkTransform === 'function') captureLinkTransform(bd, nl);
        render();
      }
      forcePropsRebuild();
    } else {
      render(); pushHistory(); updateLayersPanel(); forcePropsRebuild();
    }
    if (typeof updateActionBar === 'function') updateActionBar();
  }

  function replacePhotoPlaceholder(layer) {
    if (!layer) return;
    var inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*';
    inp.onchange = function () {
      var file = inp.files && inp.files[0]; if (!file) return;
      var slot = {
        bx: layer.centered ? layer.x - layer.w / 2 : layer.x,
        by: layer.centered ? layer.y - layer.h / 2 : layer.y,
        bw: layer.w, bh: layer.h,
        maskShape: layer._maskShape || null,
        lockId: layer._tplLockId || null,
        phId: layer.id,
      };
      /* The next image layer pushed claims this slot (survives async pipelines). */
      S._slotIntent = { slot: slot, t: Date.now() };
      var isSvgFile = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
      if (S.method === 'engrave' && !isSvgFile) {
        /* ENGRAVE: photos route through remove-bg + AI line art, logos through
           the normal engrave path — exactly like the Upload tab. The result is
           settled into the slot by the pushLayer hook. */
        handleEngraveUpload(file);
        return;
      }
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () { addImageLayer(img, file.name, e.target.result, false, file); };
        img.onerror = function () { S._slotIntent = null; toast('img_open_fail'); };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    };
    /* Same session-level upload agreement as the Upload tab — this path
       previously opened the file dialog ungated. */
    requireCopyrightAgreement(() => inp.click());
  }

  /* Swap the image in an EXISTING photo (a filled template slot or a normal upload)
     in place — keeps position, the size box, and any mask/backdrop. Distinct from
     replacePhotoPlaceholder, which fills an empty slot. */
  function swapPhoto(layer) {
    var l = layer || selLayer(); if (!l) return;
    var inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*';
    inp.onchange = function () {
      var file = inp.files && inp.files[0]; if (!file) return;
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          var cur = selLayer() || l; if (!cur) return;
          var nar = (img.naturalWidth || img.width) / ((img.naturalHeight || img.height) || 1) || 1;
          var bd = backdropOfGroup(cur);
          var cx = cur.centered ? cur.x : cur.x + cur.w / 2;
          var cy = cur.centered ? cur.y : cur.y + cur.h / 2;
          /* Was this a cutout sitting on a decorative background? The new opaque photo
             hides that background until it's re-cut, so we guide the user afterwards. */
          var _hadDecorBg = !!(cur._bgRemoved && bd && !bd._maskMarkup);
          cur.img = img; cur.b64 = e.target.result; cur.ar = nar; cur._bgRemoved = false; cur._sourceFile = file;
          if (bd && bd._maskMarkup) {
            /* COVER the mask box so the new photo fills the shape (no gaps). */
            var box = Math.max(bd.w, bd.h), w, h;
            if (nar >= 1) { h = box; w = box * nar; } else { w = box; h = box / nar; }
            cur.centered = false; cur.w = w; cur.h = h;
            cur.x = cx - w / 2; cur.y = cy - h / 2;
          } else {
            /* Plain photo: preserve displayed width, adjust height to the new aspect. */
            var w2 = cur.w, h2 = w2 / nar;
            cur.centered = false; cur.w = w2; cur.h = h2;
            cur.x = cx - w2 / 2; cur.y = cy - h2 / 2;
          }
          if (bd && bd._linked !== false && typeof captureLinkTransform === 'function') captureLinkTransform(bd, cur);
          pushHistory(); render(); forcePropsRebuild();
          if (typeof updateActionBar === 'function') updateActionBar();
          if (_hadDecorBg) toast('photo_swap_bg');
        };
        img.onerror = function () { toast('img_open_fail'); };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    };
    /* Same session-level upload agreement as the Upload tab (was ungated). */
    requireCopyrightAgreement(() => inp.click());
  }

  /* (#48) Replace a template's SVG/graphic layer with an uploaded image. The image is
     fitted into the SVG's current footprint (preserve aspect, centred) — a straight swap
     with NO reframe step. Keeps the template lock so the image stays part of the group.
     Mirrors swapClipartLayer's box-fit. */
  function replaceTemplateSvgWithImage(layer) {
    if (!layer) return;
    const box = bbox(layer);
    const lockId = layer._tplLockId || null;
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*';
    inp.onchange = function () {
      const file = inp.files && inp.files[0]; if (!file) return;
      const reader = new FileReader();
      reader.onload = function (e) {
        const img = new Image();
        img.onload = function () {
          addImageLayer(img, file.name, e.target.result, false, file); /* user upload (trims + tracks) */
          const nl = layers()[layers().length - 1];
          if (!nl) return;
          const ar = nl.ar || (nl.w / (nl.h || 1)) || 1;
          let w = box.w, h = w / ar;
          if (h > box.h) { h = box.h; w = h * ar; }
          nl.centered = false; nl.w = w; nl.h = h;
          nl.x = box.x + (box.w - w) / 2; nl.y = box.y + (box.h - h) / 2;
          if (lockId) {
            nl._tplLockId = lockId; nl._noResize = true;
            /* Flag as a FILLED TEMPLATE PHOTO (mirrors settleIntoSlot). Without
               _tplPhoto the replaced graphic fell out of every swap surface:
               the action bar hides Swap on locked layers and the personalize
               panel only lists graphics (isSvg) or photos (_tplPhoto) — so the
               customer's upload became un-swappable (Brandon, Jul 17 2026).
               With it, the panel shows "Swap photo" → swapPhoto → OS picker
               (photo library on mobile, file browser on desktop). */
            nl._tplPhoto = true; nl.isRasterUpload = false; nl.name = 'Photo';
          }
          /* Remove the original SVG layer. */
          const arr = layers(); const i = arr.indexOf(layer); if (i >= 0) arr.splice(i, 1);
          S.sel = nl.id;
          render(); updateLayersPanel(); forcePropsRebuild();
          if (typeof updateActionBar === 'function') updateActionBar();
          pushHistory();
          toast('graphic_replaced');
        };
        img.onerror = function () { toast('img_open_fail'); };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    };
    /* Same session-level upload agreement as the Upload tab (was ungated). */
    requireCopyrightAgreement(() => inp.click());
  }

  var STATE_OUTLINES = {};
  var STATE_NAMES = [];

  function loadStateOutlines() {
    try {
      var el = document.getElementById('dt-state-outlines');
      if (!el) return;
      var data = JSON.parse(el.textContent);
      if (data && typeof data === 'object') {
        STATE_OUTLINES = data;
        STATE_NAMES = Object.keys(data).sort();
      }
    } catch (e) { DEBUG && console.warn('[dt] state outlines parse failed', e); }
  }

  var DT_HEART_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
    '<path d="M12 21s-7.5-4.9-7.5-10.4A4.1 4.1 0 0 1 12 7.4a4.1 4.1 0 0 1 7.5 3.2C19.5 16.1 12 21 12 21z" fill="#F47E46"/></svg>';

  /* Re-rasterize a state layer with a new state's markup, preserving its centre
     and footprint while honouring the new shape's aspect ratio. */
  function swapStateLayer(l, name) {
    var markup = STATE_OUTLINES[name]; if (!markup || !l) return;
    var cx = l.centered ? l.x : l.x + l.w / 2;
    var cy = l.centered ? l.y : l.y + l.h / 2;
    var maxDim = Math.max(l.w, l.h);
    var blob = new Blob([svgWithDisplaySize(markup)], { type: 'image/svg+xml' });
    var url = URL.createObjectURL(blob);
    var img = new Image();
    img.onload = function () {
      var trimmed = trimToContent(img);
      var useImg = trimmed ? trimmed.img : img;
      var tW = trimmed ? trimmed.w : (img.naturalWidth || 200);
      var tH = trimmed ? trimmed.h : (img.naturalHeight || 200);
      var w, h;
      if (tW >= tH) { w = maxDim; h = maxDim * (tH / tW); }
      else { h = maxDim; w = maxDim * (tW / tH); }
      URL.revokeObjectURL(url);
      var commit = function () {
        l.img = useImg; l.svgMarkup = markup; l.stateName = name; l.name = name + ' outline';
        l.w = w; l.h = h; l.ar = tW / tH; l.centered = false;
        l.x = cx - w / 2; l.y = cy - h / 2;
        ensureSvgInkBox(l); /* markup changed — refresh the production parity map */
        render(); pushHistory(); updateLayersPanel(); forcePropsRebuild();
        if (typeof syncMobileSelection === 'function') syncMobileSelection();
      };
      /* trimToContent hands back a fresh Image whose src isn't decoded yet —
         paint only once it's ready, or the swap won't show until the next render. */
      if (useImg.complete && useImg.naturalWidth) commit();
      else if (useImg.decode) useImg.decode().then(commit).catch(commit);
      else { useImg.onload = commit; useImg.onerror = commit; }
    };
    img.onerror = function () { URL.revokeObjectURL(url); };
    img.src = url;
  }

  /* Searchable "Change state" library — opens from the outline's edit control. */
  function openStatePicker(layer) {
    if (!layer) return;
    if (!STATE_NAMES.length) { toast('states_loading'); return; }
    document.getElementById('dt-state-picker')?.remove();
    var ov = document.createElement('div');
    ov.className = 'dt-confirm-overlay';
    ov.id = 'dt-state-picker';
    ov.setAttribute('role', 'dialog'); ov.setAttribute('aria-modal', 'true');
    ov.innerHTML =
      '<div class="dt-state-pick">' +
        '<div class="dt-state-pick__head">' +
          '<span class="dt-state-pick__title">Choose a state</span>' +
          '<button type="button" class="dt-rbg-close" id="dt-state-close">&times;</button>' +
        '</div>' +
        '<input type="search" id="dt-state-search" class="dt-input" placeholder="Search states…" autocomplete="off">' +
        '<div class="dt-state-grid" id="dt-state-grid"></div>' +
        '<button type="button" class="dt-state-recolor" id="dt-state-recolor">Recolor outline</button>' +
      '</div>';
    (document.querySelector('.dt-body') || document.body).appendChild(ov);
    var grid = ov.querySelector('#dt-state-grid');
    var search = ov.querySelector('#dt-state-search');
    function draw(q) {
      q = (q || '').trim().toLowerCase();
      var list = q ? STATE_NAMES.filter(function (n) { return n.toLowerCase().indexOf(q) !== -1; }) : STATE_NAMES;
      grid.innerHTML = list.length
        ? list.map(function (n) {
            var cur = (n === layer.stateName) ? ' is-current' : '';
            return '<button type="button" class="dt-state-cell' + cur + '" data-name="' + escHtml(n) + '">' +
              '<span class="dt-state-cell__art">' + (STATE_OUTLINES[n] || '') + '</span>' +
              '<span class="dt-state-cell__name">' + escHtml(n) + '</span></button>';
          }).join('')
        : '<div class="dt-empty-state">No states match</div>';
      grid.querySelectorAll('.dt-state-cell').forEach(function (c) {
        c.addEventListener('click', function () {
          swapStateLayer(layer, c.dataset.name);
          close();
        });
      });
    }
    var teardown = wireModalA11y(ov, { onEscape: function () { close(); }, initial: function () { return search; } });
    function close() { teardown(); ov.remove(); }
    ov.querySelector('#dt-state-close').addEventListener('click', close);
    ov.addEventListener('click', function (e) { if (e.target === ov) close(); });
    ov.querySelector('#dt-state-recolor').addEventListener('click', function () { close(); openSvgEditor(layer); });
    search.addEventListener('input', function () { draw(this.value); });
    draw('');
  }

  /* Apply the State Outline template: outline + name line + draggable heart. */
  function applyStateTemplate() {
    var pa = S.pa;
    if (!pa) { toast('pick_side'); return; }
    if (isMobile() && S._closeMobileDrawer) S._closeMobileDrawer();
    var defaultState = STATE_NAMES.indexOf('Texas') !== -1 ? 'Texas' : (STATE_NAMES[0] || null);
    if (!defaultState) { toast('states_loading'); return; }
    var lockId = uid();  /* TEMPLATE LOCK: state template acts as one unit (heart stays draggable) */

    /* Family-name text line, fit + centred under the outline */
    var _stateSeed = seedTextColor(); /* UX-Aug20: auto-contrast for state template */
    var fs = Math.round(pa.h * 0.10);
    var nameLayer = {
      id: uid(), type: 'text', name: 'Family Name', text: 'The Carters',
      x: pa.x + pa.w / 2, y: pa.y + pa.h * 0.86, w: 0, h: fs,
      font: pickStarterFont('script'), fontSize: fs,
      color: _stateSeed.color, _autoColor: _stateSeed.auto,
      bold: false, italic: false, textAlign: 'center', rotation: 0,
      flipH: false, flipV: false, visible: true, centered: true, ar: null, isRasterUpload: false,
      _tplLockId: lockId,
    };
    measureText(nameLayer);
    if (nameLayer.w > pa.w * 0.88 && nameLayer.w > 0) {
      nameLayer.fontSize = Math.max(10, Math.round(nameLayer.fontSize * (pa.w * 0.88) / nameLayer.w));
      measureText(nameLayer);
    }
    nameLayer.ar = nameLayer.w / (nameLayer.h || 1);

    /* State outline (SVG layer), then the heart on top, then the name */
    addSvgLayer(STATE_OUTLINES[defaultState], defaultState + ' outline', { isStateOutline: true, stateName: defaultState, _tplLockId: lockId });
    /* position the outline in the upper portion once it lands */
    var ptries = 0;
    var place = function () {
      var arr = layers();
      var sl = null;
      for (var i = arr.length - 1; i >= 0; i--) { if (arr[i].isStateOutline) { sl = arr[i]; break; } }
      if (!sl) { if (++ptries < 80) setTimeout(place, 30); return; }
      var targetH = pa.h * 0.58;
      var sc = targetH / (sl.h || targetH);
      sl.w *= sc; sl.h *= sc;
      sl.centered = false;
      sl.x = pa.x + (pa.w - sl.w) / 2;
      sl.y = pa.y + pa.h * 0.40 - sl.h / 2;
      pushLayer(nameLayer);
      addSvgLayer(DT_HEART_SVG, 'Heart', { isStateHeart: true, _tplLockId: lockId, _lockDraggable: true });
      var htries = 0;
      var placeHeart = function () {
        var a2 = layers(), hl = null;
        for (var k = a2.length - 1; k >= 0; k--) { if (a2[k].isStateHeart) { hl = a2[k]; break; } }
        if (!hl) { if (++htries < 80) setTimeout(placeHeart, 30); return; }
        var hh = pa.h * 0.12, hw = hh;
        hl.w = hw; hl.h = hh; hl.centered = false;
        hl.x = pa.x + pa.w / 2 - hw / 2;
        hl.y = pa.y + pa.h * 0.40 - hh / 2;
        S.sel = sl.id;
        render(); pushHistory(); updateLayersPanel(); forcePropsRebuild();
        if (isMobile()) requestZoomToPA(50, true);
        toast('state_hint');
        openStatePicker(sl);
      };
      placeHeart();
    };
    place();
  }

  /* ══════════════════════════════════════════════════════════════════
   * COMPLETION CELEBRATION — rewards finishing, fires on successful cart add
   * ══════════════════════════════════════════════════════════════════ */
  function showCelebration() {
    var host = app || document.querySelector('.dt-app') || document.body;
    if (!host || document.querySelector('.dt-celebrate')) return;
    var ov = document.createElement('div');
    ov.className = 'dt-celebrate';
    ov.setAttribute('role', 'status');
    ov.setAttribute('aria-live', 'polite');
    var colors = ['#F47E46', '#344E41', '#E06A38', '#F2C94C', '#7BA68F'];
    var conf = '';
    for (var i = 0; i < 52; i++) {
      var left = Math.round(Math.random() * 100);
      var delay = (Math.random() * 0.45).toFixed(2);
      var dur = (1.4 + Math.random() * 0.9).toFixed(2);
      var col = colors[i % colors.length];
      var w = Math.round(6 + Math.random() * 6);
      var drift = Math.round(-40 + Math.random() * 80);
      conf += '<span class="dt-confetti" aria-hidden="true" style="left:' + left + '%;--dt-drift:' + drift + 'px;background:' + col +
        ';width:' + w + 'px;height:' + Math.round(w * 0.55) + 'px;animation-delay:' + delay + 's;animation-duration:' + dur + 's;"></span>';
    }
    ov.innerHTML = conf +
      '<div class="dt-celebrate__card">' +
        '<div class="dt-celebrate__icon"><svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>' +
        '<div class="dt-celebrate__title">Your design is ready!</div>' +
        '<div class="dt-celebrate__sub">Taking you to your cart\u2026</div>' +
      '</div>';
    host.appendChild(ov);
  }

  function celebrateThenGoToCart() {
    S._allowUnload = true; /* design is in the cart — don't fire the leave-site prompt */
    try { showCelebration(); } catch (e) { /* never let the celebration block the redirect */ }
    setTimeout(function () { window.location.href = '/cart'; }, 1500);
  }

  /* Lightweight, repeatable toast (reuses the shared .dt-toast element/timer) */
  /* ══════════════════════════════════════════════════════════════════
   * IN-UI COLOR PICKER (MOBILE) — Brandon, Jul 17 2026
   * On mobile the native color dialog never opens. A capture-phase click
   * interceptor catches every <input type="color"> activation (direct or
   * via wrapping <label>) and opens this picker instead. It applies colors
   * by setting input.value and dispatching synthetic input/change events,
   * so every existing handler (text color, monogram, tint, SVG editor,
   * template fields, backdrop seed) works unchanged. Desktop untouched.
   * ══════════════════════════════════════════════════════════════════ */
  const RECENT_COLORS_KEY = 'dt_recent_colors';

  function _hex6(c) {
    if (!c) return null;
    c = String(c).trim().toLowerCase();
    let m = c.match(/^#([0-9a-f]{3})$/);
    if (m) return '#' + m[1][0] + m[1][0] + m[1][1] + m[1][1] + m[1][2] + m[1][2];
    m = c.match(/^#([0-9a-f]{6})$/);
    if (m) return '#' + m[1];
    m = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (m) return '#' + [m[1], m[2], m[3]].map(n => (+n).toString(16).padStart(2, '0')).join('');
    return null;
  }
  function _hexToHsv(hex) {
    const n = parseInt(hex.slice(1), 16);
    const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    let h = 0;
    if (d) {
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60; if (h < 0) h += 360;
    }
    return { h, s: mx ? d / mx : 0, v: mx };
  }
  function _hsvToHex(h, s, v) {
    const f = n => {
      const k = (n + h / 60) % 6;
      const c = v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
      return Math.round(c * 255).toString(16).padStart(2, '0');
    };
    return '#' + f(5) + f(3) + f(1);
  }

  /** Distinct colors currently used by visible layers across ALL sides:
   *  text colors, monogram tones, raster tints, and SVG fills/strokes. */
  function designColorsInUse() {
    const seen = [];
    const add = c => { const h = _hex6(c); if (h && seen.indexOf(h) === -1) seen.push(h); };
    Object.values(S.byView).forEach(arr => (arr || []).forEach(l => {
      if (!l || l.visible === false || l.isBackdrop) return;
      if (l.type === 'text') add(l.color);
      if (l.isMonogram) { add(l.monoColor); add(l.monoColor2); }
      if (l.tintColor) add(l.tintColor);
      if (l.isSvg && l.svgMarkup) {
        const ms = l.svgMarkup.match(/(?:fill|stroke)="(#[0-9a-fA-F]{3,6}|rgb\([^)]*\))"/g) || [];
        for (let i = 0; i < ms.length && i < 40; i++) add(ms[i].slice(ms[i].indexOf('"') + 1, -1));
      }
    }));
    return seen.slice(0, 12);
  }
  function _recentColors() {
    try { return (JSON.parse(localStorage.getItem(RECENT_COLORS_KEY) || '[]') || []).filter(_hex6).slice(0, 10); }
    catch (_) { return []; }
  }
  function _pushRecentColor(hex) {
    hex = _hex6(hex); if (!hex) return;
    try {
      const list = _recentColors().filter(c => c !== hex);
      list.unshift(hex);
      localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(list.slice(0, 10)));
    } catch (_) {}
  }

  let _cpickState = null; /* { sel: {id?, cls?, key?}, ref, hsv, opened } */

  function _cpickResolveInput() {
    if (!_cpickState) return null;
    const s = _cpickState.sel;
    let el = null;
    if (s.id) el = document.getElementById(s.id);
    else if (s.cls) {
      el = document.querySelector('input[type="color"].' + s.cls + (s.key ? '[data-' + s.key[0] + '="' + s.key[1] + '"]' : ''));
    }
    /* Fall back to the original node (still fine if not re-rendered) */
    return el || _cpickState.ref;
  }
  function _cpickApply(hex, fireChange) {
    const inp = _cpickResolveInput(); if (!inp) return;
    inp.value = hex;
    inp.dispatchEvent(new Event('input', { bubbles: true }));
    if (fireChange) inp.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function _cpickEl() {
    let p = $('dt-cpick');
    if (p) return p;
    p = document.createElement('div');
    p.className = 'dt-cpick'; p.id = 'dt-cpick'; p.setAttribute('hidden', '');
    p.innerHTML =
      '<div class="dt-cpick__head"><span class="dt-cpick__title">Color</span>' +
      '<span class="dt-cpick__cur"><span class="dt-cpick__chip" id="dt-cpick-chip"></span><span class="dt-cpick__hexlbl" id="dt-cpick-hex">#000000</span></span></div>' +
      '<div class="dt-cpick__sec" id="dt-cpick-used-sec" hidden><div class="dt-cpick__cap">In this design</div><div class="dt-cpick__row" id="dt-cpick-used"></div></div>' +
      '<div class="dt-cpick__sec" id="dt-cpick-recent-sec" hidden><div class="dt-cpick__cap">Recent</div><div class="dt-cpick__row" id="dt-cpick-recent"></div></div>' +
      '<div class="dt-cpick__svwrap"><canvas id="dt-cpick-sv" width="560" height="280"></canvas><div class="dt-cpick__dot" id="dt-cpick-dot"></div></div>' +
      '<input type="range" class="dt-cpick__hue" id="dt-cpick-hue" min="0" max="360" step="1" aria-label="Hue">' +
      '<button type="button" class="dt-btn dt-btn--primary dt-cpick__done" id="dt-cpick-done">Done</button>';
    document.body.appendChild(p);
    /* Interactions inside the picker never reach the document-level
       outside-tap closers of other popovers. */
    p.addEventListener('click', e => e.stopPropagation());
    p.querySelector('#dt-cpick-done').addEventListener('click', closeDtColorPop);

    const sv = p.querySelector('#dt-cpick-sv');
    const pick = ev => {
      const r = sv.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, ((ev.touches ? ev.touches[0].clientX : ev.clientX) - r.left) / r.width));
      const y = Math.max(0, Math.min(1, ((ev.touches ? ev.touches[0].clientY : ev.clientY) - r.top) / r.height));
      if (!_cpickState) return;
      _cpickState.hsv.s = x; _cpickState.hsv.v = 1 - y;
      _cpickSync(false);
      _cpickApply(_hsvToHex(_cpickState.hsv.h, x, 1 - y), false);
    };
    let dragging = false;
    sv.addEventListener('pointerdown', e => { dragging = true; sv.setPointerCapture(e.pointerId); pick(e); e.preventDefault(); });
    sv.addEventListener('pointermove', e => { if (dragging) pick(e); });
    sv.addEventListener('pointerup', () => { dragging = false; });
    p.querySelector('#dt-cpick-hue').addEventListener('input', function () {
      if (!_cpickState) return;
      _cpickState.hsv.h = +this.value;
      _cpickSync(false);
      _cpickApply(_hsvToHex(_cpickState.hsv.h, _cpickState.hsv.s, _cpickState.hsv.v), false);
    });
    return p;
  }

  function _cpickDrawSV() {
    const sv = document.getElementById('dt-cpick-sv'); if (!sv || !_cpickState) return;
    const ctx2 = sv.getContext('2d');
    ctx2.fillStyle = _hsvToHex(_cpickState.hsv.h, 1, 1);
    ctx2.fillRect(0, 0, sv.width, sv.height);
    let g = ctx2.createLinearGradient(0, 0, sv.width, 0);
    g.addColorStop(0, '#ffffff'); g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx2.fillStyle = g; ctx2.fillRect(0, 0, sv.width, sv.height);
    g = ctx2.createLinearGradient(0, 0, 0, sv.height);
    g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, '#000000');
    ctx2.fillStyle = g; ctx2.fillRect(0, 0, sv.width, sv.height);
  }
  /** Refresh chip/hex/dot/hue/canvas from _cpickState. redrawSwatches only on open. */
  function _cpickSync(full) {
    const st = _cpickState; if (!st) return;
    const hex = _hsvToHex(st.hsv.h, st.hsv.s, st.hsv.v);
    const chip = $('dt-cpick-chip'), hx = $('dt-cpick-hex'), hue = $('dt-cpick-hue'), dot = $('dt-cpick-dot');
    if (chip) chip.style.background = hex;
    if (hx) hx.textContent = hex;
    if (hue && full) hue.value = Math.round(st.hsv.h);
    _cpickDrawSV();
    const wrap = document.querySelector('.dt-cpick__svwrap');
    if (dot && wrap) {
      dot.style.left = (st.hsv.s * 100) + '%';
      dot.style.top = ((1 - st.hsv.v) * 100) + '%';
      dot.style.background = hex;
    }
    if (full) {
      const build = (rowId, secId, colors) => {
        const row = $(rowId), sec = $(secId);
        if (!row || !sec) return;
        if (!colors.length) { sec.setAttribute('hidden', ''); return; }
        sec.removeAttribute('hidden');
        row.innerHTML = colors.map(c =>
          '<button type="button" class="dt-cpick__sw" data-c="' + c + '" style="background:' + c +
          (c === '#ffffff' ? ';border-color:var(--dt-border-2)' : '') + '" aria-label="' + c + '"></button>').join('');
        row.querySelectorAll('.dt-cpick__sw').forEach(b => b.addEventListener('click', () => {
          const h = _hexToHsv(b.dataset.c);
          _cpickState.hsv = h;
          _cpickSync(true);
          _cpickApply(b.dataset.c, false);
        }));
      };
      build('dt-cpick-used', 'dt-cpick-used-sec', designColorsInUse());
      build('dt-cpick-recent', 'dt-cpick-recent-sec', _recentColors());
    }
  }

  function openDtColorPop(inp) {
    const p = _cpickEl();
    /* Retargeting while open (tap another color chip): drop the previous
       outside-closer FIRST or it would close the picker we just reopened
       and leak a dead document listener. */
    if (p._onDoc) { document.removeEventListener('click', p._onDoc, true); p._onDoc = null; }
    const sel = { id: inp.id || null, cls: null, key: null };
    if (!sel.id) {
      if (inp.classList.contains('dt-svg-cinp')) { sel.cls = 'dt-svg-cinp'; sel.key = ['i', inp.dataset.i]; }
      else if (inp.classList.contains('dt-tpl-field__color')) { sel.cls = 'dt-tpl-field__color'; sel.key = ['ti', inp.dataset.ti]; }
    }
    const start = _hex6(inp.value) || '#111111';
    _cpickState = { sel, ref: inp, hsv: _hexToHsv(start), opened: Date.now() };
    p.removeAttribute('hidden');
    requestAnimationFrame(() => p.classList.add('open'));
    _cpickSync(true);
    /* Outside tap closes (attach next tick so the opening tap doesn't). */
    setTimeout(() => {
      const onDoc = ev => { if (!p.contains(ev.target)) closeDtColorPop(); };
      p._onDoc = onDoc;
      document.addEventListener('click', onDoc, true);
    }, 0);
  }
  function closeDtColorPop() {
    const p = $('dt-cpick'); if (!p) return;
    if (p._onDoc) { document.removeEventListener('click', p._onDoc, true); p._onDoc = null; }
    if (p.hasAttribute('hidden')) return;
    if (_cpickState) {
      const hex = _hsvToHex(_cpickState.hsv.h, _cpickState.hsv.s, _cpickState.hsv.v);
      _cpickApply(hex, true);          /* change → history hooks fire once */
      _pushRecentColor(hex);
    }
    p.classList.remove('open');
    p.setAttribute('hidden', '');
    _cpickState = null;
  }

  /* Capture-phase interception: on mobile, no native color dialog, ever.
     iOS Safari opens its native colour picker on INPUT FOCUS (which fires
     before click), so intercepting click alone isn't enough. We block focus
     at the earliest pointer event (pointerdown / mousedown) and then open
     our custom picker on the later click event. */
  function _isColorInput(t) {
    if (t && t.tagName === 'INPUT' && t.type === 'color') return t;
    if (t && t.closest) {
      var lab = t.closest('label');
      if (lab) {
        var ctrl = lab.querySelector('input, textarea, select, button');
        if (ctrl && ctrl.tagName === 'INPUT' && ctrl.type === 'color') return ctrl;
      }
    }
    return null;
  }
  /* Block focus BEFORE it happens — prevents iOS native picker. */
  ['pointerdown', 'mousedown', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      if (!isMobile()) return;
      if (_isColorInput(e.target)) e.preventDefault();
    }, true);
  });
  /* Safety net: if focus lands on a color input anyway, blur immediately. */
  document.addEventListener('focus', function (e) {
    if (!isMobile()) return;
    if (e.target && e.target.tagName === 'INPUT' && e.target.type === 'color') {
      e.target.blur();
    }
  }, true);
  /* Open our custom picker on click (fires after pointerdown). */
  document.addEventListener('click', function (e) {
    if (!isMobile()) return;
    var inp = _isColorInput(e.target);
    if (!inp) return;
    e.preventDefault();
    e.stopPropagation();
    openDtColorPop(inp);
  }, true);

  /* ══════════════════════════════════════════════════════════════════
   * TOAST STRINGS (Brandon, Jul 17 2026) — every toast lives here, keyed,
   * written SHORT so it always fits one line on mobile. The editable copy
   * is the JSON block in design-tool.liquid (#dt-toast-strings) — edit it
   * like a Shopify template JSON; it overrides these defaults at load.
   * {vars} are replaced by toast(key, vars). Unknown keys fall back to
   * showing the key itself so a typo is visible, never silent.
   * ══════════════════════════════════════════════════════════════════ */
  const TOASTS = {
    side_editing:       'Editing {side} — {prev} saved',
    side_upcharge:      'Extra side = separate item (+{price})',
    method_engrave:     'Recolored for engraving',
    method_print:       'Recolored for full color',
    preview_empty:      'Add something first',
    preview_saved:      'Preview downloaded',
    removebg_fail:      'Remove BG failed — try again',
    color_remove_fail:  'Color removal failed — try again',
    text_contrast:      'Hard to read? Tap text to recolor',
    recipe_added:       'Now make it yours!',
    pick_side:          'Pick a product side first',
    tpl_added_text:     'Template added — edit your text',
    tpl_added:          'Template added!',
    tpl_applied_text:   'Applied — edit your text',
    tpl_applied:        'Template applied!',
    tpl_loading:        'Loading template…',
    tpl_load_fail:      'Template failed to load',
    tpl_empty:          'That template is empty',
    tpl_no_design:      'Template has no design',
    tpl_mask_reframe:   'Use Reframe to reposition',
    switch_side:        'Switch to the {side} side first',
    save_tpl_empty:     'Add text or art first',
    img_open_fail:      'Couldn’t open that image',
    photo_swap_bg:      'Swapped — tap Remove BG again',
    graphic_replaced:   'Graphic replaced!',
    graphic_swapped:    'Graphic swapped!',
    graphic_load_fail:  'Couldn’t load that graphic',
    clip_swap_pick:     'Pick a replacement graphic',
    no_editable_colors: 'No editable colors here',
    engrave_one_color:  'Choose what gets engraved',
    states_loading:     'States loading — one moment',
    state_hint:         'Drag the heart to your town',
    autosaved:          'Saved on this device',
    copied:             'Copied',
    copy_fail:          'Copy failed',
    copy_select:        'Select a field to copy',
    drop_image:         'Drop an image file (PNG, JPG, SVG, PDF)',
    dup_blocked:        'Grouped elements can’t duplicate',
    approve_required:   'Confirm your design review first',
  };
  /* Merge overrides from the theme's JSON block (only known keys). */
  (function () {
    try {
      const el = document.getElementById('dt-toast-strings');
      if (!el) return;
      const data = JSON.parse(el.textContent || '{}');
      Object.keys(data).forEach(k => {
        if (typeof data[k] === 'string' && data[k].trim() && Object.prototype.hasOwnProperty.call(TOASTS, k)) TOASTS[k] = data[k];
      });
    } catch (_) { /* malformed JSON → defaults */ }
  })();
  function toast(key, vars, ms) {
    let msg = TOASTS[key] || key;
    /* Replacer FUNCTION: a plain string replacement treats $ sequences
       specially — '$20' in a price must land literally. */
    if (vars) Object.keys(vars).forEach(k => { msg = msg.replace('{' + k + '}', () => vars[k]); });
    flashToast(msg, ms);
  }

  function flashToast(msg, ms) {
    var t = document.querySelector('.dt-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'dt-toast';
      t.innerHTML = '<span class="dt-toast__text"></span>';
      (document.querySelector('.dt-body') || document.body).appendChild(t);
    }
    var span = t.querySelector('.dt-toast__text') || t;
    span.textContent = msg;
    t.style.background = '';            /* #4: clear any bg left by a previous styled toast */
    t.classList.add('visible');
    clearTimeout(t._timer);             /* #3: share the single timer handle */
    t._timer = setTimeout(function () { t.classList.remove('visible'); }, ms || 2500);
  }


  /* Desktop: create layer + immediately enter contenteditable edit mode */
  function addTextLayerAndEdit(text, pos) {
    /* If there's already an unmodified default text layer, select it instead of creating another */
    const existingDefault = layers().find(l => l.type === 'text' && l.text === 'Your Text');
    if (existingDefault) {
      S.sel = existingDefault.id;
      render();
      buildProps();
      enterInlineEdit(existingDefault, true);
      _addingText = false;
      return;
    }

    const pa = S.pa;
    const _seed = seedTextColor(); /* UX-Aug15 #1 */
    const l = {
      id: uid(), type: 'text', name: text.replace(/\n/g, ' ').slice(0, 25), text,
      x: (pos && pos.x) || pa.x + pa.w/2, y: (pos && pos.y) || pa.y + pa.h/2,
      w: 0, h: S.textSize,
      font: S.textFont, fontSize: S.textSize, color: _seed.color, _autoColor: _seed.auto,
      bold: S.textBold, italic: S.textItalic,
      rotation: 0, flipH: false, flipV: false, visible: true, centered: true,
      ar: null, isRasterUpload: false,
    };
    document.fonts.load(l.fontSize + 'px "' + l.font + '"')
      .catch(() => { /* font load failed — proceed with fallback */ })
      .then(() => {
        try {
          measureText(l);
          /* Auto-fit: scale fontSize so text fills ~90% of print area width */
          if (l.w > 0 && pa.w > 0) {
            const targetW = pa.w * 0.9;
            const scaleW = targetW / l.w;
            const scaleH = l.h > 0 ? (pa.h * 0.85) / l.h : Infinity;
            const scale = Math.min(scaleW, scaleH);
            l.fontSize = Math.round(l.fontSize * scale);
            l.fontSize = Math.max(10, Math.min(120, l.fontSize));
            measureText(l);
          }
          l.ar = l.w / (l.h || 1);
          pushLayer(l);
          /* UX-Aug15 W0: desktop Add Text never fired dt_layer_add (only addTextLayer did) —
             the desktop layer_add funnel was an undercount. Same call as addTextLayer. */
          dtAnalytics.layerAdd('text', text);
          enterInlineEdit(l, true);
        } catch (err) {
          DEBUG && console.error('[dt] addTextLayerAndEdit error:', err);
        }
      })
      .finally(() => { _addingText = false; });
  }

  /* ══════════════════════════════════════════════════════════════════
   * UPLOAD + REMOVE.BG
   * ══════════════════════════════════════════════════════════════════ */
  /* Keyboard support for the role="button" drop zones (Enter/Space → open
     picker). The zones are click-activated and on desktop the visible Browse
     button is hidden, so without this keyboard-only users couldn't upload. */
  function zoneKeyActivate(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      e.currentTarget.click();
    }
  }

  /* ── Copyright agreement gate ──────────────────────────
     Session-level: user must agree once before any upload. The agreement
     persists for the session (sessionStorage) so it doesn't re-prompt
     on every file. Shows a confirmation dialog on first upload attempt. */
  let _copyrightAgreed = false;
  try { _copyrightAgreed = sessionStorage.getItem('dt_copyright_agreed') === '1'; } catch(_) {}

  function requireCopyrightAgreement(onAccept) {
    if (_copyrightAgreed) { onAccept(); return; }
    const ov = document.createElement('div'); ov.className = 'dt-rbg-overlay';
    ov.setAttribute('role', 'dialog'); ov.setAttribute('aria-modal', 'true');
    ov.innerHTML = '<div class="dt-rbg-panel dt-rbg-panel--md">' +
      '<div style="font-size:16px;font-weight:700;margin-bottom:12px;color:var(--dt-text,#222)">Upload agreement</div>' +
      '<label style="display:flex;gap:10px;align-items:flex-start;cursor:pointer;font-size:13px;line-height:1.5;color:var(--dt-text-2,#444);margin-bottom:20px">' +
        '<input type="checkbox" id="dt-copyright-check" style="margin-top:3px;width:18px;height:18px;flex-shrink:0;accent-color:var(--dt-forest)">' +
        '<span>I confirm that I own or have the right to use all images and artwork I upload. I will not upload copyrighted material without permission from the rights holder.</span>' +
      '</label>' +
      '<div style="display:flex;gap:10px">' +
        '<button id="dt-copyright-ok" class="dt-btn dt-btn-primary" style="flex:1;opacity:0.5;pointer-events:none" disabled>Continue</button>' +
        '<button id="dt-copyright-cancel" class="dt-btn" style="flex:1;background:var(--dt-surface,#f5f5f5);color:var(--dt-text,#333)">Cancel</button>' +
      '</div></div>';
    document.body.appendChild(ov);
    const chk = ov.querySelector('#dt-copyright-check');
    const okBtn = ov.querySelector('#dt-copyright-ok');
    chk.addEventListener('change', function() {
      okBtn.disabled = !chk.checked;
      okBtn.style.opacity = chk.checked ? '1' : '0.5';
      okBtn.style.pointerEvents = chk.checked ? '' : 'none';
    });
    okBtn.addEventListener('click', function() {
      _copyrightAgreed = true;
      try { sessionStorage.setItem('dt_copyright_agreed', '1'); } catch(_) {}
      ov.remove();
      onAccept();
    });
    ov.querySelector('#dt-copyright-cancel').addEventListener('click', function() { ov.remove(); });
    ov.addEventListener('click', function(e) { if (e.target === ov) ov.remove(); });
  }

  function setupUpload() {
    const zone = $('dt-upload-zone'), fileInp = $('dt-file-input'), camInp = $('dt-camera-input');
    /* iOS's native file sheet already offers Take Photo, so a separate camera
       button is redundant there. Kept for Android/desktop where the picker
       opens straight into a file browser. */
    const _iosUA = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    /* rev 6b (Brandon, Aug 17): the separate Camera button is redundant on EVERY touch
       device, not just iOS — the Browse/native file picker already offers Take Photo
       alongside Photo Library and Files. iOS removed it via _iosUA; extend that to all
       mobile/touch (Android + emulated), leaving Browse Files as the single entry. The
       Browse button then flexes to fill the row (.dt-upload-btns is display:flex). */
    if (_iosUA || isMobile()) { $('dt-upload-camera')?.remove(); $('dt-engrave-camera')?.remove(); }
    if (!zone || !fileInp) return;
    zone.addEventListener('click', () => requireCopyrightAgreement(() => fileInp.click()));
    zone.addEventListener('keydown', zoneKeyActivate);
    zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('drag-over'); });
    zone.addEventListener('dragleave', () => zone.classList.remove('drag-over'));
    zone.addEventListener('drop', e => {
      e.preventDefault(); zone.classList.remove('drag-over');
      /* Capture the File NOW — e.dataTransfer goes inert once the drop event
         ends, so reading files[0] inside the copyright-modal callback returned
         undefined and threw at file.type (GA4: Safari + Chrome, Jul 2026). */
      const dropped = e.dataTransfer.files[0];
      if (dropped) requireCopyrightAgreement(() => handleFile(dropped));
    });
    $('dt-upload-camera')?.addEventListener('click', e => { e.stopPropagation(); requireCopyrightAgreement(() => camInp?.click()); });
    $('dt-upload-browse')?.addEventListener('click', e => { e.stopPropagation(); requireCopyrightAgreement(() => fileInp.click()); });
    fileInp.addEventListener('change', () => { if (fileInp.files[0]) handleFile(fileInp.files[0]); fileInp.value = ''; });
    camInp?.addEventListener('change', () => { if (camInp.files[0]) handleFile(camInp.files[0]); camInp.value = ''; });

    /* ── Engrave single-zone upload wiring ───────────────
       One button. handleEngraveUpload auto-detects: logos/SVG/PDF → normal
       path; photographs → AI line-art conversion. Mirrors the print zone. */
    const engZone = $('dt-engrave-zone'), engInp = $('dt-engrave-input'), engCamInp = $('dt-engrave-camera-input');
    if (engZone && engInp) {
      engZone.addEventListener('click', () => requireCopyrightAgreement(() => engInp.click()));
      engZone.addEventListener('keydown', zoneKeyActivate);
      engZone.addEventListener('dragover', e => { e.preventDefault(); engZone.classList.add('drag-over'); });
      engZone.addEventListener('dragleave', () => engZone.classList.remove('drag-over'));
      engZone.addEventListener('drop', e => {
        e.preventDefault(); engZone.classList.remove('drag-over');
        /* Same dead-DataTransfer capture as the print drop zone above */
        const dropped = e.dataTransfer.files[0];
        if (dropped) requireCopyrightAgreement(() => handleEngraveUpload(dropped));
      });
      $('dt-engrave-camera')?.addEventListener('click', e => { e.stopPropagation(); requireCopyrightAgreement(() => engCamInp?.click()); });
      $('dt-engrave-browse')?.addEventListener('click', e => { e.stopPropagation(); requireCopyrightAgreement(() => engInp.click()); });
      engInp.addEventListener('change', () => { if (engInp.files[0]) handleEngraveUpload(engInp.files[0]); engInp.value = ''; });
      engCamInp?.addEventListener('change', () => { if (engCamInp.files[0]) handleEngraveUpload(engCamInp.files[0]); engCamInp.value = ''; });
    }

    /* Set initial visibility based on current method */
    const stdUpload = $('dt-upload-standard'), engUpload = $('dt-upload-engrave');
    if (stdUpload && engUpload) {
      stdUpload.style.display = S.method === 'engrave' ? 'none' : '';
      engUpload.style.display = S.method === 'engrave' ? '' : 'none';
    }
  }

  /* Recent uploads — session-only thumbnails for quick re-use */
  S._recentUploads = [];
  function addRecentUpload(b64, name, sourceFile) {
    if (!b64 || S._recentUploads.length >= 8) return;
    if (S._recentUploads.some(r => r.name === name)) return;
    S._recentUploads.unshift({ b64: b64.length < 500000 ? b64 : null, name, sourceFile: sourceFile || null });
    updateRecentUploads();
  }
  function updateRecentUploads() {
    const el = $('dt-recent-uploads'); if (!el) return;
    const items = S._recentUploads.filter(r => r.b64);
    if (!items.length) { el.innerHTML = ''; return; }
    el.innerHTML = `<div class="dt-label" style="margin-bottom:6px">Recent</div>
      <div class="dt-recent-row">${items.map((r, i) =>
        `<div class="dt-recent-thumb" data-i="${i}" title="${r.name}"><img src="${r.b64}" alt=""></div>`
      ).join('')}</div>`;
    el.querySelectorAll('.dt-recent-thumb').forEach((t, i) => {
      t.addEventListener('click', () => {
        const r = items[i]; if (!r?.b64) return;
        const img = new Image(); img.crossOrigin = 'anonymous';
        img.onload = () => addImageLayer(img, r.name, r.b64, false, r.sourceFile);
        img.src = r.b64;
      });
    });
  }

  /* ── Upload Quality Helpers ──────────────────────────── */

  function showUploadError(msg) {
    hideLoading();
    const ov = document.createElement('div'); ov.className = 'dt-rbg-overlay';
    ov.innerHTML = '<div class="dt-rbg-panel dt-rbg-panel--xs dt-rbg-panel--center">' +
      '<div class="dt-dlg-title">Upload Issue</div>' +
      '<div class="dt-dlg-body">' + msg + '</div>' +
      '<button class="dt-rbg-use-btn">Got It</button></div>';
    document.body.appendChild(ov);
    ov.querySelector('button').addEventListener('click', () => ov.remove());
    ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  }

  function showUploadWarning(msg, onContinue) {
    /* The loading overlay is z-index 9999 and input-blocking; this dialog is
       z-index 310. Without hiding the spinner first the dialog renders
       UNDERNEATH it and the customer can't click either button — uploads
       froze on "Processing image..." (customer complaints, Jul 2026).
       Mirrors showUploadError, which has always done this. */
    hideLoading();
    const ov = document.createElement('div'); ov.className = 'dt-rbg-overlay';
    ov.innerHTML = '<div class="dt-rbg-panel dt-rbg-panel--sm dt-rbg-panel--center">' +
      '<div class="dt-dlg-title">Heads Up</div>' +
      '<div class="dt-dlg-body">' + msg + '</div>' +
      '<div style="display:flex;gap:8px">' +
      '<button class="dt-rbg-use-btn" style="flex:1" id="dt-warn-continue">Use Anyway</button>' +
      '<button style="flex:1;padding:10px;border:1.5px solid var(--dt-border);border-radius:8px;background:transparent;font-weight:700;cursor:pointer;font-family:inherit;font-size:13px" id="dt-warn-cancel">Cancel</button>' +
      '</div></div>';
    document.body.appendChild(ov);
    ov.querySelector('#dt-warn-continue').addEventListener('click', () => { ov.remove(); if (onContinue) onContinue(); });
    ov.querySelector('#dt-warn-cancel').addEventListener('click', () => ov.remove());
    ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  }

  function sanitizeSvg(markup) {
    try {
      /* Try strict XML parse first — gives the cleanest DOM-based sanitization */
      const parser = new DOMParser();
      const doc = parser.parseFromString(markup, 'image/svg+xml');

      /* If the XML parse failed (malformed SVG), fall back to regex sanitization.
         Many SVGs from design tools have HTML-like quirks (unquoted attrs, undeclared
         entities) that pass in browsers but fail strict XML mode. */
      if (doc.querySelector('parsererror')) {
        DEBUG && console.warn('[dt] SVG has XML parse issues — using regex sanitization');
        return markup
          .replace(/<script[\s\S]*?<\/script\s*>/gi, '')
          .replace(/<foreignObject[\s\S]*?<\/foreignObject\s*>/gi, '')
          .replace(/\s+on[a-z]+\s*=\s*"[^"]*"/gi, '')
          .replace(/\s+on[a-z]+\s*=\s*'[^']*'/gi, '')
          .replace(/href\s*=\s*"javascript:[^"]*"/gi, 'href=""')
          .replace(/href\s*=\s*'javascript:[^']*'/gi, "href=''")
          .replace(/currentColor/gi, '#000000');
      }

      const svg = doc.documentElement;
      svg.querySelectorAll('script').forEach(el => el.remove());
      svg.querySelectorAll('foreignObject').forEach(el => el.remove());
      svg.querySelectorAll('*').forEach(el => {
        for (const attr of [...el.attributes]) {
          const name = attr.name.toLowerCase();
          if (name.startsWith('on')) el.removeAttribute(attr.name);
          if ((name === 'href' || name === 'xlink:href') &&
              attr.value.trim().toLowerCase().startsWith('javascript:')) {
            el.removeAttribute(attr.name);
          }
        }
      });
      svg.querySelectorAll('style').forEach(el => {
        if (el.textContent.includes('@import')) el.remove();
      });
      /* Resolve currentColor to a concrete value — production files need
         real colors, not CSS keywords. #000000 is what currentColor resolves
         to in a standalone SVG context (no parent CSS). */
      let result = new XMLSerializer().serializeToString(svg);
      result = result.replace(/currentColor/gi, '#000000');
      return result;
    } catch (err) {
      /* If anything goes wrong, return the original markup rather than losing it */
      DEBUG && console.warn('[dt] SVG sanitization failed, using original:', err.message);
      return markup;
    }
  }

  /* ── Photo Detection Hint ────────────────────────────── */
  function isLikelyPhoto(img) {
    /* Downsample to 64x64 and count unique colors. Photos have thousands;
       logos/line art have < 100. */
    try {
      const c = document.createElement('canvas'); c.width = 64; c.height = 64;
      const cx = c.getContext('2d');
      cx.drawImage(img, 0, 0, 64, 64);
      const d = cx.getImageData(0, 0, 64, 64).data;
      /* Signal 1: unique quantized colors (color photos have hundreds).
         Signal 2: soft tonal texture — photos (including B&W/sepia) are full
         of small neighbor-to-neighbor luminance steps, while logos/line art
         are flat regions split by hard edges. Color count alone missed every
         grayscale photo (≤16 quantized grays → routed to the print flow). */
      const colors = new Set();
      const L = new Float32Array(4096), op = new Uint8Array(4096);
      for (let i = 0; i < 4096; i++) {
        const o = i * 4;
        if (d[o + 3] < 128) continue; /* skip transparent */
        op[i] = 1;
        L[i] = 0.299 * d[o] + 0.587 * d[o + 1] + 0.114 * d[o + 2];
        colors.add((d[o] >> 4) + ',' + (d[o + 1] >> 4) + ',' + (d[o + 2] >> 4));
      }
      if (colors.size > 200) return true;
      let soft = 0, hard = 0, n = 0;
      for (let y = 0; y < 64; y++) for (let x = 0; x < 63; x++) {
        const i = y * 64 + x;
        if (!op[i] || !op[i + 1]) continue;
        const df = Math.abs(L[i] - L[i + 1]); n++;
        if (df > 48) hard++; else if (df >= 3) soft++;
      }
      /* v2.9.17: the texture test needs a real sample, and it must not divide by zero.
         MEASURED on the Fleet Marine Force order (Jul 27 2026): a background-removed
         black-line-art logo scored colors=7 (screaming "line art") yet was flagged a
         PHOTO, so print never traced it. Mechanism: the alpha >= 128 gate above skips
         partial-alpha pixels, and on cut-out line art 85% of the ink IS partial alpha —
         so the gate discards exactly the hard black/white boundaries that define line
         art. What survives is the soft interior of each stroke. hard lands at 0, and
         `sf > 3 * (hard/n)` degenerates to `sf > 0`, which is true for anything.
         Only 332 of ~4032 neighbour pairs were even sampled.
         A background-removed PHOTO does not hit this: it still fills the frame (samples
         in the thousands) and keeps hard edges. Grayscale photos — the case this texture
         test was added for — are likewise unaffected, they sample fully.
         So: require a meaningful sample AND at least one hard edge before trusting it;
         otherwise fall back to the colour-count signal alone, which is already correct
         here. Verified both ways on the real files: cut-out n=332 -> not a photo;
         original JPEG n=4032 hard=720 -> test runs, 0.326 > 0.536 false -> not a photo. */
      if (!n || n < 600 || !hard) return false;
      const sf = soft / n;
      return sf > 0.22 && sf > 3 * (hard / n);
    } catch (_) { return false; }
  }

  /* ── v2.9.16: PER-LAYER production classification ────────────────────────
     Every visible layer falls in exactly one bucket, and one production file can
     carry all of them:
       A  isNativeVector  — already real paths, emitted untouched
       B  isTraceableArt  — sent to Vectorizer.ai and emitted as paths
       C  isPhotoLayer    — stays an embedded raster
     (text is neither; it is outlined via opentype.js)
     Module scope on purpose: generateSideProduction decides WHAT to trace and
     generateProductionPDF decides WHAT to embed, and the two must never disagree
     about which bucket a layer is in.
     HARD EXCLUDE (Brandon, Jul 27 2026): removedColor / _rcTrim are ALWAYS photo and
     never traced. Remove Color REPLACES l.img with the edited bitmap, so isLikelyPhoto()
     would be judging that edit — stripping a flat background off a photograph could
     otherwise flip it into the traceable bucket and ship it posterized.
     _bgRemoved IS DELIBERATELY *NOT* IN THAT LIST, despite the name. It does not mean
     "the user removed a background"; it means "this layer has no background to remove",
     and it is set on ANY upload that already carries transparency — see the
     hasMeaningfulTransparency() branch (~6289, comment "already a cutout") plus ~6672
     and the alreadyCut default at ~6829. Excluding it would put every transparent-PNG
     logo — the single most common thing customers upload — into bucket C and trace
     nothing, defeating the entire change. A genuinely background-removed PHOTO is still
     caught: remove.bg output of a photo is still a photo, so isPhoto (persisted at
     upload) or the live isLikelyPhoto() check classifies it correctly. */
  const isNativeVector = l => l.type !== 'text' && !!(l.isSvg && l.svgMarkup);
  const isPhotoLayer   = l => l.type !== 'text' && !isNativeVector(l) && !!(
    l.isPhoto || l._tplPhoto ||
    l.removedColor || l._rcTrim ||
    ((l.isRasterUpload || l._tplPhoto) && l.img && isLikelyPhoto(l.img))
  );
  const isTraceableArt = l => l.type !== 'text' && !isNativeVector(l) && !isPhotoLayer(l);

  function handleFileWithPhotoHint(file) {
    /* Logo path — run through normal handleFile, but after image loads,
       check if it looks like a photo and show a soft hint if so. */
    /* deprecated: superseded by handleEngraveUpload (single auto-detect entry) */
    return handleFile(file);
  }

  /* ── Engrave single-button router ──────────────────────
     One upload entry for engrave mode. Auto-detects and routes silently:
       • SVG / PDF  → normal path (vector / line art by nature)
       • raster photo (isLikelyPhoto) → AI line-art conversion
       • raster logo / line art       → normal path
     If the AI session limit is reached, a detected photo falls back to the
     normal path so the upload never dead-ends. */
  function handleEngraveUpload(file) {
    if (!file) {
      /* Was a silent return — the customer saw nothing happen. Surface it. */
      showUploadError('We couldn’t read that file. Please try uploading it again, or use the file picker instead of drag-and-drop.');
      return;
    }
    const isSvg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (isSvg || isPdf) { handleFile(file); return; }
    if (!file.type.startsWith('image/')) { handleFile(file); return; }

    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        if (isLikelyPhoto(img)) {
          if (_engraveAiSessions < ENGRAVE_AI_LIMIT) {
            handleEngravePhoto(file);
          } else {
            /* Out of AI conversions this session — don't silently mis-route a
               photo through the logo path; tell them, then add it as-is. */
            /* toast, not showTip — showTip is globally disabled (v2.11.0 review C3;
               this message had been silent since the tips were turned off) */
            toast('You’ve used this session’s photo conversions. Adding your photo as-is — you can still position and resize it.', null, 5000);
            handleFile(file);
          }
        } else {
          handleFile(file);
        }
      };
      img.onerror = () => handleFile(file);
      img.src = e.target.result;
    };
    reader.onerror = () => handleFile(file);
    reader.readAsDataURL(file);
  }

  /* ── Engrave Photo AI Conversion ───────────────────── */
  let _engraveAiSessions = 0;
  const ENGRAVE_AI_LIMIT = 5;

  function handleEngravePhoto(file) {
    /* Validate: must be a raster image */
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      showUploadError('The AI conversion works with photographs (PNG, JPG). For logos and line art, use the other upload option.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showUploadError('This file is ' + (file.size / 1024 / 1024).toFixed(1) + 'MB. Maximum is 20MB.');
      return;
    }
    if (_engraveAiSessions >= ENGRAVE_AI_LIMIT) {
      showUploadError('You\u2019ve reached the AI photo-conversion limit for this session. You can still upload a logo, SVG, or simpler line art and we\u2019ll add it directly.');
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      const b64 = e.target.result;
      const img = new Image();
      img.onload = () => {
        /* Downscale to max 2048px and compress to JPEG for the API.
           CRITICAL: letterbox onto a WHITE SQUARE canvas. The image API
           returns fixed-aspect output and CROPS a non-square input to fill
           it — order "Gulf Blues" shipped with both text edges cut off
           because the source was wider than tall. A square input can never
           be cropped (square in → square out); the white padding reads as
           background to the model and is stripped after conversion
           (stripWhiteBackground + ink trim), so nothing extra ships. */
        const nw = img.naturalWidth, nh = img.naturalHeight;
        const needsResize = nw > 2048 || nh > 2048;
        const tw = needsResize ? Math.round(nw * (2048 / Math.max(nw, nh))) : nw;
        const th = needsResize ? Math.round(nh * (2048 / Math.max(nw, nh))) : nh;
        const side = Math.max(tw, th);
        /* Always re-encode as JPEG — PNG base64 can be 10x larger */
        const c = document.createElement('canvas'); c.width = side; c.height = side;
        const cx2 = c.getContext('2d');
        cx2.fillStyle = '#FFFFFF'; cx2.fillRect(0, 0, side, side);
        cx2.drawImage(img, Math.round((side - tw) / 2), Math.round((side - th) / 2), tw, th);
        const sendB64 = c.toDataURL('image/jpeg', 0.92);
        processEngraveAI(sendB64, b64, file.name, undefined, file);
      };
      img.onerror = () => showUploadError("Couldn\u2019t open this file. It may be corrupt.");
      img.src = b64;
    };
    reader.readAsDataURL(file);
  }

  async function processEngraveAI(apiB64, originalB64, fileName, mode, sourceFile) {
    mode = mode || 'detailed';

    /* ── Loading modal with timer ──────────────────────── */
    const ov = document.createElement('div'); ov.className = 'dt-rbg-overlay';
    let seconds = 0;
    ov.innerHTML =
      '<div class="dt-rbg-panel dt-rbg-panel--xs dt-rbg-panel--center">' +
      '<div style="font-size:15px;font-weight:800;margin-bottom:6px">Converting Your Photo</div>' +
      '<div style="font-size:12px;color:var(--dt-muted);margin-bottom:16px">Our AI is turning your photo into engrave-ready line art. This usually takes 30\u201360 seconds.</div>' +
      '<div id="dt-ai-timer" style="font-size:24px;font-weight:800;color:var(--dt-coral);margin-bottom:12px">0s</div>' +
      '<div style="width:100%;height:4px;border-radius:2px;background:var(--dt-border);overflow:hidden"><div id="dt-ai-bar" style="height:100%;width:0%;background:var(--dt-coral);transition:width 1s linear;border-radius:2px"></div></div>' +
      '<button id="dt-ai-cancel" style="margin-top:14px;padding:8px 20px;border:1.5px solid var(--dt-border);border-radius:8px;background:transparent;font-size:12px;font-weight:700;cursor:pointer;font-family:var(--dt-font,system-ui)">Cancel</button>' +
      '</div>';
    document.body.appendChild(ov);

    const timer = setInterval(() => {
      seconds++;
      const el = ov.querySelector('#dt-ai-timer'); if (el) el.textContent = seconds + 's';
      const bar = ov.querySelector('#dt-ai-bar');
      if (bar) bar.style.width = Math.min(95, (seconds / 60) * 100) + '%';
    }, 1000);

    let aborted = false;
    const controller = new AbortController();
    ov.querySelector('#dt-ai-cancel')?.addEventListener('click', () => { aborted = true; controller.abort(); clearInterval(timer); ov.remove(); S._slotIntent = null; });

    try {
      const result = await callEngraveAI(apiB64, mode, controller.signal);
      clearInterval(timer);
      if (aborted) return;
      ov.remove();
      _engraveAiSessions++;

      /* ── Preview modal: original vs AI result ─────── */
      showEngravePreview(originalB64, result, fileName, apiB64, sourceFile);

    } catch (err) {
      clearInterval(timer);
      if (aborted) return;
      ov.remove();

      const msg = err.message || 'AI conversion failed';
      if (msg.includes('content_policy')) {
        showUploadError('This image couldn\u2019t be processed due to content restrictions. Try a different photo.');
      } else if (msg.includes('rate_limit')) {
        showUploadError('You\u2019ve reached the AI conversion limit. Please try again later.');
      } else if (msg.includes('auth_failed')) {
        showUploadError('Session expired. Please refresh the page and try again.');
      } else {
        showUploadError('Something went wrong converting your photo. ' + msg);
      }
    }
  }

  async function callEngraveAI(imageB64, mode, signal) {
    const url = CFG.engraveAiUrl;
    if (!url) throw new Error('AI conversion not configured');

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Customer-Id': String(CFG.customerId || 0),
        'X-Customer-Token': CFG.customerToken || '',
      },
      body: JSON.stringify({ image: imageB64, mode: mode || 'detailed' }),
      signal,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) throw new Error('auth_failed');
      if (res.status === 429) throw new Error(data.error || 'rate_limit');
      throw new Error(data.error || data.message || 'Server error ' + res.status);
    }

    const data = await res.json();
    if (!data.image) throw new Error('No image in response');
    return data.image;
  }

  function showEngravePreview(originalB64, resultB64, fileName, apiB64, sourceFile) {
    const ov = document.createElement('div'); ov.className = 'dt-rbg-overlay';
    ov.innerHTML =
      '<div class="dt-rbg-panel dt-rbg-panel--lg">' +
      '<div class="dt-rbg-header"><span class="dt-rbg-title">Engrave Art Preview</span><button class="dt-rbg-close" id="dt-aipreview-close">&times;</button></div>' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px">' +
      '<div style="text-align:center"><div style="font-size:10px;font-weight:700;color:var(--dt-muted);margin-bottom:6px;text-transform:uppercase">Your Photo</div>' +
      '<div style="background:var(--dt-bg);border-radius:10px;padding:8px;aspect-ratio:1;display:flex;align-items:center;justify-content:center"><img src="' + originalB64 + '" style="max-width:100%;max-height:200px;border-radius:6px"></div></div>' +
      '<div style="text-align:center"><div style="font-size:10px;font-weight:700;color:var(--dt-coral);margin-bottom:6px;text-transform:uppercase">Engrave Ready</div>' +
      '<div id="dt-aipreview-zoom" style="background:#fff;border-radius:10px;padding:8px;aspect-ratio:1;display:flex;align-items:center;justify-content:center;border:1px solid var(--dt-border);cursor:pointer;position:relative">' +
      '<img src="' + resultB64 + '" style="max-width:100%;max-height:200px;border-radius:6px">' +
      '<div style="position:absolute;bottom:4px;right:6px;font-size:9px;color:var(--dt-muted);pointer-events:none">Tap to enlarge</div>' +
      '</div></div>' +
      '</div>' +
      '<button class="dt-rbg-use-btn" id="dt-aipreview-use" style="width:100%;margin-bottom:8px">Use This Design</button>' +
      '<div style="display:flex;gap:8px">' +
      '<button id="dt-aipreview-simplify" style="flex:1;padding:10px;border:1.5px solid var(--dt-border);border-radius:8px;background:transparent;font-size:11.5px;font-weight:700;cursor:pointer;font-family:var(--dt-font,system-ui);color:var(--dt-text)">Simplify &amp; Retry</button>' +
      '<button id="dt-aipreview-newphoto" style="flex:1;padding:10px;border:1.5px solid var(--dt-border);border-radius:8px;background:transparent;font-size:11.5px;font-weight:700;cursor:pointer;font-family:var(--dt-font,system-ui);color:var(--dt-text)">Try Different Photo</button>' +
      '</div></div>';
    document.body.appendChild(ov);

    /* Use This Design — strip white bg, vectorize, add to canvas */
    ov.querySelector('#dt-aipreview-zoom')?.addEventListener('click', () => {
      const lb = document.createElement('div');
      lb.style.cssText = 'position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;cursor:zoom-out;padding:20px';
      lb.innerHTML = '<img src="' + resultB64 + '" style="max-width:90%;max-height:90%;border-radius:8px;box-shadow:0 12px 40px rgba(0,0,0,0.4)">';
      lb.addEventListener('click', () => lb.remove());
      document.body.appendChild(lb);
    });
    ov.querySelector('#dt-aipreview-use').addEventListener('click', async () => {
      const btn = ov.querySelector('#dt-aipreview-use');
      btn.textContent = 'Processing\u2026'; btn.disabled = true;
      /* Declared OUTSIDE the try: the catch's raster fallback reads it.
         (It was const inside the try, so every vectorize failure threw
         ReferenceError instead of falling back to raster.) */
      let cleanB64 = resultB64;
      try {
        /* Strip white background → make transparent for engrave compatibility */
        cleanB64 = await stripWhiteBackground(resultB64);

        /* Trim to the ink's alpha bbox: removes the square letterbox padding
           handleEngravePhoto added for the API (plus any white-margin slop
           the model painted), so the placed layer hugs the artwork instead
           of carrying dead margins. */
        cleanB64 = await cropPngToAlpha(cleanB64, 8);

        /* Vectorize the clean line art → SVG */
        btn.textContent = 'Vectorizing\u2026';
        /* v2.13.6: 4 → 2 (REVERT of v2.13.3's bump; CB37496/CB-20260813-XVNQ).
           gpt-image-1 line art is SOFT-GRAY, not true 2-color — at maxColors 4
           the quantizer splits the ink into adjacent gray plateaus, and
           vectorizer.ai's gap filler (always on in api/vectorize.js) draws a
           fill="none" stroke-width=2 seam stroke along virtually every plateau
           boundary. At production those seams hit the v2.11.3 stroke→ribbon
           converter and ship as hundreds of sliver ribbons in the DXF/EPS —
           the CB37496 prepress "tons of artifacts" incident. At 2, soft-gray
           ink collapses to one group: (almost) no adjacencies, no seam blizzard.
           WHY THE v2.13.3 A/B MISSED IT: it verified byte-identical output on
           TRUE 2-color line art — the one input class where 2 vs 4 CANNOT
           differ. AI-generated line art from photos is exactly the class where
           it does. Do NOT re-bump this "for consistency" with the production
           trace: that path is followed by _recolorSvgInkBlack + the F3 region
           audit; this one stores the vectorizer output verbatim as the layer's
           svgMarkup. (Adversarial 2nd-agent review, Aug 14: GO — call reached
           only via engrave-photo → AI filter → "Use This Design"; the four
           production callVectorize sites and the raster fallback untouched.) */
        const svgB64 = await callVectorize(cleanB64, 'svg', 2);
        /* Decode the SVG from base64 */
        const svgMarkup = atob(svgB64.replace(/^data:[^;]+;base64,/, ''));
        const sanitized = sanitizeSvg(svgMarkup);
        /* _fitPA: place sized-to-fit in the print area. (The old post-add
           "_preVectorized" tagging here was async-broken — addSvgLayer adds
           the layer on image load, so layers() didn't contain it yet — and
           the flag was never read anywhere; removed. SVG layers already skip
           re-vectorization structurally in generateSideProduction.) */
        addSvgLayer(sanitized, fileName.replace(/\.[^.]+$/, '') + '-engrave', { _sourceFile: sourceFile, _fitPA: true });
        ov.remove();
      } catch (err) {
        DEBUG && console.warn('[dt] Vectorization of AI result failed, using raster:', err.message);
        /* Fallback: add as raster image with white bg stripped */
        const img = new Image();
        img.onload = () => {
          addImageLayer(img, fileName + '-engrave', cleanB64, false, sourceFile);
          /* addImageLayer is synchronous and selects the new layer — apply
             the same sized-to-fit placement as the vector path. */
          try { fitLayerToPA(); } catch (_) {}
          ov.remove();
        };
        img.onerror = () => { showUploadError('Failed to load the converted image.'); ov.remove(); };
        img.src = cleanB64;
      }
    });

    /* Simplify & Retry — re-run with simplified prompt */
    ov.querySelector('#dt-aipreview-simplify').addEventListener('click', () => {
      ov.remove();
      processEngraveAI(apiB64, originalB64, fileName, 'simplified', sourceFile);
    });

    /* Try Different Photo — reopen the single engrave uploader */
    ov.querySelector('#dt-aipreview-newphoto').addEventListener('click', () => {
      ov.remove();
      const inp = $('dt-engrave-input');
      if (inp) inp.click();
    });

    ov.querySelector('#dt-aipreview-close').addEventListener('click', () => { ov.remove(); S._slotIntent = null; });
    ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  }

  /* ── Strip White Background ──────────────────────────
     Converts white/near-white pixels to transparent. Used on AI engrave
     results where the prompt requests "white background" (produces cleaner
     AI output) but the engrave pipeline needs transparent background. */
  /* v2.11.1 — Despeckle the strip result: drop isolated opaque islands <= minKeep
     pixels. A hard white threshold on a JPEG leaves ~hundreds of tiny opaque
     specks (mosquito noise / block ringing around the black↔white edges) floating
     in what should be empty transparent space. Measured on the CB-...-2F3B poster:
     607 specks <= 10px = only 0.3% of ink but visible dust in the cutout.
     4-connected flood fill over the KEPT (opaque) mask; a component is enumerated
     only up to minKeep+1 pixels — past that it is known-big and we stop recording,
     so the memory bound is the O(n) `seen`/`stack` pair, not per-component storage.
     Real line-art strokes/letters are all part of much larger components and are
     untouched; only true islands vanish. Skipped above 12MP to protect mobile
     memory (typical uploads are 1–3MP; specks matter most on those JPEGs). */
  function _despeckleStrip(d, w, h, minKeep) {
    const n = w * h;
    if (n > 12000000 || minKeep < 1) return;   /* memory guard / no-op */
    const op = new Uint8Array(n);
    for (let i = 0, p = 3; i < n; i++, p += 4) op[i] = d[p] > 8 ? 1 : 0;  /* kept = opaque */
    const seen = new Uint8Array(n);
    const stack = new Int32Array(n);
    const small = new Int32Array(minKeep + 1);
    for (let s = 0; s < n; s++) {
      if (!op[s] || seen[s]) continue;
      let sp = 0, cc = 0, big = false;
      stack[sp++] = s; seen[s] = 1;
      while (sp) {
        const p = stack[--sp];
        if (!big) { if (cc <= minKeep) small[cc] = p; cc++; if (cc > minKeep) big = true; }
        const x = p % w, y = (p / w) | 0;
        if (x > 0     && op[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; stack[sp++] = p - 1; }
        if (x < w - 1 && op[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; stack[sp++] = p + 1; }
        if (y > 0     && op[p - w] && !seen[p - w]) { seen[p - w] = 1; stack[sp++] = p - w; }
        if (y < h - 1 && op[p + w] && !seen[p + w]) { seen[p + w] = 1; stack[sp++] = p + w; }
      }
      if (!big) { for (let k = 0; k < cc; k++) d[small[k] * 4 + 3] = 0; }  /* island → transparent */
    }
  }

  function stripWhiteBackground(b64) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        const cx = c.getContext('2d');
        cx.drawImage(img, 0, 0);
        const id = cx.getImageData(0, 0, c.width, c.height);
        const d = id.data;
        /* Threshold: pixels where R, G, B are all > 230 are "white" → transparent */
        for (let i = 0; i < d.length; i += 4) {
          if (d[i] > 230 && d[i + 1] > 230 && d[i + 2] > 230) {
            d[i + 3] = 0; /* Set alpha to 0 */
          }
        }
        /* v2.11.1: remove JPEG-noise dust left in the now-transparent areas.
           minKeep scales with resolution (~13px at 1254², validated there),
           floored/capped so it neither misses specks nor eats real detail. */
        const minKeep = Math.max(8, Math.min(48, Math.round(c.width * c.height * 8e-6)));
        _despeckleStrip(d, c.width, c.height, minKeep);
        cx.putImageData(id, 0, 0);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = () => resolve(b64); /* fallback: return original */
      img.src = b64;
    });
  }

  /* ── v2.10.2 ENGRAVE AUTO-PREP helpers (Brandon, Jul 30 2026) ─────────
     Is the image's border ring predominantly white paper? Guards the
     automatic white-strip so inverted art (white lines on dark ground)
     is never eaten — a dark border means the white pixels ARE the art. */
  function _borderIsWhite(img) {
    try {
      const nw = img.naturalWidth, nh = img.naturalHeight; if (!nw || !nh) return false;
      const SC = 160, sc = Math.min(1, SC / Math.max(nw, nh));
      const sw = Math.max(2, Math.round(nw * sc)), sh = Math.max(2, Math.round(nh * sc));
      const c = document.createElement('canvas'); c.width = sw; c.height = sh;
      const cx = c.getContext('2d', { willReadFrequently: true });
      cx.drawImage(img, 0, 0, sw, sh);
      const d = cx.getImageData(0, 0, sw, sh).data;
      let white = 0, total = 0;
      const px = (x, y) => {
        const i = (y * sw + x) * 4;
        total++;
        if (d[i + 3] > 200 && d[i] >= 240 && d[i + 1] >= 240 && d[i + 2] >= 240) white++;
      };
      for (let x = 0; x < sw; x++) { px(x, 0); px(x, sh - 1); }
      for (let y = 1; y < sh - 1; y++) { px(0, y); px(sw - 1, y); }
      c.width = 0;
      return total > 0 && (white / total) >= 0.6;
    } catch (_) { return false; }
  }

  /* v2.10.3: is this image the "paper + ink" class engrave auto-prep should
     handle? The border-ring test alone missed FRAMED art (incident poster:
     dark drawn frame touches the canvas edge, white paper ENCLOSED inside —
     exactly the class the vectorizer does NOT omit as background, verified
     against a same-day clean order whose edge-connected white was silently
     dropped by the trace). Eligible when EITHER the border ring is white
     (classic logo-on-paper) OR the tonal split says "bright paper majority
     over dark ink": light class mean >= 225 and light fraction 35–98.5%.
     Inverted art (light strokes on dark ground) fails the fraction test;
     the post-strip usable check remains the final guard. */
  function _engraveAutoPrepEligible(img) {
    if (_borderIsWhite(img)) return true;
    try {
      const nw = img.naturalWidth, nh = img.naturalHeight; if (!nw || !nh) return false;
      const SC = 160, sc = Math.min(1, SC / Math.max(nw, nh));
      const sw = Math.max(1, Math.round(nw * sc)), sh = Math.max(1, Math.round(nh * sc));
      const c = document.createElement('canvas'); c.width = sw; c.height = sh;
      const cx = c.getContext('2d', { willReadFrequently: true });
      cx.drawImage(img, 0, 0, sw, sh);
      const d = cx.getImageData(0, 0, sw, sh).data;
      const hist = new Float64Array(256); let n = 0;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 128) continue;
        hist[Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) | 0]++; n++;
      }
      c.width = 0;
      if (!n) return false;
      /* Otsu threshold over the luminance histogram */
      let sumAll = 0; for (let t = 0; t < 256; t++) sumAll += t * hist[t];
      let sumB = 0, wB = 0, best = 0, thr = 0;
      for (let t = 0; t < 256; t++) {
        wB += hist[t]; if (!wB) continue;
        const wF = n - wB; if (!wF) break;
        sumB += t * hist[t];
        const mB = sumB / wB, mF = (sumAll - sumB) / wF;
        const v = wB * wF * (mB - mF) * (mB - mF);
        if (v > best) { best = v; thr = t; }
      }
      let lightN = 0, lightSum = 0;
      for (let t = thr + 1; t < 256; t++) { lightN += hist[t]; lightSum += t * hist[t]; }
      if (!lightN) return false;
      const lightFrac = lightN / n, lightMean = lightSum / lightN;
      return lightMean >= 225 && lightFrac >= 0.35 && lightFrac <= 0.985;
    } catch (_) { return false; }
  }

  /* Did the white-strip actually produce usable engrave art? Requires that
     SOME ink survived (not a blank page) and that a meaningful amount of
     background actually became transparent (strip did real work) —
     otherwise the caller falls back to the normal picker. */
  function _stripResultUsable(img) {
    try {
      const nw = img.naturalWidth, nh = img.naturalHeight; if (!nw || !nh) return false;
      const SC = 160, sc = Math.min(1, SC / Math.max(nw, nh));
      const sw = Math.max(1, Math.round(nw * sc)), sh = Math.max(1, Math.round(nh * sc));
      const c = document.createElement('canvas'); c.width = sw; c.height = sh;
      const cx = c.getContext('2d', { willReadFrequently: true });
      cx.drawImage(img, 0, 0, sw, sh);
      const d = cx.getImageData(0, 0, sw, sh).data;
      let opaque = 0; const n = sw * sh;
      for (let i = 3; i < d.length; i += 4) if (d[i] > 128) opaque++;
      c.width = 0;
      const f = opaque / n;
      return f >= 0.002 && f <= 0.9;
    } catch (_) { return false; }
  }

  function handleFile(file) {
    if (!file) {
      /* A missing File should degrade to a clear message, never a silent
         freeze or a file.type TypeError (dead-DataTransfer class of bugs) */
      showUploadError('We couldn’t read that file. Please try uploading it again, or use the file picker instead of drag-and-drop.');
      return;
    }
    const isSvg = file.type === 'image/svg+xml' || file.name.endsWith('.svg');
    const isPdf = file.type === 'application/pdf' || file.name.endsWith('.pdf');

    /* ── 1. File size limit ────────────────────────────── */
    const maxSize = isPdf ? 10 * 1024 * 1024 : 20 * 1024 * 1024;
    if (file.size > maxSize) {
      showUploadError('This file is ' + (file.size / 1024 / 1024).toFixed(1) +
        'MB, which is over the ' + (maxSize / 1024 / 1024) + 'MB limit. Try a smaller or compressed version.');
      return;
    }

    /* ── 2. SVG upload (sanitized) ─────────────────────── */
    if (isSvg) {
      const reader = new FileReader();
      reader.onload = e => {
        const sanitized = sanitizeSvg(e.target.result);
        addSvgLayer(sanitized, file.name.replace(/\.svg$/i, ''), { _sourceFile: file });
      };
      reader.readAsText(file);
      return;
    }

    /* ── 3. PDF upload ─────────────────────────────────── */
    if (isPdf) { showLoading('Processing PDF...'); handlePdfUpload(file).finally(hideLoading); return; }

    /* ── 4. Raster image upload ────────────────────────── */
    showLoading('Processing image...');
    const reader = new FileReader();
    reader.onload = e => {
      const b64 = e.target.result;
      const img = new Image();
      img.onload = () => {
        const nw = img.naturalWidth, nh = img.naturalHeight;

        /* 4a. Minimum dimensions — reject */
        if (nw < 50 || nh < 50) {
          showUploadError('This image is only ' + nw + '\u00D7' + nh +
            ' pixels \u2014 too small for printing. Upload a version that\u2019s at least 50\u00D750 pixels.');
          return;
        }

        /* 4b. Maximum dimensions — auto-downscale to 4096px max side */
        let useImg = img, useB64 = b64;
        if (nw > 4096 || nh > 4096) {
          const scale = 4096 / Math.max(nw, nh);
          const tw = Math.round(nw * scale), th = Math.round(nh * scale);
          const c = document.createElement('canvas'); c.width = tw; c.height = th;
          c.getContext('2d').drawImage(img, 0, 0, tw, th);
          useB64 = c.toDataURL('image/png');
          const dImg = new Image();
          dImg.onload = () => checkResolutionAndProceed(dImg, useB64, file);
          dImg.src = useB64;
          return;
        }
        checkResolutionAndProceed(useImg, useB64, file);
      };
      img.onerror = () => showUploadError(
        "Couldn\u2019t open this file. It may be corrupt or an unsupported format. " +
        'Try re-exporting it as PNG or JPEG.'
      );
      img.src = b64;
    };
    reader.readAsDataURL(file);

    function checkResolutionAndProceed(img, b64, file) {
      const nw = img.naturalWidth, nh = img.naturalHeight;
      const phys = SIDE_PHYS[S.view];
      /* 4c. Resolution warning — non-blocking, user can proceed */
      if (phys) {
        const neededW = Math.ceil(phys.wInches * 150);
        const neededH = Math.ceil(phys.hInches * 150);
        if (nw < neededW || nh < neededH) {
          hideLoading(); /* processing is done — never leave the blocking spinner under the dialog */
          showUploadWarning(
            'This image is ' + nw + '\u00D7' + nh + ' pixels. For a sharp print at this size, ' +
            'we\u2019d recommend at least ' + neededW + '\u00D7' + neededH + ' pixels. ' +
            'You can still use it, but it may look blurry when printed.',
            () => finishUpload(img, b64, file)
          );
          return;
        }
      }
      finishUpload(img, b64, file);
    }

    function finishUpload(img, b64, file) {
      hideLoading();
      /* Skip the bg-removal picker for very large files (performance) */
      if (file.size > 10 * 1024 * 1024) {
        addImageLayer(img, file.name, b64, false, file);
        return;
      }
      /* S1 — transparency pre-check: an already-cut image (PNG/WebP with real
         transparency) skips removal and goes straight to the backdrop library. */
      if (hasMeaningfulTransparency(img)) {
        addImageLayer(img, file.name, b64, false, file);
        const nl = selLayer(); if (nl) nl._bgRemoved = true; /* already a cutout */
        if (S.method !== 'engrave') openBackdropLibrary(nl); /* backdrops are a print concept */
        return;
      }
      /* ── v2.10.2 ENGRAVE AUTO-PREP (Brandon, Jul 30 2026) ──────────────
         Engrave line art / logos: strip the white paper AUTOMATICALLY and
         place the art — no picker. remove.bg is a SUBJECT-isolation
         service and returns full-frame line art unchanged (the incident's
         "background not removed at all"); what engrave needs is pixel
         white→transparent, which the AI photo path already used
         (stripWhiteBackground) but direct uploads never got. The cleaned
         transparent art previews correctly on the mockup and gives the
         vectorizer the input class that has always traced well; the
         v2.10.1 converter white-filter remains the production backstop.
         Guards: photos keep their existing flows (AI routing / picker);
         a dark border means white IS the art — picker instead; a strip
         that removes nothing or everything falls back to the picker.
         The ORIGINAL file still ships to Prepress as the source upload. */
      if (S.method === 'engrave' && !isLikelyPhoto(img) && _engraveAutoPrepEligible(img)) {
        showLoading('Preparing for engraving…');
        Promise.resolve(stripWhiteBackground(b64)).then(cleanB64 => new Promise(res => {
          if (!cleanB64 || cleanB64 === b64) { res(null); return; }
          const ci = new Image();
          ci.onload = () => res({ ci, cleanB64 });
          ci.onerror = () => res(null);
          ci.src = cleanB64;
        })).then(r => {
          hideLoading();
          if (r && _stripResultUsable(r.ci)) {
            addImageLayer(r.ci, file.name, r.cleanB64, false, file);
            const nl = selLayer(); if (nl) nl._bgRemoved = true;
            /* toast, not showTip — showTip is globally disabled (v2.11.0 review C3) */
            toast('We removed the white background so only your artwork engraves.');
          } else {
            openRemoveBgPicker(img, b64, file);
          }
        }).catch(() => { hideLoading(); openRemoveBgPicker(img, b64, file); });
        return;
      }
      openRemoveBgPicker(img, b64, file);
    }
  }

  /* Does the image already carry meaningful transparency (so it's effectively a
     cutout already)? JPEG has no alpha → false. Sampled at reduced resolution. */
  function hasMeaningfulTransparency(img) {
    try {
      const nw = img.naturalWidth, nh = img.naturalHeight; if (!nw || !nh) return false;
      const SC = 200, sc = Math.min(1, SC / Math.max(nw, nh));
      const sw = Math.max(1, Math.round(nw * sc)), sh = Math.max(1, Math.round(nh * sc));
      const c = document.createElement('canvas'); c.width = sw; c.height = sh;
      const cx = c.getContext('2d'); cx.drawImage(img, 0, 0, sw, sh);
      let data; try { data = cx.getImageData(0, 0, sw, sh).data; } catch (e) { return false; }
      let clear = 0; const total = sw * sh;
      for (let i = 3; i < data.length; i += 4) if (data[i] < 10) clear++;
      return clear / total > 0.05; /* >5% transparent → treat as already-cut */
    } catch (_) { return false; }
  }

  /* ── PDF Upload — lazy-load pdf.js, render page 1 at 300 DPI ─── */
  let _pdfJsLoaded = false;
  let _pdfJsLoading = false;

  function loadPdfJs() {
    return new Promise((resolve, reject) => {
      if (_pdfJsLoaded && window.pdfjsLib) return resolve(window.pdfjsLib);
      if (_pdfJsLoading) {
        /* Wait for the existing load to finish */
        const check = setInterval(() => {
          if (window.pdfjsLib) { clearInterval(check); _pdfJsLoaded = true; resolve(window.pdfjsLib); }
        }, 100);
        setTimeout(() => { clearInterval(check); reject(new Error('pdf.js load timeout')); }, 15000);
        return;
      }
      _pdfJsLoading = true;
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.min.mjs';
      script.type = 'module';
      /* pdf.js 4.x is ESM — we need to load it via dynamic import */
      const loader = document.createElement('script');
      loader.type = 'module';
      loader.textContent = `
        import * as pdfjsLib from 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.min.mjs';
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs';
        window.pdfjsLib = pdfjsLib;
        window.dispatchEvent(new Event('pdfjsReady'));
      `;
      document.head.appendChild(loader);
      const onReady = () => {
        window.removeEventListener('pdfjsReady', onReady);
        _pdfJsLoaded = true; _pdfJsLoading = false;
        resolve(window.pdfjsLib);
      };
      window.addEventListener('pdfjsReady', onReady);
      setTimeout(() => {
        window.removeEventListener('pdfjsReady', onReady);
        if (!_pdfJsLoaded) { _pdfJsLoading = false; reject(new Error('pdf.js failed to load')); }
      }, 15000);
    });
  }

  /* ── MuPDF (WASM) — lazy-loaded ONLY when a true-vector PDF is uploaded.
     Used to convert vector PDFs to outlined SVG so the artwork stays vector
     end-to-end (never rasterized). ~3.6MB brotli payload, cached after first
     load; mirrors the lazy CDN pattern used for pdf.js and pdf-lib. ── */
  let _muPdfCache = null;
  function loadMuPdf() {
    if (_muPdfCache) return Promise.resolve(_muPdfCache);
    if (window.__mupdf) { _muPdfCache = window.__mupdf; return Promise.resolve(_muPdfCache); }
    return new Promise((resolve, reject) => {
      const onReady = () => {
        window.removeEventListener('mupdfReady', onReady);
        if (window.__mupdf) { _muPdfCache = window.__mupdf; resolve(_muPdfCache); }
        else reject(new Error('mupdf loaded but global missing'));
      };
      window.addEventListener('mupdfReady', onReady);
      const loader = document.createElement('script');
      loader.type = 'module';
      loader.textContent =
        "import * as mupdf from 'https://cdn.jsdelivr.net/npm/mupdf@1.28.0/dist/mupdf.js';" +
        "window.__mupdf = mupdf; window.dispatchEvent(new Event('mupdfReady'));";
      loader.onerror = () => { window.removeEventListener('mupdfReady', onReady); reject(new Error('Failed to load mupdf from CDN')); };
      document.head.appendChild(loader);
      setTimeout(() => { if (!_muPdfCache && !window.__mupdf) { window.removeEventListener('mupdfReady', onReady); reject(new Error('mupdf load timeout')); } }, 20000);
    });
  }

  /* Classify a pdf.js page: true vector art (live text or real vector paths)
     vs a raster wrapper (a single placed image — e.g. a PowerPoint export of
     a JPEG, like order CB-20260721-BT29's front). Mirrors the server-side
     content-stream audit: vector iff it paints text or more than a couple of
     vector paths. On any error, return false so we fall back to the existing
     (safe) rasterize path. */
  async function pdfPageIsVector(page, pdfjsLib) {
    try {
      const opList = await page.getOperatorList();
      const OPS = pdfjsLib.OPS;
      const vp = page.getViewport({ scale: 1 });
      const pageArea = Math.max(1, vp.width * vp.height);
      const paintOps = new Set([OPS.fill, OPS.eoFill, OPS.stroke, OPS.fillStroke,
        OPS.eoFillStroke, OPS.closeStroke, OPS.closeFillStroke, OPS.closeEOFillStroke]);
      const textPaintOps = new Set([OPS.showText, OPS.showSpacedText]);
      const imageOps = new Set([OPS.paintImageXObject, OPS.paintInlineImageXObject,
        OPS.paintImageMaskXObject, OPS.paintJpegXObject, OPS.paintImageXObjectRepeat].filter(v => v !== undefined));
      /* Track the CTM (save/restore/transform) so we can measure how much of the
         page each painted image covers. A PDF image is mapped from the unit
         square by the current CTM, so the placed size is the length of the CTM
         columns. An image covering ~the whole page = a raster-wrapped PDF (scan,
         PowerPoint-exported JPEG), which must rasterize even if a caption/label
         adds a little text or a few vector strokes. */
      const mul = (m, n) => [
        m[0]*n[0]+m[2]*n[1], m[1]*n[0]+m[3]*n[1],
        m[0]*n[2]+m[2]*n[3], m[1]*n[2]+m[3]*n[3],
        m[0]*n[4]+m[2]*n[5]+m[4], m[1]*n[4]+m[3]*n[5]+m[5],
      ];
      let ctm = [1,0,0,1,0,0]; const stack = [];
      let pathPaint = 0, textOps = 0, maxImgCover = 0;
      for (let i = 0; i < opList.fnArray.length; i++) {
        const fn = opList.fnArray[i];
        if (fn === OPS.save) stack.push(ctm.slice());
        else if (fn === OPS.restore) { if (stack.length) ctm = stack.pop(); }
        else if (fn === OPS.transform) { const a = opList.argsArray[i]; if (a && a.length >= 6) ctm = mul(ctm, a); }
        else if (paintOps.has(fn)) pathPaint++;
        else if (textPaintOps.has(fn)) textOps++;
        else if (imageOps.has(fn)) {
          const w = Math.hypot(ctm[0], ctm[1]), h = Math.hypot(ctm[2], ctm[3]);
          const cover = (w * h) / pageArea;
          if (cover > maxImgCover) maxImgCover = cover;
        }
      }
      if (maxImgCover >= 0.9) return false;            /* image-dominant → treat as raster */
      return (textOps > 0) || (pathPaint > 2);
    } catch (e) { DEBUG && console.warn('[dt] PDF vector-detect failed:', e.message); return false; }
  }

  /* True-vector PDF → outlined SVG via MuPDF. text=path renders glyphs as
     font-independent path outlines (required for engrave / RIP). Reads the
     file bytes fresh (pdf.js may have detached the original ArrayBuffer). */
  async function convertPdfToSvg(file) {
    const mupdf = await loadMuPdf();
    const bytes = new Uint8Array(await file.arrayBuffer());
    let doc, page, buffer, writer;
    try {
      doc = mupdf.Document.openDocument(bytes, 'application/pdf');
      page = doc.loadPage(0);
      buffer = new mupdf.Buffer();
      writer = new mupdf.DocumentWriter(buffer, 'svg', 'text=path');
      const device = writer.beginPage(page.getBounds());
      page.run(device, mupdf.Matrix.identity);
      writer.endPage();
      writer.close();
      return buffer.asString();
    } finally {
      /* Free WASM-heap objects so repeated PDF uploads don't leak. Guarded —
         binding shape varies by mupdf build; writer is already closed above. */
      try { page && page.destroy && page.destroy(); } catch (_) {}
      try { buffer && buffer.destroy && buffer.destroy(); } catch (_) {}
      try { doc && doc.destroy && doc.destroy(); } catch (_) {}
    }
  }

  /* Is a path 'd' an axis-aligned rectangle covering ~the whole page (WxH)?
     Curves disqualify it; every edge must be horizontal or vertical; its
     extent must fill the page and its area must fill its own bbox (a real
     filled rectangle, not a frame/zigzag). */
  function _pathIsPageRect(d, W, H) {
    if (!d || /[csqta]/i.test(d)) return false;          /* any curve → not a rectangle */
    const toks = d.match(/[MLHVZ]|-?\d*\.?\d+/gi);
    if (!toks) return false;
    const verts = []; let x = 0, y = 0, cmd = '';
    for (let i = 0; i < toks.length;) {
      const t = toks[i];
      if (/[MLHVZ]/i.test(t)) { cmd = t.toUpperCase(); i++; continue; }
      if (cmd === 'M' || cmd === 'L') { x = +toks[i]; y = +toks[i + 1]; i += 2; verts.push([x, y]); }
      else if (cmd === 'H') { x = +toks[i]; i += 1; verts.push([x, y]); }
      else if (cmd === 'V') { y = +toks[i]; i += 1; verts.push([x, y]); }
      else { i++; }
    }
    if (verts.length < 4) return false;
    for (let i = 1; i < verts.length; i++) {
      const dx = Math.abs(verts[i][0] - verts[i - 1][0]), dy = Math.abs(verts[i][1] - verts[i - 1][1]);
      if (dx > 0.5 && dy > 0.5) return false;            /* diagonal edge → not axis-aligned */
    }
    const xs = verts.map(v => v[0]), ys = verts.map(v => v[1]);
    const minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
    const minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
    const bw = maxX - minX, bh = maxY - minY;
    let a = 0; for (let i = 0; i < verts.length; i++) { const [x1, y1] = verts[i], [x2, y2] = verts[(i + 1) % verts.length]; a += x1 * y2 - x2 * y1; }
    a = Math.abs(a) / 2;
    const coversPage = bw >= 0.98 * W && bh >= 0.98 * H && minX <= 0.02 * W && minY <= 0.02 * H && maxX >= 0.98 * W && maxY >= 0.98 * H;
    const fillsBbox = a >= 0.98 * bw * bh;
    return coversPage && fillsBbox;
  }

  /* Remove ONLY a true page background from an extracted vector SVG: the single
     BACKMOST painted shape that is an axis-aligned rectangle covering ~the whole
     page. This is STRUCTURAL, never color-based — so white/light elements that
     are PART of the logo (knockout text, highlights, fine detail) are never
     removed, because they are painted on top and are not page-sized rectangles.
     No-op when the art is already transparent (no such background). Validated on
     real MuPDF output: strips a full-page bg rect while preserving knockout text
     and highlights, and leaves a genuinely transparent vector logo untouched. */
  function stripSvgPageBackground(markup) {
    try {
      const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return markup;
      const svg = doc.documentElement;
      let W = 0, H = 0;
      const vb = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
      if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) { W = vb[2]; H = vb[3]; }
      else { W = parseFloat(svg.getAttribute('width')) || 0; H = parseFloat(svg.getAttribute('height')) || 0; }
      if (!(W > 0 && H > 0)) return markup;

      const paintable = el => {
        if (el.closest && el.closest('defs,clipPath,mask,symbol,pattern,marker')) return false;
        const fill = (el.getAttribute('fill') || '').trim().toLowerCase();
        const style = (el.getAttribute('style') || '').toLowerCase();
        if (fill === 'none' || /fill\s*:\s*none/.test(style)) return false;
        return true;
      };
      const painted = Array.prototype.slice
        .call(svg.querySelectorAll('path,rect,circle,ellipse,polygon,polyline,use,image,text'))
        .filter(paintable);
      if (painted.length < 2) return markup;             /* need real art on top of any bg */

      const first = painted[0], tag = first.tagName.toLowerCase();
      let isBg = false;
      if (tag === 'rect') {
        const x = parseFloat(first.getAttribute('x') || '0'), y = parseFloat(first.getAttribute('y') || '0');
        const w = parseFloat(first.getAttribute('width') || '0'), h = parseFloat(first.getAttribute('height') || '0');
        isBg = w >= 0.98 * W && h >= 0.98 * H && Math.abs(x) <= 0.02 * W && Math.abs(y) <= 0.02 * H;
      } else if (tag === 'path') {
        isBg = _pathIsPageRect(first.getAttribute('d') || '', W, H);
      }
      if (!isBg) return markup;                           /* first elem isn't a page background */
      first.parentNode.removeChild(first);
      return new XMLSerializer().serializeToString(svg);
    } catch (e) { DEBUG && console.warn('[dt] bg strip skipped:', e.message); return markup; }
  }

  async function handlePdfUpload(file) {
    /* Show loading overlay */
    const overlay = document.createElement('div');
    overlay.className = 'dt-rbg-overlay';
    overlay.innerHTML = `<div class="dt-rbg-panel dt-rbg-panel--center">
      <div class="dt-spinner-ring" style="margin:0 auto 16px"></div>
      <div style="font-size:13px;color:var(--dt-text)">Processing PDF…</div>
      <div style="font-size:11px;color:var(--dt-muted);margin-top:6px">Loading PDF renderer</div>
    </div>`;
    document.body.appendChild(overlay);

    try {
      const pdfjsLib = await loadPdfJs();
      overlay.querySelector('.dt-rbg-panel div:last-child').textContent = 'Rendering page 1 at high resolution';

      /* Read file as ArrayBuffer */
      const arrayBuffer = await file.arrayBuffer();

      /* Load the PDF document */
      let pdf;
      try {
        pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      } catch (e) {
        if (e.name === 'PasswordException') throw new Error('This PDF is password-protected. Please remove the password and try again.');
        throw new Error('Unable to read this PDF: ' + (e.message || 'unknown error'));
      }

      /* Render page 1 at 300 DPI (PDF default is 72 DPI) */
      const page = await pdf.getPage(1);

      /* ── True-vector PDF → keep it VECTOR (Brandon: never rasterize a real
         vector PDF). Classify the page; if it's real vector art (paths / live
         text) rather than a placed raster image, extract it to an outlined SVG
         via MuPDF and place it as an SVG layer — production then emits DXF/EPS
         from real paths. Raster-wrapped PDFs (e.g. a PowerPoint-exported JPEG)
         fall through to the 300 DPI rasterize path below. Any failure also
         falls through, so a PDF can never dead-end. ── */
      try {
        if (await pdfPageIsVector(page, pdfjsLib)) {
          overlay.querySelector('.dt-rbg-panel div:last-child').textContent = 'Preserving vector artwork…';
          const svgRaw = await convertPdfToSvg(file);
          /* Strip only a true page-background rectangle (structural, never
             color-based) — intended logo whites are preserved. */
          const sanitized = sanitizeSvg(stripSvgPageBackground(svgRaw));
          if (sanitized && /<(path|rect|circle|ellipse|polygon|polyline|line|use|g)\b/i.test(sanitized)) {
            if (pdf.numPages > 1) showTip('pdf_multi', `Only page 1 of ${pdf.numPages} was imported. Upload each page separately if needed.`, canvas);
            overlay.remove();
            addSvgLayer(sanitized, file.name.replace(/\.pdf$/i, ''), { _sourceFile: file, _fitPA: S.method === 'engrave' });
            return;
          }
        }
      } catch (vErr) {
        DEBUG && console.warn('[dt] Vector PDF extraction failed, rasterizing:', vErr.message);
        /* fall through to the raster path below */
      }

      /* ── Raster-wrapped (or extraction-failed) PDF → render page 1 at 300 DPI ── */
      const scale = PROD_DPI / 72; /* scale for production DPI */
      const viewport = page.getViewport({ scale });

      const c = document.createElement('canvas');
      c.width = Math.round(viewport.width);
      c.height = Math.round(viewport.height);
      overlay.querySelector('.dt-rbg-panel div:last-child').textContent =
        `Rendering ${c.width}×${c.height}px (${pdf.numPages} page${pdf.numPages > 1 ? 's' : ''})`;

      await page.render({ canvasContext: c.getContext('2d'), viewport }).promise;

      const b64 = c.toDataURL('image/png');
      const img = new Image();
      img.onload = async () => {
        overlay.remove();
        /* Multi-page notice */
        if (pdf.numPages > 1) {
          showTip('pdf_multi', `Only page 1 of ${pdf.numPages} was imported. Upload each page separately if needed.`, canvas);
        }
        const baseName = file.name.replace(/\.pdf$/i, '');
        /* pdf.js renders on a transparent canvas, so a clean logo PDF arrives
           already cut out — import it directly and skip a pointless remove.bg call. */
        if (hasMeaningfulTransparency(img)) { addImageLayer(img, baseName, b64, false, file); return; }
        /* Opaque page (PDF had a background): offer background removal, but on the
           rasterized PNG — remove.bg rejects the raw PDF as an invalid file type. */
        if (file.size < 10 * 1024 * 1024) {
          let pngFile = null;
          try { const blob = await (await fetch(b64)).blob(); pngFile = new File([blob], baseName + '.png', { type: 'image/png' }); } catch (_) {}
          if (pngFile) openRemoveBgPicker(img, b64, pngFile, file);
          else addImageLayer(img, baseName, b64, false, file);
        } else {
          addImageLayer(img, baseName, b64, false, file);
        }
      };
      img.onerror = () => { overlay.remove(); throw new Error('Failed to render PDF to image'); };
      img.src = b64;

    } catch (err) {
      overlay.remove();
      DEBUG && console.error('[dt] PDF upload failed:', err);
      /* Show error in a simple alert-style overlay */
      const errOverlay = document.createElement('div');
      errOverlay.className = 'dt-rbg-overlay';
      errOverlay.innerHTML = `<div class="dt-rbg-panel dt-rbg-panel--center">
        <div style="font-size:24px;margin-bottom:12px">⚠</div>
        <div style="font-size:14px;font-weight:700;color:var(--dt-text);margin-bottom:8px">PDF Upload Failed</div>
        <div style="font-size:12px;color:var(--dt-text-2);margin-bottom:16px">${err.message}</div>
        <button class="dt-rbg-use-btn">OK</button>
      </div>`;
      document.body.appendChild(errOverlay);
      errOverlay.querySelector('button').addEventListener('click', () => errOverlay.remove());
      errOverlay.addEventListener('click', e => { if (e.target === errOverlay) errOverlay.remove(); });
    }
  }

  /* ══ "Logo on White" matte (v2.9.18) ═══════════════════════════════════
     A local, no-API alternative to remove.bg for DARK ARTWORK ON LIGHT PAPER.

     Why it exists: remove.bg's subject matte treats specular white highlights
     on glossy 3-D logo renders as semitransparency and eats them. Those
     highlights are real artwork — and because the Onyx RIP does not pick up
     white from a raster, they have to survive as vector white or they vanish
     on press.

     Why it is NOT the default, and must never become one: it assumes dark ink
     on a light, slowly-varying ground, and it forces every region enclosed by
     ink fully opaque. On a closed-outline logo (a crest border, a state
     silhouette) that fills the entire interior with background. Measured on
     2026-07-27 against known-good cutouts:
         CB36597           +445,550 wrongly-opaque px  (87% of what it kept)
         Commonwealth Mór  +203,321
         FMF line art      142,434 light px forced opaque
     Zero false negatives in every case — it never cuts too much, it simply
     refuses to cut interiors. See claude/FINDING-lowmatte-blast-radius.md.

     Therefore: offered ONLY as a user-selected card in the upload picker,
     only when lmEligible() passes, and never auto-selected. A customer picks
     it by looking at a rendered thumbnail, not by classifying their own file.

     Ported from the Python reference and validated against it on the tuning
     asset: mean |Δalpha| 0.03/255, 99.988% binary agreement, highlights kept
     2,131px in both. ── */
  const LowMatte = (function () {
    const WORK = 1024;   /* structure runs here so the pixel-denominated       */
    const INK_L = 150;   /* constants below mean the same thing at any upload  */
    const RAMP_LO = 90;  /* size. All of them were tuned at 1024.              */
    const RAMP_HI = 170;
    const SIGMA = 60;

    function luma(rgba, n, out) {
      for (let i = 0, o = 0; i < n; i++, o += 4)
        out[i] = 0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2];
      return out;
    }
    function downsample(rgba, W, H, w, h) {
      const out = new Uint8ClampedArray(w * h * 4), sx = W / w, sy = H / h;
      for (let y = 0; y < h; y++) {
        const y0 = (y * sy) | 0, y1 = Math.max(y0 + 1, ((y + 1) * sy) | 0);
        for (let x = 0; x < w; x++) {
          const x0 = (x * sx) | 0, x1 = Math.max(x0 + 1, ((x + 1) * sx) | 0);
          let r = 0, g = 0, b = 0, a = 0, c = 0;
          for (let yy = y0; yy < y1; yy++) {
            const row = yy * W;
            for (let xx = x0; xx < x1; xx++) {
              const o = (row + xx) * 4;
              r += rgba[o]; g += rgba[o + 1]; b += rgba[o + 2]; a += rgba[o + 3]; c++;
            }
          }
          const q = (y * w + x) * 4;
          out[q] = r / c; out[q + 1] = g / c; out[q + 2] = b / c; out[q + 3] = a / c;
        }
      }
      return out;
    }
    function upsample(src, w, h, W, H) {
      if (w === W && h === H) return src;
      const out = new Float32Array(W * H), sx = w / W, sy = h / H;
      for (let y = 0; y < H; y++) {
        const fy = Math.min(h - 1, Math.max(0, (y + 0.5) * sy - 0.5));
        const y0 = fy | 0, y1 = Math.min(h - 1, y0 + 1), ty = fy - y0;
        for (let x = 0; x < W; x++) {
          const fx = Math.min(w - 1, Math.max(0, (x + 0.5) * sx - 0.5));
          const x0 = fx | 0, x1 = Math.min(w - 1, x0 + 1), tx = fx - x0;
          const a = src[y0 * w + x0], b = src[y0 * w + x1];
          const c = src[y1 * w + x0], d = src[y1 * w + x1];
          out[y * W + x] = (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty;
        }
      }
      return out;
    }
    /* 3 box passes ≈ Gaussian; O(n) regardless of sigma, which matters at σ=60 */
    function boxesForGauss(sigma, n) {
      const wIdeal = Math.sqrt((12 * sigma * sigma / n) + 1);
      let wl = Math.floor(wIdeal); if (wl % 2 === 0) wl--;
      const wu = wl + 2;
      const mIdeal = (12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4);
      const m = Math.round(mIdeal), sizes = [];
      for (let i = 0; i < n; i++) sizes.push(i < m ? wl : wu);
      return sizes;
    }
    function boxH(src, dst, w, h, r) {
      const iarr = 1 / (r + r + 1);
      for (let i = 0; i < h; i++) {
        let ti = i * w, li = ti, ri = ti + r;
        const fv = src[ti], lv = src[ti + w - 1];
        let val = (r + 1) * fv, j;
        for (j = 0; j < r; j++) val += src[ti + j];
        for (j = 0; j <= r; j++) { val += src[ri++] - fv; dst[ti++] = val * iarr; }
        for (j = r + 1; j < w - r; j++) { val += src[ri++] - src[li++]; dst[ti++] = val * iarr; }
        for (j = w - r; j < w; j++) { val += lv - src[li++]; dst[ti++] = val * iarr; }
      }
    }
    function boxV(src, dst, w, h, r) {
      const iarr = 1 / (r + r + 1);
      for (let i = 0; i < w; i++) {
        let ti = i, li = ti, ri = ti + r * w;
        const fv = src[ti], lv = src[ti + w * (h - 1)];
        let val = (r + 1) * fv, j;
        for (j = 0; j < r; j++) val += src[ti + j * w];
        for (j = 0; j <= r; j++) { val += src[ri] - fv; dst[ti] = val * iarr; ri += w; ti += w; }
        for (j = r + 1; j < h - r; j++) { val += src[ri] - src[li]; dst[ti] = val * iarr; li += w; ri += w; ti += w; }
        for (j = h - r; j < h; j++) { val += lv - src[li]; dst[ti] = val * iarr; li += w; ti += w; }
      }
    }
    function gauss(src, w, h, sigma) {
      const bx = boxesForGauss(sigma, 3);
      const a = Float32Array.from(src), b = new Float32Array(w * h);
      for (let i = 0; i < 3; i++) { const r = (bx[i] - 1) / 2; boxH(a, b, w, h, r); boxV(b, a, w, h, r); }
      return a;
    }
    /* separable square structuring element */
    function morph(mask, w, h, r, isMax) {
      const tmp = new Uint8Array(w * h), out = new Uint8Array(w * h);
      let x, y, k, v;
      for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
        v = isMax ? 0 : 1;
        for (k = -r; k <= r; k++) {
          /* Out of bounds is skipped rather than counted — matches scipy at the
             dilation border, and differs from erosion only in the outermost r px
             where decoration artwork never sits. */
          const xx = x + k; if (xx < 0 || xx >= w) continue;
          const s = mask[y * w + xx];
          if (isMax) { if (s) { v = 1; break; } } else if (!s) { v = 0; break; }
        }
        tmp[y * w + x] = v;
      }
      for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
        v = isMax ? 0 : 1;
        for (k = -r; k <= r; k++) {
          const yy = y + k; if (yy < 0 || yy >= h) continue;
          const s2 = tmp[yy * w + x];
          if (isMax) { if (s2) { v = 1; break; } } else if (!s2) { v = 0; break; }
        }
        out[y * w + x] = v;
      }
      return out;
    }
    const dilate = (m, w, h, r) => morph(m, w, h, r, true);
    const erode = (m, w, h, r) => morph(m, w, h, r, false);

    /* holes = background unreachable from the image border */
    function fillHoles(mask, w, h) {
      const reach = new Uint8Array(w * h), st = [];
      let i, x, y;
      for (x = 0; x < w; x++) { st.push(x); st.push((h - 1) * w + x); }
      for (y = 0; y < h; y++) { st.push(y * w); st.push(y * w + w - 1); }
      while (st.length) {
        i = st.pop();
        if (reach[i] || mask[i]) continue;
        reach[i] = 1; x = i % w; y = (i / w) | 0;
        if (x > 0) st.push(i - 1);
        if (x < w - 1) st.push(i + 1);
        if (y > 0) st.push(i - w);
        if (y < h - 1) st.push(i + w);
      }
      const out = new Uint8Array(w * h);
      for (i = 0; i < w * h; i++) out[i] = (mask[i] || !reach[i]) ? 1 : 0;
      return out;
    }

    function analyse(rgba, W, H) {
      let w = W, h = H;
      if (Math.max(W, H) > WORK) {
        const s = WORK / Math.max(W, H);
        w = Math.max(1, Math.round(W * s)); h = Math.max(1, Math.round(H * s));
      }
      const small = (w === W && h === H) ? rgba : downsample(rgba, W, H, w, h);
      const n = w * h, L = luma(small, n, new Float32Array(n));
      let i, hasAlpha = false;
      for (i = 3; i < small.length; i += 4) if (small[i] < 250) { hasAlpha = true; break; }
      const ink = new Uint8Array(n);
      for (i = 0; i < n; i++) ink[i] = L[i] < INK_L ? 1 : 0;
      const obj = erode(dilate(ink, w, h, 2), w, h, 2);   /* closing 5×5 */
      const objf = fillHoles(obj, w, h);
      let ao = 0, af = 0;
      for (i = 0; i < n; i++) { ao += obj[i]; af += objf[i]; }
      const notBg = dilate(objf, w, h, 4);                /* 9×9 */
      let sum = 0, sq = 0, c = 0;
      for (i = 0; i < n; i++) if (!notBg[i]) { sum += L[i]; sq += L[i] * L[i]; c++; }
      const mean = c ? sum / c : 0;
      const bgStd = c ? Math.sqrt(Math.max(0, sq / c - mean * mean)) : 0;
      return { w, h, small, L, obj, objf, notBg, ink: ao / n,
               fillRatio: ao ? af / ao : 0, bgMean: mean, bgStd, hasAlpha };
    }

    /* Both gates must pass.
         fillRatio bounds the damage — it is exactly the factor by which forcing
           enclosed regions opaque grows the artwork, so a closed outline is
           rejected before it can be offered.
         bgStd establishes there is a gradient or shadow worth modelling at all;
           against flat white paper remove.bg already wins and this adds nothing.
       Calibrated 2026-07-27 on 4 assets — Lew Crew 1.08/8.4 passes; FMF 2.14/0.3,
       Commonwealth 3.02/0.0, CB36597 7.61/0.0 all rejected. Margins are wide and
       both features are mechanistic rather than curve-fitted, but that is a small
       sample: widen these only on measurement, never by feel. A wrong gate here
       is not destructive — it only shows or hides a card whose thumbnail the
       customer still has to look at. */
    function eligible(f) {
      if (!f) return false;
      if (f.hasAlpha) return false;                       /* already a cutout   */
      if (f.bgMean < 200) return false;                   /* ground not light   */
      if (f.ink < 0.01 || f.ink > 0.55) return false;
      if (f.fillRatio > 1.5) return false;                /* would fill interior */
      if (f.bgStd < 3) return false;                      /* flat — nothing to do */
      return true;
    }

    /* Structure at working res; final ramp at FULL res against an upsampled
       background model. B is a σ=60 blur — genuinely low-frequency — so
       upsampling it is lossless in practice and the alpha edge stays as sharp as
       the source instead of inheriting the working grid. */
    function matte(rgba, W, H, f) {
      f = f || analyse(rgba, W, H);
      const w = f.w, h = f.h, n = w * h;
      let i, c;
      const known = new Float32Array(n);
      for (i = 0; i < n; i++) known[i] = f.notBg[i] ? 0 : 1;
      const den = gauss(known, w, h, SIGMA), Bs = [];
      for (c = 0; c < 3; c++) {
        const num = new Float32Array(n);
        for (i = 0; i < n; i++) num[i] = f.small[i * 4 + c] * known[i];
        const g = gauss(num, w, h, SIGMA);
        for (i = 0; i < n; i++) g[i] = g[i] / (den[i] + 1e-6);
        Bs.push(upsample(g, w, h, W, H));
      }
      const N = W * H, alpha = new Float32Array(N);
      for (i = 0; i < N; i++) {
        const o = i * 4;
        const dr = rgba[o] - Bs[0][i], dg = rgba[o + 1] - Bs[1][i], db = rgba[o + 2] - Bs[2][i];
        const d = Math.sqrt(dr * dr + dg * dg + db * db);
        const a = (d - RAMP_LO) / (RAMP_HI - RAMP_LO);
        alpha[i] = a < 0 ? 0 : (a > 1 ? 1 : a);
      }
      const objfF = upsample(Float32Array.from(f.objf), w, h, W, H);
      for (i = 0; i < N; i++) if (objfF[i] > 0.5) alpha[i] = 1;
      /* drop anything not connected to the artwork (stray page marks, vignette) */
      const near = upsample(Float32Array.from(dilate(f.objf, w, h, 3)), w, h, W, H);
      const lab = new Int32Array(N), stack = [], keep = [];
      let cur = 0;
      for (i = 0; i < N; i++) {
        if (lab[i] || alpha[i] <= 0.15) continue;
        cur++; let touches = false; stack.push(i); lab[i] = cur;
        while (stack.length) {
          const p = stack.pop();
          if (near[p] > 0.5) touches = true;
          const px = p % W, py = (p / W) | 0;
          if (px > 0 && !lab[p - 1] && alpha[p - 1] > 0.15) { lab[p - 1] = cur; stack.push(p - 1); }
          if (px < W - 1 && !lab[p + 1] && alpha[p + 1] > 0.15) { lab[p + 1] = cur; stack.push(p + 1); }
          if (py > 0 && !lab[p - W] && alpha[p - W] > 0.15) { lab[p - W] = cur; stack.push(p - W); }
          if (py < H - 1 && !lab[p + W] && alpha[p + W] > 0.15) { lab[p + W] = cur; stack.push(p + W); }
        }
        keep[cur] = touches;
      }
      const out = new Uint8ClampedArray(N);
      for (i = 0; i < N; i++) out[i] = (lab[i] && keep[lab[i]]) ? Math.round(alpha[i] * 255) : 0;
      return out;
    }
    return { analyse, eligible, matte };
  })();

  /* Render the "Logo on White" cutout for an already-loaded Image, or null if
     the upload doesn't qualify. Synchronous and main-thread: ~170ms analyse +
     ~210ms matte on a 1024px upload. Called deferred so the picker paints
     first, and it runs while remove.bg is still in flight, so the card
     typically populates before the API card does. Worth a worker if we ever
     raise WORK. */
  function buildLowMatteB64(img) {
    const W = img.naturalWidth || img.width, H = img.naturalHeight || img.height;
    if (!W || !H) return null;
    try {
      const cv = document.createElement('canvas');
      cv.width = W; cv.height = H;
      const cx = cv.getContext('2d', { willReadFrequently: true });
      cx.drawImage(img, 0, 0);
      const id = cx.getImageData(0, 0, W, H);   /* throws on a tainted canvas */
      const f = LowMatte.analyse(id.data, W, H);
      if (!LowMatte.eligible(f)) return null;
      const a = LowMatte.matte(id.data, W, H, f);
      const d = id.data;
      for (let i = 0, n = W * H; i < n; i++) d[i * 4 + 3] = a[i];
      cx.putImageData(id, 0, 0);
      return cv.toDataURL('image/png');
    } catch (err) {
      DEBUG && console.warn('[dt] logo-on-white matte failed:', err && err.message);
      return null;
    }
  }

  function openRemoveBgPicker(origImg, origB64, file, originalSourceFile) {
    document.getElementById('dt-rbg-overlay')?.remove();
    let rbgB64 = null, lowB64 = null, whiteB64 = null, selected = 'orig';
    const overlay = document.createElement('div');
    overlay.id = 'dt-rbg-overlay'; overlay.className = 'dt-rbg-overlay';
    overlay.innerHTML = `<div class="dt-rbg-panel">
      <div class="dt-rbg-header"><span class="dt-rbg-title">Background Removal</span><button class="dt-rbg-close" id="dt-rbg-close">&times;</button></div>
      <div class="dt-rbg-subtitle">${S.method === 'engrave' ? 'We removed the background so just your subject engraves — keep the original if you prefer.' : 'We removed the background. Use the cutout to add a background, or keep the original.'}</div>
      <div class="dt-rbg-choices">
        <div class="dt-rbg-choice selected" id="dt-rbg-orig"><div class="dt-rbg-thumb"><img src="${origB64}" alt="Original"></div><div class="dt-rbg-label">Original</div></div>
        <div class="dt-rbg-choice" id="dt-rbg-result"><div class="dt-rbg-thumb dt-rbg-thumb--checker" id="dt-rbg-result-thumb"><div class="dt-rbg-spinner"><div class="dt-rbg-spinner-ring"></div><span>Removing background…</span></div></div><div class="dt-rbg-label">Background Removed</div></div>
        <div class="dt-rbg-choice" id="dt-rbg-low" hidden><div class="dt-rbg-thumb dt-rbg-thumb--checker" id="dt-rbg-low-thumb"></div><div class="dt-rbg-label">Logo on White</div><div class="dt-rbg-sub">keeps white highlights</div></div>
        <div class="dt-rbg-choice" id="dt-rbg-white" hidden><div class="dt-rbg-thumb dt-rbg-thumb--checker" id="dt-rbg-white-thumb"></div><div class="dt-rbg-label">White Removed</div><div class="dt-rbg-sub">line art &amp; logos</div></div>
      </div>
      <div id="dt-rbg-error" class="dt-rbg-error" style="display:none"></div>
      <button type="button" class="dt-rbg-brush-link" id="dt-rbg-brush">Not quite right? Brush the subject manually</button>
      <button class="dt-rbg-use-btn" id="dt-rbg-use">Use Selected Image</button>
    </div>`;
    document.body.appendChild(overlay);

    const origCard = overlay.querySelector('#dt-rbg-orig'), rbgCard = overlay.querySelector('#dt-rbg-result');
    const lowCard = overlay.querySelector('#dt-rbg-low'), whiteCard = overlay.querySelector('#dt-rbg-white');
    const resultThumb = overlay.querySelector('#dt-rbg-result-thumb'), errorEl = overlay.querySelector('#dt-rbg-error');
    const close = () => overlay.remove();

    function sel(v) {
      selected = v;
      origCard.classList.toggle('selected', v==='orig');
      rbgCard.classList.toggle('selected', v==='rbg');
      lowCard.classList.toggle('selected', v==='low');
      whiteCard.classList.toggle('selected', v==='white');
    }
    const _srcFile = originalSourceFile || file; /* original user file (differs from file for PDFs) */
    function useCutout(b64) {
      const img = new Image();
      img.onload = () => { addImageLayer(img, file.name, b64, false, _srcFile); const nl = selLayer(); if (nl) nl._bgRemoved = true; close(); if (S.method !== 'engrave') openBackdropLibrary(nl); };
      img.onerror = () => { addImageLayer(origImg, file.name, origB64, false, _srcFile); close(); };
      img.src = b64;
    }
    origCard.addEventListener('click', () => sel('orig'));
    rbgCard.addEventListener('click', () => { if (typeof rbgB64 === 'string') sel('rbg'); });
    lowCard.addEventListener('click', () => { if (typeof lowB64 === 'string') sel('low'); });
    whiteCard.addEventListener('click', () => { if (typeof whiteB64 === 'string') sel('white'); });
    overlay.querySelector('#dt-rbg-close').addEventListener('click', close);
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    overlay.querySelector('#dt-rbg-brush').addEventListener('click', () => openBrushSubject(origImg, origB64, file, useCutout));
    overlay.querySelector('#dt-rbg-use').addEventListener('click', () => {
      if (selected === 'rbg' && typeof rbgB64 === 'string') useCutout(rbgB64);
      else if (selected === 'low' && typeof lowB64 === 'string') useCutout(lowB64);
      else if (selected === 'white' && typeof whiteB64 === 'string') useCutout(whiteB64);
      else { addImageLayer(origImg, file.name, origB64, false, _srcFile); close(); } /* keep original → no backdrops */
    });

    /* Third card: Logo on White (LowMatte). Deferred so the overlay paints
       before we block the thread; never auto-selected. */
    setTimeout(() => {
      if (!document.body.contains(overlay)) return;   /* closed while we waited */
      const b64 = buildLowMatteB64(origImg);
      if (!b64) { lowCard.remove(); return; }
      lowB64 = b64;
      overlay.querySelector('#dt-rbg-low-thumb').innerHTML =
        `<img src="${b64}" alt="Logo on white, highlights kept">`;
      lowCard.hidden = false;
    }, 0);

    /* v2.11.1 — White Removed card: pixel-level white→transparent for line art
       and logos on white paper. remove.bg is a SUBJECT-isolation service and
       returns line art unchanged or errors out ("Could not identify foreground").
       This local strip uses the same function as engrave auto-prep and succeeds
       on every image where the background is actually white pixels.
       Eligibility: not a photo, white-paper border or Otsu split, strip usable.
       Auto-selected when remove.bg fails AND this card is ready, so the user
       lands on a working result instead of falling back to Original. */
    let _whiteCardReady = false;
    setTimeout(() => {
      if (!document.body.contains(overlay)) return;
      if (isLikelyPhoto(origImg) || !_engraveAutoPrepEligible(origImg)) { whiteCard.remove(); return; }
      stripWhiteBackground(origB64).then(cleanB64 => {
        if (!document.body.contains(overlay)) return;
        if (!cleanB64 || cleanB64 === origB64) { whiteCard.remove(); return; }
        const ci = new Image();
        ci.onload = () => {
          if (!document.body.contains(overlay)) return;
          if (!_stripResultUsable(ci)) { whiteCard.remove(); return; }
          whiteB64 = cleanB64;
          overlay.querySelector('#dt-rbg-white-thumb').innerHTML =
            `<img src="${cleanB64}" alt="White background removed">`;
          whiteCard.hidden = false;
          _whiteCardReady = true;
          /* Auto-select when remove.bg already resolved and user is on 'orig':
             covers both failure (rbgB64 === false) and success-but-non-photo
             where the ternary fell through to 'orig' before we were ready. */
          if (selected === 'orig' && !isLikelyPhoto(origImg)) sel('white');
        };
        ci.onerror = () => whiteCard.remove();
        ci.src = cleanB64;
      }).catch(() => whiteCard.remove());
    }, 0);

    let attempts = 0;
    function run() {
      attempts++;
      rbgB64 = null;
      rbgCard.classList.remove('disabled');
      resultThumb.innerHTML = `<div class="dt-rbg-spinner"><div class="dt-rbg-spinner-ring"></div><span>Removing background…</span></div>`;
      errorEl.style.display = 'none'; errorEl.innerHTML = '';
      callRemoveBg(file).then(r => {
        rbgB64 = r; resultThumb.innerHTML = `<img src="${r}" alt="BG removed">`;
        rbgCard.classList.remove('disabled');
        /* v2.11.1: for non-photos, prefer White Removed when available —
           remove.bg often returns line art unchanged (no foreground detected). */
        sel(isLikelyPhoto(origImg) ? 'rbg' : (_whiteCardReady ? 'white' : 'orig'));
      }).catch(err => {
        rbgB64 = false;
        resultThumb.innerHTML = `<div class="dt-rbg-spinner dt-rbg-spinner--fail"><span class="dt-rbg-spinner-icon">&times;</span><span>Failed</span></div>`;
        rbgCard.classList.add('disabled');
        /* v2.11.1: prefer the White Removed card over Original when available */
        sel(_whiteCardReady ? 'white' : 'orig');
        const canRetry = attempts < 3;
        const fallbackMsg = _whiteCardReady
          ? 'We detected line art — try the "White Removed" option instead.'
          : 'Use the original image, or close and upload another.';
        errorEl.innerHTML = `<span>${err.message || 'Background removal failed.'}</span>` +
          (canRetry
            ? ` <button type="button" class="dt-rbg-retry" id="dt-rbg-retry">Try again</button>`
            : ` <span>${fallbackMsg}</span>`);
        errorEl.style.display = 'block';
        errorEl.querySelector('#dt-rbg-retry')?.addEventListener('click', run);
      });
    }
    run();
  }

  /* rev 14 (Aug 26 2026 — Vercel log: remove.bg "resolution_too_high: input has 94
     megapixels, maximum 50" on a 1.9 MB Akamai logo JPEG). The picker sends the ORIGINAL
     upload to remove.bg while the frame the customer sees was already downscaled to 4096 px
     (handleFile step 4b). Two bugs in one: any upload over 50 MP is rejected outright, and
     the "Brush the subject" roi — computed in the ≤4096 px display space — was applied to
     the full-size original, i.e. the wrong region on every >4096 px image. Send remove.bg
     the same ≤4096 px frame the customer sees. JPEG in → JPEG out (no alpha to lose, keeps
     the Vercel body small); anything else → PNG. Cached per File so "Try again" and the
     brush re-run don't decode a huge file twice. Anything that goes wrong falls back to
     the original file, i.e. exactly the old behaviour. The original still ships to
     Prepress as the source upload — only what remove.bg receives changes. */
  const REMOVEBG_MAX_SIDE = 4096;
  const _rbgPrepped = new WeakMap();
  function prepRemoveBgFile(file) {
    if (typeof Blob === 'undefined' || !(file instanceof Blob)) return Promise.resolve(file);
    if (_rbgPrepped.has(file)) return _rbgPrepped.get(file);
    const p = new Promise(resolve => {
      let url = null;
      const done = out => { if (url) { try { URL.revokeObjectURL(url); } catch (_) {} } resolve(out); };
      try {
        url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          const nw = img.naturalWidth, nh = img.naturalHeight;
          if (!nw || !nh || Math.max(nw, nh) <= REMOVEBG_MAX_SIDE) { done(file); return; }
          try {
            const s = REMOVEBG_MAX_SIDE / Math.max(nw, nh);
            const c = document.createElement('canvas');
            c.width = Math.max(1, Math.round(nw * s)); c.height = Math.max(1, Math.round(nh * s));
            c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
            const jpeg = /jpe?g/i.test(file.type || '');
            c.toBlob(b => {
              if (!b) { done(file); return; }
              const base = String(file.name || 'image').replace(/\.[^.]+$/, '');
              DEBUG && console.info('[dt] remove.bg input downscaled', nw + 'x' + nh, '→', c.width + 'x' + c.height);
              done(new File([b], base + (jpeg ? '.jpg' : '.png'), { type: b.type }));
            }, jpeg ? 'image/jpeg' : 'image/png', 0.92);
          } catch (e) { done(file); }
        };
        img.onerror = () => done(file);
        img.src = url;
      } catch (e) { done(file); }
    });
    _rbgPrepped.set(file, p);
    return p;
  }

  async function callRemoveBg(file, opts) {
    const send = await prepRemoveBgFile(file); /* rev 14 */
    const fd = new FormData(); fd.append('image', send);
    if (opts && opts.roi) fd.append('roi', opts.roi);
    if (opts && opts.type) fd.append('type', opts.type);
    let res; try { res = await fetch(CFG.removebgUrl, { method: 'POST', body: fd }); } catch(e) { throw new Error('Cannot reach background removal service.'); }
    if (!res.ok) { let m = `Failed (${res.status})`; try { m = (await res.json()).error || m; } catch(_){} throw new Error(m); }
    const d = await res.json(); if (!d.image) throw new Error('Empty result'); return d.image;
  }

  /* Brushed re-detect (§2.4): show the original, let the user paint over the subject,
     translate the strokes' bounding box into a remove.bg `roi` hint, and re-run with
     crop=off so the cutout stays aligned. onResult(b64) places the new cutout. */
  function openBrushSubject(origImg, origB64, file, onResult) {
    const ov = document.createElement('div');
    ov.className = 'dt-rbg-overlay';
    ov.innerHTML = `<div class="dt-rbg-panel">
      <div class="dt-rbg-header"><span class="dt-rbg-title">Brush the subject</span><button class="dt-rbg-close" id="dt-brush-close">&times;</button></div>
      <div class="dt-rbg-subtitle">Paint over what you want to keep — a rough swipe is fine. We'll focus the cutout there and remove the rest.</div>
      <div class="dt-brush-stage" id="dt-brush-stage"><img id="dt-brush-img" alt="" draggable="false"><canvas id="dt-brush-cv"></canvas></div>
      <div class="dt-brush-tools">
        <button type="button" class="dt-brush-clear" id="dt-brush-clear">Clear</button>
        <button type="button" class="dt-brush-go" id="dt-brush-go" disabled>Remove background</button>
      </div>
      <div id="dt-brush-error" class="dt-rbg-error" style="display:none"></div>
    </div>`;
    document.body.appendChild(ov);
    const closeB = () => ov.remove();
    const img = ov.querySelector('#dt-brush-img'), cv = ov.querySelector('#dt-brush-cv');
    const cx = cv.getContext('2d'), goBtn = ov.querySelector('#dt-brush-go');
    const clearBtn = ov.querySelector('#dt-brush-clear'), errEl = ov.querySelector('#dt-brush-error');
    let bbox = null, drawing = false;
    const R = 16; /* brush radius in display px */

    ov.querySelector('#dt-brush-close').addEventListener('click', closeB);
    ov.addEventListener('click', e => { if (e.target === ov) closeB(); });

    function sizeCanvas() {
      const w = img.clientWidth, h = img.clientHeight; if (!w || !h) return;
      const dpr = window.devicePixelRatio || 1;
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      cv.style.width = w + 'px'; cv.style.height = h + 'px';
      cx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    img.onload = () => requestAnimationFrame(sizeCanvas);
    img.src = origB64;
    if (img.complete) requestAnimationFrame(sizeCanvas);

    function pt(e) {
      const r = cv.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }
    function paint(p) {
      cx.fillStyle = 'rgba(244,126,70,0.45)';
      cx.beginPath(); cx.arc(p.x, p.y, R, 0, Math.PI * 2); cx.fill();
      const x1 = p.x - R, y1 = p.y - R, x2 = p.x + R, y2 = p.y + R;
      if (!bbox) bbox = { x1, y1, x2, y2 };
      else { bbox.x1 = Math.min(bbox.x1, x1); bbox.y1 = Math.min(bbox.y1, y1); bbox.x2 = Math.max(bbox.x2, x2); bbox.y2 = Math.max(bbox.y2, y2); }
      goBtn.disabled = false;
    }
    cv.addEventListener('pointerdown', e => { e.preventDefault(); drawing = true; cv.setPointerCapture?.(e.pointerId); paint(pt(e)); });
    cv.addEventListener('pointermove', e => { if (drawing) paint(pt(e)); });
    cv.addEventListener('pointerup', () => { drawing = false; });
    cv.addEventListener('pointercancel', () => { drawing = false; });
    clearBtn.addEventListener('click', () => { cx.clearRect(0, 0, cv.width, cv.height); bbox = null; goBtn.disabled = true; errEl.style.display = 'none'; });

    goBtn.addEventListener('click', () => {
      if (!bbox) return;
      const w = cv.clientWidth || img.clientWidth || 1, h = cv.clientHeight || img.clientHeight || 1;
      const natW = img.naturalWidth || origImg.naturalWidth || w;
      const natH = img.naturalHeight || origImg.naturalHeight || h;
      /* brush bbox (display px) → integer pixels in the ORIGINAL image space.
         remove.bg rejects decimals / zero-width regions, so round, order, and
         guarantee a minimum 1px span within bounds. */
      let x1 = Math.round(Math.max(0, bbox.x1) / w * natW);
      let y1 = Math.round(Math.max(0, bbox.y1) / h * natH);
      let x2 = Math.round(Math.min(w, bbox.x2) / w * natW);
      let y2 = Math.round(Math.min(h, bbox.y2) / h * natH);
      x1 = Math.max(0, Math.min(natW - 1, x1)); y1 = Math.max(0, Math.min(natH - 1, y1));
      x2 = Math.max(x1 + 1, Math.min(natW, x2)); y2 = Math.max(y1 + 1, Math.min(natH, y2));
      const roi = `${x1}px ${y1}px ${x2}px ${y2}px`;
      goBtn.disabled = true; goBtn.textContent = 'Removing…'; errEl.style.display = 'none';
      callRemoveBg(file, { roi, type: 'auto' }).then(b64 => { closeB(); onResult(b64); })
        .catch(err => { goBtn.disabled = false; goBtn.textContent = 'Remove background'; errEl.textContent = err.message || 'Background removal failed.'; errEl.style.display = 'block'; });
    });
  }

  function addImageLayer(img, name, b64, isClipart, sourceFile) {
    dtAnalytics.layerAdd(isClipart ? 'clipart_image' : 'upload', name);
    /* Track for recent uploads panel — skip clipart (library items aren't "uploads") */
    if (!isClipart) addRecentUpload(b64, name, sourceFile);
    /* Trim transparent borders — skip for clipart (already cropped by designer) */
    const trimmed = isClipart ? null : trimToContent(img);
    const useImg = trimmed ? trimmed.img : img;
    const useB64 = trimmed ? trimmed.b64 : b64;
    /* Use explicit trim dimensions (data URL Images may not have naturalWidth set yet) */
    const nw = trimmed ? trimmed.w : (useImg.naturalWidth || 200);
    const nh = trimmed ? trimmed.h : (useImg.naturalHeight || 200);
    const pa = S.pa;
    /* Contain within the print area on BOTH axes. The old rule only capped height
       (pa.h * 0.65), so a massive/wide photo landed wider than the print area and
       immediately overflowed. Cap width at 90% of the pa (same margin as Fit). */
    let h = Math.min(pa.h * 0.65, nh * S.rscale), w = h * (nw / nh);
    if (w > pa.w * 0.9) { w = pa.w * 0.9; h = w / (nw / nh); }
    /* Auto-classify: a raster upload that already carries transparency is a cutout
       (Remove BG hides, backdrop library applies). Covers every entry path. */
    const alreadyCut = !isClipart && hasMeaningfulTransparency(img);
    /* Photo detection — photos skip vectorization in the production pipeline.
       Logos/line art get traced to vector; photos stay raster (the PDF already
       embeds them as PNGs, so vector conversion just wastes API calls and
       produces a posterized trace nobody can use). */
    const detectedPhoto = !isClipart && isLikelyPhoto(img);
    pushLayer({
      id: uid(), type: 'image', name: (name || 'Image').slice(0, 30), img: useImg, b64: useB64,
      x: pa.x + (pa.w - w)/2, y: pa.y + (pa.h - h)/2, w, h, ar: nw/nh,
      rotation: 0, flipH: false, flipV: false, visible: true,
      tintColor: null, isRasterUpload: !isClipart, isClipart: !!isClipart, centered: false,
      isPhoto: detectedPhoto || undefined,
      _bgRemoved: alreadyCut || undefined,
      ...(sourceFile ? { _sourceFile: sourceFile } : {}),
    });
  }

  /**
   * Trim transparent borders from an image.
   * Scans pixels to find content bounds, returns a cropped image.
   * Returns null if no trimming needed (fully opaque or no significant border).
   */
  function trimToContent(img) {
    const nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh || nw * nh < 100) return null;

    /* Scan for content bounds at REDUCED resolution. Phones shoot 12–50 MP, and the
       old hard skip above 25 MP left big cutouts untrimmed (subject floats inside the
       original frame). We detect the bbox on a downscaled copy, then crop from the
       full-resolution original so no quality is lost. */
    const SCAN = 1400;
    const scale = Math.min(1, SCAN / Math.max(nw, nh));
    const sw = Math.max(1, Math.round(nw * scale)), sh = Math.max(1, Math.round(nh * scale));
    const c = document.createElement('canvas'); c.width = sw; c.height = sh;
    const cx = c.getContext('2d');
    cx.drawImage(img, 0, 0, sw, sh);

    let data;
    try { data = cx.getImageData(0, 0, sw, sh).data; } catch (e) { return null; /* CORS */ }

    /* Density-based bounds: stray halo/speckle pixels (even a thin column hugging an
       edge) wrongly pin a raw min/max bbox to the frame. Instead, sum alpha per column
       and per row, then set each edge where the density first exceeds a small fraction
       of the peak. NOTE: that fraction is now 0 (below) — strays are filtered by the
       alpha gate alone, not by relative density. */
    const colSum = new Float64Array(sw), rowSum = new Float64Array(sh);
    let peakCol = 0, peakRow = 0;
    for (let y = 0; y < sh; y++) {
      for (let x = 0; x < sw; x++) {
        const a = data[(y * sw + x) * 4 + 3];
        if (a > 16) { colSum[x] += a; rowSum[y] += a; }
      }
    }
    for (let x = 0; x < sw; x++) if (colSum[x] > peakCol) peakCol = colSum[x];
    for (let y = 0; y < sh; y++) if (rowSum[y] > peakRow) peakRow = rowSum[y];
    if (peakCol === 0 || peakRow === 0) return null; /* fully transparent */
    /* 0%-of-peak (Brandon, Jul 25 2026): ANY gated ink defines the edge. The old
       2%-of-peak rule discarded sparse extremities attached to a dense body — on the
       Commonwealth Mor Wine logo the fine-line house above the solid wordmark lost its
       top 32 rows (roof apex density 0.01%–1.5% of the wordmark's peak), so the artwork
       shipped with the roof cut off. The alpha > 16 gate above is now the ONLY filter.
       MUST stay identical to computeSvgInkBox or display/production geometry desync. */
    const tcol = 0, trow = 0;
    let minX = sw, minY = sh, maxX = 0, maxY = 0;
    for (let x = 0; x < sw; x++) if (colSum[x] > tcol) { if (x < minX) minX = x; if (x > maxX) maxX = x; }
    for (let y = 0; y < sh; y++) if (rowSum[y] > trow) { if (y < minY) minY = y; if (y > maxY) maxY = y; }
    if (maxX <= minX || maxY <= minY) return null;

    /* map the scan-space bbox back to full resolution */
    minX = Math.max(0, Math.floor(minX / scale)); minY = Math.max(0, Math.floor(minY / scale));
    maxX = Math.min(nw - 1, Math.ceil(maxX / scale)); maxY = Math.min(nh - 1, Math.ceil(maxY / scale));

    const tw = maxX - minX + 1, th = maxY - minY + 1;
    /* Trim if the content box is meaningfully smaller than the frame in EITHER
       dimension. (Using the MIN border across all sides was wrong: a subject that
       legitimately touches one edge has 0 border there, which blocked trimming the
       large borders on the other three sides.) */
    if (tw >= nw * 0.98 && th >= nh * 0.98) return null;

    const tc = document.createElement('canvas'); tc.width = tw; tc.height = th;
    tc.getContext('2d').drawImage(img, minX, minY, tw, th, 0, 0, tw, th); /* crop from full-res */

    const b64 = tc.toDataURL('image/png');
    const trimmedImg = new Image();
    trimmedImg.src = b64;
    /* x/y = crop origin in the source frame; fw/fh = source frame dims —
       consumers that reposition a layer box after trimming (Remove Color
       tighten, v2.9.11) need the offset, not just the cropped size. */
    return { img: trimmedImg, b64, w: tw, h: th, x: minX, y: minY, fw: nw, fh: nh };
  }

  function addSvgLayer(markup, name, extra) {
    dtAnalytics.layerAdd('svg', name);
    /* Force high-res rasterization for display. SVGs loaded as blob URL images
       rasterize at their intrinsic size — often 150×150px default if no explicit
       width/height is set. We set dimensions to 2048px so the canvas image is crisp.
       The original markup (without forced dimensions) is stored in l.svgMarkup
       for production, which re-rasterizes at 300 DPI independently. */
    const displayMarkup = svgWithDisplaySize(markup);
    const blob = new Blob([displayMarkup], { type: 'image/svg+xml' }), url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const pa = S.pa, nw = img.naturalWidth || 200, nh = img.naturalHeight || 200;
      /* Trim transparent borders (works for SVGs with padding in their viewBox) */
      const trimmed = trimToContent(img);
      const useImg = trimmed ? trimmed.img : img;
      const tW = trimmed ? trimmed.w : nw;
      const tH = trimmed ? trimmed.h : nh;
      /* Contain within the print area on BOTH axes (same fix as addImageLayer) */
      let h = Math.min(pa.h * 0.65, tH * S.rscale), w = h * (tW / tH);
      if (w > pa.w * 0.9) { w = pa.w * 0.9; h = w / (tW / tH); }
      /* _fitPA (control flag, stripped before the layer is built): place
         SIZED TO FIT — contain at the same 0.9 factor the Fit button
         (fitLayerToPA) uses. Engrave AI results request this so the
         converted art lands filling the print area, not at 65% height. */
      const layerExtra = Object.assign({}, extra || {});
      if (layerExtra._fitPA) {
        delete layerExtra._fitPA;
        const svgAR = tW / tH, paAR = pa.w / pa.h;
        if (svgAR > paAR) { w = pa.w * 0.9; h = w / svgAR; }
        else { h = pa.h * 0.9; w = h * svgAR; }
      }
      const nl = Object.assign({
        id: uid(), type: 'image', name: (name || 'SVG').slice(0, 30), img: useImg, b64: null,
        _imgMarkupSrc: markup, /* v2.12.3: staleness tag for capture guard */
        isSvg: true, svgMarkup: markup, /* original markup for production/editing */
        x: pa.x + (pa.w - w)/2, y: pa.y + (pa.h - h)/2, w, h, ar: tW/tH,
        rotation: 0, flipH: false, flipV: false, visible: true,
        tintColor: null, isRasterUpload: false, centered: false,
      }, layerExtra);
      pushLayer(nl);
      ensureSvgInkBox(nl); /* display/production parity map (async, cached) */
      URL.revokeObjectURL(url);
    };
    img.onerror = () => { URL.revokeObjectURL(url); DEBUG && console.error('[dt] SVG load failed'); };
    img.src = url;
  }

  /* Set explicit width/height on an SVG for high-quality rasterization as a blob URL image.
     Size is DPR-aware: 768px base × devicePixelRatio, capped at 1536px.
     Memory per SVG: ~2.3MB (1x), ~4MB (1.5x), ~9MB (2x+). Production uses its own 300 DPI. */
  function svgWithDisplaySize(markup, maxPx) {
    if (!maxPx) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      maxPx = Math.round(768 * dpr); /* 768 on 1x, 1152 on 1.5x, 1536 on 2x+ */
    }
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(markup, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return markup;
      const svg = doc.documentElement;
      const vb = svg.getAttribute('viewBox');
      if (vb) {
        const parts = vb.split(/[\s,]+/).map(Number);
        const vw = parts[2] || 200, vh = parts[3] || 200;
        const scale = maxPx / Math.max(vw, vh);
        svg.setAttribute('width', Math.round(vw * scale));
        svg.setAttribute('height', Math.round(vh * scale));
      } else {
        /* No viewBox — set explicit dimensions */
        if (!svg.getAttribute('width')) svg.setAttribute('width', maxPx);
        if (!svg.getAttribute('height')) svg.setAttribute('height', maxPx);
      }
      return new XMLSerializer().serializeToString(svg);
    } catch (_) {
      return markup;
    }
  }

  /* ── SVG ink-box (display ↔ production parity) ───────────────────────
     The DISPLAY raster of an SVG layer is trimmed to its ink (trimToContent)
     and stretched into the layer box, but production re-rasterized the FULL
     viewBox uniformly — any markup whose ink doesn't fill its viewBox (every
     DTBackdrops frame: oval cameo ink is 216x276 in a 300x300 viewBox)
     shipped letterboxed and off-aspect vs the approved preview (Jul 22 order:
     circle badge became a tall narrow oval). computeSvgInkBox mirrors
     trimToContent's scan EXACTLY (alpha>16 gate, 0%-of-peak density per axis,
     floor/ceil to raster px, 0.98 both-axes no-trim guard) so
     "display trimmed" ⇔ "production remaps". Returns the ink rect in
     ABSOLUTE viewBox units (includes viewBox min-x/min-y), or null meaning
     "use the full viewBox" (parity with trimToContent returning null).
     Fixed 1024px scan raster so shipped geometry never varies with the
     customer's devicePixelRatio. */
  function computeSvgInkBox(markup) {
    return new Promise(function (resolve) {
      let url = null;
      try {
        const blob = new Blob([svgWithDisplaySize(markup, 1024)], { type: 'image/svg+xml' });
        url = URL.createObjectURL(blob);
      } catch (_) { return resolve(null); }
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        try {
          const nw = img.naturalWidth, nh = img.naturalHeight;
          if (!nw || !nh || nw * nh < 100) return resolve(null);
          const SCAN = 1400;
          const scale = Math.min(1, SCAN / Math.max(nw, nh));
          const sw = Math.max(1, Math.round(nw * scale)), sh = Math.max(1, Math.round(nh * scale));
          const c = document.createElement('canvas'); c.width = sw; c.height = sh;
          const cx = c.getContext('2d');
          cx.drawImage(img, 0, 0, sw, sh);
          let data;
          try { data = cx.getImageData(0, 0, sw, sh).data; } catch (e) { return resolve(null); }
          const colSum = new Float64Array(sw), rowSum = new Float64Array(sh);
          let peakCol = 0, peakRow = 0;
          for (let y = 0; y < sh; y++) {
            for (let x = 0; x < sw; x++) {
              const a = data[(y * sw + x) * 4 + 3];
              if (a > 16) { colSum[x] += a; rowSum[y] += a; }
            }
          }
          for (let x = 0; x < sw; x++) if (colSum[x] > peakCol) peakCol = colSum[x];
          for (let y = 0; y < sh; y++) if (rowSum[y] > peakRow) peakRow = rowSum[y];
          if (peakCol === 0 || peakRow === 0) return resolve(null);
          const tcol = 0, trow = 0; /* 0%-of-peak — mirrors trimToContent exactly */
          let minX = sw, minY = sh, maxX = 0, maxY = 0;
          for (let x = 0; x < sw; x++) if (colSum[x] > tcol) { if (x < minX) minX = x; if (x > maxX) maxX = x; }
          for (let y = 0; y < sh; y++) if (rowSum[y] > trow) { if (y < minY) minY = y; if (y > maxY) maxY = y; }
          if (maxX <= minX || maxY <= minY) return resolve(null);
          minX = Math.max(0, Math.floor(minX / scale)); minY = Math.max(0, Math.floor(minY / scale));
          maxX = Math.min(nw - 1, Math.ceil(maxX / scale)); maxY = Math.min(nh - 1, Math.ceil(maxY / scale));
          const tw = maxX - minX + 1, th = maxY - minY + 1;
          /* Same guard as trimToContent: near-full ink means the display
             raster is NOT trimmed, so production must not remap either. */
          if (tw >= nw * 0.98 && th >= nh * 0.98) return resolve(null);
          /* Raster px → ABSOLUTE viewBox units (raster spans the full viewBox). */
          let vbX = 0, vbY = 0, vbW = nw, vbH = nh;
          try {
            const vdoc = new DOMParser().parseFromString(markup, 'image/svg+xml');
            if (!vdoc.querySelector('parsererror')) {
              const vb = vdoc.documentElement.getAttribute('viewBox');
              if (vb) {
                const p = vb.split(/[\s,]+/).map(Number);
                vbX = p[0] || 0; vbY = p[1] || 0; vbW = p[2] || nw; vbH = p[3] || nh;
              } else {
                vbW = parseFloat(vdoc.documentElement.getAttribute('width')) || nw;
                vbH = parseFloat(vdoc.documentElement.getAttribute('height')) || nh;
              }
            }
          } catch (_) { /* keep raster units */ }
          resolve({
            x: vbX + (minX / nw) * vbW,
            y: vbY + (minY / nh) * vbH,
            w: (tw / nw) * vbW,
            h: (th / nh) * vbH,
          });
        } catch (err) { resolve(null); }
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }

  /* Cache the ink box on the layer, invalidated whenever svgMarkup changes.
     A null RESULT is cached too (full-viewBox layers never rescan). Fire-and-
     forget callers get a render() when a box first lands so the live canvas
     mask picks it up; production paths AWAIT this so exports are always
     exact. */
  function ensureSvgInkBox(l) {
    if (!l || !l.isSvg || !l.svgMarkup) return Promise.resolve(null);
    if (l._inkBoxSrc === l.svgMarkup) return l._inkBoxP || Promise.resolve(l._inkBox || null);
    l._inkBoxSrc = l.svgMarkup;
    l._inkBox = null;
    l._inkBoxP = computeSvgInkBox(l.svgMarkup).then(box => {
      l._inkBox = box || null;
      l._inkBoxP = null;
      if (box) { try { render(); } catch (_) {} }
      return box || null;
    });
    return l._inkBoxP;
  }

  /* Cache-only read for SYNC consumers (canvas mask clip, mockups). Returns
     null (= full-viewBox behavior) until the async compute lands. */
  function svgInkBoxOf(l) {
    return (l && l.isSvg && l._inkBoxSrc === l.svgMarkup) ? (l._inkBox || null) : null;
  }

  function refreshSvgLayer(l) {
    ensureSvgInkBox(l); /* keep production mapping in step with the display trim below */
    const _markupAtCall = l.svgMarkup; /* v2.12.3 N1: tag with BUILD-time markup, not load-time */
    const displayMarkup = svgWithDisplaySize(l.svgMarkup);
    const blob = new Blob([displayMarkup], { type: 'image/svg+xml' }), url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      /* v2.12.3 N1: if the markup changed while this raster was loading
         (rapid recolor), discard — a fresher refresh is in flight; assigning
         here would pair a stale raster with a fresh-looking tag and defeat
         the capture guard's staleness check. */
      if (l.svgMarkup !== _markupAtCall) { URL.revokeObjectURL(url); return; }
      const trimmed = trimToContent(img);
      l.img = trimmed ? trimmed.img : img;
      l._imgMarkupSrc = _markupAtCall; /* staleness tag for capture guard */
      delete l._imgFailed; delete l._rasterRetried;
      if (trimmed) {
        const cx = l.x + l.w / 2, cy = l.y + l.h / 2;
        l.ar = trimmed.w / trimmed.h;
        l.w = l.h * l.ar;
        l.x = cx - l.w / 2;
        l.y = cy - l.h / 2;
      }
      URL.revokeObjectURL(url); render();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      /* v2.12.3: was silent — the layer's display raster stayed null forever
         (editor + mockup painted nothing while production, which re-rasters
         from svgMarkup, painted the art — order CB202608051R5U sunburst).
         Warn loudly and retry once; the capture-time guard
         (ensureSideLayerPaintSources) is the hard backstop. */
      gwarn('svg_display_raster_failed', 'Display raster failed for layer "' + (l.name || l.id) + '" — retrying once');
      if (!l._rasterRetried) {
        l._rasterRetried = true;
        setTimeout(() => { try { refreshSvgLayer(l); } catch (_) {} }, 250);
      }
    };
    img.src = url;
  }

  /* ── v2.12.3: capture-time paint-source guard ─────────────────────────
     mockupPng / templateThumbPng / the live canvas all paint image layers
     from l.img (paintLayer). Production + prepress build from l.svgMarkup /
     l.b64 instead. When a display raster is missing, stale, or broken, the
     img-driven renders silently omit the layer while the production files
     include it — the customer approves one design and the files deliver
     another (order CB202608051R5U: sunburst backdrop in PRODUCTION+PREPRESS,
     absent from mockup; killer path = history snapshots serializing
     _imgSrc:null for a just-placed backdrop, see restoreHistory).

     _rebuildSvgDisplayRaster(l): GEOMETRY-NEUTRAL rebuild of l.img from
     l.svgMarkup. Unlike refreshSvgLayer it never mutates x/y/w/h/ar — the
     layer box was fixed at design time; only the paint source is repaired.
     Resolves true only when the final paint image is fully DECODED (the
     trimmed image from trimToContent starts as a not-yet-loaded data-URL
     Image — drawing it before decode paints nothing).

     l._imgMarkupSrc records which markup the current raster was built from;
     a mismatch (markup replaced by recolor/edit while the async raster
     lagged or failed) marks the raster STALE so captures rebuild instead of
     shipping old colors in the mockup and new colors in production. */
  function _rebuildSvgDisplayRaster(l) {
    return new Promise(resolve => {
      try {
        const markup = l.svgMarkup;
        ensureSvgInkBox(l); /* v2.12.3 N2: mask-clip parity — rebuilt layers need the
          ink box or maskClipFor falls back to full-viewBox mapping in captures
          while production computes the real box (letterboxed-oval class). */
        const displayMarkup = svgWithDisplaySize(markup);
        const blob = new Blob([displayMarkup], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = async () => {
          URL.revokeObjectURL(url);
          try {
            const trimmed = trimToContent(img);
            const useImg = trimmed ? trimmed.img : img;
            if (useImg.decode) { try { await useImg.decode(); } catch (_) {} }
            if (!useImg.complete || !useImg.naturalWidth) { resolve(false); return; }
            l.img = useImg;
            l._imgMarkupSrc = markup;
            delete l._imgFailed;
            resolve(true);
          } catch (_) { resolve(false); }
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(false); };
        img.src = url;
      } catch (_) { resolve(false); }
    });
  }

  /* Awaitable pre-capture guard: repair every visible layer of the side whose
     paint source is missing/broken/stale, then sync linked backdrop geometry
     (parity with renderProductionPNG, which syncs at its top). Healthy sides
     resolve immediately. Failures gwarn and never block the capture. */
  async function ensureSideLayerPaintSources(sideSlug) {
    const side = sideSlug || S.view;
    const vis = (S.byView[side] || []).filter(l => l && l.visible !== false);
    const _imgUsable = im => !!(im && im.complete && im.naturalWidth > 0);
    const jobs = [];
    for (const l of vis) {
      if (l.type !== 'image') continue;
      if (l.isSvg && l.svgMarkup) {
        const stale = l._imgMarkupSrc !== undefined && l._imgMarkupSrc !== l.svgMarkup;
        if (!_imgUsable(l.img) || stale) {
          jobs.push(_rebuildSvgDisplayRaster(l).then(ok => {
            if (!ok) gwarn('capture_img_rebuild_failed', side + ': SVG layer "' + (l.name || l.id) + '" has no paintable display raster');
            return ok;
          }));
        }
      } else if (l.b64 && !_imgUsable(l.img)) {
        jobs.push(new Promise(resolve => {
          const img = new Image();
          img.onload = async () => {
            if (img.decode) { try { await img.decode(); } catch (_) {} }
            l.img = img; delete l._imgFailed; resolve(true);
          };
          img.onerror = () => {
            gwarn('capture_img_rebuild_failed', side + ': raster layer "' + (l.name || l.id) + '" b64 failed to decode');
            resolve(false);
          };
          img.src = l.b64;
        }));
      }
    }
    if (jobs.length) { try { await Promise.all(jobs); } catch (_) {} }
    try { syncGroupedBackdrops(side); } catch (_) {}
  }

  /* ══════════════════════════════════════════════════════════════════
   * BACKDROPS — Phase 2a: placement (linking / masking / Link-Unlink land next)
   * A backdrop is a vector SVG layer from DTBackdrops (window global), inserted
   * directly BEHIND its photo layer and tagged with a shared _groupId. It rides
   * the existing svg-layer display raster + production 300-DPI re-raster paths,
   * so there is no new render/export code. Purely additive — nothing here runs
   * unless placeBackdrop()/S._placeBackdrop() is called.
   * ══════════════════════════════════════════════════════════════════ */

  /* Product colour for palette derivation: sample the mockup just above the
     print area (clean product surface); fall back to a neutral periwinkle if
     the mockup canvas is CORS-tainted or not yet loaded. */
  function getProductSwatchHex() {
    try {
      if (mImg && mImg.complete && mImg.naturalWidth) {
        const c = document.createElement('canvas'); c.width = 24; c.height = 24;
        const cx = c.getContext('2d'), pa = S.pa;
        const sx = mImg.naturalWidth / (mImg.offsetWidth || mImg.naturalWidth);
        const sy = mImg.naturalHeight / (mImg.offsetHeight || mImg.naturalHeight);
        const bx = pa.x * sx, by = Math.max(0, (pa.y - pa.h * 0.25)) * sy;
        const bw = Math.max(1, pa.w * sx), bh = Math.max(1, pa.h * 0.15 * sy);
        cx.drawImage(mImg, bx, by, bw, bh, 0, 0, 24, 24);
        const d = cx.getImageData(0, 0, 24, 24).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
        return '#' + [r, g, b].map(v => Math.round(v / n).toString(16).padStart(2, '0')).join('');
      }
    } catch (_) { /* tainted / not ready */ }
    return '#C8C8D5';
  }

  /* UX-Aug20: set the --dt-swatch-bg CSS variable on the tool container so
     monogram/template card thumbs pick up a subtle product-colour tint.
     Also updates monogram preview canvases + invalidates template thumb cache
     so card content adapts to the new product colour. Called on variant switch. */
  function updateCardSwatchBg() {
    const hex = getProductSwatchHex();
    const rgb = hexToRgb(hex);
    const app = document.getElementById('dt-app');
    if (app && rgb) {
      app.style.setProperty('--dt-swatch-bg', `rgba(${rgb.r},${rgb.g},${rgb.b},0.10)`);
    }
    /* Invalidate template thumb cache so they re-render with adaptive colours.
       If the template panel is currently visible, re-render the visible thumbs. */
    (CFG.inlineTemplates || []).forEach(tpl => { tpl._thumb = null; });
    document.querySelectorAll('.dt-tpl-thumb--tpl').forEach(img => {
      const ti = parseInt(img.dataset.ti, 10);
      const items = _lastTplItems; /* cached by renderTemplatesLibrary */
      if (items && items[ti] && items[ti].kind === 'inline') {
        renderTemplateThumb(items[ti].tpl).then(url => { if (url) img.src = url; });
      }
    });
    /* If the monogram panel is open, refresh previews with the new colour */
    const monoUi = document.getElementById('dt-monogram-ui');
    if (monoUi && monoUi.querySelector('canvas')) {
      /* Seed the preview colour from the product — same logic as opening fresh */
      const ms = seedMonoColor();
      if (ms.auto && !_editingMonoLayer) {
        const c1 = monoUi.querySelector('#dt-mono-c1');
        const c1chip = monoUi.querySelector('#dt-mono-c1-chip');
        if (c1) { c1.value = ms.color; S.monoColor = ms.color; }
        if (c1chip) c1chip.style.background = ms.color;
      }
    }
  }
  /* Run once on load to set the initial swatch bg */
  setTimeout(updateCardSwatchBg, 800);

  function _removeBackdropOfGroup(gid) {
    if (!gid) return;
    S.byView[S.view] = S.byView[S.view].filter(l => !(l.isBackdrop && l._groupId === gid));
  }

  /* #1b: build a palette where the user's chosen color IS the dominant
     backdrop color (deriveBackdropPalette is for product swatches — it picks
     HARMONIOUS brand colors and the seed itself never appears). Partners are
     tints/shades of the pick so every backdrop style stays cohesive. */
  function paletteFromUserColor(hex) {
    const h = String(hex).replace('#', '');
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    const toHex = (R, G, B) => '#' + [R, G, B].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const tint = t => toHex(r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t);
    const shade = t => toHex(r * t, g * t, b * t);
    return {
      swatch: toHex(r, g, b),
      complement: toHex(r, g, b),                      /* dominant fill = the pick */
      contrast: lum > 140 ? shade(0.45) : tint(0.82),  /* dark partner on light picks, pale on dark */
      tonal: tint(0.35),
      accent: shade(0.72),
      neutral: lum > 200 ? shade(0.9) : tint(0.92),
    };
  }
  function bgPaletteFor(photoLayer) {
    return (photoLayer && photoLayer._bgSeed)
      ? paletteFromUserColor(photoLayer._bgSeed)
      : DTBackdrops.deriveBackdropPalette(getProductSwatchHex());
  }

  function placeBackdrop(photoLayer, key) {
    if (!photoLayer || !window.DTBackdrops) return;
    const palette = bgPaletteFor(photoLayer);
    const gid = photoLayer._groupId || ('bg-' + uid());
    photoLayer._groupId = gid;
    _removeBackdropOfGroup(gid); /* replace/clear any existing backdrop for this group */

    if (key === 'transparent' || !key) {
      /* No backdrop → fully ungroup the photo, so group-only UI (Reframe) never lingers
         on what is now a plain upload. */
      photoLayer._maskKey = null; photoLayer._groupId = null; photoLayer._linked = undefined;
      render(); pushHistory(); updateLayersPanel(); return;
    }
    const gen = DTBackdrops.backdrop(key, palette, 300);
    if (!gen || !gen.svg) return;

    const cx = photoLayer.centered ? photoLayer.x : photoLayer.x + photoLayer.w / 2;
    const cy = photoLayer.centered ? photoLayer.y : photoLayer.y + photoLayer.h / 2;
    const side = gen.mask ? Math.max(photoLayer.w, photoLayer.h) * 1.18 : Math.max(photoLayer.w, photoLayer.h) * 1.15;
    const bl = {
      id: uid(), type: 'image', name: 'Background', isSvg: true, isBackdrop: true,
      _groupId: gid, _backdropKey: key, _maskMarkup: gen.mask || null, _content: gen.content || { x: 0, y: 0, w: 1, h: 1 },
      svgMarkup: gen.svg, img: null, b64: null,
      x: cx - side / 2, y: cy - side / 2, w: side, h: side, ar: 1,
      rotation: 0, flipH: false, flipV: false, visible: true,
      tintColor: null, isRasterUpload: false, centered: false,
    };
    photoLayer._maskKey = gen.mask ? key : null;
    captureLinkTransform(bl, photoLayer); /* lock the initial mask↔photo relationship */

    const arr = S.byView[S.view];
    const pi = arr.findIndex(l => l.id === photoLayer.id);
    arr.splice(pi < 0 ? 0 : pi, 0, bl); /* directly behind the photo */

    /* Auto-fit ON ADD: the backdrop extends ~1.15–1.18x past the photo, so a photo
       sized to the print area pushes its new background outside it. If the group's
       visible union (photo ∪ backdrop — same box the Fit button uses) exceeds the
       pa, shrink the photo about the union centre; the backdrop follows via
       syncGroupedBackdrops. Shrink-only and no re-centering, so a deliberately
       positioned photo never jumps when the customer picks a background. */
    const _pa = S.pa;
    const _gb = selectionBounds(photoLayer);
    if (_pa && _gb.w > 1 && _gb.h > 1) {
      const f = Math.min((_pa.w * 0.9) / _gb.w, (_pa.h * 0.9) / _gb.h);
      if (f < 1) {
        const pcx = photoLayer.centered ? photoLayer.x : photoLayer.x + photoLayer.w / 2;
        const pcy = photoLayer.centered ? photoLayer.y : photoLayer.y + photoLayer.h / 2;
        const gcx = _gb.x + _gb.w / 2, gcy = _gb.y + _gb.h / 2;
        photoLayer.w = Math.max(4, photoLayer.w * f);
        photoLayer.h = Math.max(4, photoLayer.h * f);
        const tx = gcx + (pcx - gcx) * f, ty = gcy + (pcy - gcy) * f;
        if (photoLayer.centered) { photoLayer.x = tx; photoLayer.y = ty; }
        else { photoLayer.x = tx - photoLayer.w / 2; photoLayer.y = ty - photoLayer.h / 2; }
        syncGroupedBackdrops();
      }
    }

    refreshSvgLayer(bl); /* crisp display raster (production re-rasters at 300 DPI) */
    render(); pushHistory(); updateLayersPanel(); updateLayerBadge();
  }

  /* Backdrop Library — contextual picker for a selected raster photo. Shows the 12
     procedural backdrops at the swatch palette with the user's cutout previewed on
     each; clicking places/replaces (transparent = none). Reuses the modal overlay +
     card styling so it stays visually consistent. */
  function openBackdropLibrary(photoLayer) {
    if (!photoLayer || !window.DTBackdrops) return;
    /* A template's shape mask owns this photo's group — don't let the backdrop
       library replace it. Reframe stays available. */
    if (photoLayer._tplMask) { toast('tpl_mask_reframe'); return; }
    if (document.querySelector('.dt-bg-overlay')) return; /* already open */
    let palette = bgPaletteFor(photoLayer);
    const list = DTBackdrops.listBackdrops();
    const cut = photoLayer.b64 || (photoLayer.img && photoLayer.img.src) || '';
    const ar = photoLayer.ar || 1;
    const grp = photoLayer._groupId;
    const bd0 = grp ? (S.byView[S.view] || []).find(b => b.isBackdrop && b._groupId === grp) : null;
    let curKey = bd0 ? bd0._backdropKey : 'transparent';

    const overlay = document.createElement('div');
    overlay.className = 'dt-rbg-overlay dt-bg-overlay';
    overlay.innerHTML =
      '<div class="dt-rbg-panel dt-bg-panel">' +
        '<div class="dt-bg-head"><span class="dt-bg-title">Choose a background</span>' +
          '<button class="dt-bg-close" id="dt-bg-close" aria-label="Close">&times;</button></div>' +
        '<div class="dt-bg-grid" id="dt-bg-grid"></div>' +
        '<div class="dt-bg-colorrow">' +
          '<span class="dt-bg-colorrow__lbl">Background color</span>' +
          '<label class="dt-bg-colorrow__pick" title="Pick a background color">' +
            '<span class="dt-prop-color-chip" id="dt-bg-seed-chip"></span>' +
            '<input type="color" id="dt-bg-seed" class="dt-hidden-input">' +
          '</label>' +
          '<button class="dt-btn dt-bg-colorrow__auto" id="dt-bg-seed-auto" type="button">Auto</button>' +
        '</div>' +
        '<button class="dt-rbg-use-btn dt-bg-done" id="dt-bg-done" style="width:100%;margin-top:12px">Done</button>' +
      '</div>';
    document.body.appendChild(overlay);
    const grid = overlay.querySelector('#dt-bg-grid');

    list.forEach(item => {
      grid.insertAdjacentHTML('beforeend',
        '<button type="button" class="dt-bg-tile' + (item.key === curKey ? ' selected' : '') + '" data-key="' + item.key + '" title="' + item.label + '">' +
          '<span class="dt-bg-tile__art"><canvas class="dt-bg-tile__cv" width="320" height="320"></canvas></span>' +
          '<span class="dt-bg-tile__lbl">' + item.label + '</span>' +
        '</button>');
    });

    /* A tile is a literal mini-render of placeBackdrop's result: backdrop SVG filled,
       then the trimmed cutout composited with the SAME sizing rule (mask → cover &
       clip via buildMaskPath; non-mask → centered at 1/1.15; disc_pop overflows). */
    const photoImg = new Image();
    function renderTile(cv, key) {
      const TILE = cv.width, c = cv.getContext('2d');
      c.clearRect(0, 0, TILE, TILE);
      const cxc = TILE / 2, cyc = TILE / 2;
      /* Place the subject at a given max-dimension, centered. */
      const place = (maxDim) => {
        let pw, ph;
        if (ar >= 1) { pw = maxDim; ph = maxDim / ar; } else { ph = maxDim; pw = maxDim * ar; }
        return { pw, ph, px: cxc - pw / 2, py: cyc - ph / 2 };
      };
      const drawPhoto = (box, clip) => {
        if (!photoImg.naturalWidth) return;
        c.save(); if (clip) c.clip(clip); c.drawImage(photoImg, box.px, box.py, box.pw, box.ph); c.restore();
      };
      if (key === 'transparent') { drawPhoto(place(TILE / 1.15), null); return; }
      const gen = DTBackdrops.backdrop(key, palette, 300); /* 0..300 space (matches buildMaskPath) */
      if (!gen || !gen.svg) { drawPhoto(place(TILE / 1.15), null); return; }
      const isMask = !!gen.mask;
      const sImg = new Image();
      sImg.onload = () => {
        c.clearRect(0, 0, TILE, TILE);
        if (isMask) {
          /* Prominent medallion enclosing the subject (mirrors placeBackdrop's
             max-dim*1.18 sizing): draw the disc/blob fill, then the subject framed
             inside it with the fill showing around. */
          const D = TILE * 0.98, mx = cxc - D / 2, my = cyc - D / 2;
          c.drawImage(sImg, mx, my, D, D);
          drawPhoto(place(D * 0.86), buildMaskPath(gen.mask, mx, my, D, D, 0));
        } else {
          c.drawImage(sImg, 0, 0, TILE, TILE);
          drawPhoto(place(TILE / 1.15), null);
        }
      };
      sImg.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(gen.svg);
    }
    const renderAll = () => grid.querySelectorAll('.dt-bg-tile').forEach(btn => {
      const cv = btn.querySelector('canvas'); if (cv) renderTile(cv, btn.dataset.key);
    });
    photoImg.onload = renderAll; photoImg.onerror = renderAll;
    if (cut) photoImg.src = cut; else renderAll();

    const close = () => overlay.remove();
    overlay.querySelector('#dt-bg-close').addEventListener('click', close);
    overlay.querySelector('#dt-bg-done').addEventListener('click', close);
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    grid.querySelectorAll('.dt-bg-tile').forEach(btn => btn.addEventListener('click', () => {
      grid.querySelectorAll('.dt-bg-tile').forEach(b => b.classList.toggle('selected', b === btn));
      curKey = btn.dataset.key;
      placeBackdrop(photoLayer, btn.dataset.key);
    }));

    /* #1b: background color — re-derive the palette from the chosen seed,
       refresh every tile preview, and regenerate the placed backdrop live.
       'change' (not 'input') so color-picker scrubbing doesn't spam history. */
    const seedChip = overlay.querySelector('#dt-bg-seed-chip');
    const seedInp = overlay.querySelector('#dt-bg-seed');
    const syncSeedUi = () => {
      const cur = photoLayer._bgSeed || getProductSwatchHex();
      if (seedChip) seedChip.style.background = cur;
      if (seedInp && /^#[0-9a-fA-F]{6}$/.test(cur)) seedInp.value = cur;
    };
    const reSeed = (hex) => {
      photoLayer._bgSeed = hex || null;
      palette = bgPaletteFor(photoLayer);
      syncSeedUi(); renderAll();
      if (curKey && curKey !== 'transparent') placeBackdrop(photoLayer, curKey);
    };
    seedInp?.addEventListener('change', () => reSeed(seedInp.value));
    overlay.querySelector('#dt-bg-seed-auto')?.addEventListener('click', () => reSeed(null));
    syncSeedUi();
  }

  /* Phase 2b — linked transform: keep each linked backdrop locked to its photo.
     Derived from the photo's transform every render, so it tracks drag / resize /
     pinch / nudge with no per-handler hooks. Inert unless a linked backdrop exists;
     never touches a normal (non-backdrop) layer. _linked === false (Phase 2d unlink)
     opts a backdrop out so it can be moved independently. */
  /* Capture the mask↔photo relationship at link time: the mask's centre offset and
     size ratio relative to the photo. syncGroupedBackdrops then reproduces that fixed
     relationship as the photo moves/scales — so RELINK after a reframe keeps the mask
     exactly where it is (we re-capture here), then they move together from there. */
  function captureLinkTransform(bl, photo) {
    if (!bl || !photo) return;
    const pw = photo.w > 0 ? photo.w : 1;
    const pcx = photo.centered ? photo.x : photo.x + photo.w / 2;
    const pcy = photo.centered ? photo.y : photo.y + photo.h / 2;
    const bcx = bl.centered ? bl.x : bl.x + bl.w / 2;
    const bcy = bl.centered ? bl.y : bl.y + bl.h / 2;
    /* Offset stored in the photo's LOCAL frame (un-rotated), so it orbits correctly
       when the photo later rotates. */
    const dx = bcx - pcx, dy = bcy - pcy;
    const pr = -(photo.rotation || 0) * Math.PI / 180, cs = Math.cos(pr), sn = Math.sin(pr);
    bl._linkW = pw;
    /* v2.13.5: capture the MAJOR axis (max of w,h) so syncGroupedBackdrops'
       side2 = photo.w * _relScale always represents the major axis, from
       which the minor axis is derived via bl.ar. Without this, AR < 1
       backdrops (oval_mask) would shrink on each relink because _relScale
       would capture the minor-axis ratio and the AR reduction would
       compound. */
    bl._relScale = Math.max(bl.w, bl.h, 1) / pw;
    bl._relX = dx * cs - dy * sn;
    bl._relY = dx * sn + dy * cs;
    bl._relRot = (bl.rotation || 0) - (photo.rotation || 0);
  }

  function syncGroupedBackdrops(side) {
    const arr = S.byView[side || S.view]; if (!arr) return;
    for (let i = 0; i < arr.length; i++) {
      const bl = arr[i];
      if (!bl.isBackdrop || !bl._groupId || bl._linked === false) continue;
      const photo = arr.find(l => !l.isBackdrop && l._groupId === bl._groupId);
      if (!photo) continue;
      if (bl._relScale == null) captureLinkTransform(bl, photo); /* legacy / first sync */
      const pcx = photo.centered ? photo.x : photo.x + photo.w / 2;
      const pcy = photo.centered ? photo.y : photo.y + photo.h / 2;
      const k = (photo.w || 1) / (bl._linkW || 1);   /* photo scale since link */
      const side2 = (photo.w || 1) * bl._relScale;    /* mask size, scaled with photo */
      /* Re-rotate the photo-local offset by the photo's current rotation so the mask
         orbits the photo centre instead of detaching. */
      const pr = (photo.rotation || 0) * Math.PI / 180, cs = Math.cos(pr), sn = Math.sin(pr);
      const ox2 = (bl._relX || 0) * k, oy2 = (bl._relY || 0) * k;
      const bcx = pcx + (ox2 * cs - oy2 * sn), bcy = pcy + (ox2 * sn + oy2 * cs);
      /* v2.13.5: respect the backdrop's display-raster aspect ratio after
         trimToContent. The old code forced bl.w = bl.h = side2 (square),
         which stretched non-square ink (blob, arch, heart_frame, oval) so
         the drawn raster was squeezed into a square box. Now side2 is the
         MAJOR axis and the other axis follows bl.ar. */
      const bar = bl.ar && bl.ar > 0 ? bl.ar : 1;
      if (bar >= 1) { bl.w = side2; bl.h = side2 / bar; }
      else          { bl.h = side2; bl.w = side2 * bar; }
      bl.centered = false;
      bl.x = bcx - bl.w / 2; bl.y = bcy - bl.h / 2;
      bl.rotation = (photo.rotation || 0) + (bl._relRot || 0);
    }
  }

  /* Build a clip Path2D from a backdrop's mask markup (<circle>/<path> in 0..300
     space), mapped to a target rect (rx,ry,rw,rh) + rotation. Shared by the canvas
     preview and the 300-DPI production render so the clip is identical in both. */
  function buildMaskPath(maskMarkup, rx, ry, rw, rh, rotDeg, srcBox) {
    if (!maskMarkup || !rw) return null;
    let local;
    const cm = maskMarkup.match(/<circle[^>]*\bcx="([\d.]+)"[^>]*\bcy="([\d.]+)"[^>]*\br="([\d.]+)"/);
    if (cm) { local = new Path2D(); local.arc(+cm[1], +cm[2], +cm[3], 0, Math.PI * 2); }
    else {
      const pm = maskMarkup.match(/<path[^>]*\bd="([^"]+)"/);
      if (!pm) return null;
      try { local = new Path2D(pm[1]); } catch (_) { return null; }
    }
    /* srcBox = the backdrop ART's ink box in the same 0..300 frame (absolute
       viewBox units). The display raster is trimmed to that ink and stretched
       into the layer box, so the mask must ride the SAME mapping or the clip
       lands narrower than the drawn frame (Jul 22 order: photo arc-cropped by
       a letterboxed oval the customer never saw). No srcBox → legacy uniform
       rw/300 mapping (also the transparent circle/heart crop masks, whose
       empty art has no ink box). */
    const m = new DOMMatrix()
      .translateSelf(rx + rw / 2, ry + rh / 2)
      .rotateSelf(rotDeg || 0)
      .translateSelf(-rw / 2, -rh / 2);
    if (srcBox && srcBox.w > 0 && srcBox.h > 0) {
      m.scaleSelf(rw / srcBox.w, rh / srcBox.h).translateSelf(-srcBox.x, -srcBox.y);
    } else {
      const scale = rw / 300;
      m.scaleSelf(scale, scale);
    }
    const out = new Path2D();
    out.addPath(local, m);
    return out;
  }

  /* Clip path for a photo layer, in whatever space mapRect produces (mapRect maps a
     backdrop's layer coords → target canvas coords). Returns null if no active mask. */
  function maskClipFor(photo, arr, mapRect) {
    if (!photo || !photo._maskKey || photo.isBackdrop) return null;
    const bd = arr.find(b => b.isBackdrop && b._groupId === photo._groupId && b._maskMarkup);
    if (!bd) return null;
    const r = mapRect(bd);
    return buildMaskPath(bd._maskMarkup, r.x, r.y, r.w, r.h, bd.rotation, svgInkBoxOf(bd));
  }

  /* Placement API — the backdrop library UI (later phase) will call placeBackdrop
     directly. Exposed on S so it can be exercised on the selected photo meanwhile. */
  S._placeBackdrop = function (key) {
    const l = selLayer();
    if (l && l.type === 'image' && !l.isBackdrop && !l.isMonogram && !l.isSvg) placeBackdrop(l, key);
    else console.warn('[dt] select a photo (raster) layer first');
  };
  S._backdropKeys = window.DTBackdrops ? DTBackdrops.listBackdrops().map(b => b.key) : [];
  /* Console test hook (temporary — removed once the backdrop library UI lands).
     `S` is closure-private, so expose the placement API on window for testing. */
  window.dtPlaceBackdrop = function (key) { return S._placeBackdrop(key); };
  window.dtBackdropKeys = S._backdropKeys;
  /* Link/Unlink a photo from its mask. Unlink freezes the mask so the photo can be
     reframed (pan/zoom) within it; relink resumes the mask tracking the photo. */
  S._toggleLink = function () {
    const l = selLayer(); if (!l || l.isBackdrop) return 'select a photo';
    /* Maskless template photo (no backdrop): toggle a transient reframe flag so it can
       be repositioned/resized inside the locked template. */
    if (!l._groupId) {
      if (!(l._tplPhoto || l._tplLockId)) return 'select a photo that has a backdrop';
      return l._reframing ? (S._exitReframe(), 'LOCKED') : (S._enterReframe(l), 'REFRAMING');
    }
    const bd = (S.byView[S.view] || []).find(b => b.isBackdrop && b._groupId === l._groupId);
    if (!bd) return 'no backdrop in this group';
    return bd._linked === false ? (S._exitReframe(), 'LOCKED') : (S._enterReframe(l), 'REFRAMING');
  };
  /* Enter reframe (transient): unlink so the photo can be panned/zoomed in the fixed mask. */
  S._enterReframe = function (photo) {
    const l = photo || selLayer();
    if (!l || l.isBackdrop) return false;
    /* Maskless template photo: no backdrop to unlink — flag the photo itself. */
    if (!l._groupId) {
      if (!(l._tplPhoto || l._tplLockId) || l._reframing) return false;
      l._reframing = true;
      pushHistory(); render(); forcePropsRebuild(); updateLayersPanel();
      return true;
    }
    const bd = (S.byView[S.view] || []).find(b => b.isBackdrop && b._groupId === l._groupId);
    if (!bd || bd._linked === false) return false;
    bd._linked = false; if (bd._maskMarkup) clampPhotoCoversMask(l, bd);
    pushHistory(); render(); forcePropsRebuild(); updateLayersPanel();
    return true;
  };
  /* Exit reframe (transient): relink any reframing mask group, committing the new framing
     in place (relink re-captures geometry, so the mask doesn't jump). */
  S._exitReframe = function () {
    const arr = S.byView[S.view] || []; let changed = false;
    arr.forEach(bd => {
      if (bd.isBackdrop && bd._linked === false) {
        const ph = arr.find(p => !p.isBackdrop && p._groupId === bd._groupId);
        if (ph) { bd._linked = true; captureLinkTransform(bd, ph); changed = true; }
      }
    });
    /* Commit any maskless template-photo reframe (no backdrop to relink). */
    arr.forEach(l => { if (l._reframing) { l._reframing = false; changed = true; } });
    if (changed) { pushHistory(); render(); forcePropsRebuild(); updateLayersPanel(); }
    return changed;
  };
  window.dtToggleLink = function () { return S._toggleLink(); };
  window.dtPrintSim = function (on) { S._printSim = (on !== false); _lightCache = {}; render(); return S._printSim; };
  window.dtCurve = function (diam) { S._curveOverride = (diam == null ? null : diam); render(); return S._curveOverride; };

  /* ══════════════════════════════════════════════════════════════════
   * SVG COLOR UTILITIES
   * ══════════════════════════════════════════════════════════════════ */
  function parseSvgColors(markup) {
    const map = {};
    const re1 = /(?:fill|stroke|stop-color)=["']([^"']+)["']/gi;
    const re2 = /(?:fill|stroke|stop-color)\s*:\s*([^;"'}\s]+)/gi;
    function rec(v) { if (!v || v === 'none' || v === 'transparent' || v.startsWith('url')) return; const c = normColor(v); if (c) map[c] = (map[c]||0)+1; }
    let m; while ((m = re1.exec(markup))) rec(m[1]); while ((m = re2.exec(markup))) rec(m[1]);
    return Object.entries(map).map(([hex,count]) => ({hex,count})).sort((a,b) => b.count - a.count);
  }

  function normColor(v) {
    v = v.trim().toLowerCase();
    if (v === 'currentcolor') return '#000000'; /* Resolve CSS keyword to black */
    if (/^#[0-9a-f]{6}$/.test(v)) return v;
    if (/^#[0-9a-f]{3}$/.test(v)) return '#'+v[1]+v[1]+v[2]+v[2]+v[3]+v[3];
    /* rgb() / rgba() support */
    const rgbMatch = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]).toString(16).padStart(2,'0');
      const g = parseInt(rgbMatch[2]).toString(16).padStart(2,'0');
      const b = parseInt(rgbMatch[3]).toString(16).padStart(2,'0');
      return '#' + r + g + b;
    }
    const named = {black:'#000000',white:'#ffffff',red:'#ff0000',green:'#008000',blue:'#0000ff',yellow:'#ffff00',orange:'#ffa500',gray:'#808080',grey:'#808080',navy:'#000080',teal:'#008080',purple:'#800080',pink:'#ffc0cb',brown:'#a52a2a',cyan:'#00ffff',magenta:'#ff00ff',lime:'#00ff00',silver:'#c0c0c0',gold:'#ffd700'};
    return named[v] || null;
  }

  function recolorSvg(markup, oldHex, newHex) {
    /* Compare by NORMALIZED colour, not by literal string. parseSvgColors reports the
       normalized #rrggbb, but the markup may write that colour as #fff, "white",
       rgb(255,255,255), or uppercase — matching the literal normalized hex missed all
       of those, which is the root cause of "white won't turn to skip/transparent" (the
       fill was never changed, so it stayed opaque and still engraved). Now every
       fill/stroke value (attribute, inline style, and <style> CSS) is normalized and
       only swapped when it resolves to oldHex. none/transparent/url(...) normalize to
       null and are left untouched. Same routine powers the print color editor. */
    markup = markup.replace(/((?:fill|stroke|stop-color)=["'])([^"']+)(["'])/gi,
      (m, pre, val, post) => (normColor(val) === oldHex ? pre + newHex + post : m));
    markup = markup.replace(/((?:fill|stroke|stop-color)\s*:\s*)([^;"'}\s]+)/gi,
      (m, pre, val) => (normColor(val) === oldHex ? pre + newHex : m));
    return markup;
  }

  /* ══════════════════════════════════════════════════════════════════
   * SVG COLOR EDITOR (modal with live preview, Apply/Cancel)
   * ══════════════════════════════════════════════════════════════════ */

  /* Many clipart SVGs rely on the SVG default fill (#000000) without
     declaring it, so parseSvgColors finds zero colors and the editor
     silently does nothing. Walk the DOM, resolve inherited or default
     fills, and stamp them as explicit attributes so the color editor
     can detect and recolor them. */
  function _ensureExplicitFills(markup) {
    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(markup, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return markup;
      var svg = doc.documentElement;
      var shapes = svg.querySelectorAll('path,circle,rect,ellipse,polygon,polyline,line');
      if (!shapes.length) return markup;
      var changed = false;
      shapes.forEach(function(el) {
        if (el.hasAttribute('fill')) return;
        /* Walk ancestors to find an inherited fill */
        var p = el.parentElement, inherited = null;
        while (p && p !== doc) {
          if (p.hasAttribute('fill')) { inherited = p.getAttribute('fill'); break; }
          p = p.parentElement;
        }
        el.setAttribute('fill', inherited || '#000000');
        changed = true;
      });
      return changed ? new XMLSerializer().serializeToString(svg) : markup;
    } catch (_) { return markup; }
  }

  /* Engrave SVG editor — each color becomes BLACK (engraved) or TRANSPARENT
     (skipped). Shared by the desktop props button and the mobile action bar. */
  function openSvgEngraveEditor(l2) {
    if (!l2 || !l2.isSvg) return;
    var colors = parseSvgColors(l2.svgMarkup);
    if (!colors.length) {
      l2.svgMarkup = _ensureExplicitFills(l2.svgMarkup);
      colors = parseSvgColors(l2.svgMarkup);
    }
    if (!colors.length) { toast('no_editable_colors'); return; }
    const allHexes = colors.map(c => c.hex);
    let workingCopy = l2.svgMarkup;
    let pinned = null; // a swatch tap "pins" a spotlight (so it survives mouseleave / works on touch)

    const ov = document.createElement('div'); ov.className = 'dt-rbg-overlay';
    const rows = colors.map((c, i) =>
      '<div class="dt-eng-row" data-i="' + i + '" data-hex="' + c.hex + '" style="display:flex;align-items:center;gap:10px;padding:8px;border-bottom:1px solid var(--dt-border)">' +
      '<button type="button" class="dt-eng-swatch" title="Highlight this color" aria-label="Highlight color ' + c.hex + '" aria-pressed="false" style="width:32px;height:32px;border-radius:6px;background:' + c.hex + ';border:1px solid #ddd;flex-shrink:0;cursor:pointer;padding:0"></button>' +
      '<span style="flex:1;font-size:12px;font-family:var(--dt-mono)">' + c.hex + '</span>' +
      '<select class="dt-engrave-sel" data-orig="' + c.hex + '" data-i="' + i + '" aria-label="Color ' + c.hex + ' — engrave or leave blank" style="padding:4px 8px;border-radius:6px;border:1px solid var(--dt-border);font-size:12px">' +
      '<option value="#000000">Engrave (black)</option>' +
      '<option value="none">Leave blank</option></select></div>'
    ).join('');
    ov.innerHTML = '<div class="dt-rbg-panel dt-rbg-panel--lg">' +
      '<div class="dt-rbg-header"><span class="dt-rbg-title">Engrave Colors</span><button class="dt-rbg-close" id="dt-eng-close">&times;</button></div>' +
      '<div style="font-size:11px;color:var(--dt-muted);margin-bottom:10px">Engraving is one color. Choose which parts get engraved \u2014 the rest is left blank. Hover or tap a swatch to see where each color is.</div>' +
      '<div class="dt-eng-compare">' +
        '<div class="dt-eng-pane"><div class="dt-eng-pane__label">Original</div><div class="dt-eng-pane__img" id="dt-eng-orig"></div></div>' +
        '<div class="dt-eng-pane"><div class="dt-eng-pane__label">Engrave preview</div><div class="dt-eng-pane__img" id="dt-eng-result"></div></div>' +
      '</div>' +
      '<div id="dt-eng-rows">' + rows + '</div>' +
      '<button class="dt-rbg-use-btn" id="dt-eng-apply" style="width:100%;margin-top:12px">Apply</button></div>';
    document.body.appendChild(ov);

    const origPane = ov.querySelector('#dt-eng-orig');
    const resultPane = ov.querySelector('#dt-eng-result');

    /* Swap a pane's image, revoking the PREVIOUS blob URL (the old image has long
       since loaded). The current URL is revoked on close. Prevents the leak the
       old onload-revoke had under rapid changes. */
    function setPane(container, markup) {
      const prev = container._url;
      const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }));
      container._url = url;
      container.innerHTML = '<img src="' + url + '" alt="" style="max-width:100%;max-height:160px;display:block;margin:0 auto">';
      if (prev) { try { URL.revokeObjectURL(prev); } catch (_) {} }
    }
    /* The live engrave result: each color -> black (engrave) or transparent (blank). */
    function buildResult() {
      let preview = l2.svgMarkup;
      ov.querySelectorAll('.dt-engrave-sel').forEach(sel => {
        const target = sel.value === 'none' ? 'transparent' : sel.value;
        preview = recolorSvg(preview, sel.dataset.orig, target);
      });
      workingCopy = preview;
      return preview;
    }
    /* Spotlight: single pass over fill/stroke so each ORIGINAL palette color maps
       to red (active) or grey (others) from the source value \u2014 no cascade collision
       even if the art happens to use the grey/red we paint with. */
    function buildSpotlight(activeHex) {
      const map = v => {
        const n = normColor(v);
        if (!n || allHexes.indexOf(n) === -1) return null; // none / url(...) / off-palette: leave
        return n === activeHex ? '#ff3b30' : '#e6e6e6';
      };
      let mk = l2.svgMarkup;
      mk = mk.replace(/((?:fill|stroke)=["'])([^"']+)(["'])/gi, (m, pre, val, post) => { const nv = map(val); return nv ? pre + nv + post : m; });
      mk = mk.replace(/((?:fill|stroke)\s*:\s*)([^;"'}\s]+)/gi, (m, pre, val) => { const nv = map(val); return nv ? pre + nv : m; });
      return mk;
    }
    function showOriginal(hex) {
      setPane(origPane, hex ? buildSpotlight(hex) : l2.svgMarkup);
      ov.querySelectorAll('.dt-eng-row').forEach(r => r.classList.toggle('is-active', !!hex && r.dataset.hex === hex));
    }
    function updateResult() { setPane(resultPane, buildResult()); }

    setPane(origPane, l2.svgMarkup);
    updateResult();

    function syncSwatchPressed() {
      ov.querySelectorAll('.dt-eng-swatch').forEach(b =>
        b.setAttribute('aria-pressed', (pinned && b.closest('.dt-eng-row').dataset.hex === pinned) ? 'true' : 'false'));
    }
    ov.querySelectorAll('.dt-engrave-sel').forEach(sel => {
      sel.addEventListener('change', updateResult);
      sel.addEventListener('focus', () => { if (!pinned) showOriginal(sel.dataset.orig); });
      sel.addEventListener('blur', () => { if (!pinned) showOriginal(null); }); // keyboard: clear spotlight when focus leaves
    });
    ov.querySelectorAll('.dt-eng-row').forEach(row => {
      row.addEventListener('mouseenter', () => { if (!pinned) showOriginal(row.dataset.hex); });
    });
    ov.querySelector('#dt-eng-rows').addEventListener('mouseleave', () => { if (!pinned) showOriginal(null); });
    ov.querySelectorAll('.dt-eng-swatch').forEach(sw => {
      sw.addEventListener('click', () => {
        const hex = sw.closest('.dt-eng-row').dataset.hex;
        if (pinned === hex) { pinned = null; showOriginal(null); }
        else { pinned = hex; showOriginal(hex); }
        syncSwatchPressed();
      });
    });

    function cleanup() {
      document.removeEventListener('keydown', onKey);
      [origPane, resultPane].forEach(p => { if (p && p._url) { try { URL.revokeObjectURL(p._url); } catch (_) {} } });
      ov.remove();
    }
    const onKey = e => { if (e.key === 'Escape') cleanup(); };
    document.addEventListener('keydown', onKey);
    ov.querySelector('#dt-eng-close')?.addEventListener('click', cleanup);
    ov.addEventListener('click', e => { if (e.target === ov) cleanup(); });
    ov.querySelector('#dt-eng-apply')?.addEventListener('click', () => {
      buildResult();
      l2.svgMarkup = workingCopy;
      refreshSvgLayer(l2); cleanup(); pushHistory(); forcePropsRebuild();
    });
  }

  function openSvgEditor(l) {
    document.getElementById('dt-svg-editor')?.remove();
    const backup = l.svgMarkup;
    let workingCopy = l.svgMarkup;
    var colors = parseSvgColors(workingCopy);
    if (!colors.length) {
      l.svgMarkup = _ensureExplicitFills(l.svgMarkup);
      workingCopy = l.svgMarkup;
      colors = parseSvgColors(workingCopy);
    }
    if (!colors.length) { return; }

    const overlay = document.createElement('div');
    overlay.id = 'dt-svg-editor'; overlay.className = 'dt-rbg-overlay';

    overlay.innerHTML = `<div class="dt-rbg-panel">
      <div class="dt-rbg-header"><span class="dt-rbg-title">Edit Colors</span><button class="dt-rbg-close" id="dt-svg-close">&times;</button></div>
      <div class="dt-rbg-subtitle">Click any color swatch to replace it throughout the SVG.</div>
      <div class="dt-svg-preview" id="dt-svg-preview"></div>
      <div id="dt-svg-colors" style="display:flex;flex-direction:column;gap:8px;margin:12px 0"></div>
      <div style="display:flex;gap:8px">
        <button class="dt-rbg-use-btn" id="dt-svg-apply" style="flex:1">Apply</button>
        <button class="dt-rbg-skip" id="dt-svg-cancel" style="flex:1;margin:0;border:1.5px solid var(--dt-border);border-radius:var(--dt-btn-radius);padding:10px;background:var(--dt-surface)">Cancel</button>
      </div>
    </div>`;
    document.body.appendChild(overlay);

    const previewEl = overlay.querySelector('#dt-svg-preview');
    const colorsEl = overlay.querySelector('#dt-svg-colors');

    function updatePreview() {
      const blob = new Blob([workingCopy], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      previewEl.innerHTML = `<img src="${url}" alt="Preview" style="max-width:100%;max-height:200px;display:block;margin:0 auto" onload="URL.revokeObjectURL(this.src)">`;
    }
    updatePreview();

    colorsEl.innerHTML = colors.map((c, i) => `
      <div class="dt-svg-color-row" style="display:flex;align-items:center;gap:10px">
        <label style="width:44px;height:44px;min-width:44px;border-radius:10px;border:2px solid rgba(0,0,0,0.12);overflow:hidden;cursor:pointer;display:block;background:${c.hex}">
          <input type="color" class="dt-svg-cinp" value="${c.hex}" data-i="${i}" style="width:60px;height:60px;border:none;padding:0;margin:-8px;cursor:pointer;opacity:0">
        </label>
        <span class="dt-svg-hex" style="font-family:var(--dt-mono);font-size:12px;color:var(--dt-text-2)">${c.hex}</span>
        <span style="font-size:10px;color:var(--dt-muted)">${c.count} use${c.count!==1?'s':''}</span>
      </div>`).join('');

    colorsEl.querySelectorAll('.dt-svg-cinp').forEach(inp => {
      const lbl = inp.closest('label');
      const hexSpan = inp.closest('.dt-svg-color-row')?.querySelector('.dt-svg-hex');
      inp.addEventListener('input', function() {
        const i = parseInt(this.dataset.i), oldHex = colors[i].hex, newHex = this.value;
        workingCopy = recolorSvg(workingCopy, oldHex, newHex);
        colors[i].hex = newHex;
        if (lbl) lbl.style.background = newHex;
        if (hexSpan) hexSpan.textContent = newHex;
        updatePreview();
      });
    });

    const close = () => overlay.remove();
    overlay.querySelector('#dt-svg-close').addEventListener('click', () => { close(); /* Cancel — no change */ });
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); /* Cancel */ });
    overlay.querySelector('#dt-svg-cancel').addEventListener('click', close);
    overlay.querySelector('#dt-svg-apply').addEventListener('click', () => {
      l.svgMarkup = workingCopy;
      refreshSvgLayer(l);
      pushHistory();
      forcePropsRebuild();
      close();
    });
  }

  /* ══════════════════════════════════════════════════════════════════
   * MONOGRAM TOOL (13 styles)
   * ══════════════════════════════════════════════════════════════════ */
  const MONO_STYLES = [
    { name: 'Circle Frame', font: "'Cormorant Garamond', serif", twoTone: true, render(c,t,w,h,clr,clr2) { const fr=clr2||clr;const cx=w/2,cy=h/2,r=Math.min(w,h)*0.38;c.strokeStyle=fr;c.lineWidth=Math.max(1.5,h*0.015);c.beginPath();c.arc(cx,cy,r,0,Math.PI*2);c.stroke();c.beginPath();c.arc(cx,cy,r*0.90,0,Math.PI*2);c.stroke();const fs=Math.floor(h*0.28);c.font=`700 ${fs}px 'Cormorant Garamond',serif`;c.fillStyle=clr;const mw=c.measureText(t.toUpperCase()).width,sc=mw>r*1.4?r*1.4/mw:1;c.save();c.translate(cx,cy);if(sc<1)c.scale(sc,sc);c.textAlign='center';c.textBaseline='middle';c.fillText(t.toUpperCase(),0,0);c.restore(); }},
    { name: 'Diamond Frame', font: "'Italiana', sans-serif", twoTone: true, render(c,t,w,h,clr,clr2) { const fr=clr2||clr;const cx=w/2,cy=h/2,s=Math.min(w,h)*0.34;c.save();c.translate(cx,cy);c.rotate(Math.PI/4);c.strokeStyle=fr;c.lineWidth=Math.max(1.5,h*0.015);c.strokeRect(-s,-s,s*2,s*2);c.strokeRect(-s*0.88,-s*0.88,s*1.76,s*1.76);c.restore();const fs=Math.floor(h*0.26);c.font=`${fs}px 'Italiana',sans-serif`;c.fillStyle=clr;const mw=c.measureText(t.toUpperCase()).width,sc=mw>s*1.2?s*1.2/mw:1;c.save();c.translate(cx,cy);if(sc<1)c.scale(sc,sc);c.textAlign='center';c.textBaseline='middle';c.fillText(t.toUpperCase(),0,0);c.restore(); }},
    { name: 'Modern Thin', font: "'Poiret One', sans-serif", render(c,t,w,h,clr) { const cx=w/2,lw=w*0.45,lh=Math.max(1.5,h*0.012);c.fillStyle=clr;c.fillRect(cx-lw/2,h*0.22,lw,lh);c.fillRect(cx-lw/2,h*0.76,lw,lh);const fs=Math.floor(h*0.30),sp=t.toUpperCase().split('').join('\u200A\u200A\u200A');c.font=`${fs}px 'Poiret One',sans-serif`;const mw=c.measureText(sp).width,sc=mw>w*0.80?w*0.80/mw:1;c.save();c.translate(cx,h*0.49);if(sc<1)c.scale(sc,sc);c.textAlign='center';c.textBaseline='middle';c.fillText(sp,0,0);c.restore(); }},
    { name: 'Stacked Vertical', font: "'Montserrat', sans-serif", supports: '23', render(c,t,w,h,clr) { const chars=t.toUpperCase().split(''),n=chars.length;if(!n)return;const fs=Math.floor(h/(n+1)*0.7);c.font=`700 ${fs}px 'Montserrat',sans-serif`;c.fillStyle=clr;c.textAlign='center';c.textBaseline='middle';const gap=h/(n+1);const lineW=w*0.2,lineH=Math.max(1,h*0.008);chars.forEach((ch,i)=>{const y=gap*(i+1);c.fillText(ch,w/2,y);if(i<n-1){c.fillRect(w/2-lineW/2,y+gap*0.4,lineW,lineH);}});}},
    { name: 'Banner Ribbon', font: "'Merriweather', serif", twoTone: true, retired: true, render(c,t,w,h,clr,clr2) { const fr=clr2||clr;const cx=w/2,cy=h/2,bw=w*0.8,bh=h*0.32,fold=bw*0.08;c.fillStyle=fr;c.globalAlpha=0.12;c.beginPath();c.moveTo(cx-bw/2,cy-bh/2);c.lineTo(cx+bw/2,cy-bh/2);c.lineTo(cx+bw/2+fold,cy);c.lineTo(cx+bw/2,cy+bh/2);c.lineTo(cx-bw/2,cy+bh/2);c.lineTo(cx-bw/2-fold,cy);c.closePath();c.fill();c.globalAlpha=1;c.strokeStyle=fr;c.lineWidth=Math.max(1,h*0.012);c.stroke();const fs=Math.floor(bh*0.5);c.font=`700 ${fs}px 'Merriweather',serif`;c.fillStyle=clr;const mw=c.measureText(t.toUpperCase()).width,sc=mw>bw*0.75?bw*0.75/mw:1;c.save();c.translate(cx,cy);if(sc<1)c.scale(sc,sc);c.textAlign='center';c.textBaseline='middle';c.fillText(t.toUpperCase(),0,0);c.restore();}},
    { name: 'Bracket Frame', font: "'Raleway', sans-serif", twoTone: true, retired: true, render(c,t,w,h,clr,clr2) { const fr=clr2||clr;const cx=w/2,cy=h/2,bw=w*0.7,bh=h*0.5,bk=bw*0.08;c.strokeStyle=fr;c.lineWidth=Math.max(1.5,h*0.012);c.beginPath();c.moveTo(cx-bw/2+bk,cy-bh/2);c.lineTo(cx-bw/2,cy-bh/2);c.lineTo(cx-bw/2,cy+bh/2);c.lineTo(cx-bw/2+bk,cy+bh/2);c.stroke();c.beginPath();c.moveTo(cx+bw/2-bk,cy-bh/2);c.lineTo(cx+bw/2,cy-bh/2);c.lineTo(cx+bw/2,cy+bh/2);c.lineTo(cx+bw/2-bk,cy+bh/2);c.stroke();const lw=bw*0.6,lh2=Math.max(1,h*0.008);c.fillStyle=fr;c.fillRect(cx-lw/2,cy-bh/2-h*0.06,lw,lh2);c.fillRect(cx-lw/2,cy+bh/2+h*0.05,lw,lh2);const fs=Math.floor(h*0.22);c.font=`600 ${fs}px 'Raleway',sans-serif`;c.fillStyle=clr;const sp=t.toUpperCase().split('').join('  ');const mw=c.measureText(sp).width,sc=mw>bw*0.7?bw*0.7/mw:1;c.save();c.translate(cx,cy);if(sc<1)c.scale(sc,sc);c.textAlign='center';c.textBaseline='middle';c.fillText(sp,0,0);c.restore();}},
    { name: 'Split Monogram', font: "'Oswald', sans-serif", supports: '1', render(c,t,w,h,clr) { const chars=t.toUpperCase().split('');if(!chars.length)return;const cx=w/2,cy=h/2;c.fillStyle=clr;c.textAlign='center';c.textBaseline='middle';const lh=Math.max(1.5,h*0.01);if(chars.length===1){const fs=Math.floor(h*0.5);c.font=`700 ${fs}px 'Oswald',sans-serif`;c.fillText(chars[0],cx,cy);const m=c.measureText(chars[0]);const tTop=cy-(m.actualBoundingBoxAscent||fs*0.4);const tBot=cy+(m.actualBoundingBoxDescent||fs*0.1);const gap=h*0.03,lw=w*0.5;c.fillRect(cx-lw/2,tTop-gap-lh,lw,lh);c.fillRect(cx-lw/2,tBot+gap,lw,lh);}else if(chars.length===2){const fs=Math.floor(h*0.35);c.font=`700 ${fs}px 'Oswald',sans-serif`;c.fillText(chars[0],cx-w*0.15,cy);c.fillText(chars[1],cx+w*0.15,cy);const m=c.measureText(chars[0]);const tTop=cy-(m.actualBoundingBoxAscent||fs*0.4);const tBot=cy+(m.actualBoundingBoxDescent||fs*0.1);const gap=h*0.03,lw=w*0.5;c.fillRect(cx-lw/2,tTop-gap-lh,lw,lh);c.fillRect(cx-lw/2,tBot+gap,lw,lh);}else{const bigFs=Math.floor(h*0.45),smFs=Math.floor(h*0.22);c.font=`700 ${bigFs}px 'Oswald',sans-serif`;const mid=chars[Math.floor(chars.length/2)];const m=c.measureText(mid);const tTop=cy-(m.actualBoundingBoxAscent||bigFs*0.4);const tBot=cy+(m.actualBoundingBoxDescent||bigFs*0.1);const gap=h*0.03,lw=w*0.45;c.fillText(mid,cx,cy);c.fillRect(cx-lw/2,tTop-gap-lh,lw,lh);c.fillRect(cx-lw/2,tBot+gap,lw,lh);c.font=`500 ${smFs}px 'Oswald',sans-serif`;const left=chars.slice(0,Math.floor(chars.length/2)).join('');const right=chars.slice(Math.floor(chars.length/2)+1).join('');c.fillText(left,cx-w*0.28,cy);c.fillText(right,cx+w*0.28,cy);}}},
    { name: 'Classic Serif', font: "'Cormorant Garamond', serif", supports: '3', twoTone: true, render(c,t,w,h,clr,clr2){ const ac=clr2||clr;const ch=(t||'').toUpperCase().split('');if(!ch.length)return;const cx=w/2,cy=h/2;c.textAlign='center';c.textBaseline='middle';if(ch.length===1){c.fillStyle=clr;c.font=`600 ${Math.floor(h*0.62)}px 'Cormorant Garamond',serif`;c.fillText(ch[0],cx,cy);}else if(ch.length===2){const fs=Math.floor(h*0.5);c.font=`600 ${fs}px 'Cormorant Garamond',serif`;c.fillStyle=clr;c.fillText(ch[0],cx-w*0.17,cy);c.fillStyle=ac;c.fillText(ch[1],cx+w*0.17,cy);}else{c.font=`600 ${Math.floor(h*0.34)}px 'Cormorant Garamond',serif`;c.fillStyle=clr;c.fillText(ch[0],cx-w*0.27,cy+h*0.01);c.fillText(ch[2],cx+w*0.27,cy+h*0.01);c.font=`600 ${Math.floor(h*0.64)}px 'Cormorant Garamond',serif`;c.fillStyle=ac;c.fillText(ch[1],cx,cy);} } },
    { name: 'Vine Laurel', font: "'Cormorant Garamond', serif", supports: '1', twoTone: true, render(c,t,w,h,clr,clr2){ const fr=clr2||clr;const R=Math.min(w,h)*0.42,cx=w/2,cy=h/2,N=7;c.fillStyle=fr;const leaf=(x,y,rot)=>{c.save();c.translate(x,y);c.rotate(rot);c.beginPath();c.ellipse(0,0,R*0.15,R*0.065,0,0,Math.PI*2);c.fill();c.restore();};for(let i=0;i<N;i++){const tt=i/(N-1);const pr=(158-tt*126)*Math.PI/180;leaf(cx+Math.sin(pr)*R,cy-Math.cos(pr)*R,-pr+Math.PI/2);const pl=(-158+tt*126)*Math.PI/180;leaf(cx+Math.sin(pl)*R,cy-Math.cos(pl)*R,-pl+Math.PI/2);}const txt=(t||'').toUpperCase();const fs=Math.floor(h*0.4);c.fillStyle=clr;c.font=`600 ${fs}px 'Cormorant Garamond',serif`;c.textAlign='center';c.textBaseline='middle';const mw=c.measureText(txt).width,sc=mw>R*1.1?R*1.1/mw:1;c.save();c.translate(cx,cy);if(sc<1)c.scale(sc,sc);c.fillText(txt,0,0);c.restore(); } },
    { name: 'Crest Shield', font: "'Cormorant Garamond', serif", supports: '12', twoTone: true, render(c,t,w,h,clr,clr2){ const fr=clr2||clr;const cx=w/2,halfW=Math.min(w,h)*0.30,top=h*0.13,midY=h*0.54,botY=h*0.88;c.strokeStyle=fr;c.lineWidth=Math.max(1.5,h*0.016);c.beginPath();c.moveTo(cx-halfW,top);c.lineTo(cx+halfW,top);c.lineTo(cx+halfW,midY);c.bezierCurveTo(cx+halfW,midY+(botY-midY)*0.5,cx+halfW*0.4,botY-(botY-midY)*0.08,cx,botY);c.bezierCurveTo(cx-halfW*0.4,botY-(botY-midY)*0.08,cx-halfW,midY+(botY-midY)*0.5,cx-halfW,midY);c.closePath();c.stroke();c.save();c.globalAlpha=0.5;c.lineWidth=Math.max(1,h*0.008);const iw=halfW*0.82,it=top+h*0.035,im=midY-h*0.01,ib=botY-h*0.05;c.beginPath();c.moveTo(cx-iw,it);c.lineTo(cx+iw,it);c.lineTo(cx+iw,im);c.bezierCurveTo(cx+iw,im+(ib-im)*0.5,cx+iw*0.4,ib-(ib-im)*0.08,cx,ib);c.bezierCurveTo(cx-iw*0.4,ib-(ib-im)*0.08,cx-iw,im+(ib-im)*0.5,cx-iw,im);c.closePath();c.stroke();c.restore();const txt=(t||'').toUpperCase().slice(0,2);const fs=Math.floor(h*0.3);c.fillStyle=clr;c.font=`600 ${fs}px 'Cormorant Garamond',serif`;c.textAlign='center';c.textBaseline='middle';const mw=c.measureText(txt).width,sc=mw>halfW*1.4?halfW*1.4/mw:1;c.save();c.translate(cx,top+(botY-top)*0.42);if(sc<1)c.scale(sc,sc);c.fillText(txt,0,0);c.restore(); } },
    { name: 'Block Modern', font: "'Montserrat', sans-serif", supports: '1', render(c,t,w,h,clr){ const s=Math.min(w,h)*0.66,x=(w-s)/2,y=(h-s)/2,r=s*0.16;c.fillStyle=clr;c.beginPath();c.moveTo(x+r,y);c.arcTo(x+s,y,x+s,y+s,r);c.arcTo(x+s,y+s,x,y+s,r);c.arcTo(x,y+s,x,y,r);c.arcTo(x,y,x+s,y,r);c.closePath();c.fill();const txt=(t||'').toUpperCase()||'A';c.save();c.globalCompositeOperation='destination-out';const fs=Math.floor(s*0.6);c.font=`800 ${fs}px 'Montserrat',sans-serif`;c.textAlign='center';c.textBaseline='middle';c.fillStyle='#000';const mw=c.measureText(txt).width,sc=mw>s*0.74?s*0.74/mw:1;c.save();c.translate(w/2,h/2);if(sc<1)c.scale(sc,sc);c.fillText(txt,0,s*0.02);c.restore();c.restore(); } },
    { name: 'Floral Wreath', font: "'Cormorant Garamond', serif", supports: '2', twoTone: true, render(c,t,w,h,clr,clr2){ const fr=clr2||clr;const R=Math.min(w,h)*0.40,cx=w/2,cy=h/2,N=16;c.fillStyle=fr;for(let i=0;i<N;i++){const a=(i/N)*Math.PI*2;const x=cx+Math.cos(a)*R,y=cy+Math.sin(a)*R;c.save();c.translate(x,y);c.rotate(a+Math.PI/2);c.beginPath();c.ellipse(0,0,R*0.13,R*0.055,0,0,Math.PI*2);c.fill();c.restore();const a2=((i+0.5)/N)*Math.PI*2;c.beginPath();c.arc(cx+Math.cos(a2)*R,cy+Math.sin(a2)*R,R*0.028,0,Math.PI*2);c.fill();}const txt=(t||'').toUpperCase();const fs=Math.floor(h*0.34);c.fillStyle=clr;c.font=`600 ${fs}px 'Cormorant Garamond',serif`;c.textAlign='center';c.textBaseline='middle';const mw=c.measureText(txt).width,sc=mw>R*1.25?R*1.25/mw:1;c.save();c.translate(cx,cy);if(sc<1)c.scale(sc,sc);c.fillText(txt,0,0);c.restore(); } },
    { name: 'Duogram', font: "'Cormorant Garamond', serif", supports: '2', twoTone: true, render(c,t,w,h,clr,clr2){ const chars=((t||'').toUpperCase()||'AB').split('').slice(0,2);c.textAlign='center';c.textBaseline='middle';const fs=Math.floor(h*0.62);c.font=`italic 600 ${fs}px 'Cormorant Garamond',serif`;const ws=chars.map(ch=>c.measureText(ch).width);const ov=fs*0.18;let total=ws.reduce((s,v)=>s+v,0)-ov*Math.max(0,chars.length-1);const maxW=w*0.84,sc=total>maxW?maxW/total:1;c.save();c.translate(w/2,h/2);if(sc<1)c.scale(sc,sc);let x=-total/2;chars.forEach((ch,i)=>{c.fillStyle=(i===1&&clr2)?clr2:clr;c.fillText(ch,x+ws[i]/2,0);x+=ws[i]-ov;});c.restore(); } },
  ];

  function setupMonogram() {
    $('dt-open-monogram')?.addEventListener('click', openMonogramPopup);
  }

  /* Build monogram UI inside the desktop panel instead of a popup overlay */
  function buildMonogramInPanel() {
    const panel = $('dt-panel-monogram');
    if (!panel) return;
    openMonogramPopup(panel);   /* header preservation handled inside */
  }

  let _editingMonoLayer = null;
  /* Open the full monogram editor pre-filled to edit a placed monogram.
     Desktop → the left-panel flyout (same place as adding / other panel edits);
     mobile → the standard overlay. */
  function openMonogramEditor(layer) {
    if (!layer || !layer.isMonogram) return;
    _editingMonoLayer = layer;
    if (layer.monoStyle != null && layer.monoStyle >= 0) S.monoStyle = layer.monoStyle;
    if (layer.monoColor) S.monoColor = layer.monoColor;
    if (layer.monoColor2) S.monoColor2 = layer.monoColor2;
    if (isMobile()) { openMonogramPopup(null, layer); return; }
    const panel = $('dt-panel-monogram');
    if (!panel) { openMonogramPopup(null, layer); return; }
    /* Deselect so the floating properties overlay doesn't cover the panel,
       then activate the monogram panel exactly like a tool-tab open. */
    S.sel = null; S.iact = null;
    $$('.dt-sidebar__tab').forEach(b => b.classList.toggle('active', b.dataset.panel === 'monogram'));
    $$('.dt-panel').forEach(p => p.classList.remove('active'));
    panel.classList.add('active');
    document.querySelector('.dt-sidebar__panels')?.classList.add('has-active-panel');
    document.querySelector('.dt-sidebar--left')?.classList.add('has-active-tab');
    /* Match the add-flow: editing a placed monogram opens the same wide
       library drawer the Monogram tab uses (was slot-width only). */
    openLibraryDrawer();
    forcePropsRebuild(); render();
    openMonogramPopup(panel, layer);
  }

  function openMonogramPopup(targetPanel, editLayer) {
    const isPanel = !!targetPanel;
    /* UX-Aug20: seed monogram colour from product luminance (new monograms only).
       Deferred: seed only the input element value, NOT S.monoColor — avoids the
       cancel-leak where S.monoColor gets mutated before the user commits. */
    var _seedMono = (!editLayer && S.method === 'print') ? seedMonoColor() : null;
    var _initColor = (_seedMono && _seedMono.auto) ? _seedMono.color : S.monoColor;
    let _userPickedColor = false; /* UX-Aug20: dirty flag — true once the user touches the color picker */
    let container;

    if (isPanel) {
      /* LIBRARY SHELL: keep the panel's standard title/subtitle header — render
         the monogram builder into a dedicated ui div below it (every other
         library keeps its header; this one was wiping it). */
      if (targetPanel.classList && targetPanel.classList.contains('dt-panel')) {
        let monoUi = targetPanel.querySelector('#dt-monogram-ui');
        if (!monoUi) {
          const head = targetPanel.querySelector('.dt-panel__header');
          targetPanel.innerHTML = '';
          if (head) targetPanel.appendChild(head);
          monoUi = document.createElement('div'); monoUi.id = 'dt-monogram-ui';
          targetPanel.appendChild(monoUi);
        }
        container = monoUi;
      } else {
        container = targetPanel;
      }
    } else {
      document.getElementById('dt-mono-overlay')?.remove();
      container = document.createElement('div');
      container.id = 'dt-mono-overlay';
      container.className = 'dt-rbg-overlay' + (isMobile() ? ' dt-above-tabs dt-kb-aware' : '');
    }
    let letterCount = 3;  /* Default: traditional 3-letter monogram */

    const monoContent = `
      <div class="dt-dlg-field">
        <div class="dt-label">Your Initials</div>
        <input type="text" id="dt-mono-input" class="dt-input" maxlength="3" placeholder="ABC" style="text-align:center;text-transform:uppercase">
      </div>
      <div class="dt-mono-row1">
        <div id="dt-mono-letter-tabs" class="dt-tpl-cats" role="tablist" aria-label="Monogram letter count">
          <button class="dt-tpl-cat dt-mono-ltab is-active" type="button" role="tab" aria-selected="true" data-letters="3">3 Letter</button>
          <button class="dt-tpl-cat dt-mono-ltab" type="button" role="tab" aria-selected="false" data-letters="2">2 Letter</button>
          <button class="dt-tpl-cat dt-mono-ltab" type="button" role="tab" aria-selected="false" data-letters="1">1 Letter</button>
        </div>
        <div id="dt-mono-colors"${S.method === 'engrave' ? ' hidden' : ''}>
          <label class="dt-mono-swatch" title="Monogram color">
            <div class="dt-prop-color-chip" id="dt-mono-c1-chip" style="background:${_initColor}"></div>
            <span id="dt-mono-c1-lbl" class="dt-sr-only">Color</span>
            <input type="color" id="dt-mono-c1" value="${_initColor}" class="dt-hidden-input">
          </label>
          <label class="dt-mono-swatch" id="dt-mono-c2-wrap" hidden title="Second color">
            <div class="dt-prop-color-chip" id="dt-mono-c2-chip" style="background:${S.monoColor2 || '#F47E46'}"></div>
            <span class="dt-sr-only">Color 2</span>
            <input type="color" id="dt-mono-c2" value="${S.monoColor2 || '#F47E46'}" class="dt-hidden-input">
          </label>
        </div>
      </div>
      <div class="dt-label">Style</div>
      <div class="dt-picker-carousel">
        <button class="dt-picker-arrow dt-picker-arrow--left" id="dt-mono-prev"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        <div class="dt-mono-picker" id="dt-mono-picker"></div>
        <button class="dt-picker-arrow dt-picker-arrow--right" id="dt-mono-next"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
      </div>
      <button class="dt-rbg-use-btn" id="dt-mono-add" style="width:100%;margin-top:16px">Add Monogram to Design</button>`;

    if (isPanel) {
      container.innerHTML = monoContent;
    } else {
      container.innerHTML = `<div class="dt-rbg-panel">
        <div class="dt-rbg-header"><span class="dt-rbg-title">Create Monogram</span><button class="dt-rbg-close" id="dt-mono-close">&times;</button></div>
        <div class="dt-rbg-scroll">${monoContent}</div>
      </div>`;
      document.body.appendChild(container);
    }

    const grid = container.querySelector('#dt-mono-picker');
    const input = container.querySelector('#dt-mono-input');
    const tabContainer = container.querySelector('#dt-mono-letter-tabs');
    /* On mobile add-flow: no default selection — user must pick a style.
       Edit flow always pre-selects the layer's current style. */
    const _monoNoDefault = isMobile() && !editLayer;
    let currentStyle = _monoNoDefault ? -2 : S.monoStyle;

    /* ── Alpha characters only — block numbers, symbols, emoji ── */
    if (input) {
      input.addEventListener('keydown', e => {
        /* Allow control keys: Backspace, Delete, Tab, Arrow keys, Enter */
        if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape'].includes(e.key)) return;
        /* Allow Ctrl/Cmd shortcuts (copy, paste, select all) */
        if (e.ctrlKey || e.metaKey) return;
        /* Block anything that's not a single letter A-Z */
        if (!/^[a-zA-Z]$/.test(e.key)) e.preventDefault();
      });
      input.addEventListener('input', () => {
        /* Strip any non-alpha characters that slip through (paste, autocomplete) */
        const clean = input.value.replace(/[^a-zA-Z]/g, '');
        if (clean !== input.value) input.value = clean;
      });
      input.addEventListener('paste', e => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData('text/plain');
        const clean = text.replace(/[^a-zA-Z]/g, '').slice(0, input.maxLength);
        document.execCommand('insertText', false, clean);
      });
    }

    /* ── Default placeholder per letter count ──────────── */
    const PLACEHOLDERS = { 1: 'A', 2: 'AB', 3: 'ABC' };

    /* ── Letter count tab handler ─────────────────────── */
    function setLetterCount(n) {
      letterCount = n;
      /* Update tab visual state */
      tabContainer.querySelectorAll('.dt-mono-ltab').forEach(btn => {
        const on = parseInt(btn.dataset.letters) === n;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', String(on));
      });
      /* Truncate input + update placeholder */
      if (input) {
        input.maxLength = n;
        input.placeholder = PLACEHOLDERS[n] || 'ABC';
        if (input.value.length > n) input.value = input.value.slice(0, n);
      }
      /* Filter custom style cards by supports flag.
         Built-in MONO_STYLES cards always show (they handle any letter count). */
      container.querySelectorAll('.dt-mono-card[data-supports]').forEach(card => {
        const supports = card.dataset.supports || '';
        card.style.display = supports.includes(String(n)) ? '' : 'none';
      });
      refreshPreviews();
    }

    tabContainer.addEventListener('click', e => {
      const btn = e.target.closest('.dt-mono-ltab');
      if (!btn) return;
      setLetterCount(parseInt(btn.dataset.letters) || 3);
    });

    /* ── Build built-in style cards (3-letter only) ──── */
    MONO_STYLES.forEach((style, i) => {
      /* Retired styles stay in the array so monoStyle INDICES never shift (saved
         designs that reference a later style keep rendering correctly), but they
         are not offered as a card in the picker. */
      if (style.retired) return;
      const card = document.createElement('div');
      card.className = 'dt-card dt-card--nocaption dt-mono-card' + (i === currentStyle ? ' selected' : '');
      card.dataset.i = i;
      card.dataset.supports = style.supports || '123';
      card.setAttribute('role', 'button'); card.tabIndex = 0; card.setAttribute('aria-label', style.name || 'Monogram style');
      /* rev 6: one badge, one meaning ("colours can be changed after adding") — every monogram is
         recolourable; the two-dot "two-tone" notation is retired (that fact lives in the props panel). */
      card.insertAdjacentHTML('beforeend', CARD_CHECK + CARD_BADGE);
      const _monoDpr = Math.min(window.devicePixelRatio || 1, 2);
      const _monoLogical = 160;
      const _monoPx = Math.round(_monoLogical * _monoDpr);
      const cvs = document.createElement('canvas'); cvs.width = _monoPx; cvs.height = _monoPx;
      const media = document.createElement('div'); media.className = 'dt-card__media'; media.appendChild(cvs);
      card.appendChild(media);
      const lbl = document.createElement('div');
      lbl.className = 'dt-card__caption dt-mono-card__label'; lbl.textContent = style.name;
      card.appendChild(lbl);
      grid.appendChild(card);
    });

    /* Auto-scroll to currently selected style */
    setTimeout(() => {
      const sel = grid.querySelector('.dt-mono-card.selected');
      if (sel) sel.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 100);

    /* Arrow buttons for desktop scroll */
    container.querySelector('#dt-mono-prev')?.addEventListener('click', () => {
      grid.scrollBy({ left: -150, behavior: 'smooth' });
    });
    container.querySelector('#dt-mono-next')?.addEventListener('click', () => {
      grid.scrollBy({ left: 150, behavior: 'smooth' });
    });

    /* ── Preview renderer — respects letterCount ──────── */
    function refreshPreviews() {
      const rawText = (input?.value || '').trim().toUpperCase() || PLACEHOLDERS[letterCount] || 'ABC';
      const t = rawText.slice(0, letterCount);

      /* Built-in style previews. Render by the card's REAL array index (card.dataset.i),
         NOT the forEach position: retired styles are skipped in the build loop, so any
         position after them is offset from MONO_STYLES. That offset made "Floral Wreath"
         render the Crest Shield, "Crest Shield" render Classic Serif, "Block Modern" render
         Vine Laurel, etc. Skip custom-style cards (they carry cvs._monoStyle, rendered below). */
      grid.querySelectorAll('.dt-mono-card').forEach((card) => {
        const cvs = card.querySelector('canvas');
        if (!cvs || cvs._monoStyle) return;
        const idx = parseInt(card.dataset.i, 10);
        const style = MONO_STYLES[idx];
        if (!style) return;
        const c = cvs.getContext('2d');
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        c.setTransform(dpr, 0, 0, dpr, 0, 0);
        c.clearRect(0, 0, 160, 160);
        try { style.render(c, t, 160, 160, S.monoColor, S.monoColor2); } catch (_) {}
      });
      /* Custom style previews — use the style's max supported count
         if the current tab exceeds what it supports */
      container.querySelectorAll('canvas').forEach(cvs => {
        if (cvs._monoStyle) {
          const ms = cvs._monoStyle;
          const sup = ms.supports || {};
          let previewText = t;
          if (!sup[letterCount]) {
            if (sup[1]) previewText = t.slice(0, 1);
            else if (sup[2]) previewText = t.slice(0, 2);
          }
          renderCustomMonoPreview(cvs, ms, previewText, S.monoColor);
        }
      });
    }

    document.fonts.ready.then(refreshPreviews);
    input?.addEventListener('input', refreshPreviews);

    /* ── Colour controls (Color 2 appears only for two-tone styles like Duogram) ── */
    const c1 = container.querySelector('#dt-mono-c1');
    const c2 = container.querySelector('#dt-mono-c2');
    const c1chip = container.querySelector('#dt-mono-c1-chip');
    const c2chip = container.querySelector('#dt-mono-c2-chip');
    const c2wrap = container.querySelector('#dt-mono-c2-wrap');
    const c1lbl = container.querySelector('#dt-mono-c1-lbl');
    function updateColorUI() {
      const st = currentStyle >= 0 ? MONO_STYLES[currentStyle] : null;
      const twoTone = !!(st && st.twoTone);
      if (c2wrap) c2wrap.hidden = !twoTone;
      if (c1lbl) c1lbl.textContent = twoTone ? 'Color 1' : 'Color';
    }
    /* UX-Aug20 rev 2: debounce refreshPreviews on color drag — each preview
       re-renders all built-in canvases + supersampled custom-style canvases,
       which at 60 Hz input rate caused progressive lag. Chip updates are instant. */
    var _prevTimer = null;
    var _debouncedPreviews = function () { if (_prevTimer) clearTimeout(_prevTimer); _prevTimer = setTimeout(function () { _prevTimer = null; refreshPreviews(); }, 100); };
    c1?.addEventListener('input', () => { S.monoColor = c1.value; _userPickedColor = true; /* UX-Aug20 */ if (c1chip) c1chip.style.background = c1.value; _debouncedPreviews(); });
    c2?.addEventListener('input', () => { S.monoColor2 = c2.value; if (c2chip) c2chip.style.background = c2.value; _debouncedPreviews(); });
    updateColorUI();

    /* Apply the default letter-count filter on open (add flow). The edit flow
       sets its own count from the existing monogram further below. */
    if (!editLayer) setLetterCount(letterCount);

    /* Preload border images for custom styles, re-render when loaded */
    const borderUrls = new Set();
    (CFG.monogramCollections || []).forEach(coll => {
      (coll.styles || []).forEach(ms => { if (ms.borderImage) borderUrls.add(ms.borderImage); });
    });
    if (borderUrls.size > 0) {
      Promise.all([...borderUrls].map(loadBorderImage)).then(() => {
        refreshPreviews();
      });
    }

    const _monoAddBtn = container.querySelector('#dt-mono-add');
    /* On mobile add-flow, hide the Add button until the user picks a style */
    if (_monoNoDefault && _monoAddBtn) _monoAddBtn.style.display = 'none';

    grid.addEventListener('click', e => {
      const card = e.target.closest('.dt-mono-card'); if (!card) return;
      currentStyle = parseInt(card.dataset.i); S.monoStyle = currentStyle;
      updateColorUI();
      /* Built-in and custom cards share this grid; toggling here selects the
         clicked card and deselects every other card (custom cards have no
         data-i, so they evaluate false and clear). Do NOT add a second pass
         that removes .selected from [data-supports] cards — built-in cards
         also carry data-supports, so it would wipe the selection we just set. */
      grid.querySelectorAll('.dt-mono-card').forEach(c => c.classList.toggle('selected', c.dataset.i == currentStyle));
      /* Reveal the Add button once a style is chosen (mobile add-flow) */
      if (_monoAddBtn) _monoAddBtn.style.display = '';
    });

    const close = () => { if (isPanel) { /* panel stays, just clear selection */ } else { container.remove(); } };
    container.querySelector('#dt-mono-close')?.addEventListener('click', close);
    container.addEventListener('click', e => { if (e.target === container) close(); });

    _monoAddBtn.addEventListener('click', () => {
      const raw = (input?.value || '').trim().toUpperCase();
      const t = raw.slice(0, letterCount);
      if (!t) {
        /* Flash the input with a hint + scroll it into view (it can be off-screen
           when the user scrolled to the Add button, making the red border invisible). */
        input.placeholder = 'Enter your initials';
        input.style.borderColor = 'var(--dt-coral,#f47e46)';
        input.style.boxShadow = '0 0 0 3px rgba(244,126,70,0.2)';
        input?.focus();
        try { input.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
        toast('Enter your initials above to create a monogram.');
        setTimeout(() => {
          input.placeholder = PLACEHOLDERS[letterCount] || 'ABC';
          input.style.borderColor = '';
          input.style.boxShadow = '';
        }, 2500);
        return;
      }
      /* Commit immediately. Do NOT gate on the GLOBAL document.fonts.ready — selecting a
         style card calls refreshPreviews(), which sets canvas fonts and can push
         fonts.ready back into a pending state, so gating here deferred the first Save
         until every preview font settled. That delay looked like "nothing happened",
         so the user clicked Save a second time (the real "click Save twice" cause).
         addMonogramLayer / addCustomMonogramLayer each preload their OWN specific font
         before rendering, so font-readiness is handled precisely without this gate. */
      const commit = () => {
        /* UX-Aug20: commit the seeded colour into S.monoColor now (deferred from popup open).
           Flag auto only when the seed was used AND the user didn't touch the picker. */
        const _commitColor = c1 ? c1.value : S.monoColor;
        S.monoColor = _commitColor;
        const _monoAutoColor = !editLayer && _seedMono && _seedMono.auto && !_userPickedColor;
        if (currentStyle === -1 && container._customMonoStyle) {
          addCustomMonogramLayer(t, container._customMonoStyle, S.monoColor, editLayer, { _autoColor: _monoAutoColor });
        } else {
          addMonogramLayer(t, currentStyle, S.monoColor, editLayer, S.monoColor2, _monoAutoColor);
        }
        if (editLayer) _editingMonoLayer = null;
        if (!isPanel) close();
        /* Mirror the Graphics add flow: close the desktop panel/drawer so the
           new/updated monogram's Properties overlay is visible. The drawer
           floats OVER the canvas (z:60) above the props overlay (z:40), so
           leaving it open buried the props of the layer just added. */
        else closeDesktopToolPanel();
      };
      commit();
    });

    /* ── Custom Monogram Collections (from metaobjects) ── */
    if (CFG.monogramCollections?.length) {
      CFG.monogramCollections.forEach((coll, colIdx) => {
        (coll.styles || []).forEach((ms, si) => {
          const sourceType = Array.isArray(ms.sourceType) ? ms.sourceType[0] : ms.sourceType;

          /* Normalize supports flags (Shopify booleans can arrive as various types) */
          const sup = ms.supports || {};
          const supports1 = sup[1] === true || sup[1] === 'true' || sup['1'] === true || sup['1'] === 'true';
          const supports2 = sup[2] === true || sup[2] === 'true' || sup['2'] === true || sup['2'] === 'true';
          const supports3 = sup[3] === true || sup[3] === 'true' || sup['3'] === true || sup['3'] === 'true';

          /* Compact string of supported letter counts for data-attribute filtering */
          let supStr = '';
          if (supports1) supStr += '1';
          if (supports2) supStr += '2';
          if (supports3) supStr += '3';
          if (!supStr) supStr = '123'; /* If none are set, assume all */

          const card = document.createElement('div');
          /* rev 6b (Brandon, Aug 17): custom (metaobject) monograms now use the SAME
             .dt-card anatomy + badge as the built-in styles. They were a bare
             .dt-mono-card, which is why custom monograms kept the old border/badge look
             on both desktop and mobile after the rev-6 card system landed. */
          card.className = 'dt-card dt-card--nocaption dt-mono-card';
          card.dataset.supports = supStr;
          card.setAttribute('role', 'button'); card.tabIndex = 0; card.setAttribute('aria-label', ms.name || 'Monogram style');
          card.insertAdjacentHTML('beforeend', CARD_CHECK + CARD_BADGE);

          const cvs = document.createElement('canvas');
          const _cdpr = Math.min(window.devicePixelRatio || 1, 2);
          cvs.width = Math.round(160 * _cdpr); cvs.height = Math.round(160 * _cdpr);
          cvs._monoStyle = ms;
          /* Store normalized supports back for refreshPreviews */
          ms.supports = { 1: supports1, 2: supports2, 3: supports3 };
          const media = document.createElement('div'); media.className = 'dt-card__media'; media.appendChild(cvs);
          card.appendChild(media);

          const label = document.createElement('div');
          label.className = 'dt-card__caption dt-mono-card__label';
          label.textContent = ms.name || 'Style ' + (si + 1);
          card.appendChild(label);

          /* Font registration: one @font-face per available slot — ONCE per
             session. This block used to run on EVERY popup open (tab open,
             edit, re-open), appending duplicate @font-face <style> rules for
             the same families and re-firing loads for every slot of every
             style. The duplicates accumulate, and canvas measureText/fillText
             font matching gets progressively heavier — the prime suspect in
             the first-"Edit Monogram" renderer stall seen in live testing.
             ms._fontFamilies doubles as the already-registered flag (persists
             across opens because ms lives on the global CFG). */
          if (sourceType === 'font' || !sourceType) {
            if (!ms._fontFamilies) {
              const slotUrls = {
                single: ms.files?.single || '',
                left:   ms.files?.left   || '',
                center: ms.files?.center || '',
                right:  ms.files?.right  || '',
              };
              const safeName = (coll.name || 'Custom').replace(/\s/g, '');
              const families = {};

              Object.entries(slotUrls).forEach(([slot, url]) => {
                if (!url) return;
                const fontName = 'MonoCustom_' + colIdx + '_' + si + '_' + slot + '_' + safeName;
                const rule = document.createElement('style');
                rule.textContent = "@font-face { font-family: '" + fontName +
                  "'; src: url('" + url + "'); font-display: swap; }";
                document.head.appendChild(rule);
                families[slot] = fontName;
              });

              ms._fontFamilies = families;
              ms._canvasFontFamily = families.single || families.center ||
                                     families.left   || families.right  || null;
            }

            const fontNames = Object.values(ms._fontFamilies);
            if (fontNames.length) {
              const defaultText = supports1 && !supports3 ? 'A'
                                : supports2 && !supports3 ? 'AB'
                                : 'ABC';
              Promise.all(fontNames.map(fn =>
                document.fonts.load('400 48px "' + fn + '"')
              )).then(() => {
                renderCustomMonoPreview(cvs, ms, defaultText, S.monoColor);
              }).catch(() => {
                renderCustomMonoPreview(cvs, ms, defaultText, S.monoColor);
              });
            } else {
              DEBUG && console.warn('[dt] monogram: no font URLs for style', ms.name);
              renderCustomMonoPreview(cvs, ms, 'A', S.monoColor);
            }
          } else {
            renderCustomMonoPreview(cvs, ms, 'A', S.monoColor);
          }

          card.addEventListener('click', e => {
            e.stopPropagation();
            grid.querySelectorAll('.dt-mono-card').forEach(c => c.classList.remove('selected'));
            card.classList.add('selected');
            currentStyle = -1;
            container._customMonoStyle = ms;
            /* Reveal the Add button once a style is chosen (mobile add-flow) */
            if (_monoAddBtn) _monoAddBtn.style.display = '';
            /* Warm THIS style's specific font(s) + border image the moment it's picked,
               so the very first Save renders instantly with no async wait inside the
               commit (belt-and-suspenders alongside removing the global fonts.ready gate). */
            try {
              Object.values(ms._fontFamilies || {}).forEach(function (fam) {
                if (fam && document.fonts && document.fonts.load) document.fonts.load("64px '" + fam + "'").catch(function () {});
              });
              if (ms.borderImage) loadBorderImage(ms.borderImage);
            } catch (_warmErr) {}
            refreshPreviews();
          });

          grid.appendChild(card);

          /* Hide card if it doesn't support the default tab (3-letter) */
          if (!supStr.includes(String(letterCount))) card.style.display = 'none';
        });
      });
    }

    /* ── Edit mode: pre-fill from the monogram being edited ── */
    if (editLayer) {
      if (input) input.value = (editLayer.monoText || '');
      setLetterCount((editLayer.monoText || 'ABC').length || 3);
      const addBtn2 = container.querySelector('#dt-mono-add'); if (addBtn2) addBtn2.textContent = 'Save Changes';
      const titleEl = container.querySelector('.dt-rbg-title'); if (titleEl) titleEl.textContent = 'Edit Monogram';
      /* Pre-select a custom collection style by name if this monogram uses one */
      if (editLayer.monoStyle === -1 && editLayer.customMonoStyleName) {
        container.querySelectorAll('canvas').forEach(cvs => {
          if (cvs._monoStyle && cvs._monoStyle.name === editLayer.customMonoStyleName) {
            currentStyle = -1; container._customMonoStyle = cvs._monoStyle;
            grid.querySelectorAll('.dt-mono-card').forEach(c => c.classList.remove('selected'));
            container.querySelectorAll('.dt-mono-card[data-supports]').forEach(c => c.classList.remove('selected'));
            const card = cvs.closest('.dt-mono-card'); if (card) card.classList.add('selected');
          }
        });
      }
      refreshPreviews();
    }

    /* No auto-focus on open — keeps the mobile keyboard from covering the style grid,
       and matches the Add Text panel, which doesn't auto-focus either. */
  }

  /* ── Custom Monogram Preview (renders on canvas in the picker) ── */
  /* ── Per-position font picker ─────────────────────────
     Returns the most specific font family for a given letter position,
     with sensible fallbacks if that slot wasn't uploaded. */
  function _monoFontFor(ms, position) {
    const f = ms._fontFamilies || {};
    return f[position] ||
           f.single  ||
           f.left    ||
           f.center  ||
           f.right   ||
           ms._canvasFontFamily ||
           'serif';
  }

  /* ── Glyph-template monogram styles (v2.9.33) ──────────────────────
     Some monogram fonts are ligature-style: EVERY part of the design — the
     left form, the centre form, the right form, and the surrounding frame —
     is a glyph in ONE font file, positioned by the font's own advances and,
     for the Square Diamond family, by its `calt` contextual alternates.
     Such a font cannot be driven by the per-slot renderer below, because
     that issues one fillText per letter and per-glyph shaping never fires
     calt: the positional forms are never substituted and the frame glyph
     never lands. Measured Jul 29 2026 in Chromium, both failure modes.

     A style opts in by setting `glyph_template` on its metaobject entry.
     The template is a print-format string:
        {N}    substitute the Nth initial the customer typed
        {NU}   ... forced UPPERCASE      {NL}   ... forced lowercase
        {{ }}  literal braces
        anything else   a literal character handed straight to the font
     e.g.  Octagone       (2 letters, frame on '1'):  {1U}{2L}1
           Square Diamond (3 letters, frame on '?'):  {1}{2}{3}?

     A style with NO template is untouched — it takes the per-slot path
     below exactly as before. This is the only behavioural gate; nothing
     else in the monogram pipeline changed. */
  function monoGlyphTemplate(ms) {
    const t = ms && ms.glyphTemplate;
    return (typeof t === 'string' && t.trim()) ? t : null;
  }

  function buildGlyphString(tpl, initials) {
    const src = String(initials == null ? '' : initials);
    return String(tpl).replace(/\{\{|\}\}|\{(\d)([UL])?\}/g, function (m, n, cs) {
      if (m === '{{') return '{';
      if (m === '}}') return '}';
      const ch = src[Number(n) - 1] || '';
      return cs === 'U' ? ch.toUpperCase() : cs === 'L' ? ch.toLowerCase() : ch;
    });
  }

  /* Draw a templated monogram as ONE fillText, centred on its INK box, scaled
     to fill the given box. Returns false (drawing nothing) if there is nothing
     to draw or the face has not loaded — callers treat a blank canvas as
     "retry after fonts.ready", which is the behaviour we want.

     Centring is on actualBoundingBox*, NOT measureText().width. The frame
     glyph in these fonts carries a large negative left side bearing so it
     draws BACKWARDS over the letters already set — Octagone's frame spans
     x -840..97 on a 137-unit advance. Advance width is therefore not the
     visual extent, and centring on it puts the design far left of centre.

     layout.spacing / centerScale / baselineOffset are deliberately NOT read
     here. They exist to fake letter placement that these fonts already do
     themselves; applying them would shift letters out of their own frame.
     defaultColor, textScale and textOffsetY still apply. */
  function drawTemplatedMono(ctx2, ms, tpl, text, boxW, boxH, fillColor) {
    const str = buildGlyphString(tpl, text);
    if (!str) return false;
    const fam       = _monoFontFor(ms, 'single');
    const textScale = ms.layout?.textScale   || 1.0;
    const textOffY  = ms.layout?.textOffsetY || 0;
    const FILL  = 0.9;                          /* fraction of the box the ink fills */
    const probe = Math.max(8, Math.round(boxH * 0.5));  /* measure once, then scale */

    ctx2.textAlign = 'left'; ctx2.textBaseline = 'alphabetic';
    ctx2.font = probe + "px '" + fam + "', serif";
    let m;
    try { m = ctx2.measureText(str); } catch (_e) { return false; }
    const inkW = (m.actualBoundingBoxLeft || 0) + (m.actualBoundingBoxRight || 0);
    const inkH = (m.actualBoundingBoxAscent || 0) + (m.actualBoundingBoxDescent || 0);
    if (!(inkW > 0) || !(inkH > 0)) return false;

    /* Fit, then apply textScale — but never past the box edge. A monogram that
       overflows the square is silently CLIPPED, and that clip is baked into the
       production PNG/PDF/EPS, so the clamp is a production guard, not cosmetic. */
    const fitted = probe * Math.min((boxW * FILL) / inkW, (boxH * FILL) / inkH);
    const maxFit = probe * Math.min(boxW / inkW, boxH / inkH);
    const fs = Math.max(1, Math.floor(Math.min(fitted * textScale, maxFit)));

    ctx2.font = fs + "px '" + fam + "', serif";
    let m2;
    try { m2 = ctx2.measureText(str); } catch (_e) { return false; }
    const L = m2.actualBoundingBoxLeft    || 0, R = m2.actualBoundingBoxRight   || 0;
    const A = m2.actualBoundingBoxAscent  || 0, D = m2.actualBoundingBoxDescent || 0;
    if (!(L + R > 0) || !(A + D > 0)) return false;

    /* Pen origin sits at x; ink spans x-L .. x+R, so ink centre is x+(R-L)/2.
       Same reasoning vertically against the alphabetic baseline. */
    ctx2.fillStyle = fillColor;
    ctx2.fillText(str, boxW / 2 - (R - L) / 2,
                       boxH / 2 + (A - D) / 2 + textOffY * boxH);
    return true;
  }

  /* ── Monogram border image cache ──────────────────── */
  const _borderImgCache = new Map();

  function loadBorderImage(url) {
    if (!url) return Promise.resolve(null);
    if (_borderImgCache.has(url)) return Promise.resolve(_borderImgCache.get(url));
    return new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { _borderImgCache.set(url, img); resolve(img); };
      img.onerror = () => { _borderImgCache.set(url, null); resolve(null); };
      img.src = url;
    });
  }

  /**
   * Draw a border image on a monogram canvas and tint everything to the fill color.
   * Preserves the border's aspect ratio (centered, not stretched).
   * Call AFTER all text/content is drawn. Uses source-in to recolor all content.
   */
  function applyMonoBorderAndTint(ctx2, w, h, borderImg, fillColor) {
    /* Operate in the canvas's ACTUAL backing pixels via an identity transform. The old
       version sized the temp capture by the LOGICAL w×h while copying the full backing
       canvas (w×dpr); on retina/mobile (dpr>1) that captured only the top-left fraction of
       the text and misaligned it with the border — the "Strap doesn't display right on
       mobile" bug. cv.width/height are dpr-correct, so this works at any pixel ratio.
       Saved/restored so the caller's transform is untouched. */
    const cv = ctx2.canvas, BW = cv.width, BH = cv.height;
    ctx2.save();
    ctx2.setTransform(1, 0, 0, 1, 0, 0);
    if (borderImg) {
      const tempCanvas = document.createElement('canvas'); tempCanvas.width = BW; tempCanvas.height = BH;
      tempCanvas.getContext('2d').drawImage(cv, 0, 0);

      ctx2.clearRect(0, 0, BW, BH);

      /* Draw border centered, preserving aspect ratio (no stretching) */
      const imgW = borderImg.naturalWidth || BW;
      const imgH = borderImg.naturalHeight || BH;
      const imgAr = imgW / imgH;
      const canvasAr = BW / BH;
      let bw, bh, bx, by;
      if (imgAr > canvasAr) {
        bw = BW; bh = BW / imgAr; bx = 0; by = (BH - bh) / 2;
      } else {
        bh = BH; bw = BH * imgAr; bx = (BW - bw) / 2; by = 0;
      }
      ctx2.drawImage(borderImg, bx, by, bw, bh);

      /* Detect whether border is a filled/solid shape (vs outline/frame).
         Sample the center 20% region — if most pixels are opaque, the border
         fills its interior and text drawn on top would be invisible after
         uniform source-in tinting (both become the same flat color).  In that
         case knock the text OUT of the border so letters appear as transparent
         cutouts in the colored shape — the standard "reverse monogram" look. */
      var isFilled = false;
      try {
        var sx = Math.floor(BW * 0.4), sy = Math.floor(BH * 0.4);
        var sw = Math.max(1, Math.floor(BW * 0.2)), sh = Math.max(1, Math.floor(BH * 0.2));
        var sampleData = ctx2.getImageData(sx, sy, sw, sh).data;
        var opaqueCount = 0;
        for (var i = 3; i < sampleData.length; i += 4) {
          if (sampleData[i] > 128) opaqueCount++;
        }
        isFilled = opaqueCount / (sampleData.length / 4) > 0.5;
      } catch (_e) { /* CORS / security — fall back to overlay */ }

      if (isFilled) {
        /* Filled border: knock text out so letters are visible as cutouts */
        ctx2.globalCompositeOperation = 'destination-out';
        ctx2.drawImage(tempCanvas, 0, 0);
      } else {
        /* Outline/frame border: draw text on top (standard overlay) */
        ctx2.drawImage(tempCanvas, 0, 0);
      }
    }

    /* Tint all remaining content to the monogram color */
    ctx2.globalCompositeOperation = 'source-in';
    ctx2.fillStyle = fillColor;
    ctx2.fillRect(0, 0, BW, BH);
    ctx2.globalCompositeOperation = 'source-over';
    ctx2.restore();
  }

  /* Non-transparent bounding box of a 2D context's backing pixels. Returns
     {x,y,w,h} or null if fully transparent. Throws if the canvas is tainted
     (cross-origin image without CORS) — callers catch and skip the trim. */
  function _alphaBounds(ctx, W, H) {
    const data = ctx.getImageData(0, 0, W, H).data;
    let minX = W, minY = H, maxX = -1, maxY = -1;
    for (let y = 0; y < H; y++) {
      const row = y * W;
      for (let x = 0; x < W; x++) {
        if (data[(row + x) * 4 + 3] > 8) {
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  }

  function renderCustomMonoPreview(cvs, ms, text, color) {
    /* SUPERSAMPLE: draw the whole composite into a hi-res offscreen (4× the
       card's 160px box) so text is rasterized large and the cached CDN border
       image is drawn at full resolution, then trim to content and DOWNSCALE
       into the card (below). The earlier pass rendered at card size and scaled
       up, which was blurry — downscaling from 4× is crisp. All the drawing math
       below is unchanged; it just runs in this larger w×h space. */
    const _SS = 4, _D = 160 * _SS;
    const _off = document.createElement('canvas'); _off.width = _D; _off.height = _D;
    const ctx2 = _off.getContext('2d');
    const w = _D, h = _D;
    ctx2.clearRect(0, 0, w, h);

    const spacing      = ms.layout?.spacing       || 0.05;
    const centerScale  = ms.layout?.centerScale   || 1.0;
    const baselineOff  = ms.layout?.baselineOffset || 0;
    const fillColor    = color || ms.layout?.defaultColor || '#2a1f18';
    const textScale    = ms.layout?.textScale      || 1.0;
    const textOffY     = ms.layout?.textOffsetY    || 0;
    const cy           = h / 2 + textOffY * h; /* vertical center for text */

    /* Helper to set a font on the context for a given letter position */
    const setFont = (px, pos) => {
      ctx2.font = px + "px '" + _monoFontFor(ms, pos) + "', serif";
    };

    ctx2.fillStyle = fillColor;

    const _tpl = monoGlyphTemplate(ms);
    if (_tpl) {
      /* Templated style — one run, the font does the layout. Card and placed
         layer share drawTemplatedMono so the thumbnail cannot drift from the
         thing the customer actually gets. */
      drawTemplatedMono(ctx2, ms, _tpl, text, w, h, fillColor);

    } else if (text.length === 1) {
      /* Single letter — use the `single` font face */
      const fs = Math.floor(h * 0.45 * textScale);
      setFont(fs, 'single');
      ctx2.textAlign = 'center'; ctx2.textBaseline = 'middle';
      ctx2.fillText(text, w / 2, cy);

    } else if (text.length === 2) {
      /* Two letters — left + right faces, no center */
      const sideFs = Math.floor(h * 0.38 * textScale);

      setFont(sideFs, 'left');
      const lw = ctx2.measureText(text[0]).width;
      setFont(sideFs, 'right');
      const rw = ctx2.measureText(text[1]).width;

      const gap = spacing * w;
      const totalW = lw + rw + gap;
      const scale = totalW > w * 0.9 ? (w * 0.9) / totalW : 1;
      const startX = (w - totalW * scale) / 2;

      ctx2.save();
      ctx2.translate(startX, 0);
      if (scale < 1) ctx2.scale(scale, scale);
      ctx2.textAlign = 'left'; ctx2.textBaseline = 'middle';

      setFont(sideFs, 'left');
      ctx2.fillText(text[0], 0, cy);
      setFont(sideFs, 'right');
      ctx2.fillText(text[1], lw + gap, cy);
      ctx2.restore();

    } else if (text.length === 3) {
      /* Three letters — left, center (larger), right */
      const sideFs   = Math.floor(h * 0.3 * textScale);
      const centerFs = Math.floor(sideFs * centerScale);

      setFont(sideFs, 'left');
      const lw = ctx2.measureText(text[0]).width;
      setFont(sideFs, 'right');
      const rw = ctx2.measureText(text[2]).width;
      setFont(centerFs, 'center');
      const cw = ctx2.measureText(text[1]).width;

      const gap = spacing * w;
      const totalW = lw + cw + rw + gap * 2;
      const scale = totalW > w * 0.9 ? (w * 0.9) / totalW : 1;
      const startX = (w - totalW * scale) / 2;

      ctx2.save();
      ctx2.translate(startX, 0);
      if (scale < 1) ctx2.scale(scale, scale);
      ctx2.textAlign = 'left'; ctx2.textBaseline = 'middle';

      setFont(sideFs, 'left');
      ctx2.fillText(text[0], 0, cy);

      setFont(centerFs, 'center');
      ctx2.fillText(text[1], lw + gap, cy + baselineOff * h);

      setFont(sideFs, 'right');
      ctx2.fillText(text[2], lw + cw + gap * 2, cy);
      ctx2.restore();

    } else {
      /* 4+ letters — fall back to single-font centered render */
      const fs = Math.floor(h * 0.35 * textScale);
      setFont(fs, 'single');
      ctx2.textAlign = 'center'; ctx2.textBaseline = 'middle';
      const measured = ctx2.measureText(text).width;
      const maxW = w * 0.85;
      if (measured > maxW) {
        ctx2.save(); ctx2.translate(w/2, cy); ctx2.scale(maxW / measured, maxW / measured);
        ctx2.fillText(text, 0, 0); ctx2.restore();
      } else {
        ctx2.fillText(text, w / 2, cy);
      }
    }

    /* Apply border (if exists in cache) and tint everything uniformly */
    const borderImg = ms.borderImage ? _borderImgCache.get(ms.borderImage) : null;
    if (borderImg) {
      applyMonoBorderAndTint(ctx2, w, h, borderImg, fillColor);
    }

    /* ── Fill the card ──────────────────────────────────────────────
       The offscreen above is 4× the card, so the art is drawn large and crisp
       (text re-rasterized big; CDN border image at full resolution). Trim to
       its non-transparent bounds and DOWNSCALE into the card to fill ~86% —
       custom styles now match the built-in cards without blur. Thumbnail only:
       the placed monogram renders + trims separately in addCustomMonogramLayer,
       so production output is unchanged. */
    const out = cvs.getContext('2d');
    out.setTransform(1, 0, 0, 1, 0, 0);
    const _BW = cvs.width, _BH = cvs.height;
    out.clearRect(0, 0, _BW, _BH);
    out.imageSmoothingEnabled = true; out.imageSmoothingQuality = 'high';
    const _FILL = 0.86;   /* fraction of the card the art occupies */
    let _b = null;
    try { _b = _alphaBounds(ctx2, _D, _D); } catch (_e) { _b = null; }
    if (_b) {
      const s = Math.min((_BW * _FILL) / _b.w, (_BH * _FILL) / _b.h);
      const dw = _b.w * s, dh = _b.h * s;
      out.drawImage(_off, _b.x, _b.y, _b.w, _b.h, (_BW - dw) / 2, (_BH - dh) / 2, dw, dh);
    } else {
      /* Tainted canvas (border image without CORS) — fit the whole offscreen. */
      const s = Math.min((_BW * _FILL) / _D, (_BH * _FILL) / _D);
      const dw = _D * s, dh = _D * s;
      out.drawImage(_off, 0, 0, _D, _D, (_BW - dw) / 2, (_BH - dh) / 2, dw, dh);
    }
  }

  /* ── Custom Monogram Layer (from metaobject styles) ── */
  function addCustomMonogramLayer(text, monoStyle, color, existing, opts) {
    dtAnalytics.layerAdd('custom_monogram', text);
    if (!monoStyle) return;
    /* Preload this style's custom font(s) before rendering. Canvas text does NOT trigger
       font loading, so on the first Save the text falls back to serif and the monogram
       renders wrong/incomplete — only "taking" on a second Save (the click-twice bug for
       custom styles like the script monograms). Load every face this style uses, flag it
       so we don't recurse, then re-enter to render for real. */
    if (!monoStyle._fontsPreloaded && document.fonts && document.fonts.load) {
      var _fams = [];
      var _ff = monoStyle._fontFamilies || {};
      ['single', 'left', 'center', 'right'].forEach(function (p) { if (_ff[p]) _fams.push(_ff[p]); });
      if (monoStyle._canvasFontFamily) _fams.push(monoStyle._canvasFontFamily);
      _fams = _fams.filter(function (v, i, a) { return v && a.indexOf(v) === i; });
      if (_fams.length) {
        Promise.all(_fams.map(function (f) { return document.fonts.load("64px '" + f + "'").catch(function () {}); }))
          .then(function () {
            /* UX-Aug20 rev 2: bail if a newer color rebuild superseded us */
            if (opts && opts._monoGen !== undefined && _monoRebuildGen !== opts._monoGen) return;
            monoStyle._fontsPreloaded = true; addCustomMonogramLayer(text, monoStyle, color, existing, opts);
          });
        return;
      }
      monoStyle._fontsPreloaded = true;
    }
    /* v2.9.15: 512 -> 2048. CUSTOM monograms are the only layer family that ships
       an UPSCALED raster to production. renderProductionPNG re-renders built-in
       MONO_STYLES at the real production size (monoSize = max(lw, lh), see the
       l.monoStyle >= 0 branch), but a custom monogram has monoStyle === -1, fails
       that test, and falls through to the generic raster branch — where this
       canvas, trimmed, is simply scaled into the layer box. At 512 that was an
       upscale, and the jagged ring on the Jul 23 order (round_monogram_circle_500)
       is exactly it.
       2048 is sized from the real decoration areas, not picked round: across the
       five monogram-enabled products the largest area is Simple Modern 24 oz at
       2.5 x 6 in, so a monogram scaled to fill needs 6 * 300 = 1800 px. 2048 covers
       every live product with 248 px to spare; 1024 would be short by 776 px.
       COST, measured on the live "Round" style: the PNG data URL goes 47 KB -> 283 KB.
       serializeLayers keeps b64 TWICE per history snapshot (as b64 and as _imgSrc)
       and MAX_UNDO is 50, so worst case per side rises ~4.6 MB -> ~27.6 MB. Accepted.
       serializeDesign does NOT carry it (that gate needs isRasterUpload, which is
       false for monograms), so autosave and the saved-design payload are unaffected.
       trimToContent is safe at this size — it detects the bbox on a 1400 px downscale
       and crops from the full-resolution original.
       STOPGAP. Stops mattering once the monogram emits real svgMarkup and rides the
       vector branch; still governs the editor preview and thumbnails until then. */
    const size = 2048;
    const off = document.createElement('canvas');
    off.width = size; off.height = size;
    const oc = off.getContext('2d');

    if (monoStyle.sourceType === 'font' || !monoStyle.sourceType ||
        (Array.isArray(monoStyle.sourceType) && monoStyle.sourceType[0] === 'font')) {

      const spacing      = monoStyle.layout?.spacing       || 0.05;
      const centerScale  = monoStyle.layout?.centerScale   || 1.0;
      const baselineOff  = monoStyle.layout?.baselineOffset || 0;
      const textScale    = monoStyle.layout?.textScale      || 1.0;
      const textOffY     = monoStyle.layout?.textOffsetY    || 0;
      const cy           = size / 2 + textOffY * size;

      const setFont = (px, pos) => {
        oc.font = px + "px '" + _monoFontFor(monoStyle, pos) + "', serif";
      };

      oc.fillStyle = color || monoStyle.layout?.defaultColor || '#2a1f18';

      const _tpl = monoGlyphTemplate(monoStyle);
      if (_tpl) {
        /* Templated style — see drawTemplatedMono. If it draws nothing (face
           not loaded yet) the canvas stays blank and the existing blank-retry
           below re-enters once after fonts.ready, then leaves the placed layer
           untouched rather than committing an empty monogram. */
        drawTemplatedMono(oc, monoStyle, _tpl, text, size, size, oc.fillStyle);

      } else if (text.length === 1) {
        const fs = Math.floor(size * 0.45 * textScale);
        setFont(fs, 'single');
        oc.textAlign = 'center'; oc.textBaseline = 'middle';
        oc.fillText(text, size / 2, cy);

      } else if (text.length === 2) {
        /* Two letters — left + right faces */
        const sideFs = Math.floor(size * 0.38 * textScale);

        setFont(sideFs, 'left');
        const lw = oc.measureText(text[0]).width;
        setFont(sideFs, 'right');
        const rw = oc.measureText(text[1]).width;

        const gap = spacing * size;
        const totalW = lw + rw + gap;
        const scale = totalW > size * 0.9 ? (size * 0.9) / totalW : 1;
        const startX = (size - totalW * scale) / 2;

        oc.save();
        oc.translate(startX, 0);
        if (scale < 1) oc.scale(scale, scale);
        oc.textAlign = 'left'; oc.textBaseline = 'middle';

        setFont(sideFs, 'left');
        oc.fillText(text[0], 0, cy);
        setFont(sideFs, 'right');
        oc.fillText(text[1], lw + gap, cy);
        oc.restore();

      } else if (text.length === 3) {
        /* Classic three-letter monogram — left, center (larger), right */
        const sideFs   = Math.floor(size * 0.3 * textScale);
        const centerFs = Math.floor(sideFs * centerScale);

        setFont(sideFs, 'left');
        const leftW = oc.measureText(text[0]).width;
        setFont(centerFs, 'center');
        const centerW = oc.measureText(text[1]).width;
        setFont(sideFs, 'right');
        const rightW = oc.measureText(text[2]).width;

        const totalW = leftW + centerW + rightW + spacing * size * 2;
        const scale = totalW > size * 0.9 ? (size * 0.9) / totalW : 1;
        const startX = (size - totalW * scale) / 2;

        oc.save();
        oc.translate(startX, 0);
        if (scale < 1) oc.scale(scale, scale);
        oc.textAlign = 'left'; oc.textBaseline = 'middle';

        setFont(sideFs, 'left');
        oc.fillText(text[0], 0, cy);

        setFont(centerFs, 'center');
        oc.fillText(text[1], leftW + spacing * size, cy + baselineOff * size);

        setFont(sideFs, 'right');
        oc.fillText(text[2], leftW + centerW + spacing * size * 2, cy);
        oc.restore();

      } else {
        /* 4+ letters — single-font centered fallback */
        const fs = Math.floor(size * 0.35 * textScale);
        setFont(fs, 'single');
        oc.textAlign = 'center'; oc.textBaseline = 'middle';
        oc.fillText(text, size / 2, cy);
      }
    }

    /* Apply border image (if the style has one) and tint uniformly */
    async function finishMonoLayer() {
      /* UX-Aug20 rev 2: bail early if a newer color rebuild superseded us —
         avoids the heavy toDataURL + trimToContent + Image load chain. */
      const _gen = opts && opts._monoGen;
      if (_gen !== undefined && _monoRebuildGen !== _gen) return;

      const fillColor = color || monoStyle.layout?.defaultColor || '#2a1f18';
      if (monoStyle.borderImage) {
        const borderImg = await loadBorderImage(monoStyle.borderImage);
        if (_gen !== undefined && _monoRebuildGen !== _gen) return;  /* stale after await */
        if (borderImg) {
          applyMonoBorderAndTint(oc, size, size, borderImg, fillColor);
        }
      }

      const b64 = off.toDataURL('image/png');
      const img = new Image();
      img.onload = () => {
        if (_gen !== undefined && _monoRebuildGen !== _gen) return;  /* stale */
        const trimmed = trimToContent(img);
        /* Blank render = the style's font/border wasn't actually ready yet.
           NEVER commit a blank — it wiped the placed monogram and made style
           changes need a second Save. Wait for fonts, redo the whole render
           once; if it is STILL blank, leave the placed layer untouched. */
        if (!trimmed) {
          if (!(opts && opts._retriedBlank)) {
            const opts2 = Object.assign({}, opts, { _retriedBlank: true });
            (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve())
              .then(() => {
                if (_gen !== undefined && _monoRebuildGen !== _gen) return;
                addCustomMonogramLayer(text, monoStyle, color, existing, opts2);
              });
          }
          return;
        }
        const useImg = trimmed.img;
        const useB64 = trimmed.b64;
        const tW = trimmed.w;
        const tH = trimmed.h;
        const tAr = tW / tH;

        whenImgReady(useImg, () => {
        if (_gen !== undefined && _monoRebuildGen !== _gen) return;  /* stale */
        const pa = S.pa;
        const h = Math.min(pa.h * 0.55, pa.w * 0.55);
        const w = h * tAr;
        if (existing) {
          const cx = existing.x + existing.w / 2, cy = existing.y + existing.h / 2;
          existing.img = useImg; existing.b64 = useB64; existing.ar = tAr;
          existing.w = existing.h * tAr; existing.x = cx - existing.w / 2; existing.y = cy - existing.h / 2;
          existing.name = 'Monogram: ' + text;
          existing.isMonogram = true; existing.monoText = text;
          existing.monoStyle = -1; existing.monoColor = color;
          existing.customMonoStyleName = monoStyle.name;
          render();
          if (!(opts && opts.silent)) { pushHistory(); forcePropsRebuild(); }
          updateActionBar();
          return;
        }
        pushLayer({
          id: uid(), type: 'image', name: 'Monogram: ' + text,
          img: useImg, b64: useB64, x: pa.x + (pa.w - w) / 2, y: pa.y + (pa.h - h) / 2,
          w, h, ar: tAr, rotation: 0, flipH: false, flipV: false,
          visible: true, tintColor: null,
          isMonogram: true, monoText: text,
          monoStyle: -1, monoColor: color,
          customMonoStyleName: monoStyle.name,
          isRasterUpload: false,
          _autoColor: !!(opts && opts._autoColor), /* UX-Aug20 */
        });
        });
      };
      img.src = b64;
    }
    finishMonoLayer();
  }

  function addMonogramLayer(text, styleIdx, color, existing, color2, _autoColor) {
    dtAnalytics.layerAdd('monogram', text);
    const style = MONO_STYLES[styleIdx]; if (!style) return;
    /* Wait for fonts to finish loading before rendering. A freshly-picked style's font
       may not be loaded yet, so the FIRST render produced a blank/fallback monogram and
       the change only "took" on a second Save (the click-twice bug). fonts.ready resolves
       instantly when the font is already cached, so this adds no delay in the common case. */
    const run = (isRetry) => {
    const size = 512, off = document.createElement('canvas'); off.width = size; off.height = size;
    const oc = off.getContext('2d'); style.render(oc, text, size, size, color, color2);
    const b64 = off.toDataURL('image/png');
    const img = new Image();
    img.onload = () => {
      /* Trim empty space around the monogram */
      const trimmed = trimToContent(img);
      /* A fully-blank render means a resource (custom font or async border image) wasn't
         ready yet. Instead of wiping the monogram to nothing on the first Save (the
         "click twice" bug), wait for fonts + retry the render once. */
      if (!trimmed && !isRetry) {
        (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(function () { run(true); });
        return;
      }
      /* Still blank after the font retry — keep whatever is placed rather
         than committing an invisible monogram. rev 12: surface a toast so the
         user knows the add didn't silently succeed. */
      if (!trimmed && isRetry) { toast("Couldn’t render that monogram — try selecting the style again."); return; }
      const useImg = trimmed ? trimmed.img : img;
      const useB64 = trimmed ? trimmed.b64 : b64;
      const tW = trimmed ? trimmed.w : 512;
      const tH = trimmed ? trimmed.h : 512;
      const tAr = tW / tH;

      whenImgReady(useImg, () => {
      if (existing) {
        /* Edit in place — keep centre + height, re-fit width, update mono props */
        const cx = existing.x + existing.w / 2, cy = existing.y + existing.h / 2;
        existing.img = useImg; existing.b64 = useB64; existing.ar = tAr;
        existing.w = existing.h * tAr; existing.x = cx - existing.w / 2; existing.y = cy - existing.h / 2;
        existing.name = 'Mono: ' + text;
        existing.isMonogram = true; existing.monoText = text; existing.monoStyle = styleIdx; existing.monoColor = color; existing.monoColor2 = color2;
        existing.customMonoStyleName = undefined;
        render(); pushHistory(); forcePropsRebuild(); updateActionBar();
        return;
      }
      const pa = S.pa, h = Math.min(pa.h*0.55, pa.w*0.55), w = h * tAr;
      pushLayer({
        id: uid(), type: 'image', name: 'Mono: ' + text, img: useImg, b64: useB64,
        x: pa.x + (pa.w-w)/2, y: pa.y + (pa.h-h)/2, w, h, ar: tAr,
        rotation: 0, flipH: false, flipV: false, visible: true,
        tintColor: null, isRasterUpload: false, centered: false,
        isMonogram: true, monoText: text, monoStyle: styleIdx, monoColor: color, monoColor2: color2,
        _autoColor: !!_autoColor, /* UX-Aug20: auto-contrast flag */
      });
      });
    };
    img.src = b64;
    };
    /* Canvas text does NOT trigger @font-face loading, so a freshly-picked style's
       Google font isn't ready on the first render — the monogram came out wrong/blank
       and only "took" on a second Save (the click-twice bug). Register + explicitly
       LOAD the style's font, THEN render. The blank-retry inside run() stays as a
       belt-and-suspenders. */
    var _fm = (style.font || '').match(/'([^']+)'|"([^"]+)"/);
    var _fam = _fm ? (_fm[1] || _fm[2]) : '';
    if (_fam && typeof ensureGoogleFont === 'function') { try { ensureGoogleFont(_fam); } catch (e) {} }
    var _fp = (document.fonts && document.fonts.load && style.font)
      ? Promise.all([
          document.fonts.load('700 64px ' + style.font).catch(function () {}),
          document.fonts.load('400 64px ' + style.font).catch(function () {})
        ])
      : Promise.resolve();
    Promise.resolve(_fp).then(function () { run(false); });
  }

  /* ══════════════════════════════════════════════════════════════════
   * DESIGN TEMPLATES — apply a saved design recipe onto the CURRENT product
   * Reuses restoreDesign (which re-fits to this product's print area and
   * rebuilds text as editable text + SVG as vector). Never switches the
   * product, variant, or decoration method.
   * ══════════════════════════════════════════════════════════════════ */
  async function applyTemplate(design, opts) {
    opts = opts || {};
    /* reject arrays — typeof [] === 'object' would otherwise slip through */
    if (!design || typeof design !== 'object' || Array.isArray(design.sides) ||
        !design.sides || typeof design.sides !== 'object') {
      toast('tpl_load_fail');
      return false;
    }

    /* never mutate the caller's object (matters once templates get cached) */
    design = JSON.parse(JSON.stringify(design));
    /* a template must never change the product, variant, or method */
    delete design.method; delete design.variantId;

    /* Decide which side(s) this template lands on.
       - Single-side template → the side the user is CURRENTLY viewing, so picking
         a template while decorating a second side targets THAT side instead of
         overwriting whatever side the template happened to be authored on.
       - Multi-side template → keep its own mapping (intentionally multi-side),
         unless none of its sides exist on this product, then drop it on the
         current side so it still applies. */
    let tplSides = Object.keys(design.sides);
    const remapToCurrent = () => { const r = {}; r[S.view] = design.sides[tplSides[0]]; design.sides = r; tplSides = Object.keys(design.sides); };
    if (tplSides.length === 1) {
      if (tplSides[0] !== S.view) remapToCurrent();
    } else if (tplSides.length && !tplSides.some(s => CFG.sides.includes(s))) {
      remapToCurrent();
    }

    /* don't clear the canvas for an empty template */
    const totalLayers = tplSides.reduce((n, s) =>
      n + ((design.sides[s] && Array.isArray(design.sides[s].layers)) ? design.sides[s].layers.length : 0), 0);
    if (!totalLayers) { toast('tpl_empty'); return false; }

    /* confirm only if a side this template will overwrite already has artwork
       (the per-side clear below preserves every other side) */
    const targetSides = tplSides.filter(s => CFG.sides.includes(s));
    const overwriting = targetSides.some(s => (S.byView[s] || []).some(l => l.visible !== false));
    if (opts.confirm !== false && overwriting &&
        !(await dtConfirm('It will replace the design on ' + (targetSides.length > 1 ? 'these sides' : 'this side') + '.', { title: 'Apply this template?', okLabel: 'Apply' }))) return false;

    /* substitute any font that's no longer available */
    validateTemplateFonts(design);

    /* UX-Aug20: ensure S.pa reflects the real print area before restoreDesign
       maps saved coordinates. Without this, the very first template application
       (before the ResizeObserver rAF fires) uses the fallback 300×300 PA, so
       every layer lands in the wrong coordinate space and Fit appears broken. */
    syncCanvas();

    /* clear ONLY the sides this template defines, so work on other sides
       (e.g. a back the user already designed) is preserved. */
    targetSides.forEach(s => { S.byView[s] = []; });
    S.sel = null; S.iact = null;
    restoreDesign(design);  /* re-fits to this product's print area; fresh IDs */

    /* TEMPLATE LOCK: everything this template placed acts as one unit. */
    const _lockId = uid();
    const _stampLock = () => targetSides.forEach(s => (S.byView[s] || []).forEach(l => { if (!l._tplLockId) l._tplLockId = _lockId; }));
    _stampLock();
    document.fonts.ready.then(() => {
      _stampLock();
      targetSides.forEach(s => (S.byView[s] || []).forEach(l => { if (l.type === 'text' && l._tplLockId) fitTemplateTextLayer(l); }));
      render();
    });

    updateLayersPanel(); forcePropsRebuild(); updateCartBtn(); render(); syncMobileSelection();
    /* Guided text editing: show the "Make it yours" form right where the gallery
       was. On mobile the drawer is a bottom sheet, so the canvas stays visible
       above it and updates live. Only close the drawer if there's no text. */
    const hasText = showTemplatePersonalize();
    if (isMobile()) {
      if (!hasText && S._closeMobileDrawer) S._closeMobileDrawer();
      requestZoomToPA(50, true);
    }
    /* #7: capture history AFTER async (SVG/monogram) layers settle, so undo/redo
       reflects the full template rather than only its synchronous text layers. */
    setTimeout(function () { _stampLock(); pushHistory(); }, 350);
    toast(hasText ? 'tpl_applied_text' : 'tpl_applied');
    return true;
  }

  function validateTemplateFonts(design) {
    const avail = (typeof getAllFonts === 'function') ? getAllFonts() : [];
    const names = new Set();
    avail.forEach(f => { if (f.fontValue) names.add(f.fontValue); if (f.name) names.add(f.name); });
    /* #8: fall back to a real text font, never a graphic/image-tagged one */
    const textFont = avail.find(f => (typeof isGraphicCategory === 'function') ? !isGraphicCategory(f.category) : true) || avail[0];
    const fallback = (textFont && textFont.fontValue) || 'Montserrat';
    Object.values(design.sides || {}).forEach(side => {
      (side.layers || []).forEach(l => {
        if (l.type !== 'text') return;
        if (!l.font) { l.font = fallback; return; }
        /* Previously this stripped any font not already in the list. Now we
           register it (load the Google family + add it to the picker) so the
           template keeps its intended font AND it's editable. */
        if (!names.has(l.font)) ensureGoogleFont(l.font);
      });
    });
  }

  /* Fetch a template's JSON (lazy — only on tap) and apply it. */
  let _templateLoading = false;
  async function loadAndApplyTemplate(designUrl) {
    /* #9: ignore rapid double-taps that would fire concurrent loads */
    if (_templateLoading) return;
    if (!designUrl) { toast('tpl_no_design'); return; }
    /* #10: confirm BEFORE the network round-trip — no wasted fetch, no
       odd delay between tap and the confirm dialog. */
    if (getDesignedSides().length &&
        !(await dtConfirm('It will replace your current design.', { title: 'Apply this template?', okLabel: 'Apply' }))) return;
    _templateLoading = true;
    toast('tpl_loading', null, 8000);  /* loading feedback on slow networks */
    try {
      const res = await fetch(designUrl, { cache: 'no-cache' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const design = await res.json();
      await applyTemplate(design, { confirm: false });   /* already confirmed above */
    } catch (e) {
      DEBUG && console.warn('[dt] template load failed', e);
      toast('tpl_load_fail');
    } finally {
      _templateLoading = false;
    }
  }

  /* HTML-escape helper for the templates UI (esc elsewhere is scope-local). */
  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ── Templates gallery UI — category chips + filtered library + personalize ── */
  function setupTemplates() {
    const ui = $('dt-templates-ui'); if (!ui) return;
    const items = allTemplateItems();
    if (!items.length) {
      ui.innerHTML = staffBarHtml() +
        '<div class="dt-tpl-empty">' +
          '<div class="dt-tpl-empty__title">No templates yet</div>' +
          '<div class="dt-tpl-empty__sub">Browse graphics to build your own design.</div>' +
          '<button class="dt-btn dt-btn--primary" id="dt-tpl-empty-graphics" type="button">Browse Graphics</button>' +
        '</div>';
      wireStaffSave(ui);
      ui.querySelector('#dt-tpl-empty-graphics')?.addEventListener('click', () => routeToTool('clipart'));
      return;
    }
    /* Categories come from the merged item set ("All" first). */
    var cats = ['All'];
    items.forEach(function (it) { if (cats.indexOf(it.cat) === -1) cats.push(it.cat); });
    S._tplCats = cats;
    if (!S._tplActiveCat || cats.indexOf(S._tplActiveCat) === -1) S._tplActiveCat = 'All';
    renderTemplatesLibrary(ui);
  }

  /* Condense the many source categories into 5 customer-facing buckets.
     Unknown (e.g. metaobject gallery) names are bucketed by keyword so the
     tab row never grows past 5 + All. */
  var TPL_CAT_MAP = {
    'Photographic': 'Photo & Monogram', 'Photo + Monogram': 'Photo & Monogram',
    'Name & Number': 'Names & Numbers', 'Names': 'Names & Numbers',
    'Typographic': 'Typographic',
    'Occasion & Seasonal': 'Occasions', 'Occasion Combos': 'Occasions', 'Keepsake & Sentimental': 'Occasions',
    'Lifestyle & Hobby': 'Lifestyle & Business', 'Corporate & B2B': 'Lifestyle & Business', 'Places & Roots': 'Lifestyle & Business',
  };
  function metaCat(c) {
    if (TPL_CAT_MAP[c]) return TPL_CAT_MAP[c];
    var lc = String(c || '').toLowerCase();
    if (/photo|monogram|image|portrait/.test(lc)) return 'Photo & Monogram';
    if (/name|number|roster|team|monogram/.test(lc)) return 'Names & Numbers';
    if (/occasion|season|holiday|wedding|birth|baby|keepsake|sentiment|anniversary|memor/.test(lc)) return 'Occasions';
    if (/business|corporate|b2b|logo|event|lifestyle|hobby|place|root|state|sport|outdoor/.test(lc)) return 'Lifestyle & Business';
    return 'Typographic';
  }

  /* Unified item list: built-in parametric templates + State Outline +
     metaobject gallery items, each tagged by kind + condensed category. */
  function allTemplateItems() {
    var out = [];
    INLINE_TEMPLATES.forEach(function (t) {
      out.push({ kind: 'inline', cat: metaCat(t.category || 'Templates'), name: t.name, tpl: t });
    });
    if (STATE_NAMES.length) out.push({ kind: 'state', cat: metaCat('Places & Roots'), name: 'State Outline' });
    (CFG.templateGalleries || []).forEach(function (g) {
      (g.items || []).forEach(function (it) {
        out.push({ kind: 'meta', cat: metaCat(g.name || 'Templates'), name: it.name || 'Template', it: it });
      });
    });
    return out;
  }

  /* Staff-only "Save current design as template" affordance (CFG.isStaff). */
  function staffBarHtml() {
    return CFG.isStaff
      ? '<button class="dt-btn dt-tpl-savebtn" id="dt-tpl-save" type="button">+ Save current design as template</button>'
      : '';
  }
  function wireStaffSave(ui) {
    if (!CFG.isStaff) return;
    ui.querySelector('#dt-tpl-save')?.addEventListener('click', saveAsTemplate);
  }

  /* Keep a filter pill visible by scrolling ONLY its own horizontal row. Avoids
     scrollIntoView(), which scrolls every ancestor scroller — including the window
     and the tool modal — and would jerk the underlying page (desktop popover) or
     nudge the modal vertically. Pure horizontal delta on the pill row. */
  function centerPillInRow(chip) {
    const row = chip && chip.closest('.dt-tpl-cats');
    if (!row) return;
    const cr = chip.getBoundingClientRect(), rr = row.getBoundingClientRect();
    const delta = (cr.left + cr.width / 2) - (rr.left + rr.width / 2);
    if (Math.abs(delta) < 1) return;
    if (row.scrollBy) row.scrollBy({ left: delta, behavior: 'smooth' });
    else row.scrollLeft += delta;
  }

  var _lastTplItems = null; /* UX-Aug20: cached for updateCardSwatchBg to re-render visible thumbs on variant switch */
  function renderTemplatesLibrary(ui) {
    var all = allTemplateItems();
    var cats = S._tplCats, active = S._tplActiveCat;
    var q = (S._tplQuery || '').trim().toLowerCase();
    var items = (active === 'All') ? all : all.filter(function (it) { return it.cat === active; });
    _lastTplItems = items; /* UX-Aug20 */
    if (q) items = items.filter(function (it) { return (it.name || '').toLowerCase().indexOf(q) !== -1; });
    ui.innerHTML = staffBarHtml() +
      '<div class="dt-lib-sticky">' +
      '<div class="dt-tpl-searchrow"><input type="search" id="dt-tpl-search" class="dt-input" placeholder="Search templates…" autocomplete="off" value="' + escHtml(S._tplQuery || '') + '"></div>' +
      '<div class="dt-tpl-cats" role="tablist" aria-label="Template categories">' +
        cats.map(function (c) { return '<button class="dt-tpl-cat' + (c === active ? ' is-active' : '') +
          '" role="tab" type="button" aria-selected="' + (c === active) + '" data-cat="' + escHtml(c) + '">' + escHtml(c) + '</button>'; }).join('') +
      '</div></div>' +
      '<div class="dt-tpl-grid" id="dt-tpl-grid">' +
        (items.length
          ? items.map(function (it, i) { return unifiedTemplateCardHtml(it, i); }).join('')
          : '<div class="dt-empty-state">' + (q ? 'No matches — try another word.' : 'No templates in this category') + '</div>') +
      '</div>';
    wireStaffSave(ui);
    var searchInp = ui.querySelector('#dt-tpl-search');
    if (searchInp) {
      searchInp.addEventListener('input', function () {
        S._tplQuery = this.value;
        var pos = this.selectionStart;
        renderTemplatesLibrary(ui);
        var again = ui.querySelector('#dt-tpl-search');
        if (again) { again.focus(); try { again.setSelectionRange(pos, pos); } catch (_) {} }
      });
    }
    ui.querySelectorAll('.dt-tpl-cat').forEach(function (chip) {
      chip.addEventListener('click', function () {
        S._tplActiveCat = chip.dataset.cat;
        renderTemplatesLibrary(ui);
        /* (#41) The full re-render rebuilds the pill row (resetting its horizontal
           scroll) and the grid — re-find the now-active pill and keep it in view, and
           start the grid back at the top. */
        var act = ui.querySelector('.dt-tpl-cat.is-active') || ui.querySelector('.dt-tpl-cat.active');
        if (act) centerPillInRow(act);
        var sc = ui.closest('.dt-tool-modal__content') || ui.closest('#dt-mobile-drawer-content');
        if (sc) sc.scrollTop = 0;
      });
    });
    ui.querySelectorAll('.dt-tpl-card').forEach(function (card) {
      attachTplGhost(card, function () { return items[parseInt(card.dataset.i, 10)]; });
      card.addEventListener('click', function () {
        var it = items[parseInt(card.dataset.i, 10)]; if (!it) return;
        hideTplGhost();
        if (it.kind === 'inline') applyInlineTemplate(it.tpl);
        else if (it.kind === 'state') applyStateTemplate();
        else if (it.kind === 'meta') loadAndApplyTemplate(it.it.designUrl);
      });
    });
    /* Render faithful thumbnails for built-in templates (async, cached). */
    ui.querySelectorAll('.dt-tpl-thumb--tpl').forEach(function (img) {
      var it = items[parseInt(img.dataset.ti, 10)];
      if (it && it.kind === 'inline') renderTemplateThumb(it.tpl).then(function (url) { if (url) img.src = url; });
    });
  }

  /* Render a template to a faithful PNG thumbnail using the SAME canvas text
     drawing (fontStr + fillText) and the SAME layout math as applyInlineTemplate,
     so the card preview matches exactly what lands on the canvas. Cached per
     template + decoration method. Returns a Promise<dataURL>. */
  function renderTemplateThumb(tpl) {
    if (tpl._thumb && tpl._thumbMethod === S.method) return Promise.resolve(tpl._thumb);
    var lines = (tpl.lines || []).filter(function (l) { return l && l.t; });
    var fams = {}; lines.forEach(function (l) { if (l.font) fams[l.font] = 1; });
    var loads = Object.keys(fams).map(function (f) { ensureGoogleFont(f); return document.fonts.load('24px "' + f + '"').catch(function () {}); });
    return Promise.all(loads).then(function () {
      return new Promise(function (resolve) {
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var SZ = 220, pad = SZ * 0.09;
        var cv = document.createElement('canvas'); cv.width = SZ * dpr; cv.height = SZ * dpr;
        var c = cv.getContext('2d'); c.scale(dpr, dpr);
        var pa = { x: pad, y: pad, w: SZ - 2 * pad, h: SZ - 2 * pad };
        var GAP = 6, iconRel = tpl.icon ? 64 : 0, photoRel = tpl.photo ? 120 : 0;
        var textRel = lines.reduce(function (a, l) { return a + (l.size || 16) * ((String(l.t || '').match(/\n/g) || []).length + 1); }, 0);
        var nB = lines.length + (iconRel ? 1 : 0) + (photoRel ? 1 : 0);
        var totalRel = textRel + iconRel + photoRel + GAP * Math.max(0, nB - 1);
        var scale = (pa.h * 0.82) / (totalRel || pa.h);
        var engrave = S.method === 'engrave';
        /* UX-Aug20: adaptive preview colour — on dark products, dark-default text
           renders white so the thumbnail matches what the placed template will look like. */
        var _thumbSeed = engrave ? null : seedTextColor();
        var textItems = lines.map(function (l) {
          var fs = Math.max(4, (l.size || 16) * scale);
          c.font = fontStr({ font: l.font, fontSize: fs });
          var w = c.measureText(l.t).width;
          var rot = l.rotation || 0, isVert = Math.abs(rot) === 90;
          var fitDim = isVert ? pa.h * 0.82 : pa.w * 0.9;
          if (w > fitDim && w > 0) { fs = Math.max(4, fs * fitDim / w); c.font = fontStr({ font: l.font, fontSize: fs }); w = c.measureText(l.t).width; }
          var _tColor = engrave ? '#000000'
            : (_thumbSeed && _thumbSeed.auto && _isDarkDefault(l.color)) ? _thumbSeed.color
            : (l.color || '#2C2825');
          return { t: l.t, font: l.font, fs: fs, color: _tColor, h: isVert ? w : fs, rotation: rot };
        });
        var blocks = [];
        if (tpl.icon && tpl.iconPos !== 'bottom') blocks.push({ type: 'icon', h: iconRel * scale });
        if (tpl.photo) blocks.push({ type: 'photo', h: photoRel * scale });
        textItems.forEach(function (ti) { blocks.push({ type: 'text', h: ti.h, ti: ti }); });
        if (tpl.icon && tpl.iconPos === 'bottom') blocks.push({ type: 'icon', h: iconRel * scale });
        var gap = pa.h * 0.03;
        var totalH = blocks.reduce(function (a, b) { return a + b.h; }, 0) + gap * Math.max(0, blocks.length - 1);
        var cyTop = pa.y + (pa.h - totalH) / 2;
        var iconJobs = [];
        blocks.forEach(function (b) {
          var cy = cyTop + b.h / 2;
          if (b.type === 'text') {
            c.save();
            c.font = fontStr({ font: b.ti.font, fontSize: b.ti.fs });
            c.fillStyle = b.ti.color; c.textAlign = 'center'; c.textBaseline = 'middle';
            if (b.ti.rotation) { c.translate(pa.x + pa.w / 2, cy); c.rotate(b.ti.rotation * Math.PI / 180); c.fillText(b.ti.t, 0, 0); }
            else { c.fillText(b.ti.t, pa.x + pa.w / 2, cy); }
            c.restore();
          } else if (b.type === 'photo') {
            var pw = b.h, cx2 = pa.x + pa.w / 2, cy2 = cy;
            c.save(); c.strokeStyle = '#8A8077'; c.setLineDash([5, 4]); c.lineWidth = 1.5;
            if (tpl.mask === 'circle') { c.beginPath(); c.arc(cx2, cy2, pw / 2, 0, Math.PI * 2); c.stroke(); }
            else if (tpl.mask === 'heart') {
              /* Heart outline (0..100 path centred on the slot) so the thumbnail
                 previews the real crop shape instead of a square. */
              var hs = pw / 100;
              var hbase = new Path2D('M50 86 C18 64 6 44 6 30 C6 18 16 10 27 10 C36 10 44 16 50 26 C56 16 64 10 73 10 C84 10 94 18 94 30 C94 44 82 64 50 86 Z');
              var hpath = new Path2D();
              hpath.addPath(hbase, new DOMMatrix([hs, 0, 0, hs, cx2 - 50 * hs, cy2 - 48 * hs]));
              c.stroke(hpath);
            }
            else { c.strokeRect(cx2 - pw / 2, cy2 - pw / 2, pw, b.h); }
            c.restore();
          } else if (b.type === 'icon') {
            iconJobs.push({ cy: cy, h: b.h });
          }
          cyTop += b.h + gap;
        });
        var finish = function () { tpl._thumb = cv.toDataURL('image/png'); tpl._thumbMethod = S.method; resolve(tpl._thumb); };
        if (!iconJobs.length) { finish(); return; }
        var done = 0;
        iconJobs.forEach(function (job) {
          var blob = new Blob([svgWithDisplaySize(tpl.icon)], { type: 'image/svg+xml' });
          var url = URL.createObjectURL(blob), im = new Image();
          im.onload = function () {
            var ar = (im.naturalWidth || 1) / (im.naturalHeight || 1);
            var h = job.h, w = h * ar;
            try {
              if (engrave) c.filter = 'brightness(0)';   /* #10: engrave previews are BLACK */
              c.drawImage(im, pa.x + pa.w / 2 - w / 2, job.cy - h / 2, w, h);
            } catch (e) {}
            c.filter = 'none';
            URL.revokeObjectURL(url);
            if (++done === iconJobs.length) finish();
          };
          im.onerror = function () { URL.revokeObjectURL(url); if (++done === iconJobs.length) finish(); };
          im.src = url;
        });
      });
    });
  }

  function unifiedTemplateCardHtml(it, i) {
    var inner;
    if (it.kind === 'inline') inner = '<img class="dt-tpl-thumb dt-tpl-thumb--tpl" data-ti="' + i + '" alt=""' + (it.tpl._thumb && it.tpl._thumbMethod === S.method ? ' src="' + it.tpl._thumb + '"' : '') + '>';
    else if (it.kind === 'state') inner = '<span class="dt-tpl-thumb dt-tplprev dt-tplprev--state">' + (STATE_OUTLINES['Texas'] || '') + '</span>';
    else inner = it.it.thumbnail
      ? '<img class="dt-tpl-thumb" src="' + escHtml(it.it.thumbnail) + '" alt="" loading="lazy">'
      : '<div class="dt-tpl-thumb dt-tpl-thumb--blank">' + escHtml((it.name || 'T').slice(0, 1)) + '</div>';
    return '<button class="dt-tpl-card dt-card" data-i="' + i + '" type="button" aria-label="Use template: ' + escHtml(it.name || 'Template') + '">' +
      '<span class="dt-card__media">' + inner + '</span><span class="dt-card__caption dt-tpl-name">' + escHtml(it.name || 'Template') + '</span></button>';
  }

  /* Gather the editable text layers across all sides, in reading order. */
  function collectTemplateTextLayers() {
    const out = [];
    CFG.sides.forEach(side => {
      (S.byView[side] || [])
        .filter(l => l.type === 'text' && l.visible !== false)
        .sort((a, b) => (a.y || 0) - (b.y || 0))
        .forEach(layer => out.push({ side, layer }));
    });
    return out;
  }

  /* Guided "Make it yours" form — one labeled input per text spot, live-updating
     the canvas. Returns false if the template has no text (caller then closes the
     mobile drawer to reveal the design). */
  function showTemplatePersonalize(target) {
    const inProps = !!target;
    const ui = target || $('dt-templates-ui'); if (!ui) return false;
    /* In the properties panel, scope strictly to the SELECTED template's members */
    const selL = inProps ? selLayer() : null;
    const scopeLock = selL && selL._tplLockId ? selL._tplLockId : null;
    const inScope = l => !scopeLock || l._tplLockId === scopeLock;
    const texts = collectTemplateTextLayers().filter(t => inScope(t.layer));
    const engrave = S.method === 'engrave';
    /* Photo slots + state outlines in scope */
    const photos = [];
    const states = [];
    const svgs = [];
    CFG.sides.forEach(side => {
      (S.byView[side] || []).forEach(layer => {
        if (layer.visible === false || !inScope(layer)) return;
        if (layer.isPhotoPlaceholder) photos.push({ side, layer, placeholder: true });
        else if (layer._tplPhoto) photos.push({ side, layer, placeholder: false });
        else if (layer.isStateOutline) states.push({ side, layer });
        /* (#48) Template SVG/graphic members (carry _tplLockId) can be swapped for an
           uploaded image. Standalone clipart (no lock) is excluded. */
        else if (layer.isSvg && !layer.isMonogram && layer._tplLockId) svgs.push({ side, layer });
      });
    });
    if (!texts.length && !photos.length && !states.length && !svgs.length) return false;
    const hexOf = c => (/^#[0-9a-fA-F]{6}$/.test(c || '') ? c : '#111111');
    ui.innerHTML =
      '<div class="dt-tpl-personalize">' +
        '<div class="dt-tpl-personalize__head">Make it yours</div>' +
        '<div class="dt-tpl-personalize__sub">Edit the text' + (engrave ? '' : ' and colors') + (photos.length ? ', and your photo,' : '') + ' below — the design updates live.</div>' +
        texts.map((t, i) =>
          '<label class="dt-tpl-field">' +
            '<span class="dt-tpl-field__cap">' + escHtml(t.layer.text || ('Text ' + (i + 1))) + '</span>' +
            '<span class="dt-tpl-field__row">' +
              ((t.layer.text || '').indexOf('\n') !== -1
                ? '<textarea class="dt-input dt-tpl-field__input" data-ti="' + i + '" rows="' + Math.min(4, (t.layer.text.match(/\n/g) || []).length + 1) + '" maxlength="160">' + escHtml(t.layer.text) + '</textarea>'
                : '<input class="dt-input dt-tpl-field__input" data-ti="' + i + '" type="text" value="' + escHtml(t.layer.text || '') + '" maxlength="80" autocomplete="off">') +
              (engrave ? '' :
              '<span class="dt-tpl-swatch" title="Text color">' +
                '<span class="dt-prop-color-chip" data-tchip="' + i + '" style="background:' + hexOf(t.layer.color) + '"></span>' +
                '<input type="color" class="dt-tpl-field__color" data-ti="' + i + '" value="' + hexOf(t.layer.color) + '" aria-label="Color for this text">' +
              '</span>') +
            '</span>' +
          '</label>').join('') +
        states.map((st, i) =>
          '<div class="dt-tpl-photo">' +
            '<span class="dt-tpl-field__cap">State</span>' +
            '<div class="dt-tpl-photo__btns">' +
              '<button class="dt-btn" data-sti="' + i + '" type="button">Change state · ' + escHtml(st.layer.stateName || 'Pick one') + '</button>' +
            '</div>' +
          '</div>').join('') +
        photos.map((p, i) =>
          '<div class="dt-tpl-photo">' +
            '<span class="dt-tpl-field__cap">Photo</span>' +
            '<div class="dt-tpl-photo__btns">' +
              (p.placeholder
                ? '<button class="dt-btn dt-btn--primary" data-paddi="' + i + '" type="button">Add your photo</button>'
                : '<button class="dt-btn" data-pswapi="' + i + '" type="button">Swap photo</button>' +
                  '<button class="dt-btn" data-prefi="' + i + '" type="button">' + (tplReframing(p.layer) ? 'Done reframing' : 'Reframe / resize') + '</button>') +
            '</div>' +
          '</div>').join('') +
        svgs.map((s, i) =>
          '<div class="dt-tpl-photo">' +
            '<span class="dt-tpl-field__cap">Graphic</span>' +
            '<div class="dt-tpl-photo__btns">' +
              '<button class="dt-btn" data-svgrepl="' + i + '" type="button">Replace with image</button>' +
            '</div>' +
          '</div>').join('') +
        /* Placement (Brandon, Jul 28 2026): updatePropsPanel returns early for a
           locked template, so this form IS the template's whole desktop panel —
           without these it had no Fit and no Center at all (mobile only had Fit,
           on the action bar). Same markup/classes as the standard layer panel's
           placement row, so it inherits .dt-prop-place styling untouched. */
        /* MOBILE: Fit lives ONLY in the action bar (same rule as Delete), so the
           panel row keeps Center alone here — the action bar has no Center. */
        (inProps
          ? '<div class="dt-prop-place">' +
              (isMobile() ? '' : '<button class="dt-prop-place-btn" id="dt-tpl-fit" type="button" title="Fit the whole template to the print area"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3.5" width="12" height="9" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M5 6.5h6M5 9.5h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg><span>Fit to Print Area</span></button>') +
              '<button class="dt-prop-place-btn" id="dt-tpl-center" type="button" title="Center the whole template in the print area"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2.5" width="12" height="11" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M8 5v6M5 8h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg><span>Center</span></button>' +
            '</div>'
          : '') +
        /* MOBILE DELETE RULE (Brandon, Jul 28 2026): no open panel carries a
           delete on mobile — the red Delete in the action bar is the single
           delete for every layer type, and it stays reachable because the props
           drawer stops above the tab bar (measured: drawer 441–696, bar 698–768
           at 390x844). Desktop keeps this button; its sidebar has no other one
           now that the element toolbar hides for templates. */
        (inProps && isMobile()
          ? ''
          : inProps
          ? '<div class="dt-tpl-personalize__actions">' +
              /* No in-panel finish button here: while editing a template the floating
                 Done FAB is present (z-250, above the drawer) and is the single,
                 consistently-labeled finish control. A second "View design" button was
                 a confusing duplicate. The browse (just-applied) variant below keeps its
                 button because there the FAB may be absent (nothing selected yet). */
              '<button class="dt-btn dt-tpl-delete" id="dt-tpl-delete" type="button">Delete template</button>' +
            '</div>'
          : '<div class="dt-tpl-personalize__actions">' +
              (isMobile() ? '<button class="dt-btn dt-btn--primary" id="dt-tpl-done" type="button">View design</button>' : '') +
              '<button class="dt-btn" id="dt-tpl-back" type="button">‹ Browse templates</button>' +
            '</div>') +
      '</div>';
    ui.querySelectorAll('.dt-tpl-field__input').forEach(inp => {
      const t = texts[parseInt(inp.dataset.ti, 10)];
      inp.addEventListener('focus', () => { if (t.side === S.view) { S.sel = t.layer.id; render(); } });
      inp.addEventListener('input', () => {
        t.layer.text = inp.value;
        t.layer.name = (inp.value || 'Text').replace(/\n/g, ' ').slice(0, 25);
        if (t.layer._tplLockId) fitTemplateTextLayer(t.layer);
        else { measureText(t.layer); t.layer.ar = t.layer.w / (t.layer.h || 1); }
        render(); updateLayersPanel(); updateCartBtn();
      });
      inp.addEventListener('change', () => pushHistory());
    });
    ui.querySelectorAll('.dt-tpl-field__color').forEach(cinp => {
      const t = texts[parseInt(cinp.dataset.ti, 10)];
      cinp.addEventListener('input', () => {
        t.layer.color = cinp.value;
        const chip = ui.querySelector('[data-tchip="' + cinp.dataset.ti + '"]');
        if (chip) chip.style.background = cinp.value;
        render();
      });
      cinp.addEventListener('change', () => pushHistory());
    });
    const needSide = (p) => {
      if (p.side === S.view) return true;
      toast('switch_side', { side: CFG.sideConfigs[p.side]?.label || p.side });
      return false;
    };
    ui.querySelectorAll('[data-paddi]').forEach(btn => btn.addEventListener('click', () => {
      const p = photos[parseInt(btn.dataset.paddi, 10)];
      if (!needSide(p)) return;
      replacePhotoPlaceholder(p.layer);
      /* refresh the panel once the picked photo lands (placeholder → photo) */
      const _watch = setInterval(() => {
        if (!(S.byView[p.side] || []).includes(p.layer)) { clearInterval(_watch); try { showTemplatePersonalize(); } catch (_) {} }
      }, 600);
      setTimeout(() => clearInterval(_watch), 60000);
    }));
    ui.querySelectorAll('[data-pswapi]').forEach(btn => btn.addEventListener('click', () => {
      const p = photos[parseInt(btn.dataset.pswapi, 10)];
      if (needSide(p)) swapPhoto(p.layer);
    }));
    ui.querySelectorAll('[data-prefi]').forEach(btn => btn.addEventListener('click', () => {
      const p = photos[parseInt(btn.dataset.prefi, 10)];
      if (!needSide(p)) return;
      S.sel = p.layer.id; render();
      if (typeof S._toggleLink === 'function') S._toggleLink();
      btn.textContent = tplReframing(p.layer) ? 'Done reframing' : 'Reframe / resize';
      if (typeof updateActionBar === 'function') updateActionBar();
      if (isMobile() && tplReframing(p.layer) && S._closeMobileDrawer) S._closeMobileDrawer();
    }));
    ui.querySelectorAll('[data-sti]').forEach(btn => btn.addEventListener('click', () => {
      const st = states[parseInt(btn.dataset.sti, 10)];
      if (st.side !== S.view) { toast('switch_side', { side: CFG.sideConfigs[st.side]?.label || st.side }); return; }
      openStatePicker(st.layer);
    }));
    ui.querySelectorAll('[data-svgrepl]').forEach(btn => btn.addEventListener('click', () => {
      const s = svgs[parseInt(btn.dataset.svgrepl, 10)];
      if (!s) return;
      if (s.side !== S.view) { toast('switch_side', { side: CFG.sideConfigs[s.side]?.label || s.side }); return; }
      replaceTemplateSvgWithImage(s.layer);
      /* Refresh the editor once the SVG is gone (replaced by the image). */
      const _w = setInterval(() => {
        if (!(S.byView[s.side] || []).includes(s.layer)) { clearInterval(_w); try { showTemplatePersonalize(target); } catch (_) {} }
      }, 500);
      setTimeout(() => clearInterval(_w), 60000);
    }));
    ui.querySelector('#dt-tpl-back')?.addEventListener('click', () => setupTemplates());
    ui.querySelector('#dt-tpl-done')?.addEventListener('click', () => { if (S._closeMobileDrawer) S._closeMobileDrawer(); });
    ui.querySelector('#dt-tpl-delete')?.addEventListener('click', () => {
      const m = S.sel || ((S.byView[S.view] || []).find(x => x._tplLockId) || {}).id;
      if (m) confirmDelete(m);
    });
    /* Resolve the template member at CLICK time (the panel outlives a selection
       change), preferring the selection and falling back to any member on this
       side — same rule the Delete handler above uses. */
    const _tplMember = () => {
      const sl = selLayer();
      if (sl && sl._tplLockId) return sl;
      return (S.byView[S.view] || []).find(x => x._tplLockId && (!scopeLock || x._tplLockId === scopeLock)) || null;
    };
    ui.querySelector('#dt-tpl-fit')?.addEventListener('click', () => {
      const m = _tplMember(); if (m) fitTemplateGroup(m);
    });
    ui.querySelector('#dt-tpl-center')?.addEventListener('click', () => {
      const m = _tplMember(); if (m) centerTemplateGroup(m);
    });
    return true;
  }

  /* ── Staff: save the current design as a reusable template ── */
  async function saveAsTemplate() {
    if (!CFG.isStaff) return;
    if (!getDesignedSides().length) { toast('save_tpl_empty'); return; }
    const name = (window.prompt('Name this template (e.g. "Anniversary Cheers"):', '') || '').trim();
    if (!name) return;
    const slug = (name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)) || 'template';
    const templateId = slug + '-' + Math.random().toString(36).slice(2, 6);
    const btn = $('dt-tpl-save');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving\u2026'; }
    try {
      const design = serializeForSave();
      /* templates are product/variant/method-agnostic (server strips too) */
      delete design.method; delete design.variantId;
      await uploadDesignImages(design, 'templates/' + templateId);
      await ensureSideLayerPaintSources(S.view); /* v2.12.3 */
      const thumbnail = templateThumbPng(S.view) || mockupPng(S.view);
      const result = await apiSaveTemplate(templateId, name, design, thumbnail);
      showTemplateSavedResult($('dt-templates-ui'), {
        name,
        designUrl: result.designUrl || '',
        thumbnailUrl: result.thumbnailUrl || '',
      });
    } catch (e) {
      DEBUG && console.warn('[dt] save template failed', e);
      flashToast((e.message || 'Could not save template').slice(0, 40));
      if (btn) { btn.disabled = false; btn.textContent = '+ Save current design as template'; }
    }
  }

  async function apiSaveTemplate(templateId, name, design, thumbnail) {
    const res = await fetch(CFG.designsApiUrl, {
      method: 'POST',
      headers: designApiHeaders(),
      body: JSON.stringify({ mode: 'template', designId: templateId, name, design, thumbnail, email: CFG.customerEmail }),
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || ('Save failed: ' + res.status)); }
    return res.json();
  }

  function showTemplateSavedResult(ui, r) {
    if (!ui) return;
    const field = (cap, val) =>
      '<label class="dt-tpl-field"><span class="dt-tpl-field__cap">' + escHtml(cap) + '</span>' +
      '<input class="dt-input" type="text" readonly value="' + escHtml(val) + '" onclick="this.select()"></label>';
    ui.innerHTML =
      '<div class="dt-tpl-personalize">' +
        '<div class="dt-tpl-personalize__head">Template saved</div>' +
        '<div class="dt-tpl-personalize__sub">Create a <b>Design Template</b> metaobject and paste these in, then add it to a Template Gallery.</div>' +
        field('name', r.name) + field('design_url', r.designUrl) + field('thumbnail_url', r.thumbnailUrl) +
        '<div class="dt-tpl-personalize__actions">' +
          '<button class="dt-btn dt-btn--primary" id="dt-tpl-copyall" type="button">Copy all</button>' +
          '<button class="dt-btn" id="dt-tpl-saved-back" type="button">Done</button>' +
        '</div>' +
      '</div>';
    ui.querySelector('#dt-tpl-copyall')?.addEventListener('click', () => {
      const text = 'name: ' + r.name + '\ndesign_url: ' + r.designUrl + '\nthumbnail_url: ' + r.thumbnailUrl;
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(() => toast('copied'), () => toast('copy_fail'));
      else toast('copy_select');
    });
    ui.querySelector('#dt-tpl-saved-back')?.addEventListener('click', () => setupTemplates());
  }

  /* ══════════════════════════════════════════════════════════════════
   * CLIPART GALLERIES (SVG + PNG)
   * ══════════════════════════════════════════════════════════════════ */
  function setupClipart() {
    const ui = $('dt-clipart-ui'); if (!ui) return;
    const galleries = CFG.clipartGalleries;
    if (!galleries?.length) { ui.innerHTML = `<div class="dt-empty-state">No clipart available</div>`; return; }
    showClipartLibrary(ui, galleries);
  }

  /* "View all" library with category filter pills + search — same pattern as
     the Templates panel, consistent on desktop and mobile. The item-add flow
     (renderClipItem / wireClipItems / showClipPreview) is unchanged. */
  function showClipartLibrary(ui, galleries) {
    const allItems = galleries.flatMap(g => (g.items || []).map(it => ({ ...it, galleryName: g.name })));
    const cats = ['All'].concat(galleries.map(g => g.name || 'Category').filter((v, i, a) => a.indexOf(v) === i));
    if (!S._clipCat || cats.indexOf(S._clipCat) === -1) S._clipCat = 'All';
    S._clipQuery = '';

    ui.innerHTML =
      '<div class="dt-lib-sticky">' +
      '<div style="margin-bottom:10px"><input type="search" id="dt-clip-search" class="dt-input" placeholder="Search clipart\u2026" autocomplete="off"></div>' +
      '<div class="dt-tpl-cats" role="tablist" aria-label="Clipart categories">' +
        cats.map(c => '<button class="dt-tpl-cat' + (c === S._clipCat ? ' is-active' : '') +
          '" role="tab" type="button" aria-selected="' + (c === S._clipCat) + '" data-cat="' + escHtml(c) + '">' + escHtml(c) + '</button>').join('') +
      '</div></div>' +
      '<div class="dt-clipart-grid" id="dt-clip-grid" ' + (isMobile() ? 'style="max-height:none;overflow:visible"' : '') + '></div>';

    const grid = ui.querySelector('#dt-clip-grid');
    const searchInp = ui.querySelector('#dt-clip-search');

    function render() {
      const method = currentDecorationMethod();
      const engraveMode = /engrav/i.test(method); // matches Engrave / Engraving / Engraved
      const q = S._clipQuery.trim().toLowerCase();
      let items;
      if (q) items = allItems.filter(it => (it.label || '').toLowerCase().includes(q) || (it.galleryName || '').toLowerCase().includes(q) || (it.keywords || '').toLowerCase().includes(q));
      else if (S._clipCat === 'All') items = allItems;
      else items = allItems.filter(it => (it.galleryName || 'Category') === S._clipCat);
      /* Method-aware visibility:
         - Engrave (any active method whose name contains "engrave"): show ALL clipart.
           Laser engraving renders any artwork as monochrome, so nothing is incompatible.
           This also avoids hiding the ~half of items whose metaobject
           compatible_decoration_methods omit "Laser Engrave".
         - Print methods: show items with no methods list (legacy/unfielded) or whose
           methods include the exact current print method. */
      if (engraveMode) {
        /* Engrave is single-colour; a fixed-colour (non-recolorable) graphic can't be
           reduced for engraving, so hide those from the engrave library entirely. */
        items = items.filter(it => it.recolorable !== false);
      } else {
        items = items.filter(it => !(it.methods && it.methods.length) || it.methods.indexOf(method) !== -1);
      }
      grid.innerHTML = items.length
        ? items.map((it, i) => renderClipItem(it, i)).join('')
        : '<div class="dt-empty-state">No clipart found</div>';
      wireClipItems(grid, items);
    }

    /* Scroll the filtered grid back to the top + keep the chosen pill visible.
       (#41) Switching a filter should never leave you mid-scroll or with the active
       pill scrolled off the right edge of its row. */
    function resetClipScroll(activeChip) {
      if (grid) grid.scrollTop = 0;
      const sc = ui.closest('.dt-tool-modal__content') || ui.closest('#dt-mobile-drawer-content');
      if (sc) sc.scrollTop = 0;
      if (activeChip) centerPillInRow(activeChip);
    }
    ui.querySelectorAll('.dt-tpl-cat').forEach(chip => chip.addEventListener('click', () => {
      S._clipCat = chip.dataset.cat; S._clipQuery = '';
      dtAnalytics.clipartBrowse(chip.dataset.cat);
      if (searchInp) searchInp.value = '';
      ui.querySelectorAll('.dt-tpl-cat').forEach(c => {
        const on = c.dataset.cat === S._clipCat;
        c.classList.toggle('is-active', on); c.setAttribute('aria-selected', on);
      });
      render();
      resetClipScroll(chip);
    }));
    searchInp?.addEventListener('input', function () { S._clipQuery = this.value; render(); resetClipScroll(null); });

    render();
  }

  function renderClipItem(it, i) {
    /* Escape the label once — it lands in title= and alt= (a label with a quote
       would otherwise break the attribute / inject markup). */
    const lbl = escHtml(it.label || '');
    const name = '<div class="dt-card__caption dt-clip-name">' + lbl + '</div>';
    /* rev 6: the tile IS the card (.dt-card alias on .dt-clip-item — click/selected handlers unchanged);
       badge is a real element (announced), caption slot always present (hidden by --nocaption). */
    const open = (extra) => `<div class="dt-clip-item dt-card dt-card--nocaption${extra}" data-i="${i}" title="${lbl}" role="button" tabindex="0" aria-label="${lbl || 'Graphic'}">` + CARD_CHECK;
    /* Inline SVG markup (legacy text field). */
    if (it.svg) return open(' dt-clip-item--recolor') + CARD_BADGE + `<div class="dt-card__media">${it.svg}</div>${name}</div>`;
    /* SVG stored as a FILE: render it through <img> (no crossorigin — display only);
       the editable markup is fetched on add. `--svgimg` keeps it out of the engrave
       black-tint (which targets raster PNG thumbnails); `--recolor` shows the badge.
       onerror hides a broken/404 file rather than showing a broken-image glyph. */
    if (it.svgUrl) {
      const rc = it.recolorable !== false ? ' dt-clip-item--recolor' : '';
      return open(' dt-clip-item--svgimg' + rc) + (rc ? CARD_BADGE : '') + `<div class="dt-card__media"><img class="is-vector" src="${it.svgUrl}" alt="${lbl}" loading="lazy" decoding="async" onerror="this.style.display='none'" style="width:100%;height:100%;object-fit:contain"></div>${name}</div>`;
    }
    if (it.image) return open('') + `<div class="dt-card__media"><img src="${it.image}" alt="${lbl}" loading="lazy" decoding="async" crossorigin="anonymous" onerror="this.style.display='none'" style="width:100%;height:100%;object-fit:contain"></div>${name}</div>`;
    return '';
  }
  /* The effective decoration method for the current side: engrave -> "Laser Engrave",
     otherwise the side's configured print method. (Same resolution the cart uses.) */
  function currentDecorationMethod() {
    if (S.method === 'engrave') return 'Laser Engrave';
    return (CFG.sideConfigs[S.view] && CFG.sideConfigs[S.view].decorationMethod) || CFG.printMethodName || 'Cylindrical Print';
  }
  /* Which SVG file to load for a clip item: single-color methods (Laser Engrave,
     Vinyl Print) prefer the 1-bit single_color_svg when present, else fall back to
     the color svg (the picker method-filter ensures a single-color path exists). */
  function clipSvgUrlFor(it) {
    if (!it) return null;
    const m = currentDecorationMethod();
    if ((m === 'Laser Engrave' || m === 'Vinyl Print') && it.singleColorSvgUrl) return it.singleColorSvgUrl;
    return it.svgUrl || null;
  }
  /* Resolve a clipart item to SVG markup: inline if present, else fetch the right file
     (method-aware via clipSvgUrlFor) and sanitize. Caches per URL on the item so repeat
     adds — and the two methods' variants — don't re-fetch. An 8s timeout (AbortController)
     keeps a hung request from leaving the add silently stuck. cb(markup) — or cb(null) on
     any failure / non-SVG response (callers toast and abort, never half-add). */
  function resolveClipSvg(it, cb) {
    if (it && it.svg) { cb(it.svg); return; }
    const url = clipSvgUrlFor(it);
    if (url) {
      it._svgCache = it._svgCache || {};
      if (it._svgCache[url]) { cb(it._svgCache[url]); return; }
      const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      const to = setTimeout(() => { try { ctrl && ctrl.abort(); } catch (_) {} }, 8000);
      fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
        .then(r => { if (!r.ok) throw 0; return r.text(); })
        .then(txt => {
          clearTimeout(to);
          const mk = (txt && txt.indexOf('<svg') !== -1) ? (sanitizeSvg(txt) || txt) : null;
          if (mk) it._svgCache[url] = mk;
          cb(mk);
        })
        .catch(() => { clearTimeout(to); cb(null); });
      return;
    }
    cb(null);
  }

  /* ── Clipart swap: "Swap" on a graphic goes BACK to the library and the
     next pick REPLACES the selected graphic in place (box-fit, keeps tint). */
  function isClipartLayer(l) {
    if (!l || l.isBackdrop || l.isMonogram || l.isPhotoPlaceholder || l._tplLockId) return false;
    if (l.isSvg) return true;
    return l.type === 'image' && !l.isRasterUpload && !l._tplPhoto && !l._tplMask;
  }
  function startClipartSwap(l) {
    if (!l) return;
    S._clipSwapTarget = { id: l.id, side: S.view, t: Date.now() };
    routeToTool('clipart');
    toast('clip_swap_pick');
  }
  function clipSwapTargetLayer() {
    const t = S._clipSwapTarget;
    if (!t || Date.now() - t.t > 180000) { S._clipSwapTarget = null; return null; }
    return (S.byView[t.side] || []).find(x => x.id === t.id) || null;
  }
  function swapClipartLayer(tgt, it) {
    const box = bbox(tgt);
    const finish = nl => {
      if (!nl) return;
      const ar = nl.ar || (nl.w / (nl.h || 1)) || 1;
      let w = box.w, h = w / ar;
      if (h > box.h) { h = box.h; w = h * ar; }
      nl.centered = false; nl.w = w; nl.h = h;
      nl.x = box.x + (box.w - w) / 2; nl.y = box.y + (box.h - h) / 2;
      if (tgt.tintColor) nl.tintColor = tgt.tintColor;
      const arr = layers(); const i = arr.indexOf(tgt); if (i >= 0) arr.splice(i, 1);
      S.sel = nl.id;
      render(); updateLayersPanel(); forcePropsRebuild(); updateActionBar(); pushHistory();
      toast('graphic_swapped');
    };
    if (it.svg || it.svgUrl || it.singleColorSvgUrl) {
      resolveClipSvg(it, markup => {
        if (!markup) { toast('graphic_load_fail'); return; }
        const tag = 'clipswap-' + uid();
        addSvgLayer(markup, it.label, { _swapTag: tag, recolorable: it.recolorable });
        let tries = 0;
        const poll = () => {
          const nl = layers().find(x => x._swapTag === tag);
          if (nl) { delete nl._swapTag; finish(nl); return; }
          if (++tries < 80) setTimeout(poll, 25);
        };
        poll();
      });
    } else if (it.image) {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        addImageLayer(img, it.label, it.image, true);
        finish(layers()[layers().length - 1]);
      };
      img.src = it.image;
    }
  }

  /* Desktop: close the left tool panel (incl. the wide Graphics drawer) so the
     Properties overlay — revealed by pushLayer's forcePropsRebuild once the new
     layer is selected — is actually visible, matching the add-text flow. The
     drawer floats OVER the canvas (#49), so unlike the in-slot panels the props
     overlay can't simply cover it. Mirrors the click-outside close at setup. */
  function closeDesktopToolPanel() {
    if (isMobile()) return;
    $$('.dt-sidebar__tab').forEach(b => b.classList.remove('active'));
    $$('.dt-panel').forEach(p => p.classList.remove('active'));
    document.querySelector('.dt-sidebar__panels')?.classList.remove('has-active-panel');
    const sb = document.querySelector('.dt-sidebar--left');
    if (sb) { sb.classList.remove('has-active-tab'); sb.classList.remove('dt-clipart-open'); }
  }

  function wireClipItems(grid, items) {
    if (!grid) return;
    /* #3 (mobile batch): select-then-commit via a sticky dock — the monogram
       pattern. The confirm modal added a step to every single add. */
    const ui = grid.closest('#dt-clipart-ui') || grid.parentElement;
    let dock = ui.querySelector('#dt-clip-add');
    if (!dock) {
      dock = document.createElement('button');
      dock.id = 'dt-clip-add'; dock.type = 'button';
      dock.className = 'dt-btn dt-btn--primary dt-btn--full';
      ui.appendChild(dock);
      dock.addEventListener('click', () => {
        const it = dock._item; if (!it) return;
        dtAnalytics.clipartAdd(it.label, it.galleryName || S._clipCat || '');
        const swapTgt = clipSwapTargetLayer();
        if (swapTgt) { S._clipSwapTarget = null; swapClipartLayer(swapTgt, it); }
        else if (it.svg || it.svgUrl || it.singleColorSvgUrl) {
          resolveClipSvg(it, markup => {
            if (!markup) { toast('graphic_load_fail'); return; }
            if (S.method === 'engrave') {
              /* engrave is single-color: previews stay full color in the library,
                 and the choice of what engraves happens HERE, at add time */
              const tag = 'engprompt-' + uid();
              addSvgLayer(markup, it.label, { _swapTag: tag, recolorable: it.recolorable });
              let tries = 0;
              const poll = () => {
                const nl = layers().find(x => x._swapTag === tag);
                if (nl) {
                  delete nl._swapTag;
                  const cols = parseSvgColors(nl.svgMarkup || '');
                  if (cols.length > 1) {
                    toast('engrave_one_color');
                    setTimeout(() => openSvgEngraveEditor(nl), 350);
                  }
                  return;
                }
                if (++tries < 80) setTimeout(poll, 25);
              };
              poll();
            } else addSvgLayer(markup, it.label, { recolorable: it.recolorable });
          });
        }
        else if (it.image) { const img = new Image(); img.crossOrigin = 'anonymous'; img.onload = () => addImageLayer(img, it.label, it.image, true); img.src = it.image; }
        dock._item = null; dock.disabled = true;
        grid.querySelectorAll('.dt-clip-item.selected').forEach(x => x.classList.remove('selected'));
        /* Reveal the Properties panel for the incoming layer — same as add-text.
           Closing here (not in the async add callbacks) also covers Cancel/Escape
           out of the engrave color chooser: the layer is already on canvas. */
        closeDesktopToolPanel();
      });
    }
    dock._item = null; dock.disabled = true;
    dock.textContent = clipSwapTargetLayer() ? 'Replace Graphic' : 'Add to Design';
    grid.querySelectorAll('.dt-clip-item').forEach(el => el.addEventListener('click', () => {
      const it = items[parseInt(el.dataset.i)]; if (!it) return;
      grid.querySelectorAll('.dt-clip-item.selected').forEach(x => x.classList.remove('selected'));
      el.classList.add('selected');
      dock._item = it; dock.disabled = false;
      dock.textContent = clipSwapTargetLayer() ? 'Replace Graphic' : 'Add to Design';
      nudgeStickyDock(dock);
    }));
  }

  /* rev 11 (Brandon, Aug 19 — iOS "Add to Design only shows at the bottom of the panel"):
     the dock is display:none while disabled and becomes a position:sticky block on the first
     tap. WebKit does not compute a freshly-displayed sticky box's stuck offset until the
     scroller moves, so give the scroller a 1px nudge on the next frame. No-op off-mobile and
     when the panel is too short to scroll (the dock is then simply in view). The companion
     CSS fix (the card's tabindex focus was flipping the dock to position:static via
     :focus-within) lives in design-tool.css. */
  function nudgeStickyDock(dock) {
    if (!dock || !isMobile()) return;
    requestAnimationFrame(() => {
      const sc = dock.closest('.dt-tool-modal__content') || dock.closest('#dt-mobile-drawer-content') || dock.closest('.dt-rbg-scroll');
      if (!sc) return;
      const t = sc.scrollTop;
      sc.scrollTop = t + 1; sc.scrollTop = t;
    });
  }

  function showClipPreview(it) {
    const ov = document.createElement('div');
    ov.className = 'dt-confirm-overlay';
    const content = it.svg
      ? '<div class="dt-clip-preview__img">' + it.svg + '</div>'
      : '<div class="dt-clip-preview__img"><img src="' + (it.svgUrl || it.image) + '" alt="' + (it.label||'') + '" style="max-width:100%;max-height:100%;object-fit:contain"></div>';
    ov.innerHTML = '<div class="dt-clip-preview">' +
      content +
      '<div class="dt-clip-preview__name">' + (it.label || 'Clipart') + '</div>' +
      '<div class="dt-confirm-dialog__btns">' +
      '<button class="dt-confirm-dialog__btn dt-confirm-dialog__btn--cancel">Cancel</button>' +
      '<button class="dt-confirm-dialog__btn" style="background:var(--dt-forest);color:white">' + (clipSwapTargetLayer() ? 'Replace Graphic' : 'Add to Design') + '</button>' +
      '</div></div>';
    ov.querySelector('.dt-confirm-dialog__btn--cancel').addEventListener('click', () => ov.remove());
    ov.querySelector('.dt-confirm-dialog__btn:last-child').addEventListener('click', () => {
      ov.remove();
      const swapTgt = clipSwapTargetLayer();
      if (swapTgt) { S._clipSwapTarget = null; swapClipartLayer(swapTgt, it); return; }
      if (it.svg || it.svgUrl || it.singleColorSvgUrl) resolveClipSvg(it, m => { if (m) addSvgLayer(m, it.label); else toast('graphic_load_fail'); });
      else if (it.image) { const img = new Image(); img.crossOrigin = 'anonymous'; img.onload = () => addImageLayer(img, it.label, it.image, true); img.src = it.image; }
    });
    ov.addEventListener('click', (e) => { if (e.target === ov) ov.remove(); });
    document.querySelector('.dt-body')?.appendChild(ov);
  }

  /* ══════════════════════════════════════════════════════════════════
   * UNDO / REDO
   * ══════════════════════════════════════════════════════════════════ */
  function serializeLayers(ls) {
    return JSON.stringify(ls.map(l => {
      /* File/Image objects can't survive JSON round-trip. _rcTrim must be
         stripped WITH _originalImg/_originalB64 — the trio describes one
         consistent source frame; restoring one without the others lets a
         later Remove Color re-pick remap geometry across mismatched frames
         (v2.9.11 review finding F1). */
      /* v2.11.0: _invertOrigB64 joins the strip set — it would otherwise ride
         every one of the 50 undo snapshots as a second full image payload.
         Un-invert after undo degrades to the involution recompute. _invGates
         is a derived cache. */
      const { _sourceFile, _originalImg, _originalB64, _rcTrim, _invertOrigB64, _invGates, ...rest } = l;
      /* v2.12.3: never persist a blob: URL as _imgSrc — blob URLs are revoked
         after the display raster loads, so a snapshot holding one reloads as
         onerror → _imgFailed → red "Image unavailable" painted into captures.
         SVG layers whose raster is missing (or blob-backed) snapshot with
         _imgSrc:null and restoreHistory rebuilds them from svgMarkup (the
         CB202608051R5U backdrop path: placeBackdrop pushHistory'd before the
         async raster landed, so the placement snapshot held _imgSrc:null and
         undo produced a layer no img-driven render could paint). */
      const _src = l.img && l.img.src && !/^blob:/i.test(l.img.src) ? l.img.src : null;
      return { ...rest, img: l.img ? '[img]' : null, _imgSrc: _src || l.b64 || null };
    }));
  }

  function pushHistory() {
    try { if (!isMobile() && S.sel) { const _m = $('dt-ctx-meta'); if (_m) _m.textContent = ctxMetaText(selLayer()); } } catch (_) {} /* UX-Aug15 #3 */
    const h = S.history[S.view], snap = serializeLayers(layers());
    h.stack = h.stack.slice(0, h.index + 1); h.stack.push(snap);
    if (h.stack.length > MAX_UNDO) h.stack.shift();
    h.index = h.stack.length - 1; updateHistoryBtns();
    markSerialDirty();
    autosave();
  }

  /* ── localStorage autosave ────────────────────────── */
  function autosave() {
    try {
      const design = serializeDesign();
      /* Only include layer structure — strip b64 image data for non-logged-in users */
      const data = {
        version: 2,
        productHandle: CFG.productHandle,
        variantId: S.variantId,
        method: S.method,
        savedAt: Date.now(),
        design: design,
      };
      const str = JSON.stringify(data);
      if (str.length > 4 * 1024 * 1024) return; /* > 4MB — skip, too large for localStorage */
      /* CRITICAL: never let an EMPTY design overwrite a real draft. Init's
         history seeding autosaves before checkAutosaveDraft runs — without
         this guard it wiped every returning visitor's draft, so the restore
         banner / auto-restore could never fire. */
      const _hasContent = Object.values(S.byView).some(a => a && a.length);
      if (!_hasContent && localStorage.getItem('dt_draft_' + CFG.productHandle)) return;
      localStorage.setItem('dt_draft_' + CFG.productHandle, str);
      /* CRO #17 — first-ever autosave OF REAL WORK: tell the user it's kept.
         (init() autosaves an empty design — don't burn the notice on that) */
      if (!localStorage.getItem('dt_autosave_noticed') &&
          Object.values(S.byView).some(a => a && a.length)) {
        localStorage.setItem('dt_autosave_noticed', '1');
        toast('autosaved');
      }
    } catch(e) { /* QuotaExceeded or private mode — silently fail */ }
  }

  function clearAutosave() {
    try { localStorage.removeItem('dt_draft_' + CFG.productHandle); } catch(e) {}
  }

  function checkAutosaveDraft() {
    try {
      const raw = localStorage.getItem('dt_draft_' + CFG.productHandle);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data?.design?.sides) return;

      const hasContent = Object.values(data.design.sides).some(s => s.layers?.length > 0);
      if (!hasContent) { clearAutosave(); return; }

      const age = Date.now() - data.savedAt;

      /* Auto-restore without banner in three cases:
         1. Draft < 2 min old — clearly a page refresh / back-nav, any user
         2. Draft < 5 min old and customer is logged in (just created account / logged in)
         3. We just consumed a login redirect (dt_return_url was set and cleared by the redirect snippet) */
      const justRedirected = sessionStorage.getItem('dt_just_redirected');
      if (justRedirected) sessionStorage.removeItem('dt_just_redirected');

      if (age < 120000 || (age < 300000 && CFG.customerLoggedIn) || justRedirected) {
        whenCanvasReady(() => performRestore(data));
        return;
      }

      const mins = Math.round(age / 60000);
      const hours = Math.round(age / 3600000);
      const ageStr = mins < 60 ? `${mins} min ago` : hours < 24 ? `${hours}h ago` : `${Math.round(hours/24)}d ago`;
      showRestoreBanner(ageStr, data);
    } catch(e) { clearAutosave(); }
  }

  /* Re-activate the Start panel after clearing a draft, so the user lands on
     the familiar launchpad instead of a blank canvas with background bleed. */
  function activateStartPanel() {
    if (isMobile()) return; /* mobile uses the tab bar, not the panel */
    const startTab = document.querySelector('.dt-sidebar__tab[data-panel="start"]');
    const startPanel = document.getElementById('dt-panel-start');
    if (!startTab || !startPanel) return;
    $$('.dt-sidebar__tab').forEach(b => b.classList.remove('active'));
    $$('.dt-panel').forEach(p => p.classList.remove('active'));
    startTab.classList.add('active');
    startPanel.classList.add('active');
    document.querySelector('.dt-sidebar__panels')?.classList.add('has-active-panel');
    document.querySelector('.dt-sidebar--left')?.classList.add('has-active-tab');
    renderStartStep(0);
  }

  function showRestoreBanner(ageStr, data) {
    const existing = document.querySelector('.dt-restore-banner');
    if (existing) existing.remove();

    const banner = document.createElement('div');
    banner.className = 'dt-restore-banner';
    banner.innerHTML = `
      <span class="dt-restore-banner__icon">•</span>
      <span class="dt-restore-banner__text">You have an unfinished design from ${ageStr}</span>
      <button class="dt-restore-banner__btn dt-restore-banner__btn--primary" id="dt-restore-continue">Continue</button>
      <button class="dt-restore-banner__btn" id="dt-restore-dismiss">Start Fresh</button>`;

    const main = $('dt-main');
    if (main) main.parentElement.insertBefore(banner, main);
    else document.querySelector('.dt-header')?.insertAdjacentElement('afterend', banner);

    banner.querySelector('#dt-restore-continue')?.addEventListener('click', () => {
      whenCanvasReady(() => performRestore(data));
      banner.remove();
    });
    banner.querySelector('#dt-restore-dismiss')?.addEventListener('click', () => {
      clearAutosave(); banner.remove();
      /* Re-activate the Start panel so the user isn't left staring at a blank canvas */
      activateStartPanel();
    });

    /* Auto-dismiss: once the user starts designing (any layer added or panel
       opened), the banner is stale. Check periodically and remove it. */
    let _bannerCheck = setInterval(() => {
      if (!document.contains(banner)) { clearInterval(_bannerCheck); return; }
      const hasWork = Object.values(S.byView).some(a => a && a.length > 0);
      if (hasWork) { banner.remove(); clearInterval(_bannerCheck); }
    }, 2000);
    /* Hard timeout: dismiss after 30s regardless */
    setTimeout(() => { if (document.contains(banner)) banner.remove(); clearInterval(_bannerCheck); }, 30000);
  }

  /* Restoring maps saved coords against the CURRENT print area — if the
     mockup hasn't loaded/synced yet (slow connection, hidden tab stalling
     the sizing RAF), S.pa is degenerate and every layer lands off-canvas.
     Wait for a real canvas before restoring; give up after ~30s. */
  function whenCanvasReady(cb, tries) {
    const ready = mImg && mImg.complete && mImg.naturalWidth > 0 &&
      mImg.offsetWidth > 0 && S.pa && S.pa.w > 10 && canvas && canvas.width > 320;
    if (ready || (tries || 0) > 120) { cb(); return; }
    setTimeout(() => whenCanvasReady(cb, (tries || 0) + 1), 250);
  }

  function performRestore(data) {
    /* #6/#7: a returning draft means this isn't a blank start — leave the Start
       panel and suppress the first-visit launcher so neither covers the design. */
    document.getElementById('dt-welcome-overlay')?.remove();
    S._suppressWelcome = true;   /* layers restore async — the 800ms overlay must not race them */
    if (!isMobile()) {
      const startTab = document.querySelector('.dt-sidebar__tab[data-panel="start"]');
      const startPanel = document.getElementById('dt-panel-start');
      if (startTab) startTab.classList.remove('active');
      if (startPanel) startPanel.classList.remove('active');
      document.querySelector('.dt-sidebar__panels')?.classList.remove('has-active-panel');
      document.querySelector('.dt-sidebar--left')?.classList.remove('has-active-tab');
    }
    /* Restore decoration method + sync toggle buttons */
    if (data.method) {
      S.method = data.method;
      if (app) app.dataset.method = data.method;
      try { reseedAutoTextColors('restore-method'); } catch (_) {} /* UX-Aug15 #1 (final review B11) */
      /* Sync active button(s) — covers desktop + mobile (same data-method) */
      $$('.dt-method-btn').forEach(b => b.classList.toggle('active', b.dataset.method === data.method));
      /* Sync wrapper data-pos so the sliding thumb lands on the right side */
      $$('.dt-method').forEach(w => { w.dataset.pos = (data.method === 'print') ? '1' : '0'; });
      /* v2.10.1: ALSO swap the upload panels — restore synced the toggle but
         left the PRINT upload zone visible for a restored engrave design.
         Result: S.method === 'engrave' (production ran the engrave recipe)
         while uploads ran handleFile with the remove-bg picker instead of
         handleEngraveUpload — the reported "engrave uploads prompt like
         print", and the door that let un-stripped paper reach the trace.
         Mirrors applyMethod's panel toggle exactly. */
      const _rStd = $('dt-upload-standard'), _rEng = $('dt-upload-engrave');
      if (_rStd && _rEng) {
        _rStd.style.display = data.method === 'engrave' ? 'none' : '';
        _rEng.style.display = data.method === 'engrave' ? '' : 'none';
      }
    }

    /* Restore variant if it differs — switch color swatch */
    if (data.variantId && data.variantId !== S.variantId) {
      const swatch = document.querySelector(`.dt-color-swatch-btn[data-variant-id="${data.variantId}"]:not([hidden])`); /* rev 5: never restore onto a sold-out colour */
      if (swatch) {
        swatch.click(); /* triggers variant switch + mockup load */
        /* Wait for the mockup image to actually load before restoring layers,
           so syncCanvas/computePA have the correct rscale. */
        /* UX-Aug15 W0: the 3s fallback used to fire even after the load listener had
           already restored → duplicated layers when the mockup loaded in <3s. One-shot guard. */
        let _restoreFallbackT = null, _restored = false;
        const _restoreOnce = () => { if (_restored) return; _restored = true; if (_restoreFallbackT) clearTimeout(_restoreFallbackT); restoreDesign(data.design); };
        const _restoreOnLoad = () => {
          mImg.removeEventListener('load', _restoreOnLoad);
          _restoreOnce();
        };
        if (mImg.complete && mImg.naturalWidth) {
          _restoreOnce();
        } else {
          mImg.addEventListener('load', _restoreOnLoad, { once: true });
          /* Safety fallback — if the image load never fires (e.g. cached/404) */
          _restoreFallbackT = setTimeout(() => { mImg.removeEventListener('load', _restoreOnLoad); _restoreOnce(); }, 3000);
        }
        return;
      }
      /* Variant not found (removed from product) — continue with current variant */
    }

    restoreDesign(data.design);
  }

  /* Backgrounds: async image loads can land a backdrop AFTER its photo —
     re-seat each backdrop directly behind its group photo, then let
     syncGroupedBackdrops re-capture the link (its legacy/first-sync path).
     Shared by the autosave restore AND the saved-designs loader. */
  function settleRestoredGroups() {
    CFG.sides.forEach(s2 => {
      const arr2 = S.byView[s2]; if (!arr2 || !arr2.length) return;
      arr2.filter(b => b.isBackdrop && b._groupId).forEach(bd => {
        const pi = arr2.findIndex(l2 => !l2.isBackdrop && l2._groupId === bd._groupId);
        const bi = arr2.indexOf(bd);
        if (pi >= 0 && bi > pi) { arr2.splice(bi, 1); arr2.splice(pi, 0, bd); }
      });
      syncGroupedBackdrops(s2);
    });
    render();
  }
  function scheduleGroupSettle() {
    setTimeout(settleRestoredGroups, 600);
    setTimeout(settleRestoredGroups, 2200);
  }

  function restoreDesign(design) {
    if (!design?.sides) return;
    /* Make sure any Google fonts this design uses are loaded + editable (covers
       templates and saved designs alike). Idempotent. */
    registerDesignFonts(design);
    CFG.sides.forEach(slug => {
      const sideData = design.sides[slug];
      if (!sideData?.layers?.length) return;

      /* Map saved positions to current print area.
         Even on the same product, S.rscale may differ (different viewport size). */
      const curPA = S.pa;
      let savedPA = sideData.renderedPrintArea;
      if (!savedPA) savedPA = curPA; /* same product, assume same PA */

      const fitScale = Math.min(curPA.w / (savedPA.w || curPA.w), curPA.h / (savedPA.h || curPA.h), 2.5);
      const savedCX = savedPA.x + savedPA.w / 2;
      const savedCY = savedPA.y + savedPA.h / 2;
      const curCX = curPA.x + curPA.w / 2;
      const curCY = curPA.y + curPA.h / 2;
      const mapX = x => curCX + (x - savedCX) * fitScale;
      const mapY = y => curCY + (y - savedCY) * fitScale;

      sideData.layers.forEach(ld => {
        if (ld.type === 'text') {
          const fs = Math.max(8, Math.round((ld.fontSize || 36) * fitScale));
          const l = {
            id: uid(), type: 'text', name: ld.name || ld.text?.slice(0, 20),
            text: ld.text || '', font: ld.font || S.textFont || 'Montserrat', fontSize: fs,
            color: guardRestoredTextColor(ld.color || '#111111').color, _autoColor: guardRestoredTextColor(ld.color || '#111111').auto,
            bold: ld.bold || false, italic: ld.italic || false,
            lineHeight: ld.lineHeight || null,
            x: mapX(ld.x), y: mapY(ld.y), w: (ld.w || 100) * fitScale, h: fs,
            rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
            visible: true, centered: ld.centered ?? true, ar: null, isRasterUpload: false,
            _bulkField: ld._bulkField || false,
          };
          measureText(l); l.ar = l.w / (l.h || 1);
          S.byView[slug].push(l);
        } else if (ld.isMonogram) {
          /* Restore monogram with mapped position */
          const style = MONO_STYLES[ld.monoStyle];
          if (style) {
            const size = 512;
            const off = document.createElement('canvas'); off.width = size; off.height = size;
            const oc2 = off.getContext('2d');
            try { style.render(oc2, ld.monoText, size, size, ld.monoColor); } catch(_) {}
            const b64 = off.toDataURL('image/png');
            const img = new Image();
            img.onload = () => {
              const w = (ld.w || 80) * fitScale, h = (ld.h || 80) * fitScale;
              S.byView[slug].push({
                id: uid(), type: 'image', name: 'Monogram: ' + ld.monoText,
                img, b64, x: mapX(ld.x || savedCX), y: mapY(ld.y || savedCY),
                w, h, ar: 1,
                rotation: ld.rotation || 0, flipH: false, flipV: false,
                visible: true, centered: true,
                tintColor: null, isMonogram: true,
                monoText: ld.monoText, monoStyle: ld.monoStyle, monoColor: ld.monoColor, monoColor2: ld.monoColor2,
                isRasterUpload: false,
              });
              updateLayersPanel(); render();
            };
            img.src = b64;
          }
        }
        /* SVG clipart layers */
        else if (ld.isSvg && ld.svgMarkup) {
          const markup = ld.svgMarkup;
          const displayMarkup = svgWithDisplaySize(markup);
          const blob = new Blob([displayMarkup], { type: 'image/svg+xml' });
          const blobUrl = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => {
            /* Trim the display raster to ink — same as fresh placement
               (refreshSvgLayer/addSvgLayer) — so a RESTORED design paints
               identically to the session it was designed in. Saved w/h are
               kept verbatim (the box was captured post-trim at design time);
               only the paint source changes. */
            const trimmed = trimToContent(img);
            let useImg = img;
            if (trimmed) {
              useImg = trimmed.img;
              if (useImg.decode) useImg.decode().then(() => render()).catch(() => {});
            }
            const w = (ld.w || 100) * fitScale, h = (ld.h || 100) * fitScale;
            const nl = {
              id: uid(), type: 'image', name: ld.name || 'Clipart',
              img: useImg, b64: null, isSvg: true, svgMarkup: markup,
              _imgMarkupSrc: markup, /* v2.12.3: staleness tag for capture guard */
              x: mapX(ld.x || savedCX), y: mapY(ld.y || savedCY),
              w, h, ar: w / h,
              rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
              visible: true, centered: ld.centered ?? true,
              tintColor: ld.tintColor || null, isRasterUpload: false,
              _groupId: ld._groupId || null, isBackdrop: !!ld.isBackdrop,
              _backdropKey: ld._backdropKey || null, _maskMarkup: ld._maskMarkup || null,
              _content: ld._content || null, _bgSeed: ld._bgSeed || null,
            };
            S.byView[slug].push(nl);
            ensureSvgInkBox(nl); /* display/production parity map */
            URL.revokeObjectURL(blobUrl);
            updateLayersPanel(); updateCartBtn(); render();
          };
          img.onerror = () => {
            URL.revokeObjectURL(blobUrl);
            /* v2.12.3: was a SILENT LAYER DROP — a failed display raster
               deleted the whole layer from the restored design (and the next
               save persisted the loss). Push the layer with img:null instead:
               the capture guard (ensureSideLayerPaintSources) rebuilds the
               paint source from svgMarkup before any mockup/thumbnail, and
               production always builds from markup. */
            gwarn('restore_svg_raster_failed', 'Display raster failed on restore for "' + (ld.name || 'Clipart') + '" — layer kept, raster deferred');
            const w = (ld.w || 100) * fitScale, h = (ld.h || 100) * fitScale;
            S.byView[slug].push({
              id: uid(), type: 'image', name: ld.name || 'Clipart',
              img: null, b64: null, isSvg: true, svgMarkup: markup,
              x: mapX(ld.x || savedCX), y: mapY(ld.y || savedCY),
              w, h, ar: w / h,
              rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
              visible: true, centered: ld.centered ?? true,
              tintColor: ld.tintColor || null, isRasterUpload: false,
              _groupId: ld._groupId || null, isBackdrop: !!ld.isBackdrop,
              _backdropKey: ld._backdropKey || null, _maskMarkup: ld._maskMarkup || null,
              _content: ld._content || null, _bgSeed: ld._bgSeed || null,
            });
            updateLayersPanel(); render();
          };
          img.src = blobUrl;
        }
        /* Small raster images (saved as b64) — includes filled template
           photo slots / replaced graphics (_tplPhoto, isRasterUpload=false),
           which were previously dropped on restore. */
        else if (ld.b64 && (ld.isRasterUpload || ld._tplPhoto)) {
          const img = new Image(); img.crossOrigin = 'anonymous';
          img.onload = () => {
            const w = (ld.w || 100) * fitScale, h = (ld.h || 100) * fitScale;
            S.byView[slug].push({
              id: uid(), type: 'image', name: ld.name || 'Image',
              img, b64: ld.b64,
              x: mapX(ld.x || savedCX), y: mapY(ld.y || savedCY),
              w, h, ar: w / h,
              rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
              visible: true, centered: ld.centered ?? false,
              tintColor: ld.tintColor || null, isRasterUpload: !!ld.isRasterUpload,
              isPhoto: ld.isPhoto || false,
              _bgRemoved: ld._bgRemoved || false,
              _engraveInverted: ld._engraveInverted || false, /* v2.11.0: b64 already carries the inverted pixels */
              removedColor: ld.removedColor || null,
              _groupId: ld._groupId || null, _maskKey: ld._maskKey || null,
              _bgSeed: ld._bgSeed || null,
              _tplLockId: ld._tplLockId || null,
              ...(ld._tplPhoto ? { _tplPhoto: true, _noResize: true } : {}),
              ...(ld._tplMask ? { _tplMask: true } : {}),
            });
            updateLayersPanel(); updateCartBtn(); render();
          };
          img.src = ld.b64;
        }
      });
    });

    scheduleGroupSettle();

    /* Wait for fonts then re-measure text */
    document.fonts?.ready?.then(() => {
      CFG.sides.forEach(s => {
        S.byView[s].forEach(l => { if (l.type === 'text') { measureText(l); l.ar = l.w / (l.h || 1); } });
      });
      render();
    });

    updateLayersPanel(); updateCartBtn(); render();
  }

  /* ══════════════════════════════════════════════════════════════════
   * CUSTOMER SAVED DESIGNS (server-side persistence)
   * ══════════════════════════════════════════════════════════════════ */

  /* ── API helpers ───────────────────────────────────── */
  function designApiHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-Customer-Id': String(CFG.customerId),
      'X-Customer-Token': CFG.customerToken || '',
    };
  }

  async function apiListDesigns() {
    const res = await fetch(
      `${CFG.designsApiUrl}?cid=${CFG.customerId}&token=${encodeURIComponent(CFG.customerToken)}`,
      { headers: designApiHeaders() }
    );
    if (!res.ok) throw new Error(`List failed: ${res.status}`);
    return res.json();
  }

  async function apiSaveDesign(designId, design, thumbnail) {
    const res = await fetch(CFG.designsApiUrl, {
      method: 'POST',
      headers: designApiHeaders(),
      body: JSON.stringify({
        designId,
        design,
        thumbnail,
        productHandle: CFG.productHandle,
        productTitle: CFG.productTitle,
        email: CFG.customerEmail,
        method: S.method,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Save failed: ${res.status}`);
    }
    return res.json();
  }

  async function apiDeleteDesign(designId) {
    const res = await fetch(
      `${CFG.designsApiUrl}?id=${designId}&cid=${CFG.customerId}&token=${encodeURIComponent(CFG.customerToken)}`,
      { method: 'DELETE', headers: designApiHeaders() }
    );
    if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    return res.json();
  }

  /* ── Serialize for server save (includes SVG markup + image S3 URLs) ── */
  function serializeForSave() {
    const design = serializeDesign();
    /* Extend layers with SVG markup and image references */
    CFG.sides.forEach(s => {
      const ls = S.byView[s];
      const sd = design.sides[s];
      if (!sd?.layers) return;
      sd.layers.forEach((ld, i) => {
        const l = ls[i];
        if (!l) return;
        if (l.isSvg && l.svgMarkup) ld.svgMarkup = l.svgMarkup;
        /* For raster images, save b64 data URL (will be uploaded to S3 separately for large files) */
        if (l.isRasterUpload && l.b64) ld.b64 = l.b64;
      });
    });
    return design;
  }

  /* ── Upload raster images to S3, replace b64 with URLs ── */
  async function uploadDesignImages(design, folder) {
    for (const sSlug of Object.keys(design.sides)) {
      const side = design.sides[sSlug];
      if (!side?.layers) continue;
      for (const ld of side.layers) {
        if (ld.b64 && (ld.isRasterUpload || ld._tplPhoto) && ld.b64.length > 10000) {
          try {
            const url = await uploadToS3(ld.b64, `${ld.name || 'image'}.png`, 'image/png', folder);
            if (url) { ld.imageUrl = url; delete ld.b64; }
          } catch (e) { DEBUG && console.warn('[dt] Image upload failed, keeping b64:', e.message); }
        }
      }
    }
  }

  /* ── Save design (explicit button) ─────────────────── */
  async function saveDesignToServer() {
    if (!CFG.customerLoggedIn) {
      openLoginPromptModal(); return;
    }

    const btn = $('dt-save-design-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

    try {
      const designId = 'design-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
      const design = serializeForSave();
      const s3Folder = `saved-designs/${CFG.customerId}`;

      /* Upload raster images to S3 first */
      await uploadDesignImages(design, s3Folder);

      /* Generate thumbnail */
      await ensureSideLayerPaintSources(S.view); /* v2.12.3 */
      const thumbnail = mockupPng(S.view);

      /* Save to server */
      const result = await apiSaveDesign(designId, design, thumbnail);

      if (btn) { btn.textContent = 'Saved'; setTimeout(() => { syncSavedDock(); }, 2000); }
      loadSavedDesignsGrid();
    } catch (err) {
      DEBUG && console.error('[dt] Save failed:', err);
      if (btn) { btn.textContent = (err.message || "Couldn't save").slice(0, 40); setTimeout(() => { syncSavedDock(); }, 3000); }
    }
  }

  /* ── Auto-save on cart add (for logged-in customers) ── */
  async function autoSaveOnCart() {
    if (!CFG.customerLoggedIn) return;
    try {
      const designId = 'cart-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
      const design = serializeForSave();
      const s3Folder = `saved-designs/${CFG.customerId}`;
      await uploadDesignImages(design, s3Folder);
      await ensureSideLayerPaintSources(S.view); /* v2.12.3 */
      const thumbnail = mockupPng(S.view);
      await apiSaveDesign(designId, design, thumbnail);
    } catch (e) { DEBUG && console.warn('[dt] Auto-save on cart failed:', e.message); }
  }

  /* ── Load saved design ─────────────────────────────── */
  async function loadSavedDesign(designId) {
    try {
      const data = await (await fetch(
        `${CFG.designsApiUrl}?cid=${CFG.customerId}&token=${encodeURIComponent(CFG.customerToken)}`,
        { headers: designApiHeaders() }
      )).json();

      /* Find the design in the list, then fetch full data from S3 */
      const url = `https://${S3_BUCKET_URL()}/saved-designs/${CFG.customerId}/${designId}.json`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Design not found');
      const record = await res.json();

      /* Portable restore with auto-fit */
      restoreServerDesign(record.design);
    } catch (err) {
      DEBUG && console.error('[dt] Load failed:', err);
      alert('Failed to load design: ' + (err.message || 'Unknown error'));
    }
  }

  function S3_BUCKET_URL() {
    return `custom-branding-design-files.s3.us-east-2.amazonaws.com`;
  }

  /* ── Portable restore with auto-fit contain ────────── */
  function restoreServerDesign(design) {
    if (!design?.sides) return;
    let aspectWarning = false;

    CFG.sides.forEach(slug => {
      const saved = design.sides[slug];
      if (!saved?.layers?.length) return;

      /* Current rendered print area (canvas-space pixels) */
      const curPA = S.pa;

      /* Saved rendered print area — same coordinate space as saved layer positions.
         Fall back to computing from raw PA × current rscale for old saves. */
      let savedPA = saved.renderedPrintArea;
      if (!savedPA && saved.printArea) {
        savedPA = {
          x: saved.printArea.x * S.rscale,
          y: saved.printArea.y * S.rscale,
          w: saved.printArea.w * S.rscale,
          h: saved.printArea.h * S.rscale,
        };
      }
      if (!savedPA) savedPA = curPA;

      /* Contain-fit scale: shrink design to fit within new print area */
      const fitScale = Math.min(curPA.w / savedPA.w, curPA.h / savedPA.h, 1.5);

      /* Centers for repositioning */
      const savedCX = savedPA.x + savedPA.w / 2;
      const savedCY = savedPA.y + savedPA.h / 2;
      const curCX = curPA.x + curPA.w / 2;
      const curCY = curPA.y + curPA.h / 2;
      /* Aspect ratio warning */
      if (saved.physicalDims && SIDE_PHYS[slug]) {
        const savedAR = saved.physicalDims.wInches / saved.physicalDims.hInches;
        const curAR = SIDE_PHYS[slug].wInches / SIDE_PHYS[slug].hInches;
        if (Math.abs(savedAR - curAR) > 0.3) aspectWarning = true;
      }

      saved.layers.forEach(ld => {
        /* Transform position from saved PA space to current PA space */
        const mapX = x => curCX + (x - savedCX) * fitScale;
        const mapY = y => curCY + (y - savedCY) * fitScale;

        if (ld.type === 'text') {
          const scaledFontSize = Math.max(8, Math.round((ld.fontSize || 36) * fitScale));
          const l = {
            id: uid(), type: 'text', name: ld.name || ld.text?.slice(0, 20),
            text: ld.text || '', font: ld.font || S.textFont || 'Montserrat',
            fontSize: scaledFontSize,
            color: guardRestoredTextColor(ld.color || '#111111').color, _autoColor: guardRestoredTextColor(ld.color || '#111111').auto,
            bold: ld.bold || false, italic: ld.italic || false,
            x: mapX(ld.x), y: mapY(ld.y),
            w: (ld.w || 100) * fitScale, h: scaledFontSize,
            rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
            visible: true, centered: ld.centered ?? true, ar: null, isRasterUpload: false,
            _bulkField: ld._bulkField || false,
          };
          measureText(l); l.ar = l.w / (l.h || 1);
          S.byView[slug].push(l);

        } else if (ld.isMonogram) {
          /* Restore monogram manually with mapped position (not via addMonogramLayer which auto-centers) */
          const style = MONO_STYLES[ld.monoStyle];
          if (style) {
            const size = 512;
            const off = document.createElement('canvas'); off.width = size; off.height = size;
            const oc2 = off.getContext('2d');
            try { style.render(oc2, ld.monoText, size, size, ld.monoColor); } catch(_) {}
            const b64 = off.toDataURL('image/png');
            const img = new Image();
            img.onload = () => {
              const w = (ld.w || 100) * fitScale;
              const h = (ld.h || 100) * fitScale;
              S.byView[slug].push({
                id: uid(), type: 'image', name: 'Monogram: ' + ld.monoText,
                img, b64, x: mapX(ld.x || savedCX), y: mapY(ld.y || savedCY),
                w, h, ar: 1,
                rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
                visible: true, centered: ld.centered ?? true,
                tintColor: null, isMonogram: true,
                monoText: ld.monoText, monoStyle: ld.monoStyle, monoColor: ld.monoColor, monoColor2: ld.monoColor2,
                isRasterUpload: false,
              });
              updateLayersPanel(); updateCartBtn(); render();
            };
            img.src = b64;
          }

        } else if (ld.svgMarkup) {
          /* Restore SVG manually with mapped position (not via addSvgLayer which auto-centers).
             svgWithDisplaySize + trimToContent for parity with fresh placement — the raw
             markup rasterized at intrinsic size (~150px) untrimmed, so restored designs
             painted blurry AND letterboxed vs the session they were designed in. */
          const markup = ld.svgMarkup;
          const blob = new Blob([svgWithDisplaySize(markup)], { type: 'image/svg+xml' });
          const blobUrl = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => {
            const trimmed = trimToContent(img);
            let useImg = img;
            if (trimmed) {
              useImg = trimmed.img;
              if (useImg.decode) useImg.decode().then(() => render()).catch(() => {});
            }
            const w = (ld.w || 100) * fitScale;
            const h = (ld.h || 100) * fitScale;
            const nl = {
              id: uid(), type: 'image', name: ld.name || 'Clipart',
              img: useImg, b64: null, isSvg: true, svgMarkup: markup,
              _imgMarkupSrc: markup, /* v2.12.3: staleness tag for capture guard */
              x: mapX(ld.x || savedCX), y: mapY(ld.y || savedCY),
              w, h, ar: w / h,
              rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
              visible: true, centered: ld.centered ?? true,
              tintColor: ld.tintColor || null, isRasterUpload: false,
              _groupId: ld._groupId || null, isBackdrop: !!ld.isBackdrop,
              _backdropKey: ld._backdropKey || null, _maskMarkup: ld._maskMarkup || null,
              _content: ld._content || null, _bgSeed: ld._bgSeed || null,
            };
            S.byView[slug].push(nl);
            ensureSvgInkBox(nl); /* display/production parity map */
            URL.revokeObjectURL(blobUrl);
            updateLayersPanel(); updateCartBtn(); render();
          };
          img.onerror = () => {
            URL.revokeObjectURL(blobUrl);
            /* v2.12.3: was a SILENT LAYER DROP — a failed display raster
               deleted the whole layer from the restored design (and the next
               save persisted the loss). Push the layer with img:null instead:
               the capture guard (ensureSideLayerPaintSources) rebuilds the
               paint source from svgMarkup before any mockup/thumbnail, and
               production always builds from markup. */
            gwarn('restore_svg_raster_failed', 'Display raster failed on restore for "' + (ld.name || 'Clipart') + '" — layer kept, raster deferred');
            const w = (ld.w || 100) * fitScale, h = (ld.h || 100) * fitScale;
            S.byView[slug].push({
              id: uid(), type: 'image', name: ld.name || 'Clipart',
              img: null, b64: null, isSvg: true, svgMarkup: markup,
              x: mapX(ld.x || savedCX), y: mapY(ld.y || savedCY),
              w, h, ar: w / h,
              rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
              visible: true, centered: ld.centered ?? true,
              tintColor: ld.tintColor || null, isRasterUpload: false,
              _groupId: ld._groupId || null, isBackdrop: !!ld.isBackdrop,
              _backdropKey: ld._backdropKey || null, _maskMarkup: ld._maskMarkup || null,
              _content: ld._content || null, _bgSeed: ld._bgSeed || null,
            });
            updateLayersPanel(); render();
          };
          img.src = blobUrl;

        } else if (ld.imageUrl || ld.b64) {
          const src = ld.imageUrl || ld.b64;
          const img = new Image(); img.crossOrigin = 'anonymous';
          img.onload = () => {
            const w = (ld.w || 100) * fitScale;
            const h = (ld.h || 100) * fitScale;
            S.byView[slug].push({
              id: uid(), type: 'image', name: ld.name || 'Image',
              img, b64: ld.b64 || src,
              x: mapX(ld.x || savedCX), y: mapY(ld.y || savedCY),
              w, h, ar: w / h,
              rotation: ld.rotation || 0, flipH: ld.flipH || false, flipV: ld.flipV || false,
              visible: true, centered: ld.centered ?? false,
              tintColor: ld.tintColor || null,
              isRasterUpload: ld.isRasterUpload !== undefined ? !!ld.isRasterUpload : true,
              isPhoto: ld.isPhoto || false,
              /* v2.11.0 review C2: the server-restore path dropped these flags
                 (autosave restore carried them) — an inverted plate came back
                 unflagged: XOR parity lost, cart prop unset, destructive
                 re-invert offered. _bgRemoved restored too (same gap class). */
              _bgRemoved: ld._bgRemoved || false,
              _engraveInverted: ld._engraveInverted || false,
              _groupId: ld._groupId || null, _maskKey: ld._maskKey || null,
              _bgSeed: ld._bgSeed || null,
              _tplLockId: ld._tplLockId || null,
              ...(ld._tplPhoto ? { _tplPhoto: true, _noResize: true } : {}),
              ...(ld._tplMask ? { _tplMask: true } : {}),
            });
            updateLayersPanel(); updateCartBtn(); render();
          };
          img.src = src;
        }
      });
    });

    /* Wait for fonts then re-measure text */
    document.fonts?.ready?.then(() => {
      CFG.sides.forEach(s => {
        S.byView[s].forEach(l => { if (l.type === 'text') { measureText(l); l.ar = l.w / (l.h || 1); } });
      });
      render();
    });

    scheduleGroupSettle();
    updateLayersPanel(); updateCartBtn(); render();

    if (aspectWarning) {
      setTimeout(() => {
        alert('This design was created for a different print area shape. It has been scaled to fit. You may want to adjust the layout.');
      }, 500);
    }
  }

  /* ── Delete saved design ───────────────────────────── */
  async function deleteSavedDesign(designId) {
    if (!confirm('Delete this saved design?')) return;
    try {
      await apiDeleteDesign(designId);
      loadSavedDesignsGrid();
    } catch (err) {
      DEBUG && console.error('[dt] Delete failed:', err);
      alert('Failed to delete: ' + (err.message || 'Unknown error'));
    }
  }

  /* ── Saved tab UI ──────────────────────────────────── */
  const SAVE_BTN_HTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 14H3a1 1 0 01-1-1V3a1 1 0 011-1h7.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V13a1 1 0 01-1 1z" stroke="currentColor" stroke-width="1.5"/><path d="M5 2v3h5V2M5 14v-4h6v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg> Save Design';
  /* rev 6: contextual dock under the Saved grid — nothing selected → "Save Design"; a card
     selected → "Load design" + quiet "Delete" link. JS is the source of truth (no :has() reliance). */
  function syncSavedDock() {
    const btn = $('dt-save-design-btn'), del = $('dt-saved-del'), dock = $('dt-saved-dock');
    if (!btn) return;
    const sel = S._savedSel && document.querySelector('.dt-saved-card[data-id="' + S._savedSel + '"]') ? S._savedSel : null;
    if (!sel) S._savedSel = null;
    btn.innerHTML = sel ? 'Load design' : SAVE_BTN_HTML;
    btn.disabled = false;
    if (del) del.hidden = !sel;
    if (dock) dock.classList.toggle('dt-dock--sel', !!sel);
  }
  function setupSavedTab() {
    const btn = $('dt-save-design-btn');
    if (btn && !$('dt-saved-dock')) {
      const dock = document.createElement('div'); dock.className = 'dt-dock'; dock.id = 'dt-saved-dock';
      btn.parentNode.insertBefore(dock, btn); dock.appendChild(btn); btn.classList.add('dt-dock__primary');
      const del = document.createElement('button'); del.type = 'button'; del.id = 'dt-saved-del'; del.className = 'dt-dock__link'; del.textContent = 'Delete'; del.hidden = true;
      del.addEventListener('click', () => { if (S._savedSel) deleteSavedDesign(S._savedSel); });
      dock.appendChild(del);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && S._savedSel) { S._savedSel = null; document.querySelectorAll('.dt-saved-card.selected').forEach(c => { c.classList.remove('selected'); c.setAttribute('aria-pressed', 'false'); }); syncSavedDock(); }
      });
    }
    btn?.addEventListener('click', () => {
      if (S._savedSel) { const id = S._savedSel; loadSavedDesign(id); S._savedSel = null; document.querySelectorAll('.dt-saved-card.selected').forEach(c => { c.classList.remove('selected'); c.setAttribute('aria-pressed', 'false'); }); syncSavedDock(); }
      else saveDesignToServer();
    });
    if (CFG.customerLoggedIn) loadSavedDesignsGrid();
  }

  /* Saved panel drawer sync — widen to the shared library-drawer layout ONLY when
     the customer actually has saved designs. The signin prompt, empty state, and
     error state keep the normal slot width. Guarded on the panel being active so
     background refreshes (after save/delete from elsewhere) never pop the drawer. */
  function syncSavedDrawer(hasDesigns) {
    if (isMobile()) return;
    const panel = $('dt-panel-saved');
    if (!panel || !panel.classList.contains('active')) return;
    if (hasDesigns) openLibraryDrawer();
    else document.querySelector('.dt-sidebar--left')?.classList.remove('dt-clipart-open');
  }

  async function loadSavedDesignsGrid() {
    const grid = $('dt-saved-grid');
    if (!grid) return;

    const saveBtn = $('dt-save-design-btn');
    if (saveBtn) saveBtn.style.display = CFG.customerLoggedIn ? '' : 'none';
    const savedDock = $('dt-saved-dock'); if (savedDock) savedDock.style.display = CFG.customerLoggedIn ? '' : 'none';

    if (!CFG.customerLoggedIn) {
      const retUrl = window.location.pathname + window.location.search;
      grid.innerHTML = `<div class="dt-saved-signin">
        <div class="dt-saved-signin__icon"><svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M6 21V8a2 2 0 012-2h8a2 2 0 012 2v13l-6-3.5L6 21z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg></div>
        <div class="dt-saved-signin__title">Save &amp; revisit your designs</div>
        <div class="dt-saved-signin__desc">Log in to save this design to your account and pick up anytime, on any device.</div>
        <div class="dt-saved-signin__note">Your work is auto-saved on this device — log in to keep it on any device.</div>
        <a href="/customer_identity/login?return_to=${encodeURIComponent(retUrl)}" class="dt-saved-signin__btn dt-login-redirect">Log in</a>
        <a href="/customer_identity/login?return_to=${encodeURIComponent(retUrl)}" class="dt-saved-signin__create dt-login-redirect">New here? Create an account</a>
      </div>`;
      grid.querySelectorAll('.dt-login-redirect, .dt-saved-signin__create').forEach(a =>
        a.addEventListener('click', () => { try { autosave(); localStorage.setItem('dt_return_url', retUrl); } catch(e) {} })
      );
      syncSavedDrawer(false);
      return;
    }

    grid.innerHTML = '<div class="dt-saved-loading"><div class="dt-spinner-ring"></div> Loading saved designs…</div>';

    try {
      const data = await apiListDesigns();
      if (!data.designs?.length) {
        grid.innerHTML = `<div class="dt-empty-state" style="font-size:12px">
          <div class="dt-empty-state__icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z" stroke="currentColor" stroke-width="1.5"/><path d="M12 8v8M8 12h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div>
          No saved designs yet.<br>Create a design and click "Save Design" to keep it.
          <div style="font-size:10px;color:var(--dt-muted);margin-top:6px">${data.count || 0}/${data.max || MAX_DESIGNS} slots used</div>
        </div>`;
        syncSavedDrawer(false);
        return;
      }

      /* rev 6: Saved joins select → commit. No per-card buttons; click selects (aria-pressed),
         the dock's primary flips to "Load design" and a quiet Delete link appears (syncSavedDock). */
      S._savedSel = null;
      grid.innerHTML = `<div class="dt-saved-count">${data.count}/${data.max} designs saved</div>` +
        data.designs.map(d => `
          <button type="button" class="dt-saved-card dt-card dt-card--stage" data-id="${d.id}" aria-pressed="false" aria-label="${escHtml(d.productTitle || 'Design')} — ${d.method === 'engrave' ? 'Engrave' : 'Print'}, ${timeAgo(d.savedAt)}">
            ${CARD_CHECK}
            <span class="dt-card__media dt-saved-card__thumb"><img src="${d.thumbnailUrl}" alt="" onerror="this.style.display='none'"></span>
            <span class="dt-card__caption dt-saved-card__title">${escHtml(d.productTitle || 'Design')}</span>
            <span class="dt-card__meta dt-saved-card__meta">${d.method === 'engrave' ? 'Engrave' : 'Print'} · ${d.sideCount} side${d.sideCount !== 1 ? 's' : ''} · ${timeAgo(d.savedAt)}</span>
          </button>
        `).join('');

      grid.querySelectorAll('.dt-saved-card').forEach(card => card.addEventListener('click', () => {
        const id = card.dataset.id;
        S._savedSel = (S._savedSel === id) ? null : id;
        grid.querySelectorAll('.dt-saved-card').forEach(c => { const on = c.dataset.id === S._savedSel; c.classList.toggle('selected', on); c.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        syncSavedDock();
      }));
      syncSavedDock();
      syncSavedDrawer(true);
    } catch (err) {
      DEBUG && console.error('[dt] Load saved designs failed:', err);
      grid.innerHTML = '<div class="dt-empty-state" style="font-size:11px;color:var(--dt-danger)">Couldn\'t load your saved designs</div>';
      syncSavedDrawer(false);
    }
  }

  function timeAgo(dateStr) {
    if (!dateStr) return '';
    const ms = Date.now() - new Date(dateStr).getTime();
    const h = Math.round(ms / 3600000);
    if (h < 1) return 'just now';
    if (h < 24) return h + 'h ago';
    const d = Math.round(h / 24);
    if (d < 30) return d + 'd ago';
    return Math.round(d / 30) + 'mo ago';
  }

  /* ── Login prompt modal (for guests clicking Save) ── */
  function openLoginPromptModal() {
    document.getElementById('dt-login-modal')?.remove();
    const currentUrl = window.location.pathname + window.location.search;
    const returnUrl = encodeURIComponent(currentUrl);
    const overlay = document.createElement('div');
    overlay.id = 'dt-login-modal'; overlay.className = 'dt-rbg-overlay';
    overlay.innerHTML = `<div class="dt-rbg-panel dt-rbg-panel--md">
      <div class="dt-rbg-header">
        <span class="dt-rbg-title">Save Your Design</span>
        <button class="dt-rbg-close" id="dt-login-close">&times;</button>
      </div>
      <div style="font-size:13px;color:var(--dt-text-2);line-height:1.6;margin:8px 0 20px">
        Create a free account to save your designs and access them anytime, from any device.
      </div>
      <a href="/customer_identity/login?return_to=${returnUrl}" class="dt-rbg-use-btn dt-login-redirect" style="display:block;text-align:center;text-decoration:none;margin-bottom:10px">Create Account</a>
      <a href="/customer_identity/login?return_to=${returnUrl}" class="dt-rbg-skip dt-login-redirect" style="display:block;text-align:center;text-decoration:none;margin-bottom:16px">Log In to Existing Account</a>
      <div style="border-top:1px solid var(--dt-border);padding-top:14px;text-align:center">
        <button id="dt-login-dismiss" style="background:none;border:none;color:var(--dt-muted);font-family:var(--dt-font);font-size:12px;cursor:pointer;padding:6px 12px">
          No thanks, continue as guest
        </button>
        <div style="font-size:10px;color:var(--dt-muted);margin-top:6px;font-style:italic">
          Your design will only be saved in this browser until you close it.
        </div>
      </div>
    </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('#dt-login-close').addEventListener('click', close);
    overlay.querySelector('#dt-login-dismiss').addEventListener('click', close);
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });

    /* Save design + return URL to localStorage before navigating to login/register.
       autosave() guarantees the design is saved even if the last edit didn't trigger pushHistory.
       The redirect snippet in theme.liquid will redirect back after successful login. */
    overlay.querySelectorAll('.dt-login-redirect').forEach(a => {
      a.addEventListener('click', () => {
        try { autosave(); localStorage.setItem('dt_return_url', currentUrl); } catch(e) {}
      });
    });
  }

  function undo() { const h = S.history[S.view]; if (h.index <= 0) return; h.index--; restoreHistory(h.stack[h.index]); }
  function redo() { const h = S.history[S.view]; if (h.index >= h.stack.length - 1) return; h.index++; restoreHistory(h.stack[h.index]); }

  function restoreHistory(snap) {
    const parsed = JSON.parse(snap), restored = []; let pending = 0;
    parsed.forEach(ld => {
      const l = { ...ld, img: null };
      if (ld._imgSrc && ld._imgSrc !== '[img]') { pending++; const img = new Image(); img.crossOrigin = 'anonymous'; img.onload = () => { l.img = img; pending--; if (!pending) render(); }; img.onerror = () => {
        /* v2.12.3: SVG layers can always rebuild from markup — never flag
           _imgFailed (the red placeholder would paint into captures). */
        if (l.isSvg && l.svgMarkup) {
          _rebuildSvgDisplayRaster(l).then(ok => {
            if (!ok) gwarn('undo_svg_raster_failed', 'Undo/redo could not rebuild display raster for "' + (l.name || l.id) + '"'); /* N4 */
            pending--; if (!pending) render();
          });
          return;
        }
        DEBUG && console.warn('[dt] Undo/redo: image failed to reload:', ld._imgSrc.slice(0, 80)); l._imgFailed = true; pending--; if (!pending) render(); }; img.src = ld._imgSrc; }
      else if (l.isSvg && l.svgMarkup) {
        /* v2.12.3: snapshot carried no restorable raster (e.g. the placement
           snapshot of a just-added backdrop, taken before its async display
           raster landed) — rebuild geometry-neutrally from markup so the
           editor and every img-driven capture paint the layer. */
        pending++;
        _rebuildSvgDisplayRaster(l).then(ok => {
          if (!ok) gwarn('undo_svg_raster_failed', 'Undo/redo could not rebuild display raster for "' + (l.name || l.id) + '"');
          pending--; if (!pending) render();
        });
      }
      delete l._imgSrc; restored.push(l);
    });
    S.byView[S.view] = restored; S.sel = null; S.iact = null;
    /* UX-Aug15 #1 (final review A2): a history snapshot can carry an auto-white layer taken
       in print; if we're now in engrave, park flagged layers dark again (flagged only). */
    try { reseedAutoTextColors('history'); } catch (_) {}
    updateLayersPanel(); forcePropsRebuild(); updateCartBtn(); updateHistoryBtns();
    syncMobileSelection();
    if (!pending) render();
  }

  function updateHistoryBtns() {
    const h = S.history[S.view];
    if (undoBtn) undoBtn.disabled = h.index <= 0;
    if (redoBtn) redoBtn.disabled = h.index >= h.stack.length - 1;
    /* Floating undo/redo pill */
    const pUndo = $('dt-pill-undo'), pRedo = $('dt-pill-redo');
    if (pUndo) pUndo.disabled = h.index <= 0;
    if (pRedo) pRedo.disabled = h.index >= h.stack.length - 1;
    /* Mobile floating undo/redo */
    const fUndo = $('dt-undo-float'), fRedo = $('dt-redo-float');
    if (fUndo) fUndo.disabled = h.index <= 0;
    if (fRedo) fRedo.disabled = h.index >= h.stack.length - 1;
  }

  /* ══════════════════════════════════════════════════════════════════
   * INLINE TEXT EDITING (mobile — floating input over canvas)
   * ══════════════════════════════════════════════════════════════════ */
  function openInlineTextEdit(l) {
    closeInlineTextEdit();
    if (!l || l.type !== 'text') return;

    /* Fixed bottom bar — always visible, zoom-independent */
    const bar = document.createElement('div');
    bar.id = 'dt-inline-bar';
    bar.className = 'dt-inline-bar';
    bar.innerHTML = `
      <textarea id="dt-inline-input" class="dt-inline-bar__input"
        rows="1" autocomplete="off" autocorrect="off" spellcheck="false"
        placeholder="Type here...">${l.text.replace(/</g, '&lt;')}</textarea>
    `;
    bar._layerId = l.id;
    document.querySelector('.dt-body')?.appendChild(bar);
    /* (#45) While typing, hide the bottom chrome (tab bar + snap/dims FABs) so they
       don't peek behind/around the edit field. Restored in closeInlineTextEdit. */
    document.getElementById('dt-app')?.classList.add('dt-text-editing');

    const input = bar.querySelector('#dt-inline-input');

    /* Auto-expand textarea height as lines are added */
    function autoResize() {
      input.style.height = 'auto';
      /* (#45) Lock the field to ~the main toolbar height (44px floor) for short text, so
         it doesn't shrink below the toolbar; grow per added line up to a cap, then it
         scrolls internally. */
      input.style.height = Math.max(44, Math.min(160, input.scrollHeight)) + 'px';
    }

    /* Real-time canvas update */
    input.addEventListener('input', () => {
      const l2 = layers().find(x => x.id === bar._layerId);
      if (!l2) return;
      l2.text = input.value; l2.name = input.value.replace(/\n/g, ' ').slice(0, 25) || 'Text';
      measureText(l2); l2.ar = l2.w / (l2.h || 1);
      render();
      autoResize();
    });

    /* Escape key closes the edit bar */
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); closeInlineTextEdit(); }
    });

    /* No Done button — the ✓ checkmark handles exit.
       Tapping canvas closes edit bar via closeInlineTextEdit in onDown. */

    /* Auto-focus so keyboard appears immediately */
    requestAnimationFrame(() => {
      autoResize(); /* size to existing (possibly multi-line) text on open */
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  function closeInlineTextEdit() {
    const bar = document.getElementById('dt-inline-bar');
    if (!bar) return;
    const l = layers().find(x => x.id === bar._layerId);
    if (l?.type === 'text') pushHistory();
    document.activeElement?.blur();
    bar.remove();
    document.getElementById('dt-app')?.classList.remove('dt-text-editing');
    /* Re-zoom to PA after keyboard closes — delay for layout settle */
    if (isMobile()) requestZoomToPA(200);
  }

  /* ══════════════════════════════════════════════════════════════════
   * CANVAS INTERACTION — drag, resize handles, pan, pinch zoom
   * ══════════════════════════════════════════════════════════════════ */
  let _pointers = {}; /* pointerId → {x, y} for multi-touch tracking */

  /* ══════════════════════════════════════════════════════════════════
   * FLOATING VERTICAL RESIZE SLIDER
   * ══════════════════════════════════════════════════════════════════ */
  function setupDoneButton() {
    $('dt-done-btn')?.addEventListener('click', exitDesignMode);
  }

  /* ── Viewport aria-hidden sync ──────────────────────────────────
     Desktop sidebar tabs and mobile bottom tabs are duplicate DOM for
     responsive layout. Mark the inactive viewport's copy aria-hidden
     so screen readers only traverse the visible set. Also covers the
     desktop vs mobile method (Engrave/Print) toggles. */
  function syncViewportAria() {
    const mob = isMobile();
    /* Desktop sidebar (hidden on mobile via CSS) */
    const sidebar = $('dt-sidebar-left');
    if (sidebar) sidebar.setAttribute('aria-hidden', mob ? 'true' : 'false');
    /* Mobile bottom tabs (hidden on desktop via CSS) */
    const mobileTabs = $('dt-mobile-tabs');
    if (mobileTabs) mobileTabs.setAttribute('aria-hidden', mob ? 'false' : 'true');
    /* Desktop method toggle */
    const methodD = $('dt-method');
    if (methodD) methodD.setAttribute('aria-hidden', mob ? 'true' : 'false');
    /* Mobile method toggle */
    const methodM = $('dt-method-mobile');
    if (methodM) methodM.setAttribute('aria-hidden', mob ? 'false' : 'true');
    /* Desktop qty stepper */
    const qtyD = $('dt-qty-desktop');
    if (qtyD) qtyD.setAttribute('aria-hidden', mob ? 'true' : 'false');
    /* Mobile cart bar */
    const cartBar = $('dt-cart-bar');
    if (cartBar) cartBar.setAttribute('aria-hidden', mob ? 'false' : 'true');
  }
  function setupViewportAria() {
    syncViewportAria();
    window.addEventListener('resize', syncViewportAria);
  }

  /* Global paste handler — pastes text into selected text layer */
  document.addEventListener('paste', e => {
    if (document.querySelector('.dt-inline-input')) return; /* inline edit handles its own paste */
    const l = selLayer();
    if (!l || l.type !== 'text') return;
    const text = e.clipboardData?.getData('text/plain');
    if (text) {
      e.preventDefault();
      l.text += text; l.name = l.text.slice(0, 25) || 'Text';
      measureText(l); l.ar = l.w / (l.h || 1);
      pushHistory(); render(); forcePropsRebuild();
    }
  });

  /* Map a font file URL to its CSS @font-face format() keyword. Returns '' for
     unknown extensions so we omit the hint and let the browser sniff the file. */
  function fontFaceFormat(url) {
    var u = String(url || '').toLowerCase().split('?')[0].split('#')[0];
    if (u.endsWith('.woff2')) return 'woff2';
    if (u.endsWith('.woff'))  return 'woff';
    if (u.endsWith('.otf'))   return 'opentype';
    if (u.endsWith('.ttf'))   return 'truetype';
    return '';
  }

  /* CSS font-style only accepts normal | italic | oblique. Anything else
     (e.g. "Regular", "Bold") would be an invalid descriptor, so coerce. */
  function normalizeFontStyle(s) {
    return /^(italic|oblique)$/i.test(s || '') ? String(s).toLowerCase() : 'normal';
  }

  /* ── Custom Font Registration (from designtool.custom_fonts metaobjects) ── */
  function registerCustomFonts() {
    if (!CFG.customFonts?.length) return;
    const style = document.createElement('style');
    const rules = [];
    const families = new Set();

    CFG.customFonts.forEach(cf => {
      if (!cf.fontFileUrl || !cf.familyCss) return;
      /* Extract the primary family name from the CSS string */
      const family = cf.familyCss.split(',')[0].replace(/['"]/g, '').trim();
      const fmt = fontFaceFormat(cf.fontFileUrl);
      const src = fmt
        ? `url('${cf.fontFileUrl}') format('${fmt}')`
        : `url('${cf.fontFileUrl}')`;
      rules.push(`@font-face {
        font-family: '${family}';
        src: ${src};
        font-weight: ${cf.weight || 400};
        font-style: ${normalizeFontStyle(cf.style)};
        font-display: swap;
      }`);
      families.add(family);
    });

    style.textContent = rules.join('\n');
    document.head.appendChild(style);
    /* Pre-load all custom fonts so they're ready for canvas */
    families.forEach(family => {
      document.fonts.load('400 16px "' + family + '"').catch(() => {});
      document.fonts.load('700 16px "' + family + '"').catch(() => {});
    });
  }

  /* ── Runtime Google fonts discovered in templates / loaded designs ──────
     Templates reference Google font families by name. Those families usually
     aren't in CFG.fonts or the metaobject custom fonts, so without this they'd
     be stripped on load and wouldn't appear in the font picker. ensureGoogleFont
     injects the Google Fonts stylesheet (lenient CSS API — ignores weights a
     family lacks instead of failing), exposes the family in getAllFonts so it's
     selectable/editable, and preloads it for the canvas. */
  var RUNTIME_FONTS = [];
  var _injectedFontFamilies = Object.create(null);

  function googleFontHref(family) {
    return 'https://fonts.googleapis.com/css?family=' +
      encodeURIComponent(family).replace(/%20/g, '+') + ':400,700&display=swap';
  }

  function ensureGoogleFont(family) {
    if (!family || typeof family !== 'string') return;
    family = family.trim();
    if (!family) return;
    var lower = family.toLowerCase();
    if (_injectedFontFamilies[lower]) return;
    /* Is this family already declared by a metaobject custom font? There are TWO
       cases, and every build through v2.9.33 conflated them — which is why every
       template font was stranded in the hardcoded 'standard' bucket below with no
       way to recategorise it from Shopify:

         SELF-HOSTED — the entry carries a font file. registerCustomFonts has
           already written an @font-face for it, so there is nothing to fetch.
           Return, exactly as before.

         WEBFONT-BY-NAME — the entry has family_css but NO font file. This is how
           you declare a Google family you want to name and categorise yourself.
           registerCustomFonts SKIPS fileless entries (its `!cf.fontFileUrl`
           guard, 11374), so NOTHING else in the app will ever load this family.
           The stylesheet still has to be injected here, or the font silently
           renders as a fallback — which is what made "just add a metaobject row"
           a trap rather than a fix.

       In BOTH known cases we must not push a RUNTIME_FONTS twin: the family is
       already in the picker carrying the metaobject's display name and its real
       category, and a twin would shadow that category with 'standard'. */
    var known = getAllFonts().find(function (f) {
      return (f.fontValue && f.fontValue.toLowerCase() === lower) ||
             (f.name && f.name.toLowerCase() === lower);
    });
    var selfHosted = !!(known && (known.cdnUrl ||
      (known.faces || []).some(function (fc) { return fc && fc.fontFileUrl; })));
    if (known && selfHosted) { _injectedFontFamilies[lower] = true; return; }
    _injectedFontFamilies[lower] = true;
    try {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = googleFontHref(family);
      link.setAttribute('data-dt-font', family);
      document.head.appendChild(link);
    } catch (e) {}
    /* Expose an UNKNOWN family in the picker so it stays selectable and editable.
       A family matched above is deliberately NOT pushed — it is already listed,
       and its metaobject category is the whole point. */
    if (!known) {
      _allFontsCache = null; /* invalidate memoized list */
      RUNTIME_FONTS.push({
        name: family, fontValue: family,
        css: "'" + family + "', sans-serif",
        source: 'template', category: 'standard',
      });
    }
    /* preload for canvas, then repaint when the face is ready */
    try {
      Promise.all([
        document.fonts.load('400 24px "' + family + '"').catch(function () {}),
        document.fonts.load('700 24px "' + family + '"').catch(function () {}),
      ]).then(function () { if (typeof render === 'function') render(); });
    } catch (e) {}
  }

  /* Register every text-layer font in a design so it loads + becomes editable. */
  function registerDesignFonts(design) {
    if (!design || !design.sides) return;
    var seen = Object.create(null);
    Object.keys(design.sides).forEach(function (slug) {
      var side = design.sides[slug];
      ((side && side.layers) || []).forEach(function (l) {
        if (l && l.type === 'text' && l.font && !seen[l.font]) {
          seen[l.font] = true;
          ensureGoogleFont(l.font);
        }
      });
    });
  }

  /* Resolve a fontValue slug (e.g. "amplitude") to its display name
     (e.g. "Amplitude"). Returns the slug as-is if no match is found. */
  function fontDisplayName(fontValue) {
    if (!fontValue) return '';
    const f = getAllFonts().find(fn => fn.fontValue === fontValue || fn.name === fontValue);
    return f ? f.name : fontValue;
  }

  /* ── Get all available fonts (standard + custom) grouped for picker ── */
  let _allFontsCache = null;
  function getAllFonts() {
    if (_allFontsCache) return _allFontsCache;
    const fonts = [];
    /* Standard Google Fonts — name is plain family, css is for style attributes */
    (CFG.fonts || []).forEach(f => {
      fonts.push({ name: f, fontValue: f, css: "'" + f + "', sans-serif", source: 'google', category: 'standard' });
    });
    /* Custom fonts from metaobjects, grouped by family */
    const customFamilies = {};
    (CFG.customFonts || []).forEach(cf => {
      const family = cf.familyName || cf.displayName;
      if (!customFamilies[family]) customFamilies[family] = [];
      customFamilies[family].push(cf);
    });
    Object.entries(customFamilies).forEach(([family, faces]) => {
      /* Use the first face as the default for the family */
      const primary = faces.find(f => f.weight === 400 && f.style === 'normal') || faces[0];
      /* Skip families that can't actually render — no @font-face source (font file)
         AND no family CSS naming a system font. registerCustomFonts() injects faces
         under the same condition, so this keeps the picker/Surprise me in sync with
         what's truly available and avoids offering a font that renders as fallback. */
      const renderable = faces.some(f => f.fontFileUrl) || (primary.familyCss && primary.familyCss.trim());
      if (!renderable) return;
      /* fontValue = the family name registered in @font-face (what fontStr wraps) */
      const registeredFamily = (primary.familyCss || '').split(',')[0].replace(/['"]/g, '').trim() || family;
      fonts.push({
        name: primary.displayName || family,
        fontValue: registeredFamily,
        familyName: family,
        css: primary.familyCss || ("'" + family + "', sans-serif"),
        source: 'custom',
        category: primary.category || 'sans',
        cdnUrl: primary.fontFileUrl,
        faces: faces,
      });
    });
    /* Runtime fonts discovered in templates/designs (Google families by name),
       so they show in the picker and match as the selected font when editing. */
    (RUNTIME_FONTS || []).forEach(function (rf) {
      if (!fonts.some(function (f) { return f.fontValue === rf.fontValue || f.name === rf.name; })) fonts.push(rf);
    });
    _allFontsCache = fonts;
    return fonts;
  }

  /* ── Desktop scroll-wheel zoom (anchored on PA center) ───── */
  function setupWheelZoom() {
    const ca = $('dt-canvas-area');
    if (!ca) return;
    ca.addEventListener('wheel', e => {
      e.preventDefault();
      if (S._textEditing) return;
      const delta = e.deltaY > 0 ? 0.92 : 1.08; /* zoom out / zoom in */
      const newZoom = Math.min(2.5, Math.max(0.85, S.zoom * delta));
      if (newZoom === S.zoom) return;

      /* Anchor on PA center — keep decoration area centered */
      const caRect = ca.getBoundingClientRect();
      const wrapNatLeft = (mWrap?.getBoundingClientRect().left || 0) - (S._panX || 0);
      const wrapNatTop  = (mWrap?.getBoundingClientRect().top  || 0) - (S._panY || 0);
      const paCX = S.pa.x + S.pa.w / 2;
      const paCY = S.pa.y + S.pa.h / 2;
      const screenCX = caRect.left + caRect.width / 2;
      const screenCY = caRect.top + caRect.height / 2;

      S.zoom = newZoom;
      S._panX = screenCX - wrapNatLeft - paCX * newZoom;
      S._panY = screenCY - wrapNatTop  - paCY * newZoom;

      if (mWrap) {
        mWrap.style.transformOrigin = '0 0';
        mWrap.style.transform = `translate(${S._panX}px,${S._panY}px) scale(${S.zoom})`;
      }
      /* Debounced full re-render for crisp canvas */
      clearTimeout(S._wheelTimer);
      S._wheelTimer = setTimeout(() => { syncCanvas(); syncPrintOverlay(); render(); }, 150);
    }, { passive: false });
  }

  function setupCanvasInteraction() {
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp); /* iOS: interrupted gestures */
    canvas.addEventListener('pointerleave', onUp);
    /* Prevent default touch actions on the canvas */
    canvas.style.touchAction = 'none';
  }

  function toCanvas(e) {
    /* Simplified: maps screen coordinates to canvas layout-space (offsetWidth/Height).
       getBoundingClientRect() accounts for all CSS transforms (zoom, pan).
       This formula works at ANY zoom, render scale, or DPR — no dependencies on buffer size. */
    const r = canvas.getBoundingClientRect();
    const w = mImg.offsetWidth || 1, h = mImg.offsetHeight || 1;
    return { x: (e.clientX - r.left) * w / r.width, y: (e.clientY - r.top) * h / r.height };
  }

  /* Rotation-aware (rotBox is a no-op at rotation 0): a vertical template line
     is grabbable over the tall column it actually paints, not the wide band
     its unrotated rect would occupy. */
  function hitLayer(l, px, py) { const { x, y, w, h } = rotBox(bbox(l), l); return px >= x-8 && px <= x+w+8 && py >= y-8 && py <= y+h+8; }

  function onDown(e) {
    /* Close inline text edit on any canvas tap */
    if (document.getElementById('dt-inline-bar')) { closeInlineTextEdit(); return; }

    /* Block all canvas interactions (pan, zoom, drag) while desktop text editor is open */
    if (S._textEditing) return;

    /* Stale pointer cleanup: if no active interaction and existing pointers
       are older than 300ms, they're stale (missed pointerup). Clear them.
       This prevents false pinch detection while allowing legitimate two-finger gestures. */
    const now = Date.now();
    if (!S.iact) {
      const existing = Object.entries(_pointers);
      if (existing.length > 0 && existing.every(([,p]) => now - p.t > 300)) {
        _pointers = {};
      }
    }
    _pointers[e.pointerId] = { x: e.clientX, y: e.clientY, t: now };
    const pointerCount = Object.keys(_pointers).length;

    /* Two fingers → enter pinch mode, cancel any layer drag */
    if (pointerCount >= 2) {
      /* Dismiss keyboard on any two-finger gesture */
      document.activeElement?.blur();
      const pts = Object.values(_pointers);
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);

      if (isMobile() && S.mode === 'design') {
        const _sl = selLayer();
        if (S.sel && (!(_sl && _sl._noResize) || (_sl && _sl._tplLockId))) {
          /* DESIGN MODE with element: two fingers resize */
          S.iact = { type: 'pinch-resize' };
          const l = _sl;
          const lb = l ? bbox(l) : { x:0, y:0, w:100, h:100 };
          S._pinchStart = { dist, origW: l?.w || 100, origH: l?.h || 100, origFontSize: l?.fontSize || 36,
            centerX: lb.x + lb.w / 2, centerY: lb.y + lb.h / 2 };
          if (l && l._tplLockId && !tplReframing(l) && !l._lockDraggable) {
            const gb = selectionBounds(l);
            S._pinchStart.lockScale = { cx: gb.x + gb.w / 2, cy: gb.y + gb.h / 2, members: lockScaleMembers(l) };
          }
        } else {
          /* DESIGN MODE without element, or a size-locked template photo: ignore pinch */
          S.iact = { type: 'pinch-ignore' };
        }
      } else {
        /* VIEW MODE: two fingers zoom centered on decoration area */
        S.iact = { type: 'pinch' };
        /* Ensure no transition is animating before reading rect */
        if (mWrap) { mWrap.classList.remove('dt-zoom-transition'); mWrap.style.transition = 'none'; }
        const natLeft = mWrap.getBoundingClientRect().left - (S._panX || 0);
        const natTop  = mWrap.getBoundingClientRect().top  - (S._panY || 0);
        const ca = $('dt-canvas-area');
        const caRect = ca ? ca.getBoundingClientRect() : { left: 0, top: 0, width: 400, height: 600 };
        S._pinchStart = {
          dist, zoom: S.zoom, natLeft, natTop,
          paCX: S.pa.x + S.pa.w / 2,
          paCY: S.pa.y + S.pa.h / 2,
          screenCX: caRect.left + caRect.width / 2,
          screenCY: caRect.top + caRect.height / 2,
        };
      }
      /* GPU-promote wrap ONLY for view-mode pinch zoom.
         pinch-resize modifies the layer, not the wrap — touching the wrap
         here causes an origin jump (center→0,0) and is unnecessary overhead. */
      if (S.iact?.type === 'pinch' && mWrap) {
        mWrap.style.willChange = 'transform';
        mWrap.style.transition = 'none';
        mWrap.style.transformOrigin = '0 0';
      }
      S._pinchFrameCount = 0;
      canvas.style.cursor = 'default';
      return;
    }

    const { x, y } = toCanvas(e);
    const cur = selLayer();

    /* Check for corner handle hit on desktop (resize) */
    if (cur && !isMobile()) {
      const h = hitHandle(cur, x, y);
      if (h) {
        const b = (cur._tplLockId && !tplReframing(cur) && !cur._lockDraggable) ? selectionBounds(cur) : bbox(cur);
        S.iact = {
          type: 'resize', handle: h,
          sx: x, sy: y,
          ox: b.x, oy: b.y, ow: b.w, oh: b.h,
          ar: b.w / Math.max(b.h, 1),
          initFontSize: cur.type === 'text' ? cur.fontSize : null,
          initH: b.h,
        };
        if (cur._tplLockId && !tplReframing(cur) && !cur._lockDraggable) {
          S.iact.lockScale = {
            cx: b.x + b.w / 2, cy: b.y + b.h / 2,
            d0: Math.max(8, Math.hypot(x - (b.x + b.w / 2), y - (b.y + b.h / 2))),
            members: lockScaleMembers(cur),
          };
        }
        canvas.style.cursor = HANDLE_CURSORS[h] || 'default';
        return;
      }
    }

    /* Deferred selection: don't select on pointerdown.
       On mobile, immediate selection causes accidental selects during two-finger zoom.
       Instead: record the hit, wait for gesture classification.
       - If finger lifts without moving: tap → select (in onUp)
       - If finger moves > threshold: drag → select + drag (in onMove)
       - If second finger arrives: pinch → no selection */
    let found = null;
    if (isMobile()) {
      /* #6: prefer the SMALLEST layer under the tap, not the top-most — a large
         decoration can no longer steal taps aimed at small text beneath it.
         Top-most seeds the pick; a lower layer wins only if meaningfully smaller. */
      let bestArea = Infinity;
      for (let i = layers().length - 1; i >= 0; i--) {
        const L = layers()[i];
        if (L.visible === false || L.isBackdrop) continue;
        if (!hitLayer(L, x, y)) continue;
        const b = bbox(L), area = Math.max(1, b.w * b.h);
        if (!found || area < bestArea * 0.92) { found = L; bestArea = area; }
      }
    } else {
      for (let i = layers().length - 1; i >= 0; i--) { if (layers()[i].visible === false || layers()[i].isBackdrop) continue; if (hitLayer(layers()[i], x, y)) { found = layers()[i]; break; } }
    }
    /* #3: mobile edit mode locks the selection — tap Done first, then pick
       another layer. (Members of the same locked template are exempt.) */
    if (isMobile() && S.mode === 'design' && found && S.sel && found.id !== S.sel) {
      const _curL = layers().find(q => q.id === S.sel);
      const _sameLock = _curL && _curL._tplLockId && found._tplLockId === _curL._tplLockId;
      if (!_sameLock) found = null;
    }

    /* Transient reframe: a press anywhere other than the reframing photo commits + exits
       (tap empty canvas, or tap another element). Handle-grabs on the reframing photo
       returned earlier above, so resizing it does not exit. */
    const _rfBd = layers().find(b => b.isBackdrop && b._linked === false);
    if (_rfBd) {
      const _rfPhoto = layers().find(p => !p.isBackdrop && p._groupId === _rfBd._groupId);
      if (_rfPhoto && (!found || found.id !== _rfPhoto.id)) S._exitReframe();
    }
    /* Same for a maskless template photo being reframed (no backdrop to find above). */
    const _rfMl = layers().find(p => p._reframing && !p.isBackdrop);
    if (_rfMl && (!found || found.id !== _rfMl.id)) S._exitReframe();

    /* Double-tap detection: check in onDown (not onUp) because
       pending-tap often promotes to drag before pointerup on mobile */
    if (isMobile() && found && S.sel === found.id) {
      const now2 = Date.now();
      if (S._lastTapDown && S._lastTapDown.id === found.id && (now2 - S._lastTapDown.t) < 400) {
        /* Double-tap → edit words (text) or open the monogram editor; styling
           now lives in the action bar's focused popovers, not a props drawer. */
        if (found._tplLockId && !tplReframing(found)) openTemplateEditPanel();
        else if (found.type === 'text') openInlineTextEdit(found);
        else if (found.isMonogram) openMonogramEditor(found);
        else if (found._groupId && !found.isBackdrop) S._enterReframe(found);
        S._lastTapDown = null;
        return; /* don't process further — editing started */
      }
      S._lastTapDown = { id: found.id, t: now2 };
    } else {
      S._lastTapDown = found ? { id: found.id, t: Date.now() } : null;
    }

    const prevSel = S.sel;

    if (isMobile() && S.mode === 'view') {
      /* VIEW MODE mobile: single finger always starts a view-pan.
         If finger lifts without moving, it's a tap → select element. */
      S.iact = {
        type: 'view-pan',
        hitId: found?.id || null,
        startX: e.clientX, startY: e.clientY,
        startPanX: S._panX || 0, startPanY: S._panY || 0,
        started: false,
      };
      if (mWrap) { mWrap.style.willChange = 'transform'; mWrap.style.transition = 'none'; mWrap.style.transformOrigin = '0 0'; }
    } else if (isMobile() && S.mode === 'design') {
      if (found) {
        /* Design mode: drag the element */
        S.sel = found.id;
        const cx = found.centered ? found.x : found.x + found.w/2;
        const cy = found.centered ? found.y : found.y + found.h/2;
        S.iact = { type: 'drag', offX: x - cx, offY: y - cy, startX: x, startY: y, started: false };
        attachLockDrag(S.iact, found);
        updateLayersPanel(); render(); forcePropsRebuild(); syncMobileSelection();
      } else {
        /* Empty canvas in design mode — keep selection for two-finger resize.
           UX-Aug15 (spec §5 A3): a plain single-finger TAP here (no move, no second
           finger) deselects on release — see onUp. cx0/cy0 = client coords for the
           movement test. */
        S.iact = { type: 'idle-design', startX: x, startY: y, cx0: e.clientX, cy0: e.clientY };
      }
    } else {
      /* Desktop */
      if (found) {
        S.sel = found.id;
        const cx = found.centered ? found.x : found.x + found.w/2;
        const cy = found.centered ? found.y : found.y + found.h/2;
        S.iact = { type: 'drag', offX: x - cx, offY: y - cy, startX: x, startY: y, started: false };
        attachLockDrag(S.iact, found);
        canvas.style.cursor = 'grabbing';
      } else {
        S.sel = null; S.iact = null;
        canvas.style.cursor = 'default';
      }
      updateLayersPanel(); render();
      if (S.sel !== prevSel) { forcePropsRebuild(); syncMobileSelection(); }
    }
  }

  function onMove(e) {
    if (_pointers[e.pointerId]) _pointers[e.pointerId] = { x: e.clientX, y: e.clientY, t: Date.now() };
    const pointerCount = Object.keys(_pointers).length;

    /* ── DESIGN MODE: two-finger resize element ───── */
    if (S.iact?.type === 'pinch-resize' && pointerCount >= 2) {
      const pts = Object.values(_pointers);
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      const ps = S._pinchStart;
      const scale = dist / ps.dist;
      /* Store latest scale — rAF applies once per frame */
      S._resizePending = scale;
      if (!S._resizeRaf) {
        S._resizeRaf = requestAnimationFrame(() => {
          S._resizeRaf = null;
          const sc = S._resizePending; if (!sc) return;
          const l = selLayer(); if (!l) return;
          /* TEMPLATE LOCK: pinch scales the whole template */
          if (S._pinchStart && S._pinchStart.lockScale) {
            applyLockScale(S._pinchStart.lockScale, sc);
            render();
            return;
          }
          const nw = Math.max(MIN_DIM, ps.origW * sc), nh = Math.max(MIN_DIM, ps.origH * sc);
          const bd = (!l.isBackdrop && l._groupId)
            ? layers().find(b => b.isBackdrop && b._groupId === l._groupId) : null;
          if (bd && bd._linked === false && bd._maskMarkup) {
            /* Unlinked MASK reframe: zoom about the FIXED mask centre, then clamp to cover. */
            const acx = bd.centered ? bd.x : bd.x + bd.w / 2, acy = bd.centered ? bd.y : bd.y + bd.h / 2;
            const ncx = acx + (ps.centerX - acx) * sc, ncy = acy + (ps.centerY - acy) * sc;
            l.w = nw; l.h = nh;
            if (l.centered) { l.x = ncx; l.y = ncy; } else { l.x = ncx - nw / 2; l.y = ncy - nh / 2; }
            clampPhotoCoversMask(l, bd);
          } else {
            const cx = ps.centerX, cy = ps.centerY;
            l.w = nw; l.h = nh;
            if (l.type === 'text') l.fontSize = Math.max(6, Math.round(ps.origFontSize * sc));
            if (l.centered) { l.x = cx; l.y = cy; } else { l.x = cx - nw / 2; l.y = cy - nh / 2; }
          }
          render();
        });
      }
      return;
    }

    /* ── VIEW MODE: free pinch zoom + pan ────────────── */
    if (S.iact?.type === 'pinch' && pointerCount >= 2) {
      const pts = Object.values(_pointers);
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      const ps = S._pinchStart;
      const newZoom = Math.min(2.5, Math.max(0.85, ps.zoom * (dist / ps.dist)));
      /* Zoom anchored on PA center — decoration area stays centered */
      S._pinchPending = {
        zoom: newZoom,
        panX: ps.screenCX - ps.natLeft - ps.paCX * newZoom,
        panY: ps.screenCY - ps.natTop  - ps.paCY * newZoom,
      };
      if (!S._pinchRaf) {
        S._pinchRaf = requestAnimationFrame(() => {
          S._pinchRaf = null;
          const p = S._pinchPending; if (!p) return;
          S.zoom = p.zoom; S._panX = p.panX; S._panY = p.panY;
          liveView(p.zoom, p.panX, p.panY);
        });
      }
      return;
    }

    /* ── Layer drag / resize ────────────────────────── */
    const { x, y } = toCanvas(e);

    /* View-pan: single finger pans the view in view mode */
    if (S.iact?.type === 'view-pan' && pointerCount <= 1) {
      const dx = e.clientX - S.iact.startX;
      const dy = e.clientY - S.iact.startY;
      if (!S.iact.started && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      S.iact.started = true;
      /* Store latest values — rAF applies once per frame */
      S._panPending = {
        panX: S.iact.startPanX + dx,
        panY: S.iact.startPanY + dy,
      };
      if (!S._panRaf) {
        S._panRaf = requestAnimationFrame(() => {
          S._panRaf = null;
          const p = S._panPending; if (!p) return;
          S._panX = p.panX; S._panY = p.panY;
          liveView(S.zoom, S._panX, S._panY);
        });
      }
      return;
    }

    const cur = selLayer();

    if (!S.iact || !cur) {
      if (cur && !isMobile()) {
        const h = hitHandle(cur, x, y);
        canvas.style.cursor = h ? HANDLE_CURSORS[h] : (hitLayer(cur, x, y) ? 'move' : 'default');
      } else if (cur) {
        canvas.style.cursor = hitLayer(cur, x, y) ? 'move' : 'default';
      }
      return;
    }

    if (S.iact.type === 'drag') {
      if (!S.iact.started) {
        if (Math.hypot(x - S.iact.startX, y - S.iact.startY) < DRAG_THRESHOLD) return;
        S.iact.started = true;
      }
      let nx = x - S.iact.offX, ny = y - S.iact.offY;
      /* Snap guides — adjust position if near a snap target */
      if (S.snapEnabled) {
        const snap = computeSnap(cur, nx, ny);
        nx = snap.x; ny = snap.y;
        S._snapLines = snap.lines;
      } else { S._snapLines = []; }
      if (cur.centered) { cur.x = nx; cur.y = ny; } else { cur.x = nx - cur.w/2; cur.y = ny - cur.h/2; }
      /* UNLINKED reframe: dragging pans the photo within a frozen mask — clamp so it
         always covers the mask (no gap). Linked groups move via sync, no clamp needed. */
      if (cur._groupId && !cur.isBackdrop) {
        const bd = layers().find(b => b.isBackdrop && b._groupId === cur._groupId);
        if (bd && bd._linked === false && bd._maskMarkup) clampPhotoCoversMask(cur, bd);
      }
      /* TEMPLATE LOCK: the whole template moves with the dragged member */
      if (S.iact.lockPeers) {
        const ccx2 = cur.centered ? cur.x : cur.x + cur.w / 2;
        const ccy2 = cur.centered ? cur.y : cur.y + cur.h / 2;
        S.iact.lockPeers.forEach(p => {
          const ncx = ccx2 + p.dx, ncy = ccy2 + p.dy;
          if (p.l.centered) { p.l.x = ncx; p.l.y = ncy; } else { p.l.x = ncx - p.l.w / 2; p.l.y = ncy - p.l.h / 2; }
        });
      }
    } else if (S.iact.type === 'resize') {
      if (S.iact.lockScale) {
        const ls = S.iact.lockScale;
        applyLockScale(ls, Math.hypot(x - ls.cx, y - ls.cy) / ls.d0);
      } else doResize(cur, x, y);
       /* update slider during handle drag */
    }
    render();
  }

  function onUp(e) {
    S._snapLines = [];
    delete _pointers[e?.pointerId];
    const remaining = Object.keys(_pointers).length;

    /* If dropping from 2 fingers to 1, clear pinch state */
    if ((S.iact?.type === 'pinch' || S.iact?.type === 'pinch-resize' || S.iact?.type === 'pinch-ignore') && remaining < 2) {
      if (S.iact?.type === 'pinch-resize') pushHistory();
      /* Cancel any pending rAF + remove GPU promotion */
      if (S._pinchRaf) { cancelAnimationFrame(S._pinchRaf); S._pinchRaf = null; }
      if (S._resizeRaf) { cancelAnimationFrame(S._resizeRaf); S._resizeRaf = null; }
      if (S._panRaf) { cancelAnimationFrame(S._panRaf); S._panRaf = null; }
      S._pinchPending = null; S._resizePending = null; S._panPending = null;
      /* Apply final values if pending */
      if (mWrap) { mWrap.style.willChange = ''; mWrap.style.transition = ''; }
      syncCanvas(); syncPrintOverlay(); render();
      S.iact = null;
      /* Clear ALL pointers when exiting pinch — prevents stale entries */
      if (remaining === 0) _pointers = {};
      return;
    }

    /* View-pan that ended without moving = tap → select element */
    if (S.iact?.type === 'view-pan' && !S.iact.started && S.iact.hitId) {
      S.sel = S.iact.hitId;
      updateLayersPanel(); render();
      forcePropsRebuild(); syncMobileSelection();
    }
    if (S.iact?.type === 'view-pan') {
      if (mWrap) { mWrap.style.willChange = ''; mWrap.style.transition = ''; }
      S.iact = null;
    }
    if (S.iact?.type === 'resize' || S.iact?.started) pushHistory();
    /* UX-Aug15 (spec §5 A3): tap on empty canvas while a layer is selected = deselect
       (the primary "finish" gesture; ✓ Done in the bar is the labeled one). Only when
       it was a real tap (< 8px, single finger), not during inline text edit or template
       reframe. Deselecting exits design mode via syncMobileSelection. */
    if (S.iact?.type === 'idle-design' && remaining === 0 && isMobile() && S.mode === 'design' && S.sel
        && e && typeof S.iact.cx0 === 'number' && Math.hypot((e.clientX || 0) - S.iact.cx0, (e.clientY || 0) - S.iact.cy0) < 8
        && !S._inlineEditLayer && !(typeof tplReframing === 'function' && tplReframing(selLayer()))) {
      S.iact = null; S.sel = null;
      updateLayersPanel(); render(); forcePropsRebuild(); syncMobileSelection();
      return;
    }
    if (S.iact?.type !== 'pinch' && S.iact?.type !== 'pinch-resize' && S.iact?.type !== 'pinch-ignore' && S.iact?.type !== 'idle-design') S.iact = null;
    S._snapLines = [];
    canvas.style.cursor = 'default';
    render(); /* redraw to clear snap guide lines */

    /* Safety: if no pointers remain, clear the dictionary entirely */
    if (remaining === 0) _pointers = {};
  }

  /**
   * Set zoom with pan limits — image edges can't cross canvas area edges (Option C).
   */
  function clampAndSetZoom(z, px, py) {
    const ca = $('dt-canvas-area');
    if (ca && mImg.offsetWidth) {
      const imgW = mImg.offsetWidth * z;
      const imgH = mImg.offsetHeight * z;
      const caW = ca.offsetWidth;
      const caH = ca.offsetHeight;
      /* When using transformOrigin 0 0 with pan, the wrap's top-left moves by pan.
         With center center and no pan, the image is centered.
         Limit: image edges can't go past canvas area edges. */
      const maxPanX = Math.max(0, (imgW - caW) / 2 + caW * 0.1);
      const maxPanY = Math.max(0, (imgH - caH) / 2 + caH * 0.1);
      px = Math.max(-maxPanX, Math.min(maxPanX, px));
      py = Math.max(-maxPanY, Math.min(maxPanY, py));
    }
    setZoom(z, px, py);
  }

  /**
   * Proportional resize from corner handle.
   * All 4 corners maintain aspect ratio.
   */
  function doResize(l, mx, my) {
    const { handle:h, sx, sy, ox, oy, ow, oh, ar, initFontSize, initH } = S.iact;
    const dx = mx - sx, dy = my - sy;

    /* Grouped photo with a backdrop. */
    if (l._groupId && !l.isBackdrop && typeof layers === 'function') {
      const bd = layers().find(b => b.isBackdrop && b._groupId === l._groupId);
      if (bd) {
        if (bd._linked !== false || !bd._maskMarkup) {
          /* LINKED, or NON-MASK reframe: scale the photo around its own centre. When
             linked the mask follows via syncGroupedBackdrops; when reframing a non-mask
             backdrop, sync skips it so the photo resizes freely over a fixed backdrop. */
          const ocx = l.centered ? ox : ox + ow / 2, ocy = l.centered ? oy : oy + oh / 2;
          const d0 = Math.hypot(sx - ocx, sy - ocy) || 1;
          const f = Math.hypot(mx - ocx, my - ocy) / d0;
          const fw = Math.max(MIN_DIM, ow * f), fh = fw / (ar || 1);
          l.w = fw; l.h = fh;
          if (l.centered) { l.x = ocx; l.y = ocy; } else { l.x = ocx - fw / 2; l.y = ocy - fh / 2; }
        } else {
          /* UNLINKED (reframe): zoom the photo about the FIXED mask centre, preserving
             framing, then clamp so it still covers the mask (min-scale floor). */
          const acx = bd.centered ? bd.x : bd.x + bd.w / 2, acy = bd.centered ? bd.y : bd.y + bd.h / 2;
          const d0 = Math.hypot(sx - acx, sy - acy) || 1;
          const f = Math.hypot(mx - acx, my - acy) / d0;
          const fw = Math.max(MIN_DIM, ow * f), fh = fw / (ar || 1);
          const opcx = l.centered ? ox : ox + ow / 2, opcy = l.centered ? oy : oy + oh / 2;
          const ncx = acx + (opcx - acx) * f, ncy = acy + (opcy - acy) * f;
          l.w = fw; l.h = fh;
          if (l.centered) { l.x = ncx; l.y = ncy; } else { l.x = ncx - fw / 2; l.y = ncy - fh / 2; }
          clampPhotoCoversMask(l, bd);
        }
        return;
      }
    }

    /* Determine new dimensions based on which corner */
    let nw = ow, nh = oh, nx = ox, ny = oy;
    if (h.includes('l')) { nx = ox + dx; nw = ow - dx; }
    if (h.includes('r')) { nw = ow + dx; }
    if (h.includes('t')) { ny = oy + dy; nh = oh - dy; }
    if (h.includes('b')) { nh = oh + dy; }

    /* Force proportional — use whichever delta is larger */
    const dw = nw - ow, dh = nh - oh;
    let fw, fh;
    if (Math.abs(dw) >= Math.abs(dh)) { fw = Math.max(MIN_DIM, nw); fh = fw / ar; }
    else { fh = Math.max(MIN_DIM, nh); fw = fh * ar; }

    /* Reposition for left/top handles to keep opposite corner anchored */
    if (h.includes('l')) nx = ox + ow - fw;
    if (h.includes('t')) ny = oy + oh - fh;

    l.w = fw; l.h = fh;
    if (l.centered) { l.x = nx + fw/2; l.y = ny + fh/2; }
    else { l.x = nx; l.y = ny; }

    /* Text — scale font size with height, measure multiline correctly */
    if (l.type === 'text' && initFontSize && initH) {
      l.fontSize = Math.max(6, Math.round(initFontSize * (fh / initH)));
      measureText(l); /* handles multiline split, ink bounds, and sets l.w / l.h */
      if (l.centered) { l.x = nx + l.w/2; l.y = ny + l.h/2; }
      else { l.x = nx; l.y = ny; }
    }
  }

  /* ══════════════════════════════════════════════════════════════════
   * SNAP GUIDES — alignment guides during drag
   * ══════════════════════════════════════════════════════════════════ */
  const SNAP_THRESHOLD = 5;

  function computeSnap(cur, nx, ny) {
    const lines = [];
    const b = {
      x: cur.centered ? nx - cur.w/2 : nx,
      y: cur.centered ? ny - cur.h/2 : ny,
      w: cur.w, h: cur.h,
    };
    const cx = b.x + b.w/2, cy = b.y + b.h/2;
    const pa = S.pa;
    let snappedX = nx, snappedY = ny;

    /* Build snap targets: PA center, PA edges, other layer edges/centers */
    const targetsX = [
      { pos: pa.x + pa.w/2, label: 'pa-cx' },
      { pos: pa.x, label: 'pa-l' },
      { pos: pa.x + pa.w, label: 'pa-r' },
    ];
    const targetsY = [
      { pos: pa.y + pa.h/2, label: 'pa-cy' },
      { pos: pa.y, label: 'pa-t' },
      { pos: pa.y + pa.h, label: 'pa-b' },
    ];

    /* Add other visible layers as snap targets */
    layers().forEach(l => {
      if (l.id === cur.id || l.visible === false) return;
      const lb = bbox(l);
      targetsX.push({ pos: lb.x + lb.w/2, label: 'l-cx' });
      targetsX.push({ pos: lb.x, label: 'l-l' });
      targetsX.push({ pos: lb.x + lb.w, label: 'l-r' });
      targetsY.push({ pos: lb.y + lb.h/2, label: 'l-cy' });
      targetsY.push({ pos: lb.y, label: 'l-t' });
      targetsY.push({ pos: lb.y + lb.h, label: 'l-b' });
    });

    /* Check center-to-target X */
    let bestDx = SNAP_THRESHOLD + 1;
    targetsX.forEach(t => {
      const d = Math.abs(cx - t.pos);
      if (d < SNAP_THRESHOLD && d < bestDx) {
        bestDx = d;
        snappedX = cur.centered ? t.pos : t.pos - cur.w/2;
        lines.push({ axis: 'x', pos: t.pos });
      }
    });
    /* Also check left-edge and right-edge alignment */
    targetsX.forEach(t => {
      const dL = Math.abs(b.x - t.pos);
      const dR = Math.abs(b.x + b.w - t.pos);
      if (dL < SNAP_THRESHOLD && dL < bestDx) {
        bestDx = dL;
        snappedX = cur.centered ? t.pos + cur.w/2 : t.pos;
        lines.push({ axis: 'x', pos: t.pos });
      }
      if (dR < SNAP_THRESHOLD && dR < bestDx) {
        bestDx = dR;
        snappedX = cur.centered ? t.pos - cur.w/2 : t.pos - cur.w;
        lines.push({ axis: 'x', pos: t.pos });
      }
    });

    /* Check center-to-target Y */
    let bestDy = SNAP_THRESHOLD + 1;
    targetsY.forEach(t => {
      const d = Math.abs(cy - t.pos);
      if (d < SNAP_THRESHOLD && d < bestDy) {
        bestDy = d;
        snappedY = cur.centered ? t.pos : t.pos - cur.h/2;
        lines.push({ axis: 'y', pos: t.pos });
      }
    });
    /* Also check top-edge and bottom-edge alignment */
    targetsY.forEach(t => {
      const dT = Math.abs(b.y - t.pos);
      const dB = Math.abs(b.y + b.h - t.pos);
      if (dT < SNAP_THRESHOLD && dT < bestDy) {
        bestDy = dT;
        snappedY = cur.centered ? t.pos + cur.h/2 : t.pos;
        lines.push({ axis: 'y', pos: t.pos });
      }
      if (dB < SNAP_THRESHOLD && dB < bestDy) {
        bestDy = dB;
        snappedY = cur.centered ? t.pos - cur.h/2 : t.pos - cur.h;
        lines.push({ axis: 'y', pos: t.pos });
      }
    });

    /* Keep only the best snap per axis */
    const bestLines = [];
    if (bestDx <= SNAP_THRESHOLD) bestLines.push(lines.filter(l => l.axis === 'x').pop());
    if (bestDy <= SNAP_THRESHOLD) bestLines.push(lines.filter(l => l.axis === 'y').pop());

    return { x: snappedX, y: snappedY, lines: bestLines.filter(Boolean) };
  }

  function drawSnapLines() {
    if (!S._snapLines.length) return;
    ctx.save();
    ctx.strokeStyle = '#2196F3';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.globalAlpha = 0.8;
    S._snapLines.forEach(sl => {
      ctx.beginPath();
      if (sl.axis === 'x') {
        ctx.moveTo(sl.pos, S.pa.y - 10);
        ctx.lineTo(sl.pos, S.pa.y + S.pa.h + 10);
      } else {
        ctx.moveTo(S.pa.x - 10, sl.pos);
        ctx.lineTo(S.pa.x + S.pa.w + 10, sl.pos);
      }
      ctx.stroke();
    });
    ctx.restore();
  }

  function toggleSnap() {
    S.snapEnabled = !S.snapEnabled;
    try { localStorage.setItem('dt_snap', S.snapEnabled ? 'on' : 'off'); } catch (_) {}
    /* Update all toggle buttons */
    $$('.dt-snap-toggle').forEach(b => b.classList.toggle('active', S.snapEnabled));
    try { if (S.snapEnabled && !localStorage.getItem('dt_tip_snap')) showTip('snap', 'Elements will snap to alignment guides as you drag.', null); } catch (_) {}
  }

  /* ══════════════════════════════════════════════════════════════════
   * INLINE TEXT EDITING
   * Desktop/tablet: contenteditable div inside canvas-wrap (in-place editing)
   * Mobile: bottom bar input (openInlineTextEdit / closeInlineTextEdit above)
   * ══════════════════════════════════════════════════════════════════ */
  let _inlineEditDiv = null;

  function setupInlineTextEdit() {
    /* Desktop: native dblclick on canvas enters edit mode */
    canvas.addEventListener('dblclick', e => {
      if (isMobile()) return;
      const { x, y } = toCanvas(e);
      const cur = selLayer();
      if (cur && cur._tplLockId && !tplReframing(cur) && hitLayer(cur, x, y)) {
        openTemplateEditPanel();
        e.preventDefault();
      } else if (cur?.type === 'text' && hitLayer(cur, x, y)) {
        enterInlineEdit(cur, false);
        e.preventDefault();
      } else if (cur && cur._groupId && !cur.isBackdrop && hitLayer(cur, x, y)) {
        if (S._enterReframe(cur)) e.preventDefault();
      } else {
        /* E3 — double-click an EMPTY spot inside the print area: add text there */
        let hitAny = null;
        for (let i = layers().length - 1; i >= 0; i--) { const L = layers()[i]; if (L.visible === false || L.isBackdrop) continue; if (hitLayer(L, x, y)) { hitAny = L; break; } }
        const pa = S.pa;
        if (!hitAny && x >= pa.x && x <= pa.x + pa.w && y >= pa.y && y <= pa.y + pa.h) {
          addTextLayerAndEdit('Your Text', { x: x, y: y });
          e.preventDefault();
        }
      }
    });
    /* Mobile double-tap is handled in onDown (line ~3200) via
       openInlineTextEdit — no separate handler needed here. */
  }

  /* Geometry for the inline editor. The div must land exactly on the canvas
     text. Horizontal: canvas (textAlign:center) and CSS (text-align:center)
     both centre each line by its ADVANCE width — sizing the div by ink width
     (l.w) let it auto-grow around the wider advance box and drift the text
     sideways. So the div gets an explicit advance-based width centred on l.x.
     Vertical: anchored to the line-frame box (totalH) around l.y, matching
     paintLayer's line layout; the residual baseline mismatch (canvas
     'middle' anchoring vs CSS line-box centring) is corrected after mount
     by _alignInlineEditBaseline. Note paintLayer anchors text at l.x/l.y
     regardless of l.centered, so the editor does too. */
  function _inlineEditGeom(l) {
    const lines = (l.text || '').split('\n');
    const lineH = (l.fontSize || 36) * (l.lineHeight || 1.25);
    const frameH = lines.length * lineH;
    ctx.save(); ctx.font = fontStr(l);
    let adv = 0;
    lines.forEach(line => { const w = ctx.measureText(line).width; if (w > adv) adv = w; });
    ctx.restore();
    const w = Math.max(40, Math.ceil(adv) + 2);
    return { w, frameH, left: l.x - w / 2, top: l.y - frameH / 2 };
  }

  /* Nudge the div so its first-line baseline sits exactly on the canvas
     text baseline. Canvas: each line's em-middle is anchored at the line
     centre; (alphabetic ascent − middle ascent) of the SAME string in the
     SAME engine gives the em-middle→baseline distance. DOM: a zero-size
     inline-block probe's bottom sits on the rendered baseline — measured,
     not assumed, so font-metric quirks can't reintroduce drift. */
  function _alignInlineEditBaseline(l, div) {
    const lineH = (l.fontSize || 36) * (l.lineHeight || 1.25);
    const frameH = ((l.text || '').split('\n').length) * lineH;
    ctx.save(); ctx.font = fontStr(l);
    ctx.textBaseline = 'alphabetic'; const a = ctx.measureText('Hg').actualBoundingBoxAscent;
    ctx.textBaseline = 'middle';     const m = ctx.measureText('Hg').actualBoundingBoxAscent;
    ctx.restore();
    if (a === undefined || m === undefined) return; /* ancient engine: keep frame alignment */
    const canvasBase = (l.y - frameH / 2) + lineH / 2 + (a - m); /* wrap coords */

    const probe = document.createElement('span');
    probe.style.cssText = 'display:inline-block;width:0;height:0;padding:0;margin:0;border:0;';
    div.insertBefore(probe, div.firstChild);
    const wrapR = mWrap.getBoundingClientRect();
    const scale = (mWrap.offsetWidth ? wrapR.width / mWrap.offsetWidth : 1) || 1; /* zoom transform */
    const domBase = (probe.getBoundingClientRect().bottom - wrapR.top) / scale;
    probe.remove();

    const dy = canvasBase - domBase;
    if (Math.abs(dy) > 0.05) div.style.top = (parseFloat(div.style.top) + dy) + 'px';
  }

  /**
   * Create a contenteditable div inside canvas-wrap, positioned exactly
   * over the text layer. The div IS the editing surface — canvas text is
   * hidden while active so there's no duplication.
   *
   * @param {object} l          The text layer to edit
   * @param {boolean} selectAll If true, pre-select all text (for "Add Text" flow)
   */
  function enterInlineEdit(l, selectAll) {
    exitInlineEdit();
    if (!l || l.type !== 'text') return;

    S._textEditing = true;
    S._inlineEditLayer = l;

    /* Position in canvas-wrap coordinate space (same as layer coords). */
    const g = _inlineEditGeom(l);

    const div = document.createElement('div');
    div.className = 'dt-inline-edit';
    div.contentEditable = 'true';
    div.spellcheck = false;
    /* Set content with line breaks preserved as <br> */
    div.innerHTML = (l.text || '').split('\n')
      .map(line => line.replace(/</g, '&lt;').replace(/>/g, '&gt;'))
      .join('<br>');
    div.style.cssText = `
      position: absolute; z-index: 10;
      left: ${g.left}px; top: ${g.top}px;
      width: ${g.w}px; min-height: ${Math.max(16, g.frameH)}px;
      font-family: '${l.font}', sans-serif;
      font-size: ${l.fontSize || 36}px;
      font-weight: ${l.bold ? 'bold' : 'normal'};
      font-style: ${l.italic ? 'italic' : 'normal'};
      color: ${l.color || '#111111'};
      text-align: center;
      line-height: ${l.lineHeight || 1.25};
      white-space: pre;
      overflow: visible;
      outline: 2px solid var(--dt-coral, #f47e46);
      outline-offset: 6px;
      background: transparent;
      border: none; padding: 0; margin: 0;
      cursor: text;
      box-sizing: content-box;
      caret-color: var(--dt-coral, #f47e46);
    `;
    mWrap.appendChild(div);
    _inlineEditDiv = div;
    _alignInlineEditBaseline(l, div);
    /* If the font arrives after mount, both canvas metrics and DOM layout
       change — re-run the geometry so the editor stays on the text. */
    try {
      document.fonts.load(`${l.fontSize || 36}px '${l.font}'`).then(() => {
        if (_inlineEditDiv === div) syncInlineEdit(l, div);
      });
    } catch (_) {}

    /* Hide the canvas text layer so it doesn't double-render underneath */
    l._inlineHidden = true;
    render();

    /* Focus and optionally select all (for "Add Text" flow) */
    requestAnimationFrame(() => {
      div.focus();
      if (selectAll) {
        const range = document.createRange();
        range.selectNodeContents(div);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        /* Place cursor at end */
        const sel = window.getSelection();
        sel.selectAllChildren(div);
        sel.collapseToEnd();
      }
    });

    /* ── Live resize on input ────────────────────────── */
    div.addEventListener('input', () => syncInlineEdit(l, div));

    /* ── Enter inserts line break, Escape commits ────── */
    div.addEventListener('keydown', e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        /* Insert a clean <br> instead of letting the browser create <div> blocks */
        e.preventDefault();
        const sel = window.getSelection();
        if (!sel.rangeCount) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const br = document.createElement('br');
        range.insertNode(br);
        /* Move cursor after the <br> */
        range.setStartAfter(br);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        /* Trigger input sync */
        syncInlineEdit(l, div);
      }
      if (e.key === 'Escape') { e.preventDefault(); exitInlineEdit(); }
    });

    /* Prevent paste from inserting HTML — plain text with newlines only */
    div.addEventListener('paste', e => {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData('text/plain');
      /* Replace \r\n and \r with \n, then insert as HTML with <br> for newlines */
      const clean = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const html = clean.split('\n').map(l => l.replace(/</g, '&lt;').replace(/>/g, '&gt;')).join('<br>');
      document.execCommand('insertHTML', false, html);
    });

    /* ── Click outside to exit ───────────────────────── */
    setTimeout(() => {
      document.addEventListener('mousedown', _inlineClickOutside);
    }, 80);
  }

  /* Sync the layer's text, measure, resize the div, re-render handles */
  function syncInlineEdit(l, div) {
    /* innerText preserves <br> as \n; textContent doesn't */
    l.text = (div.innerText || '').replace(/\u200B/g, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    /* Trim trailing newline that browsers sometimes add */
    if (l.text.endsWith('\n')) l.text = l.text.slice(0, -1);
    l.name = l.text.replace(/\n/g, ' ').slice(0, 25) || 'Text';
    measureText(l);
    l.ar = l.w / (l.h || 1);

    /* Re-run the shared geometry (advance-width box + frame anchor) and
       baseline pass so the editor tracks the canvas text while typing. */
    const g = _inlineEditGeom(l);
    div.style.width = g.w + 'px';
    div.style.minHeight = Math.max(16, g.frameH) + 'px';
    div.style.left = g.left + 'px';
    div.style.top = g.top + 'px';
    _alignInlineEditBaseline(l, div);

    /* Re-render canvas (handles/selection box update, text stays hidden) */
    render();
  }

  function _inlineClickOutside(e) {
    if (_inlineEditDiv && !_inlineEditDiv.contains(e.target)) {
      /* Don't exit if the click is on a properties panel control */
      if (e.target.closest && e.target.closest('.dt-props-panel, .dt-sidebar--right, .dt-mobile-drawer')) return;
      exitInlineEdit();
    }
  }

  function exitInlineEdit() {
    document.removeEventListener('mousedown', _inlineClickOutside);
    S._textEditing = false;

    if (_inlineEditDiv) {
      _inlineEditDiv.remove();
      _inlineEditDiv = null;
    }

    if (S._inlineEditLayer) {
      const l = S._inlineEditLayer;
      l._inlineHidden = false;
      S._inlineEditLayer = null;

      /* Remove empty text layers instead of leaving invisible placeholders */
      if (!l.text.trim()) {
        deleteLayer(l.id);
      } else {
        pushHistory();
        render();
        updateLayersPanel();
        forcePropsRebuild();
      }
    }
  }

  /* ══════════════════════════════════════════════════════════════════
   * DECORATION METHOD HELP BUBBLE
   * Shows "Best For" examples when user switches between engrave/print
   * ══════════════════════════════════════════════════════════════════ */
  function showMethodBubble(method, anchorBtn) {
    if (isMobile()) return;  /* bubble is 280px — doesn't fit on mobile screens */
    document.querySelector('.dt-method-bubble')?.remove();

    const engraveTips = [
      { icon: '<svg viewBox="0 0 40 40" fill="none"><path d="M8 32L16 12L24 28L32 8" stroke="#1a1410" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="22" r="3" fill="#ddd" stroke="#1a1410" stroke-width="1.5"/></svg>', label: 'Line Drawings' },
      { icon: '<svg viewBox="0 0 40 40" fill="none"><rect x="6" y="10" width="28" height="20" rx="3" stroke="#1a1410" stroke-width="1.8"/><circle cx="20" cy="18" r="4" stroke="#1a1410" stroke-width="1.5"/><path d="M12 26h16" stroke="#1a1410" stroke-width="1.5" stroke-linecap="round"/></svg>', label: '1\u20134 Color Logos' },
      { icon: '<svg viewBox="0 0 40 40" fill="none"><path d="M8 28V14a2 2 0 012-2h20a2 2 0 012 2v14" stroke="#1a1410" stroke-width="1.8"/><path d="M12 20h4M12 24h8M24 18h4" stroke="#1a1410" stroke-width="1.5" stroke-linecap="round"/></svg>', label: 'Text & Group Orders' },
    ];
    const printTips = [
      { icon: '<svg viewBox="0 0 40 40" fill="none"><rect x="6" y="6" width="28" height="28" rx="3" fill="#e8f0fe"/><circle cx="16" cy="16" r="4" fill="#f47e46"/><circle cx="26" cy="14" r="3" fill="#4a90d9"/><path d="M6 28l10-8 6 6 6-4 6 6" stroke="#3a7d5c" stroke-width="1.5" fill="none"/></svg>', label: 'Photographs' },
      { icon: '<svg viewBox="0 0 40 40" fill="none"><rect x="6" y="8" width="28" height="24" rx="3" fill="#fef3e8"/><circle cx="16" cy="20" r="5" fill="#f47e46"/><rect x="23" y="14" width="8" height="8" rx="1" fill="#4a90d9"/><path d="M10 28h20" stroke="#1a1410" stroke-width="1.2"/></svg>', label: 'Complex Logos' },
      { icon: '<svg viewBox="0 0 40 40" fill="none"><path d="M10 30C10 14 16 10 20 10s10 4 10 20" stroke="#f47e46" stroke-width="2.5" fill="none"/><circle cx="14" cy="24" r="2" fill="#4a90d9"/><circle cx="26" cy="22" r="2.5" fill="#3a7d5c"/><path d="M18 28l2-6 2 6" fill="#f4c542"/></svg>', label: 'Kids Drawings' },
      { icon: '<svg viewBox="0 0 40 40" fill="none"><text x="20" y="26" text-anchor="middle" font-size="18" font-weight="900" fill="#f47e46" font-family="serif">AB</text><circle cx="20" cy="20" r="15" stroke="#f47e46" stroke-width="1.5" fill="none"/></svg>', label: 'Decorative Text' },
    ];

    const tips = method === 'engrave' ? engraveTips : printTips;
    const bubble = document.createElement('div');
    bubble.className = 'dt-method-bubble';
    bubble.style.cssText = 'position:absolute;z-index:100;top:calc(100% + 12px);left:50%;transform:translateX(-50%);background:#fff;border-radius:14px;box-shadow:0 8px 30px rgba(0,0,0,0.18),0 0 0 1px rgba(0,0,0,0.04);padding:14px 16px;width:280px;font-family:var(--dt-font,system-ui);pointer-events:auto;animation:dtBubbleIn 0.2s ease';
    bubble.innerHTML =
      '<style>@keyframes dtBubbleIn{from{opacity:0;transform:translateX(-50%) translateY(-6px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}</style>' +
      '<div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.06em;color:var(--dt-muted,#9e8878);margin-bottom:10px">Best for</div>' +
      '<div style="display:grid;grid-template-columns:repeat(' + (tips.length > 3 ? 2 : tips.length) + ',1fr);gap:10px">' +
      tips.map(t =>
        '<div style="text-align:center">' +
        '<div style="width:48px;height:48px;margin:0 auto 6px;border-radius:10px;background:var(--dt-bg,#f7f3ee);display:flex;align-items:center;justify-content:center">' + t.icon + '</div>' +
        '<div style="font-size:10.5px;font-weight:700;color:var(--dt-text,#1a1410);line-height:1.3">' + t.label + '</div></div>'
      ).join('') +
      '</div>' +
      '<div style="position:absolute;top:-6px;left:50%;width:12px;height:12px;background:#fff;transform:translateX(-50%) rotate(45deg);box-shadow:-1px -1px 0 rgba(0,0,0,0.04)"></div>';

    if (!anchorBtn) return;   /* single-method badge → no bubble */
    const toggleWrap = anchorBtn.closest('.dt-method-toggle') || anchorBtn.parentElement;
    if (toggleWrap) { toggleWrap.style.position = 'relative'; toggleWrap.appendChild(bubble); }

    const dismiss = () => { bubble.style.opacity = '0'; bubble.style.transition = 'opacity 0.15s'; setTimeout(() => bubble.remove(), 150); };
    setTimeout(dismiss, 5000);
    bubble.addEventListener('click', e => e.stopPropagation());
    setTimeout(() => document.addEventListener('click', dismiss, { once: true }), 200);
  }

  /* ══════════════════════════════════════════════════════════════════
   * WELCOME OVERLAY — first-load design prompt
   * Action cards inside the print area, product visible behind.
   * ══════════════════════════════════════════════════════════════════ */
  function showWelcomeOverlay() {
    /* Roadmap 2 + polish: the full grid greets the first two visits; after
       that the start wizard and idle inspiration carry onboarding (repeat-
       visitor fatigue — the overlay is a door regulars shouldn't re-open). */
    if (S._suppressWelcome) return;   /* a draft/saved design is restoring */
    let _wlc = 0; try { _wlc = parseInt(localStorage.getItem('dt_welcome_count') || '0', 10) || 0; } catch (e) {}
    if (_wlc >= 2) return;
    if (!mWrap) return;
    if (CFG.sides.some(s => S.byView[s]?.length > 0)) return;

    const host = app || document.querySelector('.dt-app') || mWrap;
    const coral = (getComputedStyle(document.documentElement).getPropertyValue('--dt-forest').trim() || '#344E41');
    /* (variable keeps its old name to leave the icon templates untouched —
       decorative icons are FOREST now; saturated coral belongs to CTAs) */

    /* Persona-aware paths — mirror the Start panel cards */
    const paths = [
      { goto: 'templates', title: 'Start from a template', desc: 'Fastest &mdash; edit a finished design',
        icon: '<svg width="30" height="30" viewBox="0 0 20 20" fill="none"><rect x="2.5" y="2.5" width="6" height="6" rx="1.5" stroke="' + coral + '" stroke-width="1.4"/><rect x="11.5" y="2.5" width="6" height="6" rx="1.5" stroke="' + coral + '" stroke-width="1.4"/><rect x="2.5" y="11.5" width="6" height="6" rx="1.5" stroke="' + coral + '" stroke-width="1.4"/><rect x="11.5" y="11.5" width="6" height="6" rx="1.5" stroke="' + coral + '" stroke-width="1.4"/></svg>' },
      { goto: 'upload', mode: 'logo', title: 'Upload my logo', desc: 'Brand it &mdash; company gifts &amp; team swag',
        icon: '<svg width="30" height="30" viewBox="0 0 20 20" fill="none"><path d="M10 3V13M10 3L6 7M10 3L14 7" stroke="' + coral + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 13V15C3 16.1 3.9 17 5 17H15C16.1 17 17 16.1 17 15V13" stroke="' + coral + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' },
      { goto: 'text', title: 'Add a name or text', desc: 'The personal touch &mdash; names &amp; dates',
        icon: '<svg width="30" height="30" viewBox="0 0 20 20" fill="none"><path d="M4 5V4H16V5" stroke="' + coral + '" stroke-width="1.5" stroke-linecap="round"/><path d="M10 4V16" stroke="' + coral + '" stroke-width="1.5" stroke-linecap="round"/><path d="M7 16H13" stroke="' + coral + '" stroke-width="1.5" stroke-linecap="round"/></svg>' },
      { goto: 'monogram', title: 'Create a monogram', desc: 'Timeless &amp; elegant &mdash; classic initials',
        icon: '<svg width="30" height="30" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8.4" stroke="' + coral + '" stroke-width="1.1"/><text x="7.2" y="13.1" text-anchor="middle" font-size="8" font-weight="700" font-style="italic" font-family="Georgia,serif" fill="' + coral + '">A</text><text x="12.6" y="13.1" text-anchor="middle" font-size="8" font-weight="700" font-style="italic" font-family="Georgia,serif" fill="' + coral + '">B</text></svg>' },
      { goto: 'photo', title: 'Add a photo', desc: 'A favorite photo, your way',
        icon: '<svg width="30" height="30" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="16" height="12" rx="2.5" stroke="' + coral + '" stroke-width="1.4"/><path d="M6.5 5L7.6 3H12.4L13.5 5" stroke="' + coral + '" stroke-width="1.4" stroke-linejoin="round"/><circle cx="10" cy="11" r="3" stroke="' + coral + '" stroke-width="1.4"/></svg>' },
      { goto: 'bulk', title: 'Group order', desc: 'Many names &mdash; teams, weddings &amp; events',
        icon: '<svg width="30" height="30" viewBox="0 0 20 20" fill="none"><circle cx="7" cy="7" r="2.5" stroke="' + coral + '" stroke-width="1.4"/><path d="M2.5 16C2.5 13.5 4.5 12 7 12C9.5 12 11.5 13.5 11.5 16" stroke="' + coral + '" stroke-width="1.4" stroke-linecap="round"/><circle cx="14" cy="7.5" r="2" stroke="' + coral + '" stroke-width="1.4"/><path d="M13 12.2C15.3 12.5 17 14 17 16" stroke="' + coral + '" stroke-width="1.4" stroke-linecap="round"/></svg>' },
    ];

    const overlay = document.createElement('div');
    overlay.id = 'dt-welcome-overlay';
    overlay.style.cssText = 'position:absolute;z-index:300;inset:0;display:flex;align-items:center;justify-content:center;padding:20px;';
    overlay.innerHTML =
      '<style>' +
      '@keyframes dtWelcomeIn{from{opacity:0}to{opacity:1}}' +
      '@keyframes dtWelcomeCard{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}' +
      '#dt-welcome-overlay{animation:dtWelcomeIn 0.25s ease;background:linear-gradient(180deg,rgba(255,255,255,0.97),rgba(247,243,238,0.97));backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);border-top:3px solid var(--dt-forest,#344E41)}' +
      '.dt-wl{width:100%;max-width:600px;max-height:100%;overflow-y:auto;text-align:center;font-family:var(--dt-font,system-ui)}' +
      '.dt-wl__logo{margin-bottom:10px}' +
      '.dt-wl__title{font-family:var(--dt-font-heading,inherit);font-size:24px;font-weight:800;color:var(--dt-text,#2c2825);margin-bottom:4px}' +
      '.dt-wl__sub{font-size:13px;color:var(--dt-muted,#8a8077);margin-bottom:20px}' +
      '.dt-wl__grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}' +
      '.dt-wl-card{display:flex;flex-direction:column;align-items:center;gap:8px;padding:18px 12px;border-radius:14px;cursor:pointer;background:#fff;border:1.5px solid var(--dt-border,#d8dcde);transition:all 0.14s ease;animation:dtWelcomeCard 0.3s ease both}' +
      '.dt-wl-card:hover{border-color:' + coral + ';box-shadow:0 6px 18px rgba(52,78,65,0.12);transform:translateY(-2px)}' +
      '.dt-wl-card:active{transform:scale(0.98)}' +
      '.dt-wl-card__title{font-size:13px;font-weight:700;color:var(--dt-text,#2c2825);line-height:1.2}' +
      '.dt-wl-card__desc{font-size:10.5px;color:var(--dt-muted,#8a8077);line-height:1.35}' +
      '.dt-wl__skip{margin-bottom:16px;border:none;background:transparent;color:var(--dt-muted,#8a8077);font-family:inherit;font-size:12px;cursor:pointer;min-height:44px}' +
      '.dt-wl__skip:hover{color:var(--dt-text,#2c2825)}' +
      '@media(max-width:1024px),(pointer:coarse){.dt-wl__grid{grid-template-columns:1fr 1fr}.dt-wl__title{font-size:20px}}' +
      '</style>' +
      '<div class="dt-wl">' +
      (function(){var l=document.querySelector('.dt-header__logo');return l?'<div class="dt-wl__logo"><img src="'+l.src+'" alt="'+((l.alt||''))+'" style="max-height:36px;width:auto"></div>':'';})() +
      '<div class="dt-wl__title">What are we making today?</div>' +
      '<div class="dt-wl__sub">Pick a starting point &mdash; you can change anything later.</div>' +
      '<button class="dt-wl__skip" id="dt-wl-skip">Skip &mdash; just open the tool</button>' +
      '<div class="dt-wl__grid">' +
      paths.map((p, i) =>
        '<div class="dt-wl-card" data-goto="' + p.goto + '"' + (p.mode ? ' data-mode="' + p.mode + '"' : '') + ' style="animation-delay:' + (i * 0.04) + 's">' +
        '<div style="height:30px">' + p.icon + '</div>' +
        '<div class="dt-wl-card__title">' + p.title + '</div>' +
        '<div class="dt-wl-card__desc">' + p.desc + '</div></div>'
      ).join('') +
      '</div>' +
      inspirationSectionHtml() +
      '</div>';

    host.appendChild(overlay);
    populateInspiration(overlay, 6);   /* keep the overlay tidy: one row of customer photos */
    try { localStorage.setItem('dt_welcome_count', String(_wlc + 1)); } catch (e) {}

    function dismiss() {
      overlay.style.opacity = '0'; overlay.style.transition = 'opacity 0.2s';
      setTimeout(() => overlay.remove(), 200);
    }

    overlay.querySelectorAll('.dt-wl-card').forEach(card => {
      card.addEventListener('click', () => {
        dismiss();
        routeToTool(card.dataset.goto, card.dataset.mode);
      });
    });
    overlay.querySelector('#dt-wl-skip').addEventListener('click', dismiss);
  }

  /* ── CRO #23 — first-run coach marks: 3 tiny tap-through steps ──
     Waits for the welcome overlay to clear, then points at
     (1) the Start tab, (2) the canvas, (3) the Add-to-cart CTA. ── */
  function maybeStartTour(tries) {
    if (localStorage.getItem('dt_tour_done')) return;
    if (document.getElementById('dt-welcome-overlay')) {
      if ((tries || 0) < 60) setTimeout(function () { maybeStartTour((tries || 0) + 1); }, 700);
      return;
    }
    startCoachTour();
  }

  function startCoachTour() {
    return; /* disabled for now */
    if (document.getElementById('dt-tour')) return;
    /* Roadmap 2: the start wizard owns "pick a starting point" — the tour
       now only teaches the canvas gestures and the CTA. */
    var steps = [
      { sel: '#dt-canvas-wrap',
        text: 'Drag anything to move it \u2014 double-tap text to edit it.' },
      { sel: isMobile() ? '#dt-add-cart-mobile' : '#dt-add-cart',
        text: 'When your design is ready, add it to your cart here.' },
    ];
    var i = 0;
    var ov = document.createElement('div');
    ov.id = 'dt-tour';
    ov.innerHTML = '<div class="dt-tour-tip" role="dialog" aria-live="polite">' +
      '<div class="dt-tour-tip__text"></div>' +
      '<div class="dt-tour-tip__row"><span class="dt-tour-tip__count"></span>' +
      '<button class="dt-tour-tip__next" type="button">Next</button></div></div>';
    document.body.appendChild(ov);
    var tip = ov.querySelector('.dt-tour-tip');
    function place() {
      var st = steps[i], el = document.querySelector(st.sel);
      tip.querySelector('.dt-tour-tip__text').textContent = st.text;
      tip.querySelector('.dt-tour-tip__count').textContent = (i + 1) + ' of ' + steps.length;
      tip.querySelector('.dt-tour-tip__next').textContent = (i === steps.length - 1) ? 'Done' : 'Next';
      var vw = window.innerWidth, vh = window.innerHeight, w = 260;
      var r = el ? el.getBoundingClientRect()
                 : { left: vw / 2 - w / 2, right: vw / 2 + w / 2, top: vh / 2, bottom: vh / 2, width: w, height: 0 };
      tip.style.left = Math.max(10, Math.min(vw - w - 10, r.left + r.width / 2 - w / 2)) + 'px';
      var t = (r.bottom + 120 < vh) ? (r.bottom + 10) : (r.top - 120);
      tip.style.top = Math.max(10, Math.min(vh - 130, t)) + 'px';
      tip.style.bottom = '';
    }
    function done() { try { localStorage.setItem('dt_tour_done', '1'); } catch (_) {} ov.remove(); }
    function next() { i++; if (i >= steps.length) { done(); return; } place(); }
    ov.addEventListener('click', function (e) { if (e.target === ov) next(); });
    ov.querySelector('.dt-tour-tip__next').addEventListener('click', next);
    window.addEventListener('resize', function _r() {
      if (!document.getElementById('dt-tour')) { window.removeEventListener('resize', _r); return; }
      place();
    });
    place();
  }

  /* ══════════════════════════════════════════════════════════════════
   * CONTEXTUAL TOOLTIPS — show once per feature, dismissed by click or timeout
   * ══════════════════════════════════════════════════════════════════ */
  function showTip(id, text, anchorEl) {
    return; /* disabled for now */
    try { if (localStorage.getItem('dt_tip_' + id)) return; } catch (_) {}
    /* Remove any existing tip */
    document.querySelector('.dt-tip')?.remove();

    const tip = document.createElement('div');
    tip.className = 'dt-tip';
    tip.innerHTML = `<span class="dt-tip__text">${text}</span><button class="dt-tip__close">&times;</button>`;

    /* Position near anchor if provided, else top-center of canvas area */
    if (anchorEl) {
      const r = anchorEl.getBoundingClientRect();
      tip.style.position = 'fixed';
      tip.style.left = Math.min(r.left, window.innerWidth - 280) + 'px';
      tip.style.top = (r.bottom + 8) + 'px';
    } else {
      const ca = $('dt-canvas-area');
      if (ca) {
        const r = ca.getBoundingClientRect();
        tip.style.position = 'fixed';
        tip.style.left = (r.left + r.width/2 - 140) + 'px';
        tip.style.top = (r.top + 12) + 'px';
      }
    }

    document.body.appendChild(tip);
    const dismiss = () => {
      try { localStorage.setItem('dt_tip_' + id, '1'); } catch (_) {}
      tip.remove();
    };
    tip.querySelector('.dt-tip__close')?.addEventListener('click', dismiss);
    tip.addEventListener('click', dismiss);
    setTimeout(dismiss, 8000);
  }

  /* ══════════════════════════════════════════════════════════════════
   * TOOLBAR
   * ══════════════════════════════════════════════════════════════════ */
  function setupToolbar() {
    $('dt-tb-fwd')?.addEventListener('click', () => reorder(1));
    $('dt-tb-bwd')?.addEventListener('click', () => reorder(-1));
    $('dt-tb-front')?.addEventListener('click', () => reorderExtreme(1));
    $('dt-tb-back')?.addEventListener('click', () => reorderExtreme(-1));
    $('dt-tb-delete')?.addEventListener('click', () => { if (S.sel) deleteLayer(S.sel); });
    undoBtn?.addEventListener('click', undo);
    redoBtn?.addEventListener('click', redo);
    /* Mobile floating undo/redo */
    $('dt-undo-float')?.addEventListener('click', undo);
    $('dt-redo-float')?.addEventListener('click', redo);
  }

  /* ══════════════════════════════════════════════════════════════════
   * ZOOM
   * ══════════════════════════════════════════════════════════════════ */
  function setupZoom() {
    $('dt-zoom-in')?.addEventListener('click', () => setZoom(S.zoom * 1.15));
    $('dt-zoom-out')?.addEventListener('click', () => setZoom(S.zoom * 0.87));
  }

  let _hiResTimer;
  function setZoom(z, panX, panY, immediate) {
    S.zoom = Math.min(3, Math.max(0.25, z));
    if (panX !== undefined) S._panX = panX;
    if (panY !== undefined) S._panY = panY;

    /* Instant visual feedback */
    if (isMobile()) {
      liveView(S.zoom, S._panX || 0, S._panY || 0);
    } else if (mWrap) {
      if (panX !== undefined || S._panX || S._panY) {
        mWrap.style.transformOrigin = '0 0';
        mWrap.style.transform = `translate(${S._panX}px,${S._panY}px) scale(${S.zoom})`;
      } else {
        mWrap.style.transformOrigin = S.zoom !== 1 ? 'center center' : '';
        mWrap.style.transform = S.zoom !== 1 ? `scale(${S.zoom})` : '';
      }
    }

    /* Update all zoom level labels */
    const pct = Math.round(S.zoom * 100) + '%';
    if (zoomLbl) zoomLbl.textContent = pct;
    const ql = $('dt-qz-level'); if (ql) ql.textContent = pct;
    repositionQuickZoom();

    /* Hi-res re-render: immediate for button clicks, debounced for pinch gestures.
       syncCanvas updates the buffer to match the zoom level, render() redraws crisply. */
    clearTimeout(_hiResTimer);
    if (immediate) {
      syncCanvas(); syncPrintOverlay(); render(); maybeUpgradeMockup();
    } else {
      _hiResTimer = setTimeout(() => { syncCanvas(); syncPrintOverlay(); render(); maybeUpgradeMockup(); }, 150);
    }
  }

  /* Position quick-zoom 25% of the way from print area right edge toward screen right */
  function repositionQuickZoom() {
    const qz = $('dt-quick-zoom');
    const dimsBtn = $('dt-dims-toggle');
    if (isMobile()) return;
    const wrap = document.querySelector('.dt-canvas-wrap');
    if (!wrap || !canvas) return;
    /* UX-Aug15 #6: the zoom control is a horizontal strip centred at the bottom of the
       canvas area (was a vertical satellite 25% right of the print area). */
    const ca = $('dt-canvas-area');
    const caRect = ca ? ca.getBoundingClientRect() : { left: 0, width: window.innerWidth };
    const targetX = caRect.left + caRect.width / 2;
    if (qz) {
      qz.style.left = Math.round(targetX) + 'px';
      qz.style.right = 'auto';
      qz.style.top = 'auto';
      qz.style.transform = 'translateX(-50%)';
    }
    /* Position dims button at the same X, below the quick zoom panel
       (only when it's still a satellite — the polish pass docks it in the card) */
    if (dimsBtn && qz && dimsBtn.parentElement !== qz) {
      const qzBottom = qz ? qz.getBoundingClientRect().bottom : (window.innerHeight / 2);
      dimsBtn.style.left = Math.round(targetX - 20) + 'px';
      dimsBtn.style.right = 'auto';
      dimsBtn.style.top = Math.round(qzBottom + 12) + 'px';
    }
  }
  window.addEventListener('resize', repositionQuickZoom);
  /* Also reposition after short delay on init to catch first layout */
  setTimeout(repositionQuickZoom, 500);
  setTimeout(repositionQuickZoom, 1500);

  /* ══════════════════════════════════════════════════════════════════
   * KEYBOARD
   * ══════════════════════════════════════════════════════════════════ */
  /* ══════════════════════════════════════════════════════════════════
   * DESKTOP DELIGHT — drag-drop, context menu, template ghosts,
   * etch sweep, idle inspiration  (June 11 batch)
   * ══════════════════════════════════════════════════════════════════ */

  /* E1 — drop an image anywhere on the canvas to add it (engrave-aware) */
  function setupCanvasDrop() {
    const area = $('dt-canvas-area'); if (!area) return;
    let depth = 0;
    const hasFile = e => e.dataTransfer && Array.from(e.dataTransfer.items || []).some(i => i.kind === 'file');
    area.addEventListener('dragenter', e => { if (!hasFile(e)) return; e.preventDefault(); depth++; area.classList.add('dt-droptarget'); });
    area.addEventListener('dragover', e => { if (!hasFile(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; });
    area.addEventListener('dragleave', () => { if (--depth <= 0) { depth = 0; area.classList.remove('dt-droptarget'); } });
    area.addEventListener('drop', e => {
      e.preventDefault(); depth = 0; area.classList.remove('dt-droptarget');
      const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (!f) return;
      const okType = /^image\//.test(f.type) || /\.(svg|pdf)$/i.test(f.name) || f.type === 'application/pdf';
      if (!okType) { toast('drop_image'); return; }
      if (S.method === 'engrave') handleEngraveUpload(f); else handleFile(f);
    });
  }

  /* E2 — duplicate + right-click menu */
  function duplicateLayer(id) {
    const l = layers().find(x => x.id === id); if (!l) return;
    if (l._tplLockId || l._groupId || l.isBackdrop) { toast('dup_blocked'); return; }
    const c2 = { ...l, id: uid(), name: ((l.name || 'Element') + ' copy').slice(0, 28) };
    c2.x = (c2.x || 0) + 14; c2.y = (c2.y || 0) + 14;
    delete c2._introT;
    pushLayer(c2);
  }

  function setupContextMenu() {
    if (!canvas) return;
    canvas.addEventListener('contextmenu', e => {
      if (isMobile()) return;
      const { x, y } = toCanvas(e);
      let hit = null;
      for (let i = layers().length - 1; i >= 0; i--) { const L = layers()[i]; if (L.visible === false || L.isBackdrop) continue; if (hitLayer(L, x, y)) { hit = L; break; } }
      if (!hit) return;   /* empty canvas keeps the browser menu */
      e.preventDefault();
      S.sel = hit.id; updateLayersPanel(); forcePropsRebuild(); render();
      document.getElementById('dt-ctx-menu')?.remove();
      const m = document.createElement('div');
      m.id = 'dt-ctx-menu';
      const canDup = !(hit._tplLockId || hit._groupId || hit.isBackdrop);
      m.innerHTML =
        (canDup ? '<button type="button" data-act="dup">Duplicate<span>Ctrl+D</span></button>' : '') +
        '<button type="button" data-act="fwd">Bring forward</button>' +
        '<button type="button" data-act="bwd">Send backward</button>' +
        '<button type="button" data-act="fit">Fit to print area</button>' +
        '<button type="button" data-act="center">Center</button>' +
        '<hr><button type="button" data-act="del" class="dt-ctx-danger">Delete<span>Del</span></button>';
      document.body.appendChild(m);
      m.style.left = Math.max(8, Math.min(window.innerWidth - m.offsetWidth - 8, e.clientX)) + 'px';
      m.style.top = Math.max(8, Math.min(window.innerHeight - m.offsetHeight - 8, e.clientY)) + 'px';
      const close = () => { m.remove(); document.removeEventListener('pointerdown', onDoc, true); document.removeEventListener('keydown', onKey, true); };
      const onDoc = ev => { if (!m.contains(ev.target)) close(); };
      const onKey = ev => { if (ev.key === 'Escape') close(); };
      setTimeout(() => { document.addEventListener('pointerdown', onDoc, true); document.addEventListener('keydown', onKey, true); }, 0);
      m.addEventListener('click', ev => {
        const act = ev.target.closest('button')?.dataset.act; if (!act) return;
        close();
        if (act === 'dup') duplicateLayer(hit.id);
        else if (act === 'fwd') reorder(1);
        else if (act === 'bwd') reorder(-1);
        else if (act === 'fit') fitLayerToPA();
        else if (act === 'center') centerLayerInPA();
        else if (act === 'del') confirmDelete(hit.id);
      });
    });
  }

  /* E4/F4 — template ghost previews on the product */
  let _tplGhost = null, _tplGhostLabel = null;
  function showTplGhost(it, opts) {
    if (isMobile() || !mWrap || !it || it.kind === 'state') return;
    hideTplGhost();
    const g = document.createElement('img');
    g.className = 'dt-tpl-ghost' + (opts && opts.idle ? ' dt-tpl-ghost--idle' : '');
    g._it = it; g.alt = '';
    if (it.kind === 'inline') {
      if (it.tpl._thumb && it.tpl._thumbMethod === S.method) g.src = it.tpl._thumb;
      else renderTemplateThumb(it.tpl).then(u => { if (u && _tplGhost === g) g.src = u; });
    } else if (it.kind === 'meta' && it.it.thumbnail) g.src = it.it.thumbnail;
    else return;
    g.style.left = S.pa.x + 'px'; g.style.top = S.pa.y + 'px';
    g.style.width = S.pa.w + 'px'; g.style.height = S.pa.h + 'px';
    mWrap.appendChild(g);
    _tplGhost = g;
    if (opts && opts.idle) {
      g.addEventListener('click', () => { hideTplGhost(); routeToTool('templates'); });
      const lb = document.createElement('div');
      lb.className = 'dt-tpl-ghost-label';
      lb.textContent = 'Browse templates \u2192';
      lb.style.left = (S.pa.x + S.pa.w / 2) + 'px';
      lb.style.top = (S.pa.y + S.pa.h + 10) + 'px';
      mWrap.appendChild(lb);
      _tplGhostLabel = lb;
    }
  }
  function hideTplGhost() {
    _tplGhost?.remove(); _tplGhost = null;
    _tplGhostLabel?.remove(); _tplGhostLabel = null;
  }
  function attachTplGhost(card, getIt) {
    if (isMobile()) return;
    card.addEventListener('mouseenter', () => showTplGhost(getIt()));
    card.addEventListener('mouseleave', hideTplGhost);
  }

  /* F1 — a laser line sweeps the canvas when switching to engrave */
  function runEtchSweep() {
    if (!mWrap) return;
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    mWrap.querySelector('.dt-etch-sweep')?.remove();
    const el = document.createElement('div');
    el.className = 'dt-etch-sweep';
    mWrap.appendChild(el);
    setTimeout(() => el.remove(), 850);
  }

  /* F4 — idle inspiration: an empty, untouched canvas starts trying on
     popular templates (ghosts cycle; any interaction stops it) */
  let _lastActivity = Date.now(), _idleOn = false, _idleIdx = 0;
  function setupIdleInspiration() {
    if (isMobile()) return;
    const bump = (ev) => {
      _lastActivity = Date.now();
      if (_idleOn) {
        /* The idle ghost image + label both route to templates on click, but
           pointerdown fires (and removes the ghost) BEFORE click reaches the
           element. So we detect the hit here and route after cleanup. */
        const hitGhost = ev && (_tplGhost && _tplGhost.contains(ev.target) ||
                                _tplGhostLabel && _tplGhostLabel.contains(ev.target));
        _idleOn = false; hideTplGhost();
        if (hitGhost) routeToTool('templates');
      }
    };
    ['pointerdown', 'keydown', 'wheel'].forEach(ev => document.addEventListener(ev, bump, { passive: true }));
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setInterval(() => {
      const empty = !CFG.sides.some(s2 => (S.byView[s2] || []).some(l => l.visible !== false));
      const blocked = hasModal() || document.querySelector('.dt-confirm-overlay, #dt-welcome-overlay, #dt-tour, .dt-rbg-overlay, [contenteditable="true"]');
      if (!empty || blocked || document.visibilityState !== 'visible') { if (_idleOn) { _idleOn = false; hideTplGhost(); } return; }
      if (Date.now() - _lastActivity < 18000) return;
      const items = allTemplateItems().filter(i2 => i2.kind !== 'state').slice(0, 6);
      if (!items.length) return;
      _idleOn = true;
      showTplGhost(items[_idleIdx++ % items.length], { idle: true });
    }, 4200);
  }

  function setupKeyboard() {
    document.addEventListener('keydown', e => {
      if (S._textEditing) return; /* editing text inline — let the editor own every key (arrows, backspace, etc.) */
      const ae = document.activeElement;
      if (['INPUT','TEXTAREA','SELECT'].includes(ae?.tagName) || ae?.isContentEditable) return;
      if (hasModal()) return;
      if (S._mobileActivePanel) return;

      if ((e.key === 'Delete' || e.key === 'Backspace') && S.sel) { deleteLayer(S.sel); e.preventDefault(); }
      if (e.key === 'Escape') { S.sel = null; S.iact = null; updateLayersPanel(); forcePropsRebuild(); render(); syncMobileSelection(); }

      const n = e.shiftKey ? 10 : 2;
      if (e.key === 'ArrowLeft')  { nudgeLayer(-n, 0); e.preventDefault(); }
      if (e.key === 'ArrowRight') { nudgeLayer(n, 0); e.preventDefault(); }
      if (e.key === 'ArrowUp')    { nudgeLayer(0, -n); e.preventDefault(); }
      if (e.key === 'ArrowDown')  { nudgeLayer(0, n); e.preventDefault(); }

      if ((e.ctrlKey||e.metaKey) && (e.key === 'd' || e.key === 'D')) { if (S.sel) duplicateLayer(S.sel); e.preventDefault(); }
      if ((e.ctrlKey||e.metaKey) && e.key === 'z' && !e.shiftKey) { undo(); e.preventDefault(); }
      if ((e.ctrlKey||e.metaKey) && e.key === 'z' && e.shiftKey) { redo(); e.preventDefault(); }
      if ((e.ctrlKey||e.metaKey) && e.key === 'y') { redo(); e.preventDefault(); }
    });
  }

  var _nudgeTimer = 0;
  function nudgeLayer(dx, dy) {
    const l = selLayer(); if (!l) return;
    l.x += dx; l.y += dy; render();
    /* Debounced history push — batches rapid arrow key presses into one undo step */
    clearTimeout(_nudgeTimer);
    _nudgeTimer = setTimeout(() => { pushHistory(); }, 300);
  }

  /* ══════════════════════════════════════════════════════════════════
   * RESIZE HANDLER
   * ══════════════════════════════════════════════════════════════════ */
  let _rt, _wasMobile = isMobile();
  function setupResize() {
    window.addEventListener('resize', () => {
      syncHeaderHeight();
      clearTimeout(_rt);
      _rt = setTimeout(() => {
        const nowMobile = isMobile();
        /* RAF ensures layout has settled before measuring offsetWidth */
        requestAnimationFrame(() => {
          /* Invalidate cached pa/rscale for non-active sides — they were
             computed at the old canvas size and are now stale.  The active
             side's entries are rewritten by syncCanvas() immediately. */
          CFG.sides.forEach(function(side) {
            if (side !== S.view) {
              delete S.paByView[side];
              delete S.rscaleByView[side];
            }
          });
          syncCanvas(); syncPrintOverlay(); render();
        });

        if (nowMobile !== _wasMobile) {
          _wasMobile = nowMobile;
          if (!nowMobile) {
            S._closeMobileDrawer?.();
            /* removed */
            syncMobileSelection();
            setupCanvasRow(); /* UX-Aug15 #2: desktop row is built lazily on first desktop layout */
          } else {
            syncMobileSelection();
          }
          forcePropsRebuild();
          /* The Layers rows now render their delete X only on desktop, so a
             breakpoint flip (tablet rotate, window resize across 1024) has to
             rebuild them too — forcePropsRebuild alone would leave the rows
             stale in the old mode. */
          updateLayersPanel();
        }
      }, 150);
    });

    /* Keep the canvas + print area fitted whenever the canvas AREA changes
       size — opening/closing tool panels and drawers, orientation, font
       shifts — not just on the window 'resize' event. This is what keeps the
       print area locked to the product on tablets (where a side panel is a
       large share of the width) and prevents drift on other actions.
       Observing the flex area (whose size is set by the layout, not by its
       canvas content) avoids any resize feedback loop. */
    const _canvasArea = $('dt-canvas-area');
    if (window.ResizeObserver && _canvasArea) {
      let _roRaf = 0;
      new ResizeObserver(() => {
        cancelAnimationFrame(_roRaf);
        _roRaf = requestAnimationFrame(() => {
          CFG.sides.forEach(function(side) {
            if (side !== S.view) { delete S.paByView[side]; delete S.rscaleByView[side]; }
          });
          syncCanvas(); syncPrintOverlay(); render();
        });
      }).observe(_canvasArea);
    }
  }

  /* ══════════════════════════════════════════════════════════════════
   * SNAP ALIGNMENT (9-point)
   * ══════════════════════════════════════════════════════════════════ */
  function doAlign(pos) {
    const l = selLayer(); if (!l) return;
    const p = S.pa, b = bbox(l);
    const pts = {
      tl:[p.x, p.y], tc:[p.x+(p.w-b.w)/2, p.y], tr:[p.x+p.w-b.w, p.y],
      ml:[p.x, p.y+(p.h-b.h)/2], mc:[p.x+(p.w-b.w)/2, p.y+(p.h-b.h)/2], mr:[p.x+p.w-b.w, p.y+(p.h-b.h)/2],
      bl:[p.x, p.y+p.h-b.h], bc:[p.x+(p.w-b.w)/2, p.y+p.h-b.h], br:[p.x+p.w-b.w, p.y+p.h-b.h],
    };
    const [nx, ny] = pts[pos] || [b.x, b.y];
    if (l.centered) { l.x = nx + b.w/2; l.y = ny + b.h/2; } else { l.x = nx; l.y = ny; }
    pushHistory(); render(); forcePropsRebuild();
  }

  /* ══════════════════════════════════════════════════════════════════
   * PROPERTIES PANEL
   * ══════════════════════════════════════════════════════════════════ */
  let _propDebounce;


  /* ══════════════════════════════════════════════════════════════════
   * UX-Aug15 #3 · DESKTOP CANVAS-TOP ACTIONS BAR (#dt-ctx-bar)
   * Fit / Center / size meta used to live at the foot of a ~900px props scroll and
   * Delete at the sidebar foot; Duplicate had no button. This bar sits in-flow under
   * the canvas-top row (reserved height, never over the print area) and carries
   * ACTIONS ONLY: Size − / + · Fit · Center · Snap ┃ Delete, plus the live meta
   * line. Font / Color / B / I stay at the top of the props panel (one control, one
   * place). Delete routes through confirmDelete (dialog); keyboard Del stays instant.
   * When the bar is present the old #dt-canvas-toolbar Delete, the panel Fit/Center
   * row and #dt-prop-delete are CSS-hidden (design-tool.css, same commit) — ordering
   * lives in Layers-in-slot (#8) and the right-click menu / Ctrl+D stay as accelerators.
   * ══════════════════════════════════════════════════════════════════ */
  function ensureCtxBar() {
    let bar = $('dt-ctx-bar');
    if (bar) return bar;
    const area = $('dt-canvas-area'); if (!area) return null;
    bar = document.createElement('div');
    bar.id = 'dt-ctx-bar';
    bar.className = 'dt-ctx-bar';
    bar.setAttribute('role', 'toolbar');
    bar.setAttribute('aria-label', 'Selected element actions');
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML =
      '<div class="dt-ctx-bar__group dt-ctx-bar__size" id="dt-ctx-size">' +
        '<span class="dt-ctx-bar__lbl">Size</span>' +
        '<button type="button" class="dt-ctx-btn dt-ctx-btn--icon" data-act="smaller" title="Smaller" aria-label="Smaller"><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>' +
        '<button type="button" class="dt-ctx-btn dt-ctx-btn--icon" data-act="bigger" title="Bigger" aria-label="Bigger"><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8M6 2v8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>' +
      '</div>' +
      '<div class="dt-ctx-bar__group">' +
        '<button type="button" class="dt-ctx-btn" data-act="fit" title="Fit to print area">Fit</button>' +
        '<button type="button" class="dt-ctx-btn" data-act="center" title="Center in print area">Center</button>' +
        '<button type="button" class="dt-ctx-btn dt-snap-toggle' + (S.snapEnabled ? ' active' : '') + '" data-act="snap" title="Toggle snap guides">' +
          '<svg width="14" height="14" viewBox="0 0 20 20" fill="none" style="vertical-align:-2px;margin-right:3px"><path d="M10 2V6M10 14V18M2 10H6M14 10H18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><rect x="7" y="7" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.5"/></svg>Snap</button>' +
      '</div>' +
      '<div class="dt-ctx-bar__group dt-ctx-bar__end">' +
        '<button type="button" class="dt-ctx-btn dt-ctx-btn--danger" data-act="del" title="Delete (Del)">' +
          '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 4H13L12 14H4L3 4Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M6 4V2.5H10V4" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>' +
          '<span>Delete</span>' +
        '</button>' +
      '</div>' +
      /* meta LAST and shrinkable, so on a narrow canvas (wide props open) it truncates instead of
         pushing Delete off the edge */
      '<span class="dt-ctx-bar__meta" id="dt-ctx-meta" aria-live="polite"></span>';
    /* In-flow, directly under the canvas-top row */
    const row = $('dt-header-row2');
    if (row && row.parentNode === area) row.insertAdjacentElement('afterend', bar);
    else area.insertBefore(bar, area.firstChild);
    bar.addEventListener('mousedown', e => e.preventDefault()); /* keep focus on the canvas selection */
    bar.addEventListener('click', e => {
      const btn = e.target.closest('.dt-ctx-btn'); if (!btn) return;
      const l = selLayer(); if (!l) return;
      const act = btn.dataset.act;
      if (act === 'fit') { if (l._tplLockId && !tplReframing(l)) fitTemplateGroup(l); else fitLayerToPA(); }
      else if (act === 'center') { if (l._tplLockId && !tplReframing(l)) centerTemplateGroup(l); else centerLayerInPA(); }
      else if (act === 'snap') { toggleSnap(); const sb = btn; sb.classList.toggle('active', S.snapEnabled); showSnapFeedback(S.snapEnabled ? 'Snap ON — elements align to guides' : 'Snap OFF'); }
      else if (act === 'del') { confirmDelete(l.id); }
      else if (act === 'bigger' || act === 'smaller') { ctxResize(l, act === 'bigger' ? 1 : -1); }
      updateCtxBar(selLayer());
    });
    return bar;
  }
  /* Size step: text → fontSize ±2 (measureText keeps the box honest); everything else →
     ×1.08 / ÷1.08 about the layer centre. Same clamps as the keyboard/handles paths. */
  function ctxResize(l, dir) {
    if (!l || l._noResize) return;
    if (l._tplLockId && !tplReframing(l)) { toast('Use Fit to size a template as a whole.', null, 2600); return; }
    if (l.type === 'text') {
      const cx = l.centered ? l.x : l.x + l.w / 2, cy = l.centered ? l.y : l.y + l.h / 2;
      l.fontSize = Math.max(8, Math.min(400, Math.round((l.fontSize || 36) + dir * 2)));
      measureText(l); l.ar = l.w / (l.h || 1);
      if (!l.centered) { l.x = cx - l.w / 2; l.y = cy - l.h / 2; }
    } else {
      const k = dir > 0 ? 1.08 : 1 / 1.08;
      const cx = l.centered ? l.x : l.x + l.w / 2, cy = l.centered ? l.y : l.y + l.h / 2;
      const nw = Math.max(6, l.w * k), nh = Math.max(6, l.h * k);
      l.w = nw; l.h = nh;
      if (!l.centered) { l.x = cx - nw / 2; l.y = cy - nh / 2; }
      if (typeof syncGroupedBackdrops === 'function') syncGroupedBackdrops();
    }
    pushHistory(); render();
  }
  function ctxMetaText(l) {
    try {
      const phys = SIDE_PHYS[S.view]; if (!phys || !(phys.wInches > 0) || !l) return '';
      const b = (typeof selectionBounds === 'function') ? selectionBounds(l) : { w: l.w, h: l.h };
      const wIn = pxToInches(b.w / S.rscale, S.view), hIn = pxToInches(b.h / S.rscale, S.view);
      return wIn.toFixed(2) + '″ × ' + hIn.toFixed(2) + '″';
    } catch (_) { return ''; }
  }
  function updateCtxBar(l) {
    if (isMobile()) return;
    const bar = ensureCtxBar(); if (!bar) return;
    const has = !!l;
    bar.classList.toggle('is-active', has);
    bar.setAttribute('aria-hidden', has ? 'false' : 'true');
    if (!has) return;
    const tpl = !!(l._tplLockId && !tplReframing(l));
    /* Snap button always visible (global toggle, not per-layer); keep in sync */
    const snapBtn = bar.querySelector('[data-act="snap"]');
    if (snapBtn) snapBtn.classList.toggle('active', S.snapEnabled);
    const sizeG = $('dt-ctx-size'); if (sizeG) sizeG.hidden = !!(l._noResize || tpl);
    const del = bar.querySelector('[data-act="del"] span'); if (del) del.textContent = tpl ? 'Delete template' : 'Delete';
    const meta = $('dt-ctx-meta'); if (meta) meta.textContent = ctxMetaText(l);
    positionCtxBar();
  }
  /* UX-Aug15 rev 3 — the actions bar is a FLOATING toolbar anchored to the selection (Canva/
     Figma pattern), not a full-width strip: it sits centred above the selected layer's bounds
     (below it when there's no room above), clamped to the visible canvas (right of any wide
     props overlay). Screen mapping is trivial and zoom-proof: canvas CSS px → screen via the
     canvas element's transformed rect / its untransformed offset size. Re-placed on every
     render() (rAF-deduped), on window resize and after zoom. */
  let _ctxPlaceRaf = 0;
  function scheduleCtxBarPlace() {
    if (isMobile() || !S.sel || _ctxPlaceRaf) return;
    const bar = $('dt-ctx-bar'); if (!bar || !bar.classList.contains('is-active')) return;
    _ctxPlaceRaf = requestAnimationFrame(() => { _ctxPlaceRaf = 0; positionCtxBar(); });
  }
  function positionCtxBar() {
    try {
      if (isMobile()) return;
      const bar = $('dt-ctx-bar'); const l = selLayer();
      if (!bar || !l || !bar.classList.contains('is-active') || !canvas) return;
      const area = $('dt-canvas-area'); if (!area) return;
      const b = (typeof selectionBounds === 'function') ? selectionBounds(l) : bbox(l);
      const cr = canvas.getBoundingClientRect();
      const cw = canvas.offsetWidth || 1, ch = canvas.offsetHeight || 1;
      const sx = cr.width / cw, sy = cr.height / ch;
      const left = cr.left + b.x * sx, top = cr.top + b.y * sy, w = b.w * sx, h = b.h * sy;
      const ar = area.getBoundingClientRect();
      const inset = parseFloat(getComputedStyle(area).getPropertyValue('--dt-row-inset')) || 0;
      const bw = bar.offsetWidth || 360, bh = bar.offsetHeight || 42;
      const rowEl = $('dt-header-row2');
      const topLimit = (rowEl && rowEl.offsetParent ? rowEl.getBoundingClientRect().bottom : ar.top) + 34; /* room for the caption above the pill */
      let x = left + w / 2 - bw / 2;
      x = Math.max(ar.left + inset + 12, Math.min(ar.right - bw - 12, x));
      /* Prefer ABOVE the selection. If that would straddle the print-area's top edge, hop just
         above the print area instead (when there's room); otherwise flip BELOW the selection. */
      let y = top - bh - 16;
      const paTopScreen = cr.top + (S.pa ? S.pa.y * sy : -1e9);
      if (S.pa && y < paTopScreen && y + bh > paTopScreen) {
        const yAbovePA = paTopScreen - bh - 10;
        y = (yAbovePA >= topLimit) ? yAbovePA : (top + h + 18);
      }
      if (y < topLimit) y = top + h + 18;               /* flip below the layer */
      if (y + bh > ar.bottom - 12) y = ar.bottom - bh - 12;
      bar.style.left = Math.round(x) + 'px';
      bar.style.top  = Math.round(y) + 'px';
    } catch (_) {}
  }
  window.addEventListener('resize', () => scheduleCtxBarPlace());

  /* UX-Aug15 #2 (post-deploy fix): when the props overlay is open (it paints over the panel
     slot AND overhangs into the canvas area when wide), inset the canvas-top row + actions bar
     by that overhang so nothing is covered. --dt-row-inset lives on #dt-canvas-area. */
  function syncCanvasRowInset() {
    try {
      if (isMobile()) return;
      const area = $('dt-canvas-area'); if (!area) return;
      const right = document.querySelector('.dt-sidebar--right');
      let inset = 0;
      if (right && !right.classList.contains('hidden') && !right.classList.contains('collapsed') && getComputedStyle(right).display !== 'none') {
        const a = area.getBoundingClientRect(), r = right.getBoundingClientRect();
        if (r.width > 0) inset = Math.max(0, Math.round(r.right - a.left));
      }
      area.style.setProperty('--dt-row-inset', inset + 'px');
      /* rev 3: keep the PRODUCT centred in the VISIBLE canvas (right of the overlay) by panning
         the view — a pure transform, no reflow, no rscale change. Half the overhang delta. */
      const prev = syncCanvasRowInset._last || 0;
      if (inset !== prev) {
        syncCanvasRowInset._last = inset;
        const d = (inset - prev) / 2;
        if (d && mWrap) {
          mWrap.classList.add('dt-zoom-transition');
          setZoom(S.zoom, (S._panX || 0) + d, (S._panY || 0), true);
          setTimeout(() => { mWrap.classList.remove('dt-zoom-transition'); scheduleCtxBarPlace(); }, 360);
        }
      }
      scheduleCtxBarPlace();
    } catch (_) {}
  }
  window.addEventListener('resize', () => requestAnimationFrame(syncCanvasRowInset));
  function forcePropsRebuild() { S._lastPropsLayerId = null; updatePropsPanel(); }

  /* Desktop: align the floating Properties overlay to the right edge of the tab
     rail, so it sits over the panel slot rather than as a surprise right column.
     Same #dt-props-panel element — only its position changes. */
  function positionPropsOverlay() {
    if (isMobile()) return;
    const right = document.querySelector('.dt-sidebar--right');
    const tabs  = $('dt-left-tabs');
    const main  = document.querySelector('.dt-main');
    if (!right || !tabs || !main) return;
    const m = main.getBoundingClientRect(), t = tabs.getBoundingClientRect();
    right.style.left = Math.max(0, Math.round(t.right - m.left)) + 'px';
  }
  window.addEventListener('resize', positionPropsOverlay);

  function updatePropsPanel() {
    const panel = $('dt-props-panel'); if (!panel) return;
    const l = selLayer();
    const sidebar = document.querySelector('.dt-sidebar--right');

    /* Position the properties overlay over the reserved left panel slot (desktop). The
       slot is fixed-width, so this never resizes the canvas. Widen the overlay to the
       library-drawer width for text (3-col font grid) AND for library-born elements —
       clipart/graphics, monograms, template groups, and photos with a background — so
       every edit panel opens as large as the panel that created the element. Plain
       uploads keep the compact width. */
    if (sidebar && !isMobile()) {
      sidebar.classList.toggle('hidden', !l);
      /* UX-Aug15 #2 (post-deploy fix): the wide (640px) props overlay is BACK for text /
         library-born layers — the panels were designed for that width and cramped/cropped at
         320. It no longer hides the canvas-top row/bar: syncCanvasRowInset() pads them by the
         overlay's overhang into the canvas area, so Front/Back + method stay reachable. */
      const wide = !!(l && (l.type === 'text' || isClipartLayer(l) || l.isMonogram ||
        l._tplLockId || (l._groupId && !l.isBackdrop)));
      sidebar.classList.toggle('dt-props-wide', wide);
      requestAnimationFrame(syncCanvasRowInset); setTimeout(syncCanvasRowInset, 320); /* after the panel entrance transform */
      /* Non-text wide panels flow their few controls two-up (.dt-props-grid,
         CSS) so the drawer width doesn't read as one sparse column. Text keeps
         its single column — the font picker and format rows are full-width.
         UX-Aug20: template layers also stay single-column — the "Make it yours"
         personalize form is a single-column layout with full-width inputs. */
      sidebar.classList.toggle('dt-props-grid', wide && l.type !== 'text' && !l._tplLockId);
      if (l) positionPropsOverlay();
    }

    /* TEMPLATE LOCK: the "Make it yours" panel carries its own labeled "Delete
       template", and a locked template has no independently reorderable members
       (reorder/More are already withheld everywhere else for templates) — so the
       generic element toolbar at the foot of the sidebar hides entirely while one
       is selected. It was showing a SECOND, full-width red Delete for the same
       action (Brandon, Jul 28 2026). Runs before the no-change early return so
       it always tracks the current selection. */
    const _elTb = $('dt-canvas-toolbar');
    if (_elTb) _elTb.classList.toggle('dt-canvas-toolbar--tpl', !!(l && l._tplLockId && !tplReframing(l)));
    updateCtxBar(l); /* UX-Aug15 #3 — runs on every call so the meta line tracks moves/resizes */

    if (l && l.id === S._lastPropsLayerId) return;
    S._lastPropsLayerId = l?.id || null;

    const sidebarHeader = sidebar?.querySelector('.dt-sidebar__header');
    if (sidebarHeader) sidebarHeader.textContent = l
      ? layerTypeLabel(l) + ' \u2014 ' + (l.name || (l.text ? l.text.substring(0, 20) : '') || 'Element')
      : 'Properties';
    /* Set element label for desktop CSS ::before header strip */
    panel.dataset.elementLabel = l ? layerTypeLabel(l) + ': ' + (l.name || l.text?.substring(0, 20) || 'Element') : '';

    if (!l) {
      panel.innerHTML = '';
      return;
    }

    /* TEMPLATE LOCK: properties ARE the single template editor — text fields,
       color swatches, photo controls, and Delete. No intermediate step. */
    if (l._tplLockId) {
      if (sidebarHeader) sidebarHeader.textContent = 'Template';
      panel.dataset.elementLabel = 'Template';
      panel.innerHTML = '';
      const ok = showTemplatePersonalize(panel);
      /* Same mobile delete rule as showTemplatePersonalize — action bar only. */
      if (!ok) panel.innerHTML = '<div class="dt-tpl-locknote">This template has no editable text or photo.</div>' +
        (isMobile() ? '' : '<button class="dt-prop-color-btn" id="dt-prop-tpl-del" style="color:var(--dt-danger)">Delete template</button>');
      $('dt-prop-tpl-del')?.addEventListener('click', () => confirmDelete(l.id));
      return;
    }

    const phys = SIDE_PHYS[S.view];
    const wIn = phys ? pxToInches(l.w / S.rscale, S.view).toFixed(2) : '';
    const hIn = phys ? pxToInches(l.h / S.rscale, S.view).toFixed(2) : '';
    let html = '';

    /* ═══ TEXT LAYER ═══════════════════════════════════ */
    if (l.type === 'text') {
      /* Mobile: no textarea — text edited via floating input bar on double-tap.
         Desktop: textarea for direct editing. */
      if (!isMobile()) {
        html += '<textarea id="dt-prop-textarea" class="dt-prop-textarea" rows="3" placeholder="Type your text">' + l.text + '</textarea>';
      }

      /* High-frequency controls (colour, weight, alignment, line height) are built as
         one block, and the font grid as another. On DESKTOP the controls render ABOVE
         the font grid so colour/alignment are never buried below the long font list —
         the fonts keep their full, browsable space below. On mobile, colour lives in the
         action bar and the font row stays at the top of the panel (unchanged). */
      var ctrlHtml = '';
      if (!isMobile() && S.method !== 'engrave') {
        /* On-brand colour: tap a preset swatch (one click, no OS dialog) or the "+"
           custom swatch for any colour. Replaces the off-brand native colour input. */
        var _tpresets = CFG.colorPresets || ['#111111','#ffffff','#f47e46','#344e41','#c0392b','#2980b9','#8e44ad','#d4a373','#27ae60','#f39c12'];
        var _tcur = (l.color || '#111111').toLowerCase();
        ctrlHtml += '<div class="dt-color-presets-row" role="group" aria-label="Text color">' +
          _tpresets.map(function (p) {
            var on = p.toLowerCase() === _tcur ? ' is-active' : '';
            return '<button type="button" class="dt-color-preset' + on + '" data-color="' + p + '" style="background:' + p + '" aria-label="Color ' + p + '" title="' + p + '"></button>';
          }).join('') +
          '<label class="dt-color-preset dt-color-preset--custom" title="Custom color" aria-label="Custom color" style="background:' + (l.color || '#111111') + '"><input type="color" id="dt-prop-tcolor" value="' + (l.color || '#111111') + '" class="dt-hidden-input"><svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9" stroke="#fff" stroke-width="2.2" stroke-linecap="round"/></svg></label>' +
          '</div>';
      }
      const align = l.textAlign || 'center';
      ctrlHtml += '<div class="dt-prop-toolbar dt-prop-style-row">';
      ctrlHtml += '<button class="dt-prop-toggle' + (l.bold ? ' active' : '') + '" id="dt-prop-bold"><strong>B</strong></button>';
      ctrlHtml += '<button class="dt-prop-toggle' + (l.italic ? ' active' : '') + '" id="dt-prop-italic"><em>I</em></button>';
      ctrlHtml += '<span class="dt-prop-style-sep"></span>';
      ctrlHtml += '<button class="dt-prop-toggle' + (align === 'left' ? ' active' : '') + '" id="dt-prop-align-l" data-align="left" title="Left"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M1 2h12M1 5h8M1 8h12M1 11h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></button>';
      ctrlHtml += '<button class="dt-prop-toggle' + (align === 'center' ? ' active' : '') + '" id="dt-prop-align-c" data-align="center" title="Align text center"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M1 2h12M3 5h8M1 8h12M4 11h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></button>';
      ctrlHtml += '<button class="dt-prop-toggle' + (align === 'right' ? ' active' : '') + '" id="dt-prop-align-r" data-align="right" title="Right"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M1 2h12M5 5h8M1 8h12M7 11h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></button>';
      ctrlHtml += '</div>';
      const lh = l.lineHeight || 1.2;
      ctrlHtml += '<div class="dt-prop-toolbar" style="gap:6px"><span style="font-size:9px;color:var(--dt-muted);white-space:nowrap">Line Height</span>';
      ctrlHtml += '<input type="range" id="dt-prop-lh" min="0.8" max="2.5" step="0.1" value="' + lh + '" class="dt-prop-slider" style="flex:1;accent-color:var(--dt-forest)">';
      ctrlHtml += '<span id="dt-prop-lh-val" style="font-size:10px;font-weight:600;width:28px;text-align:right">' + lh.toFixed(1) + '</span></div>';

      /* Font picker: 2-column card grid on desktop, horizontal scroll on mobile */
      const allFonts = getAllFonts();
      const fontCats = ['All'].concat(allFonts.map(f => (f.category || '').trim()).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i));
      if (!S._fontCat || fontCats.indexOf(S._fontCat) === -1) S._fontCat = 'All';
      var fontHtml = '';
      if (fontCats.length > 2) {
        fontHtml += '<div class="dt-tpl-cats dt-font-cats" role="tablist" aria-label="Font categories">' +
          fontCats.map(c => '<button class="dt-tpl-cat' + (c === S._fontCat ? ' is-active' : '') + '" type="button" data-fcat="' + escHtml(c) + '">' + escHtml(c) + '</button>').join('') +
          '</div>';
      }
      fontHtml += '<div class="dt-font-picker" id="dt-prop-font-picker">';
      const previewText = (l.text && l.text !== 'Your Text' ? l.text.slice(0, 8) : 'Aa');
      const escPreview = previewText.replace(/'/g, '&#39;').replace(/"/g, '&quot;');
      if (!allFonts.length) {
        fontHtml += '<div class="dt-font-empty" style="grid-column:1/-1;padding:14px;font-size:var(--dt-text-sm);color:var(--dt-muted);text-align:center;line-height:1.4">No fonts are set up yet. Add fonts in your Custom Fonts metaobject to enable text styling.</div>';
      }
      fontHtml += allFonts.map(f => {
        const sel = (l.font === f.fontValue || l.font === f.name) ? ' selected' : '';
        const fcat = (f.category || '').trim();
        const hide = (S._fontCat !== 'All' && fcat !== S._fontCat) ? ' dt-font-card--hidden' : '';
        return '<div class="dt-font-card dt-card dt-card--type' + sel + hide + '" data-font="' + f.fontValue + '" data-source="' + f.source + '" data-fcat="' + escHtml(fcat) + '" role="button" tabindex="0" aria-label="' + escHtml(f.name) + '">' + CARD_CHECK +
          '<div class="dt-card__media"><div class="dt-font-card__preview dt-card__preview" style="font-family:' + f.css + '">' + escPreview + '</div></div>' +
          '<div class="dt-font-card__name dt-card__caption">' + f.name + '</div></div>';
      }).join('');
      fontHtml += '</div>';

      /* Desktop: controls first, fonts below (no burial). Mobile: fonts first (unchanged). */
      html += isMobile() ? (fontHtml + ctrlHtml) : (ctrlHtml + fontHtml);
    }

    /* ═══ STATE OUTLINE — searchable swap library ══════ */
    if (l.isPhotoPlaceholder) {
      html += '<button class="dt-prop-color-btn" id="dt-prop-addphoto"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" stroke-width="1.3"/><circle cx="6" cy="7" r="1.5" stroke="currentColor" stroke-width="1.1"/><path d="M3 12l3.5-3.5L9 11l2-2 2 2.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg> Add Your Photo</button>';
    }
    if (l.isStateOutline) {
      html += '<button class="dt-prop-color-btn" id="dt-prop-statepick"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1.5C5.5 1.5 3.5 3.4 3.5 6c0 3.2 4.5 8 4.5 8s4.5-4.8 4.5-8c0-2.6-2-4.5-4.5-4.5z" stroke="currentColor" stroke-width="1.3"/><circle cx="8" cy="6" r="1.6" fill="currentColor"/></svg> Change State (' + escHtml(l.stateName || 'pick') + ')</button>';
    }

    /* ═══ SVG LAYER ════════════════════════════════════ */
    if (l.type === 'image' && l.isSvg && !l.isPhotoPlaceholder) {
      if (S.method === 'engrave') {
        /* Engrave: only BLACK or TRANSPARENT for each SVG color */
        html += '<button class="dt-prop-color-btn" id="dt-prop-svgedit-engrave"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="5" cy="8" r="3" fill="#000"/><circle cx="11" cy="8" r="3" fill="none" stroke="#ccc"/></svg> Set Engrave Colors</button>';
      } else {
        html += '<button class="dt-prop-color-btn" id="dt-prop-svgedit"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="5" cy="8" r="3" fill="#e8714a"/><circle cx="11" cy="8" r="3" fill="#2196f3"/></svg> Edit Colors</button>';
      }
    }

    /* ═══ RASTER IMAGE TINT ═══════════════════════════ */
    /* Desktop inline row; on mobile the action-bar swatch opens the palette popover. */
    if (l.type === 'image' && !l.isSvg && !l.isMonogram && !l.isRasterUpload && S.method !== 'engrave' && !isMobile()) {
      const noTintBg = 'background:linear-gradient(135deg,#eee 45%,#e74c3c 45%,#e74c3c 55%,#eee 55%)';
      html += '<label class="dt-prop-color-row">';
      html += '<div class="dt-prop-color-chip" style="background:' + (l.tintColor || 'transparent') + ';' + (!l.tintColor ? noTintBg : '') + '"></div>';
      html += '<span class="dt-prop-color-hex">' + (l.tintColor || 'Original colors') + '</span>';
      html += '<input type="color" id="dt-prop-tint" value="' + (l.tintColor || '#ff0000') + '" class="dt-hidden-input">';
      if (l.tintColor) html += '<button id="dt-prop-tint-clear" class="dt-prop-clear-btn">&times;</button>';
      html += '</label>';
    }

    /* ═══ SWAP GRAPHIC — clipart/graphics re-pick from the library ════
       Sits directly after Edit Colors (both are content actions) so the
       wide-props 2-up grid pairs them on one row; placement follows. */
    if (isClipartLayer(l)) {
      html += '<button class="dt-prop-color-btn" id="dt-prop-swap-clip"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 6h9M9 3l3 3-3 3M14 10H5M7 13l-3-3 3-3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg> Swap Graphic</button>';
    }

    /* ═══ IMAGE POSITIONING — Fit/Center to print area ════ */
    if (l.type === 'image' || l.type === 'text') {
      html += '<div class="dt-prop-place">';
      /* MOBILE: Fit lives ONLY in the action bar (Brandon's rule, Jul 28 2026),
         so the row keeps Center alone here — the action bar carries no Center. */
      if (!isMobile()) html += '<button class="dt-prop-place-btn" id="dt-prop-fit-pa"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3.5" width="12" height="9" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M5 6.5h6M5 9.5h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg><span>Fit to Print Area</span></button>';
      html += '<button class="dt-prop-place-btn" id="dt-prop-center-pa" title="Center in print area"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2.5" width="12" height="11" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M8 5v6M5 8h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg><span>Center</span></button>';
      html += '</div>';
    }

    /* ═══ BACKGROUND LIBRARY — cutouts only (a backdrop behind an opaque photo is invisible) ════ */
    if (eligBackdrop(l)) {
      html += '<div class="dt-prop-place">';
      html += '<button class="dt-prop-place-btn" id="dt-prop-bg"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2.5" width="12" height="11" rx="1.5" stroke="currentColor" stroke-width="1.3"/><circle cx="5.8" cy="6.2" r="1.3" fill="currentColor"/><path d="M2.6 12l3.2-3 2.4 2.3L11 8.2l2.4 2.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Background</span></button>';
      html += '</div>';
    }

    /* ═══ PHOTO REFRAME — for a photo grouped with a backdrop ════ */
    {
      const _bd = (l.type === 'image' && !l.isSvg && !l.isMonogram && !l.isPhotoPlaceholder) ? backdropOfGroup(l) : null;
      if (_bd) {
        const _reframing = _bd._linked === false;
        const _ico = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 10l4-4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M9 4l1-1a2.1 2.1 0 0 1 3 3l-1 1M7 12l-1 1a2.1 2.1 0 0 1-3-3l1-1" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
        html += '<div class="dt-prop-place">';
        html += '<button class="dt-prop-place-btn' + (_reframing ? ' dt-prop-place-btn--active' : '') + '" id="dt-prop-reframe">' + _ico + '<span>' + (_reframing ? 'Done reframing' : 'Reframe photo') + '</span></button>';
        html += '</div>';
      }
    }

    /* ═══ BACKGROUND REMOVAL — opaque raster uploads only ════ */
    if (eligRemoveBg(l)) {
      html += '<button class="dt-prop-color-btn" id="dt-prop-remove-bg"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8a6 6 0 1 1 12 0A6 6 0 0 1 2 8z" stroke="currentColor" stroke-width="1.3"/><path d="M6 6l4 4M10 6l-4 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg> Remove Background</button>';
    }

    /* ═══ ENGRAVE INVERT (v2.11.0) — gated raster uploads in engrave mode ════ */
    if (eligEngraveInvert(l)) {
      html += '<button class="dt-prop-color-btn" id="dt-prop-engrave-invert"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M8 2v12" stroke="currentColor" stroke-width="1.3"/><path d="M4 8h2.5M10.5 6l2 2-2 2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg> ' +
        (l._engraveInverted ? 'Un-invert (lines engrave)' : 'Invert for Dark Products') + '</button>';
    }

    /* ═══ REMOVE COLOR — raster uploads: tap a dominant colour to make it transparent ════ */
    if (eligRemoveColor(l)) {
      var _rcColors = extractDominantColors(l._originalImg || l.img);
      if (_rcColors.length >= 1) {
        html += '<div class="dt-rc-section">';
        html += '<div class="dt-rc-label">Remove a Color</div>';
        html += '<div class="dt-rc-swatches">';
        _rcColors.forEach(function (hex) {
          var sel = l.removedColor && l.removedColor.toLowerCase() === hex.toLowerCase() ? ' dt-rc-swatch--active' : '';
          html += '<button class="dt-rc-swatch' + sel + '" data-rc="' + hex + '" style="background:' + hex + '" title="Remove ' + hex + '" aria-label="Remove color ' + hex + '"></button>';
        });
        html += '</div>';
        if (l.removedColor && l._originalB64) {
          html += '<button class="dt-rc-undo" id="dt-rc-undo">Restore original colors</button>';
        }
        html += '</div>';
      }
    }

    /* ═══ MONOGRAM COLOR ══════════════════════════════ */
    /* Monogram — full edit (initials, letter count, style incl. custom, colour) */
    if (l.isMonogram) {
      html += '<button class="dt-prop-color-btn" id="dt-prop-mono-edit"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5l2 2L6 12H4v-2L11.5 2.5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg> Edit Monogram</button>';
    }

    /* Monogram color — desktop inline row(s); mobile uses the popup / action-bar palette */
    if (l.isMonogram && S.method !== 'engrave' && !isMobile()) {
      const _mTwo = MONO_STYLES[l.monoStyle] && MONO_STYLES[l.monoStyle].twoTone;
      if (_mTwo) {
        const _c2 = l.monoColor2 || '#F47E46';
        html += '<label class="dt-prop-color-row"><span class="dt-prop-color-lbl">Color 1</span><div class="dt-prop-color-chip" style="background:' + l.monoColor + '"></div><span class="dt-prop-color-hex">' + l.monoColor + '</span><input type="color" id="dt-prop-mono-color" value="' + l.monoColor + '" class="dt-hidden-input"></label>';
        html += '<label class="dt-prop-color-row"><span class="dt-prop-color-lbl">Color 2</span><div class="dt-prop-color-chip" style="background:' + _c2 + '"></div><span class="dt-prop-color-hex">' + _c2 + '</span><input type="color" id="dt-prop-mono-color2" value="' + _c2 + '" class="dt-hidden-input"></label>';
      } else {
        html += '<label class="dt-prop-color-row"><div class="dt-prop-color-chip" style="background:' + l.monoColor + '"></div><span class="dt-prop-color-hex">' + l.monoColor + '</span><input type="color" id="dt-prop-mono-color" value="' + l.monoColor + '" class="dt-hidden-input"></label>';
      }
    }

    /* ═══ DIMENSIONS (resize is via floating slider) ═══ */
    if (phys) {
      const paW = S.pa.w, paH = S.pa.h;
      const fitDim = l.ar >= 1 ? paW : paH;
      const curDim = l.ar >= 1 ? l.w : l.h;
      const pct = Math.round((curDim / fitDim) * 100);
      html += '<div class="dt-prop-dims">' + wIn + '" &times; ' + hIn + '" &middot; ' + pct + '% of area</div>';
    }

    /* ═══ POSITION GRID (desktop only) ═══════════════ */
    if (!isMobile()) {
      html += '<div class="dt-prop-pos-row">';
      html += '<div class="dt-align-grid">';
      html += '<button class="dt-align-btn" data-a="tl" aria-label="Align top-left" title="Top-left">\u2196</button><button class="dt-align-btn" data-a="tc" aria-label="Align top-center" title="Top-center">\u2191</button><button class="dt-align-btn" data-a="tr" aria-label="Align top-right" title="Top-right">\u2197</button>';
      html += '<button class="dt-align-btn" data-a="ml" aria-label="Align middle-left" title="Middle-left">\u2190</button><button class="dt-align-btn" data-a="mc" aria-label="Center in print area" title="Center">\u2295</button><button class="dt-align-btn" data-a="mr" aria-label="Align middle-right" title="Middle-right">\u2192</button>';
      html += '<button class="dt-align-btn" data-a="bl" aria-label="Align bottom-left" title="Bottom-left">\u2199</button><button class="dt-align-btn" data-a="bc" aria-label="Align bottom-center" title="Bottom-center">\u2193</button><button class="dt-align-btn" data-a="br" aria-label="Align bottom-right" title="Bottom-right">\u2198</button>';
      html += '</div>';
      html += '<button class="dt-snap-toggle-sm dt-snap-toggle' + (S.snapEnabled ? ' active' : '') + '" id="dt-prop-snap"><svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M10 2V6M10 14V18M2 10H6M14 10H18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><rect x="7" y="7" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.5"/></svg> Snap</button>';
      html += '</div>';
    }

    /* ═══ ACTION BAR ══════════════════════════════════ */
    html += '<div class="dt-prop-actions">';
    html += '<button class="dt-prop-act" id="dt-prop-fwd" title="Forward"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M8 2L5 5M8 2l3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 10h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>';
    html += '<button class="dt-prop-act" id="dt-prop-bwd" title="Backward"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 14V6M8 14l-3-3M8 14l3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>';
    /* Snap is a GLOBAL canvas toggle, not a per-layer action. On mobile it
       already lives in the floating column next to the ruler (#dt-snap-fab),
       so it does NOT repeat here — having it in both places read as a
       duplicated button. Desktop keeps its single #dt-prop-snap by the
       alignment grid. */
    html += '<div style="flex:1"></div>';
    html += '<button class="dt-prop-act dt-prop-act--danger" id="dt-prop-delete" title="Delete"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 5.5h8l-.75 7.5a1 1 0 01-1 .9H5.75a1 1 0 01-1-.9L4 5.5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M2.5 5.5h11" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M6 3h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></button>';
    html += '</div>';

    panel.innerHTML = html;

    /* ═══ WIRE EVENTS ═════════════════════════════════ */
    panel.querySelectorAll('.dt-align-btn').forEach(b => b.addEventListener('click', () => doAlign(b.dataset.a)));
    $('dt-prop-snap')?.addEventListener('click', toggleSnap);

    $('dt-prop-fwd')?.addEventListener('click', () => reorder(1));
    $('dt-prop-bwd')?.addEventListener('click', () => reorder(-1));
    $('dt-prop-delete')?.addEventListener('click', () => { if (S.sel) deleteLayer(S.sel); });

    /* Text controls */
    $('dt-prop-textarea')?.addEventListener('input', function() {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.text = this.value; l2.name = this.value.slice(0, 25) || 'Text';
      measureText(l2); l2.ar = l2.w / (l2.h || 1); render(); updateLayersPanel();
      /* Keep the inline edit div in sync if it's active */
      if (S._inlineEditLayer === l2 && _inlineEditDiv) {
        _inlineEditDiv.textContent = l2.text;
        syncInlineEdit(l2, _inlineEditDiv);
      }
    });

    /* Font cards — unified handler for both mobile and desktop */
    panel.querySelectorAll('.dt-font-card').forEach(card => card.addEventListener('click', () => {
      const f = card.dataset.font, l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.font = f;
      dtAnalytics.fontSelect(fontDisplayName(f));
      document.fonts.load('36px "' + f + '"').then(() => {
        measureText(l2); l2.ar = l2.w / (l2.h || 1); render();
        if (S._inlineEditLayer === l2 && _inlineEditDiv) {
          _inlineEditDiv.style.fontFamily = "'" + l2.font + "', sans-serif";
          syncInlineEdit(l2, _inlineEditDiv);
        }
      }).catch(() => { measureText(l2); render(); });
      panel.querySelectorAll('.dt-font-card').forEach(c => c.classList.toggle('selected', c.dataset.font === f));
      updateActionBar();
      pushHistory();
    }));
    /* Font category pills — filter the row in place (no full rebuild). */
    panel.querySelectorAll('.dt-font-cats .dt-tpl-cat').forEach(chip => chip.addEventListener('click', () => {
      S._fontCat = chip.dataset.fcat;
      panel.querySelectorAll('.dt-font-cats .dt-tpl-cat').forEach(c => c.classList.toggle('is-active', c.dataset.fcat === S._fontCat));
      panel.querySelectorAll('#dt-prop-font-picker .dt-font-card').forEach(card => {
        const show = (S._fontCat === 'All' || card.dataset.fcat === S._fontCat);
        card.classList.toggle('dt-font-card--hidden', !show);
      });
    }));
    /* Line height */
    $('dt-prop-lh')?.addEventListener('input', function() {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.lineHeight = parseFloat(this.value);
      const val = $('dt-prop-lh-val'); if (val) val.textContent = l2.lineHeight.toFixed(1);
      measureText(l2); l2.ar = l2.w / (l2.h || 1); render();
    });
    $('dt-prop-lh')?.addEventListener('change', pushHistory);
    /* Text alignment */
    ['l','c','r'].forEach(a => {
      $('dt-prop-align-' + a)?.addEventListener('click', function() {
        const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
        l2.textAlign = this.dataset.align;
        $$('#dt-prop-align-l,#dt-prop-align-c,#dt-prop-align-r').forEach(b => b.classList.toggle('active', b.dataset.align === l2.textAlign));
        measureText(l2); pushHistory(); render();
      });
    });
    $('dt-prop-bold')?.addEventListener('click', () => {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.bold = !l2.bold; measureText(l2); l2.ar = l2.w / (l2.h || 1); pushHistory(); render(); forcePropsRebuild();
      if (S._inlineEditLayer === l2 && _inlineEditDiv) {
        _inlineEditDiv.style.fontWeight = l2.bold ? 'bold' : 'normal';
        syncInlineEdit(l2, _inlineEditDiv);
      }
    });
    $('dt-prop-italic')?.addEventListener('click', () => {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.italic = !l2.italic; measureText(l2); l2.ar = l2.w / (l2.h || 1); pushHistory(); render(); forcePropsRebuild();
      if (S._inlineEditLayer === l2 && _inlineEditDiv) {
        _inlineEditDiv.style.fontStyle = l2.italic ? 'italic' : 'normal';
        syncInlineEdit(l2, _inlineEditDiv);
      }
    });
    /* Brand preset swatches + the "+" custom native input share one apply path. */
    var _colorTimer = 0;
    function _applyTextColor(hex) {
      const l2 = selLayer(); if (!l2 || l2.type !== 'text') return;
      l2.color = hex; l2._autoColor = false;
      panel.querySelectorAll('.dt-color-preset').forEach(function (s) { s.classList.toggle('is-active', (s.dataset.color || '').toLowerCase() === hex.toLowerCase()); });
      const cust = panel.querySelector('.dt-color-preset--custom'); if (cust) cust.style.background = hex;
      const ci = $('dt-prop-tcolor'); if (ci && ci.value.toLowerCase() !== hex.toLowerCase()) ci.value = hex;
      render();
      if (S._inlineEditLayer === l2 && _inlineEditDiv) { _inlineEditDiv.style.color = l2.color; }
      clearTimeout(_colorTimer);
      _colorTimer = setTimeout(() => { pushHistory(); }, 300);
    }
    $('dt-prop-tcolor')?.addEventListener('input', function () { _applyTextColor(this.value); });
    panel.querySelectorAll('.dt-color-preset[data-color]').forEach(function (sw) {
      sw.addEventListener('click', function () { _applyTextColor(sw.dataset.color); });
    });

    /* State outline — searchable swap library */
    $('dt-prop-addphoto')?.addEventListener('click', () => {
      const l2 = selLayer();
      if (l2?.isPhotoPlaceholder) replacePhotoPlaceholder(l2);
    });
    $('dt-prop-statepick')?.addEventListener('click', () => {
      const l2 = selLayer();
      if (l2?.isStateOutline) openStatePicker(l2);
    });
    /* SVG color editor */
    $('dt-prop-svgedit')?.addEventListener('click', () => {
      const l2 = selLayer();
      if (l2?.isSvg) openSvgEditor(l2);
    });
    /* Engrave SVG editor — each color becomes BLACK or TRANSPARENT */
    $('dt-prop-svgedit-engrave')?.addEventListener('click', () => {
      const l2 = selLayer(); if (!l2?.isSvg) return;
      openSvgEngraveEditor(l2);
    });

    /* Tint controls */

    var _tintTimer = 0;
    $('dt-prop-tint')?.addEventListener('input', function() {
      const l2 = selLayer(); if (!l2) return;
      l2.tintColor = this.value;
      const chip = panel.querySelector('.dt-prop-color-chip'); if (chip) chip.style.background = this.value;
      const hex = panel.querySelector('.dt-prop-color-hex'); if (hex) hex.textContent = this.value;
      render();
      clearTimeout(_tintTimer);
      _tintTimer = setTimeout(() => { pushHistory(); }, 300);
    });
    $('dt-prop-tint-clear')?.addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation();
      const l2 = selLayer(); if (!l2) return;
      l2.tintColor = null; render(); forcePropsRebuild(); pushHistory();
    });

    /* Background removal from properties pane */
    /* Fit to Print Area */
    $('dt-prop-fit-pa')?.addEventListener('click', fitLayerToPA);
    /* Center on Print Area */
    $('dt-prop-center-pa')?.addEventListener('click', centerLayerInPA);
    /* Reframe / Lock photo in mask */
    $('dt-prop-reframe')?.addEventListener('click', () => S._toggleLink());
    /* Open backdrop library */
    $('dt-prop-bg')?.addEventListener('click', () => openBackdropLibrary(selLayer()));
    $('dt-prop-swap-clip')?.addEventListener('click', () => startClipartSwap(selLayer()));

    $('dt-prop-remove-bg')?.addEventListener('click', function() {
      const btn = this;
      removeBgForLayer((st, msg) => {
        if (st === 'busy') { btn.textContent = 'Removing…'; btn.disabled = true; }
        else if (st === 'error') { btn.textContent = msg || "Couldn't remove background"; }
      });
    });

    /* Engrave Invert (v2.11.0) — opens the two-card picker; toggles back when applied */
    $('dt-prop-engrave-invert')?.addEventListener('click', () => openEngraveInvertPicker());

    /* Remove Color — swatch click removes that colour, undo restores original */
    panel.querySelectorAll('.dt-rc-swatch').forEach(function (sw) {
      sw.addEventListener('click', function () {
        var hex = this.dataset.rc;
        if (!hex) return;
        applyRemoveColor(hex);
      });
    });
    $('dt-rc-undo')?.addEventListener('click', undoRemoveColor);

    /* Monogram color — routes through rebuildMonogramColor so it recolors BOTH built-in
       and custom (metaobject) monograms (the latter were previously ignored). */
    $('dt-prop-mono-color')?.addEventListener('input', function() {
      const l2 = selLayer(); if (!l2?.isMonogram) return;
      rebuildMonogramColor(l2, this.value, 1);
      const row = this.closest('.dt-prop-color-row');
      const chip = row?.querySelector('.dt-prop-color-chip'); if (chip) chip.style.background = this.value;
      const hex = row?.querySelector('.dt-prop-color-hex'); if (hex) hex.textContent = this.value;
    });

    /* Monogram color 2 (two-tone built-in styles — frame/accent) */
    $('dt-prop-mono-color2')?.addEventListener('input', function() {
      const l2 = selLayer(); if (!l2?.isMonogram) return;
      rebuildMonogramColor(l2, this.value, 2);
      const row = this.closest('.dt-prop-color-row');
      const chip = row?.querySelector('.dt-prop-color-chip'); if (chip) chip.style.background = this.value;
      const hex = row?.querySelector('.dt-prop-color-hex'); if (hex) hex.textContent = this.value;
    });

    /* Monogram — open the full editor pre-filled */
    $('dt-prop-mono-edit')?.addEventListener('click', () => {
      const l2 = selLayer(); if (l2?.isMonogram) openMonogramEditor(l2);
    });

    /* Resize slider removed — handled by floating vertical slider (dt-vslider) */
  }

  /* ══════════════════════════════════════════════════════════════════
   * UI UPDATES
   * ══════════════════════════════════════════════════════════════════ */
  /* Hide the Layers tab (desktop sidebar + mobile tab bar) until the current side has
     2+ elements. A single element has nothing to stack/reorder, and an early "Layers"
     tab makes the tool look more complex than it is. Toggling one class on #dt-app lets
     CSS hide both tabs in one place. Runs from updateLayersPanel, so it stays in sync on
     add, delete, side switch, and restore. */
  function updateLayersTabVisibility() {
    const app = document.getElementById('dt-app');
    if (!app) return;
    /* UX-Aug15: keep the mobile side segment's ✓/+ marks in step with the layer count */
    if (isMobile() && $('dt-side-seg')) { try { buildSideSegment(getVisibleSides()); } catch (_) {} }
    /* Collage empty-state gate: the inspiration collage in the panel slot is a
       fresh-start invitation — once THIS side has any layer it reads as clutter
       behind a real design, so CSS hides it via #dt-app.dt-has-design. */
    app.classList.toggle('dt-has-design', layers().length >= 1);
    /* Orderable-unit count: a photo+its backdrop move as one, so the pair
       alone offers nothing to reorder (and nothing to show in Layers). */
    const multi = layers().filter(x => !x.isBackdrop).length >= 2;
    if (app.classList.contains('dt-has-layers') === multi) return; // no change
    app.classList.toggle('dt-has-layers', multi);
    /* If Layers was open when it became unavailable (e.g. deleted down to one element),
       close it so the user isn't stranded on a now-hidden tab/panel. */
    if (!multi) {
      const openLayers = document.querySelector('.dt-sidebar__tab[data-panel="layers"].active, .dt-mobile-tab[data-panel="layers"].active');
      if (openLayers) {
        document.querySelectorAll('[data-panel="layers"]').forEach(b => b.classList.remove('active'));
        $('dt-panel-layers')?.classList.remove('active');
        document.querySelector('.dt-sidebar__panels')?.classList.remove('has-active-panel');
        document.querySelector('.dt-sidebar--left')?.classList.remove('has-active-tab');
        if (isMobile() && S._closeMobileDrawer) S._closeMobileDrawer();
      }
    }
  }

  function updateLayersPanel() {
    updateLayersTabVisibility();
    const list = $('dt-layers-list'), empty = $('dt-layers-empty'), ls = layers();
    /* A11y (WCAG 1.1.1): describe the canvas contents for screen readers */
    if (canvas) {
      canvas.setAttribute('role', 'img');
      const _vis = ls.filter(l => l.visible !== false);
      const _parts = _vis.map(l => l.type === 'text'
        ? `text "${String(l.text || l.name || '').slice(0, 40)}"`
        : l.isMonogram ? `monogram "${String(l.monoText || l.name || '').slice(0, 10)}"`
        : `${(l.isClipart || l.isSvg) ? 'graphic' : 'image'} "${String(l.name || '').slice(0, 40)}"`);
      const _sideLbl = CFG.sideConfigs[S.view]?.label || S.view;
      canvas.setAttribute('aria-label', `Design canvas, ${_sideLbl}: ` +
        (_parts.length ? `${_parts.length} element${_parts.length === 1 ? '' : 's'} — ${_parts.join('; ')}` : 'empty'));
    }
    /* A11y: remember keyboard focus inside the list across the innerHTML rebuild */
    const _af = document.activeElement;
    const _focusRow = _af?.closest?.('.dt-layer-item')?.dataset.id;
    const _focusAct = _af?.dataset?.act || (_af?.classList?.contains('dt-layer-item__body') ? 'select' : null);
    if (!ls.length) { if (list) list.innerHTML = ''; if (empty) empty.style.display = ''; return; }
    if (empty) empty.style.display = 'none'; if (!list) return;
    /* Link state for a layer that belongs to a complete photo+backdrop group. */
    const groupLinkState = (l) => {
      if (!l._groupId) return null;
      const bd = ls.find(b => b.isBackdrop && b._groupId === l._groupId);
      const ph = ls.find(p => !p.isBackdrop && p._groupId === l._groupId);
      if (!bd || !ph) return null;
      return bd._linked === false ? 'unlinked' : 'linked';
    };
    list.innerHTML = [...ls].reverse().map(l => {
      const link = groupLinkState(l);
      const chain = link === 'unlinked'
        ? '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M9 4.5l1-1a2 2 0 0 1 2.8 2.8l-1 1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M7 11.5l-1 1a2 2 0 0 1-2.8-2.8l1-1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>'
        : '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M6.5 9.5l3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M9 4.5l1-1a2 2 0 0 1 2.8 2.8l-1 1M7 11.5l-1 1a2 2 0 0 1-2.8-2.8l1-1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
      const linkBadge = link ? `<span class="dt-layer-link dt-layer-link--${link}" title="${link === 'linked' ? 'Linked to background' : 'Reframing (unlinked from background)'}" aria-hidden="true">${chain}</span>` : '';
      const tLabel = l.type === 'text' ? 'Text' : l.isMonogram ? 'Monogram' : (l.isClipart || l.isSvg) ? 'Graphic' : 'Image';
      const thumb = l.b64
        ? `<img src="${l.b64}" alt="">`
        : (l.type === 'text'
            ? `<span class="dt-layer-item__tletter" style="font-family:${(l.font || 'inherit').replace(/"/g, '')}">${((l.name || 'T').trim()[0] || 'T').toUpperCase()}</span>`
            : '<span class="dt-layer-item__tletter">·</span>');
      const eye = l.visible === false
        ? '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M8 3.5c3 0 5.5 2.2 6.5 4.5-.4 1-1.1 1.9-2 2.6M5.2 4.7C3.6 5.6 2.4 6.9 1.5 8c1 2.3 3.5 4.5 6.5 4.5 1 0 1.9-.2 2.7-.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>'
        : '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1.5 8C2.5 5.7 5 3.5 8 3.5s5.5 2.2 6.5 4.5C13.5 10.3 11 12.5 8 12.5 5 12.5 2.5 10.3 1.5 8Z" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.4"/></svg>';
      /* Mobile delete rule (Brandon, Jul 28 2026): the Layers modal is an open
         panel, so its per-row X goes on mobile — select the row, delete from the
         action bar. Desktop keeps it. Reorder + visibility are unaffected. */
      const delAct = isMobile() ? ''
        : '<button class="dt-layer-action" data-act="del" title="Delete"><svg width="11" height="11" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>';
      return `
      <div class="dt-layer-item${l.id === S.sel ? ' selected' : ''}${link ? ' dt-layer-item--grouped' : ''}" data-id="${l.id}">
        <div class="dt-layer-item__thumb">${thumb}</div>
        <button type="button" class="dt-layer-item__body" aria-pressed="${l.id === S.sel}"
          aria-label="Select layer: ${String(l.name || 'Layer').replace(/"/g, '&quot;')}, ${tLabel}">
          <span class="dt-layer-item__name">${l.name || 'Layer'}</span>
          <span class="dt-layer-item__type">${tLabel}</span>
        </button>
        ${linkBadge}
        <div class="dt-layer-item__actions">
          <button class="dt-layer-action" data-act="up" title="Move up"><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 3v6M3 5.5L6 3l3 2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
          <button class="dt-layer-action" data-act="down" title="Move down"><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 9V3M3 6.5L6 9l3-2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
          <button class="dt-layer-action" data-act="vis" title="Show / hide">${eye}</button>
          ${delAct}
        </div>
      </div>`;
    }).join('');
    list.querySelectorAll('.dt-layer-item').forEach(el => el.addEventListener('click', e => {
      const act = e.target.closest('[data-act]')?.dataset.act, id = el.dataset.id;
      if (act === 'up') { S.sel = id; reorder(1); forcePropsRebuild(); syncMobileSelection(); return; }
      if (act === 'down') { S.sel = id; reorder(-1); forcePropsRebuild(); syncMobileSelection(); return; }
      if (act === 'del') { deleteLayer(id); return; }
      if (act === 'vis') { const l2 = layers().find(x => x.id === id); if (l2) { l2.visible = l2.visible === false; pushHistory(); updateLayersPanel(); render(); } return; }
      S.sel = id; updateLayersPanel(); forcePropsRebuild(); render(); syncMobileSelection();
    }));
    /* A11y: restore keyboard focus lost to the innerHTML rebuild */
    if (_focusRow && _focusAct) {
      const _row = list.querySelector(`.dt-layer-item[data-id="${_focusRow}"]`);
      const _t = _focusAct === 'select'
        ? _row?.querySelector('.dt-layer-item__body')
        : _row?.querySelector(`[data-act="${_focusAct}"]`);
      if (_t) try { _t.focus({ preventScroll: true }); } catch (_) {}
    }
  }

  function getQty() {
    const qi = $('dt-qty-input');
    return qi ? Math.max(1, Math.min(999, parseInt(qi.value, 10) || 1)) : 1;
  }
  function setQty(v) {
    v = Math.max(1, Math.min(999, parseInt(v, 10) || 1));
    const qi = $('dt-qty-input'); if (qi) qi.value = v;
    const qd = $('dt-qty-display'); if (qd) qd.textContent = v;
    const qid = $('dt-qty-input-d'); if (qid) qid.value = v;
    updateCartBtn();
  }

  function updateCartBtn() {
    const designed = CFG.sides.filter(s => S.byView[s]?.some(l => l.visible !== false));
    const has = designed.length > 0;
    if (cartBtn) {
      cartBtn.disabled = !has;
      /* Empty state communicates the next step instead of showing an ambiguous ghost:
         a clear muted button labelled "Add a design to start" with the price hidden. */
      cartBtn.classList.toggle('dt-cart-btn--empty', !has);
      cartBtn.title = has ? '' : 'Add a design to your product to continue';
      /* CRO: the header button OPENS THE REVIEW — label it for that step.
         While the review is open it doubles as the real confirm. */
      const _inReview = document.body.classList.contains('dt-imr-open');
      const _clbl = $('dt-cart-label'); if (_clbl) _clbl.textContent = has ? (_inReview ? 'Add to Cart' : 'Continue') : 'Add a design to start';
      const _cprice = $('dt-cart-price'); if (_cprice) _cprice.style.display = has ? '' : 'none';
    }
    const mobileCartBtn = $('dt-add-cart-mobile');
    if (mobileCartBtn) {
      mobileCartBtn.disabled = !has;
      /* CRO #13 — the disabled mobile CTA tells the user the next step */
      mobileCartBtn.classList.toggle('dt-cart-btn--empty', !has);
    }
    const barLbl = $('dt-cart-bar-label');
    if (barLbl) barLbl.textContent = has ? 'Continue' : 'Tap Text or Templates to begin';

    /* CRO #16 — running price: base + per-side upcharges, qty-aware (both CTAs) */
    const basePrice = CFG.variants?.find(v => v.id === S.variantId)?.price || 0;
    const upchargeCents = calcUpcharges(designed).reduce((a, c) => a + (c.cents || 0), 0);
    const totalCents = basePrice + upchargeCents;
    const priceStr = '$' + (totalCents / 100).toFixed(2);
    const qty = getQty();

    const priceEl = $('dt-cart-price');
    if (priceEl) priceEl.textContent = qty > 1 ? `${priceStr} x ${qty}` : priceStr;
    /* Mobile cart bar price (hidden until something is designed) */
    const barPriceEl = $('dt-cart-price-bar');
    if (barPriceEl) {
      barPriceEl.style.display = has ? '' : 'none';
      barPriceEl.textContent = qty > 1 ? `${priceStr} x ${qty}` : priceStr;
    }
  }

  /* ══════════════════════════════════════════════════════════════════
   * PRODUCTION FILE GENERATION
   * ══════════════════════════════════════════════════════════════════ */

  /* ── opentype.js integration: text → outlined vector paths ──────
     Converts text layers to SVG <path> elements using actual font glyph
     outlines. Eliminates the rasterize→Vectorizer.ai→trace round-trip
     for text, producing mathematically exact vector output. */

  let _opentypeLib = null;
  const _fontCache = new Map();   /* fontFileUrl → opentype.Font */
  const _fontBinaryCache = new Map(); /* fontFileUrl → ArrayBuffer (raw TTF/OTF bytes for jsPDF embedding) */

  async function loadOpentype() {
    if (_opentypeLib) return _opentypeLib;
    if (window.opentype) { _opentypeLib = window.opentype; return _opentypeLib; }
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/opentype.js@1.3.4/dist/opentype.min.js';
      s.onload = () => { _opentypeLib = window.opentype; resolve(_opentypeLib); };
      s.onerror = () => reject(new Error('Failed to load opentype.js'));
      document.head.appendChild(s);
    });
  }

  /* Resolve a font name to its binary file URL. Custom fonts have fontFileUrl
     directly. Google Fonts need their CSS fetched to extract the actual .woff2/.ttf URL. */
  async function resolveFontFileUrl(fontName) {
    /* Check custom fonts first */
    const cf = (CFG.customFonts || []).find(f => {
      if (f.familyCss === fontName || f.familyName === fontName || f.displayName === fontName) return true;
      const cleaned = (f.familyCss || '').split(',')[0].replace(/['"]/g, '').trim();
      return cleaned === fontName;
    });
    if (cf?.fontFileUrl) return cf.fontFileUrl;

    /* Google Fonts CSS2 API is case-sensitive (e.g. "graduate" fails, "Graduate" works).
       Try the name as-is first, then retry with title-cased form if that fails. */
    const titleCase = fontName.replace(/\b\w/g, c => c.toUpperCase());
    const candidates = [fontName];
    if (titleCase !== fontName) candidates.push(titleCase);

    for (const name of candidates) {
      try {
        const cssUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name)}:wght@400;700&display=swap`;
        const res = await fetch(cssUrl, {
          headers: { 'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36' }
        });
        if (!res.ok) continue;
        const css = await res.text();
        /* Extract the first font URL from the CSS (typically woff2) */
        const urlMatch = css.match(/url\(([^)]+)\)/);
        if (urlMatch) return urlMatch[1].replace(/['"]/g, '');
      } catch (e) {
        DEBUG && console.warn('[dt] Google Fonts URL resolution failed for', name, e.message);
      }
    }
    return null;
  }

  /* Base URL of the font-ttf Vercel endpoint (api/font-ttf.js — lives on the
     same deployment as vectorize, so derive it from CFG.vectorizeUrl).
     Returns '' when vectorizeUrl is unset (dev fallback: direct fetch). */
  function _fontTtfBase() {
    const v = (CFG.vectorizeUrl || '').replace(/\/+$/, '');
    return v ? v.replace(/\/[^/]+$/, '/font-ttf') : '';
  }

  /* Build the ordered list of font-URL candidates for outlining.
     Both branches route through font-ttf because opentype.js cannot parse
     woff2, and woff2 is what both sources actually serve:
       • Custom fonts (metaobjects) → mode B (?url=): Shopify's CDN returns
         woff2 bytes even when the file URL says .ttf/.otf — the endpoint
         sniffs the signature and decompresses server-side. TTF/OTF/woff1
         pass through untouched.
       • Google fonts → mode A (?family=): browsers can't spoof the legacy
         UA (User-Agent is a forbidden fetch header), so Google always
         serves woff2 to direct requests — the endpoint fetches real TTF
         with a legacy UA server-side. The CSS API is case-sensitive, so
         try the name as-is, then title-cased, then slug→spaced
         ("great-vibes" → "Great Vibes"). */
  function _fontUrlCandidates(fontName, bold, italic) {
    const base = _fontTtfBase();
    const cf = (CFG.customFonts || []).find(f => {
      if (f.familyCss === fontName || f.familyName === fontName || f.displayName === fontName) return true;
      const cleaned = (f.familyCss || '').split(',')[0].replace(/['"]/g, '').trim();
      return cleaned === fontName;
    });
    if (cf?.fontFileUrl) {
      return base ? [`${base}?url=${encodeURIComponent(cf.fontFileUrl)}`] : [cf.fontFileUrl];
    }
    const names = [fontName];
    const titleCase = fontName.replace(/\b\w/g, c => c.toUpperCase());
    if (!names.includes(titleCase)) names.push(titleCase);
    const spaced = fontName.replace(/[-_]+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
    if (spaced && !names.includes(spaced)) names.push(spaced);
    if (!base) return []; /* no endpoint → caller falls back to legacy resolver */
    /* Requested variant first, then progressively plainer faces — Google's
       legacy CSS API 404s when a family lacks the requested variant
       (verified live: Anton:700 → 404, no silent substitution), so bold or
       italic on a family without that face must degrade to the closest
       available one instead of failing outlining altogether. NOTE: the
       editor canvas DOES synthesize bold/italic for missing faces (Chrome
       faux styles) — buildTextOutlinePaths detects a plainer served face
       and reproduces the synthesis (14° shear / 0.03em stroke) so vectors
       match what the customer approved. */
    const variants = [{ w: bold ? 700 : 400, i: !!italic }];
    if (italic) variants.push({ w: bold ? 700 : 400, i: false });
    if (bold) variants.push({ w: 400, i: !!italic });
    if (bold && italic) variants.push({ w: 400, i: false });
    const urls = [];
    variants.forEach(v => names.forEach(n =>
      urls.push(`${base}?family=${encodeURIComponent(n)}&weight=${v.w}${v.i ? '&italic=1' : ''}`)));
    return [...new Set(urls)];
  }

  /* Load and parse a font into an opentype.Font object via the font-ttf
     endpoint. Tries candidates in order; caches per-URL (success AND
     failure, so a broken font is only probed once per session). Falls back
     to the legacy direct resolver when no endpoint is configured. */
  async function loadFontForOutlining(fontName, bold, italic) {
    let urls = _fontUrlCandidates(fontName, bold, italic);
    if (!urls.length) {
      const legacy = await resolveFontFileUrl(fontName);
      if (!legacy) return null;
      urls = [legacy];
    }

    const ot = await loadOpentype();
    for (const url of urls) {
      if (_fontCache.has(url)) {
        const cached = _fontCache.get(url);
        if (cached) return cached;
        continue; /* known-bad candidate — try the next one */
      }
      try {
        const res = await fetch(url);
        if (!res.ok) { _fontCache.set(url, null); continue; }
        /* Mode A reports the face Google actually served — flag silent
           substitutions (e.g. bold requested on a single-weight family) */
        const servedW = res.headers.get('x-font-weight');
        if (DEBUG && bold && servedW && servedW !== '700') {
          console.warn('[dt] font-ttf: no 700 face for', fontName, '— serving', servedW);
        }
        const buf = await res.arrayBuffer();
        _fontBinaryCache.set(url, buf.slice(0)); /* save a copy before opentype consumes it */
        const font = ot.parse(buf);
        _fontCache.set(url, font);
        return font;
      } catch (e) {
        DEBUG && console.warn('[dt] opentype font load failed:', fontName, url, e.message);
        _fontCache.set(url, null);
      }
    }
    return null;
  }

  /**
   * Retrieve the cached raw font binary (ArrayBuffer) for a font name.
   * Available for font embedding in jsPDF via addFileToVFS/addFont if needed.
   * Returns { buffer: ArrayBuffer, font: opentype.Font } or null.
   */
  function getCachedFontBinary(fontName, bold, italic) {
    const urls = _fontUrlCandidates(fontName, bold, italic);
    for (const url of urls) {
      if (_fontBinaryCache.has(url) && _fontCache.has(url) && _fontCache.get(url)) {
        return { buffer: _fontBinaryCache.get(url), font: _fontCache.get(url) };
      }
    }
    return null;
  }

  /**
   * Build outlined SVG <path> elements for all text layers on a side.
   * Returns an array of { pathData, fill, transform } objects ready to
   * compose into an SVG document. Each text layer becomes one or more
   * <path> elements with exact placement matching buildTextOnlySvg / renderProductionPNG.
   *
   * @param {string} sideSlug - side identifier
   * @param {object} coordSpace - { pxW, pxH, uniformScale, offX, offY, sidePA }
   * @param {boolean} forceBlack - true for engrave (all fills → #000000)
   * @returns {Array<{pathData: string, fill: string, transform: string}>}
   */
  async function buildTextOutlinePaths(sideSlug, coordSpace, forceBlack) {
    const ls = S.byView[sideSlug];
    const visible = (ls || []).filter(l => l.visible !== false);
    const textLayers = visible.filter(l => l.type === 'text' && (l.text || '').trim());
    if (!textLayers.length) return [];

    const { pxW, pxH, uniformScale, offX, offY, sidePA } = coordSpace;
    const results = [];

    for (const l of textLayers) {
      const zIdx = visible.indexOf(l); /* original z-position among visible layers */
      const fontName = l.font || 'Montserrat';
      const font = await loadFontForOutlining(fontName, !!l.bold, !!l.italic);
      if (!font) {
        console.warn('[dt] opentype: could not load font for outlining:', fontName);
        gwarn('font_outline_fail', 'Could not outline font "' + fontName + '" – text layer "' + (l.text || '').slice(0, 30) + '" will use raster fallback');
        return [];  /* signal caller to fall back to raster composite */
      }

      /* Faux-style detection. The editor canvas SYNTHESIZES italic (14°
         oblique) and bold (~0.03em stroke) whenever the loaded face lacks
         the real style — that synthesized look is what the customer
         approved, so outlined vectors must reproduce it instead of
         shipping upright/regular glyphs (order CB36331 "Super Shiney":
         custom metaobject font with one file, bold+italic toggled — the
         vector outputs lost both). Read the face's ACTUAL style from its
         tables; a real italic/bold face needs no synthesis. */
      const _post = font.tables && font.tables.post;
      const _head = font.tables && font.tables.head;
      const _os2 = font.tables && font.tables.os2;
      const faceItalic = !!((_post && _post.italicAngle) || (_head && (_head.macStyle & 2)));
      const faceBold = !!((_head && (_head.macStyle & 1)) || (_os2 && _os2.usWeightClass >= 600));
      const fauxItalic = !!l.italic && !faceItalic;
      const fauxBold = !!l.bold && !faceBold;

      const lx = (l.x - sidePA.x) * uniformScale + offX;
      const ly = (l.y - sidePA.y) * uniformScale + offY;
      const lw = (l.w || 0) * uniformScale;
      const lh = (l.h || 0) * uniformScale;
      const cx = l.centered ? lx : lx + lw / 2;
      const cy = l.centered ? ly : ly + lh / 2;
      const fs = (l.fontSize || 36) * uniformScale;
      const fillColor = forceBlack ? '#000000' : (l.color || '#000');

      const lines = (l.text || '').split('\n');
      const lineH = fs * (l.lineHeight || 1.25);
      const totalH = lines.length * lineH;
      const startY = cy - totalH / 2 + lineH / 2;

      /* Build transform for rotation/flip (mirrors buildTextOnlySvg) */
      const xforms = [];
      if (l.rotation) xforms.push(`rotate(${l.rotation} ${cx.toFixed(2)} ${cy.toFixed(2)})`);
      if (l.flipH || l.flipV) {
        xforms.push(`translate(${cx.toFixed(2)} ${cy.toFixed(2)})`);
        xforms.push(`scale(${l.flipH ? -1 : 1} ${l.flipV ? -1 : 1})`);
        xforms.push(`translate(${(-cx).toFixed(2)} ${(-cy).toFixed(2)})`);
      }
      const tfAttr = xforms.length ? xforms.join(' ') : '';

      /* Convert each line of text to outlined paths */
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) continue;

        const drawX = l.centered ? lx : lx + lw / 2;
        const drawY = startY + i * lineH;

        /* opentype.js getPath: x,y is baseline-left. We need to center-align
           and position at the vertical center like textBaseline='middle'. */
        const ascent = font.ascender / font.unitsPerEm * fs;
        const descent = font.descender / font.unitsPerEm * fs;
        const fontMid = (ascent + descent) / 2;

        /* Measure width for center alignment */
        const advWidth = font.getAdvanceWidth(line, fs);
        const pathX = drawX - advWidth / 2;
        const pathY = drawY + fontMid;  /* shift from center to baseline */

        const path = font.getPath(line, pathX, pathY, fs);

        /* Faux italic: shear every point about the line's BASELINE, baked
           straight into the path data (a skewX transform attr would be
           dropped by flattenSvgPaths and the geo worker). Matches Chrome's
           synthesis: measured 13.9° ≈ the CSS 14° oblique standard.
           x' = x + (baselineY - y) * tan(14°) leans glyph tops right. */
        if (fauxItalic) {
          const K = 0.24933; /* tan(14°) */
          path.commands.forEach(cmd => {
            if (cmd.x !== undefined) cmd.x += (pathY - cmd.y) * K;
            if (cmd.x1 !== undefined) cmd.x1 += (pathY - cmd.y1) * K;
            if (cmd.x2 !== undefined) cmd.x2 += (pathY - cmd.y2) * K;
          });
        }
        const pathData = path.toPathData(2);  /* 2 decimal places */

        if (pathData && pathData !== 'M0 0') {
          const entry = { pathData, fill: fillColor, transform: tfAttr, zIdx };
          /* Faux bold: same-color stroke fattens the glyphs like the
             canvas's synthetic bold (measured: +0.03em total width). SVG
             and the PDFs honor it; the fill-only engrave builders bake it
             in by contour dilation (v2.9.11, geoWorkerConvert). */
          if (fauxBold) entry.strokeWidth = +(fs * 0.03).toFixed(2);
          results.push(entry);
        }
      }
    }

    return results;
  }

  /**
   * Build coordinate-space parameters used by both buildTextOnlySvg and
   * buildTextOutlinePaths. Extracted to avoid duplication.
   */
  function getProductionCoordSpace(sideSlug) {
    const phys = SIDE_PHYS[sideSlug];
    if (!phys) return null;
    const DPI = PROD_DPI;
    const pa = CFG.sideConfigs[sideSlug]?.printArea || { x: 0, y: 0, w: 300, h: 300 };
    const pxW = Math.round(phys.wInches * DPI);
    const pxH = Math.round(phys.hInches * DPI);
    const _rs = S.rscaleByView[sideSlug] || S.rscale;
    const sidePA = { x: pa.x * _rs, y: pa.y * _rs, w: pa.w * _rs, h: pa.h * _rs };
    const scX = pxW / sidePA.w, scY = pxH / sidePA.h;
    const uniformScale = Math.min(scX, scY);
    const offX = (pxW - sidePA.w * uniformScale) / 2;
    const offY = (pxH - sidePA.h * uniformScale) / 2;
    return { pxW, pxH, uniformScale, offX, offY, sidePA, phys };
  }

  /**
   * True when any visible layer's (rotation-aware) bounds extend past the
   * side's print area. Production SVG builders use this to decide whether
   * the print-area clipPath is actually needed — emitting it unconditionally
   * makes Illustrator show "Clipping will be lost on roundtrip to Tiny" on
   * every production SVG, even when nothing is clipped.
   */
  function sideHasPrintAreaOverflow(sideSlug) {
    const cs = getProductionCoordSpace(sideSlug);
    if (!cs) return true; /* unknown geometry — keep the safe clip */
    const { sidePA } = cs;
    const tol = Math.max(sidePA.w, sidePA.h) * 0.002 + 0.25; /* canvas px */
    const vis = (S.byView[sideSlug] || []).filter(l => l.visible !== false);
    for (const l of vis) {
      const w = l.w || 0, h = l.h || 0;
      const cx = l.centered ? l.x : l.x + w / 2;
      const cy = l.centered ? l.y : l.y + h / 2;
      const r = (l.rotation || 0) * Math.PI / 180;
      const hx = Math.abs(Math.cos(r)) * w / 2 + Math.abs(Math.sin(r)) * h / 2;
      const hy = Math.abs(Math.sin(r)) * w / 2 + Math.abs(Math.cos(r)) * h / 2;
      if (cx - hx < sidePA.x - tol || cy - hy < sidePA.y - tol ||
          cx + hx > sidePA.x + sidePA.w + tol || cy + hy > sidePA.y + sidePA.h + tol) return true;
    }
    return false;
  }

  /**
   * Shared opener for production SVG builders: emits the print-area clipPath
   * wrapper only when content actually overflows the print area.
   * Returns the markup between the <svg> tag and the first content element.
   * Always paired with a closing `</g></svg>`.
   */
  function productionClipOpen(sideSlug, pxW, pxH) {
    if (!sideHasPrintAreaOverflow(sideSlug)) return '<g>';
    return `<defs><clipPath id="dtPaClip"><rect x="0" y="0" width="${pxW}" height="${pxH}"/></clipPath></defs>` +
           `<g clip-path="url(#dtPaClip)">`;
  }

  /**
   * Build an outlined SVG string from text layers using opentype.js.
   * Text becomes <path> elements instead of <text> — no font dependency
   * in the output file. Used for EPS/DXF conversion where geoWorkerConvert
   * needs path data, not live text.
   *
   * For mixed text+vector designs, this produces the text portion;
   * vector layer SVG markup is handled by buildCompositeVectorSvg.
   */
  async function buildOutlinedTextSvg(sideSlug, forceBlack) {
    const cs = getProductionCoordSpace(sideSlug);
    if (!cs) return null;

    const paths = await buildTextOutlinePaths(sideSlug, cs, forceBlack);
    if (!paths.length) return null;

    const { pxW, pxH, phys } = cs;
    const parts = [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${phys.wInches}in" height="${phys.hInches}in" viewBox="0 0 ${pxW} ${pxH}">`,
      productionClipOpen(sideSlug, pxW, pxH),
    ];

    paths.forEach((p, idx) => {
      const tf = p.transform ? ` transform="${p.transform}"` : '';
      const sw = p.strokeWidth ? ` stroke="${p.fill}" stroke-width="${p.strokeWidth}" stroke-linejoin="round"` : '';
      parts.push(`<path id="text-${idx}" d="${p.pathData}" fill="${p.fill}"${sw}${tf}/>`);
    });

    parts.push('</g></svg>');
    return parts.join('\n');
  }

  function isVectorEligible(sideSlug) {
    const ls = S.byView[sideSlug];
    if (!ls?.length) return false;
    return ls.filter(l => l.visible !== false).every(l => !l.isRasterUpload);
  }

  function isUVDTF(sideSlug) {
    const method = CFG.sideConfigs[sideSlug]?.decorationMethod || '';
    return method.toLowerCase().includes('uv dtf');
  }

  /**
   * Render all visible layers to a composite PNG at 300 DPI.
   */
  async function renderProductionPNG(sideSlug, layerFilter, resScale, bleedPx, opts) {
    const ls = S.byView[sideSlug];
    if (!ls?.length) { DEBUG && console.warn('[dt] renderProductionPNG: no layers for', sideSlug); return null; }
    const visible = ls.filter(l => l.visible !== false);
    if (!visible.length) { DEBUG && console.warn('[dt] renderProductionPNG: no visible layers for', sideSlug); return null; }
    syncGroupedBackdrops(sideSlug); /* position linked backdrops for THIS side before export */

    /* Ensure all fonts used on this side are loaded before rendering text.
       Canvas fillText silently falls back to system fonts if the font isn't ready. */
    const textLayers = visible.filter(l => l.type === 'text');
    if (textLayers.length) {
      const fontPromises = textLayers.map(l => {
        const spec = `${l.italic?'italic ':''}${l.bold?'bold ':''}16px '${l.font||'Montserrat'}'`;
        return document.fonts.load(spec).catch(() => {});
      });
      await Promise.all(fontPromises);
    }

    const phys = SIDE_PHYS[sideSlug];
    const DPI = PROD_DPI;
    const pa = CFG.sideConfigs[sideSlug]?.printArea || { x:0, y:0, w:300, h:300 };
    let pxW, pxH;
    if (phys) { pxW = Math.round(phys.wInches * DPI); pxH = Math.round(phys.hInches * DPI); }
    else { pxW = pa.w; pxH = pa.h; }

    /* Optional supersampling (trace input): every placement value below is
       derived from pxW/pxH, so scaling them scales the whole render. */
    if (resScale && resScale > 1) { pxW = Math.round(pxW * resScale); pxH = Math.round(pxH * resScale); }

    const _rs = S.rscaleByView[sideSlug] || S.rscale;
    const sidePA = {
      x: pa.x * _rs,
      y: pa.y * _rs,
      w: pa.w * _rs,
      h: pa.h * _rs,
    };
    /* Ink boxes MUST be resolved before rendering: SVG layers and the photo
       mask map their ink rect (what the display stretches into the layer box)
       — an unresolved cache would silently fall back to full-viewBox mapping
       and re-introduce the letterboxed-oval defect. */
    await Promise.all(visible.filter(l => l.isSvg && l.svgMarkup).map(ensureSvgInkBox));
    /* Optional bleed margin (prepress only): expand the canvas by bleedPx on
       EVERY side and shift placement so the print area is centered inside the
       bleed. Captures up to bleedPx of artwork that overflows the print area,
       so the prepress can carry the bleed. Defaults to 0 → byte-identical to
       every existing caller (production, engrave, trace input, mockups). */
    const _bleed = (bleedPx && bleedPx > 0) ? Math.round(bleedPx * ((resScale && resScale > 1) ? resScale : 1)) : 0;
    const cvW = pxW + _bleed * 2, cvH = pxH + _bleed * 2;
    const off = document.createElement('canvas'); off.width = cvW; off.height = cvH;
    const oc = off.getContext('2d');
    const scX = pxW / sidePA.w, scY = pxH / sidePA.h;
    const uniformScale = Math.min(scX, scY);
    const offX = (pxW - sidePA.w * uniformScale) / 2 + _bleed, offY = (pxH - sidePA.h * uniformScale) / 2 + _bleed;

    for (const l of visible) {
      if (layerFilter && !layerFilter(l)) continue;
      oc.save();
      const _pclip = maskClipFor(l, visible, b => ({
        x: (b.x - sidePA.x) * uniformScale + offX,
        y: (b.y - sidePA.y) * uniformScale + offY,
        w: b.w * uniformScale, h: b.h * uniformScale,
      }));
      if (_pclip) oc.clip(_pclip);
      const lx = (l.x - sidePA.x) * uniformScale + offX;
      const ly = (l.y - sidePA.y) * uniformScale + offY;
      const lw = l.w * uniformScale, lh = l.h * uniformScale;
      const cx = l.centered ? lx : lx + lw/2, cy = l.centered ? ly : ly + lh/2;

      oc.translate(cx, cy);
      if (l.rotation) oc.rotate((l.rotation * Math.PI) / 180);
      if (l.flipH) oc.scale(-1, 1); if (l.flipV) oc.scale(1, -1);
      oc.translate(-cx, -cy);

      if (l.type === 'text') {
        /* ── Multi-line text at production resolution ──────── */
        const fs = (l.fontSize || 36) * uniformScale;
        oc.font = `${l.italic?'italic ':''}${l.bold?'bold ':''}${fs}px '${l.font||'Montserrat'}', sans-serif`;
        oc.fillStyle = S.method === 'engrave' ? '#000000' : (l.color || '#000');
        oc.textBaseline = 'middle'; oc.textAlign = 'center';
        const lines = (l.text || '').split('\n');
        const lineH = fs * (l.lineHeight || 1.25);
        const totalH = lines.length * lineH;
        const startY = ly - totalH / 2 + lineH / 2;
        lines.forEach((line, i) => {
          oc.fillText(line, lx, startY + i * lineH);
        });

      } else if (l.type === 'image' && l.isSvg && l.svgMarkup) {
        /* ── SVG: re-rasterize at production resolution ────── */
        const dx = l.centered ? lx - lw/2 : lx, dy = l.centered ? ly - lh/2 : ly;
        const targetW = Math.max(1, Math.round(lw)), targetH = Math.max(1, Math.round(lh));
        try {
          const hiResImg = await rasterizeSvgForProduction(l.svgMarkup, targetW, targetH, svgInkBoxOf(l));
          oc.drawImage(hiResImg, dx, dy, lw, lh);
        } catch (err) {
          /* Fallback to existing image if re-rasterization fails */
          DEBUG && console.warn('[dt]   SVG re-rasterize failed, using original:', err.message);
          if (l.img) oc.drawImage(l.img, dx, dy, lw, lh);
        }

      } else if (l.type === 'image' && l.isMonogram && l.monoStyle >= 0 && MONO_STYLES[l.monoStyle]) {
        /* ── Built-in monogram: re-render at production resolution ──
           Styles render into a SQUARE frame, but addMonogramLayer TRIMS the
           transparent padding and sizes the layer to the trimmed ink (a
           Stacked Vertical monogram's box is ~1:3). Drawing the untrimmed
           square straight into that box crushed the art horizontally, so
           mirror the design-time trim: render square, find the ink's alpha
           bbox, and map only that region into the layer box. */
        const dx = l.centered ? lx - lw/2 : lx, dy = l.centered ? ly - lh/2 : ly;
        const monoSize = Math.max(Math.round(lw), Math.round(lh), 64);
        const mc = document.createElement('canvas'); mc.width = monoSize; mc.height = monoSize;
        const mx = mc.getContext('2d');
        try {
          MONO_STYLES[l.monoStyle].render(mx, l.monoText, monoSize, monoSize, l.monoColor || '#000', l.monoColor2);
          /* Alpha bounding box of the rendered ink */
          const _md = mx.getImageData(0, 0, monoSize, monoSize).data;
          let bx1 = monoSize, by1 = monoSize, bx2 = -1, by2 = -1;
          for (let py = 0; py < monoSize; py++) {
            for (let px2 = 0; px2 < monoSize; px2++) {
              if (_md[(py * monoSize + px2) * 4 + 3] > 8) {
                if (px2 < bx1) bx1 = px2; if (px2 > bx2) bx2 = px2;
                if (py < by1) by1 = py; if (py > by2) by2 = py;
              }
            }
          }
          if (bx2 >= bx1 && by2 >= by1) {
            oc.drawImage(mc, bx1, by1, bx2 - bx1 + 1, by2 - by1 + 1, dx, dy, lw, lh);
          } else {
            oc.drawImage(mc, dx, dy, lw, lh); /* blank render guard — keep old behavior */
          }
        } catch (err) {
          DEBUG && console.warn('[dt]   Monogram re-render failed, using original:', err.message);
          if (l.img) oc.drawImage(l.img, dx, dy, lw, lh);
        }

      } else if (l.type === 'image' && l.img) {
        /* ── Raster image (uploads, custom monograms) ──────── */
        const dx = l.centered ? lx - lw/2 : lx, dy = l.centered ? ly - lh/2 : ly;
        if (l.tintColor && l.tintColor !== 'none' && S.method !== 'engrave') {
          /* Tint via offscreen canvas — isolates source-atop compositing */
          const tw = Math.max(1, Math.ceil(lw)), th = Math.max(1, Math.ceil(lh));
          const tc = document.createElement('canvas'); tc.width = tw; tc.height = th;
          const tx = tc.getContext('2d');
          tx.drawImage(l.img, 0, 0, tw, th);
          tx.globalCompositeOperation = 'source-atop';
          tx.globalAlpha = 0.75;
          tx.fillStyle = l.tintColor;
          tx.fillRect(0, 0, tw, th);
          oc.drawImage(tc, dx, dy, lw, lh);
        } else {
          oc.drawImage(l.img, dx, dy, lw, lh);
        }
      }
      oc.restore();
    }

    /* ── Engrave: force all pixels to black (preserve alpha) ──────
       Laser engrave files must be black-only. Any non-transparent pixel
       becomes #000000. Alpha is preserved for anti-aliased edges. This
       guarantees clean vectorizer input regardless of layer colors.
       v2.12.2 EXCEPTION — opts.preserveColors (TRACE INPUT ONLY): the
       vectorizer detects shapes far more reliably with the source's color
       boundaries intact (CB20260804KPOX: black-flattened input intermittently
       dropped i/l stems; the identical geometry in original colors traced
       5/5 burst trials with zero missed ink pixels). The trace call keeps
       colors here and the traced SVG is recolored to black AFTER
       vectorization (_recolorSvgInkBlack) — every other caller (composite
       embed, reference PNG, transfer PNG, prepress) still gets black. */
    if (S.method === 'engrave' && !(opts && opts.preserveColors)) {
      const imageData = oc.getImageData(0, 0, cvW, cvH);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] > 0) {
          d[i] = 0;      /* R → black */
          d[i + 1] = 0;  /* G → black */
          d[i + 2] = 0;  /* B → black */
          /* Alpha stays as-is for smooth edges */
        }
      }
      oc.putImageData(imageData, 0, 0);
    }

    return off.toDataURL('image/png');
  }

  /* ── v2.12.2: recolor traced SVG ink to black (ENGRAVE trace only) ──
     The engrave trace now vectorizes in ORIGINAL colors (better shape
     detection — see renderProductionPNG preserveColors), so the returned
     SVG carries the source palette (e.g. white letters, blue mark) plus
     gap-filler stroke paths in near-source colors (burst test observed
     stroke="#edeee7" hairlines). Engrave is 1-bit: every fill AND stroke
     becomes #000000 before ANY downstream consumer sees the SVG. This is
     load-bearing, not cosmetic — the EPS/DXF converter's paper-white drop
     rules (absolute >=240 + relative luminance split) would otherwise
     delete white-on-transparent ink as "paper" (this exact order's text
     is white). fill="none"/stroke="none" are structure, not ink — kept. */
  function _recolorSvgInkBlack(svgStr) {
    if (!svgStr) return svgStr;
    return svgStr
      .replace(/\bfill="(?!none")[^"]*"/g, 'fill="#000000"')
      .replace(/\bstroke="(?!none")[^"]*"/g, 'stroke="#000000"');
  }

  /* ── v2.12.2: TRACE COVERAGE AUDIT (engrave; non-blocking) ──────────
     Detects the silent-incomplete-trace failure class (CB20260804KPOX:
     vectorizer intermittently dropped 3 letter stems and the file shipped
     with zero signal). Rasterizes the traced SVG offscreen at reduced
     resolution and counts trace-input ink pixels the SVG fails to cover.
     Telemetry ONLY — never throws, never blocks generation or the cart;
     fire-and-forget so a rasterization failure can't stall an order.
     Watch dt_error for `engrave_trace_coverage` in GA4: any hit means the
     vectorizer dropped geometry and that order needs manual review before
     the laser. missedPct is embedded in the message. */
  function _auditTraceCoverage(inputPng, tracedSvg, tag) {
    try {
      const AUDIT_W = 640;           /* downsampled — cheap + plenty to see a stem */
      const MISS_THRESHOLD = 0.5;    /* % of input ink missing from the trace */
      const loadImg = src => new Promise((res, rej) => {
        const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src;
      });
      return loadImg(inputPng).then(inImg => {
        const w = AUDIT_W, h = Math.max(1, Math.round(inImg.height * (AUDIT_W / inImg.width)));
        const ic = document.createElement('canvas'); ic.width = w; ic.height = h;
        ic.getContext('2d').drawImage(inImg, 0, 0, w, h);
        const inD = ic.getContext('2d').getImageData(0, 0, w, h).data;
        const blobUrl = URL.createObjectURL(new Blob([tracedSvg], { type: 'image/svg+xml' }));
        return loadImg(blobUrl).then(svgImg => {
          URL.revokeObjectURL(blobUrl);
          const sc = document.createElement('canvas'); sc.width = w; sc.height = h;
          sc.getContext('2d').drawImage(svgImg, 0, 0, w, h);
          const svD = sc.getContext('2d').getImageData(0, 0, w, h).data;
          let ink = 0, missed = 0;
          for (let i = 3; i < inD.length; i += 4) {
            if (inD[i] > 128) { ink++; if (svD[i] < 32) missed++; }
          }
          const pct = ink ? +(100 * missed / ink).toFixed(2) : 0;
          DEBUG && console.log('[dt] trace coverage', tag, ink + ' ink px,', missed + ' missed (' + pct + '%)');
          if (ink > 0 && pct > MISS_THRESHOLD) {
            gwarn('engrave_trace_coverage',
              'Traced SVG is missing ' + pct + '% of trace-input ink for ' + tag +
              ' (' + missed + '/' + ink + ' px @' + w + 'w) — vectorizer dropped geometry; review this order');
          }
          return pct;
        }, () => null);
      }, () => null);
    } catch (_) { return Promise.resolve(null); }
  }

  /* ── v2.13.0 (F3): REGION-LEVEL TRACE AUDIT (engrave; blocking consumer) ──
     Successor to _auditTraceCoverage's global percentage, which cannot see
     thin-glyph loss: HM1F's two missing capital I's were 0.595% of total ink
     (vs the 0.5% threshold measured at 640w — inside noise), and a single
     KPOX-class stem is ~0.2% — invisible BY DESIGN of a global metric. This
     audit labels every connected ink REGION of the trace input and requires
     each auditable region to be >=50% covered by the rasterized trace. A
     dropped stem is a 0%-covered region — unmissable regardless of its share
     of total ink.
     Resolution: capped at 1024w (~half trace-input res; a 16px stem is still
     ~8px wide). Memory: a few MB transient — mobile-safe.
     Tolerance: trace mask dilated 1px before coverage so sub-pixel edge slop
     can't fail a region. Area floor scale-relative: max(8, 30px2-at-full-res
     scaled, 0.005% of total ink) — below that is dust, not artwork.
     NEVER rejects: resolves { ok:true, error } on internal failure so an
     audit crash cannot block a cart (result still ships, matching pre-v2.13
     behavior). The CALLER decides whether a failed audit blocks. */
  function _auditTraceRegions(inputPng, tracedSvg) {
    const loadImg = src => new Promise((res, rej) => {
      const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src;
    });
    return (async () => {
      const inImg = await loadImg(inputPng);
      const w = Math.min(1024, inImg.width || 1024);
      const h = Math.max(1, Math.round(inImg.height * (w / inImg.width)));
      const scale = w / inImg.width;
      const ic = document.createElement('canvas'); ic.width = w; ic.height = h;
      ic.getContext('2d').drawImage(inImg, 0, 0, w, h);
      const inD = ic.getContext('2d').getImageData(0, 0, w, h).data;
      const blobUrl = URL.createObjectURL(new Blob([tracedSvg], { type: 'image/svg+xml' }));
      let svgImg;
      try { svgImg = await loadImg(blobUrl); } finally { URL.revokeObjectURL(blobUrl); }
      const sc = document.createElement('canvas'); sc.width = w; sc.height = h;
      sc.getContext('2d').drawImage(svgImg, 0, 0, w, h);
      const svD = sc.getContext('2d').getImageData(0, 0, w, h).data;
      const n = w * h;
      const ink = new Uint8Array(n);
      const tr = new Uint8Array(n);
      let totalInk = 0;
      for (let i = 0; i < n; i++) {
        if (inD[i * 4 + 3] > 128) { ink[i] = 1; totalInk++; }
        if (svD[i * 4 + 3] > 32) tr[i] = 1;
      }
      /* 1px 8-neighbour dilation of the trace mask */
      const trD = new Uint8Array(tr);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        if (!tr[y * w + x]) continue;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const yy = y + dy, xx = x + dx;
          if (yy >= 0 && yy < h && xx >= 0 && xx < w) trD[yy * w + xx] = 1;
        }
      }
      /* connected-component labeling: 8-conn iterative flood fill */
      const seen = new Uint8Array(n);
      const stack = new Int32Array(n);
      const minArea = Math.max(8, Math.round(30 * scale * scale), Math.round(totalInk * 0.00005));
      let comps = 0, fails = 0, worst = 1;
      const boxes = [];
      for (let s0 = 0; s0 < n; s0++) {
        if (!ink[s0] || seen[s0]) continue;
        let top = 0; stack[top++] = s0; seen[s0] = 1;
        let area = 0, cov = 0, minX = w, minY = h, maxX = 0, maxY = 0;
        while (top) {
          const p = stack[--top];
          area++; if (trD[p]) cov++;
          const px = p % w, py = (p / w) | 0;
          if (px < minX) minX = px; if (px > maxX) maxX = px;
          if (py < minY) minY = py; if (py > maxY) maxY = py;
          for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
            const yy = py + dy, xx = px + dx;
            if (yy < 0 || yy >= h || xx < 0 || xx >= w) continue;
            const q = yy * w + xx;
            if (ink[q] && !seen[q]) { seen[q] = 1; stack[top++] = q; }
          }
        }
        if (area < minArea) continue;
        comps++;
        const c = cov / area;
        if (c < worst) worst = c;
        if (c < 0.5) {
          fails++;
          if (boxes.length < 8) boxes.push(Math.round(minX / scale) + ',' + Math.round(minY / scale) +
            ' ' + Math.round((maxX - minX + 1) / scale) + 'x' + Math.round((maxY - minY + 1) / scale) +
            ' ' + Math.round(c * 100) + '%');
        }
      }
      return { ok: fails === 0, fails: fails, comps: comps, worst: worst, totalInk: totalInk, boxes: boxes };
    })().catch(e => ({
      /* v2.13.3 FAIL-CLOSED (Brandon, Aug 11 — order CB37369/WEOZ shipped
         "SURVEY NG": the traced SVG lost the capital I and the shipped files
         carried it, while the same art traces clean 3/3 on replay and the
         audit logic catches a missing I when it RUNS. The old catch returned
         ok:true — any audit crash silently PASSED the unverified trace. Now a
         crash counts as an audit failure: the retry loop re-attempts, and a
         persistently unauditable non-photo engrave trace BLOCKS the cart
         (never ship unverified). Distinctly flagged so GA4 separates
         "trace bad" from "audit could not run". */
      ok: false, crashed: true, error: (e && e.message) || 'audit-crashed',
      fails: 1, comps: 0, worst: 0,
      boxes: ['AUDIT CRASHED: ' + ((e && e.message) || 'unknown')],
    }));
  }

  /**
   * Re-rasterize SVG markup at a specific pixel size for production.
   * Sets explicit width/height on the SVG element so the browser
   * rasterizes at the target resolution instead of the viewBox default.
   */
  function rasterizeSvgForProduction(markup, targetW, targetH, inkBox) {
    return new Promise((resolve, reject) => {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(markup, 'image/svg+xml');
        const svg = doc.documentElement;
        if (doc.querySelector('parsererror')) {
          /* Can't parse — use regex to add dimensions (and the ink-box
             viewBox remap when available; replace any existing viewBox). */
          let sized = markup;
          if (inkBox && inkBox.w > 0 && inkBox.h > 0) {
            sized = sized.replace(/\spreserveAspectRatio="[^"]*"/, '');
    /* v2.12.3 TELEMETRY NET: a visible image layer with no drawable source
       paints NOTHING in this capture (paintLayer needs l.img) while
       production/prepress build the same layer from markup/b64 — the exact
       parity break of order CB202608051R5U. The capture guard should have
       repaired it; if anything still reaches here dead, say so loudly. */
    const _deadImgs = vis.filter(l => l.type === 'image' && !(l.img && l.img.complete && l.img.naturalWidth > 0));
    if (_deadImgs.length) {
      gwarn('mockup_missing_paint_source', sideSlug + ': ' + _deadImgs.length + ' visible image layer(s) with no display raster — ' +
        _deadImgs.map(l => (l.isBackdrop ? 'backdrop:' : '') + (l.name || l.id)).join(', '));
    }

            const vbAttr = `viewBox="${inkBox.x} ${inkBox.y} ${inkBox.w} ${inkBox.h}" preserveAspectRatio="none"`;
            sized = /viewBox="[^"]*"/.test(sized)
              ? sized.replace(/viewBox="[^"]*"/, vbAttr)
              : sized.replace(/<svg/, `<svg ${vbAttr}`);
          }
          sized = sized.replace(/<svg/, `<svg width="${targetW}" height="${targetH}"`);
          const blob = new Blob([sized], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
          img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('SVG load failed')); };
          img.src = url;
          return;
        }
        svg.setAttribute('width', targetW);
        svg.setAttribute('height', targetH);
        /* Ink-box remap (display parity): crop the viewBox to the ink rect
           and stretch it to the target box, exactly like the trimmed display
           raster drawn into the layer box. Absolute viewBox frame. */
        if (inkBox && inkBox.w > 0 && inkBox.h > 0) {
          svg.setAttribute('viewBox', `${inkBox.x} ${inkBox.y} ${inkBox.w} ${inkBox.h}`);
          svg.setAttribute('preserveAspectRatio', 'none');
        }
        const sized = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([sized], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('SVG load failed')); };
        img.src = url;
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Capture mockup preview — product image with design overlaid.
   */
  function mockupPng(sideSlug, scale = 1, withSim = false) {
    const ls = S.byView[sideSlug];
    if (!ls?.length || !ls.some(l => l.visible !== false)) return null;
    if (!mImg.complete || !mImg.naturalWidth) return null;

    /* Base dpr keeps everyday thumbnails small; callers that display the
       render large (immersive review hero) pass scale > 1 for a sharper
       output. All drawing is in CSS-px coordinates under the transform,
       so layout is identical at any scale. */
    const dpr = (window.devicePixelRatio || 1) * scale;
    let cw = mImg.offsetWidth, ch = mImg.offsetHeight;
    /* Guard: if the image has no layout dimensions (e.g. hidden or layout
       not yet settled), fall back to natural dimensions so we never create
       a 0×0 canvas that uploads as a 0-byte file. */
    if (!cw || !ch) {
      /* Layer coordinates are in CSS-pixel space (scaled by S.rscale).
         Approximate the CSS layout size so layers align with the mockup.
         v2.12.0: BASE width first, naturalWidth second — after the CRO #21
         hi-res CDN swap naturalWidth is 2000 while the frozen coordinate
         basis (S._mockupBaseNW, rscale, pa) stays at 1000, so leading with
         naturalWidth made cw 2x the space the layers live in and drew them
         misaligned at half size. */
      const nw = S._mockupBaseNW || mImg.naturalWidth || 1000;
      /* Height from the image's aspect when known, else from the canvas
         aspect ratio, else square (1:1). */
      const nh = (mImg.naturalWidth && mImg.naturalHeight)
        ? Math.round(nw * mImg.naturalHeight / mImg.naturalWidth)
        : (canvas.height && canvas.width
          ? Math.round(nw * canvas.height / canvas.width) : nw);
      const rs = S.rscaleByView[sideSlug] || S.rscale || 1;
      cw = Math.round(nw * rs);
      ch = Math.round(nh * rs);
      DEBUG && console.warn('[dt] mockupPng: offsetWidth was 0, using rscale*base', cw, ch);
    }
    let W = Math.round(cw * dpr), H = Math.round(ch * dpr);
    /* v2.12.0: pixel-budget clamp (same limits as the live canvas, ~L3236) —
       mockupPng had NONE, so a scale>1 capture on a dpr-3 phone could exceed
       iOS canvas caps and silently produce a blank PNG (blank mockup upload
       AND blank proof). The clamp folds into the effective dpr so all
       CSS-px drawing below is untouched. */
    const _budget = Math.min(1, 4096 / Math.max(W, H), Math.sqrt(1.6e7 / (W * H)));
    if (_budget < 1) { W = Math.max(1, Math.floor(W * _budget)); H = Math.max(1, Math.floor(H * _budget)); }
    const edpr = dpr * _budget;
    const off = document.createElement('canvas'); off.width = W; off.height = H;
    const oc = off.getContext('2d'); oc.setTransform(edpr, 0, 0, edpr, 0, 0);

    /* Draw the product mockup image */
    oc.drawImage(mImg, 0, 0, cw, ch);

    const vis = ls.filter(l => l.visible !== false);

    /* UX-Aug20: artwork-only canvas for sim effects — sim functions mask to
       artwork alpha, so they need a canvas with ONLY the design, no mockup.
       Without this the finish/sheen covers the entire PA (mockup is opaque)
       and curvature clearRect wipes the mockup leaving white gaps. */
    let _artCv = null, _artCx = null;
    if (withSim && vis.length) {
      _artCv = document.createElement('canvas'); _artCv.width = W; _artCv.height = H;
      _artCx = _artCv.getContext('2d'); _artCx.setTransform(edpr, 0, 0, edpr, 0, 0);
    }

    if (S.method === 'engrave') {
      /* Engrave mode: replicate the steel texture mask compositing.
         1. Draw all layers as black masks on an offscreen canvas.
         2. Draw the steel/overlay texture clipped to the print area.
         3. Mask the texture with the layer shapes (destination-in).
         4. Draw the result over the product mockup. */
      const mc = document.createElement('canvas'); mc.width = W; mc.height = H;
      const mctx = mc.getContext('2d'); mctx.setTransform(edpr, 0, 0, edpr, 0, 0);
      /* v2.11.0: same XOR pass as renderEngrave (mask parity — the cart/proof
         mockup must show what the DXF engraves; see v2.9.15 parity note). */
      vis.filter(l => !l._engraveInverted).forEach(l => {
        mctx.save();
        const _clip = maskClipFor(l, vis, b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
        if (_clip) mctx.clip(_clip);
        applyTransform(mctx, l); paintLayer(mctx, l, true); mctx.restore();
      });
      vis.filter(l => l._engraveInverted).forEach(l => {
        mctx.save();
        mctx.globalCompositeOperation = 'xor';
        const _clip = maskClipFor(l, vis, b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
        if (_clip) mctx.clip(_clip);
        applyTransform(mctx, l); paintLayer(mctx, l, true); mctx.restore();
      });

      const tc = document.createElement('canvas'); tc.width = W; tc.height = H;
      const tctx = tc.getContext('2d'); tctx.setTransform(edpr, 0, 0, edpr, 0, 0);
      const pa = S.pa;
      tctx.save(); tctx.beginPath(); tctx.rect(pa.x, pa.y, pa.w, pa.h); tctx.clip();
      if (S.overlayImg?.naturalWidth) {
        tctx.drawImage(S.overlayImg, pa.x, pa.y, pa.w, pa.h);
      } else {
        const g = tctx.createLinearGradient(pa.x, pa.y, pa.x+pa.w, pa.y+pa.h);
        g.addColorStop(0, '#d4d8dc'); g.addColorStop(0.5, '#c0c6cb'); g.addColorStop(1, '#c4c9cc');
        tctx.fillStyle = g; tctx.fillRect(pa.x, pa.y, pa.w, pa.h);
      }
      tctx.restore();
      tctx.globalCompositeOperation = 'destination-in';
      tctx.drawImage(mc, 0, 0, cw, ch);
      tctx.globalCompositeOperation = 'source-over';

      /* Composite the engraved layers over the product mockup (or artwork canvas for sim) */
      (_artCx || oc).drawImage(tc, 0, 0, cw, ch);
    } else {
      /* Print mode: draw layers directly over the product, CLIPPED to the
         print area — matching the live canvas (renderPrint) and the production
         render. Without this clip the cart/review/order mockup showed art
         hanging outside the print area that every production file crops. */
      const pa = S.pa;
      const _tgt = _artCx || oc;  /* UX-Aug20: artwork canvas when sim is on */
      _tgt.save();
      if (pa?.w && pa?.h) { _tgt.beginPath(); _tgt.rect(pa.x, pa.y, pa.w, pa.h); _tgt.clip(); }
      vis.forEach(l => {
        _tgt.save();
        /* Photo-mask clip — the cart/order mockup previously skipped it, so
           the APPROVED preview showed the photo unmasked while every
           production file cropped it (Jul 22 order). */
        const _clip = maskClipFor(l, vis, b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
        if (_clip) _tgt.clip(_clip);
        applyTransform(_tgt, l); paintLayer(_tgt, l, false); _tgt.restore();
      });
      _tgt.restore();
    }

    /* UX-Aug20: apply print-sim effects on the artwork-only canvas, then
       composite over the clean mockup. Artwork-only isolation ensures:
       - curvature clearRect gaps reveal the mockup underneath (not white)
       - finish/surface-light mask to artwork alpha only (not opaque mockup) */
    if (_artCv) {
      const _pa = S.pa;
      if (_pa && _pa.w && _pa.h) {
        _simTarget = { canvas: _artCv, ctx: _artCx, rs: edpr };
        try {
          applyCurvature(_pa);
          applySurfaceLight(_pa);
          if (S.method === 'engrave') applyEtchEdge(_pa);
          else applyFinish(_pa);
        } finally { _simTarget = null; }
      }
      oc.drawImage(_artCv, 0, 0, cw, ch);  /* composite simulated artwork over mockup */
    }

    /* v2.12.0: geometry contract for the proof sheet — the EXACT CSS-px
       canvas dims this capture drew with, plus the print area snapshot in
       the same space. Callers must read these instead of reconstructing
       denominators (the fallback branch above is unreachable from outside).
       toDataURL wrapped: it THROWS on a tainted canvas and the cart loop
       calls mockupPng uncaught — a taint regression must degrade to a
       missing mockup, not a failed order. */
    let _out = null;
    try { _out = off.toDataURL('image/png'); }
    catch (e) { DEBUG && console.warn('[dt] mockupPng toDataURL failed:', e.message); return null; }
    mockupPng.lastGeom = {
      cw, ch,
      pa: (S.pa && S.pa.w && S.pa.h) ? { x: S.pa.x, y: S.pa.y, w: S.pa.w, h: S.pa.h } : null,
    };
    return _out;
  }

  /* Render ONLY the print-area design contents onto a white square tile — used
     as the template gallery thumbnail (not the full product mockup). Print
     layers keep their colors; engrave layers render dark so they show on white. */
  function templateThumbPng(sideSlug) {
    const ls = S.byView[sideSlug];
    if (!ls?.length) return null;
    const vis = ls.filter(l => l.visible !== false);
    if (!vis.length) return null;
    const pa = S.pa;
    if (!pa || !pa.w || !pa.h) return null;

    const T = 440, pad = 28, inner = T - pad * 2;
    const scale = Math.min(inner / pa.w, inner / pa.h);
    const dW = pa.w * scale, dH = pa.h * scale;
    const ox = (T - dW) / 2, oy = (T - dH) / 2;

    const off = document.createElement('canvas'); off.width = T; off.height = T;
    const oc = off.getContext('2d');
    oc.fillStyle = '#ffffff'; oc.fillRect(0, 0, T, T);
    /* Map the print area into the centered tile; layers then draw normally. */
    oc.setTransform(scale, 0, 0, scale, ox - pa.x * scale, oy - pa.y * scale);

    const mask = (S.method === 'engrave');
    vis.forEach(l => {
      oc.save();
      const _clip = maskClipFor(l, vis, b => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
      if (_clip) oc.clip(_clip);
      applyTransform(oc, l); paintLayer(oc, l, mask); oc.restore();
    });

    return off.toDataURL('image/png');
  }

  /**
   * Wait for jsPDF to load.
   */
  function waitForJsPDF() {
    return new Promise((resolve, reject) => {
      if (window.jspdf?.jsPDF) return resolve(window.jspdf.jsPDF);
      let n = 0;
      const t = setInterval(() => {
        n++;
        if (window.jspdf?.jsPDF) { clearInterval(t); resolve(window.jspdf.jsPDF); }
        else if (n > 50) { clearInterval(t); reject(new Error('jsPDF failed to load')); }
      }, 100);
    });
  }

  /**
   * Lazy-load pdf-lib from CDN. Used only by PREPRESS PDF generation (live text).
   * Returns the PDFDocument class and supporting utilities from the library.
   * Loaded on demand at cart-add time — no impact on initial page load.
   */
  let _pdfLibCache = null;
  async function waitForPdfLib() {
    if (_pdfLibCache) return _pdfLibCache;
    if (window.PDFLib) { _pdfLibCache = window.PDFLib; return _pdfLibCache; }
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.min.js';
      s.onload = () => {
        if (window.PDFLib) { _pdfLibCache = window.PDFLib; resolve(_pdfLibCache); }
        else reject(new Error('pdf-lib loaded but PDFLib global not found'));
      };
      s.onerror = () => reject(new Error('Failed to load pdf-lib from CDN'));
      document.head.appendChild(s);
    });
  }

  /* Lazy-load @pdf-lib/fontkit (UMD → window.fontkit). pdf-lib's standalone
     bundle does NOT include fontkit, so embedFont(customFontBytes) throws
     ("no fontkit instance was found") and every prepress silently fell back
     to the raster painter (v2.9.4 fix, re-applied to the canonical source —
     the deployed min already carries it). Best-effort: resolves null on
     failure; the live-text painter then throws and the raster fallback
     ships, so a CDN hiccup degrades gracefully. */
  let _fontkitCache = null;
  async function waitForFontkit() {
    if (_fontkitCache) return _fontkitCache;
    if (window.fontkit) { _fontkitCache = window.fontkit; return _fontkitCache; }
    const load = src => new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = () => window.fontkit ? resolve(window.fontkit) : reject(new Error('fontkit global not found'));
      s.onerror = () => reject(new Error('fontkit CDN load failed'));
      document.head.appendChild(s);
    });
    try {
      _fontkitCache = await load('https://cdn.jsdelivr.net/npm/@pdf-lib/fontkit@1.1.1/dist/fontkit.umd.min.js');
    } catch (_) {
      try { _fontkitCache = await load('https://unpkg.com/@pdf-lib/fontkit@1.1.1/dist/fontkit.umd.min.js'); }
      catch (__) { _fontkitCache = null; }
    }
    return _fontkitCache;
  }

  /**
   * Generate a raster PDF at exact physical dimensions.
   */
  async function generateRasterPDF(sideSlug, compositePng) {
    const jsPDF = await waitForJsPDF();
    const phys = SIDE_PHYS[sideSlug];
    const pW = phys?.wInches || 4, pH = phys?.hInches || 4;
    const doc = new jsPDF({ orientation: pW >= pH ? 'landscape' : 'portrait', unit: 'in', format: [pW, pH] });
    doc.addImage(compositePng, 'PNG', 0, 0, pW, pH);
    doc.setProperties({ title: `Production - ${sideSlug} - ${S.method}`, creator: 'Custom Design Tool V1' });
    return doc.output('datauristring');
  }

  /* ── gwarn: PRODUCTION-VISIBLE pipeline warning (v2.9.10) ───────────
     Routes to GA4 dt_error via dtAnalytics + always-on console.warn.
     HISTORY: gwarn was called at 4 fallback sites since v2.8 but its
     definition was LOST (likely the Jul 6 re-baseline) — any reached
     call threw ReferenceError, and every other pipeline failure was a
     DEBUG-gated console.warn, so production file failures (e.g. an
     engrave order shipping PNG-only after a vectorizer outage, Jul 24
     Michigan order) were completely invisible. Defined as a function
     declaration so it hoists across the IIFE; dtAnalytics (const,
     defined later) is resolved at CALL time and guarded. */
  function gwarn(key, msg) {
    try { console.warn('[dt]', key + ':', msg); } catch (_) {}
    try {
      if (typeof dtAnalytics !== 'undefined' && dtAnalytics && dtAnalytics.error) dtAnalytics.error(key, msg);
    } catch (_) {}
  }

  /* ── RASTER EPS (print) — v2.9.6 ────────────────────────────────────
     Print EPS is now the 300 DPI composite embedded as a PostScript
     Level-3 DeviceRGB image with a stencil mask (ImageType 3,
     InterleaveType 2: each mask row precedes its RGB row in one
     Flate+ASCII85 stream; mask bit 1 = not painted, so nothing prints
     outside the design silhouette). WHY RASTER (Brandon, Jul 23 2026):
     the outlined vector EPS wrote device CMYK through a naive max-UCR
     formula — no ICC profile, no gamut mapping — so colors drifted on
     press (cream #F3F0E9 landed as a gray K tint), and a photo design's
     raster could not be represented at all (Jul 22 order shipped an EPS
     containing a single cream oval). A DeviceRGB raster hands color
     conversion to the color-managed RIP — the same treatment the PDFs
     get — so the EPS now matches the PDFs and the approved preview.
     ENGRAVE EPS (AI8 vector for EZCAD2) is unchanged. Limitations:
     EPS has no soft transparency, so pixels are opaque at alpha > 50%
     (AA edges harden — invisible at 300 DPI print size), and text is
     rasterized here (the PDFs still carry live/vector text). Falls back
     to an uncompressed ASCII85 stream when CompressionStream is
     unavailable. */
  function _ascii85Encode(bytes) {
    const out = [];
    for (let i = 0; i < bytes.length; i += 4) {
      const rem = Math.min(4, bytes.length - i);
      let n = 0;
      for (let k = 0; k < 4; k++) n = n * 256 + (k < rem ? bytes[i + k] : 0);
      if (n === 0 && rem === 4) { out.push('z'); continue; }
      const cs = new Array(5);
      for (let k = 4; k >= 0; k--) { cs[k] = String.fromCharCode(33 + (n % 85)); n = Math.floor(n / 85); }
      out.push(cs.slice(0, rem + 1).join(''));
    }
    return out.join('') + '~>';
  }

  /* Interleave stencil-mask rows with RGB rows (ImageType 3 / IT2).
     Mask bit 1 = masked (not painted); transparent pixels get white RGB
     so a RIP that ignores the mask still prints paper-white there. */
  function _rasterEpsInterleave(rgba, w, h) {
    const maskRowBytes = (w + 7) >> 3;
    const buf = new Uint8Array(h * (maskRowBytes + w * 3));
    let o = 0;
    for (let y = 0; y < h; y++) {
      const maskStart = o; o += maskRowBytes;
      let i = y * w * 4;
      for (let x = 0; x < w; x++, i += 4) {
        if (rgba[i + 3] <= 127) {
          buf[maskStart + (x >> 3)] |= (0x80 >> (x & 7));
          buf[o] = 255; buf[o + 1] = 255; buf[o + 2] = 255;
        } else {
          buf[o] = rgba[i]; buf[o + 1] = rgba[i + 1]; buf[o + 2] = rgba[i + 2];
        }
        o += 3;
      }
    }
    return buf;
  }

  async function _deflateBytes(bytes) {
    if (typeof CompressionStream !== 'function') return null;
    try {
      const cs = new CompressionStream('deflate'); /* zlib wrapper = FlateDecode */
      const stream = new Blob([bytes]).stream().pipeThrough(cs);
      const ab = await new Response(stream).arrayBuffer();
      return new Uint8Array(ab);
    } catch (_) { return null; }
  }

  function _assembleRasterEps(w, h, physDims, encoded, flate) {
    const ptW = physDims.wInches * 72, ptH = physDims.hInches * 72;
    const mtx = (w / ptW).toFixed(6) + ' 0 0 ' + (-h / ptH).toFixed(6) + ' 0 ' + h;
    const filters = flate
      ? 'currentfile /ASCII85Decode filter /FlateDecode filter'
      : 'currentfile /ASCII85Decode filter';
    const lines = [];
    for (let i = 0; i < encoded.length; i += 75) lines.push(encoded.slice(i, i + 75));
    return '%!PS-Adobe-3.0 EPSF-3.0\n'
      + '%%Creator: Custom Design Tool V2\n'
      + '%%CreationDate: ' + new Date().toISOString() + '\n'
      + '%%LanguageLevel: 3\n'
      + '%%BoundingBox: 0 0 ' + Math.ceil(ptW) + ' ' + Math.ceil(ptH) + '\n'
      + '%%HiResBoundingBox: 0 0 ' + ptW.toFixed(4) + ' ' + ptH.toFixed(4) + '\n'
      + '%%EndComments\n'
      + 'gsave\n'
      + '/DeviceRGB setcolorspace\n'
      + '<<\n'
      + '  /ImageType 3\n'
      + '  /InterleaveType 2\n'
      + '  /DataDict <<\n'
      + '    /ImageType 1\n'
      + '    /Width ' + w + ' /Height ' + h + '\n'
      + '    /BitsPerComponent 8\n'
      + '    /Decode [0 1 0 1 0 1]\n'
      + '    /ImageMatrix [' + mtx + ']\n'
      + '    /DataSource ' + filters + '\n'
      + '  >>\n'
      + '  /MaskDict <<\n'
      + '    /ImageType 1\n'
      + '    /Width ' + w + ' /Height ' + h + '\n'
      + '    /BitsPerComponent 1\n'
      + '    /Decode [0 1]\n'
      + '    /ImageMatrix [' + mtx + ']\n'
      + '  >>\n'
      + '>> image\n'
      + lines.join('\n') + '\n'
      + 'grestore\n'
      + '%%EOF\n';
  }

  async function generateRasterEPS(compositePng, physDims) {
    if (!compositePng || !physDims?.wInches || !physDims?.hInches) return null;
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('raster EPS: composite load failed'));
      i.src = compositePng;
    });
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return null;
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const cx = c.getContext('2d');
    cx.drawImage(img, 0, 0);
    const rgba = cx.getImageData(0, 0, w, h).data;
    const interleaved = _rasterEpsInterleave(rgba, w, h);
    const deflated = await _deflateBytes(interleaved);
    return _assembleRasterEps(w, h, physDims, _ascii85Encode(deflated || interleaved), !!deflated);
  }

  /**
   * Parse an SVG/CSS color value to [r, g, b] (0-255).
   * Handles hex (#RGB, #RRGGBB), rgb()/rgba(), and named black/white.
   * Returns [0,0,0] for values it can't parse.
   */
  function _parseSvgColor(fill) {
    if (!fill || fill === 'none' || fill === 'transparent') return [0, 0, 0];
    if (fill === 'white' || fill === '#fff' || fill === '#ffffff') return [255, 255, 255];
    if (fill === 'black' || fill === '#000' || fill === '#000000') return [0, 0, 0];
    if (fill.charAt(0) === '#') {
      var h = fill.slice(1);
      if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
      var num = parseInt(h, 16);
      if (!isNaN(num)) return [(num>>16)&255, (num>>8)&255, num&255];
    }
    var m = fill.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (m) return [parseInt(m[1],10), parseInt(m[2],10), parseInt(m[3],10)];
    return [0, 0, 0];
  }

  /**
   * Convert an SVG path d-string to jsPDF path operations.
   * Handles all SVG path commands (M, L, H, V, C, S, Q, T, A, Z)
   * with both absolute and relative variants.
   * @param {string} d   SVG path d-attribute
   * @param {number} sc  Scale factor (production-pixels → inches = 1/DPI)
   * @returns {Array<{op:string, c?:number[]}>}  jsPDF path ops
   */
  function _svgDToJsPDFOps(d, sc) {
    var tokens = d.match(/[a-zA-Z]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || [];
    var i = 0, ops = [], cx = 0, cy = 0, sx = 0, sy = 0;
    var lastCp2x = 0, lastCp2y = 0, lastQpx = 0, lastQpy = 0, prevCmd = '';
    var n = function() { return parseFloat(tokens[i++]); };
    var isN = function() { return i < tokens.length && /[0-9.+-]/.test(tokens[i]); };
    /* v2.9.23: SVG arc flags are single 0/1 chars that may be FUSED with the
       following number ("0140" = large 0, sweep 1, then x 40). The shared
       number tokenizer merges them into one token, which misaligned every
       token after the arc and silently dropped the path remainder. Peel
       flags one char at a time per the SVG BNF. */
    var nf = function() {
      var t = tokens[i];
      if (t === '0' || t === '1') { i++; return +t; }
      var c = t ? t.charAt(0) : '';
      if (c === '0' || c === '1') { tokens[i] = t.slice(1); return +c; }
      i++; return NaN;
    };
    var p = function(v) { return +(v * sc).toFixed(4); }; /* 4dp in inches = 0.0001" ≈ 0.003mm */

    while (i < tokens.length) {
      var cmd = tokens[i];
      if (/[a-zA-Z]/.test(cmd)) i++; else break;
      switch (cmd) {
        case 'M': cx=n();cy=n();sx=cx;sy=cy;
          ops.push({op:'m',c:[p(cx),p(cy)]});
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy;
          while(isN()){cx=n();cy=n();ops.push({op:'l',c:[p(cx),p(cy)]});} break;
        case 'm': cx+=n();cy+=n();sx=cx;sy=cy;
          ops.push({op:'m',c:[p(cx),p(cy)]});
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy;
          while(isN()){cx+=n();cy+=n();ops.push({op:'l',c:[p(cx),p(cy)]});} break;
        case 'L': while(isN()){cx=n();cy=n();ops.push({op:'l',c:[p(cx),p(cy)]});}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'l': while(isN()){cx+=n();cy+=n();ops.push({op:'l',c:[p(cx),p(cy)]});}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'H': while(isN()){cx=n();ops.push({op:'l',c:[p(cx),p(cy)]});}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'h': while(isN()){cx+=n();ops.push({op:'l',c:[p(cx),p(cy)]});}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'V': while(isN()){cy=n();ops.push({op:'l',c:[p(cx),p(cy)]});}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'v': while(isN()){cy+=n();ops.push({op:'l',c:[p(cx),p(cy)]});}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'C': while(isN()){var x1=n(),y1=n(),x2=n(),y2=n(),x3=n(),y3=n();
          ops.push({op:'c',c:[p(x1),p(y1),p(x2),p(y2),p(x3),p(y3)]});
          lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;} lastQpx=cx;lastQpy=cy; break;
        case 'c': while(isN()){var x1=cx+n(),y1=cy+n(),x2=cx+n(),y2=cy+n(),x3=cx+n(),y3=cy+n();
          ops.push({op:'c',c:[p(x1),p(y1),p(x2),p(y2),p(x3),p(y3)]});
          lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;} lastQpx=cx;lastQpy=cy; break;
        case 'S': case 's': { var rel=cmd==='s';
          while(isN()){
            var rx1,ry1;
            if('CcSs'.indexOf(prevCmd)>=0){rx1=2*cx-lastCp2x;ry1=2*cy-lastCp2y;}
            else{rx1=cx;ry1=cy;}
            var x2=n(),y2=n(),x3=n(),y3=n();
            if(rel){x2+=cx;y2+=cy;x3+=cx;y3+=cy;}
            ops.push({op:'c',c:[p(rx1),p(ry1),p(x2),p(y2),p(x3),p(y3)]});
            lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;prevCmd=cmd;}
          lastQpx=cx;lastQpy=cy; break;}
        case 'Q': case 'q': { var rel=cmd==='q';
          while(isN()){var qx=n(),qy=n(),ex=n(),ey=n();
            if(rel){qx+=cx;qy+=cy;ex+=cx;ey+=cy;}
            var c1x=cx+2/3*(qx-cx),c1y=cy+2/3*(qy-cy),c2x=ex+2/3*(qx-ex),c2y=ey+2/3*(qy-ey);
            ops.push({op:'c',c:[p(c1x),p(c1y),p(c2x),p(c2y),p(ex),p(ey)]});
            lastQpx=qx;lastQpy=qy;cx=ex;cy=ey;prevCmd=cmd;}
          lastCp2x=cx;lastCp2y=cy; break;}
        case 'T': case 't': { var rel=cmd==='t';
          while(isN()){
            var qx,qy;
            if('QqTt'.indexOf(prevCmd)>=0){qx=2*cx-lastQpx;qy=2*cy-lastQpy;}
            else{qx=cx;qy=cy;}
            var ex=n(),ey=n();
            if(rel){ex+=cx;ey+=cy;}
            var c1x=cx+2/3*(qx-cx),c1y=cy+2/3*(qy-cy),c2x=ex+2/3*(qx-ex),c2y=ey+2/3*(qy-ey);
            ops.push({op:'c',c:[p(c1x),p(c1y),p(c2x),p(c2y),p(ex),p(ey)]});
            lastQpx=qx;lastQpy=qy;cx=ex;cy=ey;prevCmd=cmd;}
          lastCp2x=cx;lastCp2y=cy; break;}
        case 'A': case 'a': { var rel=cmd==='a';
          while(isN()){var arx=n(),ary=n(),arot=n(),alarge=nf(),asweep=nf();
            var aex=n(),aey=n(); if(rel){aex+=cx;aey+=cy;}
            var bz=svgArcToBeziers(cx,cy,arx,ary,arot,alarge,asweep,aex,aey);
            for(var bi=0;bi<bz.length;bi++){var b=bz[bi];
              ops.push({op:'c',c:[p(b[0]),p(b[1]),p(b[2]),p(b[3]),p(b[4]),p(b[5])]});
              cx=b[4];cy=b[5];}
            if(!bz.length){ops.push({op:'l',c:[p(aex),p(aey)]});cx=aex;cy=aey;}}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;}
        case 'Z': case 'z': ops.push({op:'h'}); cx=sx;cy=sy;
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        default: i++; break;
      }
      prevCmd = cmd;
    }
    return ops;
  }

  /* ── Shared jsPDF path painter (stroke-aware) ────────────────────────
     One painter for every flattenSvgPaths entry so PRODUCTION and prepress-
     fallback PDFs stop mapping fill="none" to BLACK. Rules:
       fill + no stroke        → fill()                       (unchanged)
       fill + stroke           → correct colors, fillStroke() (faux-bold text:
                                 stroke === fill, behaves exactly as before)
       fill="none" + stroke    → stroke() ONLY, in the STROKE color (the Jul 22
                                 order shipped the orange ring as a black-filled
                                 oval painted over the photo)
       fill="none" + no stroke → skipped (no ink; parity with the EPS/DXF drop)
     shiftIn: optional coordinate offset in inches (prepress bleed).
     Returns true when it painted. */
  function _paintPdfPathEntry(doc, entry, sc, shiftIn) {
    var d = typeof entry === 'string' ? entry : entry.d;
    if (!d) return false;
    var fill = typeof entry === 'string' ? '#000' : (entry.fill || '#000');
    var stroke = typeof entry === 'string' ? null : (entry.stroke || null);
    var entrySW = typeof entry === 'string' ? 0 : (entry.strokeWidth || 0);
    var fillNone = String(fill).toLowerCase() === 'none' || String(fill).toLowerCase() === 'transparent';
    var strokeNone = !stroke || String(stroke).toLowerCase() === 'none' || String(stroke).toLowerCase() === 'transparent';
    /* Legacy contract: a bare strokeWidth with NO stroke color is faux-bold
       text (same-color stroke) — keep fattening with the fill color. */
    if (strokeNone && entrySW && !fillNone) { stroke = fill; strokeNone = false; }
    if (fillNone && strokeNone) return false; /* no representable ink */
    if (fillNone && !entrySW) entrySW = 1;    /* SVG default stroke-width */

    var pdfOps = _svgDToJsPDFOps(d, sc);
    if (!pdfOps.length) return false;
    if (shiftIn) {
      pdfOps = pdfOps.map(function (op) {
        if (!op.c) return op;
        var shifted = op.c.slice();
        for (var si = 0; si < shifted.length; si++) shifted[si] = +(shifted[si] + shiftIn).toFixed(4);
        return { op: op.op, c: shifted };
      });
    }
    try {
      doc.path(pdfOps);
      if (!strokeNone && typeof doc.fillStroke === 'function' && typeof doc.stroke === 'function') {
        var srgb = _parseSvgColor(stroke);
        doc.setDrawColor(srgb[0], srgb[1], srgb[2]);
        doc.setLineWidth((entrySW || 1) * sc);
        doc.setLineJoin('round');
        if (fillNone) {
          doc.stroke();
        } else {
          var frgb = _parseSvgColor(fill);
          doc.setFillColor(frgb[0], frgb[1], frgb[2]);
          doc.fillStroke();
        }
      } else if (!fillNone) {
        var rgb = _parseSvgColor(fill);
        doc.setFillColor(rgb[0], rgb[1], rgb[2]);
        doc.fill();
      } else {
        /* stroke-only path but this jsPDF has no stroke() — filling it would
           turn the ring into a solid disc; discard instead. */
        try { doc.discardPath(); } catch (_) {}
        return false;
      }
      return true;
    } catch (pathErr) {
      DEBUG && console.warn('[dt] PDF path draw error:', pathErr.message);
      try { doc.discardPath(); } catch (_) {}
      return false;
    }
  }

  /* ══════════════════════════════════════════════════════════════════
   * ALPHA CONTOUR TRACING — Marching Squares + Douglas-Peucker
   * Traces the opaque silhouette of a composite canvas into SVG path data
   * for use as a clipping path in the PRODUCTION PDF. Downsamples to
   * ~512 px max dimension, traces with marching squares, simplifies with
   * Douglas-Peucker, and caps per-contour point count at ~1000.
   * ══════════════════════════════════════════════════════════════════ */

  /**
   * Trace alpha contours of a canvas data URL → array of SVG path d-strings.
   * Each d-string is a closed polygon representing one connected opaque region.
   * The coordinates are in the ORIGINAL canvas pixel space (upscaled from the
   * downsampled tracing resolution).
   *
   * @param {string} pngDataUrl - composite PNG data URL
   * @param {number} origW - original canvas width in px
   * @param {number} origH - original canvas height in px
   * @param {number} [alphaThreshold=128] - alpha value above which a pixel is "opaque"
   * @returns {Promise<string[]>} array of SVG path d-strings
   */
  async function traceAlphaContours(pngDataUrl, origW, origH, alphaThreshold) {
    if (!pngDataUrl) return [];
    /* v2.9.15: alpha gate 128 -> 16. At 128 the downsample to MAX_TRACE pushed
       anti-aliased and thin-stroke pixels below the test, so they vanished from
       the silhouette — and the clip then CUT THAT INK OUT of the full-resolution
       raster it wraps. The mask was not merely coarse, it was SUBTRACTIVE
       (Commonwealth Mor Wine logo, Jul 25 2026). Measured by running this
       function against real print artwork at MAX_TRACE 512, rasterizing the
       resulting compound clip and comparing it to true ink (alpha > 0):
       ink deleted falls 3-4x. Holds under both bilinear and box downsampling,
       so it does not depend on which resampler the browser picks. See the
       v2.9.15 changelog entry for the figures and the caveats.
       Close cousin, NOT the same test: trimToContent and computeSvgInkBox use
       alpha > 16 (i.e. >= 17) as a per-pixel gate feeding a row/column density
       sum for a bbox. Same intent and same order of magnitude, one off in the
       comparison; they are not required to match and are not mirrored here.
       The EPS stencil keeps its own <= 127 gate (see _rasterEpsInterleave):
       PostScript has no soft transparency, so a 50% cut is correct there and
       the divergence is deliberate. */
    const THRESHOLD = alphaThreshold || 16;
    /* Downsample to max ~512px on longest side for performance.
       MEASURED, LEFT ALONE (v2.9.15): raising this was proposed and rejected on
       evidence. On the two assets above, going 512 -> 1024 helped one
       (7.95% -> 5.55% ink deleted) and hurt the other (0.14% -> 1.80%), and at
       full source resolution both got worse (8.77% / 3.22%) while runtime rose.
       There is no value that wins on both, so the shipped 512 stands. */
    const MAX_TRACE = 512;
    const scale = Math.min(1, MAX_TRACE / Math.max(origW, origH));
    const tw = Math.max(1, Math.round(origW * scale));
    const th = Math.max(1, Math.round(origH * scale));
    const upscale = 1 / scale; /* to map traced coords back to original space */

    /* Draw the PNG onto a small canvas to read alpha */
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('contour trace: image load failed'));
      i.src = pngDataUrl;
    });
    const tc = document.createElement('canvas');
    tc.width = tw; tc.height = th;
    const tctx = tc.getContext('2d');
    tctx.drawImage(img, 0, 0, tw, th);
    const imgData = tctx.getImageData(0, 0, tw, th);
    const alpha = imgData.data;

    /* Build binary grid: 1 = opaque, 0 = transparent.
       Grid is (tw+1) x (th+1) — marching squares walks cell corners. */
    function isOpaque(x, y) {
      if (x < 0 || x >= tw || y < 0 || y >= th) return false;
      return alpha[(y * tw + x) * 4 + 3] >= THRESHOLD;
    }

    /* ── Marching squares ──
       Walk the grid to find contour edges between opaque and transparent cells.
       Each cell is a 2×2 block of grid points; the 4-bit case index determines
       which edges the contour crosses. We follow edges to form closed polygons. */
    const visited = new Uint8Array((tw + 1) * (th + 1));
    const contours = [];

    /* Cell case: 4-bit index from corners TL, TR, BR, BL */
    function cellCase(cx, cy) {
      const tl = isOpaque(cx, cy) ? 8 : 0;
      const tr = isOpaque(cx + 1, cy) ? 4 : 0;
      const br = isOpaque(cx + 1, cy + 1) ? 2 : 0;
      const bl = isOpaque(cx, cy + 1) ? 1 : 0;
      return tl | tr | br | bl;
    }

    /* Edge midpoints for a cell at (cx, cy). Edges numbered 0-3:
       0 = top edge, 1 = right edge, 2 = bottom edge, 3 = left edge */
    function edgeMid(cx, cy, edge) {
      switch (edge) {
        case 0: return [cx + 0.5, cy];       /* top */
        case 1: return [cx + 1, cy + 0.5];   /* right */
        case 2: return [cx + 0.5, cy + 1];   /* bottom */
        case 3: return [cx, cy + 0.5];       /* left */
      }
    }

    /* For each marching-squares case, the edges the contour crosses.
       Each entry is an array of [entryEdge, exitEdge] pairs. */
    const MS_EDGES = [
      [],             /* 0: all transparent */
      [[2, 3]],       /* 1: BL */
      [[1, 2]],       /* 2: BR */
      [[1, 3]],       /* 3: BR+BL */
      [[0, 1]],       /* 4: TR */
      [[0, 3], [1, 2]], /* 5: TR+BL (saddle) */
      [[0, 2]],       /* 6: TR+BR */
      [[0, 3]],       /* 7: TR+BR+BL */
      [[3, 0]],       /* 8: TL */
      [[2, 0]],       /* 9: TL+BL */
      [[3, 0], [1, 2]], /* 10: TL+BR (saddle) */
      [[1, 0]],       /* 11: TL+BR+BL → actually TL+BL+BR */
      [[3, 1]],       /* 12: TL+TR */
      [[2, 1]],       /* 13: TL+TR+BL */
      [[3, 2]],       /* 14: TL+TR+BR */
      [],             /* 15: all opaque */
    ];

    /* Opposite edge for following contour across cells */
    const OPP_EDGE = [2, 3, 0, 1]; /* top↔bottom, right↔left */
    const EDGE_DX = [0, 1, 0, -1];
    const EDGE_DY = [-1, 0, 1, 0];

    for (let cy = -1; cy < th; cy++) {
      for (let cx = -1; cx < tw; cx++) {
        const c = cellCase(cx, cy);
        if (c === 0 || c === 15) continue;
        const edges = MS_EDGES[c];
        for (const [eIn, eOut] of edges) {
          const vKey = ((cy + 1) * (tw + 2) + (cx + 1)) * 4 + eOut;
          if (visited[vKey % visited.length]) continue;
          /* Trace this contour */
          const pts = [];
          let tcx = cx, tcy = cy, tEdge = eOut;
          let steps = 0;
          const MAX_STEPS = (tw + th) * 4;
          while (steps++ < MAX_STEPS) {
            const pt = edgeMid(tcx, tcy, tEdge);
            pts.push([pt[0] * upscale, pt[1] * upscale]);
            const vk = ((tcy + 1) * (tw + 2) + (tcx + 1)) * 4 + tEdge;
            visited[vk % visited.length] = 1;
            /* Move to neighbor cell across this edge */
            tcx += EDGE_DX[tEdge];
            tcy += EDGE_DY[tEdge];
            const nc = cellCase(tcx, tcy);
            const entryEdge = OPP_EDGE[tEdge];
            /* Find exit edge in neighbor cell */
            const ne = MS_EDGES[nc];
            let found = false;
            for (const [nIn, nOut] of ne) {
              if (nIn === entryEdge) { tEdge = nOut; found = true; break; }
            }
            if (!found) break;
            /* Check if we've returned to start */
            if (tcx === cx && tcy === cy && tEdge === eOut) break;
          }
          if (pts.length >= 3) contours.push(pts);
        }
      }
    }

    /* ── Douglas-Peucker simplification ── */
    function dpSimplify(points, tolerance) {
      if (points.length <= 2) return points;
      let maxDist = 0, maxIdx = 0;
      const a = points[0], b = points[points.length - 1];
      const dx = b[0] - a[0], dy = b[1] - a[1];
      const lenSq = dx * dx + dy * dy;
      for (let i = 1; i < points.length - 1; i++) {
        let dist;
        if (lenSq === 0) {
          const ex = points[i][0] - a[0], ey = points[i][1] - a[1];
          dist = Math.sqrt(ex * ex + ey * ey);
        } else {
          const t = Math.max(0, Math.min(1, ((points[i][0] - a[0]) * dx + (points[i][1] - a[1]) * dy) / lenSq));
          const px = a[0] + t * dx, py = a[1] + t * dy;
          const ex = points[i][0] - px, ey = points[i][1] - py;
          dist = Math.sqrt(ex * ex + ey * ey);
        }
        if (dist > maxDist) { maxDist = dist; maxIdx = i; }
      }
      if (maxDist > tolerance) {
        const left = dpSimplify(points.slice(0, maxIdx + 1), tolerance);
        const right = dpSimplify(points.slice(maxIdx), tolerance);
        return left.slice(0, -1).concat(right);
      }
      return [points[0], points[points.length - 1]];
    }

    /* Simplify each contour and cap point count */
    const MAX_PTS = 1000;
    const DP_TOL = 1.5 * upscale; /* ~1.5px at trace resolution */
    const result = [];
    for (let contour of contours) {
      contour = dpSimplify(contour, DP_TOL);
      /* If still too many points, increase tolerance iteratively */
      let tol = DP_TOL;
      while (contour.length > MAX_PTS && tol < 20 * upscale) {
        tol *= 1.5;
        contour = dpSimplify(contour, tol);
      }
      if (contour.length < 3) continue;
      /* Build SVG path d-string */
      const d = contour.map((pt, i) =>
        (i === 0 ? 'M' : 'L') + pt[0].toFixed(1) + ' ' + pt[1].toFixed(1)
      ).join(' ') + ' Z';
      result.push(d);
    }
    return result;
  }

  /**
   * Convert an array of SVG path d-strings to jsPDF path operations for
   * use as a compound clipping path. All contours become subpaths of a
   * single path (non-zero winding rule handles the union naturally).
   * Coordinates are scaled from canvas pixels to PDF inches.
   *
   * @param {string[]} dStrings - SVG path d-strings from traceAlphaContours
   * @param {number} scaleX - pixels → inches scale (1 / pxW * wInches)
   * @param {number} scaleY - pixels → inches scale (1 / pxH * hInches)
   * @returns {Array} jsPDF path operations
   */
  function contoursToPdfClipOps(dStrings, scaleX, scaleY) {
    const ops = [];
    for (const d of dStrings) {
      const pdfOps = _svgDToJsPDFOps(d, 1); /* already in px, we'll scale after */
      for (const op of pdfOps) {
        const scaled = { op: op.op };
        if (op.c) {
          scaled.c = op.c.map((v, i) => +(v * (i % 2 === 0 ? scaleX : scaleY)).toFixed(4));
        }
        ops.push(scaled);
      }
    }
    return ops;
  }

  /**
   * Generate a hybrid PDF at exact physical dimensions.
   * Text and vector content rendered as native PDF vector paths (resolution-independent).
   * Raster content embedded as PNG images at production DPI.
   * Falls back to raster PDF if jsPDF path support is unavailable.
   */
  async function generateHybridPDF(sideSlug, compositePng, tracedRasterSvg) {
    var jsPDF = await waitForJsPDF();
    var cs = getProductionCoordSpace(sideSlug);
    if (!cs) return generateRasterPDF(sideSlug, compositePng);

    /* Check jsPDF path support — fall back to raster if unavailable */
    var testDoc = new jsPDF({ unit: 'in', format: [1, 1] });
    if (typeof testDoc.path !== 'function') {
      DEBUG && console.warn('[dt] jsPDF.path not available, falling back to raster PDF');
      return generateRasterPDF(sideSlug, compositePng);
    }

    var pxW = cs.pxW, pxH = cs.pxH, phys = cs.phys;
    var wIn = phys.wInches, hIn = phys.hInches;
    var DPI = PROD_DPI;
    var isEng = S.method === 'engrave';

    var doc = new jsPDF({
      orientation: wIn >= hIn ? 'landscape' : 'portrait',
      unit: 'in', format: [wIn, hIn],
    });

    var visible = (S.byView[sideSlug] || []).filter(function(l) { return l.visible !== false; });
    var hasText = visible.some(function(l) { return l.type === 'text' && (l.text || '').trim(); });
    var hasVector = visible.some(function(l) { return l.type !== 'text' && l.isSvg && l.svgMarkup; });
    var hasRaster = visible.some(function(l) { return l.type !== 'text' && !(l.isSvg && l.svgMarkup); });

    /* ── Raster background: embed actual PNG (not traced) ──
       Engrave orders with a traced raster skip the PNG embed — the trace is
       included as vector paths below, keeping the engrave PDF all-vector. */
    var drewRaster = false;
    var useTracedRaster = isEng && !!tracedRasterSvg;
    if (hasRaster && !useTracedRaster) {
      var rasterPng = (hasText || hasVector)
        ? await renderProductionPNG(sideSlug, function(l) { return l.type !== 'text' && !(l.isSvg && l.svgMarkup); })
        : compositePng;
      if (rasterPng) { doc.addImage(rasterPng, 'PNG', 0, 0, wIn, hIn); drewRaster = true; }
    }

    /* ── Vector overlay: text + SVG layers as native PDF paths ── */
    if (hasText || hasVector || useTracedRaster) {
      var paintedCount = 0;
      var outlinedSvg = await buildCompositeOutlinedSvg(sideSlug, isEng, useTracedRaster ? tracedRasterSvg : null);
      if (outlinedSvg) {
        var parser = new DOMParser();
        var svgDoc = parser.parseFromString(outlinedSvg, 'image/svg+xml');
        if (!svgDoc.querySelector('parsererror')) {
          var paths = flattenSvgPaths(svgDoc);
          var sc = 1 / DPI;

          for (var pi = 0; pi < paths.length; pi++) {
            var entry = paths[pi];
            var d = typeof entry === 'string' ? entry : entry.d;
            var fill = typeof entry === 'string' ? '#000' : (entry.fill || '#000');
            var entrySW = typeof entry === 'string' ? 0 : (entry.strokeWidth || 0);
            if (!d) continue;

            var rgb = _parseSvgColor(fill);
            doc.setFillColor(rgb[0], rgb[1], rgb[2]);

            var pdfOps = _svgDToJsPDFOps(d, sc);
            if (pdfOps.length) {
              /* jsPDF 2.5.1's path() silently ignores a style argument —
                 path(ops, 'F') describes the path but never paints it, which
                 shipped fully blank PDFs (order CB36100). The path-builder
                 API requires an explicit fill() to emit the `f` operator.
                 Faux-bold text carries a same-color stroke: fillStroke()
                 fattens the glyphs like the canvas's synthetic bold; if
                 fillStroke is unavailable, plain fill() keeps the correct
                 glyph shape at regular weight. */
              try {
                doc.path(pdfOps);
                if (entrySW && typeof doc.fillStroke === 'function') {
                  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
                  doc.setLineWidth(entrySW * sc);
                  doc.setLineJoin('round');
                  doc.fillStroke();
                } else {
                  doc.fill();
                }
                paintedCount++;
              } catch (pathErr) {
                DEBUG && console.warn('[dt] PDF path draw error:', pathErr.message);
                try { doc.discardPath(); } catch (_) {}
              }
            }
          }
        }
      }
      if (!paintedCount && !drewRaster && compositePng) {
        /* Outlining failed or produced nothing paintable — embed the full
           composite PNG as raster fallback so the PDF is never blank. */
        gwarn('pdf_raster_fallback', 'Vector outlining produced no paintable paths for ' + sideSlug + ' – PDF uses raster composite instead');
        doc.addImage(compositePng, 'PNG', 0, 0, wIn, hIn);
      }
    }

    doc.setProperties({ title: 'Production - ' + sideSlug + ' - ' + S.method, creator: 'Custom Design Tool V2' });
    return doc.output('datauristring');
  }

  /* ══════════════════════════════════════════════════════════════════
   * PRODUCTION PDF — outlined text, print-area clipping, silhouette
   * clipping path from alpha contours. Replaces generateHybridPDF for
   * the print path. Filename ends with "PRODUCTION".
   * ══════════════════════════════════════════════════════════════════ */
  async function generateProductionPDF(sideSlug, compositePng, tracedSvgStr) {
    var jsPDF = await waitForJsPDF();
    var cs = getProductionCoordSpace(sideSlug);
    if (!cs) return generateRasterPDF(sideSlug, compositePng);

    var testDoc = new jsPDF({ unit: 'in', format: [1, 1] });
    if (typeof testDoc.path !== 'function') {
      DEBUG && console.warn('[dt] jsPDF.path not available, falling back to raster PDF');
      return generateRasterPDF(sideSlug, compositePng);
    }

    var pxW = cs.pxW, pxH = cs.pxH, phys = cs.phys;
    var wIn = phys.wInches, hIn = phys.hInches;
    var DPI = PROD_DPI;

    var doc = new jsPDF({
      orientation: wIn >= hIn ? 'landscape' : 'portrait',
      unit: 'in', format: [wIn, hIn],
    });

    var visible = (S.byView[sideSlug] || []).filter(function(l) { return l.visible !== false; });
    var hasText = visible.some(function(l) { return l.type === 'text' && (l.text || '').trim(); });
    var hasVector = visible.some(function(l) { return l.type !== 'text' && l.isSvg && l.svgMarkup; });
    var hasRaster = visible.some(function(l) { return l.type !== 'text' && !(l.isSvg && l.svgMarkup); });

    /* ── Silhouette clip: RASTER EMBED ONLY (v2.9.8, ROMO CPA order) ──
       The traced silhouette is a <=512px marching-squares polygon of the
       composite (Douglas-Peucker simplified). Clipping VECTOR content with
       it laid a low-res copy of the art over the true outlines and CHEWED
       every glyph edge — a text-only back's crisp opentype paths rendered
       shredded while the EPS (no such clip) was perfect. Vector paths ARE
       their own exact silhouette; the clip's only legitimate job is the
       safety crop around the raster PNG for RIPs that ignore its SMask.
       So: trace only when a raster is embedded, and clip ONLY around
       addImage (open/close helpers below); vectors paint unclipped. */
    var _clipOps = null;
    if (hasRaster) {
      try {
        var silhouetteDStrings = await traceAlphaContours(compositePng, pxW, pxH);
        if (silhouetteDStrings.length) {
          var scX = wIn / pxW, scY = hIn / pxH;
          var ops = contoursToPdfClipOps(silhouetteDStrings, scX, scY);
          if (ops.length) _clipOps = ops;
        }
      } catch (traceErr) {
        DEBUG && console.warn('[dt] Silhouette trace failed:', traceErr.message);
      }
    }
    var _openSilhouetteClip = function () {
      if (!_clipOps) return false;
      try {
        doc.saveGraphicsState();
        doc.path(_clipOps);
        if (typeof doc.clip === 'function') {
          doc.clip('non-zero');
          /* jsPDF's clip() emits only `W` — end the path with `n` or the
             clip is an invalid operator sequence (viewer-dependent). */
          if (typeof doc.discardPath === 'function') doc.discardPath();
        } else if (typeof doc.clip_fixed === 'function') {
          doc.clip_fixed();
        } else {
          doc.internal.write('W n');
        }
        return true;
      } catch (clipErr) {
        DEBUG && console.warn('[dt] Silhouette clip application failed:', clipErr.message);
        try { doc.discardPath(); } catch (_) {}
        try { doc.restoreGraphicsState(); } catch (_) {}
        return false;
      }
    };

    /* ── v2.13.4 (CB37142): Print-area rectangle clip for VECTOR content ──
       v2.9.8 scoped the silhouette clip to wrap ONLY the raster addImage
       (correct — the low-res traced silhouette was shredding vector text
       glyph edges). But it left SVG layers (backdrops, clipart) painting as
       vector paths via paintOutlined with NO clip at all. The sunburst
       backdrop (151×151 canvas units on a 133×113 print area) overflowed on
       all four sides in the PRODUCTION PDF. EPS already has an artboard clip;
       SVG builder has productionClipOpen(). This adds the same to the PDF.
       Wraps ALL THREE paintOutlined call sites — (A) vectors below raster,
       (B) vectors above raster, (C) no-raster path. The silhouette clip
       (raster-only) and print-area clip (vector-only) are sequential, not
       nested, so no graphics-state conflicts. */
    var _openPrintAreaClip = function () {
      try {
        doc.saveGraphicsState();
        doc.rect(0, 0, wIn, hIn, null); /* path only, no paint (null = no style) */
        doc.clip();
        if (typeof doc.discardPath === 'function') doc.discardPath();
        return true;
      } catch (e) {
        DEBUG && console.warn('[dt] Print-area clip failed:', e.message);
        try { doc.discardPath(); } catch (_) {}
        try { doc.restoreGraphicsState(); } catch (_) {}
        return false;
      }
    };
    var _closePrintAreaClip = function (opened) {
      if (opened) { try { doc.restoreGraphicsState(); } catch (_) {} }
    };

    /* ── Layered paint: vectors BELOW the raster, raster PNG, vectors ABOVE.
       The old order (raster first, then ALL vector paths) painted a backdrop
       oval OVER the photo that sits on top of it in the editor (Jul 22
       order). Vector/text content whose layer index is below the FIRST
       raster layer now paints before the PNG embed, mirroring the editor's
       z-order (same rule as buildCompositeOutlinedSvg's traced-raster
       split, CB36156). ── */
    var paintedCount = 0;
    var sc = 1 / DPI;
    var paintOutlined = async function (pred) {
      /* v2.9.16: the third argument was hard-null since v2.9.9, which is precisely
         what severed print from the vectorizer — buildCompositeOutlinedSvg has always
         accepted and z-ordered a traced SVG. */
      var outlinedSvg = await buildCompositeOutlinedSvg(sideSlug, false, tracedSvgStr || null, pred);
      if (!outlinedSvg) return; /* null slice = nothing paintable (treated as empty) */
      var parser = new DOMParser();
      var svgDoc = parser.parseFromString(outlinedSvg, 'image/svg+xml');
      if (svgDoc.querySelector('parsererror')) return;
      var paths = flattenSvgPaths(svgDoc);
      for (var pi = 0; pi < paths.length; pi++) {
        if (_paintPdfPathEntry(doc, paths[pi], sc, 0)) paintedCount++;
      }
    };

    var drewRaster = false;
    if (hasRaster) {
      var visIdxFirstRaster = visible.findIndex(function(l) { return l.type !== 'text' && !(l.isSvg && l.svgMarkup); });
      var hasVecBelow = (hasText || hasVector) && visible.some(function(l, zi) {
        return zi < visIdxFirstRaster && (l.type === 'text' ? (l.text || '').trim() : (l.isSvg && l.svgMarkup));
      });
      if (hasVecBelow) {
        var _paClipA = _openPrintAreaClip(); /* (A) vectors BELOW raster */
        await paintOutlined(function (zi) { return zi < visIdxFirstRaster; });
        _closePrintAreaClip(_paClipA);
      }
      /* ── v2.9.16: which raster layers still get EMBEDDED ──
         With a trace in hand, bucket B is painted as vector by paintOutlined, so only
         bucket C (photos) is embedded. WITHOUT one — vectorizer down, credits out,
         anything — widen back to ALL raster, or the traced art would simply be absent
         from the file. That failure cannot be caught downstream: paintedCount is still
         > 0 from any text on the side, so the never-blank guard below never fires.
         Degrading to the exact pre-v2.9.16 output is the correct failure mode, and
         generateSideProduction has already logged print_trace_failed. */
      var _embedPred = tracedSvgStr
        ? isPhotoLayer
        : function (l) { return l.type !== 'text' && !(l.isSvg && l.svgMarkup); };
      /* Guard on the LAYER SET, never on the PNG being falsy. renderProductionPNG
         returns null only when the side has no visible layers at all — its filter is
         applied INSIDE the render loop, so a predicate matching nothing yields a fully
         TRANSPARENT png, not null. Testing truthiness would embed a blank image and set
         drewRaster, silently dropping the artwork (this is what would have half-broken
         bulk). */
      if (visible.some(_embedPred)) {
        var rasterPng = (hasText || hasVector || tracedSvgStr)
          ? await renderProductionPNG(sideSlug, _embedPred)
          : compositePng; /* all-raster, untraced: the composite IS the raster */
        if (rasterPng) {
          var _clipped = _openSilhouetteClip(); /* raster-only safety crop */
          doc.addImage(rasterPng, 'PNG', 0, 0, wIn, hIn);
          if (_clipped) { try { doc.restoreGraphicsState(); } catch (_) {} }
          drewRaster = true;
        }
      }
      if (hasText || hasVector || tracedSvgStr) {
        var _paClipB = _openPrintAreaClip(); /* (B) vectors ABOVE raster */
        await paintOutlined(hasVecBelow ? function (zi) { return zi >= visIdxFirstRaster; } : null);
        _closePrintAreaClip(_paClipB);
      }
    } else if (hasText || hasVector) {
      var _paClipC = _openPrintAreaClip(); /* (C) no-raster path */
      await paintOutlined(null);
      _closePrintAreaClip(_paClipC);
    }
    if (!paintedCount && !drewRaster && compositePng) {
      gwarn('prod_pdf_raster_fallback', 'Vector outlining produced no paintable paths for ' + sideSlug + ' – PRODUCTION PDF uses raster composite');
      doc.addImage(compositePng, 'PNG', 0, 0, wIn, hIn);
    }

    doc.setProperties({ title: 'PRODUCTION - ' + sideSlug + ' - ' + S.method, creator: 'Custom Design Tool V2' });
    /* v2.13.5 (CB37404): doc.output() is the ONLY call inside this function
       that is not wrapped in a per-operation try/catch. If a swallowed path-
       drawing error left jsPDF's internal stream in a corrupt state (unbalanced
       save/restore, dangling path operator), serialization can throw here with
       no signal. The gwarn makes that failure diagnosable; the re-throw lets
       the caller's retry loop fire. */
    try {
      return doc.output('datauristring');
    } catch (outputErr) {
      gwarn('prod_pdf_output_failed', 'jsPDF output() threw for ' + sideSlug + ': ' + (outputErr && outputErr.message));
      throw outputErr;
    }
  }

  /* ══════════════════════════════════════════════════════════════════
   * PREPRESS PDF — live/editable text via pdf-lib with per-glyph
   * placement from opentype.js, NO print-area clipping, 100px bleed
   * outside print area dimensions, clipping mask layer showing the
   * physical print area bounds. Filename ends with "PREPRESS".
   *
   * Non-text layers (rasters + vector/clipart) are composited into a
   * single PNG and embedded as a raster image — the PRODUCTION PDF
   * already carries full vector paths, so PREPRESS prioritises
   * editable text and a clean bleed-inclusive layout for the prepress
   * team. Falls back to the jsPDF outlined-text path if pdf-lib or
   * font embedding fails.
   * ══════════════════════════════════════════════════════════════════ */
  /* Shared PREPRESS shell (pdf-lib): bleed page + "Print Artwork" OCG layer +
     clip to the BLEED box + dashed trim guide (no label). paintFn(pdfDoc, page)
     paints the artwork inside the layer+clip and MAY THROW (e.g. live text
     fails); the caller then retries with a different painter. Defined once so
     the live-text path and the raster fallback share one structure. */
  async function _buildPdfLibPrepress(PDFLib, sideSlug, geom, paintFn) {
    var pdfDoc = await PDFLib.PDFDocument.create();
    /* Register fontkit BEFORE any painter runs — custom-font embedFont
       throws without it and the live-text primary painter never succeeds
       (v2.9.4). Failure is non-fatal: the painter chain's raster fallback
       still applies. */
    try {
      var _fk = await waitForFontkit();
      if (_fk && typeof pdfDoc.registerFontkit === 'function') pdfDoc.registerFontkit(_fk);
    } catch (_) { /* best-effort */ }
    pdfDoc.setTitle('PREPRESS - ' + sideSlug + ' - ' + S.method);
    pdfDoc.setCreator('Custom Design Tool V2');
    var page = pdfDoc.addPage([geom.pageWpt, geom.pageHpt]);

    /* "Print Artwork" OCG layer + clip to the BLEED box (page): bleed artwork is
       kept, the trim is shown by the guide (not clipped). Closed by a matching
       Q/EMC, emitted only if setup succeeded so operators never go unbalanced. */
    var _open = false;
    try {
      var _ref = pdfDoc.context.register(pdfDoc.context.obj({ Type: 'OCG', Name: PDFLib.PDFString.of('Print Artwork') }));
      pdfDoc.catalog.set(PDFLib.PDFName.of('OCProperties'), pdfDoc.context.obj({
        OCGs: [_ref], D: pdfDoc.context.obj({ Order: [_ref], ON: [_ref], OFF: [] }),
      }));
      var _res = page.node.Resources();
      if (!_res) { _res = pdfDoc.context.obj({}); page.node.set(PDFLib.PDFName.of('Resources'), _res); }
      var _props = _res.lookup(PDFLib.PDFName.of('Properties'));
      if (!_props) { _props = pdfDoc.context.obj({}); _res.set(PDFLib.PDFName.of('Properties'), _props); }
      _props.set(PDFLib.PDFName.of('ocPrintArt'), _ref);
      var _N = function (n) { return PDFLib.PDFNumber.of(n); };
      page.pushOperators(
        PDFLib.PDFOperator.of('BDC', [PDFLib.PDFName.of('OC'), PDFLib.PDFName.of('ocPrintArt')]),
        PDFLib.PDFOperator.of('q'),
        PDFLib.PDFOperator.of('re', [_N(0), _N(0), _N(geom.pageWpt), _N(geom.pageHpt)]),
        PDFLib.PDFOperator.of('W'), PDFLib.PDFOperator.of('n')
      );
      _open = true;
    } catch (ocgErr) { DEBUG && console.warn('[dt] PREPRESS OCG/clip setup failed:', ocgErr.message); }

    await paintFn(pdfDoc, page);   /* may throw -> caller retries with another painter */

    if (_open) { try { page.pushOperators(PDFLib.PDFOperator.of('Q'), PDFLib.PDFOperator.of('EMC')); } catch (_) {} }

    /* Dashed trim guide at the print-area bounds - no "PRINT AREA" label. */
    try {
      page.drawRectangle({
        x: geom.bleedPt, y: geom.bleedPt, width: geom.wIn * 72, height: geom.hIn * 72,
        borderColor: PDFLib.rgb(0, 0.659, 1), borderWidth: 0.5, borderDashArray: [7.2, 3.6], borderOpacity: 1,
      });
    } catch (guideErr) { DEBUG && console.warn('[dt] PREPRESS guide failed:', guideErr.message); }

    /* v2.9.31 BLANK-STREAM GUARD: any operator whose sizeInBytes() is
       non-finite (e.g. a raw-number arg) makes pdf-lib 1.17.1 save the
       page's ENTIRE content stream as ZERO bytes without throwing, so the
       painter-chain fallback never fires and a blank PREPRESS ships. Check
       every pushed operator here and THROW instead — the caller then retries
       with the raster painter (or jsPDF last resort), which can never be
       blank. An empty operator list is equally impossible for a valid page
       (the guide alone pushes ops), so treat it the same way. */
    var _csOps = (page.contentStream && page.contentStream.operators) || [];
    if (!_csOps.length) {
      gwarn('prepress_blank_stream', 'PREPRESS page has no content operators for ' + sideSlug + ' — retrying with raster painter');
      throw new Error('PREPRESS page has no content operators');
    }
    for (var _oi = 0; _oi < _csOps.length; _oi++) {
      if (!isFinite(_csOps[_oi].sizeInBytes())) {
        gwarn('prepress_blank_stream', 'PREPRESS operator #' + _oi + ' non-finite size for ' + sideSlug + ' — content stream would save blank; retrying with raster painter');
        throw new Error('PREPRESS operator #' + _oi + ' has non-finite size (content stream would save blank)');
      }
    }

    return 'data:application/pdf;base64,' + _uint8ArrayToBase64(await pdfDoc.save());
  }

  async function generatePrepressPDF(sideSlug, compositePng) {
    var cs = getProductionCoordSpace(sideSlug);
    if (!cs) return generateRasterPDF(sideSlug, compositePng);

    var phys = cs.phys;
    var wIn = phys.wInches, hIn = phys.hInches;
    var DPI = PROD_DPI;
    var PX_TO_PT = 72 / DPI;
    var BLEED_PX = 100;                 /* 100px @ 300 DPI = 0.333" bleed per side */
    var bleedIn = BLEED_PX / DPI;
    var bleedPt = BLEED_PX * PX_TO_PT;
    var pageWpt = (wIn + bleedIn * 2) * 72;
    var pageHpt = (hIn + bleedIn * 2) * 72;

    /* pdf-lib carries the layered/live-text prepress; jsPDF is used only if it
       can't load at all (that path can't add the OCG layer). */
    var PDFLib;
    try { PDFLib = await waitForPdfLib(); }
    catch (e) {
      DEBUG && console.warn('[dt] pdf-lib unavailable, using jsPDF PREPRESS:', e.message);
      return _generatePrepressPdfFallback(sideSlug, compositePng);
    }

    var geom = { pageWpt: pageWpt, pageHpt: pageHpt, bleedPt: bleedPt, wIn: wIn, hIn: hIn };
    var visible = (S.byView[sideSlug] || []).filter(function (l) { return l.visible !== false; });
    var textLayers = visible.filter(function (l) { return l.type === 'text' && (l.text || '').trim(); });
    var hasNonText = visible.some(function (l) { return l.type !== 'text' || !(l.text || '').trim(); });

    /* Embed a bleed-size PNG/JPEG data-URI filling the whole page (no-op on null). */
    async function _fillPage(pdfDoc, page, dataUri) {
      if (!dataUri) return;
      var bytes = _dataUriToUint8Array(dataUri);
      try { page.drawImage(await pdfDoc.embedPng(bytes), { x: 0, y: 0, width: pageWpt, height: pageHpt }); }
      catch (e) { page.drawImage(await pdfDoc.embedJpg(bytes), { x: 0, y: 0, width: pageWpt, height: pageHpt }); }
    }

    /* PRIMARY: non-text composite (bleed) + LIVE editable text, on the layer. */
    try {
      return await _buildPdfLibPrepress(PDFLib, sideSlug, geom, async function (pdfDoc, page) {
        if (hasNonText) {
          await _fillPage(pdfDoc, page, await renderProductionPNG(
            sideSlug,
            textLayers.length ? function (l) { return l.type !== 'text' || !(l.text || '').trim(); } : null,
            1, BLEED_PX));
        }
        if (textLayers.length) {
          await _drawLiveTextLayers(pdfDoc, page, textLayers, sideSlug, cs, bleedPt, pageHpt, DPI, PX_TO_PT, PDFLib);
        }
      });
    } catch (liveErr) {
      DEBUG && console.warn('[dt] PREPRESS live-text path failed, using proof-exact full raster:', liveErr && liveErr.message);
    }

    /* FALLBACK (pdf-lib - most accurate to proof): embed the FULL composite,
       rendered by the SAME canvas engine as the on-screen proof, at bleed size
       filling the page. Text is flattened (live text already failed) but pixel-
       matches what the customer approved, and it still carries the Print Artwork
       layer + bleed + clip + trim guide. */
    try {
      return await _buildPdfLibPrepress(PDFLib, sideSlug, geom, async function (pdfDoc, page) {
        await _fillPage(pdfDoc, page, await renderProductionPNG(sideSlug, null, 1, BLEED_PX));
      });
    } catch (rasterErr) {
      DEBUG && console.warn('[dt] PREPRESS pdf-lib full-raster failed, using jsPDF:', rasterErr && rasterErr.message);
    }

    /* LAST RESORT: jsPDF outlined (only reached if pdf-lib itself can't build). */
    return _generatePrepressPdfFallback(sideSlug, compositePng);
  }

  /* ── Helper: convert data URI to Uint8Array ── */
  function _dataUriToUint8Array(dataUri) {
    var b64 = dataUri.split(',')[1] || dataUri;
    var binary = atob(b64);
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }

  /* ── Helper: Uint8Array to base64 string ── */
  function _uint8ArrayToBase64(u8) {
    var CHUNK = 0x8000;
    var parts = [];
    for (var i = 0; i < u8.length; i += CHUNK) {
      parts.push(String.fromCharCode.apply(null, u8.subarray(i, i + CHUNK)));
    }
    return btoa(parts.join(''));
  }

  /* ── Draw live text layers into a pdf-lib page ──
     Uses opentype.js for glyph metrics (same math as buildTextOutlinePaths)
     and pdf-lib's embedFont + page.drawText for live/editable text output.
     Coordinate system: production pixels (300 DPI, top-left origin) are
     converted to PDF points (72 pt/in, bottom-left origin). */
  async function _drawLiveTextLayers(pdfDoc, page, textLayers, sideSlug, cs, bleedPt, pageHpt, DPI, PX_TO_PT, PDFLib) {
    var uniformScale = cs.uniformScale, offX = cs.offX, offY = cs.offY, sidePA = cs.sidePA;
    var embeddedFonts = {};  /* fontKey → { pdfFont, otFont, fauxBold, fauxItalic } */

    for (var ti = 0; ti < textLayers.length; ti++) {
      var l = textLayers[ti];
      var fontName = l.font || 'Montserrat';
      var bold = !!l.bold, italic = !!l.italic;

      /* Load opentype.js font for metrics */
      var otFont = await loadFontForOutlining(fontName, bold, italic);
      if (!otFont) {
        throw new Error('Could not load font for live text: ' + fontName);
      }

      /* Faux-style detection (mirrors buildTextOutlinePaths) */
      var _post = otFont.tables && otFont.tables.post;
      var _head = otFont.tables && otFont.tables.head;
      var _os2 = otFont.tables && otFont.tables.os2;
      var faceItalic = !!((_post && _post.italicAngle) || (_head && (_head.macStyle & 2)));
      var faceBold = !!((_head && (_head.macStyle & 1)) || (_os2 && _os2.usWeightClass >= 600));
      var fauxItalic = italic && !faceItalic;
      var fauxBold = bold && !faceBold;

      /* Embed font binary in pdf-lib (cached per font+style) */
      var fontKey = fontName + '|' + (bold ? 'b' : '') + (italic ? 'i' : '');
      if (!embeddedFonts[fontKey]) {
        var cached = getCachedFontBinary(fontName, bold, italic);
        if (!cached) {
          throw new Error('No cached font binary for: ' + fontName);
        }
        try {
          /* pdf-lib embedFont expects Uint8Array or ArrayBuffer */
          var fontBytes = new Uint8Array(cached.buffer);
          /* v2.9.32: clean embedded-font name. pdf-lib's default appends a
             random numeric suffix to the subset font's PostScript name
             (BaseFont /CollegiateHeavyOutline-Medium-7572), so Illustrator's
             missing-font prompt can never match a font prepress has
             installed. customName (verified on pdf-lib 1.17.1) uses the
             given name verbatim — we pass the font file's REAL PostScript
             name (what a local install of the same file would match),
             falling back to the design-data font name, spaces stripped
             (PS names never contain spaces). Viewing/printing is unaffected
             either way: the font is embedded. */
          var _psRec = cached.font && cached.font.names && cached.font.names.postScriptName;
          var _psName = (_psRec && (_psRec.en || _psRec[Object.keys(_psRec)[0]])) || fontName;
          _psName = String(_psName).replace(/\s+/g, '');
          var pdfFont = await pdfDoc.embedFont(fontBytes, { subset: true, customName: _psName });
          embeddedFonts[fontKey] = { pdfFont: pdfFont, otFont: cached.font, fauxBold: fauxBold, fauxItalic: fauxItalic };
        } catch (embedErr) {
          DEBUG && console.warn('[dt] PREPRESS font embed failed for', fontName, ':', embedErr.message);
          throw embedErr;  /* will trigger fallback to outlined */
        }
      }

      var ef = embeddedFonts[fontKey];
      var pdfFont = ef.pdfFont;

      /* ── Compute text positions in production pixels (same math as
            buildTextOutlinePaths lines 12795-12836) ── */
      var lx = (l.x - sidePA.x) * uniformScale + offX;
      var ly = (l.y - sidePA.y) * uniformScale + offY;
      var lw = (l.w || 0) * uniformScale;
      var lh = (l.h || 0) * uniformScale;
      var cx = l.centered ? lx : lx + lw / 2;
      var cy = l.centered ? ly : ly + lh / 2;
      var fs = (l.fontSize || 36) * uniformScale;
      var fillColor = l.color || '#000';
      /* UX-Aug15 #1 telemetry only (no output change): engrave prepress live text that
         isn't dark means a light layer slipped past the guards. */
      if (S.method === 'engrave' && isLightHex(fillColor)) gwarn('engrave_prepress_light_text', (l.text || '').slice(0, 40) + ' ' + fillColor);

      var lines = (l.text || '').split('\n');
      var lineH = fs * (l.lineHeight || 1.25);
      var totalH = lines.length * lineH;
      var startY = cy - totalH / 2 + lineH / 2;

      /* Parse fill color to PDF rgb */
      var rgb = _parseSvgColor(fillColor);
      var pdfColor = PDFLib.rgb(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255);

      /* Font metrics from opentype.js */
      var ascent = otFont.ascender / otFont.unitsPerEm * fs;
      var descent = otFont.descender / otFont.unitsPerEm * fs;
      var fontMid = (ascent + descent) / 2;

      /* Font size in PDF points */
      var fsPt = fs * PX_TO_PT;

      for (var li = 0; li < lines.length; li++) {
        var line = lines[li];
        if (!line.trim()) continue;

        var drawX = l.centered ? lx : lx + lw / 2;
        var drawY = startY + li * lineH;

        /* Same baseline computation as buildTextOutlinePaths */
        var advWidth = otFont.getAdvanceWidth(line, fs);
        var pathX = drawX - advWidth / 2;  /* center-aligned */
        var pathY = drawY + fontMid;       /* baseline from vertical center */

        /* Convert from production pixels (top-left origin) to PDF points (bottom-left origin):
           pdf_x = pathX * PX_TO_PT + bleedPt
           pdf_y = pageHpt - (pathY * PX_TO_PT + bleedPt) */
        var pdfX = pathX * PX_TO_PT + bleedPt;
        var pdfY = pageHpt - (pathY * PX_TO_PT + bleedPt);

        /* ── Build graphics state transforms for rotation, flip, faux italic/bold ── */
        var needsRotFlip = l.rotation || l.flipH || l.flipV;
        var needsTransform = needsRotFlip || fauxItalic || fauxBold;

        if (needsTransform) {
          page.pushOperators(PDFLib.pushGraphicsState());

          /* ── Rotation / flip: pivot around the block center (cx, cy) ── */
          if (needsRotFlip) {
            var pivotPdfX = cx * PX_TO_PT + bleedPt;
            var pivotPdfY = pageHpt - (cy * PX_TO_PT + bleedPt);

            /* Translate so pivot is at origin */
            page.pushOperators(PDFLib.concatTransformationMatrix(1, 0, 0, 1, pivotPdfX, pivotPdfY));

            /* Rotation (negate angle: PDF is CCW, canvas is CW) */
            if (l.rotation) {
              var rad = -(l.rotation) * Math.PI / 180;
              var cosR = Math.cos(rad), sinR = Math.sin(rad);
              page.pushOperators(PDFLib.concatTransformationMatrix(cosR, sinR, -sinR, cosR, 0, 0));
            }

            /* Flip */
            if (l.flipH || l.flipV) {
              var scaleX = l.flipH ? -1 : 1;
              var scaleY = l.flipV ? -1 : 1;
              page.pushOperators(PDFLib.concatTransformationMatrix(scaleX, 0, 0, scaleY, 0, 0));
            }

            /* Translate back from origin to pivot */
            page.pushOperators(PDFLib.concatTransformationMatrix(1, 0, 0, 1, -pivotPdfX, -pivotPdfY));
          }

          /* ── Faux italic: shear around THIS LINE's baseline, not block center.
               buildTextOutlinePaths bakes the shear per-glyph relative to each
               line's baseline Y (pathY). We match that by pivoting the CTM
               shear on the baseline's PDF Y coordinate for this specific line.
               In PDF bottom-up coords, a positive c coefficient in [1,0,c,1,0,0]
               leans glyph tops to the right — same visual as the canvas's 14°
               oblique synthesis. */
          if (fauxItalic) {
            var shear = 0.24933;  /* tan(14°) */
            /* pdfY is already this line's baseline in PDF coords */
            page.pushOperators(PDFLib.concatTransformationMatrix(1, 0, 0, 1, 0, pdfY));
            page.pushOperators(PDFLib.concatTransformationMatrix(1, 0, shear, 1, 0, 0));
            page.pushOperators(PDFLib.concatTransformationMatrix(1, 0, 0, 1, 0, -pdfY));
          }

          /* ── Faux bold: PDF fill+stroke text rendering mode (Tr 2).
                Produces a single text object with a thin stroke matching the
                fill colour — Illustrator reads this as one editable text
                frame with a stroke weight, not five overlapping copies.
                w  = line width (~0.03 em matches canvas synthetic bold)
                j  = round line join (prevents spikes on sharp glyph corners)
                RG = stroking colour in device RGB (same as fill)
                Tr = text rendering mode 2 (fill + stroke) ── */
          if (fauxBold) {
            /* v2.9.31: operator args MUST be PDFNumber-wrapped (same _N
               convention as the OCG/clip block). Raw JS numbers make
               PDFOperator.sizeInBytes() return NaN, and pdf-lib 1.17.1 then
               SILENTLY serializes the page's ENTIRE content stream as zero
               bytes — no throw, so the raster fallback never fired and every
               faux-bold prepress (bold toggle on a font with no true bold
               face) shipped BLANK. Root cause of the Jul 25 2026 RACE KING
               blank back PREPRESS. */
            var _Nb = function (n) { return PDFLib.PDFNumber.of(n); };
            var strokeW = fsPt * 0.03;
            page.pushOperators(
              PDFLib.PDFOperator.of('w', [_Nb(strokeW)]),
              PDFLib.PDFOperator.of('j', [_Nb(1)]),
              PDFLib.PDFOperator.of('RG', [_Nb(rgb[0] / 255), _Nb(rgb[1] / 255), _Nb(rgb[2] / 255)]),
              PDFLib.PDFOperator.of('Tr', [_Nb(2)])
            );
          }
        }

        /* ── Draw the text ── */
        var drawOpts = {
          x: pdfX,
          y: pdfY,
          size: fsPt,
          font: pdfFont,
          color: pdfColor,
        };

        page.drawText(line, drawOpts);

        if (needsTransform) {
          page.pushOperators(PDFLib.popGraphicsState());
        }
      }
    }
  }

  /* ── PREPRESS PDF fallback: jsPDF + outlined text ──
     Used when pdf-lib or font embedding fails. Produces the same layout
     (100px bleed, print-area guide) but with vectorized text instead of
     live/editable text. */
  async function _generatePrepressPdfFallback(sideSlug, compositePng) {
    var jsPDF = await waitForJsPDF();
    var cs = getProductionCoordSpace(sideSlug);
    if (!cs) return generateRasterPDF(sideSlug, compositePng);

    var pxW = cs.pxW, pxH = cs.pxH, phys = cs.phys;
    var wIn = phys.wInches, hIn = phys.hInches;
    var DPI = PROD_DPI;

    var BLEED_PX = 100;
    var bleedIn = BLEED_PX / DPI;
    var pageW = wIn + bleedIn * 2;
    var pageH = hIn + bleedIn * 2;

    var doc = new jsPDF({
      orientation: pageW >= pageH ? 'landscape' : 'portrait',
      unit: 'in', format: [pageW, pageH],
    });

    /* Clip all artwork to the BLEED box (full page), NOT the trim — bleed
       artwork is kept, the dashed guide marks the trim. jsPDF has no
       Optional-Content (named layer) support, so the "Print Artwork" layer is
       only added in the pdf-lib primary path; this rare fallback still removes
       the label and masks to the bleed box. Guarded — on any failure the
       document stays valid (just unclipped). */
    var _fbClipped = false;
    try {
      if (typeof doc.clip === 'function' && typeof doc.saveGraphicsState === 'function') {
        doc.saveGraphicsState();
        doc.rect(0, 0, pageW, pageH, null); /* path only, no paint — clip to bleed box */
        doc.clip();
        if (typeof doc.discardPath === 'function') doc.discardPath();
        _fbClipped = true;
      }
    } catch (_) {
      if (_fbClipped) { try { doc.restoreGraphicsState(); } catch (__) {} _fbClipped = false; }
    }

    var visible = (S.byView[sideSlug] || []).filter(function(l) { return l.visible !== false; });
    var hasText = visible.some(function(l) { return l.type === 'text' && (l.text || '').trim(); });
    var hasVector = visible.some(function(l) { return l.type !== 'text' && l.isSvg && l.svgMarkup; });
    var hasRaster = visible.some(function(l) { return l.type !== 'text' && !(l.isSvg && l.svgMarkup); });

    var drewRaster = false;
    if (hasRaster) {
      /* Bleed-size render placed filling the page, so overflow artwork (the
         100px bleed) is carried through — matches the pdf-lib primary path. */
      var rasterPng = await renderProductionPNG(
        sideSlug,
        (hasText || hasVector) ? function(l) { return l.type !== 'text' && !(l.isSvg && l.svgMarkup); } : null,
        1, BLEED_PX);
      if (rasterPng) {
        doc.addImage(rasterPng, 'PNG', 0, 0, pageW, pageH);
        drewRaster = true;
      }
    }

    if (hasText || hasVector) {
      var paintedCount = 0;
      var outlinedSvg = await buildCompositeOutlinedSvg(sideSlug, false, null);
      if (outlinedSvg) {
        var parser = new DOMParser();
        var svgDoc = parser.parseFromString(outlinedSvg, 'image/svg+xml');
        if (!svgDoc.querySelector('parsererror')) {
          var paths = flattenSvgPaths(svgDoc);
          var sc = 1 / DPI;
          for (var pi = 0; pi < paths.length; pi++) {
            /* Shared stroke-aware painter (fill="none" strokes no longer
               paint black); bleedIn shifts coords onto the bleed page. */
            if (_paintPdfPathEntry(doc, paths[pi], sc, bleedIn)) paintedCount++;
          }
        }
      }
      if (!paintedCount && !drewRaster && compositePng) {
        gwarn('prepress_pdf_raster_fallback', 'Vector outlining produced no paintable paths for ' + sideSlug + ' – PREPRESS PDF uses raster composite');
        doc.addImage(compositePng, 'PNG', bleedIn, bleedIn, wIn, hIn);
      }
    }

    /* Close the artwork clip before the guide (guide stays unclipped). */
    if (_fbClipped) { try { doc.restoreGraphicsState(); } catch (_) {} _fbClipped = false; }

    try {
      doc.saveGraphicsState();
      doc.setDrawColor(0, 168, 255);
      doc.setLineWidth(0.01);
      if (typeof doc.setLineDashPattern === 'function') {
        doc.setLineDashPattern([0.1, 0.05], 0);
      } else if (typeof doc.setLineDash === 'function') {
        doc.setLineDash([0.1, 0.05], 0);
      }
      doc.rect(bleedIn, bleedIn, wIn, hIn, 'S');
      /* "PRINT AREA" text label removed per prepress spec — dashed clip guide kept. */
      doc.restoreGraphicsState();
    } catch (guideErr) {
      DEBUG && console.warn('[dt] PREPRESS fallback print area guide failed:', guideErr.message);
    }

    if (!drewRaster && !hasText && !hasVector && compositePng) {
      doc.addImage(compositePng, 'PNG', bleedIn, bleedIn, wIn, hIn);
    }

    doc.setProperties({ title: 'PREPRESS (fallback) - ' + sideSlug + ' - ' + S.method, creator: 'Custom Design Tool V2' });
    return doc.output('datauristring');
  }

  /**
   * Call Vectorizer.ai via Vercel proxy.
   */
  async function callVectorize(imageB64, format, maxColors, uploadCtx) {
    /* ── v2.13.3 URL MODE (CB37298 413 fix) ────────────────────────────
       Vercel rejects request bodies over ~4.5MB at the edge, BEFORE the
       proxy runs (hard platform limit). Dense trace inputs (AI line-art
       with v2.12.2 color preserved measured 5.4MB) died there: trace
       failed → engrave side shipped PNG-only. Over the threshold we
       upload the PNG to S3 (existing presign infra; the object doubles
       as a per-order audit artifact of the exact pixels the vectorizer
       saw) and send its URL — the proxy forwards it as `image.url`,
       which vectorizer.ai fetches directly. Below threshold: byte-
       identical behavior. The upload is MEMOIZED per payload so the F3
       3-attempt retry loop re-uses one S3 object instead of uploading
       5MB three times. */
    const URL_MODE_THRESHOLD = 4000000; /* chars ≈ bytes on the wire; cap is 4.5MB */
    let body;
    if (imageB64 && imageB64.length > URL_MODE_THRESHOLD) {
      if (!callVectorize._urlCache || callVectorize._urlCache.src !== imageB64) {
        const name = 'trace-input-' + ((uploadCtx && uploadCtx.serial) || 'adhoc') + '-' + Date.now() + '.png';
        const folder = (uploadCtx && uploadCtx.folder) ? uploadCtx.folder + '/Prepress' : 'trace-inputs';
        const url = await uploadToS3(imageB64, name, 'image/png', folder, 'inline');
        callVectorize._urlCache = { src: imageB64, url };
        gwarn('vectorize_url_mode', 'Trace input ' + Math.round(imageB64.length / 1024) +
          'KB exceeds inline cap — sent via S3 URL (' + name + ')');
      }
      body = { imageUrl: callVectorize._urlCache.url, format: format || 'svg', maxColors: maxColors || 100, mode: 'production' };
    } else {
      body = { image: imageB64, format: format || 'svg', maxColors: maxColors || 100, mode: 'production' };
    }
    const res = await fetch(CFG.vectorizeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let msg = `Vectorization failed (${res.status})`;
      try { msg = (await res.json()).error || msg; } catch(_) {}
      throw new Error(msg);
    }
    const data = await res.json();
    if (!data.result) throw new Error('Vectorization returned empty result');
    return data.result;
  }

  /**
   * Convert SVG markup to a simple DXF (AC1006) with POLYLINE/VERTEX entities.
   * Coordinates are force-mapped to exact physical inches from SIDE_PHYS.
   * SVG viewBox is used ONLY for normalization (0→1), never as the output scale.
   * All coordinates are clamped to the physical decoration area bounds.
   * @param {string} svgStr - SVG markup from vectorizer
   * @param {object} physDims - { wInches, hInches } physical decoration area from metafield
   */
  /* ── Flatten all SVG visible geometry into an array of path d-strings ──
     Handles: <path>, <circle>, <ellipse>, <rect>, <line>, <polygon>,
     <polyline>, and <g transform="..."> nesting. This prevents elements
     from being silently dropped when the vectorizer returns non-path
     shapes or wraps paths inside transformed groups. */
  function flattenSvgPaths(doc) {
    const results = [];

    /* Resolve the computed fill color for an element, walking up the
       ancestor chain through <g>/<svg> parents until a fill is found. */
    function resolveFill(el) {
      let node = el;
      while (node && node.nodeType === 1) {
        const f = node.getAttribute('fill');
        if (f && f !== 'inherit') return f;
        /* Also check inline style for fill */
        const st = node.getAttribute('style');
        if (st) {
          const m = st.match(/(?:^|;)\s*fill\s*:\s*([^;]+)/i);
          if (m && m[1].trim() !== 'inherit') return m[1].trim();
        }
        node = node.parentElement;
      }
      return null; /* caller defaults to black */
    }

    /* Resolve stroke color the same way (attr or inline style, ancestor
       walk). Without this, fill="none" stroke paths were painted as BLACK
       FILLS in the PDFs and silently dropped from EPS/DXF — the Jul 22
       order's orange cameo ring shipped as a black oval. */
    function resolveStroke(el) {
      let node = el;
      while (node && node.nodeType === 1) {
        const s = node.getAttribute('stroke');
        if (s && s !== 'inherit') return s;
        const st = node.getAttribute('style');
        if (st) {
          const m = st.match(/(?:^|;)\s*stroke\s*:\s*([^;]+)/i);
          if (m && m[1].trim() !== 'inherit') return m[1].trim();
        }
        node = node.parentElement;
      }
      return null;
    }

    /* Stroke width: element attr, inline style, or the SVG default of 1
       when a stroke color is present with no declared width. Scaled by
       sqrt(|det|) of the accumulated transform so a viewBox-space width
       lands correct in production px (the Jul 22 ring shipped at half its
       intended weight). */
    function resolveStrokeWidth(el, hasStroke, mx) {
      let sw = parseFloat(el.getAttribute('stroke-width'));
      if (isNaN(sw)) {
        const st = el.getAttribute('style');
        const m = st && st.match(/(?:^|;)\s*stroke-width\s*:\s*([\d.]+)/i);
        if (m) sw = parseFloat(m[1]);
      }
      if (isNaN(sw)) sw = hasStroke ? 1 : 0;
      if (sw && mx) {
        const det = Math.abs(mx[0] * mx[3] - mx[1] * mx[2]);
        if (det > 0) sw *= Math.sqrt(det);
      }
      return sw || 0;
    }

    /* Convert an SVG transform attribute to a 3×2 affine matrix [a,b,c,d,e,f].
       Supports: matrix(), translate(), scale(), rotate(). */
    function parseTransform(attr) {
      if (!attr) return null;
      let m = [1,0,0,1,0,0]; // identity
      const ops = attr.match(/(matrix|translate|scale|rotate)\s*\(([^)]+)\)/gi) || [];
      for (const op of ops) {
        const match = op.match(/(matrix|translate|scale|rotate)\s*\(([^)]+)\)/i);
        if (!match) continue;
        const fn = match[1].toLowerCase();
        const args = match[2].split(/[\s,]+/).map(Number);
        let t;
        if (fn === 'matrix' && args.length >= 6)      t = args;
        else if (fn === 'translate')                   t = [1,0,0,1, args[0]||0, args[1]||0];
        else if (fn === 'scale')                       t = [args[0],0,0, args.length>1?args[1]:args[0], 0,0];
        else if (fn === 'rotate' && args.length >= 1) {
          const r = args[0] * Math.PI / 180, c = Math.cos(r), s = Math.sin(r);
          const cx = args[1]||0, cy = args[2]||0;
          // rotate around (cx,cy): translate(cx,cy) · rotate · translate(-cx,-cy)
          t = [c, s, -s, c, cx - c*cx + s*cy, cy - s*cx - c*cy];
        }
        else continue;
        // multiply: m = m * t
        m = [
          m[0]*t[0]+m[2]*t[1], m[1]*t[0]+m[3]*t[1],
          m[0]*t[2]+m[2]*t[3], m[1]*t[2]+m[3]*t[3],
          m[0]*t[4]+m[2]*t[5]+m[4], m[1]*t[4]+m[3]*t[5]+m[5],
        ];
      }
      return m;
    }

    /* Apply affine matrix to every coordinate pair in a path d-string.
       Re-emits as absolute commands only (M, L, C, Z). Arcs are
       approximated via cubic beziers before transform so the result
       stays exact under non-uniform scale / rotation. */
    function transformPathD(d, mx) {
      if (!mx || (mx[0]===1 && mx[1]===0 && mx[2]===0 && mx[3]===1 && mx[4]===0 && mx[5]===0)) return d;
      /* v2.9.23 (SO-332060): tx THROWS on a non-finite result instead of
         emitting literal "NaN" text. A d-string that ends mid-command
         (truncated tail) makes n() return NaN for the missing args; the
         old emitters wrote "C … NaN NaN" into the output, which
         parseSvgPathToPolylines then turned into 8 NaN vertices per curve
         — a DXF Illustrator refuses whole (error 2067). The catch below
         drops the incomplete trailing segment, keeps every complete
         command already emitted, and gwarns so the loss is visible. */
      const tx = (x,y) => {
        const ox = mx[0]*x+mx[2]*y+mx[4], oy = mx[1]*x+mx[3]*y+mx[5];
        if (!Number.isFinite(ox) || !Number.isFinite(oy)) throw new Error('non-finite path coordinate');
        return [+ox.toFixed(2), +oy.toFixed(2)];
      };
      const tokens = d.match(/[a-zA-Z]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || [];
      let i=0, out='', cx=0,cy=0, sx=0,sy=0;
      /* Curve-continuation state for S/s (cubic) and T/t (quadratic) reflection */
      let lastCp2x=0,lastCp2y=0, lastQpx=0,lastQpy=0, prevCmd='';
      const n = () => parseFloat(tokens[i++]);
      const isN = () => i<tokens.length && /[0-9.+-]/.test(tokens[i]);
      /* v2.9.23: peel fused SVG arc flags per spec — see _svgDToJsPDFOps */
      const nf = () => {
        const t = tokens[i];
        if (t === '0' || t === '1') { i++; return +t; }
        const c = t ? t.charAt(0) : '';
        if (c === '0' || c === '1') { tokens[i] = t.slice(1); return +c; }
        i++; return NaN;
      };
      try {
      while (i < tokens.length) {
        const cmd = tokens[i]; if (/[a-zA-Z]/.test(cmd)) i++; else { gwarn('path_token_misalign', 'Number found in path command position during transform bake — dropping remainder of path'); break; }
        switch(cmd) {
          case 'M': { let ax=n(),ay=n(); cx=ax;cy=ay;sx=ax;sy=ay;
            const[px,py]=tx(ax,ay); out+=`M${px} ${py} `;
            while(isN()){ax=n();ay=n();cx=ax;cy=ay; const[lx,ly]=tx(ax,ay); out+=`L${lx} ${ly} `;} break; }
          case 'm': { let ax=cx+n(),ay=cy+n(); cx=ax;cy=ay;sx=ax;sy=ay;
            const[px,py]=tx(ax,ay); out+=`M${px} ${py} `;
            while(isN()){ax=cx+n();ay=cy+n();cx=ax;cy=ay; const[lx,ly]=tx(ax,ay); out+=`L${lx} ${ly} `;} break; }
          case 'L': while(isN()){cx=n();cy=n(); const[lx,ly]=tx(cx,cy); out+=`L${lx} ${ly} `;} break;
          case 'l': while(isN()){cx+=n();cy+=n(); const[lx,ly]=tx(cx,cy); out+=`L${lx} ${ly} `;} break;
          case 'H': while(isN()){cx=n(); const[lx,ly]=tx(cx,cy); out+=`L${lx} ${ly} `;} break;
          case 'h': while(isN()){cx+=n(); const[lx,ly]=tx(cx,cy); out+=`L${lx} ${ly} `;} break;
          case 'V': while(isN()){cy=n(); const[lx,ly]=tx(cx,cy); out+=`L${lx} ${ly} `;} break;
          case 'v': while(isN()){cy+=n(); const[lx,ly]=tx(cx,cy); out+=`L${lx} ${ly} `;} break;
          case 'C': while(isN()){const x1=n(),y1=n(),x2=n(),y2=n(),x3=n(),y3=n();
            const[a,b]=tx(x1,y1),[c2,d2]=tx(x2,y2),[e,f]=tx(x3,y3);
            out+=`C${a} ${b} ${c2} ${d2} ${e} ${f} `; lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;} break;
          case 'c': while(isN()){const x1=cx+n(),y1=cy+n(),x2=cx+n(),y2=cy+n(),x3=cx+n(),y3=cy+n();
            const[a,b]=tx(x1,y1),[c2,d2]=tx(x2,y2),[e,f]=tx(x3,y3);
            out+=`C${a} ${b} ${c2} ${d2} ${e} ${f} `; lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;} break;
          /* S/Q/T/A: converted to transformed cubics — same reflection rules as
             svgPathToAIOperators, arcs via svgArcToBeziers. Previously these
             commands aborted the parser and dropped the rest of the path. */
          case 'S': case 's': { const rel=cmd==='s';
            while(isN()){
              let x1,y1;
              if ('CcSs'.indexOf(prevCmd)>=0){x1=2*cx-lastCp2x;y1=2*cy-lastCp2y;} else {x1=cx;y1=cy;}
              let x2=n(),y2=n(),x3=n(),y3=n();
              if(rel){x2+=cx;y2+=cy;x3+=cx;y3+=cy;}
              const[a,b]=tx(x1,y1),[c2,d2]=tx(x2,y2),[e,f]=tx(x3,y3);
              out+=`C${a} ${b} ${c2} ${d2} ${e} ${f} `;
              lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;prevCmd=cmd;}
            break; }
          case 'Q': case 'q': { const rel=cmd==='q';
            while(isN()){let qx=n(),qy=n(),ex=n(),ey=n();
              if(rel){qx+=cx;qy+=cy;ex+=cx;ey+=cy;}
              const c1x=cx+2/3*(qx-cx),c1y=cy+2/3*(qy-cy),c2x2=ex+2/3*(qx-ex),c2y2=ey+2/3*(qy-ey);
              const[a,b]=tx(c1x,c1y),[c2,d2]=tx(c2x2,c2y2),[e,f]=tx(ex,ey);
              out+=`C${a} ${b} ${c2} ${d2} ${e} ${f} `;
              lastQpx=qx;lastQpy=qy;cx=ex;cy=ey;prevCmd=cmd;}
            break; }
          case 'T': case 't': { const rel=cmd==='t';
            while(isN()){
              let qx,qy;
              if ('QqTt'.indexOf(prevCmd)>=0){qx=2*cx-lastQpx;qy=2*cy-lastQpy;} else {qx=cx;qy=cy;}
              let ex=n(),ey=n();
              if(rel){ex+=cx;ey+=cy;}
              const c1x=cx+2/3*(qx-cx),c1y=cy+2/3*(qy-cy),c2x2=ex+2/3*(qx-ex),c2y2=ey+2/3*(qy-ey);
              const[a,b]=tx(c1x,c1y),[c2,d2]=tx(c2x2,c2y2),[e,f]=tx(ex,ey);
              out+=`C${a} ${b} ${c2} ${d2} ${e} ${f} `;
              lastQpx=qx;lastQpy=qy;cx=ex;cy=ey;prevCmd=cmd;}
            break; }
          case 'A': case 'a': { const rel=cmd==='a';
            while(isN()){const arx=n(),ary=n(),arot=n(),alarge=nf(),asweep=nf();
              let aex=n(),aey=n(); if(rel){aex+=cx;aey+=cy;}
              const bz=svgArcToBeziers(cx,cy,arx,ary,arot,alarge,asweep,aex,aey);
              for(const bzs of bz){
                const[a1,b1]=tx(bzs[0],bzs[1]),[a2,b2]=tx(bzs[2],bzs[3]),[a3,b3]=tx(bzs[4],bzs[5]);
                out+=`C${a1} ${b1} ${a2} ${b2} ${a3} ${b3} `;cx=bzs[4];cy=bzs[5];}
              if(!bz.length){const[lx2,ly2]=tx(aex,aey);out+=`L${lx2} ${ly2} `;cx=aex;cy=aey;}}
            lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break; }
          case 'Z': case 'z': out+='Z '; cx=sx;cy=sy; break;
          default: i++; break;
        }
        prevCmd = cmd;
      }
      } catch (e) {
        /* Incomplete trailing command (see tx above): everything already in
           `out` is a complete M/L/C/Z command — ship that, drop the tail. */
        gwarn('path_truncated_tail', 'Path data ended mid-command during transform bake — dropped incomplete trailing segment, kept ' + out.length + ' chars of complete geometry');
      }
      return out;
    }

    /* Convert basic SVG shapes to path d-strings */
    function shapeToPath(el) {
      const tag = el.tagName?.toLowerCase();
      if (tag === 'circle') {
        const cx=parseFloat(el.getAttribute('cx'))||0, cy=parseFloat(el.getAttribute('cy'))||0, r=parseFloat(el.getAttribute('r'))||0;
        if (r<=0) return null;
        return `M${cx-r},${cy} A${r},${r} 0 1,0 ${cx+r},${cy} A${r},${r} 0 1,0 ${cx-r},${cy}Z`;
      }
      if (tag === 'ellipse') {
        const cx=parseFloat(el.getAttribute('cx'))||0, cy=parseFloat(el.getAttribute('cy'))||0;
        const rx=parseFloat(el.getAttribute('rx'))||0, ry=parseFloat(el.getAttribute('ry'))||0;
        if (rx<=0||ry<=0) return null;
        return `M${cx-rx},${cy} A${rx},${ry} 0 1,0 ${cx+rx},${cy} A${rx},${ry} 0 1,0 ${cx-rx},${cy}Z`;
      }
      if (tag === 'rect') {
        const x=parseFloat(el.getAttribute('x'))||0, y=parseFloat(el.getAttribute('y'))||0;
        const w=parseFloat(el.getAttribute('width'))||0, h=parseFloat(el.getAttribute('height'))||0;
        if (w<=0||h<=0) return null;
        return `M${x},${y}L${x+w},${y}L${x+w},${y+h}L${x},${y+h}Z`;
      }
      if (tag === 'line') {
        const x1=parseFloat(el.getAttribute('x1'))||0, y1=parseFloat(el.getAttribute('y1'))||0;
        const x2=parseFloat(el.getAttribute('x2'))||0, y2=parseFloat(el.getAttribute('y2'))||0;
        return `M${x1},${y1}L${x2},${y2}`;
      }
      if (tag === 'polygon' || tag === 'polyline') {
        const pts = (el.getAttribute('points')||'').trim().split(/[\s,]+/).map(Number);
        if (pts.length < 4) return null;
        let d = `M${pts[0]},${pts[1]}`;
        for (let j=2; j<pts.length-1; j+=2) d += `L${pts[j]},${pts[j+1]}`;
        if (tag === 'polygon') d += 'Z';
        return d;
      }
      return null;
    }

    /* Walk the SVG DOM, accumulating transforms from <g> ancestors.
       visited tracks <use> targets to prevent infinite recursion. */
    const _useVisited = new Set();
    function walk(node, parentMatrix) {
      for (const child of node.children) {
        const tag = child.tagName?.toLowerCase();
        if (!tag) continue;

        /* Accumulate this element's transform onto the parent matrix */
        const localT = parseTransform(child.getAttribute('transform'));
        let mx = parentMatrix;
        if (localT) {
          if (mx) {
            mx = [
              mx[0]*localT[0]+mx[2]*localT[1], mx[1]*localT[0]+mx[3]*localT[1],
              mx[0]*localT[2]+mx[2]*localT[3], mx[1]*localT[2]+mx[3]*localT[3],
              mx[0]*localT[4]+mx[2]*localT[5]+mx[4], mx[1]*localT[4]+mx[3]*localT[5]+mx[5],
            ];
          } else {
            mx = localT;
          }
        }

        if (tag === 'g' || tag === 'svg') {
          walk(child, mx);
          continue;
        }

        /* Resolve <use> by finding the referenced element and walking it
           with the <use>'s accumulated transform + x/y offset. */
        if (tag === 'use') {
          const href = child.getAttribute('href') || child.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
          if (href && href.startsWith('#')) {
            const target = doc.getElementById(href.slice(1));
            if (target && !_useVisited.has(target)) {
              _useVisited.add(target);
              const ux = parseFloat(child.getAttribute('x')) || 0;
              const uy = parseFloat(child.getAttribute('y')) || 0;
              let useMx = mx;
              if (ux || uy) {
                const tr = [1,0,0,1,ux,uy];
                useMx = useMx
                  ? [useMx[0]*tr[0]+useMx[2]*tr[1], useMx[1]*tr[0]+useMx[3]*tr[1], useMx[0]*tr[2]+useMx[2]*tr[3], useMx[1]*tr[2]+useMx[3]*tr[3], useMx[0]*tr[4]+useMx[2]*tr[5]+useMx[4], useMx[1]*tr[4]+useMx[3]*tr[5]+useMx[5]]
                  : tr;
              }
              /* If target is a group, recurse; otherwise treat as a shape */
              const tTag = target.tagName?.toLowerCase();
              if (tTag === 'g' || tTag === 'svg') { walk(target, useMx); }
              else {
                let ud = (tTag === 'path') ? target.getAttribute('d') : shapeToPath(target);
                if (ud) {
                  const _d = useMx ? transformPathD(ud, useMx) : ud;
                  const _f = resolveFill(target);
                  const _s = resolveStroke(target);
                  const _sw = resolveStrokeWidth(target, !!(_s && _s !== 'none'), useMx);
                  const _fb = target.getAttribute('data-dt-fauxbold') === '1';
                  const _dg = target.getAttribute('data-dt-guide') === '1'; /* v2.11.3 */
                  if (_f || _s || _sw) results.push({ d: _d, fill: _f, stroke: _s || undefined, strokeWidth: _sw || undefined, fauxBold: _fb || undefined, guide: _dg || undefined });
                  else results.push(_d);
                }
              }
              _useVisited.delete(target);
            }
          }
          continue;
        }

        let d = null;
        if (tag === 'path') d = child.getAttribute('d');
        else d = shapeToPath(child);

        if (d) {
          const _td = mx ? transformPathD(d, mx) : d;
          const _tf = resolveFill(child);
          /* Stroke color + width pass-through: faux-bold text (same-color
             stroke) AND real stroke-only art (fill="none" rings/outlines). */
          const _ts = resolveStroke(child);
          const _sw = resolveStrokeWidth(child, !!(_ts && _ts !== 'none'), mx);
          const _fb = child.getAttribute('data-dt-fauxbold') === '1';
          const _dg = child.getAttribute('data-dt-guide') === '1'; /* v2.11.3: bulk cell guide, never ink */
          if (_tf || _ts || _sw) results.push({ d: _td, fill: _tf, stroke: _ts || undefined, strokeWidth: _sw || undefined, fauxBold: _fb || undefined, guide: _dg || undefined });
          else results.push(_td);
        }
      }
    }

    walk(doc.documentElement, null);
    return results;
  }

  /**
   * Build a composite production SVG directly from SVG layers' original markup,
   * bypassing the rasterize→vectorize roundtrip. Each layer's paths are placed
   * in the 300 DPI production pixel space using the same coordinate mapping as
   * renderProductionPNG(). Returns null if any visible layer lacks svgMarkup
   * (mixed text/raster sides fall through to the bitmap vectorization path).
   */
  function buildCompositeVectorSvg(sideSlug) {
    const ls = S.byView[sideSlug];
    if (!ls?.length) return null;
    const visible = ls.filter(l => l.visible !== false);
    if (!visible.length) return null;
    if (!visible.every(l => l.isSvg && l.svgMarkup)) return null;

    const phys = SIDE_PHYS[sideSlug];
    if (!phys) return null;

    const DPI = PROD_DPI;
    const pa = CFG.sideConfigs[sideSlug]?.printArea || { x: 0, y: 0, w: 300, h: 300 };
    const pxW = Math.round(phys.wInches * DPI);
    const pxH = Math.round(phys.hInches * DPI);

    const _rs = S.rscaleByView[sideSlug] || S.rscale;
    const sidePA = {
      x: pa.x * _rs, y: pa.y * _rs,
      w: pa.w * _rs, h: pa.h * _rs,
    };
    const scX = pxW / sidePA.w, scY = pxH / sidePA.h;
    const uniformScale = Math.min(scX, scY);
    const offX = (pxW - sidePA.w * uniformScale) / 2;
    const offY = (pxH - sidePA.h * uniformScale) / 2;

    const parser = new DOMParser();
    const groups = [];

    for (const l of visible) {
      const doc = parser.parseFromString(l.svgMarkup, 'image/svg+xml');
      if (doc.querySelector('parsererror')) continue;
      const svgEl = doc.documentElement;

      /* Get the layer SVG's coordinate space */
      let vbX = 0, vbY = 0, vbW = 0, vbH = 0;
      const vb = svgEl.getAttribute('viewBox');
      if (vb) {
        const parts = vb.split(/[\s,]+/).map(Number);
        vbX = parts[0] || 0; vbY = parts[1] || 0;
        vbW = parts[2] || 0; vbH = parts[3] || 0;
      }
      if (!vbW) vbW = parseFloat(svgEl.getAttribute('width')) || 100;
      if (!vbH) vbH = parseFloat(svgEl.getAttribute('height')) || 100;

      /* Layer position in production pixel space (mirrors renderProductionPNG) */
      const lx = (l.x - sidePA.x) * uniformScale + offX;
      const ly = (l.y - sidePA.y) * uniformScale + offY;
      const lw = l.w * uniformScale;
      const lh = l.h * uniformScale;
      const cx = l.centered ? lx : lx + lw / 2;
      const cy = l.centered ? ly : ly + lh / 2;

      /* Build transform: rotation/flip around center, then viewBox→production mapping */
      const xforms = [];
      if (l.rotation || l.flipH || l.flipV) {
        xforms.push(`translate(${cx},${cy})`);
        if (l.rotation) xforms.push(`rotate(${l.rotation})`);
        if (l.flipH) xforms.push(`scale(-1,1)`);
        if (l.flipV) xforms.push(`scale(1,-1)`);
        xforms.push(`translate(${-cx},${-cy})`);
      }
      xforms.push(`translate(${lx},${ly})`);
      xforms.push(`scale(${lw / vbW},${lh / vbH})`);
      if (vbX || vbY) xforms.push(`translate(${-vbX},${-vbY})`);

      /* Extract inner content — namespace all ids to avoid collisions
         when multiple SVG layers are merged into one document. */
      const layerIdx = groups.length;
      let inner = Array.from(svgEl.childNodes)
        .map(n => n.nodeType === 1 ? new XMLSerializer().serializeToString(n) : '')
        .filter(Boolean)
        .join('');
      /* Rewrite id="foo" → id="L{idx}_foo" and all url(#foo) / href="#foo" refs */
      const ids = new Set();
      inner.replace(/\bid="([^"]+)"/g, (_, id) => { ids.add(id); return _; });
      if (ids.size) {
        for (const id of ids) {
          const nsId = `L${layerIdx}_${id}`;
          const esc = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          inner = inner.replace(new RegExp(`\\bid="${esc}"`, 'g'), `id="${nsId}"`);
          inner = inner.replace(new RegExp(`url\\(\\s*['"]?#${esc}['"]?\\s*\\)`, 'g'), `url(#${nsId})`);
          inner = inner.replace(new RegExp(`href="#${esc}"`, 'g'), `href="#${nsId}"`);
          inner = inner.replace(new RegExp(`xlink:href="#${esc}"`, 'g'), `xlink:href="#${nsId}"`);
        }
      }

      if (inner) groups.push(`<g transform="${xforms.join(' ')}">${inner}</g>`);
    }

    if (!groups.length) return null;
    /* Clip to the artboard only when content overflows it — matches the
       customer-approved preview and the raster production files. Layer ids
       are namespaced (L{idx}_) above, so "dtPaClip" cannot collide. */
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${pxW} ${pxH}" width="${pxW}" height="${pxH}">`
      + productionClipOpen(sideSlug, pxW, pxH)
      + `${groups.join('')}</g></svg>`;
  }

  /**
   * Round all numeric values inside SVG path d-attributes to a fixed
   * number of decimal places. Dramatically shrinks traced/clipart SVG
   * output where coordinates carry full double-precision noise.
   * Also strips redundant xmlns:xlink declarations that XMLSerializer
   * adds to every element.
   */
  /* v2.9.23 (SO-332060): split fused SVG arc flags BEFORE numeric rounding —
     "A5 5 0 0140 10" means flags 0,1 then x=40, but the number tokenizer
     reads "0140" as ONE number and parseFloat-based rounding rewrites it
     to "140", destroying the leading flag digit and misaligning every
     token after the arc. Rebuilds the d from its own tokens (space-
     separated, semantics identical) with each fused flag pair split out. */
  function _unfuseArcFlags(d) {
    var tokens = d.match(/[a-zA-Z]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || [];
    var out = [];
    var i = 0;
    var isNum = function(t) { return t !== undefined && /^[0-9.+-]/.test(t); };
    while (i < tokens.length) {
      var t = tokens[i];
      if (t === 'A' || t === 'a') {
        out.push(t); i++;
        while (isNum(tokens[i])) {
          for (var k = 0; k < 3 && isNum(tokens[i]); k++) out.push(tokens[i++]); /* rx ry rot */
          for (var f = 0; f < 2; f++) { /* two flags, possibly fused with the next number */
            var ft = tokens[i];
            if (ft === '0' || ft === '1') { out.push(ft); i++; }
            else if (ft && (ft.charAt(0) === '0' || ft.charAt(0) === '1')) { out.push(ft.charAt(0)); tokens[i] = ft.slice(1); }
            else break; /* malformed — leave for the downstream parsers to reject */
          }
          for (var k2 = 0; k2 < 2 && isNum(tokens[i]); k2++) out.push(tokens[i++]); /* x y */
        }
      } else { out.push(t); i++; }
    }
    return out.join(' ');
  }

  function _roundSvgPathData(svgStr, decimals) {
    var dp = decimals || 2;
    /* Round numbers inside d="..." attribute values */
    var out = svgStr.replace(/\bd="([^"]*)"/g, function(match, dAttr) {
      if (/[Aa]/.test(dAttr)) dAttr = _unfuseArcFlags(dAttr); /* v2.9.23 — see above */
      var rounded = dAttr.replace(/[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g, function(num) {
        var v = parseFloat(num);
        return isNaN(v) ? num : +(v.toFixed(dp));
      });
      return 'd="' + rounded + '"';
    });
    /* Strip redundant xmlns:xlink that XMLSerializer injects — but only
       if no xlink:href references remain (keeps XML valid for SVG 1.x content) */
    if (!/xlink:href/.test(out)) {
      out = out.replace(/ xmlns:xlink="http:\/\/www\.w3\.org\/1999\/xlink"/g, '');
    }
    return out;
  }

  /* v2.13.4 — NORMALIZE-BEFORE-ROUND for traced SVG (CB20260811SNU8, Aug 12).
     Vectorizer.ai emits arc-heavy SVG (A commands with sub-pixel radii).
     _roundSvgPathData(raw, 1) rounds ALL numbers — including arc radii — to
     1 decimal place, so rx=0.04 → rx=0. When normalizeSvgPathD then converts
     arcs to cubics, svgArcToBeziers returns [] for zero-radius arcs and the
     geometry collapses — thin features (i-stems, fine serifs) vanish from the
     DXF/EPS while the browser-rasterized audit SVG renders them perfectly.
     Fix: convert arcs to cubic beziers at FULL PRECISION first, then round
     the resulting (x,y) control-point coordinates. Rounding coordinates is
     safe; rounding arc parameters is destructive. Per-d try-catch: a
     malformed d-string falls back to the original (which _roundSvgPathData
     then rounds safely, matching the old behavior for that path). */
  function _normalizeAndRoundSvg(rawSvg, dp) {
    var normalized = rawSvg.replace(/\bd="([^"]+)"/g, function(_m, dd) {
      try { return 'd="' + normalizeSvgPathD(dd) + '"'; }
      catch(_) { return 'd="' + dd + '"'; }
    });
    return _roundSvgPathData(normalized, dp);
  }

  /**
   * Build a positioned SVG <g> group for a single vector layer from its
   * svgMarkup. Handles viewBox→production coordinate mapping, rotation,
   * flip, and ID namespacing. Returns an SVG <g> string, or null.
   */
  function buildSingleVectorLayerGroup(l, coordSpace, groupIdx) {
    if (!(l.isSvg && l.svgMarkup)) return null;
    const { uniformScale, offX, offY, sidePA } = coordSpace;
    const parser = new DOMParser();
    const doc = parser.parseFromString(l.svgMarkup, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return null;
    const svgEl = doc.documentElement;

    let vbX = 0, vbY = 0, vbW = 0, vbH = 0;
    const vb = svgEl.getAttribute('viewBox');
    if (vb) {
      const p = vb.split(/[\s,]+/).map(Number);
      vbX = p[0] || 0; vbY = p[1] || 0; vbW = p[2] || 0; vbH = p[3] || 0;
    }
    if (!vbW) vbW = parseFloat(svgEl.getAttribute('width')) || 100;
    if (!vbH) vbH = parseFloat(svgEl.getAttribute('height')) || 100;

    /* Ink-box remap (display parity): the display raster is TRIMMED to the
       ink and stretched into the layer box, so the vector paths must map the
       ink rect — not the full viewBox — into the box. Absolute viewBox frame
       (replaces the -vbX/-vbY origin shift below; do not stack both). Falls
       back to the full viewBox until the async cache lands — callers that
       ship files (buildCompositeOutlinedSvg) await ensureSvgInkBox first. */
    const ink = svgInkBoxOf(l);
    let srcX = vbX, srcY = vbY, srcW = vbW, srcH = vbH;
    if (ink && ink.w > 0 && ink.h > 0) { srcX = ink.x; srcY = ink.y; srcW = ink.w; srcH = ink.h; }

    const lx = (l.x - sidePA.x) * uniformScale + offX;
    const ly = (l.y - sidePA.y) * uniformScale + offY;
    const lw = l.w * uniformScale, lh = l.h * uniformScale;
    const cx = l.centered ? lx : lx + lw / 2;
    const cy = l.centered ? ly : ly + lh / 2;

    const R = v => +v.toFixed(2);
    const xforms = [];
    if (l.rotation || l.flipH || l.flipV) {
      xforms.push(`translate(${R(cx)},${R(cy)})`);
      if (l.rotation) xforms.push(`rotate(${R(l.rotation)})`);
      if (l.flipH) xforms.push(`scale(-1,1)`);
      if (l.flipV) xforms.push(`scale(1,-1)`);
      xforms.push(`translate(${R(-cx)},${R(-cy)})`);
    }
    xforms.push(`translate(${R(lx)},${R(ly)})`);
    xforms.push(`scale(${R(lw / srcW)},${R(lh / srcH)})`);
    if (srcX || srcY) xforms.push(`translate(${R(-srcX)},${R(-srcY)})`);

    let inner = Array.from(svgEl.childNodes)
      .map(n => n.nodeType === 1 ? new XMLSerializer().serializeToString(n) : '')
      .filter(Boolean).join('');
    /* Round numeric values in path d-attributes to 2 decimal places */
    inner = _roundSvgPathData(inner);
    const ids = new Set();
    inner.replace(/\bid="([^"]+)"/g, (_, id) => { ids.add(id); return _; });
    if (ids.size) {
      for (const id of ids) {
        const nsId = `L${groupIdx}_${id}`;
        const esc = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        inner = inner.replace(new RegExp(`\\bid="${esc}"`, 'g'), `id="${nsId}"`);
        inner = inner.replace(new RegExp(`url\\(\\s*['"]?#${esc}['"]?\\s*\\)`, 'g'), `url(#${nsId})`);
        inner = inner.replace(new RegExp(`href="#${esc}"`, 'g'), `href="#${nsId}"`);
        inner = inner.replace(new RegExp(`xlink:href="#${esc}"`, 'g'), `xlink:href="#${nsId}"`);
      }
    }
    return inner ? `<g transform="${xforms.join(' ')}">${inner}</g>` : null;
  }

  /**
   * Extract inner SVG content from a traced SVG string, wrapped in a <g>
   * with coordinate mapping if the viewBox differs from the target space.
   * Returns an SVG <g> string, or empty string.
   */
  function extractTracedSvgGroup(tracedSvgStr, targetW, targetH) {
    if (!tracedSvgStr) return '';
    try {
      const p = new DOMParser();
      const td = p.parseFromString(tracedSvgStr, 'image/svg+xml');
      if (td.querySelector('parsererror')) return '';
      const tsvg = td.documentElement;
      const tvb = tsvg.getAttribute('viewBox');
      let tScale = '';
      if (tvb) {
        const tp = tvb.split(/[\s,]+/).map(Number);
        const tvbW = tp[2] || targetW, tvbH = tp[3] || targetH;
        if (Math.abs(tvbW - targetW) > 1 || Math.abs(tvbH - targetH) > 1) {
          tScale = ` transform="scale(${+(targetW / tvbW).toFixed(4)},${+(targetH / tvbH).toFixed(4)})"`;
        }
      }
      /* Drop hairline-degenerate tracer artifacts (a 0.45-unit-thin ellipse
         parked at the origin in order CB36011's trace) — they engrave nothing
         visible but confuse downstream editors. clipPath children are
         structural and stay. */
      tsvg.querySelectorAll('ellipse, rect, circle').forEach(el => {
        if (el.parentElement && el.parentElement.tagName === 'clipPath') return;
        const da = parseFloat(el.getAttribute('rx') || el.getAttribute('r') || el.getAttribute('width') || 0);
        const db = parseFloat(el.getAttribute('ry') || el.getAttribute('r') || el.getAttribute('height') || 0);
        if (Math.min(Math.abs(da), Math.abs(db)) < 0.5) el.remove();
      });
      let inner = Array.from(tsvg.childNodes)
        .map(n => n.nodeType === 1 ? new XMLSerializer().serializeToString(n) : '')
        .filter(Boolean).join('');
      /* v2.13.4: normalize-before-round — convert arcs to cubic beziers at
         full precision FIRST, then round the (x,y) coordinates. The old order
         rounded arc radii to 1dp, collapsing sub-pixel arcs to zero radius
         and destroying thin features (i-stems, fine serifs) in the DXF/EPS.
         Illustrator/laser-safe: re-emit every traced path as absolute M/L/C/Z
         (see normalizeSvgPathD). The tracer's elliptical arcs — including
         zero/near-zero-radius and rotated ones — are exactly the constructs
         Adobe Illustrator's importer corrupts into filled wedges. */
      inner = _normalizeAndRoundSvg(inner, 1);
      return inner ? `<g id="traced-raster"${tScale}>${inner}</g>` : '';
    } catch (e) { return ''; }
  }

  /**
   * Build a composite production SVG combining live <text> elements,
   * vector layer markup, and optional traced raster content.
   * Text uses @font-face for editability. Vector layers keep original SVG.
   * Returns an SVG string, or null if no content.
   */
  function buildCompositeLiveTextSvg(sideSlug, forceBlack, tracedSvgStr) {
    const cs = getProductionCoordSpace(sideSlug);
    if (!cs) return null;
    const { pxW, pxH, uniformScale, offX, offY, sidePA, phys } = cs;

    const ls = S.byView[sideSlug];
    const visible = (ls || []).filter(l => l.visible !== false);
    const textLayers = visible.filter(l => l.type === 'text' && (l.text || '').trim());
    const tracedGroup = extractTracedSvgGroup(tracedSvgStr, pxW, pxH);

    if (!textLayers.length && !visible.some(l => l.isSvg && l.svgMarkup) && !tracedGroup) return null;

    const escXml = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    /* Collect @font-face / @import rules for text layers */
    const fonts = new Set();
    textLayers.forEach(l => fonts.add(l.font || 'Montserrat'));
    const fontRules = [];
    fonts.forEach(fname => {
      const cf = (CFG.customFonts || []).find(f => {
        if (f.familyCss === fname || f.familyName === fname || f.displayName === fname) return true;
        const cleaned = (f.familyCss || '').split(',')[0].replace(/['"]/g, '').trim();
        return cleaned === fname;
      });
      if (cf?.fontFileUrl) {
        const fmt = fontFaceFormat(cf.fontFileUrl);
        const src = fmt ? `url('${cf.fontFileUrl}') format('${fmt}')` : `url('${cf.fontFileUrl}')`;
        fontRules.push(`@font-face { font-family: '${escXml(fname)}'; src: ${src}; }`);
      } else if (fname !== 'sans-serif' && fname !== 'serif' && fname !== 'monospace') {
        fontRules.push(`@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(fname)}:wght@400;700&amp;display=swap');`);
      }
    });

    const parts = [
      '<?xml version="1.0" encoding="UTF-8"?>',
      `<svg xmlns="http://www.w3.org/2000/svg" width="${phys.wInches}in" height="${phys.hInches}in" viewBox="0 0 ${pxW} ${pxH}">`,
    ];
    if (fontRules.length) parts.push(`<defs><style>${fontRules.join('\n')}</style></defs>`);
    parts.push(productionClipOpen(sideSlug, pxW, pxH));

    /* Z-order around the traced raster: the traced group stands in for the
       side's RASTER layers, so text/vector layers whose z-position is BELOW
       the first raster layer must render beneath it — e.g. a backdrop
       circle under a photo (order CB36156 shipped the backdrop drawn over
       the traced logo). Everything else renders after the traced group,
       preserving editor z-order. */
    const firstRasterIdx = visible.findIndex(l => l.type !== 'text' && !(l.isSvg && l.svgMarkup));
    const belowTraced = [];
    const aboveTraced = [];

    let gIdx = 0;
    for (const l of visible) {
      const bucket = (tracedGroup && firstRasterIdx !== -1 && gIdx < firstRasterIdx) ? belowTraced : aboveTraced;
      if (l.type === 'text' && (l.text || '').trim()) {
        const lx = (l.x - sidePA.x) * uniformScale + offX;
        const ly = (l.y - sidePA.y) * uniformScale + offY;
        const lw = (l.w || 0) * uniformScale;
        const lh = (l.h || 0) * uniformScale;
        const cx = l.centered ? lx : lx + lw / 2;
        const cy = l.centered ? ly : ly + lh / 2;
        const fs = (l.fontSize || 36) * uniformScale;
        const fillColor = forceBlack ? '#000000' : (l.color || '#000');
        const fontFamily = `'${escXml(l.font || 'Montserrat')}', sans-serif`;
        const drawX = l.centered ? lx : lx + lw / 2;
        const drawY = l.centered ? ly : ly + lh / 2;
        const lines = (l.text || '').split('\n');
        const lineH = fs * (l.lineHeight || 1.25);
        const totalH = lines.length * lineH;
        const startY = drawY - totalH / 2 + lineH / 2;
        const xforms = [];
        if (l.rotation) xforms.push(`rotate(${l.rotation} ${cx.toFixed(2)} ${cy.toFixed(2)})`);
        if (l.flipH || l.flipV) {
          xforms.push(`translate(${cx.toFixed(2)} ${cy.toFixed(2)})`);
          xforms.push(`scale(${l.flipH ? -1 : 1} ${l.flipV ? -1 : 1})`);
          xforms.push(`translate(${(-cx).toFixed(2)} ${(-cy).toFixed(2)})`);
        }
        const tfAttr = xforms.length ? ` transform="${xforms.join(' ')}"` : '';
        const chunk = [`<g id="text-layer-${gIdx}"${tfAttr}>`];
        lines.forEach((line, i) => {
          const lineY = startY + i * lineH;
          /* Italic as an EXPLICIT 14° skew, not font-style="italic":
             no italic faces are ever loaded (css2 @import is wght-only;
             custom fonts ship one file), so browsers synthesize the
             oblique — but Illustrator/RIPs don't synthesize from the
             attribute and rendered text UPRIGHT (order CB36331 "Super
             Shiney"). The skew matches Chrome's synthesis (measured
             13.9° ≈ CSS 14° standard), anchored ≈ at the baseline
             (center + 0.33em) so the line doesn't drift horizontally. */
          const _skewY = (lineY + fs * 0.33).toFixed(2);
          const skewAttr = l.italic
            ? ` transform="translate(0 ${_skewY}) skewX(-14) translate(0 -${_skewY})"`
            : '';
          chunk.push(
            `<text x="${drawX.toFixed(2)}" y="${lineY.toFixed(2)}" ` +
            `text-anchor="middle" dominant-baseline="central" ` +
            `font-family="${fontFamily}" font-size="${fs.toFixed(1)}"` +
            `${l.bold ? ' font-weight="bold"' : ''}${skewAttr} ` +
            `fill="${fillColor}">${escXml(line)}</text>`
          );
        });
        chunk.push('</g>');
        bucket.push(chunk.join('\n'));
      } else if (l.isSvg && l.svgMarkup) {
        const g = buildSingleVectorLayerGroup(l, cs, gIdx);
        if (g) bucket.push(g);
      }
      gIdx++;
    }

    belowTraced.forEach(m => parts.push(m));
    if (tracedGroup) parts.push(tracedGroup);
    aboveTraced.forEach(m => parts.push(m));

    parts.push('</g></svg>');
    return parts.join('\n');
  }

  /**
   * Build a composite production SVG with all text outlined via opentype.js
   * and vector layers as original SVG groups. Optionally includes traced
   * raster content. The output is an all-paths SVG suitable for EPS/DXF
   * conversion via geoWorkerConvert — no font dependencies in the output.
   * Returns an SVG string, or null if no content. Async (opentype.js).
   */
  async function buildCompositeOutlinedSvg(sideSlug, forceBlack, tracedSvgStr, layerPred) {
    const cs = getProductionCoordSpace(sideSlug);
    if (!cs) return null;
    const { pxW, pxH, phys } = cs;

    /* Optional z-filter: layerPred(zIdx) selects which VISIBLE layers (by
       index among visible) participate. generateProductionPDF uses it to
       split vector content around the embedded raster so a backdrop no
       longer paints OVER the photo it sits behind (Jul 22 order). Omitted →
       all layers, byte-identical to every existing caller. */
    const pred = typeof layerPred === 'function' ? layerPred : null;

    /* Text → opentype.js outlined paths */
    const textPathsAll = await buildTextOutlinePaths(sideSlug, cs, forceBlack);
    const textPaths = pred ? textPathsAll.filter(p => pred(p.zIdx ?? Infinity)) : textPathsAll;

    const ls = S.byView[sideSlug];
    const visible = (ls || []).filter(l => l.visible !== false);

    /* If the side HAS text (within the predicate's slice) but outlining
       produced nothing (font fetch or parse failure), abort the outlined
       build entirely — never ship EPS/DXF or a vector PDF that silently
       omits the customer's text. The guard filters by the SAME zIdx basis
       buildTextOutlinePaths uses (index among visible), so a slice with no
       text layers can still build its vectors. Callers already handle null. */
    const _txtLayers = visible.filter((l, zi) =>
      l.type === 'text' && (l.text || '').trim() && (!pred || pred(zi)));
    if (_txtLayers.length && !textPaths.length) return null;

    /* Resolve ink boxes BEFORE building vector groups (display parity map —
       buildSingleVectorLayerGroup reads the sync cache). */
    await Promise.all(visible.filter(l => l.isSvg && l.svgMarkup).map(ensureSvgInkBox));

    /* Vector layer groups — keep each group's original z-position so it can
       be split around the traced raster content below */
    const vecGroups = [];
    let gIdx = 0;
    for (const l of visible) {
      if (l.type !== 'text' && l.isSvg && l.svgMarkup && (!pred || pred(gIdx))) {
        const g = buildSingleVectorLayerGroup(l, cs, gIdx);
        if (g) vecGroups.push({ markup: g, zIdx: gIdx });
      }
      gIdx++;
    }

    /* Traced raster content */
    const tracedGroup = extractTracedSvgGroup(tracedSvgStr, pxW, pxH);

    if (!textPaths.length && !vecGroups.length && !tracedGroup) return null;

    const parts = [
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${pxW} ${pxH}" width="${pxW}" height="${pxH}">`,
      productionClipOpen(sideSlug, pxW, pxH),
    ];

    /* Z-order around the traced raster: the traced group stands in for the
       side's RASTER layers, so vector groups / outlined text whose layer
       sits BELOW the first raster layer render beneath it (backdrop-under-
       photo, order CB36156); everything else renders above, matching the
       editor. Text still paints after vectors within each half. */
    const firstRasterIdx = visible.findIndex(l => l.type !== 'text' && !(l.isSvg && l.svgMarkup));
    const isBelow = z => !!tracedGroup && firstRasterIdx !== -1 && z < firstRasterIdx;
    const emitText = (p, idx) => {
      const tf = p.transform ? ` transform="${p.transform}"` : '';
      /* data-dt-fauxbold marks the synthetic-bold stroke explicitly so the
         engrave dilation (v2.9.11) targets ONLY faux-bold text — clipart
         with coincidentally identical stroke/fill colors is never touched. */
      const sw = p.strokeWidth ? ` stroke="${p.fill}" stroke-width="${p.strokeWidth}" stroke-linejoin="round" data-dt-fauxbold="1"` : '';
      parts.push(`<path id="text-${idx}" d="${p.pathData}" fill="${p.fill}"${sw}${tf}/>`);
    };

    vecGroups.forEach(g => { if (isBelow(g.zIdx)) parts.push(g.markup); });
    textPaths.forEach((p, idx) => { if (isBelow(p.zIdx ?? Infinity)) emitText(p, idx); });
    /* v2.9.16: gate the traced group on `pred`, like vecGroups and textPaths already are.
       generateProductionPDF calls this function TWICE per side — once for the slice below
       the embedded raster, once for the slice above — but tracedGroup was pushed
       unconditionally, so whenever both passes ran the traced art was painted TWICE:
       duplicate ink, and two elements sharing the id="traced-raster" that
       extractTracedSvgGroup hard-codes. Its z is the first raster layer, which is exactly
       the pivot isBelow() already uses, so it lands in the "above" pass. With no pred
       (single-pass callers: engrave, bulk) behaviour is unchanged. */
    if (tracedGroup && (!pred || pred(firstRasterIdx))) parts.push(tracedGroup);
    vecGroups.forEach(g => { if (!isBelow(g.zIdx)) parts.push(g.markup); });
    textPaths.forEach((p, idx) => { if (!isBelow(p.zIdx ?? Infinity)) emitText(p, idx); });

    parts.push('</g></svg>');
    return parts.join('\n');
  }

  /* ── v2.9.30 EZCAD overlap union (FINDING-ezcad-overlap-parity) ──────
     EZCAD2's autoshader fills an imported DXF group by EVEN-ODD PARITY
     across every contour in the group. A name that overlaps clipart
     therefore XORs to unfilled where the two regions cross — letters
     engrave INVERTED (RICHARD / RAMIRO / FRANKIE orders, Jul 2026).
     v2.9.23's winding normalisation cannot help: parity fill ignores
     orientation, and these contours CROSS, they don't nest.

     This function detects real cross-object overlap and boolean-unions
     ONLY the overlapping cluster(s) into crossing-free outlines — the
     region the customer-approved mockup shows. Everything else passes
     through as the SAME array references, so the emitted bytes for
     non-overlapping geometry are identical to not running this at all.

     Semantics (validated against a shapely reference on the real RICHARD
     order file before porting): objects = nesting trees (parent = smallest
     non-crossing container); region per object = even-odd of its rings;
     desired output region = UNION of object regions. Boundary rebuild =
     split all cluster segments at intersections, keep sub-segments whose
     sides differ in desired-region membership, orient interior-on-LEFT
     (exteriors come out CCW, holes CW — same convention v2.9.23 emits),
     stitch loops taking the leftmost turn at junctions.

     Self-check: parity-samples the rebuilt loops against the desired
     region; ANY doubt returns {fellBack:true} and the caller ships
     today's un-unioned geometry (status quo) plus a gwarn via meta.
     Returns null when no overlap exists (the common case — gate closed).
     Pure computation, worker-safe: no closure/DOM references. */
  function _dtEngraveOverlapUnion(polylines) {
    var TOL = 0.05;      /* closed-ring tolerance — same as the DXF emitter */
    var QK  = 1e-6;      /* stitch key quantum = emission precision (toFixed(6)) */

    /* -- collect closed rings (strip duplicate closing vertex) -- */
    var rings = [], ringPoly = [];
    for (var i = 0; i < polylines.length; i++) {
      var pts = polylines[i];
      if (pts.length > 3 &&
          Math.abs(pts[0][0] - pts[pts.length - 1][0]) < TOL &&
          Math.abs(pts[0][1] - pts[pts.length - 1][1]) < TOL) {
        var r = pts.slice(0, -1);
        if (r.length >= 3) { rings.push(r); ringPoly.push(i); }
      }
    }
    var n = rings.length;
    if (n < 2) return null;

    var bboxes = rings.map(function (r) {
      var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (var k = 0; k < r.length; k++) {
        var p = r[k];
        if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0];
        if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1];
      }
      return [x0, y0, x1, y1];
    });
    var absArea = function (r) {
      var a = 0;
      for (var k = 0, m = r.length; k < m; k++) {
        var p = r[k], q = r[(k + 1) % m];
        a += p[0] * q[1] - q[0] * p[1];
      }
      return Math.abs(a / 2);
    };
    var areas = rings.map(absArea);
    var pip = function (x, y, r) { /* even-odd ray cast */
      var inside = false;
      for (var k = 0, m = r.length, j = m - 1; k < m; j = k++) {
        var yi = r[k][1], yj = r[j][1];
        if ((yi > y) !== (yj > y) &&
            x < (r[j][0] - r[k][0]) * (y - yi) / (yj - yi) + r[k][0]) inside = !inside;
      }
      return inside;
    };
    /* y-bucketed ray cast — same half-open rule as pip, ~NB× fewer edge
       tests. Union probes hammer the big art contours (10k+ vertices);
       the naive scan made a real order take seconds. */
    var makePipTester = function (pts) {
      var m = pts.length;
      if (m < 96) return function (x, y) { return pip(x, y, pts); };
      var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, k;
      for (k = 0; k < m; k++) {
        var p = pts[k];
        if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0];
        if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1];
      }
      var NB = 64, span = Math.max(y1 - y0, 1e-9), buckets = new Array(NB);
      for (k = 0; k < NB; k++) buckets[k] = [];
      for (k = 0; k < m; k++) {
        var ya = pts[k][1], yb = pts[(k + 1) % m][1];
        var b0 = Math.max(0, Math.min(NB - 1, Math.floor((Math.min(ya, yb) - y0) / span * NB)));
        var b1 = Math.max(0, Math.min(NB - 1, Math.floor((Math.max(ya, yb) - y0) / span * NB)));
        for (var bb = b0; bb <= b1; bb++) buckets[bb].push(k);
      }
      return function (x, y) {
        if (x < x0 || x > x1 || y < y0 || y > y1) return false;
        var list = buckets[Math.max(0, Math.min(NB - 1, Math.floor((y - y0) / span * NB)))];
        var inside = false;
        for (var q = 0; q < list.length; q++) {
          var e = list[q], yi = pts[e][1], yj = pts[(e + 1) % m][1];
          if ((yi > y) !== (yj > y) &&
              x < (pts[(e + 1) % m][0] - pts[e][0]) * (y - yi) / (yj - yi) + pts[e][0]) inside = !inside;
        }
        return inside;
      };
    };
    var _ringTesters = new Array(n);
    var ringTest = function (ri, x, y) {
      var t9 = _ringTesters[ri] || (_ringTesters[ri] = makePipTester(rings[ri]));
      return t9(x, y);
    };

    /* -- transversal ring-ring crossing (strict interior params) -- */
    var segCross = function (p1, p2, p3, p4) {
      var d1x = p2[0] - p1[0], d1y = p2[1] - p1[1];
      var d2x = p4[0] - p3[0], d2y = p4[1] - p3[1];
      var den = d1x * d2y - d1y * d2x;
      if (Math.abs(den) < 1e-12) return false;
      var t = ((p3[0] - p1[0]) * d2y - (p3[1] - p1[1]) * d2x) / den;
      var u = ((p3[0] - p1[0]) * d1y - (p3[1] - p1[1]) * d1x) / den;
      var e = 1e-9;
      return t > e && t < 1 - e && u > e && u < 1 - e;
    };
    /* y-bucketed segment index per ring (lazy) so ring-vs-ring crossing
       is not O(nA×nB) against 10k-vertex art contours */
    var _segBk = new Array(n);
    var segBuckets = function (ri) {
      if (_segBk[ri]) return _segBk[ri];
      var r = rings[ri], m = r.length, b = bboxes[ri];
      var NB = 64, span = Math.max(b[3] - b[1], 1e-9), bk = new Array(NB);
      for (var k = 0; k < NB; k++) bk[k] = [];
      for (k = 0; k < m; k++) {
        var ya = r[k][1], yb = r[(k + 1) % m][1];
        var b0 = Math.max(0, Math.min(NB - 1, Math.floor((Math.min(ya, yb) - b[1]) / span * NB)));
        var b1 = Math.max(0, Math.min(NB - 1, Math.floor((Math.max(ya, yb) - b[1]) / span * NB)));
        for (var bb = b0; bb <= b1; bb++) bk[bb].push(k);
      }
      return (_segBk[ri] = { NB: NB, span: span, y0: b[1], bk: bk });
    };
    var ringsCross = function (a, b) {
      var A = rings[a], B = rings[b], BB = bboxes[b];
      var idx = segBuckets(b), mb = B.length;
      for (var k = 0, m = A.length; k < m; k++) {
        var p1 = A[k], p2 = A[(k + 1) % m];
        var sy0 = Math.min(p1[1], p2[1]), sy1 = Math.max(p1[1], p2[1]);
        if (Math.max(p1[0], p2[0]) < BB[0] || Math.min(p1[0], p2[0]) > BB[2] ||
            sy1 < BB[1] || sy0 > BB[3]) continue;
        var q0 = Math.max(0, Math.min(idx.NB - 1, Math.floor((sy0 - idx.y0) / idx.span * idx.NB)));
        var q1 = Math.max(0, Math.min(idx.NB - 1, Math.floor((sy1 - idx.y0) / idx.span * idx.NB)));
        for (var qq = q0; qq <= q1; qq++) {
          var list = idx.bk[qq];
          for (var l = 0; l < list.length; l++) {
            var e = list[l];
            if (segCross(p1, p2, B[e], B[(e + 1) % mb])) return true;
          }
        }
      }
      return false;
    };
    var crossKey = {}, bboxOverlap = function (a, b) {
      var A = bboxes[a], B = bboxes[b];
      return !(A[2] < B[0] || B[2] < A[0] || A[3] < B[1] || B[3] < A[1]);
    };
    var crossing = function (a, b) {
      var k = a < b ? a * n + b : b * n + a;
      if (crossKey[k] === undefined) crossKey[k] = bboxOverlap(a, b) && ringsCross(a, b);
      return crossKey[k];
    };

    /* -- nesting forest: parent = smallest non-crossing strict container -- */
    var mostlyInside = function (a, b) { /* 5-sample majority vote (v2.9.23 pattern) */
      var r = rings[a], nS = Math.min(5, r.length), inC = 0;
      for (var s = 0; s < nS; s++) {
        var v = r[Math.floor(s * r.length / nS)];
        if (ringTest(b, v[0], v[1])) inC++;
      }
      return inC * 2 > nS;
    };
    var parent = new Array(n);
    for (i = 0; i < n; i++) {
      var best = -1;
      for (var j = 0; j < n; j++) {
        if (i === j || areas[j] <= areas[i]) continue;
        var A = bboxes[i], B = bboxes[j];
        if (A[0] < B[0] - TOL || A[1] < B[1] - TOL || A[2] > B[2] + TOL || A[3] > B[3] + TOL) continue;
        if (crossing(i, j)) continue;
        if (!mostlyInside(i, j)) continue;
        if (best < 0 || areas[j] < areas[best]) best = j;
      }
      parent[i] = best;
    }
    var rootOf = new Array(n);
    for (i = 0; i < n; i++) {
      var w = i, guard = 0;
      while (parent[w] >= 0 && guard++ <= n) w = parent[w];
      if (guard > n) return { fellBack: true, reason: 'nesting cycle' };
      rootOf[i] = w;
    }
    var objRings = {};
    for (i = 0; i < n; i++) (objRings[rootOf[i]] = objRings[rootOf[i]] || []).push(i);
    var objIds = Object.keys(objRings).map(Number);
    if (objIds.length < 2) return null;

    /* even-odd parity of one OBJECT at a point */
    var objParity = function (x, y, oid) {
      var mem = objRings[oid], par = false;
      for (var k = 0; k < mem.length; k++) {
        var b = bboxes[mem[k]];
        if (x < b[0] || x > b[2] || y < b[1] || y > b[3]) continue;
        if (ringTest(mem[k], x, y)) par = !par;
      }
      return par;
    };

    /* -- object-level overlap detection (THE GATE) -- */
    var objOverlaps = function (oa, ob) {
      var ma = objRings[oa], mb = objRings[ob], k, l;
      for (k = 0; k < ma.length; k++) {
        for (l = 0; l < mb.length; l++) {
          if (bboxOverlap(ma[k], mb[l]) && crossing(ma[k], mb[l])) return true;
        }
      }
      /* containment without crossing: sampled vertices of one object's rings
         landing at ODD parity inside the other object (letter swallowed by a
         solid art region → merge; letter inside an art HOLE → parity even →
         no overlap, passthrough — correct either way). */
      var vertexSwallowed = function (from, into) {
        var mem = objRings[from];
        for (var k2 = 0; k2 < mem.length; k2++) {
          var r = rings[mem[k2]], nS = Math.min(5, r.length), odd = 0;
          for (var s = 0; s < nS; s++) {
            var v = r[Math.floor(s * r.length / nS)];
            if (objParity(v[0], v[1], into)) odd++;
          }
          if (odd * 2 > nS) return true;
        }
        return false;
      };
      return vertexSwallowed(oa, ob) || vertexSwallowed(ob, oa);
    };
    var uf = {}; var ufFind = function (x) { while (uf[x] !== undefined && uf[x] !== x) { uf[x] = uf[uf[x]] !== undefined ? uf[uf[x]] : uf[x]; x = uf[x]; } return x; };
    for (i = 0; i < objIds.length; i++) uf[objIds[i]] = objIds[i];
    var anyOverlap = false;
    for (i = 0; i < objIds.length; i++) {
      for (var j2 = i + 1; j2 < objIds.length; j2++) {
        var oa = objIds[i], ob = objIds[j2];
        var ba = null, bb = null, mm;
        /* object bbox = union of member ring bboxes */
        for (mm = 0; mm < objRings[oa].length; mm++) { var bx = bboxes[objRings[oa][mm]]; ba = ba ? [Math.min(ba[0], bx[0]), Math.min(ba[1], bx[1]), Math.max(ba[2], bx[2]), Math.max(ba[3], bx[3])] : bx.slice(); }
        for (mm = 0; mm < objRings[ob].length; mm++) { var by = bboxes[objRings[ob][mm]]; bb = bb ? [Math.min(bb[0], by[0]), Math.min(bb[1], by[1]), Math.max(bb[2], by[2]), Math.max(bb[3], by[3])] : by.slice(); }
        if (ba[2] < bb[0] || bb[2] < ba[0] || ba[3] < bb[1] || bb[3] < ba[1]) continue;
        if (objOverlaps(oa, ob)) { anyOverlap = true; uf[ufFind(oa)] = ufFind(ob); }
      }
    }
    if (!anyOverlap) return null;   /* GATE CLOSED — byte-identical output */

    var clusters = {};
    for (i = 0; i < objIds.length; i++) {
      var root = ufFind(objIds[i]);
      (clusters[root] = clusters[root] || []).push(objIds[i]);
    }

    /* -- rebuild each multi-object cluster -- */
    var replacedRings = {};          /* ring idx → cluster tag */
    var clusterLoops = {};           /* cluster tag → emitted loops */
    var clusterFirstRing = {};       /* cluster tag → smallest original ring idx */
    var tags = Object.keys(clusters).filter(function (t) { return clusters[t].length > 1; });
    if (!tags.length) return null;

    for (var t = 0; t < tags.length; t++) {
      var tag = tags[t];
      var members = clusters[tag];                       /* object ids */
      var memRings = [];                                 /* ring indices */
      for (i = 0; i < members.length; i++) memRings = memRings.concat(objRings[members[i]]);
      var firstRing = Math.min.apply(null, memRings);

      var desired = function (x, y) {
        for (var k = 0; k < members.length; k++) { if (objParity(x, y, members[k])) return true; }
        return false;
      };

      /* cluster bbox */
      var cb = null;
      for (i = 0; i < memRings.length; i++) {
        var b2 = bboxes[memRings[i]];
        cb = cb ? [Math.min(cb[0], b2[0]), Math.min(cb[1], b2[1]), Math.max(cb[2], b2[2]), Math.max(cb[3], b2[3])] : b2.slice();
      }

      /* segments of every member ring */
      var segs = [];
      for (i = 0; i < memRings.length; i++) {
        var r2 = rings[memRings[i]];
        for (var k3 = 0, m3 = r2.length; k3 < m3; k3++) {
          var pA = r2[k3], pB = r2[(k3 + 1) % m3];
          if (Math.abs(pA[0] - pB[0]) < 1e-9 && Math.abs(pA[1] - pB[1]) < 1e-9) continue;
          segs.push([pA[0], pA[1], pB[0], pB[1]]);
        }
      }
      var S = segs.length;
      if (!S || S > 200000) return { fellBack: true, reason: 'cluster too large (' + S + ' segments)' };

      /* uniform-grid bucketing for pairwise intersection */
      var GRID = 64;
      var cw = Math.max(cb[2] - cb[0], 1e-9) / GRID, ch = Math.max(cb[3] - cb[1], 1e-9) / GRID;
      var cells = {};
      var cellRange = function (sg) {
        var x0 = Math.min(sg[0], sg[2]), x1 = Math.max(sg[0], sg[2]);
        var y0 = Math.min(sg[1], sg[3]), y1 = Math.max(sg[1], sg[3]);
        return [
          Math.max(0, Math.floor((x0 - cb[0]) / cw)), Math.min(GRID - 1, Math.floor((x1 - cb[0]) / cw)),
          Math.max(0, Math.floor((y0 - cb[1]) / ch)), Math.min(GRID - 1, Math.floor((y1 - cb[1]) / ch)),
        ];
      };
      for (i = 0; i < S; i++) {
        var cr = cellRange(segs[i]);
        for (var cx = cr[0]; cx <= cr[1]; cx++) {
          for (var cy = cr[2]; cy <= cr[3]; cy++) {
            (cells[cx * GRID + cy] = cells[cx * GRID + cy] || []).push(i);
          }
        }
      }
      /* Split events carry ONE canonical point shared by both segments —
         computing the point per-segment drifts by float error across the
         1e-6 stitch quantum and breaks chain closure. Points landing
         within half a quantum of a segment endpoint snap to that exact
         vertex so T-junctions key onto the ring's own vertices. */
      var splitEv = [];
      for (i = 0; i < S; i++) splitEv.push([]);
      var SNAP = 1e-3; /* 1 micron: crossings this close to a vertex collapse
                          onto it, killing epsilon-slivers no probe can
                          classify. Far below engraver spot size (~30 µm). */
      var snapPt = function (P, sg3) {
        var d0 = Math.abs(P[0] - sg3[0]) + Math.abs(P[1] - sg3[1]);
        var d1 = Math.abs(P[0] - sg3[2]) + Math.abs(P[1] - sg3[3]);
        if (d0 < SNAP && d0 <= d1) return [sg3[0], sg3[1]];
        if (d1 < SNAP) return [sg3[2], sg3[3]];
        return P;
      };
      var addEv = function (si, tv, P) {
        if (tv > 1e-9 && tv < 1 - 1e-9) splitEv[si].push([tv, P]);
      };
      var pairSeen = {};
      for (var ck in cells) {
        var list = cells[ck];
        for (i = 0; i < list.length; i++) {
          for (var j3 = i + 1; j3 < list.length; j3++) {
            var a2 = list[i], b3 = list[j3];
            var pk = a2 < b3 ? a2 * S + b3 : b3 * S + a2;
            if (pairSeen[pk]) continue;
            pairSeen[pk] = 1;
            var g = segs[a2], h = segs[b3];
            var d1x = g[2] - g[0], d1y = g[3] - g[1];
            var d2x = h[2] - h[0], d2y = h[3] - h[1];
            var den = d1x * d2y - d1y * d2x;
            var e2 = 1e-9;
            if (Math.abs(den) > 1e-12) {
              var tP = ((h[0] - g[0]) * d2y - (h[1] - g[1]) * d2x) / den;
              var uP = ((h[0] - g[0]) * d1y - (h[1] - g[1]) * d1x) / den;
              if (tP > -e2 && tP < 1 + e2 && uP > -e2 && uP < 1 + e2) {
                var P = [g[0] + d1x * tP, g[1] + d1y * tP];
                P = snapPt(snapPt(P, g), h);
                addEv(a2, tP, P);
                addEv(b3, uP, P);
              }
            } else {
              /* parallel: collinear overlap → split each at the other's
                 exact endpoints (shared coordinates by construction) */
              var len2 = d1x * d1x + d1y * d1y;
              if (len2 > 0) {
                var dist = Math.abs((h[0] - g[0]) * d1y - (h[1] - g[1]) * d1x) / Math.sqrt(len2);
                if (dist < 1e-9) {
                  addEv(a2, ((h[0] - g[0]) * d1x + (h[1] - g[1]) * d1y) / len2, [h[0], h[1]]);
                  addEv(a2, ((h[2] - g[0]) * d1x + (h[3] - g[1]) * d1y) / len2, [h[2], h[3]]);
                  var lenB = d2x * d2x + d2y * d2y;
                  addEv(b3, ((g[0] - h[0]) * d2x + (g[1] - h[1]) * d2y) / lenB, [g[0], g[1]]);
                  addEv(b3, ((g[2] - h[0]) * d2x + (g[3] - h[1]) * d2y) / lenB, [g[2], g[3]]);
                }
              }
            }
          }
        }
      }

      /* classify sub-segments; keep boundary edges directed interior-on-LEFT */
      var kept = [], keyOf = function (x, y) { return Math.round(x / QK) + ',' + Math.round(y / QK); };
      var dedupe = {};
      for (i = 0; i < S; i++) {
        var sg2 = segs[i];
        var evs = splitEv[i];
        evs.push([0, [sg2[0], sg2[1]]], [1, [sg2[2], sg2[3]]]);
        evs.sort(function (a3, b4) { return a3[0] - b4[0]; });
        for (var q = 0; q < evs.length - 1; q++) {
          var P0 = evs[q][1], P1 = evs[q + 1][1];
          if (evs[q + 1][0] - evs[q][0] < 1e-12) continue;
          var ax = P0[0], ay = P0[1];
          var bx2 = P1[0], by2 = P1[1];
          var mx = (ax + bx2) / 2, my = (ay + by2) / 2;
          var dx = bx2 - ax, dy = by2 - ay;
          var len3 = Math.sqrt(dx * dx + dy * dy);
          if (len3 < 1e-9) continue;
          var off = Math.min(5e-4, len3 * 0.25);
          var nx = -dy / len3 * off, ny = dx / len3 * off;
          var inL = desired(mx + nx, my + ny);
          var inR = desired(mx - nx, my - ny);
          if (inL === inR) continue;
          var eSeg = inL ? [ax, ay, bx2, by2] : [bx2, by2, ax, ay];
          var k0 = keyOf(eSeg[0], eSeg[1]), k1 = keyOf(eSeg[2], eSeg[3]);
          if (k0 === k1) continue;   /* zero-length in stitch-key space */
          var dk = k0 + '|' + k1;
          if (dedupe[dk]) continue;
          dedupe[dk] = 1;
          kept.push(eSeg);
        }
      }
      if (!kept.length) return { fellBack: true, reason: 'no boundary edges kept' };

      /* stitch into loops — leftmost turn at junctions */
      var outMap = {};
      for (i = 0; i < kept.length; i++) {
        var sk = keyOf(kept[i][0], kept[i][1]);
        (outMap[sk] = outMap[sk] || []).push(i);
      }
      var used = new Array(kept.length);
      var loops = [];
      for (i = 0; i < kept.length; i++) {
        if (used[i]) continue;
        var loop = [[kept[i][0], kept[i][1]]];
        var startKey = keyOf(kept[i][0], kept[i][1]);
        var cur = i, steps = 0, closedLoop = false;
        while (steps++ <= kept.length) {
          used[cur] = 1;
          var e3 = kept[cur];
          loop.push([e3[2], e3[3]]);
          var ek = keyOf(e3[2], e3[3]);
          if (ek === startKey) { closedLoop = true; break; }
          var cands = outMap[ek], next = -1;
          if (cands) {
            var din = [e3[2] - e3[0], e3[3] - e3[1]];
            var bestAng = -Infinity;
            for (var c2 = 0; c2 < cands.length; c2++) {
              if (used[cands[c2]]) continue;
              var ce = kept[cands[c2]];
              var cd = [ce[2] - ce[0], ce[3] - ce[1]];
              var ang = Math.atan2(din[0] * cd[1] - din[1] * cd[0], din[0] * cd[0] + din[1] * cd[1]);
              if (ang > Math.PI - 1e-9) ang = -Math.PI; /* pure reversal = last resort */
              if (ang > bestAng) { bestAng = ang; next = cands[c2]; }
            }
          }
          if (next < 0) break;
          cur = next;
        }
        if (!closedLoop) return { fellBack: true, reason: 'open boundary chain' };
        /* drop closing dup + degenerates */
        loop.pop();
        var clean = [];
        for (var v2 = 0; v2 < loop.length; v2++) {
          var pv = clean[clean.length - 1];
          if (!pv || keyOf(pv[0], pv[1]) !== keyOf(loop[v2][0], loop[v2][1])) clean.push(loop[v2]);
        }
        if (clean.length >= 3 && absArea(clean) > 1e-6) loops.push(clean);
      }
      if (!loops.length) return { fellBack: true, reason: 'no loops produced' };

      /* self-check: parity of the rebuilt loops vs the desired region */
      var loopTesters = loops.map(function (lp) { return makePipTester(lp); });
      var loopParity = function (x, y) {
        var par = false;
        for (var k4 = 0; k4 < loops.length; k4++) { if (loopTesters[k4](x, y)) par = !par; }
        return par;
      };
      var NS = 48, bad = 0;
      for (var gy = 0; gy < NS && bad <= 3; gy++) {
        for (var gx = 0; gx < NS && bad <= 3; gx++) {
          var sx = cb[0] + (cb[2] - cb[0]) * ((gx + 0.5 + 0.318 * ((gy * 7) % 3 - 1) / 3) / NS);
          var sy = cb[1] + (cb[3] - cb[1]) * ((gy + 0.5 + 0.318 * ((gx * 5) % 3 - 1) / 3) / NS);
          if (desired(sx, sy) !== loopParity(sx, sy)) {
            /* confirm off-boundary: all 5 probes must agree it's wrong */
            var conf = 0, OFF = 0.02;
            var probes = [[sx + OFF, sy], [sx - OFF, sy], [sx, sy + OFF], [sx, sy - OFF]];
            for (var pb = 0; pb < 4; pb++) {
              if (desired(probes[pb][0], probes[pb][1]) !== loopParity(probes[pb][0], probes[pb][1])) conf++;
            }
            if (conf === 4) bad++;
          }
        }
      }
      if (bad > 3) return { fellBack: true, reason: 'self-check parity mismatch' };

      /* finite check + closing dup for the emitter */
      for (i = 0; i < loops.length; i++) {
        for (var v3 = 0; v3 < loops[i].length; v3++) {
          if (!isFinite(loops[i][v3][0]) || !isFinite(loops[i][v3][1])) return { fellBack: true, reason: 'non-finite union vertex' };
        }
        loops[i].push([loops[i][0][0], loops[i][0][1]]);
      }
      for (i = 0; i < memRings.length; i++) replacedRings[ringPoly[memRings[i]]] = tag;
      clusterLoops[tag] = loops;
      clusterFirstRing[tag] = ringPoly[firstRing];
    }

    /* -- assemble: passthrough SAME array refs; splice loops at first member -- */
    var out = [], replaced = 0, emitted = 0, tag2;
    for (i = 0; i < polylines.length; i++) {
      tag2 = replacedRings[i];
      if (tag2 === undefined) { out.push(polylines[i]); continue; }
      replaced++;
      if (clusterFirstRing[tag2] === i) {
        var ls = clusterLoops[tag2];
        for (var l2 = 0; l2 < ls.length; l2++) { out.push(ls[l2]); emitted++; }
      }
    }
    if (!replaced || !emitted) return { fellBack: true, reason: 'assembly produced nothing' };
    return { out: out, replaced: replaced, emitted: emitted };
  }

  /** Pure-computation DXF builder — takes pre-extracted path d-strings.
   *  Worker-safe: no DOM access required. */
  function _buildDxfFromPaths(pathDStrings, physDims) {
    if (!physDims?.wInches || !physDims?.hInches || !pathDStrings?.length) return null;
    /* v2.9.30: clear stale union telemetry before any early throw */
    try { (typeof self !== 'undefined' ? self : window).__dtDxfUnionMeta = null; } catch (_) {}

    /* Use the known source PNG pixel dimensions (300 DPI * physical inches)
       as the coordinate space instead of trusting the vectorizer's viewBox,
       which may be tight-cropped or padded, causing incorrect sizing. */
    const svgMinX = 0, svgMinY = 0;
    const svgW = Math.round(physDims.wInches * 300);
    const svgH = Math.round(physDims.hInches * 300);

    /* Output in millimeters — production RIP software reads DXF coords as mm */
    const outW = physDims.wInches * 25.4;
    const outH = physDims.hInches * 25.4;
    /* Parse all paths into raw SVG-coordinate polylines.
       Uses pre-extracted pathDStrings so <g> transforms, <circle>, <rect>,
       <polygon>, etc. are already included via flattenSvgPaths(). */
    const rawPolys = [];
    pathDStrings.forEach(d => {
      if (!d) return;
      const polys = parseSvgPathToPolylines(d, svgH + svgMinY);
      polys.forEach(pts => { if (pts.length >= 2) rawPolys.push(pts); });
    });

    if (!rawPolys.length) return null;

    /* v2.9.23 GUARD (SO-332060): non-finite coordinates from upstream
       (NaN baked into a flattened d-string) previously wrote literal
       "nan" vertices into the DXF — Illustrator refuses the whole file
       (error 2067) and tolerant consumers silently drop the affected
       polylines, which reads downstream as missing artwork. A corrupt
       file must NEVER ship: refuse instead, so the caller's existing
       engrave_dxf_failed / engrave_png_only warnings fire.
       Worker-safe: no closure references. */
    let _nonFinite = 0;
    rawPolys.forEach(pts => pts.forEach(p => {
      if (!Number.isFinite(p[0]) || !Number.isFinite(p[1])) _nonFinite++;
    }));
    if (_nonFinite) throw new Error('DXF refused: ' + _nonFinite + ' non-finite vertex coordinate(s) — corrupt upstream geometry (SO-332060 class)');

    /* Normalize SVG coords to 0→1 range, then scale to mm + clamp */
    let polylines = rawPolys.map(pts => pts.map(([x, y]) => {
      const normX = (x - svgMinX) / svgW;
      const normY = (y - svgMinY) / svgH;
      return [
        Math.max(0, Math.min(outW, normX * outW)),
        Math.max(0, Math.min(outH, normY * outH)),
      ];
    }));

    /* v2.9.23 WINDING NORMALISATION (SO-332060 handoff §4): a DXF POLYLINE
       carries no fill rule, so the consumer infers nesting from geometry.
       Contours arrive with mixed orientation (tracer and font outliner
       disagree), and on SO-332060 10 of 261 nested contours wound WITH
       their parent — an even-odd consumer rendered them correctly but a
       nonzero consumer filled those holes solid. Normalise so both agree:
       nesting depth even (solid) → CCW, odd (hole) → CW. Depth is computed
       from ring copies BEFORE any reversal (reversal changes neither
       containment nor area magnitude — the pts[0]-resampling trap in the
       first Python attempt cannot occur), with a multi-vertex majority
       vote so a sample landing on a shared vertex cannot misclassify.
       Worker-safe: no closure references. */
    const _closedAt = polylines.map(pts => pts.length > 3 &&
      Math.abs(pts[0][0] - pts[pts.length-1][0]) < 0.05 &&
      Math.abs(pts[0][1] - pts[pts.length-1][1]) < 0.05);
    const _rings = polylines.map((pts, k) => _closedAt[k] ? pts.slice(0, -1) : null);
    const _bboxes = _rings.map(r => {
      if (!r) return null;
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (const p of r) { if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0]; if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1]; }
      return [x0, y0, x1, y1];
    });
    const _area2 = r => { let a = 0; for (let k = 0, m = r.length; k < m; k++) { const p1 = r[k], p2 = r[(k + 1) % m]; a += p1[0] * p2[1] - p2[0] * p1[1]; } return a; };
    const _pip = (x, y, r) => { /* even-odd ray cast */
      let inside = false;
      for (let k = 0, m = r.length, j = m - 1; k < m; j = k++) {
        const yi = r[k][1], yj = r[j][1];
        if ((yi > y) !== (yj > y) && x < (r[j][0] - r[k][0]) * (y - yi) / (yj - yi) + r[k][0]) inside = !inside;
      }
      return inside;
    };
    for (let a = 0; a < polylines.length; a++) {
      if (!_rings[a]) continue;
      let depth = 0;
      for (let b = 0; b < polylines.length; b++) {
        if (b === a || !_rings[b]) continue;
        const A = _bboxes[a], B = _bboxes[b];
        if (A[0] < B[0] || A[1] < B[1] || A[2] > B[2] || A[3] > B[3]) continue; /* bbox not inside — cannot be nested */
        const r = _rings[a], nS = Math.min(5, r.length);
        let inC = 0;
        for (let s = 0; s < nS; s++) { const v = r[Math.floor(s * r.length / nS)]; if (_pip(v[0], v[1], _rings[b])) inC++; }
        if (inC * 2 > nS) depth++;
      }
      const wantCcw = depth % 2 === 0; /* even depth = solid, odd = hole */
      if ((_area2(_rings[a]) > 0) !== wantCcw) polylines[a].reverse();
    }

    /* v2.9.30 EZCAD OVERLAP UNION (FINDING-ezcad-overlap-parity): merge
       cross-object overlaps so EZCAD2's even-odd autoshade cannot invert
       name text that crosses clipart. Gate closed (null) means polylines is
       untouched and the emitted bytes are identical to v2.9.29. Any doubt
       inside the rebuild means fellBack, ship today's geometry unchanged.
       Telemetry goes through the __dtDxfUnionMeta global because gwarn is
       not defined inside the worker bundle; the main thread reports it
       (_dtReportDxfUnionMeta). Worker-safe: no closure references. */
    var _unionMeta = null;
    try {
      var _u = _dtEngraveOverlapUnion(polylines);
      if (_u && _u.out) { polylines = _u.out; _unionMeta = { replaced: _u.replaced, emitted: _u.emitted }; }
      else if (_u && _u.fellBack) _unionMeta = { fellBack: true, reason: _u.reason || '' };
    } catch (e) { _unionMeta = { fellBack: true, reason: String((e && e.message) || e) }; }
    try { (typeof self !== 'undefined' ? self : window).__dtDxfUnionMeta = _unionMeta; } catch (_) {}

    /* Build AC1006 DXF — no $INSUNITS, matches working production file format */
    let dxf = '';
    const w = (code, val) => { dxf += `  ${code}\n${val}\n`; };

    w(0, 'SECTION'); w(2, 'HEADER');
    w(9, '$ACADVER'); w(1, 'AC1006');
    w(9, '$EXTMIN'); w(10, '0.000000'); w(20, '0.000000'); w(30, '0.000000');
    w(9, '$EXTMAX'); w(10, outW.toFixed(6)); w(20, outH.toFixed(6)); w(30, '0.000000');
    w(0, 'ENDSEC');

    w(0, 'SECTION'); w(2, 'ENTITIES');
    polylines.forEach(pts => {
      const closed = pts.length > 2 &&
        Math.abs(pts[0][0] - pts[pts.length-1][0]) < 0.05 &&  /* ~0.05mm tolerance */
        Math.abs(pts[0][1] - pts[pts.length-1][1]) < 0.05;
      w(0, 'POLYLINE'); w(8, '0'); w(66, '1'); w(70, closed ? '1' : '0');
      const vtxList = closed ? pts.slice(0, -1) : pts;
      vtxList.forEach(([x, y]) => {
        w(0, 'VERTEX'); w(8, '0'); w(10, x.toFixed(6)); w(20, y.toFixed(6));
      });
      w(0, 'SEQEND');
    });
    w(0, 'ENDSEC');
    w(0, 'EOF');
    return dxf;
  }

  /**
   * Build a DXF directly from SVG layer vector paths — no raster roundtrip.
   * Each layer's SVG paths are transformed (position, scale, rotation, flip)
   * into the print area coordinate system, then mapped to physical inches.
   * Produces mathematically precise output for vector-origin layers.
   */
  /* svgToEps — removed: superseded by geoWorkerConvert('eps', ...) which
     handles the flattenSvgPaths normalization (plain strings vs {d,fill}
     objects) and offloads to a worker. Direct callers should use
     geoWorkerConvert instead. */

  /** Pure-computation EPS builder — takes pre-extracted path d-strings.
   *  Worker-safe: no DOM access required.
   *  @param {string[]} pathDStrings  SVG path d-attributes
   *  @param {object}   physDims      {wInches, hInches}
   *  @param {string[]|null} pathFills  Optional per-path SVG fill colors (hex/named).
   *                                    null → all paths rendered in CMYK black.  */
  function _buildEpsFromPaths(pathDStrings, physDims, pathFills, aiCompat, pathStrokes, pathStrokeWidths) {
    if (!physDims?.wInches || !physDims?.hInches || !pathDStrings?.length) return null;

    /* Use the known source PNG pixel dimensions (300 DPI * physical inches)
       as the coordinate space instead of trusting the vectorizer's viewBox,
       which may be tight-cropped or padded, causing incorrect sizing. */
    const svgMinX = 0, svgMinY = 0;
    const svgW = Math.round(physDims.wInches * 300);
    const svgH = Math.round(physDims.hInches * 300);

    /* Output units — v2.9.35 (Brandon, Jul 29 2026): PostScript POINTS
       (72/inch) for BOTH dialects. The engrave EPS is opened in CorelDRAW,
       which is point-native and honors the PostScript unit; the laser
       department drives EZCAD2 from the DXF (millimeters), which has always
       opened at true size.

       HISTORY — v2.9.7 (order CB36475) set the AI8 dialect to 25.4 units/inch
       because EZCAD2 is millimeter-native: it reads the AI8 operator stream's
       coordinates AS MILLIMETERS and sizes to the geometry extents (ignoring
       the artboard), so a points-scaled file opened 72/25.4 = 2.8346x
       oversized. That fixed EZCAD2 and broke Corel by the same factor in the
       other direction — art intended at 2.025in was written as 51.435 units
       and Corel read it as 51.435pt = 0.714in, i.e. 35.28% of true size, on
       every engrave order. Confirmed Jul 29 against a shipped pair: the EPS
       coordinate stream carried the IDENTICAL numbers as the known-good DXF
       (42.330 vs 42.327), so the EPS units were unambiguously millimeters.

       One coordinate stream cannot satisfy both readers — a millimeter-native
       consumer and a point-native consumer disagree by 2.8346x on the same
       numbers, and there is only one scale knob. Corel is the consumer, so
       points win and EZCAD2 keeps the DXF. DO NOT reintroduce the mm branch
       here: if EZCAD2 ever needs the EPS again, emit a SECOND, separately
       named mm file rather than rescaling this one. */
    const unitsPerInch = 72;
    const ptW = physDims.wInches * unitsPerInch;
    const ptH = physDims.hInches * unitsPerInch;
    /* v2.9.23 GUARD (SO-332060): count non-finite coordinates as they are
       mapped — NaN.toFixed() writes a literal "NaN" operand into the
       PostScript stream. Checked after the operator build; if any were
       seen the whole file is refused (see the DXF builder's guard).
       Worker-safe: no closure references. */
    let _nonFinite = 0;
    const toPsX = x => { const v = ((x - svgMinX) / svgW) * ptW; if (!Number.isFinite(v)) _nonFinite++; return v.toFixed(4); };
    const toPsY = y => { const v = ptH - ((y - svgMinY) / svgH) * ptH; if (!Number.isFinite(v)) _nonFinite++; return v.toFixed(4); };
    /* Build AI-shorthand path commands from SVG paths */
    const aiPaths = [];
    const aiFills = [];   /* parallel array — resolved fill per path entry */
    const aiStrokes = []; /* parallel array — stroke color (generic EPS only) */
    const aiStrokeWs = []; /* parallel array — stroke width in source (300dpi px) units */
    pathDStrings.forEach((d, idx) => {
      if (!d) return;
      const ops = svgPathToAIOperators(d, toPsX, toPsY);
      if (ops) {
        aiPaths.push(ops);
        aiFills.push(pathFills ? pathFills[idx] : null);
        aiStrokes.push(pathStrokes ? pathStrokes[idx] : null);
        aiStrokeWs.push(pathStrokeWidths ? pathStrokeWidths[idx] : 0);
      }
    });
    if (!aiPaths.length) return null;
    if (_nonFinite) throw new Error('EPS refused: ' + _nonFinite + ' non-finite coordinate(s) — corrupt upstream geometry (SO-332060 class)');

    /* ── Color helpers (worker-safe: no DOM) ────────────────────────── */

    /** Parse a CSS/SVG hex color (#RGB, #RRGGBB) to [r,g,b] 0-255.
     *  Returns null for values it can't parse (named colors etc). */
    function _hexToRgb(hex) {
      if (!hex || hex.charAt(0) !== '#') return null;
      let h = hex.slice(1);
      if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
      if (h.length !== 6) return null;
      const n = parseInt(h, 16);
      if (isNaN(n)) return null;
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }

    /** Parse an rgb()/rgba() CSS function to [r,g,b] 0-255. */
    function _rgbFuncToRgb(s) {
      var m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
      if (!m) return null;
      return [parseInt(m[1],10), parseInt(m[2],10), parseInt(m[3],10)];
    }

    /** Naive RGB→CMYK (UCR model). Returns 'C M Y K setcmykcolor' string. */
    function _rgbToCmykStr(r, g, b) {
      var c = 1 - r / 255, m2 = 1 - g / 255, y = 1 - b / 255;
      var k = Math.min(c, m2, y);
      if (k >= 1) return '0 0 0 1 setcmykcolor';
      c = (c - k) / (1 - k); m2 = (m2 - k) / (1 - k); y = (y - k) / (1 - k);
      return c.toFixed(4) + ' ' + m2.toFixed(4) + ' ' + y.toFixed(4) + ' ' + k.toFixed(4) + ' setcmykcolor';
    }

    /** Convert an SVG fill value to a 'setcmykcolor' string.
     *  Falls back to CMYK black for values it can't resolve. */
    function fillToPS(fill) {
      if (!fill || fill === 'none') return '0 0 0 1 setcmykcolor';
      if (fill === 'white' || fill === '#fff' || fill === '#ffffff') return '0 0 0 0 setcmykcolor';
      if (fill === 'black' || fill === '#000' || fill === '#000000') return '0 0 0 1 setcmykcolor';
      var rgb = _hexToRgb(fill) || _rgbFuncToRgb(fill);
      if (rgb) return _rgbToCmykStr(rgb[0], rgb[1], rgb[2]);
      return '0 0 0 1 setcmykcolor'; /* unresolvable → black */
    }

    /* ── Assemble a plain, strictly-conformant PostScript EPS ────────── */
    /* IMPORTANT: do NOT claim "%%Creator: Adobe Illustrator" — Illustrator
       routes AI-branded EPS through its native importer, which requires the
       embedded AI private-data stream. Without it, Illustrator reports the
       file as "too damaged to recover" even though the PostScript is valid
       (RIPs and Ghostscript open it fine). Generic-creator EPS with plain
       operators — the format our known-good vectorizer EPS used — imports
       cleanly via Illustrator's generic PostScript path. */
    const bbx1 = '0', bby1 = '0', bbx2 = Math.ceil(ptW), bby2 = Math.ceil(ptH);

    if (aiCompat) {
      /* ── EZCAD2-compatible Adobe Illustrator 8 EPS (ENGRAVE ONLY) ──
         The engraving department's EZCAD2 parses the Illustrator operator
         stream (m/L/C, f fills, k colors, Lb/Ln layer wrap) and cannot
         read generic PostScript — the plain build below fails to load
         ("failure to read DXF or DWG file entity information", Jul 17).
         Structure mirrors a known-good AI 8.0 production EPS. NOTE:
         Illustrator itself may call AI-branded EPS without private AI
         data damaged (CB36100) — this variant is for the ENGRAVER;
         print EPS keeps the generic format below. */
      /* v2.11.3 — ENGRAVE IS 1-BIT: every path in this dialect paints
         0 0 0 1 k, unconditionally (CB-20260802-5T8T shipped the State
         Outline template's #F47E46 heart as a live orange CMYK object
         while the header declared DocumentProcessColors: Black).
         forceBlack only ever reached outlined TEXT; vector layers kept
         their authored fill all the way to here. White/near-white paths
         were already dropped upstream (v2.10.1/.3 paper rules), so the
         only fills this changes are colored clipart — which the laser
         reads as ink regardless. The per-path fillToK/hasK color
         machinery this replaces is deleted, not bypassed. */
      /* AI dialect: a path object is ONE m + segments + paint op. Bare
         closepath doesn't exist — each subpath close becomes an explicit
         line back to that subpath's start. Multi-contour fills (letter
         counters/holes) are written the way Illustrator writes them and
         the way the known-good EZCAD2 file is structured: a compound
         path — *u, then each contour as its own `m …segments… f`, then
         *U. Single-contour paths are a plain `m … f`. */
      const aiBody = function (ops) {
        var subs = [], cur = null, startPt = null, lastPt = null;
        var closeCur = function () {
          if (!cur) return;
          if (startPt && lastPt && (startPt[0] !== lastPt[0] || startPt[1] !== lastPt[1])) {
            cur.push(startPt[0] + ' ' + startPt[1] + ' L');
          }
          subs.push(cur); cur = null;
        };
        ops.split('\n').forEach(function (ln) {
          ln = ln.trim();
          if (!ln) return;
          if (/ m$/.test(ln)) {
            closeCur();
            var pm = ln.split(' '); startPt = [pm[0], pm[1]]; lastPt = startPt;
            cur = [ln];
          }
          else if (/ L$/.test(ln)) { var pl = ln.split(' '); lastPt = [pl[0], pl[1]]; if (cur) cur.push(ln); }
          else if (/ C$/.test(ln)) { var pc = ln.split(' '); lastPt = [pc[4], pc[5]]; if (cur) cur.push(ln); }
          else if (ln === 'closepath') {
            if (cur && startPt && lastPt && (startPt[0] !== lastPt[0] || startPt[1] !== lastPt[1])) {
              cur.push(startPt[0] + ' ' + startPt[1] + ' L');
            }
            lastPt = startPt;
          }
        });
        closeCur();
        if (!subs.length) return '';
        var body = subs.map(function (sp) { return sp.join('\n') + '\nf\n'; }).join('');
        return subs.length > 1 ? '*u\n' + body + '*U\n' : body;
      };
      let ai = '%!PS-Adobe-3.0 EPSF-3.0\n';
      ai += '%%Creator: Adobe Illustrator(R) 8.0\n';
      ai += '%%AI8_CreatorVersion: 8.0\n';
      ai += '%%For: (Custom Branding) ()\n';
      ai += '%%Title: (engrave-production.eps)\n';
      ai += '%%CreationDate: ' + new Date().toISOString() + '\n';
      ai += '%%BoundingBox: 0 0 ' + bbx2 + ' ' + bby2 + '\n';
      ai += '%%HiResBoundingBox: 0 0 ' + ptW.toFixed(4) + ' ' + ptH.toFixed(4) + '\n';
      ai += '%%DocumentProcessColors: Black\n';
      ai += '%AI5_FileFormat 4.0\n';
      ai += '%AI3_ColorUsage: Color\n';
      ai += '%AI5_ArtSize: ' + Math.round(ptW) + ' ' + Math.round(ptH) + '\n';
      ai += '%AI5_TargetResolution: 800\n';
      ai += '%AI5_NumLayers: 1\n';
      ai += '%%EndComments\n%%BeginProlog\n%%EndProlog\n%%BeginSetup\n%%EndSetup\n';
      ai += '%AI5_BeginLayer\n';
      ai += '1 1 1 1 0 0 1 0 79 128 255 0 50 Lb\n(Layer 1) Ln\n';
      ai += '0 A\n0 O\n0 J 0 j 1 w 10 M []0 d\n0 XR\n';
      ai += '0 0 0 1 k\n'; /* v2.11.3: single forced black — see note above */
      aiPaths.forEach((ops) => {
        ai += aiBody(ops); /* emits its own f per contour (+ *u/*U wrap) */
      });
      ai += 'LB\n%AI5_EndLayer--\n%%PageTrailer\n%%Trailer\n%%EOF\n';
      /* CRLF endings: EZCAD2 is legacy Windows software and the known-good
         AI8 EPS it loads uses CRLF; harmless for every other consumer. */
      return ai.replace(/\n/g, '\r\n');
    }

    /* Minimal DSC header, mirroring the known-good vectorizer EPS exactly.
       Do NOT add %%Pages / %%Page / %%DocumentData — declaring a paged
       document makes Illustrator ignore the BoundingBox and open the file
       on its default (A4/Letter) artboard with the art centered. */
    let eps = '%!PS-Adobe-3.0 EPSF-3.0\n';
    eps += '%%CreationDate: ' + new Date().toISOString() + '\n';
    eps += '%%LanguageLevel: 3\n';
    eps += '%%BoundingBox: ' + bbx1 + ' ' + bby1 + ' ' + bbx2 + ' ' + bby2 + '\n';
    eps += '%%HiResBoundingBox: 0 0 ' + ptW.toFixed(4) + ' ' + ptH.toFixed(4) + '\n';
    eps += '%%EndComments\n';

    /* NOTE (Jul 2026, order CB36100): Illustrator ignores the %%BoundingBox
       of generic (non-AI) EPS and opens the file on its default A4 artboard
       with the art centered — a documented AI bug. Tested and rejected:
       %AI3_Cropmarks, %%Pages/%%Page/%%PageBoundingBox, guarded
       setpagedevice, and white corner-anchor paths (AI imported them as
       stray layers). None move AI's artboard. RIPs and Ghostscript honor
       the BoundingBox correctly, so this file stays strictly conformant;
       production opens the PDF or SVG in Illustrator instead — both carry
       the exact print-area artboard. */

    /* Clip all artwork to the artboard. The customer-approved preview and
       every raster production file crop at the print area, so the vector
       EPS must match. Illustrator imports this as a clipping mask; RIPs
       simply never paint outside the rectangle. */
    eps += 'newpath 0 0 moveto ' + ptW.toFixed(4) + ' 0 lineto '
         + ptW.toFixed(4) + ' ' + ptH.toFixed(4) + ' lineto 0 '
         + ptH.toFixed(4) + ' lineto closepath clip newpath\n';

    /* Draw all paths with plain PostScript operators (matches the
       known-good vectorizer EPS format that Illustrator opens cleanly).
       When pathFills is provided, set per-path color from the vectorizer
       SVG; otherwise fall back to CMYK black (engrave / legacy). */
    const hasColors = aiFills.some(f => f != null);
    if (!hasColors) eps += '0 0 0 1 setcmykcolor\n';
    let prevColorStr = hasColors ? '' : '0 0 0 1 setcmykcolor';
    aiPaths.forEach((ops, idx) => {
      const psOps = ops
        .replace(/ m\n/g, ' moveto\n')
        .replace(/ L\n/g, ' lineto\n')
        .replace(/ C\n/g, ' curveto\n');
      const fillVal = aiFills[idx];
      const strokeVal = aiStrokes[idx];
      const fillNone = String(fillVal || '').toLowerCase() === 'none';
      const hasStroke = strokeVal && String(strokeVal).toLowerCase() !== 'none';
      /* Fill pass (skipped for fill="none" — previously such paths were
         either dropped upstream or, worse, filled black). Fills implicitly
         close, so the legacy closepath stays. */
      if (!fillNone) {
        if (hasColors) {
          const cs = fillToPS(fillVal);
          if (cs !== prevColorStr) { eps += cs + '\n'; prevColorStr = cs; }
        }
        eps += 'newpath\n' + psOps + 'closepath\nfill\n';
      }
      /* Stroke pass — native PostScript stroke in the STROKE color (the
         Jul 22 order's orange cameo ring). Width arrives in 300dpi source
         px → convert to points. NO forced closepath: open strokes (waves,
         guides) must not gain a chord; source Z's are already in psOps. */
      if (hasStroke) {
        const sColor = fillToPS(strokeVal);
        const swPt = ((aiStrokeWs[idx] || 1) * 72 / 300).toFixed(4);
        eps += sColor + '\n' + swPt + ' setlinewidth\n1 setlinejoin\n1 setlinecap\n';
        eps += 'newpath\n' + psOps + 'stroke\n';
        prevColorStr = sColor;
      }
    });

    eps += '%%EOF\n';
    return eps;
  }

  /** Convert SVG path d-attribute to Adobe Illustrator shorthand operators. */
  function svgPathToAIOperators(d, px, py) {
    const tokens = d.match(/[a-zA-Z]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || [];
    let i = 0, ops = '', cx = 0, cy = 0, startX = 0, startY = 0, has = false;
    /* Track last control points for smooth continuation (S/s/T/t) */
    let lastCp2x = 0, lastCp2y = 0; /* last cubic 2nd ctrl pt */
    let lastQpx = 0, lastQpy = 0;   /* last quadratic ctrl pt */
    let prevCmd = '';
    const n = () => parseFloat(tokens[i++]);
    const isN = () => i < tokens.length && /[0-9.+-]/.test(tokens[i]);
    /* v2.9.23: peel fused SVG arc flags per spec — see _svgDToJsPDFOps */
    const nf = () => {
      const t = tokens[i];
      if (t === '0' || t === '1') { i++; return +t; }
      const c = t ? t.charAt(0) : '';
      if (c === '0' || c === '1') { tokens[i] = t.slice(1); return +c; }
      i++; return NaN;
    };

    while (i < tokens.length) {
      const cmd = tokens[i];
      if (/[a-zA-Z]/.test(cmd)) i++; else break;
      switch (cmd) {
        case 'M': cx=n();cy=n();startX=cx;startY=cy;
          ops+=px(cx)+' '+py(cy)+' m\n'; has=true;
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy;
          while(isN()){cx=n();cy=n();ops+=px(cx)+' '+py(cy)+' L\n';} break;
        case 'm': cx+=n();cy+=n();startX=cx;startY=cy;
          ops+=px(cx)+' '+py(cy)+' m\n'; has=true;
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy;
          while(isN()){cx+=n();cy+=n();ops+=px(cx)+' '+py(cy)+' L\n';} break;
        case 'L': while(isN()){cx=n();cy=n();ops+=px(cx)+' '+py(cy)+' L\n';}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'l': while(isN()){cx+=n();cy+=n();ops+=px(cx)+' '+py(cy)+' L\n';}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'H': while(isN()){cx=n();ops+=px(cx)+' '+py(cy)+' L\n';}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'h': while(isN()){cx+=n();ops+=px(cx)+' '+py(cy)+' L\n';}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'V': while(isN()){cy=n();ops+=px(cx)+' '+py(cy)+' L\n';}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'v': while(isN()){cy+=n();ops+=px(cx)+' '+py(cy)+' L\n';}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'C': while(isN()){const x1=n(),y1=n(),x2=n(),y2=n(),x3=n(),y3=n();
          ops+=px(x1)+' '+py(y1)+' '+px(x2)+' '+py(y2)+' '+px(x3)+' '+py(y3)+' C\n';
          lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;} lastQpx=cx;lastQpy=cy; break;
        case 'c': while(isN()){const x1=cx+n(),y1=cy+n(),x2=cx+n(),y2=cy+n(),x3=cx+n(),y3=cy+n();
          ops+=px(x1)+' '+py(y1)+' '+px(x2)+' '+py(y2)+' '+px(x3)+' '+py(y3)+' C\n';
          lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;} lastQpx=cx;lastQpy=cy; break;
        case 'S': case 's': { const rel=cmd==='s';
          while(isN()){
            /* Reflect last cubic cp2 if prev was C/c/S/s, else use current point */
            let rx1,ry1;
            if ('CcSs'.indexOf(prevCmd)>=0){rx1=2*cx-lastCp2x;ry1=2*cy-lastCp2y;}
            else{rx1=cx;ry1=cy;}
            let x2=n(),y2=n(),x3=n(),y3=n();
            if(rel){x2+=cx;y2+=cy;x3+=cx;y3+=cy;}
            ops+=px(rx1)+' '+py(ry1)+' '+px(x2)+' '+py(y2)+' '+px(x3)+' '+py(y3)+' C\n';
            lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;prevCmd=cmd;}
          lastQpx=cx;lastQpy=cy; break;}
        case 'Q': case 'q': { const rel=cmd==='q';
          while(isN()){let qx=n(),qy=n(),ex=n(),ey=n();
            if(rel){qx+=cx;qy+=cy;ex+=cx;ey+=cy;}
            const c1x=cx+2/3*(qx-cx),c1y=cy+2/3*(qy-cy),c2x=ex+2/3*(qx-ex),c2y=ey+2/3*(qy-ey);
            ops+=px(c1x)+' '+py(c1y)+' '+px(c2x)+' '+py(c2y)+' '+px(ex)+' '+py(ey)+' C\n';
            lastQpx=qx;lastQpy=qy;cx=ex;cy=ey;prevCmd=cmd;}
          lastCp2x=cx;lastCp2y=cy; break;}
        case 'T': case 't': { const rel=cmd==='t';
          while(isN()){
            /* Reflect last quadratic cp if prev was Q/q/T/t, else use current point */
            let qx,qy;
            if ('QqTt'.indexOf(prevCmd)>=0){qx=2*cx-lastQpx;qy=2*cy-lastQpy;}
            else{qx=cx;qy=cy;}
            let ex=n(),ey=n();
            if(rel){ex+=cx;ey+=cy;}
            const c1x=cx+2/3*(qx-cx),c1y=cy+2/3*(qy-cy),c2x=ex+2/3*(qx-ex),c2y=ey+2/3*(qy-ey);
            ops+=px(c1x)+' '+py(c1y)+' '+px(c2x)+' '+py(c2y)+' '+px(ex)+' '+py(ey)+' C\n';
            lastQpx=qx;lastQpy=qy;cx=ex;cy=ey;prevCmd=cmd;}
          lastCp2x=cx;lastCp2y=cy; break;}
        case 'A': case 'a': { const rel=cmd==='a';
          while(isN()){const arx=n(),ary=n(),arot=n(),alarge=nf(),asweep=nf();
            let aex=n(),aey=n(); if(rel){aex+=cx;aey+=cy;}
            const beziers=svgArcToBeziers(cx,cy,arx,ary,arot,alarge,asweep,aex,aey);
            for(const b of beziers){ops+=px(b[0])+' '+py(b[1])+' '+px(b[2])+' '+py(b[3])+' '+px(b[4])+' '+py(b[5])+' C\n';cx=b[4];cy=b[5];}
            if(!beziers.length){ops+=px(aex)+' '+py(aey)+' L\n';cx=aex;cy=aey;}}
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;}
        case 'Z': case 'z': ops+='closepath\n'; cx=startX;cy=startY;
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        default: i++; break;
      }
      prevCmd = cmd;
    }
    return has ? ops : null;
  }

  /**
   * Parse an SVG path d-attribute into arrays of [x,y] polyline points.
   * Bezier curves are subdivided into line segments for RIP compatibility.
   * Y is flipped (SVG y-down → DXF y-up).
   */

  /** Convert SVG arc (A/a) to cubic bezier segments.
   *  Returns array of [cp1x,cp1y, cp2x,cp2y, ex,ey] per segment. */
  function svgArcToBeziers(px, py, rx, ry, xRot, largeArc, sweep, ex, ey) {
    if (rx === 0 || ry === 0) return [];
    rx = Math.abs(rx); ry = Math.abs(ry);
    const phi = xRot * Math.PI / 180;
    const cosPhi = Math.cos(phi), sinPhi = Math.sin(phi);
    const dx2 = (px - ex) / 2, dy2 = (py - ey) / 2;
    const x1p = cosPhi * dx2 + sinPhi * dy2;
    const y1p = -sinPhi * dx2 + cosPhi * dy2;
    let rxSq = rx * rx, rySq = ry * ry;
    const x1pSq = x1p * x1p, y1pSq = y1p * y1p;
    const lambda = x1pSq / rxSq + y1pSq / rySq;
    if (lambda > 1) { const s = Math.sqrt(lambda); rx *= s; ry *= s; rxSq = rx*rx; rySq = ry*ry; }
    let sq = Math.max(0, (rxSq*rySq - rxSq*y1pSq - rySq*x1pSq) / (rxSq*y1pSq + rySq*x1pSq));
    sq = Math.sqrt(sq) * (largeArc === sweep ? -1 : 1);
    const cxp = sq * rx * y1p / ry, cyp = sq * -ry * x1p / rx;
    const cx2 = cosPhi*cxp - sinPhi*cyp + (px+ex)/2;
    const cy2 = sinPhi*cxp + cosPhi*cyp + (py+ey)/2;
    const vAng = (ux,uy,vx,vy) => {
      const d = ux*vx+uy*vy, len = Math.sqrt((ux*ux+uy*uy)*(vx*vx+vy*vy));
      let a = Math.acos(Math.max(-1, Math.min(1, d/len)));
      if (ux*vy - uy*vx < 0) a = -a; return a;
    };
    const theta1 = vAng(1,0,(x1p-cxp)/rx,(y1p-cyp)/ry);
    let dtheta = vAng((x1p-cxp)/rx,(y1p-cyp)/ry,(-x1p-cxp)/rx,(-y1p-cyp)/ry);
    if (!sweep && dtheta > 0) dtheta -= 2*Math.PI;
    if (sweep && dtheta < 0) dtheta += 2*Math.PI;
    const segs = Math.max(1, Math.ceil(Math.abs(dtheta) / (Math.PI/2)));
    const delta = dtheta / segs, t = 4/3 * Math.tan(delta/4);
    const result = [];
    let angle = theta1;
    for (let s = 0; s < segs; s++) {
      const c1 = Math.cos(angle), s1 = Math.sin(angle);
      const c2 = Math.cos(angle+delta), s2 = Math.sin(angle+delta);
      const e1x=c1-t*s1, e1y=s1+t*c1, e2x=c2+t*s2, e2y=s2-t*c2;
      result.push([
        cosPhi*rx*e1x - sinPhi*ry*e1y + cx2, sinPhi*rx*e1x + cosPhi*ry*e1y + cy2,
        cosPhi*rx*e2x - sinPhi*ry*e2y + cx2, sinPhi*rx*e2x + cosPhi*ry*e2y + cy2,
        cosPhi*rx*c2 - sinPhi*ry*s2 + cx2,   sinPhi*rx*c2 + cosPhi*ry*s2 + cy2
      ]);
      angle += delta;
    }
    return result;
  }

  /**
   * Re-emit a path d-string using ONLY absolute M/L/C/Z commands.
   * H/V collapse to L; S/T reflect into C/Q per spec then convert; Q becomes
   * an exact cubic; A runs through svgArcToBeziers (radius correction incl.
   * degenerate arcs → lineto, same as the EPS/DXF pipeline). Motivation:
   * vectorizer output is arc-heavy (order CB36011 carried 1,215 A commands,
   * 17 zero-radius + 99 near-zero + 680 rotated) and Adobe Illustrator's SVG
   * arc importer mishandles those edge cases — one bad conversion filled a
   * giant wedge over the artwork. Browsers and cubics agree everywhere.
   * Validated against CB36011: output renders pixel-identical in-browser.
   */
  function normalizeSvgPathD(d) {
    const R2 = v => Math.round(v * 100) / 100;
    const P = (x, y) => R2(x) + ' ' + R2(y);
    const tokens = d.match(/[a-zA-Z]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || [];
    let i = 0, out = '', cx = 0, cy = 0, sx = 0, sy = 0;
    let lastCp2x = 0, lastCp2y = 0, lastQpx = 0, lastQpy = 0, prevCmd = '';
    const n = () => parseFloat(tokens[i++]);
    const isN = () => i < tokens.length && /[0-9.+-]/.test(tokens[i]);
    /* v2.9.23: peel fused SVG arc flags per spec — see _svgDToJsPDFOps */
    const nf = () => {
      const t = tokens[i];
      if (t === '0' || t === '1') { i++; return +t; }
      const c = t ? t.charAt(0) : '';
      if (c === '0' || c === '1') { tokens[i] = t.slice(1); return +c; }
      i++; return NaN;
    };
    while (i < tokens.length) {
      const cmd = tokens[i]; if (/[a-zA-Z]/.test(cmd)) i++; else break;
      switch (cmd) {
        case 'M': { let ax = n(), ay = n(); cx = ax; cy = ay; sx = ax; sy = ay; out += 'M' + P(ax, ay) + ' ';
          while (isN()) { ax = n(); ay = n(); cx = ax; cy = ay; out += 'L' + P(ax, ay) + ' '; } break; }
        case 'm': { let ax = cx + n(), ay = cy + n(); cx = ax; cy = ay; sx = ax; sy = ay; out += 'M' + P(ax, ay) + ' ';
          while (isN()) { ax = cx + n(); ay = cy + n(); cx = ax; cy = ay; out += 'L' + P(ax, ay) + ' '; } break; }
        case 'L': while (isN()) { cx = n(); cy = n(); out += 'L' + P(cx, cy) + ' '; } break;
        case 'l': while (isN()) { cx += n(); cy += n(); out += 'L' + P(cx, cy) + ' '; } break;
        case 'H': while (isN()) { cx = n(); out += 'L' + P(cx, cy) + ' '; } break;
        case 'h': while (isN()) { cx += n(); out += 'L' + P(cx, cy) + ' '; } break;
        case 'V': while (isN()) { cy = n(); out += 'L' + P(cx, cy) + ' '; } break;
        case 'v': while (isN()) { cy += n(); out += 'L' + P(cx, cy) + ' '; } break;
        case 'C': case 'c': { const rel = cmd === 'c';
          while (isN()) { let x1 = n(), y1 = n(), x2 = n(), y2 = n(), x3 = n(), y3 = n();
            if (rel) { x1 += cx; y1 += cy; x2 += cx; y2 += cy; x3 += cx; y3 += cy; }
            out += 'C' + P(x1, y1) + ' ' + P(x2, y2) + ' ' + P(x3, y3) + ' ';
            lastCp2x = x2; lastCp2y = y2; cx = x3; cy = y3; } break; }
        case 'S': case 's': { const rel = cmd === 's';
          while (isN()) { let x1, y1;
            if ('CcSs'.indexOf(prevCmd) >= 0) { x1 = 2 * cx - lastCp2x; y1 = 2 * cy - lastCp2y; } else { x1 = cx; y1 = cy; }
            let x2 = n(), y2 = n(), x3 = n(), y3 = n();
            if (rel) { x2 += cx; y2 += cy; x3 += cx; y3 += cy; }
            out += 'C' + P(x1, y1) + ' ' + P(x2, y2) + ' ' + P(x3, y3) + ' ';
            lastCp2x = x2; lastCp2y = y2; cx = x3; cy = y3; prevCmd = cmd; } break; }
        case 'Q': case 'q': { const rel = cmd === 'q';
          while (isN()) { let qx = n(), qy = n(), ex = n(), ey = n();
            if (rel) { qx += cx; qy += cy; ex += cx; ey += cy; }
            const c1x = cx + 2 / 3 * (qx - cx), c1y = cy + 2 / 3 * (qy - cy), c2x = ex + 2 / 3 * (qx - ex), c2y = ey + 2 / 3 * (qy - ey);
            out += 'C' + P(c1x, c1y) + ' ' + P(c2x, c2y) + ' ' + P(ex, ey) + ' ';
            lastQpx = qx; lastQpy = qy; cx = ex; cy = ey; prevCmd = cmd; } break; }
        case 'T': case 't': { const rel = cmd === 't';
          while (isN()) { let qx, qy;
            if ('QqTt'.indexOf(prevCmd) >= 0) { qx = 2 * cx - lastQpx; qy = 2 * cy - lastQpy; } else { qx = cx; qy = cy; }
            let ex = n(), ey = n(); if (rel) { ex += cx; ey += cy; }
            const c1x = cx + 2 / 3 * (qx - cx), c1y = cy + 2 / 3 * (qy - cy), c2x = ex + 2 / 3 * (qx - ex), c2y = ey + 2 / 3 * (qy - ey);
            out += 'C' + P(c1x, c1y) + ' ' + P(c2x, c2y) + ' ' + P(ex, ey) + ' ';
            lastQpx = qx; lastQpy = qy; cx = ex; cy = ey; prevCmd = cmd; } break; }
        case 'A': case 'a': { const rel = cmd === 'a';
          while (isN()) { const arx = n(), ary = n(), arot = n(), alarge = nf(), asweep = nf();
            let aex = n(), aey = n(); if (rel) { aex += cx; aey += cy; }
            const bz = svgArcToBeziers(cx, cy, arx, ary, arot, alarge, asweep, aex, aey);
            for (const b of bz) { out += 'C' + P(b[0], b[1]) + ' ' + P(b[2], b[3]) + ' ' + P(b[4], b[5]) + ' '; cx = b[4]; cy = b[5]; }
            if (!bz.length) { out += 'L' + P(aex, aey) + ' '; cx = aex; cy = aey; } }
          lastCp2x = cx; lastCp2y = cy; lastQpx = cx; lastQpy = cy; break; }
        case 'Z': case 'z': out += 'Z '; cx = sx; cy = sy; break;
        default: i++; break;
      }
      prevCmd = cmd;
    }
    return out.trim();
  }

  /** Parse SVG path d-attribute into polyline arrays for DXF. */
  function parseSvgPathToPolylines(d, svgH) {
    const polys = [];
    let current = [];
    let cx = 0, cy = 0, startX = 0, startY = 0;
    /* Track last control points for smooth continuation (S/s/T/t) */
    let lastCp2x = 0, lastCp2y = 0;
    let lastQpx = 0, lastQpy = 0;
    let prevCmd = '';

    /* Tokenize: split into commands + numbers */
    const tokens = d.match(/[a-zA-Z]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || [];
    let i = 0;
    const num = () => parseFloat(tokens[i++]);
    const isN = () => i < tokens.length && /[0-9.+-]/.test(tokens[i]);
    /* v2.9.23: peel fused SVG arc flags per spec — see _svgDToJsPDFOps.
       Worker-safe: no closure references. */
    const numFlag = () => {
      const t = tokens[i];
      if (t === '0' || t === '1') { i++; return +t; }
      const c = t ? t.charAt(0) : '';
      if (c === '0' || c === '1') { tokens[i] = t.slice(1); return +c; }
      i++; return NaN;
    };
    const flipY = svgH ? (y => svgH - y) : (y => y); /* svgH=0 means no flip (caller handles it) */

    while (i < tokens.length) {
      const cmd = tokens[i];
      if (/[a-zA-Z]/.test(cmd)) { i++; } else { /* repeat prev */ }

      switch (cmd) {
        case 'M': cx = num(); cy = num(); startX = cx; startY = cy;
          if (current.length) polys.push(current);
          current = [[cx, flipY(cy)]];
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy;
          while (isN()) { cx = num(); cy = num(); current.push([cx, flipY(cy)]); }
          break;
        case 'm': { const dx = num(), dy = num(); cx += dx; cy += dy; startX = cx; startY = cy;
          if (current.length) polys.push(current);
          current = [[cx, flipY(cy)]];
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy;
          while (isN()) { cx += num(); cy += num(); current.push([cx, flipY(cy)]); }
          break; }
        case 'L': while (isN()) { cx = num(); cy = num(); current.push([cx, flipY(cy)]); }
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'l': while (isN()) { cx += num(); cy += num(); current.push([cx, flipY(cy)]); }
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'H': while (isN()) { cx = num(); current.push([cx, flipY(cy)]); }
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'h': while (isN()) { cx += num(); current.push([cx, flipY(cy)]); }
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'V': while (isN()) { cy = num(); current.push([cx, flipY(cy)]); }
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'v': while (isN()) { cy += num(); current.push([cx, flipY(cy)]); }
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy; break;
        case 'C': while (isN()) {
            const x1=num(),y1=num(),x2=num(),y2=num(),x3=num(),y3=num();
            subdivCubic(current, cx,cy,x1,y1,x2,y2,x3,y3, svgH);
            lastCp2x=x2;lastCp2y=y2;cx=x3; cy=y3; }
          lastQpx=cx;lastQpy=cy; break;
        case 'c': while (isN()) {
            const x1=cx+num(),y1=cy+num(),x2=cx+num(),y2=cy+num(),x3=cx+num(),y3=cy+num();
            subdivCubic(current, cx,cy,x1,y1,x2,y2,x3,y3, svgH);
            lastCp2x=x2;lastCp2y=y2;cx=x3; cy=y3; }
          lastQpx=cx;lastQpy=cy; break;
        case 'S': case 's': { const rel=cmd==='s';
          while (isN()) {
            let rx1,ry1;
            if ('CcSs'.indexOf(prevCmd)>=0){rx1=2*cx-lastCp2x;ry1=2*cy-lastCp2y;}
            else{rx1=cx;ry1=cy;}
            let x2=num(),y2=num(),x3=num(),y3=num();
            if(rel){x2+=cx;y2+=cy;x3+=cx;y3+=cy;}
            subdivCubic(current, cx,cy,rx1,ry1,x2,y2,x3,y3, svgH);
            lastCp2x=x2;lastCp2y=y2;cx=x3;cy=y3;prevCmd=cmd;
          }
          lastQpx=cx;lastQpy=cy; break; }
        case 'Q': while (isN()) {
            const qx1=num(),qy1=num(),qx2=num(),qy2=num();
            subdivQuad(current, cx,cy,qx1,qy1,qx2,qy2, svgH);
            lastQpx=qx1;lastQpy=qy1;cx=qx2; cy=qy2; }
          lastCp2x=cx;lastCp2y=cy; break;
        case 'q': while (isN()) {
            const qx1=cx+num(),qy1=cy+num(),qx2=cx+num(),qy2=cy+num();
            subdivQuad(current, cx,cy,qx1,qy1,qx2,qy2, svgH);
            lastQpx=qx1;lastQpy=qy1;cx=qx2; cy=qy2; }
          lastCp2x=cx;lastCp2y=cy; break;
        case 'T': case 't': { const rel=cmd==='t';
          while (isN()) {
            let qx,qy;
            if ('QqTt'.indexOf(prevCmd)>=0){qx=2*cx-lastQpx;qy=2*cy-lastQpy;}
            else{qx=cx;qy=cy;}
            let ex=num(),ey=num();
            if(rel){ex+=cx;ey+=cy;}
            subdivQuad(current, cx,cy,qx,qy,ex,ey, svgH);
            lastQpx=qx;lastQpy=qy;cx=ex;cy=ey;prevCmd=cmd;
          }
          lastCp2x=cx;lastCp2y=cy; break; }
        case 'A': case 'a': {
            const rel = cmd === 'a';
            while (isN()) {
              const rx=num(),ry=num(),rot=num(),large=numFlag(),sweep=numFlag();
              let ex=num(),ey=num();
              if (rel) { ex += cx; ey += cy; }
              const beziers = svgArcToBeziers(cx, cy, rx, ry, rot, large, sweep, ex, ey);
              for (const b of beziers) {
                subdivCubic(current, cx, cy, b[0], b[1], b[2], b[3], b[4], b[5], svgH || 0);
                cx = b[4]; cy = b[5];
              }
              if (!beziers.length) { current.push([ex, flipY(ey)]); cx = ex; cy = ey; }
            }
            lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy;
          } break;
        case 'Z': case 'z':
          current.push([startX, flipY(startY)]);
          cx = startX; cy = startY;
          lastCp2x=cx;lastCp2y=cy;lastQpx=cx;lastQpy=cy;
          break;
        default:
          i++; break; /* skip unknown */
      }
      prevCmd = cmd;
    }
    if (current.length >= 2) polys.push(current);
    return polys;
  }

  /* Subdivide cubic bezier into ~8 line segments */
  function subdivCubic(pts, x0,y0, x1,y1, x2,y2, x3,y3, svgH) {
    const N = 8;
    for (let s = 1; s <= N; s++) {
      const t = s / N, mt = 1-t;
      const x = mt*mt*mt*x0 + 3*mt*mt*t*x1 + 3*mt*t*t*x2 + t*t*t*x3;
      const y = mt*mt*mt*y0 + 3*mt*mt*t*y1 + 3*mt*t*t*y2 + t*t*t*y3;
      pts.push([x, svgH ? (svgH - y) : y]);
    }
  }

  /* Subdivide quadratic bezier into ~6 line segments */
  function subdivQuad(pts, x0,y0, x1,y1, x2,y2, svgH) {
    const N = 6;
    for (let s = 1; s <= N; s++) {
      const t = s / N, mt = 1-t;
      const x = mt*mt*x0 + 2*mt*t*x1 + t*t*x2;
      const y = mt*mt*y0 + 2*mt*t*y1 + t*t*y2;
      pts.push([x, svgH ? (svgH - y) : y]);
    }
  }

  /* ── Geometry Worker: offload bezier subdivision, coordinate mapping,
       and DXF/EPS string assembly to a background thread. DOM parsing
       (DOMParser + flattenSvgPaths) stays on the main thread because
       workers have no DOM. The worker receives pre-extracted path d-strings
       (plain string arrays — structured-cloneable) and returns the
       finished DXF or EPS output string.
       Falls back to synchronous main-thread execution if:
       - Worker creation fails (CSP blob: restriction, old browser)
       - Worker throws at runtime (missing API, bad path)
       - Worker response times out (30 s) ── */

  /* v2.9.30: engrave DXF overlap-union telemetry. The union runs inside
     _buildDxfFromPaths — usually in the geometry worker, where gwarn does
     not exist — so the builder leaves a meta object on self/window and the
     main thread reports it here. 'engrave_overlap_unioned' = the shipped
     DXF merged overlapping contours (expected on overlap orders, rare
     overall). 'engrave_overlap_union_fallback' = overlap was detected but
     the rebuild failed its parity self-check, so the DXF shipped UN-unioned
     exactly as before v2.9.30 — EZCAD's autoshade may invert the overlap on
     that order and prepress should eyeball it. */
  function _dtReportDxfUnionMeta(m) {
    if (!m) return;
    if (m.fellBack) gwarn('engrave_overlap_union_fallback', 'overlap detected but union self-check failed (' + (m.reason || 'unknown') + ') — DXF shipped un-unioned; EZCAD autoshade may invert the overlap');
    else gwarn('engrave_overlap_unioned', m.replaced + ' overlapping contour(s) merged into ' + m.emitted + ' union loop(s) for EZCAD parity fill');
  }

  let _geoWorker = null;
  let _geoMsgId  = 0;
  const _geoPending = {};
  const _GEO_TIMEOUT_MS = 30000;

  function _getGeoWorker() {
    if (_geoWorker) return _geoWorker;
    try {
      /* MINIFICATION-SAFE: the onmessage body must call the builders by
         whatever names they carry in THIS build (terser mangles them).
         Bind via Function.name at worker-source assembly time instead of
         hardcoding canonical names in the string literal. */
      const src = [
        svgArcToBeziers, parseSvgPathToPolylines,
        subdivCubic, subdivQuad, svgPathToAIOperators,
        _dtEngraveOverlapUnion, /* v2.9.30 overlap union */
        _buildDxfFromPaths, _buildEpsFromPaths,
      ].map(fn => fn.toString()).join('\n') + '\n'
        + 'var __dtDxf = ' + _buildDxfFromPaths.name + ';\n'
        + 'var __dtEps = ' + _buildEpsFromPaths.name + ';\n'
        + 'self.onmessage = function(e) {\n'
        + '  var d = e.data;\n'
        + '  try {\n'
        + '    var r = null;\n'
        + '    self.__dtDxfUnionMeta = null;\n'
        + '    if (d.type === "dxf") r = __dtDxf(d.pathDStrings, d.physDims);\n'
        + '    else if (d.type === "eps") r = __dtEps(d.pathDStrings, d.physDims, d.pathFills, false, d.pathStrokes, d.pathStrokeWidths);\n'
        + '    else if (d.type === "eps-ai") r = __dtEps(d.pathDStrings, d.physDims, d.pathFills, true, d.pathStrokes, d.pathStrokeWidths);\n'
        + '    self.postMessage({ id: d.id, result: r, unionMeta: self.__dtDxfUnionMeta || null });\n'
        + '  } catch (err) {\n'
        + '    self.postMessage({ id: d.id, result: null, error: err.message });\n'
        + '  }\n'
        + '};\n';
      const blob = new Blob([src], { type: 'application/javascript' });
      _geoWorker = new Worker(URL.createObjectURL(blob));
      _geoWorker.onmessage = function(e) {
        const p = _geoPending[e.data.id];
        if (p) {
          delete _geoPending[e.data.id];
          if (e.data.unionMeta) _dtReportDxfUnionMeta(e.data.unionMeta); /* v2.9.30 */
          if (e.data.error) p.reject(new Error(e.data.error));
          else p.resolve(e.data.result);
        }
      };
      _geoWorker.onerror = function() {
        for (const id of Object.keys(_geoPending)) {
          _geoPending[id].reject(new Error('Geometry worker crashed'));
          delete _geoPending[id];
        }
        _geoWorker = null;
      };
      DEBUG && console.log('[dt] Geometry worker created');
      return _geoWorker;
    } catch (e) {
      DEBUG && console.warn('[dt] Geometry worker unavailable, using main thread:', e.message);
      return null;
    }
  }

  /** Offload DXF/EPS conversion to the geometry worker.
   *  DOM parse + path extraction happen on the main thread (workers have
   *  no DOMParser). The heavy numeric work (bezier subdivision, coordinate
   *  transforms, output string assembly) runs in the worker.
   *  Falls back to synchronous main-thread execution on any failure. */
  /* ── Faux-bold dilation for engrave outputs (v2.9.11, Corpsman #2) ──
     Faux-bold text ships as fill + same-color stroke in the outlined SVG.
     PDFs and the generic print EPS stroke natively, but the engrave
     builders (AI8 EPS + DXF) are fill-only — the fattening was silently
     dropped and bold engraved at regular weight. Instead of teaching the
     laser dialects to stroke (EZCAD2 stroke semantics unverified), we bake
     the weight into the fill geometry: offset every contour by
     strokeWidth/2 — outer contours grow, holes (letter counters) shrink —
     which is exactly what the canvas's fill+stroke pair looks like.
     Runs on the main thread inside geoWorkerConvert BEFORE the d-strings
     go to the worker, so the worker contract is untouched. */

  /** Offset closed contours by ±delta. Outer-vs-counter is decided by
   *  WINDING SIGN against the dominant (largest-area) contour: fonts wind
   *  outers one way and counters the other (TrueType and CFF alike, each
   *  internally consistent), and this holds even when adjacent script
   *  glyphs OVERLAP without nesting — where point-in-polygon depth counting
   *  misclassifies a whole letter as a hole. Offset direction is then
   *  verified empirically per contour (area must grow for outers) so the
   *  y-down coordinate convention never matters. Counters a fat stroke
   *  would swallow (too small for the shrink / net edge reversal /
   *  orientation flip) are dropped — matching the canvas, where a fat
   *  enough stroke closes a counter entirely. */
  function _dilatePolysForBold(polys, delta) {
    const rings = [], srcIdx = [];
    const closedAt = [];
    /* v2.13.2: DEDUPE near-coincident consecutive vertices before offsetting
       (CB20260807W07I "hubblack"). Bezier flattening + subpath closes leave
       duplicate/sub-epsilon vertices; the bisector at a zero-length edge is
       numerically arbitrary, so the offset vertex fired up to 2.5x delta in a
       random direction — the 0.3mm "thorns" at serif corners, and the pull
       they exert on adjacent points is what made straight serif edges crooked. */
    const _dedupe = function (pts) {
      const out = [];
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i], q = out[out.length - 1];
        if (!q || Math.abs(p[0] - q[0]) > 1e-3 || Math.abs(p[1] - q[1]) > 1e-3) out.push(p);
      }
      /* closing vertex may now duplicate the first — drop it too */
      if (out.length > 1) {
        const a = out[0], b = out[out.length - 1];
        if (Math.abs(a[0] - b[0]) <= 1e-3 && Math.abs(a[1] - b[1]) <= 1e-3) out.pop();
      }
      return out;
    };
    polys.forEach(function (pts, i) {
      const cl = pts.length > 3 &&
        Math.abs(pts[0][0] - pts[pts.length - 1][0]) < 0.01 &&
        Math.abs(pts[0][1] - pts[pts.length - 1][1]) < 0.01;
      closedAt[i] = cl;
      rings.push(cl ? _dedupe(pts.slice(0, -1)) : _dedupe(pts.slice()));
      srcIdx.push(i);
    });
    const area2 = function (r) {
      let a = 0;
      for (let i = 0; i < r.length; i++) {
        const p = r[i], q = r[(i + 1) % r.length];
        a += p[0] * q[1] - q[0] * p[1];
      }
      return a / 2;
    };
    const offsetRing = function (r, d) {
      const n = r.length, out = [];
      for (let i = 0; i < n; i++) {
        const p0 = r[(i - 1 + n) % n], p1 = r[i], p2 = r[(i + 1) % n];
        let e1x = p1[0] - p0[0], e1y = p1[1] - p0[1];
        const l1 = Math.hypot(e1x, e1y) || 1; e1x /= l1; e1y /= l1;
        let e2x = p2[0] - p1[0], e2y = p2[1] - p1[1];
        const l2 = Math.hypot(e2x, e2y) || 1; e2x /= l2; e2y /= l2;
        const n1x = e1y, n1y = -e1x, n2x = e2y, n2y = -e2x;
        let mx = n1x + n2x, my = n1y + n2y;
        const ml = Math.hypot(mx, my);
        if (ml < 1e-6) { mx = n1x; my = n1y; } else { mx /= ml; my /= ml; }
        const dot = mx * n1x + my * n1y;
        const L = d / Math.max(0.4, dot); /* miter clamp ≈ 2.5×delta */
        out.push([p1[0] + mx * L, p1[1] + my * L]);
      }
      return out;
    };
    /* v2.13.2 — JOIN-AWARE offset for the EMITTED geometry (CB20260807W07I).
       offsetRing above stays as-is for the GUARD math below (its output is
       index-aligned with the input ring, which the counter-collapse and
       reflection checks depend on), but its clamped bisector emits ONE
       displaced point at every over-limit or degenerate corner — a blunted
       spike ("thorn") whose pull also bends the adjacent straight edges
       (crooked serifs). This variant emits proper joins instead:
       - normals nearly parallel (smooth run): single bisector point;
       - corner within miter limit (>= 60° included angle — a 90° serif
         corner miters to 1.414d and stays CRISP);
       - sharper than the limit: a two-point BEVEL at exactly d from each
         edge — corner excursion is capped at d, no spike is possible.
       Straight edges offset to exactly parallel straight edges. Output
       vertex count may exceed the input's — only the final emitted ring
       uses this, never the aligned guard math. */
    const MITER_DOT = 0.5; /* dot = cos(half angle); 0.5 → miter ≤ 2d */
    const offsetRingJoin = function (r, d) {
      const n = r.length, out = [];
      for (let i = 0; i < n; i++) {
        const p0 = r[(i - 1 + n) % n], p1 = r[i], p2 = r[(i + 1) % n];
        let e1x = p1[0] - p0[0], e1y = p1[1] - p0[1];
        const l1 = Math.hypot(e1x, e1y) || 1; e1x /= l1; e1y /= l1;
        let e2x = p2[0] - p1[0], e2y = p2[1] - p1[1];
        const l2 = Math.hypot(e2x, e2y) || 1; e2x /= l2; e2y /= l2;
        const n1x = e1y, n1y = -e1x, n2x = e2y, n2y = -e2x;
        let mx = n1x + n2x, my = n1y + n2y;
        const ml = Math.hypot(mx, my);
        if (ml < 1e-6) {
          /* 180° reversal (hairline tip): bevel across the tip */
          out.push([p1[0] + n1x * d, p1[1] + n1y * d]);
          out.push([p1[0] + n2x * d, p1[1] + n2y * d]);
          continue;
        }
        mx /= ml; my /= ml;
        const dot = mx * n1x + my * n1y;
        if (dot >= MITER_DOT) {
          /* true miter — exact intersection of the two offset edges */
          const L = d / dot;
          out.push([p1[0] + mx * L, p1[1] + my * L]);
        } else if ((e1x * e2y - e1y * e2x) * d > 0) {
          /* sharp corner on the OUTER side of the offset (edges diverge):
             bevel — one point per adjacent edge normal, excursion capped
             at exactly d. */
          out.push([p1[0] + n1x * d, p1[1] + n1y * d]);
          out.push([p1[0] + n2x * d, p1[1] + n2y * d]);
        } else {
          /* v2.13.2 independent-review amendment: sharp corner on the INNER
             side (offset edges CROSS — M/W crotches on grow, pointed counter
             tips on shrink). A bevel here lands past the crossing and twists
             the ring into a self-intersecting bowtie that wrongly fills the
             crotch/counter (unbounded by d). Emit the clamped bisector point
             instead — the base behavior, approximately correct on this side. */
          const L = d / Math.max(0.4, dot);
          out.push([p1[0] + mx * L, p1[1] + my * L]);
        }
      }
      return out;
    };
    /* Dominant winding = sign of the largest-|area| closed ring. */
    let domSign = 0, domArea = -1;
    for (let i = 0; i < rings.length; i++) {
      if (!closedAt[srcIdx[i]] || rings[i].length < 3) continue;
      const a = area2(rings[i]);
      if (Math.abs(a) > domArea) { domArea = Math.abs(a); domSign = a >= 0 ? 1 : -1; }
    }
    const result = [];
    for (let i = 0; i < rings.length; i++) {
      const r = rings[i];
      if (!closedAt[srcIdx[i]] || r.length < 3) { result.push(polys[srcIdx[i]]); continue; }
      const aSelf = area2(r);
      const grow = domSign === 0 || (aSelf >= 0 ? 1 : -1) === domSign;
      let _sd = delta; /* v2.13.2: remember the resolved sign for the join-aware emit */
      let off = offsetRing(r, _sd);
      const a0 = Math.abs(area2(r));
      if ((Math.abs(area2(off)) > a0) !== grow) { _sd = -delta; off = offsetRing(r, _sd); }
      if (!grow) {
        /* Counter too small to survive the shrink at all? Drop outright. */
        let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
        for (let k = 0; k < r.length; k++) {
          if (r[k][0] < bx0) bx0 = r[k][0]; if (r[k][0] > bx1) bx1 = r[k][0];
          if (r[k][1] < by0) by0 = r[k][1]; if (r[k][1] > by1) by1 = r[k][1];
        }
        if (Math.min(bx1 - bx0, by1 - by0) <= 2 * delta) continue;
        const aF = area2(off), aO = area2(r);
        if (Math.abs(aF) < 1e-9 || (aF > 0) !== (aO > 0)) continue; /* counter swallowed */
        /* Over-shrunk hole can point-reflect through its center into a
           valid-looking ring with UNCHANGED orientation (a 4px counter
           shrunk by 3px/side re-forms as a phantom 2px hole). A genuine
           shrink keeps edges pointing broadly the way they did; reflection
           reverses them. NET dot product (perimeter-weighted) so sub-pixel
           subdivision edges flipping under differential miter offsets at
           high-curvature corners can't false-positive a healthy counter. */
        let net = 0;
        for (let k = 0; k < r.length; k++) {
          const k2 = (k + 1) % r.length;
          net += (r[k2][0] - r[k][0]) * (off[k2][0] - off[k][0])
               + (r[k2][1] - r[k][1]) * (off[k2][1] - off[k][1]);
        }
        if (net <= 0) continue;
      }
      /* v2.13.2: guards above decided survival/sign on the ALIGNED offset;
         the geometry that ships comes from the join-aware variant. */
      const fin = offsetRingJoin(r, _sd);
      /* ND1 (review amendment): screen the EMITTED ring too. A sharp-cornered
         counter near collapse can survive the aligned-offset guards yet emit
         an orientation-inverted joined ring — under even-odd that ships a
         phantom unengraved hole. Area sign/magnitude needs no index
         alignment; an inverted/degenerate shrink ring is swallowed (counter
         fills solid — the established conservative semantic). Grow rings
         cannot invert (outward offset, excursion ≤ 2d). */
      if (!grow) {
        const aFin = area2(fin);
        if (Math.abs(aFin) < 1e-9 || (aFin > 0) !== (aSelf > 0)) continue;
      }
      fin.push([fin[0][0], fin[0][1]]);
      result.push(fin);
    }
    return result;
  }

  /** Rewrite a faux-bold path d-string with its contours dilated by delta
   *  (source px units — same space flattenSvgPaths bakes transforms and
   *  strokeWidth into). Falls back to the original d on any failure. */
  function _fauxBoldDilateD(d, delta) {
    try {
      const polys = parseSvgPathToPolylines(d, 0); /* 0 = no y-flip */
      if (!polys || !polys.length) return d;
      const dil = _dilatePolysForBold(polys, delta);
      if (!dil.length) return d;
      return dil.map(function (pts) {
        const cl = pts.length > 2 &&
          Math.abs(pts[0][0] - pts[pts.length - 1][0]) < 0.01 &&
          Math.abs(pts[0][1] - pts[pts.length - 1][1]) < 0.01;
        return 'M' + pts.map(function (p) {
          return p[0].toFixed(2) + ' ' + p[1].toFixed(2);
        }).join(' L ') + (cl ? ' Z' : '');
      }).join(' ');
    } catch (e) { return d; }
  }

  /** v2.11.3 — STROKE -> RIBBON (CB-20260802-5T8T). Convert a stroke-only
   *  path d (fill="none" + stroke, the hardcoded state outlines' shape) into
   *  FILLED ribbon geometry: every contour offset ±strokeWidth/2. Closed
   *  rings become an outer contour plus an inner contour reversed (a hole —
   *  even-odd and nonzero consumers both read it as a ring); open runs
   *  become one butt-capped loop (left side forward + right side back).
   *  Offset math mirrors _dilatePolysForBold's offsetRing (vertex normal
   *  bisector, miter clamp 0.4 ≈ 2.5x) — deviation from the raster proof's
   *  round joins is bounded by that clamp. An inner contour that collapses
   *  or inverts (stroke thicker than the shape) is dropped: the ribbon goes
   *  solid, which is what a painted stroke does too. Returns null on ANY
   *  failure so the caller keeps the original path — which then hits the
   *  ink filter and its engrave_paths_dropped warning instead of shipping
   *  corrupt geometry. MAIN THREAD ONLY: not in the geometry worker's
   *  stringified function list — callers must convert BEFORE postMessage. */
  function _strokeToRibbonD(d, halfW) {
    try {
      if (!(halfW > 0)) return null;
      const polys = parseSvgPathToPolylines(d, 0); /* 0 = no y-flip */
      if (!polys || !polys.length) return null;
      const area2 = function (r) {
        let a = 0;
        for (let i = 0; i < r.length; i++) {
          const p = r[i], q = r[(i + 1) % r.length];
          a += p[0] * q[1] - q[0] * p[1];
        }
        return a / 2;
      };
      /* Vertex-bisector offset. closed=false clamps ends to their single
         adjacent edge normal (butt cap). Same clamp as _dilatePolysForBold. */
      const offsetPts = function (r, dlt, closed) {
        const n = r.length, out = [];
        for (let i = 0; i < n; i++) {
          const p1 = r[i];
          const p0 = closed ? r[(i - 1 + n) % n] : r[Math.max(0, i - 1)];
          const p2 = closed ? r[(i + 1) % n] : r[Math.min(n - 1, i + 1)];
          let e1x = p1[0] - p0[0], e1y = p1[1] - p0[1];
          let e2x = p2[0] - p1[0], e2y = p2[1] - p1[1];
          if (!closed && i === 0) { e1x = e2x; e1y = e2y; }
          if (!closed && i === n - 1) { e2x = e1x; e2y = e1y; }
          const l1 = Math.hypot(e1x, e1y) || 1; e1x /= l1; e1y /= l1;
          const l2 = Math.hypot(e2x, e2y) || 1; e2x /= l2; e2y /= l2;
          const n1x = e1y, n1y = -e1x, n2x = e2y, n2y = -e2x;
          let mx = n1x + n2x, my = n1y + n2y;
          const ml = Math.hypot(mx, my);
          if (ml < 1e-6) { mx = n1x; my = n1y; } else { mx /= ml; my /= ml; }
          const dot = mx * n1x + my * n1y;
          const L = dlt / Math.max(0.4, dot);
          out.push([p1[0] + mx * L, p1[1] + my * L]);
        }
        return out;
      };
      const contours = [];
      for (let pi = 0; pi < polys.length; pi++) {
        /* Dedupe consecutive duplicates (the state d-strings repeat their
           first vertex) — a zero-length edge yields a garbage normal. */
        const src = polys[pi], pts = [];
        for (let k = 0; k < src.length; k++) {
          const q = pts[pts.length - 1];
          if (!q || Math.hypot(src[k][0] - q[0], src[k][1] - q[1]) > 1e-6) pts.push(src[k]);
        }
        if (pts.length < 2) continue;
        const closed = pts.length > 3 &&
          Math.abs(pts[0][0] - pts[pts.length - 1][0]) < 0.01 &&
          Math.abs(pts[0][1] - pts[pts.length - 1][1]) < 0.01;
        if (closed) {
          const ring = pts.slice(0, -1);
          if (ring.length < 3) continue;
          const a0 = Math.abs(area2(ring));
          if (!a0) continue;
          let outer = offsetPts(ring, halfW, true);
          if (Math.abs(area2(outer)) < a0) outer = offsetPts(ring, -halfW, true);
          let inner = offsetPts(ring, halfW, true);
          if (Math.abs(area2(inner)) >= a0) inner = offsetPts(ring, -halfW, true);
          contours.push(outer);
          /* Hole survives only if it kept its orientation (no point-
             reflection collapse) and meaningful area. */
          const aI = area2(inner);
          if (Math.abs(aI) > 1e-4 && (aI > 0) === (area2(ring) > 0)) {
            contours.push(inner.slice().reverse());
          }
        } else {
          const left = offsetPts(pts, halfW, false);
          const right = offsetPts(pts, -halfW, false).reverse();
          contours.push(left.concat(right));
        }
      }
      if (!contours.length) return null;
      return contours.map(function (c) {
        return 'M' + c.map(function (p) {
          return p[0].toFixed(2) + ' ' + p[1].toFixed(2);
        }).join(' L ') + ' Z';
      }).join(' ');
    } catch (e) { return null; }
  }

  function geoWorkerConvert(type, svgStr, physDims) {
    /* 1. DOM parse + path extraction — must stay on main thread */
    let rawPaths;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgStr, 'image/svg+xml');
      rawPaths = flattenSvgPaths(doc);
    } catch (e) {
      return Promise.resolve(null);
    }
    if (!rawPaths || !rawPaths.length) return Promise.resolve(null);

    /* Normalize: flattenSvgPaths returns plain strings (legacy) or
       {d,fill,stroke,strokeWidth} objects. Split into parallel arrays so
       workers and _buildEpsFromPaths can handle fills/strokes without
       breaking DXF. */
    /* fill="none" filter, by output type:
       - GENERIC PRINT EPS ('eps'): keep paths that carry a REAL stroke —
         PostScript strokes them natively (the Jul 22 order's orange cameo
         ring was silently dropped here, shipping an EPS with only the cream
         oval). Pure-guide paths (fill none, stroke none) still drop.
       - ENGRAVE AI8 EPS ('eps-ai') + DXF: keep the Jul 17 drop unchanged.
         EZCAD2's operator support for stroked paths is unverified, laser
         semantics for a stroked band differ from a filled outline, and the
         bulk stacked master's dashed cell-guide rect must not come back as
         a full-cell black rectangle. */
    const _hasInk = p => {
      if (typeof p === 'string') return true;
      if (String(p.fill || '').toLowerCase() !== 'none') return true;
      return type === 'eps' && !!p.stroke && String(p.stroke).toLowerCase() !== 'none';
    };
    /* ── v2.11.3 ENGRAVE STROKE -> RIBBON (CB-20260802-5T8T, Brandon Aug 3) ──
       Stroke-only vector art — every one of the 51 hardcoded state outlines
       is fill="none" stroke=... by construction — used to reach the ink
       filter below and vanish from BOTH engrave production files while every
       raster artifact (the approved proof included) stroked it natively.
       Convert stroke-only paths into filled ribbon outlines HERE, before the
       filter, so the engrave builders (fill-only AI8 EPS; fill-blind DXF)
       receive real closed geometry instead of a centerline that EZCAD2's
       autoshade would flood solid. Runs on the MAIN THREAD by necessity:
       _strokeToRibbonD is not in the geometry worker's stringified function
       list. Exclusions: the bulk stacked master's data-dt-guide cell rect
       (the reason the Jul 17 drop existed) stays dropped; 'transparent'
       strokes (the engrave editor's "Leave blank") stay invisible; print
       'eps' keeps native PostScript strokes and is untouched. The ribbon's
       fill inherits the stroke color so the paper-drop rules below still
       judge it — a white ribbon is paper and dies like any other white. */
    if (type !== 'eps') {
      rawPaths = rawPaths.map(p => {
        if (typeof p === 'string' || p.guide) return p;
        const f = String(p.fill || '').toLowerCase();
        const s = String(p.stroke || '').toLowerCase();
        if (f !== 'none' || !s || s === 'none' || s === 'transparent' || !(p.strokeWidth > 0)) return p;
        const rd = _strokeToRibbonD(p.d, p.strokeWidth / 2);
        if (!rd) return p; /* conversion failed — falls through to the drop + warning */
        return { d: rd, fill: p.stroke, stroke: null, strokeWidth: 0 };
      });
    }
    /* v2.11.3: the ink filter is no longer silent. On this order it deleted
       the state outline from both production files with zero telemetry while
       every neighbouring filter counts and warns. Guides are expected drops;
       anything else at this point is artwork the customer approved. */
    if (type !== 'eps') {
      const _lostArt = rawPaths.reduce((n, p) => n +
        ((!_hasInk(p) && !(typeof p !== 'string' && p.guide)) ? 1 : 0), 0);
      if (_lostArt) {
        gwarn('engrave_paths_dropped', _lostArt + ' inkless path(s) dropped from ' + type +
          ' output — approved artwork may be missing from the production file (stroke-only geometry that failed ribbon conversion, or fill and stroke both none)');
      }
    }
    rawPaths = rawPaths.filter(_hasInk);
    if (!rawPaths.length) return Promise.resolve(null);
    /* ── v2.10.1 — NO WHITE PAPER IN ENGRAVE OUTPUT (Brandon, Jul 30 2026) ──
       Root cause of the solid-square incident (CB-20260730-2F3B): a white-
       background upload reached the vectorizer, which returned the white
       "paper" group alongside the black art (cutouts stacking). The DXF
       builder discards fills, so every white path became a real closed
       contour: one full-print-area rectangle plus every art boundary
       DUPLICATED — EZCAD2's even-odd autoshade then fills the entire area
       solid. The AI8 EPS shipped the white shapes as real filled objects
       with the same result on a fill-blind consumer.
       Enforcement (the rule, per Brandon): white/near-white geometry NEVER
       ships in engrave production files — engrave art is 1-bit black-or-
       transparent. Dropping white here is safe for counters/holes: the
       black group carries its own hole boundaries as subpaths (verified),
       so letters keep their counters. Unknown/unparseable fills are KEPT
       (safe direction). Print 'eps' untouched — legitimate white artwork
       (white text on dark logos) must survive on the print path. */
    if (type !== 'eps') {
      const _isPaperWhite = p => {
        if (typeof p === 'string') return false;   /* legacy: no fill info — keep */
        const f = String(p.fill || '').toLowerCase();
        if (!f || f === 'none') return false;
        const rgb = _parseSvgColor(f);
        return rgb[0] >= 240 && rgb[1] >= 240 && rgb[2] >= 240;
      };
      const _nWhite = rawPaths.reduce((n, p) => n + (_isPaperWhite(p) ? 1 : 0), 0);
      if (_nWhite) {
        rawPaths = rawPaths.filter(p => !_isPaperWhite(p));
        gwarn('engrave_white_bg_dropped', _nWhite + ' white/near-white path(s) removed from ' + type +
          ' output — a paper background reached the engrave converter; check which upload flow produced this layer');
      }
      /* v2.10.3 RELATIVE rule: dim scans can cluster their paper below the
         absolute >=240 cut. Engrave is 1-bit by rule (ink + paper), so when
         the surviving fills split into clearly-separated dark and light
         groups (lightest >= 180 luminance AND >= 80 lighter than the
         darkest), the lightest group IS paper — drop it. Skipped when it
         would empty the output (all-light art: absolute rule already had
         its chance; ship rather than blank). Single-color art (L_max ==
         L_min) never triggers. */
      const _lums = rawPaths.map(p => {
        if (typeof p === 'string') return null;
        const f = String(p.fill || '').toLowerCase();
        if (!f || f === 'none') return null;
        const rgb = _parseSvgColor(f);
        return 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2];
      });
      const _known = _lums.filter(v => v !== null);
      if (_known.length > 1) {
        const _lMax = Math.max.apply(null, _known), _lMin = Math.min.apply(null, _known);
        if (_lMax >= 180 && (_lMax - _lMin) >= 80) {
          const _keep = rawPaths.filter((p, i) => _lums[i] === null || _lums[i] < _lMax - 12);
          if (_keep.length) {
            const _nRel = rawPaths.length - _keep.length;
            rawPaths = _keep;
            gwarn('engrave_white_bg_dropped', _nRel + ' lightest-group path(s) removed from ' + type +
              ' output (relative rule, fill luminance ~' + Math.round(_lMax) + ') — dim paper background reached the engrave converter');
          }
        }
      }
      if (!rawPaths.length) return Promise.resolve(null);
    }
    /* v2.9.11 (#2): engrave outputs bake faux-bold (fill + same-color
       stroke) into dilated fill geometry — the fill-only laser builders
       were shipping bold text at regular weight. Gated on the explicit
       data-dt-fauxbold marker emitText stamps on synthetic-bold text, so
       clipart with coincidentally equal stroke/fill is NEVER rewritten.
       Print 'eps' strokes natively and is left alone. */
    if (type !== 'eps') {
      rawPaths = rawPaths.map(p => {
        if (typeof p === 'string') return p;
        const swd = p.strokeWidth || 0;
        if (p.fauxBold && swd > 0 &&
            String(p.fill || '').toLowerCase() !== 'none') {
          return { d: _fauxBoldDilateD(p.d, swd / 2), fill: p.fill, stroke: null, strokeWidth: 0 };
        }
        return p;
      });
    }
    const pathDStrings = rawPaths.map(p => typeof p === 'string' ? p : p.d);
    /* v2.9.23 GUARD (SO-332060): a literal NaN/Infinity token in a
       flattened d-string means upstream transform math (ink-box mapping /
       outlining) corrupted the geometry before it ever reached the
       builders. Fail loudly HERE on the main thread — with a production-
       visible gwarn naming the output type — instead of letting the
       worker build a file that dies in Illustrator (error 2067). The
       rejected promise lands in each caller's existing catch/gwarn path. */
    const _badD = pathDStrings.reduce((n, d) => n + (/\b(?:NaN|Infinity)\b/.test(d || '') ? 1 : 0), 0);
    if (_badD) {
      gwarn('geo_nonfinite_paths', _badD + ' of ' + pathDStrings.length + ' flattened paths for ' + type + ' contain non-finite coordinates — conversion refused');
      return Promise.reject(new Error(_badD + ' non-finite flattened path d-string(s)'));
    }
    const _hasMeta = rawPaths.some(p => typeof p !== 'string');
    const pathFills = _hasMeta
      ? rawPaths.map(p => typeof p === 'string' ? null : (p.fill || null))
      : null;
    const pathStrokes = _hasMeta
      ? rawPaths.map(p => typeof p === 'string' ? null : (p.stroke || null))
      : null;
    const pathStrokeWidths = _hasMeta
      ? rawPaths.map(p => typeof p === 'string' ? 0 : (p.strokeWidth || 0))
      : null;

    /* 2. Try worker for the heavy computation */
    const w = _getGeoWorker();
    if (!w) {
      try {
        const result = type === 'dxf' ? _buildDxfFromPaths(pathDStrings, physDims)
                     : (type === 'eps' || type === 'eps-ai') ? _buildEpsFromPaths(pathDStrings, physDims, pathFills, type === 'eps-ai', pathStrokes, pathStrokeWidths) : null;
        if (type === 'dxf') _dtReportDxfUnionMeta(window.__dtDxfUnionMeta); /* v2.9.30 */
        return Promise.resolve(result);
      } catch (e) { return Promise.reject(e); }
    }

    /* 3. Post to worker with timeout — falls back to main thread if worker
          hangs or throws after creation. */
    return new Promise(function(resolve, reject) {
      const id = ++_geoMsgId;
      const mainThread = function(why) {
        DEBUG && console.warn('[dt] Geometry worker ' + why + ', falling back to main thread');
        try {
          var result = type === 'dxf' ? _buildDxfFromPaths(pathDStrings, physDims)
                     : (type === 'eps' || type === 'eps-ai') ? _buildEpsFromPaths(pathDStrings, physDims, pathFills, type === 'eps-ai', pathStrokes, pathStrokeWidths) : null;
          if (type === 'dxf') _dtReportDxfUnionMeta(window.__dtDxfUnionMeta); /* v2.9.30 */
          resolve(result);
        } catch (e) { reject(e); }
      };
      const timer = setTimeout(function() {
        delete _geoPending[id];
        mainThread('timed out');
      }, _GEO_TIMEOUT_MS);
      _geoPending[id] = {
        resolve: function(v) { clearTimeout(timer); resolve(v); },
        /* worker-side error (or crash) must not kill the file build —
           recompute on the main thread instead of rejecting. */
        reject:  function(e) { clearTimeout(timer); mainThread('errored (' + (e && e.message) + ')'); },
      };
      w.postMessage({ id: id, type: type, pathDStrings: pathDStrings, physDims: physDims, pathFills: pathFills, pathStrokes: pathStrokes, pathStrokeWidths: pathStrokeWidths });
    });
  }

  async function uploadToS3(dataUrl, filename, contentType, folder, disposition) {
    disposition = disposition || 'attachment';
    /* ── v2.11.2 — UPLOAD PAYLOAD VALIDATED BEFORE PRESIGN (Brandon, Aug 2 2026).
       Moves the base64 payload check ahead of the presign fetch and names the failing
       file / contentType / length in the thrown message, so an anonymous "empty data URL"
       GA4 error now identifies WHICH file (or toDataURL) produced it. Healthy uploads are
       byte-identical (same bytes -> same Blob -> same PUT); transport-only change — no effect
       on output files, imprint sizes, z-order, or front-end rendering. */
    const _du = (typeof dataUrl === 'string') ? dataUrl : '';
    const b64str = _du.split(',')[1];
    if (!b64str) {
      throw new Error('Upload rejected: empty data URL (no base64 payload) for '
        + filename + ' [ct=' + contentType + ', urlLen=' + _du.length
        + ', head="' + _du.slice(0, 32) + '"]');
    }
    let bytes;
    try { bytes = atob(b64str); }
    catch (_decErr) { throw new Error('Upload rejected: undecodable base64 for ' + filename + ' [ct=' + contentType + ', b64Len=' + b64str.length + ']'); }
    if (!bytes.length) throw new Error('Upload rejected: 0-byte payload for ' + filename + ' [ct=' + contentType + ']');
    let presignRes;
    try {
      presignRes = await fetch(CFG.presignUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename, contentType, folder, disposition }),
      });
    } catch (netErr) {
      throw new Error(`Presign network error: ${netErr.message}`);
    }
    if (!presignRes.ok) {
      const errText = await presignRes.text().catch(() => '');
      throw new Error(`Presign failed: ${presignRes.status} ${errText.slice(0, 100)}`);
    }
    const presignData = await presignRes.json();
    const { uploadUrl, downloadUrl } = presignData;

    /* payload validated pre-presign above (v2.11.2) — bytes already decoded */
    const ab = new ArrayBuffer(bytes.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < bytes.length; i++) ia[i] = bytes.charCodeAt(i);
    const blob = new Blob([ab], { type: contentType });

    /* Use the exact Content-Disposition the Lambda signed. If the Lambda
       doesn't return it (old deploy), fall back to attachment so the PUT
       header always matches the presigned signature. */
    const safeFilename = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
    const cdHeader = presignData.contentDisposition || `attachment; filename="${safeFilename}"`;

    let uploadRes;
    try {
      uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
          'Content-Disposition': cdHeader,
        },
        body: blob,
      });
    } catch (netErr) {
      throw new Error(`S3 PUT network error: ${netErr.message}`);
    }
    if (!uploadRes.ok) {
      const errText = await uploadRes.text().catch(() => '');
      throw new Error(`S3 upload failed: ${uploadRes.status} ${errText.slice(0, 200)}`);
    }
    return downloadUrl;
  }

  /**
   * Build a production SVG directly from text layers — no canvas rendering
   * or external vectorizer needed. Returns a data-URL, or null on failure.
   * Uses the same coordinate mapping as renderProductionPNG so position
   * accuracy matches the raster pipeline.
   */
  function buildTextOnlySvg(sideSlug) {
    const ls = S.byView[sideSlug];
    const visible = (ls || []).filter(l => l.visible !== false);
    const textLayers = visible.filter(l => l.type === 'text' && (l.text || '').trim());
    if (!textLayers.length) return null;

    const phys = SIDE_PHYS[sideSlug];
    if (!phys) return null;

    const DPI = PROD_DPI;
    const pa = CFG.sideConfigs[sideSlug]?.printArea || { x: 0, y: 0, w: 300, h: 300 };
    const pxW = Math.round(phys.wInches * DPI);
    const pxH = Math.round(phys.hInches * DPI);

    const _rs = S.rscaleByView[sideSlug] || S.rscale;
    const sidePA = { x: pa.x * _rs, y: pa.y * _rs, w: pa.w * _rs, h: pa.h * _rs };
    const scX = pxW / sidePA.w, scY = pxH / sidePA.h;
    const uniformScale = Math.min(scX, scY);
    const offX = (pxW - sidePA.w * uniformScale) / 2;
    const offY = (pxH - sidePA.h * uniformScale) / 2;

    const escXml = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    /* Collect unique fonts and build @font-face / @import rules */
    const fonts = new Set();
    textLayers.forEach(l => fonts.add(l.font || 'Montserrat'));
    const fontRules = [];
    fonts.forEach(fname => {
      /* Match against raw fields AND the cleaned familyCss (how fontValue is
         derived in getAllFonts) so the lookup succeeds regardless of whether
         familyCss is a bare slug ("amplitude") or a CSS declaration ("'Amplitude', sans-serif"). */
      const cf = (CFG.customFonts || []).find(f => {
        if (f.familyCss === fname || f.familyName === fname || f.displayName === fname) return true;
        const cleaned = (f.familyCss || '').split(',')[0].replace(/['"]/g, '').trim();
        return cleaned === fname;
      });
      if (cf?.fontFileUrl) {
        const fmt = fontFaceFormat(cf.fontFileUrl);
        const src = fmt ? `url('${cf.fontFileUrl}') format('${fmt}')` : `url('${cf.fontFileUrl}')`;
        fontRules.push(`@font-face { font-family: '${escXml(fname)}'; src: ${src}; }`);
      } else if (fname !== 'sans-serif' && fname !== 'serif' && fname !== 'monospace') {
        fontRules.push(`@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(fname)}:wght@400;700&amp;display=swap');`);
      }
    });

    const parts = [
      '<?xml version="1.0" encoding="UTF-8"?>',
      `<svg xmlns="http://www.w3.org/2000/svg" width="${phys.wInches}in" height="${phys.hInches}in" viewBox="0 0 ${pxW} ${pxH}">`,
    ];
    if (fontRules.length) parts.push(`<defs><style>${fontRules.join('\n')}</style></defs>`);
    parts.push(`<metadata>Text-only production SVG | ${textLayers.length} layer(s) | Method: ${S.method} | Side: ${sideSlug}</metadata>`);
    /* Clip to the artboard only when content overflows it — matches the
       customer-approved preview and the raster production files. */
    parts.push(productionClipOpen(sideSlug, pxW, pxH));

    textLayers.forEach((l, idx) => {
      const lx = (l.x - sidePA.x) * uniformScale + offX;
      const ly = (l.y - sidePA.y) * uniformScale + offY;
      const lw = (l.w || 0) * uniformScale;
      const lh = (l.h || 0) * uniformScale;
      /* Pivot must match canvas renderProductionPNG: centered layers
         pivot around (lx, ly); non-centered around bbox center. */
      const cx = l.centered ? lx : lx + lw / 2;
      const cy = l.centered ? ly : ly + lh / 2;
      const fs = (l.fontSize || 36) * uniformScale;
      const fillColor = S.method === 'engrave' ? '#000000' : (l.color || '#000');
      const fontFamily = `'${escXml(l.font || 'Montserrat')}', sans-serif`;

      /* Text draw position — matches canvas textAlign=center, textBaseline=middle.
         For centered layers lx is the center; for non-centered it's the top-left,
         so the draw-x must be the horizontal center of the bbox. */
      const drawX = l.centered ? lx : lx + lw / 2;
      const drawY = l.centered ? ly : ly + lh / 2;
      const lines = (l.text || '').split('\n');
      const lineH = fs * (l.lineHeight || 1.25);
      const totalH = lines.length * lineH;
      const startY = drawY - totalH / 2 + lineH / 2;

      /* Build transform attribute for rotation / flip */
      const xforms = [];
      if (l.rotation) xforms.push(`rotate(${l.rotation} ${cx.toFixed(2)} ${cy.toFixed(2)})`);
      if (l.flipH || l.flipV) {
        xforms.push(`translate(${cx.toFixed(2)} ${cy.toFixed(2)})`);
        xforms.push(`scale(${l.flipH ? -1 : 1} ${l.flipV ? -1 : 1})`);
        xforms.push(`translate(${(-cx).toFixed(2)} ${(-cy).toFixed(2)})`);
      }
      const tfAttr = xforms.length ? ` transform="${xforms.join(' ')}"` : '';

      parts.push(`<g id="layer-${idx + 1}"${tfAttr}>`);
      lines.forEach((line, i) => {
        parts.push(
          `<text x="${drawX.toFixed(2)}" y="${(startY + i * lineH).toFixed(2)}" ` +
          `text-anchor="middle" dominant-baseline="central" ` +
          `font-family="${fontFamily}" font-size="${fs.toFixed(1)}"` +
          `${l.bold ? ' font-weight="bold"' : ''}${l.italic ? ' font-style="italic"' : ''} ` +
          `fill="${fillColor}">${escXml(line)}</text>`
        );
      });
      parts.push('</g>');
    });

    parts.push('</g>'); /* close dtPaClip group */
    parts.push('</svg>');
    try {
      return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(parts.join('\n'))));
    } catch (e) {
      DEBUG && console.warn('[dt] buildTextOnlySvg failed:', e.message);
      return null;
    }
  }

  /**
   * Generate and upload all production files for one side.
   */
  /* ── Shipping-unit normalization for production SVG files ─────────
     Our builders work in a 300-units-per-inch coordinate space. Compliant
     renderers obey the width/height="Xin" attributes, but Illustrator and
     most prepress RIPs IGNORE them and map 1 SVG user unit = 1 point
     (1/72"), so a 3in file with a 900-unit viewBox opened at 12.5in — and
     supersampled traced files far larger ("SVG opens at 25.78 inches",
     production report Jul 17). Shipped SVGs are therefore rewritten to
     72 user units per inch: same inch attributes, viewBox in points, all
     content wrapped in one scale group. Pixel-identical in compliant
     renderers; physically exact in 1u=1pt apps. Internal interchange SVGs
     (buildCompositeOutlinedSvg → geo worker / PDF flattener) must STAY in
     the 300 space — only apply this at .svg upload sites. */
  const SHIP_SVG_UPI = 72; /* units per inch in shipped SVG files */
  function toShippingSvg(svgStr) {
    if (!svgStr) return svgStr;
    try {
      const open = svgStr.match(/<svg\b[^>]*>/);
      if (!open) return svgStr;
      const tag = open[0];
      const wM = tag.match(/width="([\d.]+)in"/);
      const hM = tag.match(/height="([\d.]+)in"/);
      const vbM = tag.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
      if (!wM || !hM || !vbM) return svgStr; /* unexpected shape — ship as-is */
      const wIn = parseFloat(wM[1]), hIn = parseFloat(hM[1]);
      const vbW = parseFloat(vbM[1]), vbH = parseFloat(vbM[2]);
      if (!(wIn > 0 && hIn > 0 && vbW > 0 && vbH > 0)) return svgStr;
      const k = (wIn * SHIP_SVG_UPI) / vbW;
      const newTag = tag.replace(/viewBox="[^"]*"/,
        `viewBox="0 0 ${+(wIn * SHIP_SVG_UPI).toFixed(3)} ${+(hIn * SHIP_SVG_UPI).toFixed(3)}"`);
      const tagStart = svgStr.indexOf(tag);
      const close = svgStr.lastIndexOf('</svg>');
      if (close <= tagStart) return svgStr;
      return svgStr.slice(0, tagStart) + newTag +
        `<g transform="scale(${+k.toFixed(6)})">` +
        svgStr.slice(tagStart + tag.length, close) +
        '</g></svg>';
    } catch (_) { return svgStr; }
  }

  /* ══ v2.10.0 S3 OUTPUT ROUTING (PLAN-S3-OUTPUT-ROUTING rev 9.1, Jul 30 2026) ══
     Role-based subfolders inside each line-item folder, plus the design serial
     prepended to EVERY uploaded filename. Files route by ROLE (what the file
     IS) — never by sniffing extension. The subfolder rides the presign
     `folder` argument, NEVER the filename (presign's filename sanitizer
     strips "/" and would mangle it into the name — HARD RULE). Empty
     subfolders never appear in S3 (no real directories — expected). */
  const S3_ROLE_DIR = {
    'engrave-dxf':   'Radian',        /* laser production DXF */
    'engrave-eps':   'Minis',         /* engrave production EPS */
    'print-pdf':     'ColorPrinting', /* full-print PRODUCTION PDF */
    'print-eps':     'ColorPrinting', /* full-print raster EPS */
    'transfer-png':  'DTF',           /* transparent production PNG — transfer methods only */
    'prepress-pdf':  'Prepress',
    'reference-png': 'Prepress',      /* 300dpi artwork reference, non-transfer methods */
    'source':        'Prepress',      /* customer source uploads */
    'proof-sheet':   'Prepress',
    'root':          '',              /* mockups, design-data, CSVs, template, bulk-preview */
  };

  /* The three transfer methods are ONE equivalence class for all branching
     (recipe + Flatbed exception). Each side's line-item property still
     carries its actual method token. UV DTF Print is NOT a transfer method —
     it stays FULL PRINT and its 300dpi PNG is a reference (→ Prepress). */
  const TRANSFER_METHODS = ['Vinyl Print', 'DTF Print', 'Direct to Film'];
  function isTransferMethod(m) { return TRANSFER_METHODS.indexOf(m) !== -1; }

  /* Effective per-side decoration method. Engrave comes from the GLOBAL
     toggle — sideConfigs[].decorationMethod can never say "Laser Engrave"
     (Liquid assigns a print method name to every side, engrave-capable or
     not). Same expression the cart props have always used (rev 9 review). */
  function effectiveSideMethod(sideSlug) {
    return (S.method === 'engrave')
      ? 'Laser Engrave'
      : (CFG.sideConfigs[sideSlug]?.decorationMethod || CFG.printMethodName || 'Cylindrical Print');
  }

  /* Serial-prefixed, role-routed upload. Every production-folder upload goes
     through here so the routing table above is the single source of truth. */
  function uploadProdFile(role, dataUrl, filename, contentType, s3Folder, serial, disposition) {
    const sub = S3_ROLE_DIR[role] || '';
    return uploadWithRetry(
      dataUrl,
      serial ? `${serial}-${filename}` : filename,
      contentType,
      sub ? `${s3Folder}/${sub}` : s3Folder,
      disposition);
  }

  async function generateSideProduction(sideSlug, statusFn, s3Folder, prodCtx) {
    const ls = S.byView[sideSlug];
    if (!ls?.length || !ls.some(l => l.visible !== false)) return null;
    const visible = ls.filter(l => l.visible !== false);

    const label = CFG.sideConfigs[sideSlug]?.label || sideSlug;
    const urls = {};

    /* 1. Composite PNG at 300 DPI */
    statusFn(`Rendering ${label}…`);
    const compositePng = await renderProductionPNG(sideSlug);
    if (!compositePng) return null;

    /* 2. Route by method — the effective method is resolved by the CALLER
       (rev 9) and passed in, so the recipe class is decided before any file
       is generated. Fallback self-derive keeps stray callers correct. */
    const serial = (prodCtx && prodCtx.serial) || getOrCreateSerial();
    const effMethod = (prodCtx && prodCtx.method) || effectiveSideMethod(sideSlug);
    const isEngrave = effMethod === 'Laser Engrave';
    const isTransfer = isTransferMethod(effMethod);

    /* ── 300 DPI raster PNG — ALWAYS uploaded, but its ROLE depends on the
       recipe class: for the three transfer methods this transparent render
       IS the production file (→ DTF); for every other method it is the
       artwork reference for the production team (→ Prepress). Same renderer,
       same transparency, different role. ── */
    statusFn(`Uploading ${label} raster…`);
    urls.png = await uploadProdFile(isTransfer ? 'transfer-png' : 'reference-png',
      compositePng, `${sideSlug}-300dpi.png`, 'image/png', s3Folder, serial);

    /* ── Layer classification ── */
    const hasText = visible.some(l => l.type === 'text' && (l.text || '').trim());
    const hasVector = visible.some(l => l.type !== 'text' && l.isSvg && l.svgMarkup);
    const hasRaster = visible.some(l => l.type !== 'text' && !(l.isSvg && l.svgMarkup));
    const physDims = SIDE_PHYS[sideSlug];

    /* ── Photo detection ──
       Photos (continuous-tone images) produce unusable posterized traces when
       sent through vectorizer.ai. The PDF already embeds raster content as a
       PNG, so skipping the trace loses nothing for the print path — just
       saves the API call and avoids generating garbage SVG/EPS.
       Check the persisted isPhoto flag first (set at upload time); fall back
       to a live isLikelyPhoto() check in case the flag wasn't set (layers
       created before this version, or template photos). */
    const rasterLayers = visible.filter(l => l.type !== 'text' && !(l.isSvg && l.svgMarkup));

    /* ── v2.9.16: PER-LAYER classification (Brandon, Jul 27 2026) ──
       The flags above are per-SIDE, so a side holding a photo AND a logo classified as
       "photo" and the logo stayed raster. These are per-layer, so one file can carry
       both treatments:
         A  isNativeVector  — already real paths, untouched
         B  isTraceableArt  — traced to paths        <- new in v2.9.16
         C  isPhotoLayer    — stays an embedded raster
       HARD EXCLUDE (Brandon's call): removedColor / _rcTrim are ALWAYS treated as
       photo and never traced. Remove Color REPLACES l.img, so isLikelyPhoto() would
       be judging the edited bitmap — stripping a flat background off a photograph
       could otherwise flip it into the traceable bucket and ship it posterized.
       NOTE: _bgRemoved is deliberately NOT excluded — see the comment at
       isPhotoLayer (~line 6377) for the rationale. It means "has transparency",
       not "background was removed", and excluding it would put every transparent-
       PNG logo into bucket C and trace nothing. */
    const hasPhotoRaster  = rasterLayers.some(isPhotoLayer);
    const hasTraceableArt = rasterLayers.some(isTraceableArt);
    /* Photo skip-trace is a PRINT-ONLY optimization: the print PDF embeds the
       raster as the real production file, so a trace adds nothing there.
       ENGRAVE has NO raster production file the laser software can use — it is
       DXF-native and needs vector geometry — so an engrave order that skips the
       trace ships only a reference PNG and NO usable production file (order
       CB-20260721-BT29 front: a line-art logo mis-flagged isPhoto exported a
       lone 300dpi PNG). Rule (Brandon): DXF must be output for EVERY engraving
       scenario, EPS as the fallback; a PNG/PDF-only engrave export is never
       acceptable. So engrave ALWAYS traces, even a photo raster: genuine photos
       are meant to be AI-converted to line art at upload, and if one slips
       through, a traced outline is still strictly better for the laser than a
       bare PNG (which stays uploaded as the last-resort fallback if the trace
       fails entirely). */

    /* ── Trace raster layers if needed ──
       Renders only raster/monogram layers to a separate PNG, then traces via
       callVectorize. Text and vector layers are excluded — text uses
       opentype.js and vector layers keep their original SVG paths. This
       eliminates the raster→vector round-trip for text.
       Skipped entirely when the raster content is photographic — the traced
       output would be a posterized mess, and the PDF/PNG are the correct
       production files for photos. */
    let tracedRasterSvg = null;
    /* v2.9.16: PRINT TRACES AGAIN — bucket B only. v2.9.6 made the print EPS a
       raster composite, removing this trace's last print consumer; v2.9.9 then
       deleted the call as dead weight. Each step was locally correct, and together
       they took the vectorizer out of print entirely. Measured consequence (Fleet
       Marine Force order, Jul 27): the ENGRAVE pipeline rendered that logo as 172
       contours at ~147 points each, while PRINT shipped an embedded raster under a
       177-contour clip at ~7 points each — the same shapes described 20x more
       coarsely. Print now gets the same Vectorizer.ai call engrave uses.
       ENGRAVE IS UNCHANGED: it still traces whenever the side has any raster,
       photo or not — DXF must exist for every engraving scenario (Brandon), and a
       traced outline beats a bare PNG for the laser even on a mis-flagged photo.
       skipTrace is gone: it only ever meant "photo, on the print path", which the
       per-layer split now says directly.
       Bulk has its own trace (bulkVarTracedSvg); handled at its own site. */
    /* TRANSFER methods skip the trace entirely (rev 9): its only consumer on
       the print path is the PRODUCTION PDF, which transfer suppresses — the
       call would be pure API cost plus spurious print_trace warnings. */
    if (hasRaster && !isTransfer && (isEngrave || hasTraceableArt)) {
      try {
        /* Supersample the trace input. Tracing the plain 300 DPI render gave
           small artwork only a few pixels per stroke and produced wobbly
           letterforms (order CB36098 "Brown's Roofing"). Render the trace
           input up to ~2.8 MP (vectorizer input budget), then the composite
           builders normalize coordinates back to the 300 DPI space via the
           traced SVG's viewBox (extractTracedSvgGroup). */
        const _tcs = getProductionCoordSpace(sideSlug);
        let traceSS = 1;
        if (_tcs && _tcs.pxW > 0 && _tcs.pxH > 0) {
          /* Budget = vectorizer.ai input.max_pixels maximum (3,145,828),
             which api/vectorize.js now requests explicitly — below that the
             service silently shrank inputs to 2.09MP and thin serif stems
             blurred before tracing (chopped-"N", Daniel Cochran order). */
          traceSS = Math.max(1, Math.min(3, Math.sqrt(3145828 / (_tcs.pxW * _tcs.pxH))));
        }
        /* Engrave traces every raster layer (unchanged). Print traces bucket B ONLY,
           so a photo sharing the side never reaches the vectorizer and keeps its
           embedded raster — the case the old per-side flag could not express. */
        const rasterFilter = isEngrave
          ? ((hasText || hasVector)
              ? (l => l.type !== 'text' && !(l.isSvg && l.svgMarkup))
              : null)
          : isTraceableArt;
        /* v2.12.2 ENGRAVE: trace in ORIGINAL colors. The pre-blackened
           input starved the vectorizer of color boundaries and it
           intermittently dropped thin stems (CB20260804KPOX "Spec·alists"
           — 3 stems missing from the shipped file). Color input traced the
           same geometry 5/5 burst trials with zero missed ink pixels.
           NOTE the forced trace render: engrave must NEVER reuse
           compositePng (it is force-blackened), so the render always runs
           with preserveColors even when rasterFilter is null at 1x. */
        const rasterOnlyPng = (isEngrave || rasterFilter || traceSS > 1.01)
          ? await renderProductionPNG(sideSlug, rasterFilter, traceSS, 0,
              isEngrave ? { preserveColors: true } : undefined)
          : compositePng; /* all-raster at 1x: reuse full composite */
        if (rasterOnlyPng) {
          statusFn(`Vectorizing ${label} raster art…`);
          /* v2.12.2: engrave 2 → 4. Two true inks + transparency still
             quantize to their own groups; 4 gives anti-aliased edge pixels
             headroom to resolve into the nearest ink instead of forcing
             merges that can eat thin features. All groups become black
             below, so extra groups are harmless. Print unchanged (32). */
          const maxC = isEngrave ? 4 : 32;
          if (isEngrave) {
            /* ── v2.13.0 (F3): audited trace with retry + hard block ──────
               The vectorizer intermittently drops thin stems from identical
               input (KPOX "Spec·alists", HM1F "FRE GHTL NER") — no parameter
               set prevents it (9/9 clean calls across maxColors 2/4/100 on
               HM1F's own artwork the day it shipped broken). Defense: audit
               every attempt per ink REGION (_auditTraceRegions), retry on
               failure (same-input retry recovers — KPOX went 30→34 shapes),
               keep the best-scoring attempt, and if still failing HARD-BLOCK
               the cart (Brandon: no silent ship, no raster fallback for
               engrave). Photo sides never block: a posterized photo trace
               legitimately covers <50% of many regions — telemetry only. */
            const TRACE_ATTEMPTS = 3;
            let _best = null;
            for (let _att = 1; _att <= TRACE_ATTEMPTS; _att++) {
              /* v2.13.3 (URL-mode review F1/F2): a THROWN attempt (transient
                 presign/PUT failure in URL mode, vectorizer 5xx, network)
                 previously aborted the whole loop on attempt 1 and fell to
                 the non-fatal catch — the side shipped PNG-only (the exact
                 CB37298/7EEC outcome, via 413). Exceptions now consume the
                 retry budget like audit failures; if every attempt throws
                 and no candidate exists, the cart HARD-BLOCKS (Brandon:
                 a missing production file is unacceptable — photo sides
                 included; the photo bypass below is for AUDIT quality on a
                 real trace, not for having no trace at all). */
              let _cand;
              try {
                const _tb = await callVectorize(rasterOnlyPng, 'svg', maxC, { folder: s3Folder, serial: serial });
                /* Engrave is 1-bit: flatten the color trace to black BEFORE any
                   consumer (paper-white drop rules would delete white ink). */
                _cand = _recolorSvgInkBlack(_normalizeAndRoundSvg(atob(_tb.split(',')[1] || _tb), 1));
              } catch (vecErr) {
                gwarn('engrave_trace_retry', 'Trace attempt ' + _att + '/' + TRACE_ATTEMPTS +
                  ' THREW for ' + sideSlug + ': ' + (vecErr && vecErr.message));
                if (_att < TRACE_ATTEMPTS) { statusFn(`Re-checking ${label} artwork…`); continue; }
                if (!_best) {
                  gwarn('engrave_trace_blocked', 'CART BLOCKED: trace errored on all ' +
                    TRACE_ATTEMPTS + ' attempts for ' + sideSlug + ': ' + (vecErr && vecErr.message));
                  const _e = new Error('We hit a snag preparing your engraving file — please try again.');
                  _e._dtTraceBlocked = true;
                  throw _e;
                }
                break; /* a prior attempt produced a candidate — ship the best */
              }
              const _audit = await _auditTraceRegions(rasterOnlyPng, _cand);
              if (!_best || _audit.worst > _best.audit.worst) _best = { svg: _cand, audit: _audit };
              if (_audit.ok) break;
              gwarn('engrave_trace_retry', 'Trace attempt ' + _att + '/' + TRACE_ATTEMPTS +
                ' failed region audit for ' + sideSlug + ': ' + _audit.fails + '/' + _audit.comps +
                ' region(s) uncovered, worst ' + Math.round(_audit.worst * 100) + '%' +
                (_audit.boxes && _audit.boxes.length ? ' @[' + _audit.boxes.join('; ') + ']' : ''));
              if (_att < TRACE_ATTEMPTS) statusFn(`Re-checking ${label} artwork…`);
            }
            tracedRasterSvg = _best.svg;
            if (!_best.audit.ok) {
              if (hasPhotoRaster) {
                gwarn('engrave_trace_coverage', 'Photo-side trace below region threshold for ' +
                  sideSlug + ' after ' + TRACE_ATTEMPTS + ' attempts (photo bypass, non-blocking)');
              } else {
                /* Forensics to S3 BEFORE blocking — both post-mortems (KPOX,
                   HM1F) had to reconstruct this exact evidence. Best-effort. */
                try {
                  await uploadProdFile('trace-audit-fail', rasterOnlyPng,
                    `${sideSlug}-trace-input-FAILED.png`, 'image/png', s3Folder, serial);
                  await uploadProdFile('trace-audit-fail',
                    'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(_best.svg))),
                    `${sideSlug}-trace-FAILED.svg`, 'image/svg+xml', s3Folder, serial);
                } catch (_) { /* forensics must never block the block */ }
                gwarn('engrave_trace_blocked', 'CART BLOCKED: region audit failed after ' +
                  TRACE_ATTEMPTS + ' attempts for ' + sideSlug + ' — ' + _best.audit.fails +
                  ' region(s) uncovered, worst ' + Math.round(_best.audit.worst * 100) + '%');
                const _e = new Error('We hit a snag preparing your engraving file — please try again.');
                _e._dtTraceBlocked = true;
                throw _e;
              }
            }
            /* Legacy global-percent telemetry retained for GA4 continuity. */
            _auditTraceCoverage(rasterOnlyPng, tracedRasterSvg, sideSlug);
            /* v2.13.3 ALWAYS-ON AUDIT RECEIPT: one event per engrave trace with
               the final verdict — so the next incident shows immediately whether
               the audit ran and what it measured (the WEOZ post-mortem had to
               infer this from S3 timestamps). Not an error; a receipt. */
            gwarn('engrave_trace_audit', sideSlug + ': attempts kept best of ' + TRACE_ATTEMPTS +
              ', comps=' + _best.audit.comps + ' fails=' + _best.audit.fails +
              ' worst=' + Math.round((_best.audit.worst || 0) * 100) + '%' +
              (_best.audit.crashed ? ' [AUDIT CRASHED: ' + _best.audit.error + ']' : ''));
          } else {
            /* v2.13.3: ctx enables URL mode for oversized PRINT trace inputs too
               (photo-adjacent bucket-B art can also exceed the 4.5MB edge cap). */
            const tracedB64 = await callVectorize(rasterOnlyPng, 'svg', maxC, { folder: s3Folder, serial: serial });
            tracedRasterSvg = _normalizeAndRoundSvg(atob(tracedB64.split(',')[1] || tracedB64), 1);
          }
          /* v2.9.16: a truthy but UNUSABLE trace is more dangerous than no trace at all.
             generateProductionPDF narrows its raster embed the moment tracedSvgStr is
             truthy, so a string that yields no geometry would drop bucket B from the
             file — and the never-blank guard cannot catch it, because any text on the
             side keeps paintedCount above zero. Prove the trace actually extracts before
             trusting it; otherwise fall back to null and embed the raster as before. */
          if (tracedRasterSvg) {
            const _tcheck = getProductionCoordSpace(sideSlug);
            const _tg = _tcheck ? extractTracedSvgGroup(tracedRasterSvg, _tcheck.pxW, _tcheck.pxH) : '';
            if (!_tg) {
              gwarn(isEngrave ? 'engrave_trace_empty' : 'print_trace_empty',
                    'Vectorize returned unusable geometry for ' + sideSlug + ' — falling back to raster');
              tracedRasterSvg = null;
            }
          }
        }
      } catch (traceErr) {
        /* v2.13.0 (F3): a deliberate audit block must ESCAPE this catch —
           blocking the cart is the entire point. Everything else keeps the
           old non-fatal semantics. */
        if (traceErr && traceErr._dtTraceBlocked) throw traceErr;
        /* ENGRAVE-CRITICAL: with no text/vector layers, EPS+DXF depend
           entirely on this trace — a silent failure here ships PNG-only
           (Jul 24 Michigan order). Loud by design. */
        /* PRINT (v2.9.16) is NOT fatal here: generateProductionPDF widens its raster
           embed back to include bucket B whenever tracedRasterSvg is null, so a
           vectorizer outage degrades to exactly the pre-v2.9.16 file rather than a
           PDF with the logo missing. Keyed separately so print failures are visible
           to dashboards filtered on print rather than hiding under an engrave event. */
        gwarn(isEngrave ? 'engrave_trace_failed' : 'print_trace_failed',
              'Vectorize trace failed for ' + sideSlug + ': ' + (traceErr && traceErr.message));
      }
    }

    /* Normalize a raw traced SVG for direct use as a production file:
       rescales the (possibly supersampled) trace to the 300 DPI coordinate
       space and stamps physical inch dimensions, which raw vectorizer output
       lacks. Also required so EPS/DXF conversion (which assumes the 300 DPI
       space) sizes the art correctly. */
    const normalizeTracedSvg = (traced) => {
      const cs = getProductionCoordSpace(sideSlug);
      if (!cs || !traced) return traced;
      const g = extractTracedSvgGroup(traced, cs.pxW, cs.pxH);
      /* Extraction failed: at minimum stamp physical inch dimensions onto
         the raw traced markup — its supersampled px width/height are what
         opened ~26in wide in prepress apps. */
      if (!g) return traced.replace(/<svg\b([^>]*?)\swidth="[^"]*"([^>]*?)\sheight="[^"]*"/,
        `<svg$1 width="${cs.phys.wInches}in"$2 height="${cs.phys.hInches}in"`);
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${cs.phys.wInches}in" height="${cs.phys.hInches}in" viewBox="0 0 ${cs.pxW} ${cs.pxH}">${g}</svg>`;
    };

    if (isEngrave) {
      /* ── ENGRAVE: EPS + DXF ONLY (Brandon, Jul 17 2026) ──
         The engraving department's machine software is DXF-native and
         chokes on SVG (unit misreads, then hard entity errors), so engrave
         orders ship NO SVG and NO PDF — just EPS + DXF from outlined
         paths. The 300 DPI PNG stays uploaded as reference and as the
         raster fallback when vector conversion fails entirely. */

      /* Outlined SVG for EPS/DXF — text via opentype.js, vector from
         markup, raster from trace. INTERNAL interchange only (never
         shipped as a file). No callVectorize round-trip for text. */
      try {
        statusFn(`Building ${label} outlined paths…`);
        let outlinedSvg;
        if (hasText || hasVector) {
          outlinedSvg = await buildCompositeOutlinedSvg(sideSlug, true, tracedRasterSvg);
        } else if (tracedRasterSvg) {
          outlinedSvg = normalizeTracedSvg(tracedRasterSvg);
        }
        if (outlinedSvg && physDims) {
          statusFn(`Converting ${label} to EPS…`);
          try {
            const epsStr = await geoWorkerConvert('eps-ai', outlinedSvg, physDims);
            if (epsStr) {
              const epsB64 = 'data:application/postscript;base64,' + btoa(epsStr);
              urls.eps = await uploadProdFile('engrave-eps', epsB64, `${sideSlug}-engrave.eps`, 'application/postscript', s3Folder, serial);
            }
          } catch (epsErr) { gwarn('engrave_eps_failed', 'Engrave EPS failed for ' + sideSlug + ': ' + epsErr.message); }

          statusFn(`Converting ${label} to DXF…`);
          try {
            const dxfStr = await geoWorkerConvert('dxf', outlinedSvg, physDims);
            if (dxfStr) {
              const dxfB64 = 'data:application/dxf;base64,' + btoa(dxfStr);
              urls.dxf = await uploadProdFile('engrave-dxf', dxfB64, `${sideSlug}-engrave.dxf`, 'application/dxf', s3Folder, serial);
            }
          } catch (dxfErr) { gwarn('engrave_dxf_failed', 'Engrave DXF failed for ' + sideSlug + ': ' + dxfErr.message); }
        }
      } catch (e) { gwarn('engrave_outline_failed', 'Engrave outlined build failed for ' + sideSlug + ': ' + e.message); }

      /* PREPRESS PDF for engrave too (rev 8/9, Brandon Jul 30 2026). Files in
         the Prepress subfolder ONLY — the engrave pointer set stays exactly
         DXF = file 1, EPS = file 2 (pointer assembly ignores pdfPrepress on
         the engrave branch by design; automation keying on engrave
         file_count === 2 is untouched). Non-fatal like every print PDF. */
      try {
        statusFn(`Building ${label} PREPRESS PDF…`);
        const prepPdfB64 = await generatePrepressPDF(sideSlug, compositePng);
        urls.pdfPrepress = await uploadProdFile('prepress-pdf', prepPdfB64, `${sideSlug}-print-PREPRESS.pdf`, 'application/pdf', s3Folder, serial);
      } catch (prepErr) { DEBUG && console.warn('[dt] Engrave PREPRESS PDF failed (non-fatal):', prepErr.message); }

      /* Label by what ACTUALLY shipped, not the source raster's photo flag: if
         vector (DXF/EPS) was produced it's a vector engrave regardless of how
         the art started. Only fall back to the photo-raster label when NO vector
         could be made (trace/convert failed entirely) and the PNG is the sole
         deliverable. Preserves the original signal for a non-photo engrave whose
         convert double-fails (labeled 'engrave-vector' → relabeled
         'engrave-raster-fallback' downstream when it drops to PNG). */
      /* PNG-only engrave is NEVER acceptable (Brandon's rule, v2.9.0) —
         when it happens anyway, say so loudly instead of shipping silently
         (Jul 24 Michigan order: vector conversion failed with zero signal). */
      if (!urls.dxf && !urls.eps) {
        gwarn('engrave_png_only', 'ENGRAVE order shipped WITHOUT vector files for ' + sideSlug + ' — 300dpi PNG only. Check vectorizer credits/API and dt_error events.');
      }
      urls.productionType = (urls.dxf || urls.eps) ? 'engrave-vector'
                          : hasPhotoRaster ? 'engrave-photo-raster'
                          : 'engrave-vector';

    } else {
      /* ── PRINT: PRODUCTION PDF + PREPRESS PDF + EPS (outlined) ──
         SVG output removed — dual PDF system replaces single hybrid PDF + SVG.
         PRODUCTION: outlined text, print-area clipping, silhouette clip mask.
         PREPRESS: live/editable text (pdf-lib), no clipping, 100px bleed,
                   print-area guide layer. Falls back to outlined if font
                   embedding fails. */

      /* PRODUCTION PDF: outlined text + silhouette clipping path.
         SUPPRESSED for transfer methods (rev 9, Brandon Jul 29): Vinyl /
         DTF Print / Direct to Film ship the transparent 300dpi PNG as the
         production file — no PROD PDF, no raster EPS. */
      if (!isTransfer) {
          /* v2.13.5 (CB37404): generation and upload are SEPARATE catches so
             a transient jsPDF issue retries generation (same vector inputs —
             no raster degradation) and a transient S3 issue retries only the
             upload. The old DEBUG-only catch silently swallowed the CB37404
             back-side PRODUCTION PDF with zero telemetry. */
          let _prodPdfB64 = null;
          for (let _prodAtt = 1; _prodAtt <= 2 && !_prodPdfB64; _prodAtt++) {
            try {
              statusFn(_prodAtt === 1 ? `Building ${label} PRODUCTION PDF…` : `Retrying ${label} PRODUCTION PDF…`);
              _prodPdfB64 = await generateProductionPDF(sideSlug, compositePng, tracedRasterSvg);
            } catch (genErr) {
              gwarn('prod_pdf_gen_failed', 'PRODUCTION PDF generation attempt ' + _prodAtt +
                '/2 failed for ' + sideSlug + ': ' + (genErr && genErr.message));
            }
          }
          if (_prodPdfB64) {
            try {
              urls.pdfProduction = await uploadProdFile('print-pdf', _prodPdfB64, `${sideSlug}-print-PRODUCTION.pdf`, 'application/pdf', s3Folder, serial);
            } catch (uplErr) {
              gwarn('prod_pdf_upload_failed', 'PRODUCTION PDF upload failed for ' + sideSlug +
                ': ' + (uplErr && uplErr.message));
            }
          } else {
            gwarn('prod_pdf_not_generated', 'PRODUCTION PDF permanently failed for ' + sideSlug + ' after 2 attempts — order will ship without it');
          }
      }

      /* PREPRESS PDF: live text + full export + print-area guide.
         ALL print-family methods make one, transfer included. */
      try {
        statusFn(`Building ${label} PREPRESS PDF…`);
        const prepPdfB64 = await generatePrepressPDF(sideSlug, compositePng);
        urls.pdfPrepress = await uploadProdFile('prepress-pdf', prepPdfB64, `${sideSlug}-print-PREPRESS.pdf`, 'application/pdf', s3Folder, serial);
      } catch (prepPdfErr) { DEBUG && console.warn('[dt] PREPRESS PDF failed:', prepPdfErr.message); }

      /* EPS: 300 DPI raster composite in DeviceRGB — ALL print orders
         (Brandon, Jul 23 2026). Replaces the outlined vector EPS, whose
         naive device-CMYK conversion drifted on press and which could not
         carry photo rasters at all (Jul 22 order shipped a lone cream
         oval). The color-managed RIP now converts RGB with its own
         profile — same treatment as the PDFs. See generateRasterEPS. */
      if (isTransfer) {
        /* Transfer: no raster EPS either — the PNG + PREPRESS pair is the
           complete output set for this class. */
      } else try {
        statusFn(`Building ${label} EPS…`);
        /* Higher-resolution raster for the EPS only (v2.9.20). The EPS is already
           resolution-independent — _assembleRasterEps derives ImageMatrix from
           physDims, and BoundingBox is pinned to physical inches — so more pixels
           means more DPI at IDENTICAL size.

           There is no way to mix resolutions inside one raster: the image has a
           single /Width and /Height. Rendering at 600 does not degrade 300dpi
           content (uploads are interpolated, carrying the same information), it
           simply doesn't improve it. Text, clipart and SVG DO get genuinely
           sharper, since they re-render from infinite-resolution sources.
           Measured cost: ~3.5x the EPS bytes, near-constant across content —
           Flate has no image predictor, so smooth upscaled regions compress
           barely better than detailed ones.

           Budget rather than a flat 2x because peak memory is double-buffered
           here: the hi-res PNG data URL is live at the same time as the canvas
           generateRasterEPS decodes it into. 24M px keeps a 10x10 area inside
           roughly 100MB of pixel buffers. Falls back to the 300dpi composite on
           any failure, so a large area on a phone degrades instead of throwing.

           NOTE: urls.png above still uploads the 300dpi compositePng — its name
           says 300dpi and downstream keys on it. Only the EPS changes. */
        let epsPng = compositePng;
        const _eph = SIDE_PHYS[sideSlug];
        if (_eph && _eph.wInches > 0 && _eph.hInches > 0) {
          const _ebase = Math.round(_eph.wInches * PROD_DPI) * Math.round(_eph.hInches * PROD_DPI);
          const _ess = Math.max(1, Math.min(EPS_DPI_MULT, Math.sqrt(EPS_PX_BUDGET / _ebase)));
          if (_ess > 1.01) {
            try {
              const _hi = await renderProductionPNG(sideSlug, null, _ess);
              if (_hi) epsPng = _hi;
              else gwarn('eps_hires_empty', 'Hi-res EPS render returned null for ' + sideSlug + ' — falling back to 300dpi.');
            } catch (_hiErr) {
              gwarn('eps_hires_failed', 'Hi-res EPS render failed for ' + sideSlug + ' (' + _hiErr.message + ') — falling back to 300dpi.');
            }
          }
        }
        const epsStr = await generateRasterEPS(epsPng, physDims);
        epsPng = null; /* release the hi-res data URL before the upload buffers */
        if (epsStr) {
          const epsB64 = 'data:application/postscript;base64,' + btoa(epsStr);
          /* Name says what it IS (v2.9.19). This EPS is a 300dpi DeviceRGB raster
             composite, never vector — the engrave EPS one branch up is the vector
             one, and production was reading both as "the EPS". */
          urls.eps = await uploadProdFile('print-eps', epsB64, `${sideSlug}-print-PRODUCTION-RASTER.eps`, 'application/postscript', s3Folder, serial);
        }
      } catch (e) { DEBUG && console.warn('[dt] Print EPS failed:', e.message); }

      /* Transfer orders get an HONEST type string (Brandon, Jul 30) — the
         old print-vector/print-raster strings described files this class no
         longer ships. Automation reading _production_type learns one value. */
      urls.productionType = isTransfer
        ? 'print-transfer-png'
        : hasRaster
          ? (hasPhotoRaster ? 'print-photo-300dpi' : 'print-raster-300dpi')
          : 'print-vector';
    }

    return urls;
  }

  /* ══════════════════════════════════════════════════════════════════
   * DESIGN SERIALIZATION
   * ══════════════════════════════════════════════════════════════════ */
  function generateSerial() {
    const d = new Date();
    const ds = String(d.getFullYear()) + String(d.getMonth()+1).padStart(2,'0') + String(d.getDate()).padStart(2,'0');
    const r = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `CB-${ds}-${r}`;
  }

  /* ── Serial reuse on cart-add retry ──────────────────────
     If the user retries add-to-cart without changing the design, reuse the
     same serial so S3 files overwrite in the same folder instead of orphaning
     the previous attempt's folder.  Any design mutation (tracked via
     pushHistory) marks the serial dirty, forcing a fresh one on the next
     cart-add. */
  let _cachedSerial = null;
  let _serialDirty  = true;   /* start dirty so the first add always generates */

  function getOrCreateSerial() {
    if (!_cachedSerial || _serialDirty) {
      _cachedSerial = generateSerial();
      _serialDirty  = false;
    }
    return _cachedSerial;
  }

  function markSerialDirty() { _serialDirty = true; }

  function getCartSessionId() {
    let id = sessionStorage.getItem('dt_cart_session');
    if (!id) { id = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8); sessionStorage.setItem('dt_cart_session', id); }
    return id;
  }

  function serializeDesign() {
    const sideData = {};
    CFG.sides.forEach(s => {
      const phys = SIDE_PHYS[s];
      const rpa = CFG.sideConfigs[s]?.printArea;
      sideData[s] = {
        label: CFG.sideConfigs[s]?.label || s,
        printArea: rpa,
        /* Save the rendered print area (in canvas-space pixels) so restore can
           correctly map layer positions, which are also in canvas-space. */
        renderedPrintArea: rpa ? (S.paByView[s] || { x: rpa.x * S.rscale, y: rpa.y * S.rscale, w: rpa.w * S.rscale, h: rpa.h * S.rscale }) : null,
        physicalDims: phys ? { wInches: phys.wInches, hInches: phys.hInches, dpi: Math.round(physDPI(s)) } : null,
        layers: S.byView[s].map(l => ({
          id: l.id, type: l.type, name: l.name, x: l.x, y: l.y, w: l.w, h: l.h,
          rotation: l.rotation, flipH: l.flipH, flipV: l.flipV, isRasterUpload: !!l.isRasterUpload,
          centered: !!l.centered,
          ...(l.type === 'text' ? { text: l.text, font: l.font, fontSize: l.fontSize, color: l.color, bold: l.bold, italic: l.italic, lineHeight: l.lineHeight || null, ...(l._bulkField ? { _bulkField: true } : {}) } : {}),
          ...(l.isMonogram ? { isMonogram: true, monoText: l.monoText, monoStyle: l.monoStyle, monoColor: l.monoColor, monoColor2: l.monoColor2 } : {}),
          ...(l.isSvg && l.svgMarkup ? { isSvg: true, svgMarkup: l.svgMarkup } : {}),
          ...((l.isRasterUpload || l._tplPhoto) && l.b64 && l.b64.length < 300000 ? { b64: l.b64 } : {}),
          ...(l.tintColor ? { tintColor: l.tintColor } : {}),
          ...(l.isPhoto ? { isPhoto: true } : {}),
          ...(l._bgRemoved ? { _bgRemoved: true } : {}),
          ...(l._engraveInverted ? { _engraveInverted: true } : {}),
          ...(l.removedColor ? { removedColor: l.removedColor } : {}),
          ...(l._bgSeed ? { _bgSeed: l._bgSeed } : {}),
          ...(l._groupId ? { _groupId: l._groupId } : {}),
          ...(l._maskKey ? { _maskKey: l._maskKey } : {}),
          ...(l.isBackdrop ? { isBackdrop: true, _backdropKey: l._backdropKey || null, _maskMarkup: l._maskMarkup || null, _content: l._content || null } : {}),
          ...(l._tplLockId ? { _tplLockId: l._tplLockId } : {}),
          ...(l._lockDraggable ? { _lockDraggable: true } : {}),
          ...(l.isStateOutline ? { isStateOutline: true, stateName: l.stateName || null } : {}),
          ...(l.isStateHeart ? { isStateHeart: true } : {}),
          ...(l._tplPhoto ? { _tplPhoto: true, _noResize: true } : {}),
          ...(l._tplMask ? { _tplMask: true } : {}),
          ...(l.isPhotoPlaceholder ? { isPhotoPlaceholder: true, _maskShape: l._maskShape || null } : {}),
        })),
      };
    });
    return { method: S.method, variantId: S.variantId, sides: sideData };
  }

  /* ══════════════════════════════════════════════════════════════════
   * CART — CONFIRMATION MODAL + ADD TO CART
   * ══════════════════════════════════════════════════════════════════ */

  function getDesignedSides() {
    return CFG.sides.filter(s => S.byView[s]?.some(l => l.visible !== false));
  }

  /* Format cents as a clean price label ($6, $20, $5.50) */
  function dtMoney(cents) {
    const n = (cents || 0) / 100;
    return '$' + (Number.isInteger(n) ? n : n.toFixed(2));
  }

  function calcUpcharges(designedSides) {
    const extra = designedSides.length - 1;
    if (extra <= 0) return [];
    const variantId = CFG.isCooler ? CFG.upchargeStandard : CFG.upchargeValue;
    const cents = CFG.isCooler ? CFG.upchargeStandardPrice : CFG.upchargeValuePrice;
    const charges = [];
    for (let i = 0; i < extra; i++) {
      charges.push({ variantId, cents, label: `Additional Side Decoration (+${dtMoney(cents)})` });
    }
    return charges;
  }

  /* ── Immersive review: float the info panel 20px right of the visible
     product pixels (single review) or the proof strip (group review) on
     desktop. Scans the hero PNG's alpha channel on a small offscreen
     canvas to find the rightmost opaque column; falls back to the image
     element's box if measurement fails. Clamped to the viewport. ── */
  /* Crop a PNG data-URL to its non-transparent bounding box (+pad px).
     Product renders carry large transparent margins, which made small
     products review tiny; cropping first lets CSS scale the product
     itself to the target share of the canvas. Falls back to the
     original on any failure. */
  function cropPngToAlpha(dataUrl, pad = 12) {
    return new Promise(resolve => {
      const im = new Image();
      im.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = im.naturalWidth; c.height = im.naturalHeight;
          const cx = c.getContext('2d', { willReadFrequently: true });
          cx.drawImage(im, 0, 0);
          const data = cx.getImageData(0, 0, c.width, c.height).data;
          let minX = c.width, minY = c.height, maxX = -1, maxY = -1;
          for (let y = 0; y < c.height; y++) {
            const row = y * c.width;
            for (let x = 0; x < c.width; x++) {
              if (data[(row + x) * 4 + 3] > 8) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (maxX < 0) { c.width = 0; resolve(dataUrl); return; }
          minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
          maxX = Math.min(c.width - 1, maxX + pad); maxY = Math.min(c.height - 1, maxY + pad);
          const w = maxX - minX + 1, h = maxY - minY + 1;
          const out = document.createElement('canvas'); out.width = w; out.height = h;
          out.getContext('2d').drawImage(c, minX, minY, w, h, 0, 0, w, h);
          const url = out.toDataURL('image/png');
          c.width = 0; out.width = 0;
          resolve(url);
        } catch (_) { resolve(dataUrl); }
      };
      im.onerror = () => resolve(dataUrl);
      im.src = dataUrl;
    });
  }

  /* ── Immersive review: float the info panel (see notes above each block) ── */
  function positionImrPanel(overlay) {
    const bar = overlay.querySelector('.dt-imr-bar');
    if (!bar) return;
    const strip = overlay.querySelector('.dt-imr-proof');
    if (window.innerWidth <= 768) {
      bar.style.left = ''; overlay.style.top = '';
      if (strip) strip.style.left = '';
      return;
    }
    const GAP = 120;
    overlay.style.top = Math.round(document.querySelector('.dt-header')?.getBoundingClientRect().bottom || 0) + 'px';
    /* Rightmost visible product pixel across every hero image on screen.
       Alpha-scan once per image (cached on the element). */
    const measure = img => {
      if (!(img && img.complete && img.naturalWidth)) return img ? img.getBoundingClientRect().right : 0;
      const r = img.getBoundingClientRect();
      let frac = parseFloat(img.dataset.dtAlphaFrac || '');
      if (!(frac > 0)) {
        frac = 1;
        try {
          const sw = Math.min(240, img.naturalWidth);
          const sh = Math.max(1, Math.round(img.naturalHeight * sw / img.naturalWidth));
          const c = document.createElement('canvas'); c.width = sw; c.height = sh;
          const cx = c.getContext('2d', { willReadFrequently: true });
          cx.drawImage(img, 0, 0, sw, sh);
          const d = cx.getImageData(0, 0, sw, sh).data;
          let right = 0;
          outer: for (let x = sw - 1; x >= 0; x--) {
            for (let y = 0; y < sh; y++) {
              if (d[(y * sw + x) * 4 + 3] > 8) { right = x + 1; break outer; }
            }
          }
          if (right) frac = right / sw;
          c.width = 0;
        } catch (_) {}
        img.dataset.dtAlphaFrac = frac;
      }
      /* object-fit:contain can letterbox the drawn image inside the box */
      const scale = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
      const dw = img.naturalWidth * scale;
      return r.left + (r.width - dw) / 2 + dw * frac;
    };
    let anchor = 0;
    overlay.querySelectorAll('.dt-imr-heroimg').forEach(img => { anchor = Math.max(anchor, measure(img)); });
    if (!anchor) { bar.style.left = ''; if (strip) strip.style.left = ''; return; }
    if (strip) {
      const maxStrip = window.innerWidth - strip.offsetWidth - bar.offsetWidth - GAP - 40;
      strip.style.left = Math.round(Math.min(anchor + GAP, Math.max(anchor + 24, maxStrip))) + 'px';
      anchor = strip.getBoundingClientRect().right;
    }
    bar.style.left = Math.round(Math.max(20, Math.min(anchor + GAP, window.innerWidth - bar.offsetWidth - 20))) + 'px';
  }

  function watchImrPanel(overlay) {
    const fn = () => {
      if (!document.body.contains(overlay)) { window.removeEventListener('resize', fn); return; }
      positionImrPanel(overlay);
    };
    window.addEventListener('resize', fn);
  }

  /* Review-time image-quality audit. Returns the worst below-threshold
     raster layer per designed side, using the SAME formula and 150 DPI
     threshold as the canvas quality badge (effectiveDPI at printed size).
     Computed at review time — not upload time — so resizing the image
     larger after the upload warning is still caught. */
  function collectLowResSides(designedSlugs) {
    const out = [];
    (designedSlugs || []).forEach(slug => {
      const rs = (S.rscaleByView && S.rscaleByView[slug]) || S.rscale || 1;
      (S.byView[slug] || []).forEach(l => {
        if (l.visible === false || l.type === 'text' || l.isMonogram || (l.isSvg && l.svgMarkup)) return;
        if (!l.img?.naturalWidth || !l.w) return;
        const dpi = Math.round(l.img.naturalWidth / pxToInches(l.w / rs, slug));
        if (dpi >= 150) return;
        const prev = out.find(o => o.slug === slug);
        if (prev) {
          if (dpi < prev.dpi) { prev.dpi = dpi; prev.name = l.name || 'image'; }
        } else {
          out.push({ slug, dpi, name: l.name || 'image', label: CFG.sideConfigs[slug]?.label || slug });
        }
      });
    });
    return out;
  }

  function openCartModal() {
    document.getElementById('dt-cart-modal')?.remove();
    const designed = getDesignedSides();
    dtAnalytics.cartOpen();

    /* ── Blank-item guard: no sides have been customized ── */
    if (!designed.length) {
      document.getElementById('dt-blank-confirm')?.remove();
      const ov = document.createElement('div');
      ov.id = 'dt-blank-confirm';
      ov.className = 'dt-imr-overlay';
      ov.innerHTML = `<div class="dt-rbg-panel dt-rbg-panel--md dt-rbg-panel--center">
        <div style="font-size:20px;font-weight:700;margin-bottom:10px;color:var(--dt-text,#222)">No customization detected</div>
        <div style="font-size:14px;line-height:1.5;color:var(--dt-muted,#666);margin-bottom:24px">
          You haven't added any text, images, or other design elements to this product.
          Are you sure you want to add it to your cart without customization?
        </div>
        <div style="display:flex;gap:12px;justify-content:center">
          <button id="dt-blank-cancel" class="dt-btn" style="background:var(--dt-surface,#f5f5f5);color:var(--dt-text,#333);min-width:120px">Go back</button>
          <button id="dt-blank-ok" class="dt-btn dt-btn-primary" style="min-width:160px">Add blank item</button>
        </div>
      </div>`;
      document.body.appendChild(ov);

      ov.querySelector('#dt-blank-cancel').addEventListener('click', () => ov.remove());
      ov.querySelector('#dt-blank-ok').addEventListener('click', async () => {
        const btn = ov.querySelector('#dt-blank-ok');
        btn.disabled = true; btn.textContent = 'Adding…';
        try {
          const qtyEl = $('dt-qty-input');
          const qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: S.variantId, quantity: qty, properties: { _blank_item: 'Yes' } }] }),
          });
          if (!res.ok) {
            let errMsg = 'Cart error: ' + res.status;
            try { const b = await res.json(); errMsg = b.description || b.message || (typeof b.errors === 'string' ? b.errors : b.errors ? JSON.stringify(b.errors) : null) || errMsg; } catch (_) {}
            throw new Error(errMsg);
          }
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          ov.remove();
          celebrateThenGoToCart();
        } catch (err) {
          DEBUG && console.error('[dt] Blank item cart add failed:', err);
          btn.textContent = 'Failed — try again';
          btn.disabled = false;
        }
      });
      return;
    }

    const upcharges = calcUpcharges(designed);
    const sideInfo = designed.map(s => ({
      label: CFG.sideConfigs[s]?.label || s,
      method: S.method === 'engrave' ? 'Laser Engrave' : (CFG.sideConfigs[s]?.decorationMethod || 'Print'),
      dim: SIDE_PHYS[s] ? `${SIDE_PHYS[s].wInches}" × ${SIDE_PHYS[s].hInches}"` : ''
    }));

    const overlay = document.createElement('div');
    overlay.id = 'dt-cart-modal';
    overlay.className = 'dt-imr-overlay';
    overlay.style.setProperty('--dt-imr-n', designed.length);
    if (designed.length > 1) overlay.classList.add('dt-imr--multi');

    const dotsHtml = designed.length > 1
      ? `<div class="dt-imr-dots" id="dt-cart-dots">${designed.map((_, i) => `<button class="dt-imr-dot" aria-label="View side ${i + 1}"></button>`).join('')}</div>`
      : '';

    /* Low-res audit: if any designed side carries a below-threshold image,
       the digital proof becomes REQUIRED — the customer chose "Use Anyway"
       at upload, so route the order through a human-approved proof. */
    const _esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const lowResSides = collectLowResSides(designed);
    /* $1000 proof rule: a line total (product + upcharges, qty-aware)
       greater than $1000 requires a proof. Same forced-checkbox mechanism
       as the low-res rule; executeCartAdd independently re-checks and
       stamps the policy note on the line item. */
    const _reviewLineCents = (CFG.variants?.find(v => v.id === S.variantId)?.price || 0)
      + upcharges.reduce((a, c) => a + (c.cents || 0), 0);
    const overProofLimit = !!CFG.proofVariantId && (_reviewLineCents * getQty()) / 100 > 1000;
    const forceProof = !!((lowResSides.length || overProofLimit) && CFG.proofVariantId);
    const _proofReason = overProofLimit
      ? 'we require proofs on amounts greater than $1,000, so we&rsquo;ll email you a proof to approve before production.'
      : 'because of the image quality note below, we&rsquo;ll email you a proof to approve before production.';

    /* ── UX-Aug15 review modal v2 (Aug 8 spec §3): the two toggles ("request proof" /
       "I confirm") read as unrelated switches and the only Add-to-Cart was the header chip
       on desktop — GA4 lost 21% (desktop) / 32% (mobile) of reviewers between review and
       confirm. Now ONE exclusive choice, two cards, CTA in the card at every width.
       STATE SYNTHESIS: #dt-cart-proof and #dt-cart-approve stay in the DOM as hidden
       checkboxes driven by the cards, so doConfirm / designApproved / executeCartAdd and
       the cart properties (_artist_proof, _proof_required, _design_approved, $5 proof
       variant) are byte-identical to before. The cards container carries the old
       #dt-cart-approve-row id so the existing nudge/shake + scrollIntoView keep working. */
    const _verbFor = () => {
      if (S.method === 'engrave') return 'Engrave';
      const allPrint = sideInfo.every(o => /print/i.test(o.method || ''));
      return allPrint ? 'Print' : 'Make';
    };
    const _verb = _verbFor();
    const _asShownLabel = designed.length > 1
      ? `${_verb} all ${designed.length} sides as shown`
      : `${_verb} as shown`;
    const _hiddenCbAttrs = 'aria-hidden="true" tabindex="-1" style="position:absolute;opacity:0;width:0;height:0;pointer-events:none"';
    const proofHtml = CFG.proofVariantId ? (forceProof ? `
      <input type="checkbox" id="dt-cart-proof" checked disabled ${_hiddenCbAttrs}>
      <div class="dt-imr2-cards dt-imr2-cards--forced" id="dt-cart-approve-row" role="radiogroup" aria-label="Before we make it">
        <div class="dt-imr2-card dt-imr2-card--proof is-on is-locked" role="radio" aria-checked="true" aria-disabled="true" tabindex="0">
          <span class="dt-imr2-card__mark" aria-hidden="true">✓</span>
          <span class="dt-imr2-card__body">
            <span class="dt-imr2-card__title">Digital proof included (+${dtMoney(CFG.proofPrice)})</span>
            <span class="dt-imr2-card__sub dt-imr2-card__sub--amber">Required — ${_proofReason}</span>
            <span class="dt-imr2-card__sub">Adds 1&ndash;2 business days to the estimated ship time.</span>
          </span>
        </div>
      </div>` : `
      <input type="checkbox" id="dt-cart-proof" ${_hiddenCbAttrs}>
      <input type="checkbox" id="dt-cart-approve" ${_hiddenCbAttrs}>
      <div class="dt-imr2-choose">Choose one</div>
      <div class="dt-imr2-cards" id="dt-cart-approve-row" role="radiogroup" aria-label="Choose one">
        <button type="button" class="dt-imr2-card dt-imr2-card--asshown" role="radio" aria-checked="false" data-choice="asshown">
          <span class="dt-imr2-card__mark" aria-hidden="true">✓</span>
          <span class="dt-imr2-card__body">
            <span class="dt-imr2-card__title">${_asShownLabel}</span>
            <span class="dt-imr2-card__sub">I&rsquo;ve verified all spelling, names &amp; dates are correct.</span>
          </span>
        </button>
        <button type="button" class="dt-imr2-card dt-imr2-card--proof" role="radio" aria-checked="false" data-choice="proof">
          <span class="dt-imr2-card__mark" aria-hidden="true">✓</span>
          <span class="dt-imr2-card__body">
            <span class="dt-imr2-card__title">Receive an emailed proof from our experts (+${dtMoney(CFG.proofPrice)})</span>
            <span class="dt-imr2-card__sub">Expect your proof in 1&ndash;2 business days before production begins.</span>
          </span>
        </button>
      </div>`) : `
      <input type="checkbox" id="dt-cart-approve" ${_hiddenCbAttrs}>
      <div class="dt-imr2-cards dt-imr2-cards--single" id="dt-cart-approve-row" role="radiogroup" aria-label="Confirm your design">
        <button type="button" class="dt-imr2-card dt-imr2-card--asshown" role="radio" aria-checked="false" data-choice="asshown">
          <span class="dt-imr2-card__mark" aria-hidden="true">✓</span>
          <span class="dt-imr2-card__body">
            <span class="dt-imr2-card__title">${_asShownLabel}</span>
            <span class="dt-imr2-card__sub">I&rsquo;ve verified all spelling, names &amp; dates are correct.</span>
          </span>
        </button>
      </div>`;
    /* approveHtml is folded into proofHtml above (kept as an empty string so the
       template below is unchanged). */
    const approveHtml = '';

    /* Trust rows: verbatim spell-out of every text/monogram layer per designed side,
       a contrast warning when text ≈ product colour, and light nudges for trailing
       spaces / a future 4-digit year. Read-only; nothing here touches the design. */
    const _spellRows = [];
    let _trailingSpace = false, _futureYear = false, _lowContrast = false;
    const _bgLum = sampleMockupPALum();
    const _nowYear = new Date().getFullYear();
    designed.forEach(slug => {
      const ls = (S.byView[slug] || []).filter(l => l.visible !== false && (l.type === 'text' || l.isMonogram) && ((l.text || l.monoText || '').trim()));
      if (!ls.length) return;
      const label = CFG.sideConfigs[slug]?.label || slug;
      ls.forEach(l => {
        const raw = String(l.text || l.monoText || '');
        _spellRows.push({ side: label, text: raw });
        if (raw !== raw.replace(/[ \t]+$/gm, '')) _trailingSpace = true;
        const yrs = raw.match(/\b(19|20)\d{2}\b/g); if (yrs && yrs.some(y => parseInt(y, 10) > _nowYear)) _futureYear = true;
        if (_bgLum !== null && l.type === 'text' && S.method === 'print') {
          const rgb = hexToRgb(l.color || '#111111');
          if (rgb) { const fg = relLum(rgb.r, rgb.g, rgb.b); const ratio = (Math.max(_bgLum, fg) + 0.05) / (Math.min(_bgLum, fg) + 0.05); if (ratio < 2) _lowContrast = true; }
        }
      });
    });
    const spellHtml = _spellRows.length ? `
      <div class="dt-imr2-spell">
        <div class="dt-imr2-spell__title">Your item will read exactly:</div>
        <div class="dt-imr2-spell__list">${_spellRows.map(r => `<div class="dt-imr2-spell__row">${designed.length > 1 ? `<span class="dt-imr2-spell__side">${_esc(r.side)}</span>` : ''}<span class="dt-imr2-spell__text">${_esc(r.text)}</span></div>`).join('')}</div>
        ${_trailingSpace ? '<div class="dt-imr2-nudge">Heads up: one of your lines ends with a space.</div>' : ''}
        ${_futureYear ? '<div class="dt-imr2-nudge">Heads up: a year in your text is in the future &mdash; double-check it.</div>' : ''}
      </div>` : '';
    const contrastNoticeHtml = _lowContrast ? `
      <div class="dt-imr-check" style="color:#c4870e">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 3.2L18 16.5H2L10 3.2Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M10 8.2v3.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="10" cy="14.2" r="0.9" fill="currentColor"/></svg>
        <span>Some of your text is very close to the product colour &mdash; make sure it&rsquo;s visible in the preview before you continue.</span>
      </div>` : '';

    const lowResNoticeHtml = lowResSides.length ? `
      <div class="dt-imr-check" style="color:#c4870e">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 3.2L18 16.5H2L10 3.2Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M10 8.2v3.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="10" cy="14.2" r="0.9" fill="currentColor"/></svg>
        <span>Image quality: ${lowResSides.map(o => `${_esc(o.name)} (${_esc(o.label)}) is about ${o.dpi} DPI at this print size &mdash; 150+ is recommended`).join('; ')}. It may print less sharp than it looks on screen.${forceProof ? ' A digital proof is included so you can confirm before we make it.' : ''}</span>
      </div>` : '';

    const chargeHtml = upcharges.length
      ? `<div class="dt-imr-charges">${upcharges.map(u => `<div>${u.label}</div>`).join('')}</div>`
      : '';

    overlay.innerHTML = `
      <div class="dt-imr-top">
        <span class="dt-imr-eyebrow">Review your design</span>
        <button class="dt-imr-x" id="dt-cart-close" aria-label="Back to editing">&times;</button>
      </div>
      <div class="dt-imr-main">
        <div class="dt-imr-hero">
          <div class="dt-imr-stage" id="dt-cart-stage"><div class="dt-spinner-ring"></div></div>
          <div class="dt-imr-shadow"></div>
          ${dotsHtml}
        </div>
      </div>
      <div class="dt-imr-bar">
        <div class="dt-imr-meta">
          <div>
            <div class="dt-imr-metaside" id="dt-cart-side">${sideInfo[0].label}</div>
            <div class="dt-imr-metasub" id="dt-cart-sub">${sideInfo[0].method}${sideInfo[0].dim ? ' · ' + sideInfo[0].dim : ''}</div>
          </div>
          ${chargeHtml}
        </div>
        ${spellHtml}
        ${proofHtml}
        ${approveHtml}
        ${contrastNoticeHtml}
        ${lowResNoticeHtml}
        <div class="dt-imr-check dt-imr-check--policy">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7.5" stroke="currentColor" stroke-width="1.4"/><path d="M7 10.2l2 2 4-4.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span>Double-check names, spelling, dates, and artwork. Personalized items are made as submitted, so we can&rsquo;t refund typos or incorrect details.</span>
        </div>
        <div class="dt-imr-guarantee">Backed by our 30-day guarantee: if we make a mistake, the product is defective, or it arrives damaged, we&rsquo;ll make it right.</div>
        <div class="dt-imr-actions">
          <button class="dt-imr-edit" id="dt-cart-back">← Edit design</button>
          <button class="dt-imr-add" id="dt-cart-confirm">Add to Cart</button>
        </div>
      </div>`;

    document.body.appendChild(overlay);
    document.body.classList.add('dt-imr-open', 'dt-imr-single');
    if (lowResSides.length) {
      dtAnalytics.push('dt_lowres_review', {
        dt_lowres_sides: lowResSides.map(o => `${o.slug}:${o.dpi}dpi`).join(','),
        dt_proof_forced: forceProof ? 'yes' : 'no',
      });
    }
    { const _l = $('dt-cart-label'); if (_l) _l.textContent = 'Add to Cart'; }   /* CRO: commit label appears at the commit step */
    /* UX-Aug15 review v2: focus trap + initial focus on the first card (Esc still routes
       through onReviewKey below so zoom-first dismissal is preserved). */
    const _a11yTear = wireModalA11y(overlay, { onEscape: () => {} /* onReviewKey below owns Escape (zoom-first) */, initial: () => overlay.querySelector('.dt-imr2-card[data-choice]') || overlay.querySelector('#dt-cart-confirm') });
    const close = () => {
      if (overlay.querySelector('#dt-cart-close')?.disabled) return;
      try { _a11yTear(); } catch (_) {}
      document.removeEventListener('keydown', onReviewKey);
      document.body.classList.remove('dt-imr-open', 'dt-imr-single');
      updateCartBtn();   /* restore the pre-review "Continue" label */
      overlay.classList.add('is-closing');
      setTimeout(() => overlay.remove(), 200);
    };
    const onReviewKey = e => {
      if (e.key !== 'Escape') return;
      const z = document.querySelector('.dt-imr-zoom');
      if (z) { z.remove(); return; }
      close();
    };
    document.addEventListener('keydown', onReviewKey);
    overlay.querySelector('#dt-cart-close').addEventListener('click', close);
    overlay.querySelector('#dt-cart-back').addEventListener('click', close);
    overlay.querySelector('#dt-cart-close2')?.addEventListener('click', close);

    /* UX-Aug15 review v2: cards drive the hidden checkboxes (exclusive choice). */
    {
      const _pcb = overlay.querySelector('#dt-cart-proof');
      const _acb = overlay.querySelector('#dt-cart-approve');
      const _arow = overlay.querySelector('#dt-cart-approve-row');
      const _cards = Array.from(overlay.querySelectorAll('.dt-imr2-card[data-choice]'));
      const _pick = choice => {
        _cards.forEach(c => { const on = c.dataset.choice === choice; c.classList.toggle('is-on', on); c.setAttribute('aria-checked', on ? 'true' : 'false'); });
        if (_pcb && !_pcb.disabled) _pcb.checked = (choice === 'proof');
        if (_acb) _acb.checked = (choice === 'asshown');
        _arow?.classList.remove('dt-imr-approverow--nudge');
        try { dtAnalytics.push('dt_review_choice', { dt_choice: choice }); } catch (_) {}
      };
      _cards.forEach(c => {
        c.addEventListener('click', () => _pick(c.dataset.choice));
        c.addEventListener('keydown', e => {
          if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); _pick(c.dataset.choice); }
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault(); const i = _cards.indexOf(c); const n = _cards[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : _cards.length - 1)) % _cards.length]; n.focus(); _pick(n.dataset.choice);
          }
        });
      });
    }

    const doConfirm = async () => {
      /* forceProof: low-res image in the design — proof is not optional */
      const wantProof = forceProof || overlay.querySelector('#dt-cart-proof')?.checked || false;
      /* HARD GATE: no proof requested AND design not confirmed = no add.
         Nudge the row instead of disabling the button (CRO). */
      const approveCb = overlay.querySelector('#dt-cart-approve');
      if (!wantProof && approveCb && !approveCb.checked) {
        const row = overlay.querySelector('#dt-cart-approve-row');
        if (row) {
          row.classList.remove('dt-imr-approverow--nudge');
          void row.offsetWidth; /* restart the shake animation */
          row.classList.add('dt-imr-approverow--nudge');
          row.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        toast('approve_required', null, 3500);
        return;
      }
      const designApproved = !wantProof && !!(approveCb && approveCb.checked);
      dtAnalytics.cartConfirm();
      const confirmBtn = overlay.querySelector('#dt-cart-confirm');
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.innerHTML = '<span class="dt-spinner-ring" style="width:16px;height:16px;border-width:2px;margin-right:8px;display:inline-block;vertical-align:middle"></span>Adding to cart…';
      }
      overlay.querySelector('#dt-cart-close')?.setAttribute('disabled', 'true');
      overlay.querySelector('#dt-cart-back')?.setAttribute('disabled', 'true');
      overlay.querySelector('#dt-cart-close2')?.setAttribute('disabled', 'true');
      const cartBtnHtml = cartBtn.innerHTML;
      cartBtn.disabled = true;
      cartBtn.innerHTML = '<span class="dt-spinner-ring" style="width:14px;height:14px;border-width:2px;margin-right:8px;display:inline-block;vertical-align:middle"></span>Adding…';
      try {
        await executeCartAdd(designed, upcharges, wantProof, designApproved);
      } catch (err) {
        if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.textContent = 'Add to cart →'; }
        overlay.querySelector('#dt-cart-close')?.removeAttribute('disabled');
        overlay.querySelector('#dt-cart-back')?.removeAttribute('disabled');
        overlay.querySelector('#dt-cart-close2')?.removeAttribute('disabled');
        cartBtn.innerHTML = cartBtnHtml;
        cartBtn.disabled = false;
        showError('Failed to add to cart. Please try again.');
      }
    };
    overlay.querySelector('#dt-cart-confirm').addEventListener('click', doConfirm);
    overlay.__dtConfirm = doConfirm;

    generateReviewThumbnails(designed, sideInfo, overlay);
  }

  /* Pre-render every decorated side once. Desktop shows ALL sides in a
     row, centered as a group; mobile keeps the one-at-a-time stepper. */
  async function generateReviewThumbnails(designed, sideInfo, overlay) {
    const origView = S.view;
    const pngs = [];
    for (let i = 0; i < designed.length; i++) {
      const sideSlug = designed[i];
      if (S.view !== sideSlug) {
        S.view = sideSlug;
        const src = mockupSrc();
        if (src && mImg.getAttribute('src') !== src) {
          await new Promise(r => {
            mImg.addEventListener('load', () => { try { onMockupLoad(); } catch (e) { DEBUG && console.warn('[dt] onMockupLoad threw:', e.message); } syncCanvas(); r(); }, { once: true });
            mImg.src = src;
          });
        } else computePA();
      }
      await ensureSideLayerPaintSources(sideSlug); /* v2.12.3: the REVIEW preview must show what production delivers */
      pngs[i] = mockupPng(sideSlug, 2, true) || null; /* UX-Aug20: withSim=true for review modal */
    }
    if (S.view !== origView) { S.view = origView; loadMockup(); }

    for (let i = 0; i < pngs.length; i++) {
      if (pngs[i]) pngs[i] = await cropPngToAlpha(pngs[i]);
    }

    const stage = overlay.querySelector('#dt-cart-stage');
    const sideEl = overlay.querySelector('#dt-cart-side');
    const subEl = overlay.querySelector('#dt-cart-sub');
    const dots = overlay.querySelector('#dt-cart-dots');

    stage.innerHTML = pngs.map((png, i) => `
      <figure class="dt-imr-sidefig${i === 0 ? ' is-on' : ''}">
        ${png ? `<img src="${png}" class="dt-imr-heroimg" alt="${sideInfo[i].label} preview">` : `<div class="dt-imr-unavail">Preview unavailable</div>`}
        <figcaption class="dt-imr-sidecap">${sideInfo[i].label}${sideInfo[i].dim ? ' · ' + sideInfo[i].dim : ''}</figcaption>
      </figure>`).join('');

    const zoom = src => {
      const z = document.createElement('div'); z.className = 'dt-imr-zoom';
      z.innerHTML = `<img src="${src}" alt="">`;
      z.addEventListener('click', () => z.remove());
      document.body.appendChild(z);
    };
    stage.querySelectorAll('img').forEach(img => {
      img.addEventListener('click', () => zoom(img.src));
      if (!img.complete) img.addEventListener('load', () => positionImrPanel(overlay), { once: true });
    });

    function show(i) {
      stage.querySelectorAll('.dt-imr-sidefig').forEach((f, x) => f.classList.toggle('is-on', x === i));
      sideEl.textContent = sideInfo[i].label;
      subEl.textContent = sideInfo[i].method + (sideInfo[i].dim ? ' · ' + sideInfo[i].dim : '');
      if (dots) Array.prototype.forEach.call(dots.children, (d, x) => { d.className = 'dt-imr-dot' + (x === i ? ' is-on' : ''); });
      positionImrPanel(overlay);
    }

    if (dots) Array.prototype.forEach.call(dots.children, (d, x) => d.addEventListener('click', () => show(x)));

    /* Swipe between sides on touch — the dots alone read as a "slider",
       so a horizontal swipe on the hero steps sides too. */
    let _cur = 0, _tx = null, _ty = null;
    const _show = show;
    function showAt(i) { _cur = Math.max(0, Math.min(pngs.length - 1, i)); _show(_cur); }
    stage.addEventListener('touchstart', e => {
      if (e.touches.length !== 1) { _tx = null; return; }
      _tx = e.touches[0].clientX; _ty = e.touches[0].clientY;
    }, { passive: true });
    stage.addEventListener('touchend', e => {
      if (_tx === null || !e.changedTouches.length) return;
      const dx = e.changedTouches[0].clientX - _tx;
      const dy = e.changedTouches[0].clientY - _ty;
      _tx = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) showAt(_cur + (dx < 0 ? 1 : -1));
    }, { passive: true });
    if (dots) Array.prototype.forEach.call(dots.children, (d, x) => d.addEventListener('click', () => { _cur = x; }));

    show(0);
    positionImrPanel(overlay);
    watchImrPanel(overlay);
  }

  /* Full-screen enlarged review: the product mockup big enough to read, plus the
     design-only detail crop so the customer can verify spelling/artwork. */
  function openReviewLightbox(fullSrc, focusSrc, label) {
    document.getElementById('dt-review-lightbox')?.remove();
    const ov = document.createElement('div');
    ov.id = 'dt-review-lightbox'; ov.className = 'dt-review-lightbox';
    const detail = (focusSrc && focusSrc !== fullSrc)
      ? `<div class="dt-review-lightbox__cap">Design detail — check spelling &amp; artwork</div>
         <img class="dt-review-lightbox__img dt-review-lightbox__img--focus" src="${focusSrc}" alt="Design detail">`
      : '';
    ov.innerHTML = `<div class="dt-review-lightbox__inner">
      <button class="dt-review-lightbox__close" id="dt-review-lb-close" aria-label="Close">&times;</button>
      <div class="dt-review-lightbox__label">${label || 'Design'}</div>
      <img class="dt-review-lightbox__img" src="${fullSrc}" alt="${label || 'Design'} preview">
      ${detail}
    </div>`;
    document.body.appendChild(ov);
    const close = () => ov.remove();
    ov.querySelector('#dt-review-lb-close').onclick = close;
    ov.onclick = e => { if (e.target === ov) close(); };
  }

  /* A11y (WCAG 4.1.3): visually-hidden live region so production/cart progress
     is announced to screen readers — button-text swaps alone are silent. */
  let _liveRegion = null;
  function announceStatus(msg) {
    if (!_liveRegion) {
      _liveRegion = document.createElement('div');
      _liveRegion.id = 'dt-live-status';
      _liveRegion.setAttribute('role', 'status');
      _liveRegion.setAttribute('aria-live', 'polite');
      _liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;';
      document.body.appendChild(_liveRegion);
    }
    _liveRegion.textContent = msg || '';
  }

  /* ══════════════════════════════════════════════════════════════════
   * CART WAIT OVERLAY — "Making it yours"
   * Brand-chrome progress dialog shown while add-to-cart builds and
   * uploads production files. The time estimate ADAPTS: every finished
   * run records its per-side duration in localStorage and the next
   * prediction uses the median for the same method, so "about N seconds"
   * gets more accurate per customer/device. The bar is honest — a
   * time-eased fill capped below 100% plus floor bumps on real phase
   * transitions; it never moves backward and never fakes completion.
   * ══════════════════════════════════════════════════════════════════ */
  let _dw = null;

  function _dwStats() {
    try { return JSON.parse(localStorage.getItem('dt_wait_stats') || '[]'); } catch (e) { return []; }
  }
  function _dwRecord(totalMs, sides) {
    try {
      const arr = _dwStats();
      arr.push({ t: totalMs, s: Math.max(1, sides), m: S.method });
      while (arr.length > 12) arr.shift();
      localStorage.setItem('dt_wait_stats', JSON.stringify(arr));
    } catch (e) {}
  }
  function _dwPredict(sides) {
    sides = Math.max(1, sides);
    const same = _dwStats().filter(r => r.m === S.method);
    if (same.length >= 2) {
      const perSide = same.map(r => r.t / r.s).sort((a, b) => a - b);
      const med = perSide[Math.floor(perSide.length / 2)];
      return Math.min(90000, Math.max(8000, med * sides));
    }
    /* No history yet: engrave sides round-trip the vectorizer — budget more */
    const per = S.method === 'engrave' ? 11000 : 8000;
    return Math.min(90000, 3000 + per * sides);
  }

  const _DW_ROWS = ['Design saved', 'Preview captured', 'Building production files', 'Adding to cart'];
  const _DW_KEYS = ['save', 'mockup', 'prod', 'cart'];
  const _DW_ICON_CHECK = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8.5l3.2 3.2L13 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const _DW_ICON_NOW = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 1.8a6.2 6.2 0 1 1-6.2 6.2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  const _DW_ICON_TODO = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="5" stroke="currentColor" stroke-width="1.5"/></svg>';

  function _dwBuild() {
    const el = document.createElement('div');
    el.className = 'dt-wait';
    el.id = 'dt-wait';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', 'Preparing your order');
    el.innerHTML =
      '<div class="dt-wait__card">' +
        '<div class="dt-wait__main">' +
          '<div class="dt-wait__head">' +
            '<div class="dt-wait__spin" aria-hidden="true"></div>' +
            '<div><div class="dt-wait__title">Making it yours</div>' +
            '<div class="dt-wait__sub"></div></div>' +
          '</div>' +
          '<div class="dt-wait__bar" aria-hidden="true"><div class="dt-wait__fill"></div></div>' +
          '<div class="dt-wait__steps">' +
            _DW_ROWS.map((r, i) =>
              `<div class="dt-wait__step" data-row="${i}"><span class="dt-wait__ico">${_DW_ICON_TODO}</span><span class="dt-wait__lbl">${r}</span></div>`
            ).join('') +
          '</div>' +
          '<div class="dt-wait__foot">Your design is being locked in exactly as you made it. Keep this window open.</div>' +
        '</div>' +
        '<div class="dt-wait__err" hidden>' +
          '<div class="dt-wait__errtitle">Couldn’t finish</div>' +
          '<div class="dt-wait__errsub">Nothing was added to your cart. Your design is safe.</div>' +
          '<div class="dt-wait__errdetail"></div>' +
          '<button type="button" class="dt-rbg-use-btn dt-wait__retry">Try Again</button>' +
          '<button type="button" class="dt-wait__dismiss">Close</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(el);
    return el;
  }

  function _dwSetRow(idx) {
    if (!_dw) return;
    _dw.el.querySelectorAll('.dt-wait__step').forEach((row, i) => {
      row.classList.toggle('is-done', i < idx);
      row.classList.toggle('is-now', i === idx);
      row.querySelector('.dt-wait__ico').innerHTML = i < idx ? _DW_ICON_CHECK : (i === idx ? _DW_ICON_NOW : _DW_ICON_TODO);
    });
  }

  function _dwTick() {
    if (!_dw) return;
    const elapsed = Date.now() - _dw.t0;
    /* Time-eased fill toward the prediction, then a slow crawl past it */
    let pct = elapsed <= _dw.est
      ? (elapsed / _dw.est) * 0.9
      : 0.9 + 0.06 * (1 - Math.exp(-(elapsed - _dw.est) / _dw.est));
    pct = Math.max(pct, _dw.floor);
    if (pct > _dw.pct) { /* monotonic — never move backward */
      _dw.pct = Math.min(pct, 0.97);
      _dw.el.querySelector('.dt-wait__fill').style.width = (_dw.pct * 100).toFixed(1) + '%';
    }
    if (!_dw.slow && elapsed > Math.max(45000, _dw.est * 1.6)) {
      _dw.slow = true;
      _dw.el.querySelector('.dt-wait__title').textContent = 'Still working';
      _dw.el.querySelector('.dt-wait__sub').textContent = 'Multi-side designs take a little longer. Everything is running normally.';
    }
  }

  function dtWaitOpen(sides, retryFn) {
    dtWaitClose();
    const el = _dwBuild();
    const est = _dwPredict(sides);
    _dw = { el, t0: Date.now(), sides: Math.max(1, sides), est, pct: 0, floor: 0.03,
            slow: false, retry: retryFn || null, prodSides: {}, row: -1 };
    el.querySelector('.dt-wait__sub').textContent = 'About ' + Math.max(10, Math.round(est / 5000) * 5) + ' seconds.';
    _dwSetRow(0);
    _dw.timer = setInterval(_dwTick, 250);
    _dwTick();
  }

  /* Classify live status strings into checklist rows + progress floors */
  function dtWaitStatus(msg) {
    if (!_dw || !msg) return;
    let key = 'prod';
    if (/saving design/i.test(msg)) key = 'save';
    else if (/mockup|preview/i.test(msg)) key = 'mockup';
    else if (/adding to cart/i.test(msg)) key = 'cart';
    const idx = _DW_KEYS.indexOf(key);
    if (idx > _dw.row) { _dw.row = idx; _dwSetRow(idx); }
    if (key === 'save')   _dw.floor = Math.max(_dw.floor, 0.06);
    if (key === 'mockup') _dw.floor = Math.max(_dw.floor, 0.12);
    if (key === 'cart')   _dw.floor = Math.max(_dw.floor, 0.94);
    if (key === 'prod') {
      /* Per-side floor bumps + " — Side" suffix on the row label */
      let lbl = '';
      for (const s of CFG.sides) {
        const sl = CFG.sideConfigs[s]?.label || s;
        if (sl && msg.indexOf(sl) !== -1) { lbl = sl; break; }
      }
      if (lbl) {
        _dw.prodSides[lbl] = true;
        const done = Math.max(0, Object.keys(_dw.prodSides).length - 1);
        _dw.floor = Math.max(_dw.floor, 0.18 + 0.72 * (done / _dw.sides));
        const row = _dw.el.querySelector('.dt-wait__step[data-row="2"] .dt-wait__lbl');
        if (row) row.textContent = 'Building production files — ' + lbl;
      } else {
        _dw.floor = Math.max(_dw.floor, 0.18);
      }
    }
    _dwTick();
  }

  function dtWaitDone() {
    if (!_dw) return;
    const totalMs = Date.now() - _dw.t0;
    _dwRecord(totalMs, _dw.sides);
    try {
      dtAnalytics.push('dt_cart_wait', {
        dt_wait_ms: totalMs,
        dt_wait_est_ms: _dw.est,
        dt_wait_sides: _dw.sides,
      });
    } catch (e) {}
    clearInterval(_dw.timer);
    _dwSetRow(4); /* all four rows checked */
    _dw.el.querySelector('.dt-wait__fill').style.width = '100%';
    const el = _dw.el;
    _dw = null;
    setTimeout(() => { el.classList.add('is-leaving'); setTimeout(() => el.remove(), 220); }, 180);
  }

  /* rev 14: what the overlay shows when a file upload failed twice. Names
     the file (serial prefix stripped) and the two things that fix it most
     often — a flaky connection and a VPN / ad blocker that blocks the
     upload endpoints. The draft is autosaved on this device before this
     shows, so "try again" and "reload the page" both keep the design. */
  function uploadFailDetail(file) {
    return 'Couldn’t upload ' + (file || 'your design files') +
      '. Check your connection, turn off any VPN or ad blocker, then try again. ' +
      'Your design is saved on this device.';
  }

  function dtWaitFail(detail) {
    if (!_dw) return;
    clearInterval(_dw.timer);
    const { el, retry } = _dw;
    _dw = null;
    el.querySelector('.dt-wait__main').style.display = 'none';
    const err = el.querySelector('.dt-wait__err');
    err.hidden = false;
    el.querySelector('.dt-wait__errdetail').textContent = (detail || '').slice(0, 180);
    const retryBtn = el.querySelector('.dt-wait__retry');
    const dismiss = el.querySelector('.dt-wait__dismiss');
    if (retry) retryBtn.addEventListener('click', () => { el.remove(); Promise.resolve(retry()).catch(() => {}); });
    else retryBtn.style.display = 'none';
    dismiss.addEventListener('click', () => el.remove());
    try { retryBtn.focus({ preventScroll: true }); } catch (e) {}
  }

  function dtWaitClose() {
    if (_dw) { clearInterval(_dw.timer); _dw.el.remove(); _dw = null; }
    document.getElementById('dt-wait')?.remove();
  }

  /* ══════════════════════════════════════════════════════════════════
   * PROOF SHEET v2.12.0 — supplier-template layout (YETI reference PDF,
   * Brandon Aug 3 2026; spec: claude/PLAN-PROOF-SHEET-YETI-LAYOUT.md rev 2).
   * Left column: ITEM/title/color box, imprint-location table (acumatica
   * metafields via CFG.sideConfigs), red ARTWORK DIMENSIONS (measured),
   * disclaimer, serial line. Right panel: method-checkbox legend +
   * designed mockups placed by their NON-ALPHA (or non-background)
   * bounding box at a common physical scale, dashed imprint rect overlay.
   * ══════════════════════════════════════════════════════════════════ */

  /* Hi-res proof capture around mockupPng. Picks a scale that targets
     ~PROOF_TARGET_PX of frame width (mockupPng's internal budget clamp
     keeps it iOS-safe), then reads the geometry contract mockupPng
     recorded so the print area comes back as FRACTIONS of the exact
     canvas it drew — never reconstructed denominators. */
  const PROOF_TARGET_PX = 1700;
  function captureProofMockup(sideSlug) {
    let scale = 1;
    try {
      const dpr = window.devicePixelRatio || 1;
      const rs = S.rscaleByView[sideSlug] || S.rscale || 1;
      const cwGuess = mImg.offsetWidth
        || Math.round((S._mockupBaseNW || mImg.naturalWidth || 1000) * rs);
      if (cwGuess > 0) scale = Math.min(4, Math.max(1, PROOF_TARGET_PX / (cwGuess * dpr)));
    } catch (_) {}
    const png = mockupPng(sideSlug, scale);
    if (!png) return null;
    const g = mockupPng.lastGeom;
    let paFrac = null;
    if (g && g.pa && g.cw > 0 && g.ch > 0) {
      paFrac = { x: g.pa.x / g.cw, y: g.pa.y / g.ch, w: g.pa.w / g.cw, h: g.pa.h / g.ch };
    }
    return { png, paFrac };
  }

  /* Content bounds + crop of a loaded mockup image. TWO-STAGE (plan rev 2):
     1) alpha scan (alpha>16 gate, downscaled — same pattern as trimToContent);
     2) if the frame is effectively fully opaque (CDN product photos are often
        JPG / white-bg PNG), LUMINANCE fallback — background sampled from the
        four corners, bounds = pixels deviating from it. Returns crop geometry
        in NATURAL px plus a cropped data URL; falls back to the full frame
        (trimmed:false) so an untrimmable side never breaks layout. */
  function _proofTrimCrop(img) {
    const nw = img.naturalWidth, nh = img.naturalHeight;
    const full = { png: null, fmt: 'PNG', bx: 0, by: 0, bw: nw || 1, bh: nh || 1, nw: nw || 1, nh: nh || 1, trimmed: false };
    if (!nw || !nh || nw * nh < 100) return full;
    const SCAN = 1200;
    const k = Math.min(1, SCAN / Math.max(nw, nh));
    const sw = Math.max(1, Math.round(nw * k)), sh = Math.max(1, Math.round(nh * k));
    const c = document.createElement('canvas'); c.width = sw; c.height = sh;
    const cx = c.getContext('2d');
    cx.drawImage(img, 0, 0, sw, sh);
    let data;
    try { data = cx.getImageData(0, 0, sw, sh).data; } catch (_) { return full; }

    let minX = sw, minY = sh, maxX = -1, maxY = -1;
    const scanBounds = test => {
      minX = sw; minY = sh; maxX = -1; maxY = -1;
      for (let y = 0; y < sh; y++) {
        for (let x = 0; x < sw; x++) {
          const i = (y * sw + x) * 4;
          if (test(i)) {
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
      }
      return maxX >= minX && maxY >= minY;
    };

    /* Stage 1 — alpha */
    let found = scanBounds(i => data[i + 3] > 16);
    let meaningful = found && (maxX - minX + 1 < sw * 0.985 || maxY - minY + 1 < sh * 0.985);
    let viaAlpha = meaningful;

    /* Stage 2 — luminance vs corner-sampled background */
    if (!meaningful) {
      const P = Math.max(3, Math.round(Math.min(sw, sh) * 0.02));
      const corner = (x0, y0) => {
        let r = 0, g = 0, b = 0, n = 0;
        for (let y = y0; y < y0 + P && y < sh; y++) for (let x = x0; x < x0 + P && x < sw; x++) {
          const i = (y * sw + x) * 4;
          if (data[i + 3] > 16) { r += data[i]; g += data[i + 1]; b += data[i + 2]; n++; }
        }
        return n ? [r / n, g / n, b / n] : null;
      };
      const cs = [corner(0, 0), corner(sw - P, 0), corner(0, sh - P), corner(sw - P, sh - P)].filter(Boolean);
      if (cs.length >= 3) {
        /* Corners must agree (uniform studio background) or trimming is unsafe */
        let agree = true;
        for (let a = 0; a < cs.length && agree; a++) for (let b2 = a + 1; b2 < cs.length; b2++) {
          const d = Math.abs(cs[a][0] - cs[b2][0]) + Math.abs(cs[a][1] - cs[b2][1]) + Math.abs(cs[a][2] - cs[b2][2]);
          if (d > 90) { agree = false; break; }
        }
        if (agree) {
          const bg = [0, 1, 2].map(ch2 => cs.reduce((s, cc) => s + cc[ch2], 0) / cs.length);
          const TH = 34;
          found = scanBounds(i => data[i + 3] > 16 && (
            Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]) > TH * 3 * 0.6
          ));
          meaningful = found && (maxX - minX + 1 < sw * 0.99 || maxY - minY + 1 < sh * 0.99);
        }
      }
    }
    if (!meaningful) return full;

    /* Small pad so antialiased edges survive, then map back to natural px */
    const pad = Math.max(2, Math.round(Math.max(maxX - minX, maxY - minY) * 0.015));
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(sw - 1, maxX + pad); maxY = Math.min(sh - 1, maxY + pad);
    let bx = Math.max(0, Math.floor(minX / k)), by = Math.max(0, Math.floor(minY / k));
    let bx2 = Math.min(nw - 1, Math.ceil(maxX / k)), by2 = Math.min(nh - 1, Math.ceil(maxY / k));
    const bw = bx2 - bx + 1, bh = by2 - by + 1;
    if (bw <= 4 || bh <= 4) return full;
    try {
      const tc = document.createElement('canvas'); tc.width = bw; tc.height = bh;
      tc.getContext('2d').drawImage(img, bx, by, bw, bh, 0, 0, bw, bh); /* crop from full res */
      /* Opaque crop (luminance path) -> JPEG: jsPDF embeds canvas PNGs
         without recompression, and photographic frames ballooned the sheet
         to 10MB+. Transparency (alpha path) must stay PNG. */
      const png = viaAlpha ? tc.toDataURL('image/png') : tc.toDataURL('image/jpeg', 0.9);
      const fmt = viaAlpha ? 'PNG' : 'JPEG';
      tc.width = 0;
      return { png, fmt, bx, by, bw, bh, nw, nh, trimmed: true };
    } catch (_) { return full; }
  }

  /* Measured artwork bounds for one side, in inches + mm. MUST run while the
     side's pa/rscale caches are fresh (inside the cart loop, right after its
     syncCanvas/computePA — resize deletes non-current-side caches). Uses the
     canonical CSS-px -> inches ratio (pa px per physical inch), rotation-aware
     via rotBox, and INTERSECTS with the print area: the mockup and every
     production file clip to pa, so the red block must not overstate. */
  function _sideArtworkDims(sideSlug) {
    try {
      const phys = SIDE_PHYS[sideSlug];
      const pa = (S.view === sideSlug ? S.pa : S.paByView[sideSlug]) || null;
      if (!phys || !pa || !pa.w || !pa.h) return null;
      const vis = (S.byView[sideSlug] || []).filter(l => l.visible !== false);
      if (!vis.length) return null;
      let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
      vis.forEach(l => {
        const b = rotBox(bbox(l), l);
        if (b.x < x1) x1 = b.x; if (b.y < y1) y1 = b.y;
        if (b.x + b.w > x2) x2 = b.x + b.w; if (b.y + b.h > y2) y2 = b.y + b.h;
      });
      if (!(x2 > x1 && y2 > y1)) return null;
      const ix1 = Math.max(x1, pa.x), iy1 = Math.max(y1, pa.y);
      const ix2 = Math.min(x2, pa.x + pa.w), iy2 = Math.min(y2, pa.y + pa.h);
      if (!(ix2 > ix1 && iy2 > iy1)) return null;
      const wIn = (ix2 - ix1) * phys.wInches / pa.w;
      const hIn = (iy2 - iy1) * phys.hInches / pa.h;
      return {
        wIn, hIn, mm: wIn * 25.4,
        clipped: (ix1 > x1 + 0.5 || iy1 > y1 + 0.5 || ix2 < x2 - 0.5 || iy2 < y2 - 0.5),
      };
    } catch (_) { return null; }
  }

  /* Sheet-level data for the proof shell. availableMethods comes from the RAW
     imprint_type metafield, whole-token parsed (v2.10.0 rule — the collapsed
     printMethodName hides secondary methods). colorLabel falls back to
     variant.title for single-option products (colorOptionIndex undefined);
     named colorLabel deliberately — colorName(hex) already exists (~L1834). */
  function _proofSheetData(serial, usedMethods, sideSlugs) {
    const v = (CFG.variants || []).find(vv => vv.id === S.variantId) || null;
    const sku = (v && v.sku) ? String(v.sku) : '';
    let colorLabel = '';
    if (v) {
      if (typeof CFG.colorOptionIndex === 'number' && v.options && v.options[CFG.colorOptionIndex] != null) {
        colorLabel = String(v.options[CFG.colorOptionIndex]);
      } else if (v.title && v.title !== 'Default Title') {
        colorLabel = String(v.title);
      }
    }
    const CANON = ['Laser Engrave', 'Cylindrical Print', 'Flatbed Print Hard', 'Flatbed Print Soft',
      'UV DTF Print', 'Vinyl Print', 'DTF Print', 'Direct to Film'];
    const toks = String(CFG.imprintTypeRaw || '').split(',').map(s => s.trim());
    let avail = CANON.filter(m => toks.indexOf(m) !== -1);
    const used = [...(usedMethods || [])].filter(Boolean);
    used.forEach(m => { if (avail.indexOf(m) === -1) avail.push(m); });
    if (!avail.length) avail = used.slice();
    const sides = (sideSlugs || []).map(slug => ({
      slug,
      label: CFG.sideConfigs[slug]?.label || slug,
      phys: SIDE_PHYS[slug] || null,
    }));
    let dateStr = '';
    try { dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch (_) {}
    return { sku, title: String(CFG.productTitle || ''), colorLabel, serial, date: dateStr,
      sides, availableMethods: avail, usedMethods: used };
  }

  /* Shared shell: left info column + right gray panel + method legend.
     Returns the panel's remaining content rect for the caller's mockups.
     data.artDims: [{label, wIn, hIn, mm, clipped}]; data.artNote: optional
     small gray note under the artwork block (bulk uses it). */
  const _PROOF_RED = [226, 35, 26];
  function _drawProofShell(doc, data) {
    const L_X = 0.35, L_W = 3.7;
    const P_X = 4.3, P_Y = 0.32, P_W = 10.68 - P_X, P_H = 8.18 - P_Y;

    doc.setFont('helvetica', 'bold');

    /* ── Info box ── */
    doc.setFontSize(12);
    const innerW = L_W - 0.3;
    const infoLines = [];
    if (data.sku) infoLines.push('ITEM: ' + data.sku);
    (doc.splitTextToSize(String(data.title || ''), innerW) || []).slice(0, 3).forEach(t => infoLines.push(t));
    if (data.colorLabel) infoLines.push(data.colorLabel);
    const ilh = 0.235, ipad = 0.14;
    const boxH = ipad * 2 + Math.max(1, infoLines.length) * ilh;
    doc.setFillColor(233, 233, 233);
    doc.roundedRect(L_X, 0.35, L_W, boxH, 0.09, 0.09, 'F');
    doc.setTextColor(25);
    infoLines.forEach((t, i) => doc.text(t, L_X + 0.16, 0.35 + ipad + ilh * i + 0.17));

    /* ── Imprint table (rows from data.sides = designedSides — NOT proof
       entries, so a failed capture can never drop/desync a row) ── */
    let y = 0.35 + boxH + 0.42;
    const ORD = ['Standard', 'Secondary', 'Third', 'Fourth'];
    const colSplit = L_X + 2.02;
    const rowH = 0.27;
    const tableTop = y - 0.19;
    doc.setFontSize(9.5);
    (data.sides || []).forEach((s, i) => {
      doc.setTextColor(25);
      doc.text(`${ORD[i] || 'Additional'} Imprint Location`, L_X + 0.04, y);
      doc.text(String(s.label || ''), colSplit + 0.12, y);
      y += rowH;
      if (s.phys) {
        doc.text('Maximum Imprint Size', L_X + 0.04, y);
        doc.text(`${s.phys.wInches}" (W) x ${s.phys.hInches}" (H)`, colSplit + 0.12, y);
        y += rowH;
      }
      doc.setDrawColor(150); doc.setLineWidth(0.008);
      doc.line(L_X, y - 0.17, L_X + L_W, y - 0.17);
    });
    if ((data.sides || []).length) {
      doc.setDrawColor(150); doc.setLineWidth(0.008);
      doc.line(colSplit, tableTop, colSplit, y - 0.17);
      doc.line(L_X, tableTop, L_X + L_W, tableTop);
    }

    /* ── ARTWORK DIMENSIONS ── */
    y += 0.22;
    doc.setTextColor(_PROOF_RED[0], _PROOF_RED[1], _PROOF_RED[2]);
    doc.setFontSize(11.5);
    doc.text('ARTWORK DIMENSIONS', L_X, y);
    y += 0.27;
    doc.setFontSize(9);
    let anyClipped = false;
    (data.artDims || []).forEach(a => {
      if (!a) return;
      if (a.clipped) anyClipped = true;
      const line = `${String(a.label || '').toUpperCase()} // ${a.wIn.toFixed(2)}" W X ${a.hIn.toFixed(2)}" H // ${a.mm.toFixed(1)} MM. WIDE`;
      (doc.splitTextToSize(line, L_W) || [line]).forEach(t => { doc.text(t, L_X, y); y += 0.21; });
      y += 0.03;
    });
    if (!(data.artDims || []).filter(Boolean).length) {
      doc.text('SEE PLACEMENT PANEL', L_X, y); y += 0.24;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5); doc.setTextColor(120);
    if (anyClipped) { doc.text('* Artwork sized to the imprint area (extends past it in the design).', L_X, y); y += 0.18; }
    if (data.artNote) { doc.text(String(data.artNote), L_X, y); y += 0.18; }
    doc.setFont('helvetica', 'bold');

    /* ── Disclaimer + identity ── */
    doc.setTextColor(_PROOF_RED[0], _PROOF_RED[1], _PROOF_RED[2]);
    doc.setFontSize(12);
    doc.text('VISUAL LAYOUT REFERENCE ONLY', L_X + L_W / 2, 7.72, { align: 'center' });
    doc.text('ACTUAL PRODUCT MAY VARY', L_X + L_W / 2, 7.97, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5); doc.setTextColor(130);
    doc.text(`PROOF — ${data.serial || ''}${data.date ? ' — ' + data.date : ''}`, L_X, 8.32);
    doc.setFont('helvetica', 'bold');

    /* ── Panel ── */
    doc.setFillColor(242, 242, 242);
    doc.rect(P_X, P_Y, P_W, P_H, 'F');

    /* ── Method legend ── */
    const methods = (data.availableMethods || []).slice(0, 6);
    let legendBottom = P_Y + 0.1;
    if (methods.length) {
      doc.setFontSize(9.5);
      let maxTextW = 0;
      methods.forEach(m => { maxTextW = Math.max(maxTextW, doc.getTextWidth(String(m).toUpperCase())); });
      const cb = 0.19, lrowH = 0.3;
      const legW = Math.min(P_W - 0.3, 0.12 + cb + 0.1 + maxTextW + 0.14);
      const legH = 0.1 + methods.length * lrowH + 0.04;
      const legX = P_X + P_W - legW - 0.14, legY = P_Y + 0.14;
      doc.setFillColor(214, 214, 214);
      doc.rect(legX, legY, legW, legH, 'F');
      methods.forEach((m, i) => {
        const cby = legY + 0.1 + i * lrowH;
        const cbx = legX + 0.12;
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(60); doc.setLineWidth(0.012);
        doc.rect(cbx, cby, cb, cb, 'FD');
        if ((data.usedMethods || []).indexOf(m) !== -1) {
          doc.setDrawColor(20); doc.setLineWidth(0.022);
          doc.line(cbx + 0.035, cby + 0.035, cbx + cb - 0.035, cby + cb - 0.035);
          doc.line(cbx + cb - 0.035, cby + 0.035, cbx + 0.035, cby + cb - 0.035);
        }
        doc.setTextColor(35);
        doc.text(String(m).toUpperCase(), cbx + cb + 0.1, cby + cb - 0.045);
      });
      legendBottom = legY + legH;
    }

    return {
      panel: { x: P_X, y: P_Y, w: P_W, h: P_H },
      content: {
        x: P_X + 0.15,
        y: legendBottom + 0.12,
        w: P_W - 0.3,
        h: (P_Y + P_H) - (legendBottom + 0.12) - 0.1,
      },
    };
  }

  /* Prep a proof entry for panel placement: load, trim, physical scale basis.
     ppi = trimmed-space px per PRODUCT inch, derived from the pa fraction +
     the side's physical imprint width — the common scale must be physical,
     never raw pixels (two 1000px photos can differ in px-per-inch). */
  async function _prepProofEntry(e) {
    let img = null;
    try { img = await _bulkLoadImg(e.png); } catch (_) { return null; }
    let crop = null;
    try { crop = _proofTrimCrop(img); } catch (_) {}
    if (!crop) {
      crop = { png: null, bx: 0, by: 0, bw: img.naturalWidth || 1, bh: img.naturalHeight || 1,
        nw: img.naturalWidth || 1, nh: img.naturalHeight || 1, trimmed: false };
    }
    let ppi = null;
    if (e.paFrac && e.phys && e.phys.wInches > 0) {
      const paPxW = e.paFrac.w * crop.nw;
      if (paPxW > 0) ppi = paPxW / e.phys.wInches;
    }
    return { e, crop, ppi };
  }

  /* Draw one prepped entry into a slot rect; returns nothing. commonScale is
     sheet-inches per product-inch (null -> independent aspect-fit). */
  function _drawProofSlot(doc, p, slot, commonScale, sideNo) {
    const { e, crop } = p;
    let w, h;
    if (commonScale && p.ppi) {
      w = (crop.bw / p.ppi) * commonScale;
      h = (crop.bh / p.ppi) * commonScale;
    } else {
      const ar = crop.bw / Math.max(1, crop.bh);
      w = slot.w; h = w / ar;
      if (h > slot.imgH) { h = slot.imgH; w = h * ar; }
    }
    if (w > slot.w) { h *= slot.w / w; w = slot.w; }
    if (h > slot.imgH) { w *= slot.imgH / h; h = slot.imgH; }
    const dx = slot.x + (slot.w - w) / 2;
    const dy = slot.y + (slot.imgH - h) / 2;
    try { doc.addImage(crop.png || e.png, crop.png ? (crop.fmt || 'PNG') : 'PNG', dx, dy, w, h); } catch (_) {}

    /* Dashed imprint-area rectangle (white underlay so it reads on dark and
       light products alike) — pa fraction mapped through the trim offset. */
    if (e.paFrac && crop.bw > 0 && crop.bh > 0) {
      const rx = dx + ((e.paFrac.x * crop.nw - crop.bx) / crop.bw) * w;
      const ry = dy + ((e.paFrac.y * crop.nh - crop.by) / crop.bh) * h;
      const rw = (e.paFrac.w * crop.nw / crop.bw) * w;
      const rh = (e.paFrac.h * crop.nh / crop.bh) * h;
      if (rw > 0.05 && rh > 0.05) {
        try {
          doc.setLineDashPattern([], 0);
          doc.setDrawColor(255); doc.setLineWidth(0.024);
          doc.rect(rx, ry, rw, rh);
          doc.setDrawColor(45); doc.setLineWidth(0.011);
          doc.setLineDashPattern([0.07, 0.05], 0);
          doc.rect(rx, ry, rw, rh);
          doc.setLineDashPattern([], 0);
        } catch (_) {}
      }
    }

    /* Caption. v2.12.1: the size · method sub-caption is REMOVED (Brandon,
       Aug 4) — imprint size and method already live in the left table and the
       legend. e.sub stays on the entries, deliberately unrendered. */
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30); doc.setFontSize(11);
    doc.text(`${String(e.label || '').toUpperCase()}/SIDE ${sideNo}`,
      slot.x + slot.w / 2, slot.y + slot.imgH + 0.24, { align: 'center' });
  }

  /* Build the single customer-facing PROOF file: every designed side on one
     landscape letter sheet, SIDE BY SIDE, each labeled with its location and
     imprint details. Shared by the single-item and bulk cart flows (bulk's
     variable side shows the longest-name render). Returns a PDF data URL,
     or null when there is nothing to lay out. Non-fatal by design — callers
     wrap it in try/catch and skip the _proof_file prop on failure. */
  async function buildProofSheetPdf(entries, serial, data) {
    const list = (entries || []).filter(e => e && e.png);
    if (!list.length) return null;
    const jsPDF = await waitForJsPDF();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'in', format: 'letter' }); /* 11 x 8.5 */

    data = data || _proofSheetData(serial, list.map(e => e.method), list.map(e => e.slug).filter(Boolean));
    data.artDims = list.filter(e => e.artIn).map(e => ({
      label: e.label, wIn: e.artIn.wIn, hIn: e.artIn.hIn, mm: e.artIn.mm, clipped: e.artIn.clipped,
    }));
    const shell = _drawProofShell(doc, data);

    /* Equal-width slots across the panel content area. */
    const n = list.length, gap = 0.25;
    const capBand = 0.42; /* v2.12.1: sub-caption removed — mockups get the height back */
    const slotW = (shell.content.w - gap * (n - 1)) / n;
    const imgH = shell.content.h - capBand;

    const prepped = [];
    for (const e of list) prepped.push(await _prepProofEntry(e));

    /* Common PHYSICAL scale (sheet-in per product-in) — only when every
       loadable slot has a physical basis; otherwise per-slot aspect-fit so
       one untrimmable/unmeasurable side never shrinks the rest. */
    const valid = prepped.filter(Boolean);
    if (!valid.length) return null;
    let common = null;
    if (valid.every(p => p.ppi)) {
      common = Infinity;
      valid.forEach(p => {
        const wI = p.crop.bw / p.ppi, hI = p.crop.bh / p.ppi;
        common = Math.min(common, slotW / wI, imgH / hI);
      });
      if (!isFinite(common) || common <= 0) common = null;
    }

    for (let i = 0; i < prepped.length; i++) {
      const p = prepped[i];
      if (!p) continue;
      const slot = {
        x: shell.content.x + i * (slotW + gap),
        y: shell.content.y,
        w: slotW, imgH,
      };
      _drawProofSlot(doc, p, slot, common, i + 1);
    }
    return doc.output('datauristring');
  }

  /* ── BULK proof sheet (rev 9 spec, Brandon Jul 30 2026) ──────────────
     ONE page, TWO columns. Column 1: every name at its fitted size — the
     same relative sizes the review stage showed (drawn with the actual
     font; document.fonts already loaded it during the review pass).
     Column 2: the decorated side(s) with the LONGEST name rendered — the
     same mockups the review used. Replaces buildProofSheetPdf for BULK
     orders only; single-item orders keep the side-by-side sheet. */
  async function buildBulkProofSheetPdf(sizedNames, entries, serial, style, data) {
    const names = sizedNames || [];
    const sides = (entries || []).filter(e => e && e.png);
    if (!names.length && !sides.length) return null;
    const jsPDF = await waitForJsPDF();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'in', format: 'letter' }); /* 11 x 8.5 */

    data = data || _proofSheetData(serial, sides.map(e => e.method), sides.map(e => e.slug).filter(Boolean));
    data.artDims = sides.filter(e => e.artIn).map(e => ({
      label: e.label, wIn: e.artIn.wIn, hIn: e.artIn.hIn, mm: e.artIn.mm, clipped: e.artIn.clipped,
    }));
    data.artNote = 'Variable side measured at the longest name.';
    const shell = _drawProofShell(doc, data);

    /* Panel interior: names block (left) + decorated side(s) (right).
       v2.12.1: TWO stacked header lines in the names column, and BOTH headers
       hard-clipped to their own column width — the single-line header grew
       with the font name and ran into the placement header (CB-20260804-6DC5). */
    const c = shell.content;
    const headBand = 0.38;
    const nameCol = { x: c.x, y: c.y + headBand, w: c.w * 0.44, h: c.h - headBand };
    const sideCol = { x: c.x + c.w * 0.44 + 0.22, y: c.y + headBand, w: c.w - c.w * 0.44 - 0.22, h: c.h - headBand };
    const top = nameCol.y, colH = nameCol.h;
    doc.setFontSize(8);
    doc.setTextColor(120);
    const _clip1 = (s, w) => (doc.splitTextToSize(String(s), w) || [String(s)])[0];
    doc.text(_clip1(`${names.length} NAMES — AT RELATIVE PRINT SIZE`, nameCol.w), nameCol.x, c.y + 0.08);
    if (style && style.font) {
      doc.text(_clip1(`FONT: ${String(fontDisplayName(style.font)).toUpperCase()}`, nameCol.w), nameCol.x, c.y + 0.235);
    }
    doc.text(_clip1('PLACEMENT — LONGEST NAME SHOWN', sideCol.w), sideCol.x, c.y + 0.08);
    doc.setTextColor(0);

    /* Column 1 — one canvas, names drawn at their fitted px sizes, the whole
       block scaled to fit the column box. A huge batch simply gets smaller:
       the spec is a single page. Canvas is created AT the target scale so
       its dimensions can never exceed browser canvas caps. */
    if (names.length) {
      try {
        const GAP = 0.28; /* inter-row gap as a fraction of each name's font size */
        const mc = document.createElement('canvas').getContext('2d');
        /* v2.12.1: rows sized from the INK box (actualBoundingBox*), never
           measureText().width — script fonts (Dancing Script) carry swash ink
           OUTSIDE the advance box (negative left bearing, right overhang,
           ascent/descent past the em box) and the old advance-sized canvas
           CLIPPED it on a live proof (CB-20260804-6DC5): a clipped name on a
           proof reads as "your name will be engraved clipped." Same trap the
           v2.9.33 monogram work documented. Fallback estimates (metric
           undefined) are deliberately over-generous — padding wastes a little
           whitespace; clipping ships a wrong proof. */
        const rows = names.map(n => {
          mc.font = fontStr({ font: style.font, fontSize: n.fontSize, bold: style.bold, italic: style.italic });
          const m = mc.measureText(n.name);
          const fs = n.fontSize;
          const aL = (m.actualBoundingBoxLeft != null) ? Math.max(0, m.actualBoundingBoxLeft) : fs * 0.35;
          const aR = (m.actualBoundingBoxRight != null) ? Math.max(1, m.actualBoundingBoxRight) : (m.width || fs) + fs * 0.35;
          const aA = (m.actualBoundingBoxAscent != null) ? Math.max(1, m.actualBoundingBoxAscent) : fs;
          const aD = (m.actualBoundingBoxDescent != null) ? Math.max(0, m.actualBoundingBoxDescent) : fs * 0.4;
          const pad = fs * 0.06; /* antialias/hinting safety ring */
          return { n, aL, aA, pad, w: aL + aR + pad * 2, h: aA + aD + pad * 2 };
        });
        let maxW = 1, totalH = 0;
        rows.forEach((r, i) => { maxW = Math.max(maxW, r.w); totalH += r.h + (i ? r.n.fontSize * GAP : 0); });
        const DPI = 300; /* v2.12.0: 200 -> 300 (hi-res proof spec) */
        let k = Math.min((nameCol.w * DPI) / maxW, (colH * DPI) / totalH);
        /* Device-pixel pad FLOOR: on big batches the whole block downscales
           hard and the 6% ring can collapse below one output pixel — an
           antialiased glyph edge then touches the card edge. Add a fixed
           4-device-px ring in content units and re-fit. */
        const extra = 4 / Math.max(k, 1e-6);
        rows.forEach(r => { r.w += extra * 2; r.h += extra * 2; });
        maxW += extra * 2; totalH += extra * 2 * rows.length;
        k = Math.min((nameCol.w * DPI) / maxW, (colH * DPI) / totalH);
        const cW = Math.max(1, Math.ceil(maxW * k));
        const cH = Math.max(1, Math.ceil(totalH * k));
        const c = document.createElement('canvas');
        c.width = cW; c.height = cH;
        const cx = c.getContext('2d');
        cx.fillStyle = '#FFFFFF'; cx.fillRect(0, 0, cW, cH);
        cx.scale(k, k);
        cx.fillStyle = '#111111';
          let y = 0;
        rows.forEach((r, i) => {
          if (i) y += r.n.fontSize * GAP;
          cx.font = fontStr({ font: style.font, fontSize: r.n.fontSize, bold: style.bold, italic: style.italic });
          /* x offset = the name's own left-ink extent, baseline = ascent — every
             glyph's ink lands fully inside the canvas by construction. */
          cx.fillText(r.n.name, r.aL + r.pad + extra, y + r.pad + extra + r.aA);
          y += r.h;
        });
        doc.addImage(c.toDataURL('image/png'), 'PNG', nameCol.x, top, cW / DPI, cH / DPI);
        c.width = 0;
      } catch (e) { DEBUG && console.warn('[dt] proof name column failed:', e.message); }
    }

    /* Column 2 — decorated side(s), stacked slots: alpha/background-trimmed,
       common PHYSICAL scale, dashed imprint rect (shared _drawProofSlot). */
    if (sides.length) {
      const capBand = 0.42; /* v2.12.1: sub-caption removed */
      const slotH = sideCol.h / sides.length;
      const imgH = slotH - capBand;
      const prepped = [];
      for (const e of sides) prepped.push(await _prepProofEntry(e));
      const valid = prepped.filter(Boolean);
      let common = null;
      if (valid.length && valid.every(p => p.ppi)) {
        common = Infinity;
        valid.forEach(p => {
          const wI = p.crop.bw / p.ppi, hI = p.crop.bh / p.ppi;
          common = Math.min(common, sideCol.w / wI, imgH / hI);
        });
        if (!isFinite(common) || common <= 0) common = null;
      }
      for (let i = 0; i < prepped.length; i++) {
        const p = prepped[i];
        if (!p) continue;
        _drawProofSlot(doc, p, {
          x: sideCol.x, y: sideCol.y + i * slotH, w: sideCol.w, imgH,
        }, common, i + 1);
      }
    }
    return doc.output('datauristring');
  }

  async function executeCartAdd(designedSides, upcharges, wantProof, designApproved) {
    cartBtn.disabled = true;
    const setStatus = msg => { cartBtn.textContent = msg; announceStatus(msg); dtWaitStatus(msg); };
    dtWaitOpen(designedSides.length, () => executeCartAdd(designedSides, upcharges, wantProof, designApproved));

    const sessionId = getCartSessionId();
    const serial = getOrCreateSerial();
    const lineFolder = `${S.variantId}-${serial}`;
    const s3Folder = `${sessionId}/${lineFolder}`;
    const cartOrigView = S.view;

    try {
      const POSITION_LABELS = ['Primary', 'Secondary', 'Third', 'Fourth'];

      const props = {
        _design_serial: serial,
        _s3_folder: `https://us-east-2.console.aws.amazon.com/s3/buckets/custom-branding-design-files?region=us-east-2&prefix=production-files/${s3Folder}/`,
        _location_count: String(designedSides.length),
      };

      /* Track every distinct decoration method actually used in this order
         so we can build a clean global _design_method summary at the end.
         Per-side methods come from CFG.sideConfigs[slug].decorationMethod,
         which is already resolved server-side in Liquid (including the
         Lid/Flatbed Hard + Vinyl exception). */
      const resolvedMethods = new Set();
      /* Per-side mockups collected for the single all-sides PROOF file */
      const _proofEntries = [];

      /* $1000 proof rule — recomputed here so it never depends on UI state:
         line total = (variant price + upcharge cents) x quantity. Over the
         threshold, the proof becomes required and the policy note is
         stamped on the line item for production/ERP visibility. */
      const qtyEl = $('dt-qty-input');
      const qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
      const _lineBaseCents = (CFG.variants?.find(v => v.id === S.variantId)?.price || 0)
        + upcharges.reduce((a, c) => a + (c.cents || 0), 0);
      if (CFG.proofVariantId && (_lineBaseCents * qty) / 100 > 1000) {
        wantProof = true;
        props._proof_required = 'We require proofs on amounts greater than $1000';
      }

      if (wantProof && CFG.proofVariantId) props._artist_proof = 'Yes';
      /* No proof: the review-modal confirmation checkbox was checked. */
      else if (designApproved) props._design_approved = 'Yes - design reviewed as shown';

      let cartImageUrl = null;

      /* Full design snapshot (JSON) into the order's production folder —
         production can recreate any layer exactly (fonts, sizes, colors,
         positions, monogram styles) without relying on the inline
         _design_data cart property surviving its 4800-char limit. */
      try {
        setStatus('Saving design data…');
        const _ddUrl = await uploadProdFile('root',
          'data:application/json;base64,' + btoa(unescape(encodeURIComponent(JSON.stringify(serializeDesign())))),
          'design-data.json', 'application/json', s3Folder, serial, 'attachment');
        if (_ddUrl) props._design_data_url = _ddUrl;
      } catch (ddErr) { DEBUG && console.warn('[dt] design-data.json upload failed (non-fatal):', ddErr.message); }

      /* Process each designed side — positional keys (Primary/Secondary/Third)
         follow CFG.sides ordering which matches metafield ordering.
         View restore is deferred until AFTER the loop to avoid a race: the old
         per-iteration restore called loadMockup() (async, non-awaited) which
         left mImg in a loading state.  If the next iteration's sideSlug matched
         the restored S.view the switch block was skipped, mockupPng() saw
         mImg.complete === false, and returned null — silently dropping the
         mockup for that side. */
      for (let sideIdx = 0; sideIdx < designedSides.length; sideIdx++) {
        const sideSlug = designedSides[sideIdx];
        const pos = POSITION_LABELS[sideIdx];
        const label = CFG.sideConfigs[sideSlug]?.label || sideSlug;

        /* Positional location label */
        props[`_${pos}_location`] = label;

        /* Switch to this side's mockup if needed */
        if (S.view !== sideSlug) {
          S.view = sideSlug;
          const src = mockupSrc();
          if (src && mImg.getAttribute('src') !== src) {
            await new Promise((resolve, reject) => {
              mImg.addEventListener('load', () => {
                try { onMockupLoad(); } catch (e) { DEBUG && console.warn('[dt] onMockupLoad threw during cart-add:', e.message); }
                /* Write rscale/pa cache synchronously — don't rely on the
                   RAF inside onMockupLoad, which might not fire before we
                   read the cache in generateSideProduction. */
                syncCanvas();
                resolve();
              }, { once: true });
              mImg.onerror = () => reject(new Error('Mockup image failed to load: ' + src));
              mImg.src = src;
            });
          } else computePA();
        }

        /* Mockup preview (product + design) — inline disposition for browser display.
           v2.12.0: hi-res capture + pa geometry via captureProofMockup (proof sheet). */
        setStatus(`Capturing ${label} mockup…`);
        await ensureSideLayerPaintSources(sideSlug); /* v2.12.3: repair missing/stale display rasters BEFORE capture */
        const _cap = captureProofMockup(sideSlug);
        const mPng = _cap ? _cap.png : null;
        if (mPng) {
          const mUrl = await uploadProdFile('root', mPng, `${sideSlug}-mockup.png`, 'image/png', s3Folder, serial, 'inline');
          if (mUrl) { props[`_${pos}_mockup_preview`] = mUrl; if (!cartImageUrl) cartImageUrl = mUrl; }
        }

        /* Effective per-side decoration method — resolved BEFORE production
           generation (rev 9) so the recipe class (engrave / full print /
           transfer) routes every file correctly. Also feeds the global
           _design_method summary below. */
        const sideMethod = effectiveSideMethod(sideSlug);
        resolvedMethods.add(sideMethod);

        /* Production files — attachment disposition (default) for download. */
        const prodUrls = await generateSideProduction(sideSlug, setStatus, s3Folder,
          { serial, method: sideMethod });

        if (prodUrls) {
          props[`_${pos}_production_type`] = prodUrls.productionType;
          /* Collect files in fixed order, filter nulls, number sequentially.
             Print orders: PDF first (the accurate raster), then vector backups.
             Engrave orders: vector files first (SVG/DXF/EPS are primary).
             If vectorization failed and no files exist, fall back to the 300 DPI PNG. */
          const isPrintType = !(prodUrls.productionType || '').startsWith('engrave');
          /* Engrave ships vector only (machine software is DXF-native).
             Order is fixed by spec (Brandon): DXF is file 1, EPS is file 2.
             DOS-EPS twin cut Jul 24 2026 (v2.9.11): both loaded fine in
             EZCAD2; plain EPS kept (DOS cost a TIFF preview render + an
             extra sequential upload at add-to-cart). */
          /* Print orders: PRODUCTION PDF first, then PREPRESS PDF, then EPS backup.
             SVG removed — dual PDF system replaces SVG + single PDF.
             TRANSFER orders (rev 9): the transparent PNG IS the production
             file — file 1 = PNG, file 2 = PREPRESS PDF. Without the explicit
             branch the suppression would leave the PNG unreferenced.
             Engrave: pdfPrepress exists now (rev 8) but is Prepress-folder-only
             — deliberately NOT a pointer file, count stays exactly 2. */
          let prodFiles;
          if (prodUrls.productionType === 'print-transfer-png') {
            prodFiles = [prodUrls.png, prodUrls.pdfPrepress].filter(Boolean);
          } else if (isPrintType) {
            prodFiles = [prodUrls.pdfProduction, prodUrls.pdfPrepress, prodUrls.eps, prodUrls.dxf].filter(Boolean);
          } else {
            prodFiles = [prodUrls.dxf, prodUrls.eps].filter(Boolean);
          }
          if (prodFiles.length === 0 && prodUrls.png) {
            prodFiles = [prodUrls.png];
            props[`_${pos}_production_type`] = prodUrls.productionType.replace('vector', 'raster-fallback');
          }
          props[`_${pos}_production_file_count`] = String(prodFiles.length);
          prodFiles.forEach((url, fileIdx) => {
            props[`_${pos}_production_file_${fileIdx + 1}`] = url;
          });
        } else {
          props[`_${pos}_production_file_count`] = '0';
        }

        /* Metadata */
        const phys = SIDE_PHYS[sideSlug];
        if (phys) props[`_${pos}_imprint_size`] = `${phys.wInches}" x ${phys.hInches}"`;

        /* Image-quality flag for prepress — mirrors the forced-proof logic
           in the review (150 DPI threshold at printed size) */
        const _lowRes = collectLowResSides([sideSlug]);
        if (_lowRes.length) {
          props[`_${pos}_image_quality`] = `LOW RES — ${_lowRes[0].name} ≈ ${_lowRes[0].dpi} DPI at printed size (150+ recommended)`;
        }

        /* Per-side font — collect unique font display names for prepress reference */
        const sideLayers = S.byView[sideSlug]?.filter(l => l.visible !== false) || [];
        const sideFonts = [...new Set(sideLayers.filter(l => l.type === 'text' && l.font).map(l => fontDisplayName(l.font)))];
        if (sideFonts.length) props[`_${pos}_font`] = sideFonts.join(', ');

        /* v2.11.0: production must know an inverted engrave is INTENTIONAL —
           the file's mostly-solid plate would otherwise read as a defect. */
        if (sideLayers.some(l => l._engraveInverted)) props[`_${pos}_engrave_inverted`] = 'Yes';

        /* (Per-side decoration method now resolved ABOVE, before the
           production call — rev 9. sideMethod is in scope here.) */

        /* Collect for the single all-sides PROOF file (built after the loop).
           v2.12.0: artIn is measured NOW, while this side's pa/rscale caches
           are fresh from the switch above — resize deletes non-current-side
           caches, so measuring at proof-build time would be unreliable. */
        if (mPng) {
          _proofEntries.push({
            label,
            slug: sideSlug,
            png: mPng,
            sub: (phys ? `${phys.wInches}" x ${phys.hInches}" · ` : '') + sideMethod,
            paFrac: _cap ? _cap.paFrac : null,
            phys: phys || null,
            method: sideMethod,
            artIn: _sideArtworkDims(sideSlug),
          });
        }

        /* Source file uploads — non-fatal, uses shared helper (→ Prepress) */
        await uploadSourceFiles(sideLayers, sideSlug, pos, s3Folder, props, setStatus, serial);
      }

      /* Build the global _design_method summary:
           1 method across all sides → that method, verbatim
           multiple methods (e.g. Lid=Flatbed + Front=Vinyl) → comma-joined
           no designs at all (defensive) → fall back to UI mode label */
      if (resolvedMethods.size === 1) {
        props._design_method = [...resolvedMethods][0];
      } else if (resolvedMethods.size > 1) {
        props._design_method = [...resolvedMethods].join(', ');
      } else {
        props._design_method = S.method === 'engrave' ? 'Laser Engrave' : (CFG.printMethodName || 'Cylindrical Print');
      }

      /* ── Single PROOF file: every designed side, side by side ── */
      try {
        setStatus('Building proof sheet…');
        const _sheetData = _proofSheetData(serial, resolvedMethods, designedSides);
        const _proofPdf = await buildProofSheetPdf(_proofEntries, serial, _sheetData);
        if (_proofPdf) {
          const _pfUrl = await uploadProdFile('proof-sheet', _proofPdf, 'proof-sheet.pdf', 'application/pdf', s3Folder, serial);
          if (_pfUrl) props._proof_file = _pfUrl;
        }
      } catch (pfErr) { DEBUG && console.warn('[dt] Proof sheet failed (non-fatal):', pfErr.message); }

      /* Cart image */
      if (cartImageUrl) props._cart_image = cartImageUrl;

      /* Design data JSON — deep-copy then strip b64/svgMarkup to stay under
         Shopify's property value size limit. Full fidelity save is in autoSaveOnCart. */
      const designSnapshot = JSON.parse(JSON.stringify(serializeDesign()));
      for (const sKey of Object.keys(designSnapshot.sides || {})) {
        (designSnapshot.sides[sKey].layers || []).forEach(ld => {
          delete ld.b64;
          delete ld.svgMarkup;
          delete ld._maskMarkup;
          delete ld._content;
        });
      }
      /* Shopify line-item properties have a ~5000-char value limit.
         If design_data exceeds this, strip layer-level metadata progressively. */
      let designJson = JSON.stringify(designSnapshot);
      if (designJson.length > 4800) {
        for (const sKey of Object.keys(designSnapshot.sides || {})) {
          (designSnapshot.sides[sKey].layers || []).forEach(ld => {
            delete ld._imgSrc; delete ld._originalUrl; delete ld._vectorUrl;
          });
        }
        designJson = JSON.stringify(designSnapshot);
      }
      if (designJson.length > 4800) {
        DEBUG && console.warn('[dt] design_data still over 4800 chars (' + designJson.length + '), omitting from cart props');
        /* Don't attach an oversized value — the S3 autosave has full fidelity */
      } else {
        props._design_data = designJson;
      }

      /* ── Add main product to cart (qty read above with the proof rule) ── */
      setStatus('Adding to cart…');
      const cartItems = [{ id: S.variantId, quantity: qty, properties: props }];

      /* Upcharge line items (skip any without a configured variant) */
      upcharges.forEach(u => {
        if (!u.variantId) return;
        cartItems.push({ id: u.variantId, quantity: qty, properties: { _design_serial: serial, _upcharge_for: serial } });
      });

      /* Artist proof line item — only if a proof variant is actually configured,
         otherwise the /cart/add.js call would fail on an undefined id */
      if (wantProof && CFG.proofVariantId) {
        cartItems.push({ id: CFG.proofVariantId, quantity: 1, properties: { _design_serial: serial, _proof_for: serial } });
      }

      /* Submit all items atomically via Shopify's multi-item add endpoint.
         This prevents partial orders where the main product succeeds but an
         upcharge or proof line item fails. */
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems }),
      });
      if (!res.ok) {
        let errMsg = `Cart add failed: ${res.status}`;
        try { const b = await res.json(); errMsg = b.description || b.message || (typeof b.errors === 'string' ? b.errors : b.errors ? JSON.stringify(b.errors) : null) || errMsg; } catch (_) {}
        throw new Error(errMsg);
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      dtAnalytics.cartSuccess(serial);
      /* rev 9 guard: a SUCCESSFUL add consumes the serial. Without this, a
         following bulk add with no design edit reuses it — same S3 folder
         (root files overwritten) and a duplicate _design_serial, which
         cart-sync uses as its unique main-line key. A FAILED add still
         leaves the serial cached so retry overwrites the same keys. */
      markSerialDirty();
      dtWaitDone();
      clearAutosave();
      autoSaveOnCart();
      celebrateThenGoToCart();

    } catch (err) {
      DEBUG && console.error('[dt] Cart error:', err);
      dtAnalytics.cartError(err.message || 'Unknown error');
      /* rev 14: an upload failure gets a customer-readable line (which file,
         what to try) instead of the raw transport error; the raw reason still
         goes to GA4 above and to the console in uploadWithRetry. */
      const short = err.dtUploadFile
        ? 'Upload failed — try again'
        : (err.message || 'Unknown error').slice(0, 60);
      dtWaitFail(err.dtUploadFile ? uploadFailDetail(err.dtUploadFile) : short);
      cartBtn.textContent = short;
      cartBtn.disabled = false;
      setTimeout(() => { cartBtn.textContent = 'Checkout'; updateCartBtn(); }, 6000);
      throw err; /* re-throw so doConfirm's catch re-enables the review modal */
    } finally {
      /* Always restore the user's original view — even if cart-add threw */
      if (S.view !== cartOrigView) { S.view = cartOrigView; loadMockup(); }
    }
  }

  /**
   * Upload with one retry. On second failure, offer localStorage save.
   */
  /** Read a File/Blob as a data-URL string (for uploadToS3). */
  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(r.error || new Error('FileReader error'));
      r.readAsDataURL(file);
    });
  }

  /** Map file extension → content type for source uploads.
   *  Falls back to application/octet-stream for unknown extensions. */
  const SRC_EXT_CT = {
    png:'image/png', jpg:'image/jpeg', jpeg:'image/jpeg', svg:'image/svg+xml',
    pdf:'application/pdf', webp:'image/webp', gif:'image/gif', bmp:'image/bmp',
    tif:'image/tiff', tiff:'image/tiff', heic:'image/heic', heif:'image/heif',
  };

  /**
   * Upload original source files for one side to S3.
   * Collects unique File references from visible layers, reads each as data URL,
   * uploads with retry, and populates line item properties.
   * Failures are non-fatal — logged but never re-thrown.
   * @returns {number} count of successfully uploaded source files
   */
  async function uploadSourceFiles(sideLayers, sideSlug, pos, s3Folder, props, statusFn, serial) {
    /* Build an upload queue from two sources:
       1. Live File objects (_sourceFile) — the original upload, best quality.
       2. Fallback: l.b64 data URLs for raster layers whose _sourceFile was lost
          (autosave / saved-design restore strips File objects because they can't
          survive JSON serialization). The production team gets the canvas-state
          version rather than nothing. */
    const uploads = [];          // { file?: File, dataUrl?: string, label: string }
    const seenFiles = new Set();
    const coveredLayerIds = new Set();

    sideLayers.forEach(l => {
      if (l._sourceFile instanceof File && !seenFiles.has(l._sourceFile)) {
        seenFiles.add(l._sourceFile);
        uploads.push({ file: l._sourceFile, label: l._sourceFile.name || l.name || 'file' });
        coveredLayerIds.add(l.id);
      }
    });

    /* Fallback pass — raster layers with b64 data but no File reference */
    sideLayers.forEach(l => {
      if (l.isRasterUpload && l.b64 && !coveredLayerIds.has(l.id)) {
        uploads.push({ dataUrl: l.b64, label: l.name || 'image' });
      }
    });

    if (!uploads.length) return 0;
    statusFn('Uploading source files…');
    let srcIdx = 0;

    for (const u of uploads) {
      try {
        let dataUrl, ext, ct;
        if (u.file) {
          dataUrl = await readFileAsDataUrl(u.file);
          ext = (u.file.name || 'file').split('.').pop().toLowerCase();
          ct = SRC_EXT_CT[ext] || 'application/octet-stream';
        } else {
          dataUrl = u.dataUrl;
          const mimeMatch = dataUrl.match(/^data:([^;]+);/);
          ct = mimeMatch ? mimeMatch[1] : 'image/png';
          ext = ct.split('/').pop().replace('jpeg', 'jpg');
        }
        const srcName = `${sideSlug}-source-${srcIdx + 1}.${ext}`;
        const url = await uploadProdFile('source', dataUrl, srcName, ct, s3Folder, serial, 'attachment');
        if (url) {
          srcIdx++;
          props[`_${pos}_source_file_${srcIdx}`] = url;
        } else {
          DEBUG && console.warn('[dt] Source upload returned falsy URL for', u.label);
        }
      } catch (srcErr) {
        DEBUG && console.warn('[dt] Source file upload failed for', u.label, srcErr.message);
      }
    }
    if (srcIdx > 0) props[`_${pos}_source_file_count`] = String(srcIdx);
    return srcIdx;
  }

  /* rev 14 (Aug 26 2026): a customer saw a native browser confirm() —
     "Upload failed. Would you like to save your design locally…" — in the
     middle of the "Making it yours" overlay. That dialog came from here
     (offerLocalSave, now removed). It was wrong on three counts: it looked
     like a broken site, it duplicated the overlay's own "Couldn't finish /
     Try Again" panel, and the key it wrote (dt_saved_design_<variant>) was
     never READ by anything — the real draft is the autosave
     (dt_draft_<handle>, restored by checkAutosaveDraft on reload). It also
     fired on the NON-fatal design-data.json path where the order still went
     through. Now: brief pause before the retry, silent autosave() on the
     final failure, one unconditional console.warn so a customer's console
     screenshot is diagnostic, and the thrown error names the FILE
     (err.dtUploadFile) so the overlay can say what didn't upload. */
  const UPLOAD_RETRY_PAUSE_MS = 1500;
  function uploadDisplayName(filename) {
    /* "CB-20260826-XXXX-front-mockup.png" → "front-mockup.png" */
    return String(filename || 'file').replace(/^CB-\d{8}-[A-Z0-9]+-/i, '');
  }
  async function uploadWithRetry(dataUrl, filename, contentType, folder, disposition) {
    try {
      return await uploadToS3(dataUrl, filename, contentType, folder, disposition);
    } catch (err1) {
      DEBUG && console.warn('[dt] Upload attempt 1 failed:', filename, err1.message, '— retrying…');
      await new Promise(r => setTimeout(r, UPLOAD_RETRY_PAUSE_MS));
      try {
        return await uploadToS3(dataUrl, filename, contentType, folder, disposition);
      } catch (err2) {
        const shortName = uploadDisplayName(filename);
        const reason = (err2 && err2.message) || 'Unknown error';
        console.warn('[dt] Upload failed after retry:', filename, '—', reason);
        dtAnalytics.error('s3_upload', shortName + ': ' + reason);
        /* Keep the draft on this device (no dialog). The overlay's error
           panel already says "Your design is safe" — make that true. */
        try { autosave(); } catch (_) {}
        const err = new Error('Upload failed (' + shortName + '): ' + reason);
        err.dtUploadFile = shortName;
        err.cause = err2;
        throw err;
      }
    }
  }

  function setupCart() {
    if (!cartBtn) return;
    cartBtn.addEventListener('click', () => {
      const openReview = document.getElementById('dt-cart-modal');
      if (openReview && openReview.__dtConfirm) { openReview.__dtConfirm(); return; }
      openCartModal();
    });
    /* Wire mobile review button too */
    $('dt-add-cart-mobile')?.addEventListener('click', () => {
      /* Generate clean preview first (no handles/borders) */
      S.previewMode = true;
      S.sel = null; S.iact = null;
      render(); syncPrintOverlay();
      /* Short delay for clean render, then open cart review */
      setTimeout(() => {
        S.previewMode = false;
        openCartModal();
      }, 50);
    });
    $('dt-bulk-order-btn')?.addEventListener('click', openBulkOrderModal);
    $('dt-bulk-order-btn-mobile')?.addEventListener('click', openBulkOrderModal);

    /* ── Qty stepper (mobile cart bar) — collapsible ── */
    const qtyInput = $('dt-qty-input');
    const qtyMinus = $('dt-qty-minus');
    const qtyPlus  = $('dt-qty-plus');
    const qtyLabel = $('dt-qty-label');
    const qtyDisplay = $('dt-qty-display');
    const qtyWrap = $('dt-cart-bar-qty');

    if (qtyLabel && qtyWrap) {
      /* Tap label to expand stepper, hide label */
      qtyLabel.addEventListener('click', () => {
        qtyWrap.classList.add('expanded');
        qtyLabel.classList.add('hidden');
      });
      /* Collapse stepper when tapping outside */
      document.addEventListener('click', (e) => {
        if (qtyWrap.classList.contains('expanded') &&
            !qtyWrap.contains(e.target) && e.target !== qtyLabel) {
          qtyWrap.classList.remove('expanded');
          qtyLabel.classList.remove('hidden');
          if (qtyDisplay) qtyDisplay.textContent = qtyInput?.value || '1';
        }
      });
    }
    if (qtyInput && qtyMinus && qtyPlus) {
      qtyMinus.addEventListener('click', () => { setQty(getQty() - 1); });
      qtyPlus.addEventListener('click', () => { setQty(getQty() + 1); });
    }

    /* ── Qty stepper (desktop header) — shares the same quantity state ── */
    const qtyMinusD = $('dt-qty-minus-d');
    const qtyPlusD  = $('dt-qty-plus-d');
    if (qtyMinusD && qtyPlusD) {
      qtyMinusD.addEventListener('click', () => { setQty(getQty() - 1); });
      qtyPlusD.addEventListener('click', () => { setQty(getQty() + 1); });
    }

    /* ── Color pill toggle (mobile — collapses/expands swatch strip) ── */
    const colorPill = $('dt-color-pill-mobile');
    const swatchesDiv = $('dt-color-swatches');
    if (colorPill && swatchesDiv) {
      colorPill.addEventListener('click', () => {
        const open = swatchesDiv.classList.toggle('expanded');
        colorPill.classList.toggle('open', open);
      });
      /* Collapse after swatch selection */
      swatchesDiv.addEventListener('click', (e) => {
        const btn = e.target.closest('.dt-color-swatch-btn');
        if (btn) {
          setTimeout(() => {
            swatchesDiv.classList.remove('expanded');
            colorPill.classList.remove('open');
            /* Sync pill display with new selection */
            const name = btn.dataset.colorName;
            const handle = btn.dataset.colorHandle;
            const pillDot = $('dt-color-pill-dot');
            const pillName = $('dt-color-pill-name-m');
            if (pillName) pillName.textContent = name;
            if (pillDot) pillDot.style.backgroundImage =
              `url('https://cdn.shopify.com/s/files/1/0537/7101/0220/files/${handle}.png')`;
          }, 100);
        }
      });
    }
  }

  /* ══════════════════════════════════════════════════════════════════
   * BULK ORDERING — CSV/paste names for variable data personalization
   * ══════════════════════════════════════════════════════════════════ */
  let _papaLoaded = false;

  function loadPapaParse() {
    return new Promise((resolve, reject) => {
      if (_papaLoaded && window.Papa) return resolve(window.Papa);
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js';
      s.onload = () => { _papaLoaded = true; resolve(window.Papa); };
      s.onerror = () => reject(new Error('Failed to load PapaParse'));
      document.head.appendChild(s);
    });
  }

  function getTextLayers() {
    const all = [];
    CFG.sides.forEach(s => S.byView[s].forEach(l => { if (l.type === 'text' && l.visible !== false) all.push({ ...l, side: s }); }));
    return all;
  }

  /* ══════════════════════════════════════════════════════════════════
   * BULK FULL-ARTWORK RENDERING
   * A group order swaps ONE text layer per item, but every preview and
   * production file must show the WHOLE side — static text, clipart, and
   * uploads too, not just the swapped name (order CB36253 shipped a names-
   * only engrave file and dropped the "The Klegstad's 9.19.2026" line).
   * These helpers substitute a name into the variable layer and reuse the
   * SAME single-item builders (renderProductionPNG / buildCompositeLiveTextSvg)
   * so each item renders identically to a one-off order of that name.
   * ══════════════════════════════════════════════════════════════════ */

  /* One bulk render pass at a time. The review-cells pass, the proof-PDF
     pass, and the Confirm pass all substitute names into the SAME live
     layer via bulkWithNameApplied; if two ran interleaved (e.g. Confirm
     clicked while a slow PDF renders, or a modal closed mid-pass and the
     design edited), the wrong name could bake into a file. Button disabling
     alone is brittle (the Confirm error path re-enables on a 4s timer), so
     every entry point checks this lock and every overlay close respects it. */
  let _dtBulkBusy = false;

  /* Substitute `entry` (an auto-sized name) into `layer`, run async `fn`
     while it is applied, then restore the layer exactly. Not re-entrant:
     callers must render one name at a time (the builders read the shared
     layer object), so keep these calls sequential — never in Promise.all. */
  async function bulkWithNameApplied(layer, entry, fn) {
    const o = { text: layer.text, name: layer.name, fontSize: layer.fontSize };
    layer.text = entry.name;
    layer.name = String(entry.name).slice(0, 25);
    layer.fontSize = entry.fontSize;
    measureText(layer);
    try { return await fn(); }
    finally {
      layer.text = o.text; layer.name = o.name; layer.fontSize = o.fontSize;
      measureText(layer);
    }
  }

  /* Load a data URL / URL into an <img> (Promise). Used to composite the
     per-name production PNG onto a flat background for proofs and cells. */
  function _bulkLoadImg(src) {
    return new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = () => rej(new Error('image load failed'));
      img.src = src;
    });
  }

  /* Namespace every element id + local url(#...) / href="#..." reference
     inside one cell's markup, so the stacked combined SVG carries no
     duplicate ids across cells (each cell's markup restarts ids at 0 —
     e.g. dtPaClip, text-layer-0). Without this a strict RIP resolves every
     url(#dtPaClip) to cell 0 and mis-clips the rest. Font-family names are
     not ids, so fonts still resolve. */
  function _bulkNamespaceIds(inner, prefix) {
    if (!inner) return inner;
    return inner
      .replace(/\bid="([^"]+)"/g, `id="${prefix}$1"`)
      .replace(/url\(#([^)]+)\)/g, `url(#${prefix}$1)`)
      .replace(/(\b(?:xlink:href|href)=)"#([^"]+)"/g, `$1"#${prefix}$2"`);
  }

  /* Inner markup of a builder-produced SVG (its <defs> + content group),
     stripped of the <?xml?> prolog and outer <svg> wrapper so it can be
     nested inside one cell of the stacked production file. Duplicate
     @font-face defs across cells are harmless. */
  function _bulkSvgInner(svgStr) {
    if (!svgStr) return '';
    const open = svgStr.indexOf('<svg');
    if (open === -1) return svgStr;
    const gt = svgStr.indexOf('>', open);
    const close = svgStr.lastIndexOf('</svg>');
    if (gt === -1 || close === -1 || close <= gt) return svgStr;
    return svgStr.slice(gt + 1, close);
  }

  /* Trace the variable side's RASTER layers to a vector group ONCE for the
     whole batch. The raster art is identical for every name (only the text
     changes), so we never vectorize per-name. Returns a normalized traced
     SVG string usable by buildCompositeLiveTextSvg, or null when the side
     has no raster / tracing fails (degrades to text+vector, same as a
     single-item side whose trace fails). Mirrors generateSideProduction. */
  async function bulkTraceVariableRaster(sideSlug) {
    const vis = (S.byView[sideSlug] || []).filter(l => l.visible !== false);
    const hasText   = vis.some(l => l.type === 'text' && (l.text || '').trim());
    const hasVector = vis.some(l => l.type !== 'text' && l.isSvg && l.svgMarkup);
    const hasRaster = vis.some(l => l.type !== 'text' && !(l.isSvg && l.svgMarkup));
    if (!hasRaster) return null;
    /* v2.9.16: PER-LAYER, mirroring generateSideProduction. The old rule bailed on the
       whole side if ANY raster layer was photographic, so a bulk print job with a photo
       background never traced its logo either. Now print traces bucket B and leaves the
       photo embedded. Engrave is unchanged — it traces whenever there is any raster,
       photo or not, because DXF must exist for every engraving scenario (Brandon). */
    const _rasterVis = vis.filter(l => l.type !== 'text' && !(l.isSvg && l.svgMarkup));
    const _isEng = S.method === 'engrave';
    const _hasTraceable = _rasterVis.some(isTraceableArt);
    if (!_isEng && !_hasTraceable) return null;
    try {
      const cs = getProductionCoordSpace(sideSlug);
      let traceSS = 1;
      if (cs && cs.pxW > 0 && cs.pxH > 0) {
        traceSS = Math.max(1, Math.min(3, Math.sqrt(3145828 / (cs.pxW * cs.pxH))));
      }
      /* Engrave traces every raster layer; print traces bucket B only, so a photo on
         the same side never reaches the vectorizer (v2.9.16). */
      const rasterFilter = _isEng
        ? ((hasText || hasVector)
            ? (l => l.type !== 'text' && !(l.isSvg && l.svgMarkup))
            : null)
        : isTraceableArt;
      /* v2.12.2: engrave traces in ORIGINAL colors + maxColors 4, then the
         SVG is flattened to black — mirrors generateSideProduction exactly
         (see the comments there; CB20260804KPOX stem-drop fix). */
      const rasterOnlyPng = await renderProductionPNG(sideSlug, rasterFilter, traceSS, 0,
        _isEng ? { preserveColors: true } : undefined);
      if (!rasterOnlyPng) return null;
      const maxC = _isEng ? 4 : 32;
      let rawTraced;
      if (_isEng) {
        /* v2.13.0 (F3): audited trace + retry + block — mirrors
           generateSideProduction exactly (see comments there). Forensic S3
           upload is single-path-only (no serial in scope here); bulk blocks
           carry full detail in the gwarn. Photo bypass via _rasterVis. */
        const TRACE_ATTEMPTS = 3;
        let _best = null;
        for (let _att = 1; _att <= TRACE_ATTEMPTS; _att++) {
          /* v2.13.3 (F1/F2): thrown attempts consume the retry budget;
             all-throws with no candidate hard-blocks — mirrors the
             single-item loop (see comments there). No serial in scope →
             URL-mode uploads land in the scratch trace-inputs/ folder. */
          let _cand;
          try {
            const _tb = await callVectorize(rasterOnlyPng, 'svg', maxC);
            _cand = _recolorSvgInkBlack(_normalizeAndRoundSvg(atob(_tb.split(',')[1] || _tb), 1));
          } catch (vecErr) {
            gwarn('engrave_trace_retry', 'BULK trace attempt ' + _att + '/' + TRACE_ATTEMPTS +
              ' THREW for ' + sideSlug + ': ' + (vecErr && vecErr.message));
            if (_att < TRACE_ATTEMPTS) continue;
            if (!_best) {
              gwarn('engrave_trace_blocked', 'BULK CART BLOCKED: trace errored on all ' +
                TRACE_ATTEMPTS + ' attempts for ' + sideSlug + ': ' + (vecErr && vecErr.message));
              const _e = new Error('We hit a snag preparing your engraving file — please try again.');
              _e._dtTraceBlocked = true;
              throw _e;
            }
            break;
          }
          const _audit = await _auditTraceRegions(rasterOnlyPng, _cand);
          if (!_best || _audit.worst > _best.audit.worst) _best = { svg: _cand, audit: _audit };
          if (_audit.ok) break;
          gwarn('engrave_trace_retry', 'BULK trace attempt ' + _att + '/' + TRACE_ATTEMPTS +
            ' failed region audit for ' + sideSlug + ': ' + _audit.fails + '/' + _audit.comps +
            ' region(s) uncovered, worst ' + Math.round(_audit.worst * 100) + '%');
        }
        rawTraced = _best.svg;
        if (!_best.audit.ok && !_rasterVis.some(isPhotoLayer)) {
          gwarn('engrave_trace_blocked', 'BULK CART BLOCKED: region audit failed after ' +
            TRACE_ATTEMPTS + ' attempts for ' + sideSlug + ' — ' + _best.audit.fails +
            ' region(s) uncovered');
          const _e = new Error('We hit a snag preparing your engraving file — please try again.');
          _e._dtTraceBlocked = true;
          throw _e;
        }
        _auditTraceCoverage(rasterOnlyPng, rawTraced, sideSlug + ' (bulk)'); /* legacy telemetry */
        /* v2.13.3 audit receipt — see single-item comment. */
        gwarn('engrave_trace_audit', sideSlug + ' (bulk): comps=' + _best.audit.comps +
          ' fails=' + _best.audit.fails + ' worst=' + Math.round((_best.audit.worst || 0) * 100) + '%' +
          (_best.audit.crashed ? ' [AUDIT CRASHED: ' + _best.audit.error + ']' : ''));
      } else {
        const tracedB64 = await callVectorize(rasterOnlyPng, 'svg', maxC);
        rawTraced = _normalizeAndRoundSvg(atob(tracedB64.split(',')[1] || tracedB64), 1);
      }
      /* Normalize to the 300 DPI coord space so it composits correctly. */
      if (cs) {
        const g = extractTracedSvgGroup(rawTraced, cs.pxW, cs.pxH);
        if (g) return `<svg xmlns="http://www.w3.org/2000/svg" width="${cs.phys.wInches}in" height="${cs.phys.hInches}in" viewBox="0 0 ${cs.pxW} ${cs.pxH}">${g}</svg>`;
      }
      return rawTraced;
    } catch (e) {
      /* v2.13.0 (F3): deliberate audit block escapes; else old semantics. */
      if (e && e._dtTraceBlocked) throw e;
      DEBUG && console.warn('[dt] Bulk raster trace failed:', e.message);
      return null;
    }
  }

  /* ── Bulk: auto-seed a name template + guide positioning (Option B) ── */
  function beginBulkSeed() {
    /* Create a name placeholder the user will position/style; every name inherits it. */
    addTextLayer('First Name');
    /* Show the guided coach once the layer is in (addTextLayer is async on font load) */
    setTimeout(showBulkSeedCoach, 120);
  }

  function showBulkSeedCoach() {
    return; /* disabled for now */
    document.getElementById('dt-bulk-coach')?.remove();
    const host = document.querySelector('.dt-canvas-area') || app;
    if (!host) return;
    const coach = document.createElement('div');
    coach.id = 'dt-bulk-coach';
    coach.className = 'dt-bulk-coach';
    coach.innerHTML =
      '<div class="dt-bulk-coach__text">' +
        '<strong>Place &amp; style this name</strong>' +
        '<span>Everyone\u2019s name will use this exact spot, font, and color.</span>' +
      '</div>' +
      '<button class="dt-bulk-coach__go" id="dt-bulk-coach-go">Open Group Order</button>' +
      '<button class="dt-bulk-coach__x" id="dt-bulk-coach-x" aria-label="Dismiss">' +
        '<svg width="12" height="12" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
      '</button>';
    host.appendChild(coach);
    document.getElementById('dt-bulk-coach-go').addEventListener('click', () => {
      coach.remove();
      openBulkOrderModal();
    });
    document.getElementById('dt-bulk-coach-x').addEventListener('click', () => coach.remove());
  }

  function openBulkOrderModal() {
    const textLayers = getTextLayers();
    if (!textLayers.length) {
      /* No template text yet — auto-seed a name field and guide the user to
         position/style it, instead of dead-ending with an error. */
      beginBulkSeed();
      return;
    }

    document.getElementById('dt-bulk-overlay')?.remove();
    const overlay = document.createElement('div');
    overlay.id = 'dt-bulk-overlay'; overlay.className = 'dt-rbg-overlay';

    const _seedIdx = Math.max(0, textLayers.findIndex(l => l._bulkField));
    const layerOpts = textLayers.map((l, i) => `<option value="${i}"${i === _seedIdx ? ' selected' : ''}>${l.name} — "${l.text}"</option>`).join('');

    overlay.innerHTML = `<div class="dt-rbg-panel dt-rbg-panel--xl">
      <div class="dt-rbg-header">
        <span class="dt-rbg-title">Group Order — Personalize Multiple Items</span>
        <button class="dt-rbg-close" id="dt-bulk-close">&times;</button>
      </div>

      <div class="dt-bulk-steps">
        <div class="dt-bulk-step">
          <span class="dt-bulk-step__num">1</span>
          <span>Your current design is the <strong>template</strong> — the text "${textLayers[_seedIdx].text}" will be replaced with each name below.</span>
        </div>
        <div class="dt-bulk-step">
          <span class="dt-bulk-step__num">2</span>
          <span>Type names below (one per line), paste from a spreadsheet, or upload a CSV file.</span>
        </div>
        <div class="dt-bulk-step">
          <span class="dt-bulk-step__num">3</span>
          <span>Review the preview, then add all personalized items to your cart at once.</span>
        </div>
      </div>

      ${textLayers.length > 1 ? `<div class="dt-dlg-field">
        <div class="dt-label">Which text to replace with each name?</div>
        <select id="dt-bulk-layer" class="dt-input">${layerOpts}</select>
        <div class="dt-dlg-note">Your design has ${textLayers.length} text elements. Choose which one varies per item.</div>
      </div>` : `<input type="hidden" id="dt-bulk-layer" value="${_seedIdx}">`}

      <div class="dt-dlg-field">
        <div class="dt-label">Enter names or text — one per line</div>
        <textarea id="dt-bulk-textarea" class="dt-input" style="width:100%;height:120px;resize:vertical;font-family:var(--dt-mono);font-size:12px" placeholder="John Smith&#10;Jane Doe&#10;Alex Johnson&#10;Maria Garcia&#10;David Chen"></textarea>
        <div class="dt-dlg-note">Each line = one personalized product. Max 500 items per order.</div>
      </div>

      <div style="display:flex;gap:8px;margin-bottom:6px">
        <button class="dt-btn" id="dt-bulk-csv-btn" style="flex:1">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M14 10v3a1 1 0 01-1 1H3a1 1 0 01-1-1v-3M10 3L8 1 6 3M8 1v9" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Upload CSV File
        </button>
        <input type="file" id="dt-bulk-csv-input" accept=".csv,.tsv,.txt,text/csv" hidden>
        <button class="dt-btn" id="dt-bulk-paste-btn" style="flex:1">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="4" y="2" width="8" height="3" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="3" y="5" width="10" height="9" rx="1.5" stroke="currentColor" stroke-width="1.3"/></svg>
          Paste from Clipboard
        </button>
      </div>
      <div style="font-size:10px;color:var(--dt-muted);margin-bottom:14px">CSV format: one column of names. A header row (Name, Text, etc.) is automatically skipped.</div>

      <div id="dt-bulk-preview" style="display:none">
        <div class="dt-label">Preview (<span id="dt-bulk-count">0</span> items)</div>
        <div id="dt-bulk-table-wrap" style="max-height:200px;overflow-y:auto;border:1px solid var(--dt-border);border-radius:var(--dt-radius-sm);margin-bottom:14px">
          <table id="dt-bulk-table" style="width:100%;font-size:11px;border-collapse:collapse"></table>
        </div>
      </div>

      <div id="dt-bulk-error" style="display:none;color:var(--dt-danger);font-size:12px;margin-bottom:12px"></div>

      <button class="dt-rbg-use-btn" id="dt-bulk-add" disabled>Review Order</button>

      <div style="font-size:10px;color:var(--dt-muted);margin-top:10px;line-height:1.6;text-align:center">
        All items use the same design, colors, and layout — only the selected text changes per item.
        Pricing is per item at the displayed product price.
      </div>
    </div>`;

    document.body.appendChild(overlay);
    /* No closing mid-render: a bulk render pass has a name substituted into
       the live layer — closing would let the user edit (or autosave) the
       design in that state. */
    const close = () => { if (_dtBulkBusy) return; overlay.remove(); };
    overlay.querySelector('#dt-bulk-close').addEventListener('click', close);
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });

    const textarea = overlay.querySelector('#dt-bulk-textarea');
    const csvInput = overlay.querySelector('#dt-bulk-csv-input');
    const previewDiv = overlay.querySelector('#dt-bulk-preview');
    const table = overlay.querySelector('#dt-bulk-table');
    const countSpan = overlay.querySelector('#dt-bulk-count');
    const errorDiv = overlay.querySelector('#dt-bulk-error');
    const addBtn = overlay.querySelector('#dt-bulk-add');
    let bulkNames = [];

    function updatePreview() {
      const raw = textarea.value.trim();
      if (!raw) { previewDiv.style.display = 'none'; addBtn.disabled = true; bulkNames = []; return; }
      bulkNames = raw.split('\n').map(s => s.trim()).filter(Boolean);
      if (!bulkNames.length) { previewDiv.style.display = 'none'; addBtn.disabled = true; return; }
      if (bulkNames.length > 500) {
        errorDiv.textContent = 'Group orders cap at 500 items per run.'; errorDiv.style.display = 'block'; addBtn.disabled = true; return;
      }
      errorDiv.style.display = 'none';
      countSpan.textContent = bulkNames.length;
      const preview = bulkNames.slice(0, 20);
      table.innerHTML = `<thead><tr style="background:var(--dt-bg)"><th style="padding:6px 8px;text-align:left;font-weight:600">#</th><th style="padding:6px 8px;text-align:left;font-weight:600">Personalized Text</th></tr></thead>
        <tbody>${preview.map((n, i) => `<tr style="border-top:1px solid var(--dt-border)"><td style="padding:4px 8px;color:var(--dt-muted)">${i+1}</td><td style="padding:4px 8px">${n}</td></tr>`).join('')}
        ${bulkNames.length > 20 ? `<tr style="border-top:1px solid var(--dt-border)"><td colspan="2" style="padding:6px 8px;color:var(--dt-muted);font-style:italic">…and ${bulkNames.length - 20} more</td></tr>` : ''}
        </tbody>`;
      previewDiv.style.display = '';
      addBtn.disabled = false;
      addBtn.textContent = `Review ${bulkNames.length} Items`;
    }

    textarea.addEventListener('input', updatePreview);
    overlay.querySelector('#dt-bulk-csv-btn').addEventListener('click', () => csvInput.click());

    csvInput.addEventListener('change', async () => {
      const file = csvInput.files?.[0]; if (!file) return;
      try {
        const Papa = await loadPapaParse();
        const text = await file.text();
        const result = Papa.parse(text, { header: false, skipEmptyLines: true });
        const rows = result.data.map(r => r[0]?.trim()).filter(Boolean);
        if (rows.length > 1 && /^(name|text|first|label|item)/i.test(rows[0])) rows.shift();
        textarea.value = rows.join('\n');
        updatePreview();
      } catch (e) { errorDiv.textContent = "Couldn't read your CSV: " + e.message; errorDiv.style.display = 'block'; }
      csvInput.value = '';
    });

    overlay.querySelector('#dt-bulk-paste-btn').addEventListener('click', async () => {
      try { textarea.value = await navigator.clipboard.readText(); updatePreview(); }
      catch (e) { errorDiv.textContent = "Your browser blocked clipboard access — paste straight into the text box instead."; errorDiv.style.display = 'block'; }
    });

    addBtn.addEventListener('click', async () => {
      if (!bulkNames.length) return;
      addBtn.disabled = true; addBtn.textContent = 'Measuring names…';

      const layerIdx = parseInt(overlay.querySelector('#dt-bulk-layer')?.value) || 0;
      const targetLayer = textLayers[layerIdx];

      /* Switch to the text layer's side so layers() finds it and the
         mockup thumbnail captures the correct side view. */
      const varSide = targetLayer.side || S.view;
      if (S.view !== varSide) {
        S.view = varSide;
        const src = mockupSrc();
        if (src && mImg.getAttribute('src') !== src) {
          await new Promise((resolve, reject) => {
            mImg.addEventListener('load', () => {
              try { onMockupLoad(); } catch (e) { DEBUG && console.warn('[dt] onMockupLoad threw:', e.message); }
              syncCanvas();
              resolve();
            }, { once: true });
            mImg.onerror = () => reject(new Error('Mockup image failed to load: ' + src));
            mImg.src = src;
          });
        } else computePA();
      }

      const actualLayer = layers().find(l => l.id === targetLayer.id);
      if (!actualLayer) return;

      /* ── Auto-size measurement pass ────────────────────── */
      const baseSize = actualLayer.fontSize || 36;
      const font = actualLayer.font || S.textFont || 'Montserrat';
      const bold = actualLayer.bold, italic = actualLayer.italic;
      const paW = (S.paByView[varSide] || S.pa).w;

      /* Ensure font is loaded before measuring */
      try { await document.fonts.load((bold ? 'bold ' : '') + (italic ? 'italic ' : '') + baseSize + 'px "' + font + '"'); } catch(_) {}

      const mc = document.createElement('canvas').getContext('2d');
      /* Floor in points (~5pt) converted to canvas pixels via print area */
      const _phys = SIDE_PHYS[varSide];
      const minPx = _phys ? Math.round(5 * paW / (_phys.wInches * 72)) : 6;
      const sizedNames = bulkNames.map(name => {
        mc.font = fontStr({ font, fontSize: baseSize, bold, italic });
        const w = mc.measureText(name).width;
        const fits = w <= paW * 0.95;
        const sz = fits ? baseSize : Math.floor(baseSize * (paW * 0.95) / w);
        const clamped = Math.max(minPx, sz);
        /* If clamped to floor and still overflows, flag for production review */
        const overflow = (sz < minPx);
        return { name, fontSize: clamped, scaled: !fits, overflow };
      });

      const scaledCount = sizedNames.filter(n => n.scaled).length;
      /* Find the longest name — measure every name at the SAME base size.
         (Measuring at fitted size flattened every scaled name to ~the print
         area width, so the tie always went to the first long name and a
         longer entry added later could never take over. Width at base size
         is the true length ranking: widest at base = tightest fit.) */
      let longestEntry = sizedNames[0];
      let longestW = 0;
      mc.font = fontStr({ font, fontSize: baseSize, bold, italic });
      sizedNames.forEach(n => {
        const w = mc.measureText(n.name).width;
        if (w > longestW) { longestW = w; longestEntry = n; }
      });

      /* ── Render mockup with longest name ───────────────── */
      if (_dtBulkBusy) { addBtn.disabled = false; addBtn.textContent = 'Review Order'; return; }
      _dtBulkBusy = true; /* released after the review-cells pass below */
      /* v2.12.3: paint-source repair BEFORE the longest-name text swap below —
         an await inside the swap window would let queued handlers serialize
         the falsified text (review amendment D3). */
      await ensureSideLayerPaintSources(S.view);
      const origText = actualLayer.text, origName = actualLayer.name, origSize = actualLayer.fontSize;
      actualLayer.text = longestEntry.name;
      actualLayer.name = longestEntry.name.slice(0, 25);
      actualLayer.fontSize = longestEntry.fontSize;
      measureText(actualLayer); render();
      const thumbnail = mockupPng(S.view);
      const thumbnailHi = mockupPng(S.view, 2);
      /* v2.12.0: proof-sheet data for the VARIABLE side is measured HERE,
         inside the swap window — the longest name is restored two lines
         down, so measuring at proof-build time would read the wrong text. */
      const _pvGeom = (thumbnailHi || thumbnail) ? mockupPng.lastGeom : null;
      const proofVarPaFrac = (_pvGeom && _pvGeom.pa && _pvGeom.cw > 0 && _pvGeom.ch > 0)
        ? { x: _pvGeom.pa.x / _pvGeom.cw, y: _pvGeom.pa.y / _pvGeom.ch,
            w: _pvGeom.pa.w / _pvGeom.cw, h: _pvGeom.pa.h / _pvGeom.ch }
        : null;
      const proofVarArt = _sideArtworkDims(S.view);
      actualLayer.text = origText; actualLayer.name = origName; actualLayer.fontSize = origSize;
      measureText(actualLayer); render();

      /* Tightly-cropped copy for the on-screen hero (PDF + cart thumb keep the full render) */
      const heroThumb = thumbnailHi ? await cropPngToAlpha(thumbnailHi) : null;

      /* ── Render production-proof cells (on-screen cap: 20) ─
         Per-name cells render the chosen text true-to-fitted-size inside
         the print-area boundary. We only paint the first 20 on screen;
         the full set always lives in the downloadable proof PDF. */
      addBtn.textContent = 'Rendering names…';
      const phys = SIDE_PHYS[S.view];
      const paWIn = phys ? phys.wInches : 3, paHIn = phys ? phys.hInches : 3;
      const nameDPI = 150;
      const cellW = Math.round(paWIn * nameDPI), cellH = Math.round(paHIn * nameDPI);
      const escHtml = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

      const DT_BULK_DISPLAY_CAP = 20;
      const shownNames = sizedNames.slice(0, DT_BULK_DISPLAY_CAP);
      const hiddenCount = sizedNames.length - shownNames.length;
      /* Each cell renders the FULL side (all layers) with the name swapped
         in, so the review shows exactly what ships — not just the name.
         Sequential (shared layer object); renderProductionPNG includes any
         raster/clipart layers natively. */
      const cellImgs = [];
      for (let ci = 0; ci < shownNames.length; ci++) {
        const url = await bulkWithNameApplied(actualLayer, shownNames[ci], async () => {
          const c = document.createElement('canvas');
          c.width = cellW; c.height = cellH;
          const cx = c.getContext('2d');
          cx.fillStyle = '#FAF9F6'; cx.fillRect(0, 0, cellW, cellH);
          /* Full-bleed draw: cell and composite share the print area's exact
             aspect, so any inset would stretch the art. Border paints on top. */
          try {
            const compPng = await renderProductionPNG(varSide);
            if (compPng) { const img = await _bulkLoadImg(compPng); cx.drawImage(img, 0, 0, cellW, cellH); }
          } catch (e) { DEBUG && console.warn('[dt] bulk cell render failed:', e.message); }
          cx.strokeStyle = '#C8B7A6'; cx.lineWidth = 2; cx.setLineDash([8, 6]);
          cx.strokeRect(6, 6, cellW - 12, cellH - 12); cx.setLineDash([]);
          const u = c.toDataURL('image/png'); c.width = 0; return u;
        });
        cellImgs.push(url);
      }
      _dtBulkBusy = false; /* review-cells pass done — overlays may close again */

      const methodLabel = S.method === 'engrave' ? 'Laser Engrave' : 'Color Print';
      const cellsHtml = shownNames.map((n, i) => {
        const isLong = n.name === longestEntry.name;
        const tag = n.overflow
          ? '<span class="dt-imr-tag" style="background:#e74c3c;color:#fff" title="Text too long — may clip in production">overflow</span>'
          : isLong
          ? '<span class="dt-imr-tag dt-imr-tag--long">longest</span>'
          : (n.scaled ? '<span class="dt-imr-tag dt-imr-tag--fit">auto</span>'
                      : `<span class="dt-imr-pt">${n.fontSize}px</span>`);
        return `<figure class="dt-imr-cell${isLong ? ' is-long' : ''}">
          <img class="dt-imr-cellimg" src="${cellImgs[i]}" alt="${escHtml(n.name)}" data-zoom="1">
          <figcaption class="dt-imr-cellfoot"><span class="dt-imr-cellname">${escHtml(n.name)}</span>${tag}</figcaption>
        </figure>`;
      }).join('');

      const moreHtml = hiddenCount > 0
        ? `<button class="dt-imr-more" id="dt-bulkreview-more">
             <span class="dt-imr-more__n">+${hiddenCount} more</span>
             <span class="dt-imr-more__t">Download the proof PDF to review every name</span>
           </button>`
        : '';

      const overflowCount = sizedNames.filter(n => n.overflow).length;
      const scaledNote = scaledCount
        ? `<span class="dt-imr-scaled"> · ${scaledCount} auto-sized to fit</span>`
        : '';
      const overflowNote = overflowCount
        ? `<span style="color:#e74c3c;font-weight:600"> · ${overflowCount} may clip</span>`
        : '';

      /* ── Show immersive review (light, full-screen) ────── */
      const reviewOverlay = document.createElement('div');
      reviewOverlay.className = 'dt-imr-overlay';
      reviewOverlay.innerHTML = `
        <div class="dt-imr-top">
          <span class="dt-imr-eyebrow">Group order · review names</span>
          <button class="dt-imr-x" id="dt-bulkreview-close" aria-label="Back to editing">&times;</button>
        </div>
        <div class="dt-imr-main">
          <div class="dt-imr-hero">
            ${heroThumb ? `<img src="${heroThumb}" id="dt-bulkreview-mockup" class="dt-imr-heroimg" alt="Longest name preview" data-zoom="1">` : ''}
            <div class="dt-imr-shadow"></div>
            <div class="dt-imr-herocap">
              <span class="dt-imr-badge">Longest name · auto-sized to ${longestEntry.fontSize}px</span>
              <div class="dt-imr-herosub">This is the tightest fit. Every shorter name renders larger inside the same print area.</div>
            </div>
          </div>
          <div class="dt-imr-proof">
            <div class="dt-imr-proofhd">Production proof · actual size</div>
            <div class="dt-imr-strip">${cellsHtml}${moreHtml}</div>
          </div>
        </div>
        <div class="dt-imr-bar">
          <button class="dt-imr-barx" id="dt-bulkreview-close2" aria-label="Close review">&times;</button>
          <div class="dt-imr-meta">
            <div>
              <div class="dt-imr-metaside">${escHtml(targetLayer.side || S.view)} · ${methodLabel}</div>
              <div class="dt-imr-metasub">Font: ${escHtml(font)} · one item per name${scaledNote}${overflowNote}</div>
            </div>
            <span class="dt-imr-pill">${sizedNames.length} names</span>
          </div>
          <button class="dt-btn dt-imr-pdf" id="dt-bulkreview-pdf">⇣ Download Proof PDF</button>
          <div class="dt-imr-actions">
            <button class="dt-imr-edit" id="dt-bulkreview-back">← Edit names</button>
            <button class="dt-imr-add" id="dt-bulkreview-confirm">Confirm — add ${sizedNames.length} items →</button>
          </div>
        </div>`;

      document.body.appendChild(reviewOverlay);
      document.body.classList.add('dt-imr-open');
      positionImrPanel(reviewOverlay);
      watchImrPanel(reviewOverlay);
      const bulkHeroImg = reviewOverlay.querySelector('#dt-bulkreview-mockup');
      if (bulkHeroImg && !bulkHeroImg.complete) bulkHeroImg.addEventListener('load', () => positionImrPanel(reviewOverlay), { once: true });
      const closeReview = () => {
        if (_dtBulkBusy) return; /* a render pass has a name substituted in */
        if (reviewOverlay.querySelector('#dt-bulkreview-close')?.disabled) return;
        document.removeEventListener('keydown', onBulkReviewKey);
        document.body.classList.remove('dt-imr-open');
        reviewOverlay.classList.add('is-closing');
        setTimeout(() => { reviewOverlay.remove(); addBtn.textContent = 'Review Order'; addBtn.disabled = false; }, 200);
      };

      const onBulkReviewKey = (e) => {
        if (e.key !== 'Escape') return;
        const zoomEl = document.querySelector('.dt-imr-zoom');
        if (zoomEl) zoomEl.remove();
        else closeReview();
      };
      document.addEventListener('keydown', onBulkReviewKey);

      reviewOverlay.querySelector('#dt-bulkreview-close').addEventListener('click', closeReview);
      reviewOverlay.querySelector('#dt-bulkreview-back').addEventListener('click', closeReview);
      reviewOverlay.querySelector('#dt-bulkreview-close2')?.addEventListener('click', closeReview);
      reviewOverlay.querySelector('#dt-bulkreview-more')?.addEventListener('click', () => reviewOverlay.querySelector('#dt-bulkreview-pdf')?.click());

      reviewOverlay.querySelectorAll('[data-zoom]').forEach(img => {
        img.addEventListener('click', function () {
          const z = document.createElement('div');
          z.className = 'dt-imr-zoom';
          z.innerHTML = `<img src="${this.src}" alt="">`;
          z.addEventListener('click', () => z.remove());
          document.body.appendChild(z);
        });
      });

      /* ── Download proof PDF ─────────────────────────── */
      reviewOverlay.querySelector('#dt-bulkreview-pdf').addEventListener('click', async () => {
        if (_dtBulkBusy) return; /* another render pass owns the shared layer */
        const pdfBtn = reviewOverlay.querySelector('#dt-bulkreview-pdf');
        const _pdfConfirmBtn = reviewOverlay.querySelector('#dt-bulkreview-confirm');
        _dtBulkBusy = true;
        pdfBtn.disabled = true; pdfBtn.textContent = 'Generating PDF…';
        /* Also block Confirm visually while the PDF renders — both drive
           bulkWithNameApplied on the SAME shared layer. The lock above is
           authoritative; the disables just communicate it. */
        if (_pdfConfirmBtn) _pdfConfirmBtn.disabled = true;
        try {
          const jsPDF = await waitForJsPDF();
          const doc = new jsPDF({ unit: 'in', format: 'letter' });
          doc.setFontSize(14);
          doc.text('Group Order Proof — ' + sizedNames.length + ' items', 0.75, 0.65);
          doc.setFontSize(9);
          doc.text(font + ' · ' + (S.method === 'engrave' ? 'Laser Engrave' : 'Color Print'), 0.75, 0.9);
          if (thumbnail) {
            try {
              const imgW = 5, imgH = imgW * (canvas.height / canvas.width);
              doc.addImage(thumbnail, 'PNG', (8.5 - imgW) / 2, 1.3, imgW, Math.min(imgH, 8));
            } catch (_) {}
          }
          const dpi = 200;
          const cw = Math.round(paWIn * dpi), ch = Math.round(paHIn * dpi);
          const perPage = 10;
          const cols = Math.max(1, Math.floor(perPage / paHIn));
          const pages = Math.ceil(sizedNames.length / cols);
          for (let pg = 0; pg < pages; pg++) {
            doc.addPage('letter');
            const slice = sizedNames.slice(pg * cols, (pg + 1) * cols);
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = cw; tmpCanvas.height = ch * slice.length;
            const tmpCtx = tmpCanvas.getContext('2d');
            /* Full-side artwork per cell (all layers), rendered sequentially. */
            for (let i = 0; i < slice.length; i++) {
              const yOff = i * ch;
              await bulkWithNameApplied(actualLayer, slice[i], async () => {
                tmpCtx.fillStyle = '#FFFFFF'; tmpCtx.fillRect(0, yOff, cw, ch);
                tmpCtx.strokeStyle = '#ccc'; tmpCtx.lineWidth = 1;
                tmpCtx.strokeRect(1, yOff + 1, cw - 2, ch - 2);
                try {
                  const compPng = await renderProductionPNG(varSide);
                  if (compPng) { const img = await _bulkLoadImg(compPng); tmpCtx.drawImage(img, 0, yOff, cw, ch); }
                } catch (e) { DEBUG && console.warn('[dt] bulk PDF cell render failed:', e.message); }
              });
            }
            const sliceH = paHIn * slice.length;
            try { doc.addImage(tmpCanvas.toDataURL('image/png'), 'PNG', (8.5 - paWIn) / 2, 0.5, paWIn, sliceH); } catch (_) {}
            tmpCanvas.width = 0;
          }
          doc.save('bulk-order-proof.pdf');
        } catch (err) {
          DEBUG && console.error('[dt] PDF failed:', err);
          alert('PDF generation failed: ' + err.message);
        }
        _dtBulkBusy = false;
        pdfBtn.disabled = false; pdfBtn.textContent = '⇣ Download Proof PDF';
        if (_pdfConfirmBtn) _pdfConfirmBtn.disabled = false;
      });

      /* ── Confirm — add to cart ──────────────────────── */
      reviewOverlay.querySelector('#dt-bulkreview-confirm').addEventListener('click', async () => {
        if (_dtBulkBusy) return; /* another render pass owns the shared layer */
        const confirmBtn = reviewOverlay.querySelector('#dt-bulkreview-confirm');
        _dtBulkBusy = true;
        confirmBtn.disabled = true; confirmBtn.textContent = 'Processing…';
        /* Block the PDF download while we build production files — both mutate
           the shared layer, so they must never run at the same time. */
        reviewOverlay.querySelector('#dt-bulkreview-pdf')?.setAttribute('disabled', 'true');
        reviewOverlay.querySelector('#dt-bulkreview-close')?.setAttribute('disabled', 'true');
        reviewOverlay.querySelector('#dt-bulkreview-back')?.setAttribute('disabled', 'true');
        reviewOverlay.querySelector('#dt-bulkreview-close2')?.setAttribute('disabled', 'true');
        /* Wait overlay — bulk builds per-name proofs, so budget by name count
           (each proof ≈ a light "side" of work) on top of the designed sides */
        dtWaitOpen(
          CFG.sides.filter(sl => S.byView[sl]?.some(l => l.visible !== false)).length
            + Math.ceil(sizedNames.length / 5),
          () => { confirmBtn.disabled = false; confirmBtn.click(); }
        );

        try {
          /* Bulk folder aligned to the single-item scheme (rev 9, Brandon):
             {sessionId}/{variantId}-{serial}. Serial hoisted ABOVE folder
             creation; markSerialDirty() after every successful add prevents
             a single-item add and a following no-edit bulk add from sharing
             a serial (folder overwrite + duplicate _design_serial). */
          const bulkSerial = getOrCreateSerial();
          const s3Folder = `${getCartSessionId()}/${S.variantId}-${bulkSerial}`;

          const escXml = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

          /* Build the combined production SVG — FULL side artwork per item
             (every layer, not just the swapped name). Trace any raster art
             ONCE for the whole batch (name-independent), then composite each
             name through the same builder the single-item pipeline uses and
             stack the results into PA-sized cells. */
          const bulkVarSide = targetLayer.side || S.view;
          const _bulkCS = getProductionCoordSpace(bulkVarSide);
          const bulkPxW = _bulkCS ? _bulkCS.pxW : Math.round(paWIn * PROD_DPI);
          const bulkPxH = _bulkCS ? _bulkCS.pxH : Math.round(paHIn * PROD_DPI);
          const bulkForceBlack = S.method === 'engrave';
          /* Effective method of the VARIABLE side drives the per-name recipe
             (rev 9): engrave / full print / transfer. */
          const bulkVarMethod = effectiveSideMethod(bulkVarSide);
          const bulkVarTransfer = isTransferMethod(bulkVarMethod);

          confirmBtn.textContent = 'Preparing artwork…';
          dtWaitStatus('Preparing artwork…');
          /* Transfer bulk ships PNG + PREPRESS only — no vector output, so no
             trace (same gate as the single-item path). */
          const bulkVarTracedSvg = bulkVarTransfer ? null : await bulkTraceVariableRaster(bulkVarSide);

          /* rev 9: ALL SVG uploads removed (combined live-text SVG + per-name
             SVGs + combined outlined SVG). The per-name PDF/EPS/PNG sets and
             the engrave combined masters are the production files now.
             Internal outlined SVGs still feed the engrave converters. */

          /* Stack per-item inner markups into one production SVG. Ids are
             namespaced per cell (each cell restarts at text-layer-0 etc.).
             The traced raster group (id="traced-raster") is byte-identical
             in every cell, so cell 1 keeps the real one and later cells
             reference it via <use> — without this a raster design duplicates
             the whole trace once per name and a large batch balloons the
             combined file past btoa/memory limits. */
          const _tracedInnerRaw = bulkVarTracedSvg
            ? extractTracedSvgGroup(bulkVarTracedSvg, bulkPxW, bulkPxH) : '';
          const assembleStacked = (inners, kindLabel) => {
            const parts = [
              '<?xml version="1.0" encoding="UTF-8"?>',
              `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${paWIn}in" height="${paHIn * sizedNames.length}in" viewBox="0 0 ${bulkPxW} ${bulkPxH * sizedNames.length}">`,
              `<metadata>Font: ${escXml(font)} | Items: ${sizedNames.length} | Method: ${S.method} | ${kindLabel}</metadata>`,
            ];
            sizedNames.forEach((n, i) => {
              let inner = _bulkNamespaceIds(inners[i] || '', `c${i + 1}-`);
              if (_tracedInnerRaw) {
                const tNs = _bulkNamespaceIds(_tracedInnerRaw, `c${i + 1}-`);
                inner = (i === 0)
                  ? inner.split(tNs).join(`<g id="dtBulkTracedShared">${tNs}</g>`)
                  : inner.split(tNs).join('<use xlink:href="#dtBulkTracedShared" href="#dtBulkTracedShared"/>');
              }
              parts.push(
                `<g id="item-${i + 1}" transform="translate(0,${i * bulkPxH})">`,
                `<rect width="${bulkPxW}" height="${bulkPxH}" fill="none" stroke="#ccc" stroke-width="1" stroke-dasharray="6,4" data-dt-guide="1"/>`,
                inner,
                '</g>'
              );
            });
            parts.push('</svg>');
            return parts.join('\n');
          };

          confirmBtn.textContent = 'Uploading files…';

          const templateState = serializeDesign();

          /* Lightweight copy for _design_data (strip binary to stay under Shopify limits) */
          const templateLite = JSON.parse(JSON.stringify(templateState));
          for (const sKey of Object.keys(templateLite.sides || {})) {
            (templateLite.sides[sKey].layers || []).forEach(ld => {
              delete ld.b64; delete ld.svgMarkup; delete ld._maskMarkup; delete ld._content;
            });
          }

          const csvData = 'Name,FontSizePt,Scaled\n' + sizedNames.map(n => `"${n.name.replace(/"/g, '""')}",${(n.fontSize * 72 * paWIn / paW).toFixed(1)},${n.scaled ? 'Yes' : 'No'}`).join('\n');
          const csvDataUrl = 'data:text/csv;base64,' + btoa(unescape(encodeURIComponent(csvData)));

          const [templateUrl, csvUrl] = await Promise.all([
            uploadProdFile('root', 'data:application/json;base64,' + btoa(unescape(encodeURIComponent(JSON.stringify(templateState)))), 'template-design.json', 'application/json', s3Folder, bulkSerial),
            uploadProdFile('root', csvDataUrl, 'names-with-sizes.csv', 'text/csv', s3Folder, bulkSerial),
          ]);

          /* ── Per-name production files — FULL side artwork ──
             Per name: live-text SVG, OUTLINED vector-text SVG (text as
             paths via opentype.js — no font dependency), EPS (+DXF for
             engrave) from the outlined paths via geoWorkerConvert, a
             hybrid vector-text PDF, and a 300 DPI proof PNG. Rendered
             sequentially (the composite builders read the shared layer
             object, so names must not overlap), uploaded per batch in
             parallel. Vector conversions are per-name best-effort: a
             failure skips that file, never the batch (mirrors the
             single-item pipeline's per-format try/catch). */
          const bulkPhys = SIDE_PHYS[bulkVarSide];
          const bulkSfx = bulkForceBlack ? 'engrave' : 'print';
          /* EPS only: the print branch emits a raster composite while engrave emits
             AI8 vector, and they shared one filename. Engrave keeps its name; print
             is stamped PRODUCTION-RASTER to match the single-item path (v2.9.19).
             DXF/PDF still use bulkSfx — their vector/raster nature is unambiguous. */
          const bulkEpsSfx = bulkForceBlack ? 'engrave' : 'print-PRODUCTION-RASTER';
          const bulkOutlinedInners = [];
          const PROOF_BATCH = 5;
          for (let bi = 0; bi < sizedNames.length; bi += PROOF_BATCH) {
            const batch = sizedNames.slice(bi, bi + PROOF_BATCH);
            confirmBtn.textContent = `Rendering items ${bi + 1}–${Math.min(bi + PROOF_BATCH, sizedNames.length)} of ${sizedNames.length}…`;
            dtWaitStatus('Rendering proofs…');
            const rendered = [];
            for (let j = 0; j < batch.length; j++) {
              const assets = await bulkWithNameApplied(actualLayer, batch[j], async () => {
                const compPng = await renderProductionPNG(bulkVarSide);

                /* Outlined vector text — ENGRAVE ONLY now (rev 9): its
                   consumers are the per-name EPS/DXF and the combined
                   engrave masters. Print PDFs outline internally, and every
                   SVG upload is gone, so outlining print names was waste. */
                let outlinedSvg = null;
                if (bulkForceBlack) {
                  try { outlinedSvg = await buildCompositeOutlinedSvg(bulkVarSide, true, bulkVarTracedSvg); }
                  catch (e) { gwarn('bulk_outline_failed', 'bulk outline failed: ' + e.message); }
                }

                let epsStr = null, dxfStr = null;
                if (bulkForceBlack) {
                  /* ENGRAVE per-name DXF + EPS (pre-existing; rev 9 keeps
                     them AND the combined masters — Brandon Jul 30). */
                  if (outlinedSvg && bulkPhys) {
                    try { epsStr = await geoWorkerConvert('eps-ai', outlinedSvg, bulkPhys); }
                    catch (e) { gwarn('bulk_eps_failed', 'bulk EPS failed: ' + e.message); }
                    try { dxfStr = await geoWorkerConvert('dxf', outlinedSvg, bulkPhys); }
                    catch (e) { gwarn('bulk_dxf_failed', 'bulk DXF failed: ' + e.message); }
                  }
                } else if (!bulkVarTransfer && compPng && bulkPhys) {
                  /* FULL PRINT: raster EPS from the per-name composite (v2.9.6 —
                     DeviceRGB, RIP-managed color; same rationale as the
                     single-item path). TRANSFER: suppressed. */
                  try { epsStr = await generateRasterEPS(compPng, bulkPhys); }
                  catch (e) { DEBUG && console.warn('[dt] bulk raster EPS failed:', e.message); }
                }
        
                /* PRODUCTION PDF — FULL PRINT only (engrave ships EPS+DXF;
                   transfer suppresses it, rev 9). */
                let pdfDU = null;
                if (!bulkForceBlack && !bulkVarTransfer) {
                  /* v2.9.16: MUST pass the trace. generateProductionPDF narrows its raster
                     embed to photos whenever it has one, so calling it with two arguments
                     here would embed only bucket C while nothing painted bucket B — the
                     logo would vanish from every bulk PDF while the proof still showed it. */
                  /* v2.13.5 (CB37404): same-input retry + always-on gwarn,
                     matching the per-side fix. The old DEBUG-only catch is the
                     same silent-swallow pattern that dropped CB37404. */
                  for (let _bAtt = 1; _bAtt <= 2 && !pdfDU; _bAtt++) {
                    try { pdfDU = await generateProductionPDF(bulkVarSide, compPng, bulkVarTracedSvg); }
                    catch (e) {
                      gwarn('bulk_prod_pdf_gen_failed', 'Bulk PRODUCTION PDF attempt ' + _bAtt +
                        '/2 failed for ' + bulkVarSide + ': ' + (e && e.message));
                    }
                  }
                  if (!pdfDU) {
                    gwarn('bulk_prod_pdf_not_generated', 'Bulk PRODUCTION PDF permanently failed for ' + bulkVarSide + ' after 2 attempts');
                  }
                }

                /* PREPRESS PDF — ALL classes now, engrave included (rev 8/9). */
                let prepressDU = null;
                try { prepressDU = await generatePrepressPDF(bulkVarSide, compPng); }
                catch (e) { DEBUG && console.warn('[dt] bulk PREPRESS PDF failed:', e.message); }

                /* Per-name proof PNGs are DROPPED for engrave + full print
                   (rev 9 — the proof sheet covers review). For TRANSFER the
                   transparent composite IS the production PNG: no white
                   fill, uploaded to DTF below. */
                const prodPng = bulkVarTransfer ? compPng : null;
                return { outlinedSvg, epsStr, dxfStr, pdfDU, prepressDU, prodPng };
              });
              rendered.push(assets);
              /* Collect for the combined engrave masters ('' keeps alignment;
                 stays all-empty for print/transfer, which skips the stack) */
              bulkOutlinedInners.push(_bulkSvgInner(assets.outlinedSvg));
            }
            confirmBtn.textContent = `Uploading items ${bi + 1}–${Math.min(bi + PROOF_BATCH, sizedNames.length)} of ${sizedNames.length}…`;
            dtWaitStatus('Uploading proofs…');
            await Promise.all(batch.map((n, j) => {
              const idx = bi + j;
              const r = rendered[j];
              const safeName = n.name.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
              const uploads = [];
              /* rev 9: NO SVG uploads. Per-name production sets route by role:
                 engrave EPS→Minis + DXF→Radian; print PDF+EPS→ColorPrinting;
                 PREPRESS→Prepress (all classes); transfer PNG→DTF. */
              if (r.epsStr) uploads.push(uploadProdFile(bulkForceBlack ? 'engrave-eps' : 'print-eps', 'data:application/postscript;base64,' + btoa(r.epsStr), `name-${idx + 1}-${safeName}-${bulkEpsSfx}.eps`, 'application/postscript', s3Folder, bulkSerial));
              if (r.dxfStr) uploads.push(uploadProdFile('engrave-dxf', 'data:application/dxf;base64,' + btoa(r.dxfStr), `name-${idx + 1}-${safeName}-${bulkSfx}.dxf`, 'application/dxf', s3Folder, bulkSerial));
              if (r.pdfDU)  uploads.push(uploadProdFile('print-pdf', r.pdfDU, `name-${idx + 1}-${safeName}-${bulkSfx}-PRODUCTION.pdf`, 'application/pdf', s3Folder, bulkSerial));
              if (r.prepressDU) uploads.push(uploadProdFile('prepress-pdf', r.prepressDU, `name-${idx + 1}-${safeName}-${bulkSfx}-PREPRESS.pdf`, 'application/pdf', s3Folder, bulkSerial));
              if (r.prodPng) uploads.push(uploadProdFile('transfer-png', r.prodPng, `name-${idx + 1}-${safeName}-300dpi.png`, 'image/png', s3Folder, bulkSerial));
              return Promise.all(uploads);
            }));
          }

          /* ── Combined ENGRAVE masters from the outlined stack (rev 9:
             engrave keeps BOTH the combined EPS+DXF — today's laser workflow
             — AND the per-name files above; Brandon Jul 30). Print/transfer
             build no combined masters and no SVG uploads: bulkOutlinedInners
             stays all-empty for them, so this block self-skips.
             (geoWorker's 300-units-per-inch assumption holds for the stack:
             svgH = hInches*N*300 = pxH*N). */
          let bulkEpsUrl = null, bulkDxfUrl = null;
          if (bulkOutlinedInners.some(Boolean) && bulkForceBlack) {
            confirmBtn.textContent = 'Uploading vector master…';
            dtWaitStatus('Uploading vector master…');
            const outlinedCombined = assembleStacked(bulkOutlinedInners, 'Outlined vector text per item');
            const stackedPhys = { wInches: paWIn, hInches: paHIn * sizedNames.length };
            try {
              const cEps = await geoWorkerConvert('eps-ai', outlinedCombined, stackedPhys);
              if (cEps) {
                bulkEpsUrl = await uploadProdFile('engrave-eps', 'data:application/postscript;base64,' + btoa(cEps), 'bulk-names-production.eps', 'application/postscript', s3Folder, bulkSerial);
              }
            } catch (e) { DEBUG && console.warn('[dt] combined EPS failed:', e.message); }
            try {
              const cDxf = await geoWorkerConvert('dxf', outlinedCombined, stackedPhys);
              if (cDxf) bulkDxfUrl = await uploadProdFile('engrave-dxf', 'data:application/dxf;base64,' + btoa(cDxf), 'bulk-names-production.dxf', 'application/dxf', s3Folder, bulkSerial);
            } catch (e) { DEBUG && console.warn('[dt] combined DXF failed:', e.message); }
          }

          let cartImageUrl = null;
          if (thumbnail) {
            confirmBtn.textContent = 'Uploading preview…';
            cartImageUrl = await uploadProdFile('root', thumbnail, 'bulk-preview.png', 'image/png', s3Folder, bulkSerial, 'inline');
          }

          const POSITION_LABELS = ['Primary', 'Secondary', 'Third', 'Fourth'];
          const bulkMethods = new Set();
          const bulkDesignedSides = CFG.sides.filter(slug =>
            S.byView[slug].some(l => l.visible !== false)
          );
          bulkDesignedSides.forEach(slug => bulkMethods.add(effectiveSideMethod(slug)));

          let bulkDesignMethod;
          if (bulkMethods.size === 1) {
            bulkDesignMethod = [...bulkMethods][0];
          } else if (bulkMethods.size > 1) {
            bulkDesignMethod = [...bulkMethods].join(', ');
          } else {
            bulkDesignMethod = S.method === 'engrave' ? 'Laser Engrave' : (CFG.printMethodName || 'Cylindrical Print');
          }

          /* (bulkSerial hoisted above folder creation — rev 9) */
          const variableSideSlug = targetLayer.side || S.view;

          const props = {
            _design_serial: bulkSerial,
            _design_method: bulkDesignMethod,
            _location_count: String(bulkDesignedSides.length),
            _bulk_order: 'Yes',
            _bulk_quantity: String(sizedNames.length),
            _bulk_scaled_count: String(scaledCount),
            ...(overflowCount ? { _bulk_overflow_count: String(overflowCount) } : {}),
            _bulk_variable_layer: targetLayer.name,
            _bulk_variable_side: CFG.sideConfigs[variableSideSlug]?.label || variableSideSlug,
            _bulk_font: fontDisplayName(font),
            _bulk_base_size: (baseSize * 72 * paWIn / paW).toFixed(1) + 'pt',
            _bulk_template_url: templateUrl,
            _bulk_csv_url: csvUrl,
            /* rev 9: _bulk_names_svg / _bulk_names_outlined dropped (all SVG
               uploads removed; verified writers-only, no readers). Per-name
               proof PNGs dropped too, so no _bulk_individual_proofs. */
            ...(bulkEpsUrl ? { _bulk_names_eps: bulkEpsUrl } : {}),
            ...(bulkDxfUrl ? { _bulk_names_dxf: bulkDxfUrl } : {}),
            _bulk_longest_name: longestEntry.name,
            _bulk_sample_names: sizedNames.slice(0, 5).map(n => n.name).join(', '),
            _s3_folder: `https://us-east-2.console.aws.amazon.com/s3/buckets/custom-branding-design-files?region=us-east-2&prefix=production-files/${s3Folder}/`,
            _design_data: JSON.stringify(templateLite),
          };

          /* ── Per-side mockups, production files, and metadata ── */
          const bulkProofEntries = []; /* for the single all-sides PROOF file */
          const bulkOrigView = S.view; /* v2.12.0: restored AFTER the loop (race fix below) */
          for (let sideIdx = 0; sideIdx < bulkDesignedSides.length; sideIdx++) {
            const sideSlug = bulkDesignedSides[sideIdx];
            const pos = POSITION_LABELS[sideIdx];
            const label = CFG.sideConfigs[sideSlug]?.label || sideSlug;

            props[`_${pos}_location`] = label;

            const _bulkSideMethod = effectiveSideMethod(sideSlug);
            const _bulkSidePhys = SIDE_PHYS[sideSlug];
            const _bulkSideSub = (_bulkSidePhys ? `${_bulkSidePhys.wInches}" x ${_bulkSidePhys.hInches}" · ` : '') + _bulkSideMethod;

            /* Mockup preview */
            if (sideSlug === variableSideSlug) {
              /* Variable side: reuse the pre-captured thumbnail (longest name rendered).
                 v2.12.0: the PROOF entry uses the hi-res capture + the swap-window
                 measurements (paFrac/artIn) — the cart preview upload stays scale-1. */
              if (cartImageUrl) props[`_${pos}_mockup_preview`] = cartImageUrl;
              const _vPng = thumbnailHi || thumbnail;
              if (_vPng) bulkProofEntries.push({
                label, slug: sideSlug, png: _vPng,
                sub: _bulkSideSub + ' · longest name shown',
                paFrac: proofVarPaFrac, phys: _bulkSidePhys, method: _bulkSideMethod,
                artIn: proofVarArt,
              });
            } else {
              /* Non-variable side: switch view, capture mockup, upload */
              if (S.view !== sideSlug) {
                S.view = sideSlug;
                const src = mockupSrc();
                if (src && mImg.getAttribute('src') !== src) {
                  await new Promise((resolve, reject) => {
                    mImg.addEventListener('load', () => {
                      try { onMockupLoad(); } catch (e) { DEBUG && console.warn('[dt] onMockupLoad threw:', e.message); }
                      syncCanvas();
                      resolve();
                    }, { once: true });
                    mImg.onerror = () => reject(new Error('Mockup image failed to load: ' + src));
                    mImg.src = src;
                  });
                } else computePA();
              }
              confirmBtn.textContent = `Capturing ${label} mockup…`;
              dtWaitStatus(`Capturing ${label} mockup…`);
              await ensureSideLayerPaintSources(sideSlug); /* v2.12.3: repair missing/stale display rasters BEFORE capture */
      const _bCap = captureProofMockup(sideSlug); /* v2.12.0: hi-res + pa geometry */
              const mPng = _bCap ? _bCap.png : null;
              if (mPng) {
                const mUrl = await uploadProdFile('root', mPng, `${sideSlug}-mockup.png`, 'image/png', s3Folder, bulkSerial, 'inline');
                if (mUrl) props[`_${pos}_mockup_preview`] = mUrl;
                bulkProofEntries.push({
                  label, slug: sideSlug, png: mPng, sub: _bulkSideSub,
                  paFrac: _bCap ? _bCap.paFrac : null, phys: _bulkSidePhys, method: _bulkSideMethod,
                  artIn: _sideArtworkDims(sideSlug),
                });
              }
              /* v2.12.0: per-iteration view restore REMOVED — it had the same
                 race the single flow's loop fixed (non-awaited loadMockup left
                 mImg loading; a following side sharing that src skipped the
                 switch, mockupPng saw complete===false and returned null,
                 silently dropping the side). Restored once after the loop. */
            }

            /* Production files */
            if (sideSlug === variableSideSlug) {
              if (S.method === 'engrave') {
                /* ENGRAVE: the combined masters stay the pointer files
                   (today's laser workflow — DXF file 1, EPS file 2). The
                   per-name DXF/EPS set lives in Radian/Minis via _s3_folder. */
                const vFiles = [bulkDxfUrl, bulkEpsUrl].filter(Boolean);
                if (vFiles.length) {
                  props[`_${pos}_production_type`] = 'engrave-vector-2color';
                  props[`_${pos}_production_file_count`] = String(vFiles.length);
                  vFiles.forEach((url, fi) => { props[`_${pos}_production_file_${fi + 1}`] = url; });
                } else {
                  props[`_${pos}_production_file_count`] = '0';
                }
              } else {
                /* PRINT/TRANSFER: per-name sets are FOLDER-REFERENCED
                   (Brandon Jul 30) — no per-file props; production works
                   from the _s3_folder subfolders. _bulk_quantity carries
                   the name count. */
                props[`_${pos}_production_type`] = bulkVarTransfer ? 'print-transfer-png' : 'print-pdf-eps-per-name';
                props[`_${pos}_production_file_count`] = '0';
              }
            } else {
              /* Non-variable side: generate full production files (EPS, DXF, PDF) */
              const setStatus = msg => { confirmBtn.textContent = msg; announceStatus(msg); dtWaitStatus(msg); };
              const prodUrls = await generateSideProduction(sideSlug, setStatus, s3Folder,
                { serial: bulkSerial, method: _bulkSideMethod });
              if (prodUrls) {
                props[`_${pos}_production_type`] = prodUrls.productionType;
                const _isPrint = !(prodUrls.productionType || '').startsWith('engrave');
                /* Engrave ships vector only (DXF-native machines). Spec order
                   (Brandon): DXF file 1, EPS file 2. DOS twin cut v2.9.11. */
                /* Print orders: PRODUCTION PDF first, then PREPRESS PDF, then EPS backup.
                   TRANSFER (rev 9): PNG file 1, PREPRESS file 2. */
                let prodFiles;
                if (prodUrls.productionType === 'print-transfer-png') {
                  prodFiles = [prodUrls.png, prodUrls.pdfPrepress].filter(Boolean);
                } else if (_isPrint) {
                  prodFiles = [prodUrls.pdfProduction, prodUrls.pdfPrepress, prodUrls.eps, prodUrls.dxf].filter(Boolean);
                } else {
                  prodFiles = [prodUrls.dxf, prodUrls.eps].filter(Boolean);
                }
                if (prodFiles.length === 0 && prodUrls.png) {
                  prodFiles = [prodUrls.png];
                  props[`_${pos}_production_type`] = prodUrls.productionType.replace('vector', 'raster-fallback');
                }
                props[`_${pos}_production_file_count`] = String(prodFiles.length);
                prodFiles.forEach((url, fileIdx) => {
                  props[`_${pos}_production_file_${fileIdx + 1}`] = url;
                });
              } else {
                props[`_${pos}_production_file_count`] = '0';
              }
            }

            /* Imprint size */
            const phys = SIDE_PHYS[sideSlug];
            if (phys) props[`_${pos}_imprint_size`] = `${phys.wInches}" x ${phys.hInches}"`;

            /* Per-side font */
            const bSideLayers = S.byView[sideSlug]?.filter(l => l.visible !== false) || [];
            const bSideFonts = [...new Set(bSideLayers.filter(l => l.type === 'text' && l.font).map(l => fontDisplayName(l.font)))];
            if (bSideFonts.length) props[`_${pos}_font`] = bSideFonts.join(', ');

            /* Source file uploads — non-fatal, uses shared helper (→ Prepress) */
            await uploadSourceFiles(bSideLayers, sideSlug, pos, s3Folder, props, msg => { confirmBtn.textContent = msg; }, bulkSerial);
          }
          /* v2.12.0: single deferred view restore (see race note in the loop) */
          if (S.view !== bulkOrigView) { S.view = bulkOrigView; loadMockup(); }

          /* ── Single PROOF file: every designed side, side by side ── */
          try {
            confirmBtn.textContent = 'Building proof sheet…';
            dtWaitStatus('Building proof sheet…');
            const _bulkSheetData = _proofSheetData(bulkSerial, bulkMethods, bulkDesignedSides);
            const _bpPdf = await buildBulkProofSheetPdf(sizedNames, bulkProofEntries, bulkSerial, { font, bold, italic }, _bulkSheetData);
            if (_bpPdf) {
              const _bpUrl = await uploadProdFile('proof-sheet', _bpPdf, 'proof-sheet.pdf', 'application/pdf', s3Folder, bulkSerial);
              if (_bpUrl) props._proof_file = _bpUrl;
            }
          } catch (bpErr) { DEBUG && console.warn('[dt] bulk proof sheet failed (non-fatal):', bpErr.message); }

          if (cartImageUrl) props._cart_image = cartImageUrl;

          /* ── Add main product + upcharges to cart ──────────── */
          confirmBtn.textContent = 'Adding to cart…';
          dtWaitStatus('Adding to cart…');
          const cartItems = [{ id: S.variantId, quantity: sizedNames.length, properties: props }];

          /* Upcharge line items for additional designed sides */
          const bulkUpcharges = calcUpcharges(bulkDesignedSides);
          bulkUpcharges.forEach(u => {
            if (!u.variantId) return;
            cartItems.push({ id: u.variantId, quantity: sizedNames.length, properties: { _design_serial: bulkSerial, _upcharge_for: bulkSerial } });
          });

          /* $1000 proof rule — (variant price + upcharges) x item count.
             Over the threshold, a proof is required: stamp the policy note
             on the design line and add the proof fee (qty 1 — cart-sync
             keeps proofs at one per design). */
          const _bulkBaseCents = (CFG.variants?.find(v => v.id === S.variantId)?.price || 0)
            + bulkUpcharges.reduce((a, c) => a + (c.cents || 0), 0);
          if (CFG.proofVariantId && (_bulkBaseCents * sizedNames.length) / 100 > 1000) {
            props._artist_proof = 'Yes';
            props._proof_required = 'We require proofs on amounts greater than $1000';
            cartItems.push({ id: CFG.proofVariantId, quantity: 1, properties: { _design_serial: bulkSerial, _proof_for: bulkSerial } });
          }

          /* Atomic cart add — all items in one request so the main product
             and upcharge(s) always land together or not at all. */
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: cartItems }),
          });
          if (!res.ok) {
            let errMsg = 'Cart error: ' + res.status;
            try { const b = await res.json(); errMsg = b.description || b.message || (typeof b.errors === 'string' ? b.errors : b.errors ? JSON.stringify(b.errors) : null) || errMsg; } catch (_) {}
            throw new Error(errMsg);
          }

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          /* rev 9 guard: successful add consumes the serial (see single-item) */
          markSerialDirty();
          dtWaitDone();
          clearAutosave();
          _dtBulkBusy = false;
          celebrateThenGoToCart();

        } catch (err) {
          DEBUG && console.error('[dt] Bulk order failed:', err);
          _dtBulkBusy = false;
          dtWaitFail(err.dtUploadFile ? uploadFailDetail(err.dtUploadFile) : (err.message || 'Failed').slice(0, 90)); /* rev 14 */
          reviewOverlay.querySelector('#dt-bulkreview-pdf')?.removeAttribute('disabled');
          reviewOverlay.querySelector('#dt-bulkreview-close')?.removeAttribute('disabled');
          reviewOverlay.querySelector('#dt-bulkreview-back')?.removeAttribute('disabled');
          reviewOverlay.querySelector('#dt-bulkreview-close2')?.removeAttribute('disabled');
          confirmBtn.textContent = '⚠ ' + (err.message || 'Failed');
          setTimeout(() => { confirmBtn.textContent = 'Confirm — add ' + sizedNames.length + ' items →'; confirmBtn.disabled = false; }, 4000);
        }
      });
    });
  }

  /* ══════════════════════════════════════════════════════════════════
   * ANALYTICS — GA4 dataLayer
   * ══════════════════════════════════════════════════════════════════ */
  const dtAnalytics = {
    push(eventName, params) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        dt_product: CFG.productTitle || '',
        dt_variant_id: S.variantId || '',
        dt_method: S.method || '',
        dt_device: isMobile() ? 'mobile' : 'desktop',
        ...params,
      });
    },
    toolOpen(panel)       { this.push('dt_tool_open', { dt_tool: panel }); },
    clipartBrowse(cat)    { this.push('dt_clipart_browse', { dt_clipart_category: cat }); },
    clipartAdd(item, cat) { this.push('dt_clipart_add', { dt_clipart_item: item || '', dt_clipart_category: cat || '' }); },
    fontSelect(font)      { this.push('dt_font_select', { dt_font: font }); },
    layerAdd(type, name)  { this.push('dt_layer_add', { dt_layer_type: type, dt_layer_name: (name || '').slice(0, 40) }); },
    cartOpen()            { this.push('dt_cart_open', { dt_side_count: getDesignedSides().length }); },
    cartConfirm()         { this.push('dt_cart_confirm'); },
    cartSuccess(serial)   { this.push('dt_cart_success', { dt_serial: serial }); },
    cartError(msg)        { this.push('dt_cart_error', { dt_error_message: (msg || '').slice(0, 120) }); },
    error(source, msg)    { this.push('dt_error', { dt_error_source: source, dt_error_message: (msg || '').slice(0, 200) }); },
    sideSwitch(side)      { this.push('dt_side_switch', { dt_side: side }); },
  };

  /* Global JS error tracking */
  window.addEventListener('error', function (e) {
    /* Match only design-tool script files (path part, no query string) —
       the page URL itself contains "design-tool" via ?view=design-tool,
       which previously made this capture unrelated inline-script errors */
    var srcFile = (e.filename || '').split('?')[0];
    if (/design-tool[^\/]*\.js$/.test(srcFile)) {
      dtAnalytics.error('js_runtime', e.message + ' at ' + srcFile.split('/').pop() + ':' + e.lineno);
    }
  });
  window.addEventListener('unhandledrejection', function (e) {
    dtAnalytics.error('unhandled_promise', (e.reason && e.reason.message) || String(e.reason || 'unknown'));
  });

  /* ══════════════════════════════════════════════════════════════════
   * PUBLIC DEBUG API
   * ══════════════════════════════════════════════════════════════════ */
  window.__DT__ = {
    S, CFG, ctx, canvas, layers, selLayer, bbox,
    pushLayer, deleteLayer, reorder, render,
    updateLayersPanel, updatePropsPanel: forcePropsRebuild, updateCartBtn,
    pushHistory, uid, fontStr, measureText,
    applyTransform, paintLayer, addImageLayer, addSvgLayer,
    parseDims, physDPI, pxToInches, SIDE_PHYS, MONO_STYLES,
  };

  /* ── Go! ────────────────────────────────────────── */
  try { init(); } catch(e) { DEBUG && console.error('[dt] INIT FAILED:', e); }

  /* Re-render after all web fonts load — prevents canvas text from
     showing in fallback font then jumping when the real font arrives */
  document.fonts.ready.then(() => { render(); });
  try {

  } catch(e) { DEBUG && console.warn('[dt] PA log error:', e.message); }
  CFG.sides.forEach(s => { S.view = s; pushHistory(); });
  S.view = CFG.sides[0]; updateHistoryBtns();

})(); /* v2.9.22 — TEMPLATE PLACEMENT NOW MATCHES THE CARD (Brandon, Jul 27 2026).
   applyInlineTemplate stacked text blocks on measured INK heights (l.h from measureText's
   actualBoundingBox) while renderTemplateThumb stacks on FONT-SIZE frames (h = fs). The two
   were in parity until measureText #2 switched to ink-hugging boxes; after that every
   template with a dominant big-number line (Milestone Number, Varsity, Class Of, Event
   Swag, Anniversary, Roman Numeral, Big Ampersand) placed differently than its card: the
   number's ink (~0.88em in Anton) tightened the stack, the group re-centred around it, and
   the script line's swashes overlapped the number. Font SIZES were never wrong — only the
   vertical distribution.

   Fix: applyInlineTemplate now budgets text block height as fontSize × lineCount, byte-for-
   byte the thumbnail's math (verified in an offline node-canvas port of both paths: relative
   line centres agree within 0.3% of print-area height on square/portrait/landscape areas).
   Side benefit: block heights no longer depend on font metrics, so the layout can't shift
   between fallback-font build and the document.fonts.ready re-fit.

   Not touched: renderTemplateThumb (unchanged, it was right), recipe templates (frac-based,
   separate path), fitTemplateGroup / applyLockScale (proportional, correct), and the width
   clamp (pa.w-based, still runs). Multi-line entries budget fs × lines — marginally tighter
   than their old ink height (~2.1em → 2em for two lines); gaps absorb it.

   v2.9.23 — NaN-PROOF ENGRAVE VECTORS + WINDING NORMALISATION (SO-332060, Jul 28 2026).
   Root cause of the corrupt {side}-engrave.dxf (32 NaN coords, Illustrator error 2067,
   "missing spots in the tiger"): a path d-string that ends mid-curve-command reaches
   transformPathD, whose n() returns NaN for the missing args and whose emitters wrote
   literal "C … NaN NaN" text; parseSvgPathToPolylines then blind-parseFloat'd that text
   inside the C arg run into exactly 8 terminal NaN vertices per truncated curve (and the
   lost Z dropped the closing vertex) — reproduced byte-for-byte against the delivered
   file's signature (poly 207: 25 verts, NaN idx 17-24). What truncates the d upstream is
   still unidentified (needs order-time markup); every layer is now hardened so it can
   neither corrupt a file nor pass silently:
   1. transformPathD: tx() throws on non-finite output; the incomplete trailing segment is
      dropped, complete geometry ships, gwarn 'path_truncated_tail' fires. Number found in
      command position gwarns 'path_token_misalign' before dropping the remainder.
   2. geoWorkerConvert: main-thread prescan refuses conversion (gwarn 'geo_nonfinite_paths')
      if any flattened d-string still carries NaN/Infinity text.
   3. _buildDxfFromPaths/_buildEpsFromPaths: count non-finite vertices and THROW instead of
      writing them — the callers' existing engrave_dxf_failed/engrave_png_only paths fire
      rather than uploading a corrupt file.
   4. Arc-flag fix in all five d parsers (_svgDToJsPDFOps, transformPathD,
      svgPathToAIOperators, normalizeSvgPathD, parseSvgPathToPolylines): SVG arc flags are
      single 0/1 chars that may be FUSED with the next number ("0140" = 0,1,40); the shared
      tokenizer merged them into one token, misaligning the stream and silently dropping the
      path remainder (proven: 'M10 10A15 15 0 0140 10Z' lost the whole arc). Flags are now
      peeled per the SVG BNF.
   5. Winding normalisation in _buildDxfFromPaths (handoff §4): nesting depth even → CCW,
      odd → CW, computed pre-reversal with bbox prefilter + 5-vertex majority-vote
      point-in-polygon, so even-odd and nonzero consumers agree. (The sibling defect in the
      PRODUCTION-PDF clip path — FINDING-clip-winding-cancellation — is NOT addressed here.)

   v2.9.24 — VERTICAL-TEMPLATE UI: ROTATED BOUNDS, PLACEMENT ROW, ONE DELETE (Brandon, Jul 28 2026).
   Three defects Brandon reported against the vertical ("BRANDON" down the side) template:
   1. BOUNDS GREW SIDEWAYS. bbox() has always been rotation-blind, so a line built with
      rotation ±90 got a selection box shaped like its UNROTATED rect — wide and short —
      while the glyphs painted tall and narrow, and each typed character widened the box
      left/right instead of extending it up/down. New rotBox(b, l) rotates a box's four
      corners about the SAME pivot applyTransform paints with (centered → x/y, else rect
      centre) and returns the AABB; it is an identity function at rotation 0, so every
      unrotated layer keeps its exact previous numbers. Routed through selectionBounds()
      (both the per-member template union and the single-layer return) and hitLayer(), so
      the dashed box, the four corner handles, the dimension label and click targeting all
      follow the painted glyphs. rotAABB() is untouched — it pivots on the ink-offset bbox
      centre for mask clamping, which is a different question.
   2. TEXT OVERFLOWED TOP/BOTTOM. fitTemplateTextLayer (runs on every keystroke in "Make it
      yours") clamped l.w — which for a ±90 line is its VERTICAL on-screen extent — against
      pa.w. Long entries therefore ran past the top and bottom edges and tripped the crop
      warning while measuring as "fits". Clamp axis is now pa.h for ±90, matching the fitDim
      switch applyInlineTemplate already used when building the layer. Unrotated text is
      unchanged (same pa.w * 0.92).
   3. NO PLACEMENT CONTROLS, TWO DELETES. updatePropsPanel returns early for a _tplLockId
      layer — the "Make it yours" form IS the template's whole panel — so a selected template
      had no Fit and no Center on desktop (mobile had Fit only, via the action bar), while the
      generic element toolbar at the foot of the sidebar still contributed a second, full-width
      red Delete beside the panel's own "Delete template". showTemplatePersonalize now emits a
      standard .dt-prop-place row (Fit to Print Area | Center) when it renders in the properties
      panel, wired to fitTemplateGroup and a new centerTemplateGroup (the move-only half of
      fitTemplateGroup: translate every non-backdrop member so the union centre lands on the
      print-area centre, masks following via syncGroupedBackdrops). Both handlers resolve the
      template member at CLICK time, like the Delete handler, so a selection change can't
      strand them. The toolbar hides via .dt-canvas-toolbar--tpl (CSS) toggled in
      updatePropsPanel ahead of its no-change early return.
   Correction, made in v2.9.25: the flag originally filed here claimed the duplicate Delete was
   also showing on mobile. It was not. `getComputedStyle(el).display` reports an element's OWN
   value even inside a display:none ancestor, and the probe read that instead of painted
   visibility; .dt-sidebar--right is display:none on mobile (design-tool.css:1575 and :1589), so
   the toolbar was never rendered there. The dead rule was real but inert, and is now removed.

   v2.9.25 — MOBILE: ONE DELETE, IN THE ACTION BAR (Brandon, Jul 28 2026).
   His rule: "on mobile remove the delete from all open panels — the delete should remain a red
   button in the action bar for any and all layers." Audited every layer-delete control in its
   REAL mobile ancestor chain (props panel is moved into #dt-mobile-drawer-content, tool panels
   into #dt-tool-modal-content, the element toolbar stays behind in the hidden right sidebar) and
   asserted on el.checkVisibility(), not on computed display:
     painted on mobile BEFORE  #dt-tpl-delete 300x44 · #dt-prop-tpl-del 300x52 · Layers row X 36x36
     already unpainted         #dt-prop-delete (.dt-prop-actions is display:none in the 1024px
                               block) · #dt-tb-delete (sidebar hidden) · context menu (isMobile
                               early-returns before it is built)
   The three painted ones are now gated on !isMobile():
   1. `#dt-tpl-delete` — the whole `.dt-tpl-personalize__actions` wrapper is dropped on mobile
      rather than left as an empty flex row.
   2. `#dt-prop-tpl-del` — the no-editable-content template fallback.
   3. The Layers-panel per-row X — precomputed as `delAct` beside `eye`/`thumb`, matching how that
      row already builds its parts. Reorder + show/hide are untouched.
   Because a Layers row now renders differently per breakpoint, the resize handler that already
   calls forcePropsRebuild() on a breakpoint flip also calls updateLayersPanel() — otherwise a
   tablet rotate would leave the rows in the old mode.
   Delete stays reachable while a panel is open: measured at 390x844 the props drawer occupies
   441–696 and the action bar 698–768 (no overlap), and elementFromPoint at the Delete tab's
   centre lands inside #dt-act-del. #dt-act-del is shown for every layer type — nothing in
   updateActionBar ever hides it (the template branch explicitly sets it visible, and it is absent
   from that branch's hide-list).
   CSS: (a) the dead mobile `.dt-canvas-toolbar { display: none }` is removed, with a comment
   recording why it was dead AND why it was redundant; (b) `.dt-action-tab--delete { opacity: 0.7 }`
   is dropped — that dimming was de-emphasis from when panels also offered delete, and it is now
   the only one. NOTE for Brandon: in action mode the bar is dark forest and the Delete tab's own
   colour is overridden to #FFC9BE (design-tool.css `.dt-mobile-tabs.dt-action-mode
   .dt-action-tab--delete`), i.e. pale salmon text at 6.21:1 — legible, but not a red BUTTON. A
   solid #C92F3F pill with white text measures 5.34:1 (passes AA for normal text) if that is what
   "red button" should mean; not applied without a decision. (ANSWERED in v2.9.26.)

   v2.9.26 — MOBILE: ONE FIT TOO, AND A RED DELETE BUTTON (Brandon, Jul 28 2026).
   *"if there is a duplicate FIT in a drawer, remove it and ensure that FIT shows on the action
   bar. FIT and DELETE should always show in the action bar for every type of design layer.
   Additionally, i do want to change the delete button on mobile to the red we're using, and
   white text/icon."*
   (1) FIT — same audit method as v2.9.25's delete pass (painted visibility in the real mobile
       ancestor chain, never computed display). THREE were painted on mobile alongside the action
       bar's own: `#dt-tpl-fit` 146x40 (template drawer), `#dt-prop-fit-pa` 146x40 (any text/image
       layer's props drawer), `#dt-sp-fit` 183x40 (the mobile Style sheet, text layers). All three
       are now `!isMobile()`-gated. Their CENTER siblings stay — the action bar deliberately does
       not offer Center (`centerBtn` is hidden there), so Center is not a duplicate and removing
       it would strand the action. Each `.dt-prop-place` row therefore renders Center alone on
       mobile; verified none collapses to an empty row and every row's tags stay balanced. The
       three click handlers already use `?.`, so an absent button wires nothing.
   (2) ALWAYS-ON BAR — `updateActionBar` no longer type-tests Fit:
       `fitBtn.style.display = (isText || isImageType || isPlaceholder) ? '' : 'none'` becomes an
       unconditional `''`, and `#dt-act-del` is now set visible explicitly rather than relying on
       never being hidden. Static proof: across the whole file `fitBtn.style.display` and
       `#dt-act-del`'s display are only ever assigned `''` (2 sites each — the template branch and
       the general path), and the only early returns before them are `!isMobile()`, `!l`, and the
       template branch that already sets both.
   (3) RED DELETE — `.dt-mobile-tabs.dt-action-mode .dt-action-tab--delete` was pale salmon TEXT
       (#FFC9BE) on the dark forest bar. It is now a filled `var(--dt-danger)` pill with white
       label and white icon (icon chip rgba(255,255,255,0.22)), inset `margin: 6px 4px` so it
       reads as a button, with a #C92F3F :active that also beats the generic action-mode :active
       rule. Measured after: white on #E8394A = 4.11:1, tab 112x49 (touch target >=44px kept).
       Two knock-ons caught by RENDERING it rather than trusting the rule:
         (a) `.dt-mobile-tabs` carries a 28px right-edge scroll fade (mask-image, phones only) and
             Delete is the LAST tab — the fade was washing out the pill's trailing third. It was
             invisible while the tabs were flat text. A matching 30px right gutter on the Delete
             tab (same @media 640px block) puts bar background under the fade instead.
         (b) At 7-8 tabs every `flex:1` tab shrinks to ~40px and the label truncated to "Del…".
             Delete is now `flex: 0 0 auto` with 12px side padding — the same treatment the
             desktop toolbar's Delete already has — so it holds 54x49 and reads in full for a
             3-tab template bar, a 6-tab raster bar and a 7-tab text bar alike.

   v2.9.27 — MOBILE DELETE: EVEN PADDING (Brandon, Jul 28 2026). CSS only, one rule.
   *"the delete button does not need to have such large padding Left and Right... please make left
   and right padding the same as top and bottom for the red... unless it ruins the grid spacing or
   causes uneven balance."* v2.9.26 shipped `padding: 6px 12px`; it is now a flat `padding: 6px`,
   measured even on all four sides (content insets L6 R6 T6 B6, pill 54x49 -> 42x49, label still
   not truncated on a 3-tab template bar or a 7-tab text bar).
   His "unless it causes uneven balance" caveat turned out to matter, and only a RENDER showed it:
   at 42px wide the 20px `--dt-radius` is a stadium, and the curve cut into the label's clearance
   so an even 6px padding did NOT read even — "Delete" ran right into the rounded edge. Radius is
   now 12px, which sits between the icon chip's 6px and the bar's own 18px, so the fill still
   speaks the bar's language while the 6px reads as 6px. Padding is exactly what he asked for; the
   radius is the thing that had to move for it to look that way. Four variants were rendered and
   compared (20 / 12 / 10px radius at even padding, plus a 6/10 padding control) before choosing.
   Note: the pill is now 42x49. WCAG 2.2 AA target size (24x24) is comfortably met; it is 2px under
   Apple's 44x44 HIG suggestion on the horizontal axis, with 4px of margin either side on top.

   v2.9.28 — MOBILE TEXT QUICK ACTIONS: NEW ORDER, FORMAT MOVED TO MORE (Brandon, Jul 28 2026).
   *"for the text quick actions on mobile, can we make the order, from left to right: Font, Color,

   v2.14.0 — UX BUILD, Aug 15 2026 (Brandon; three review rounds — see project doc
   claude/UX-AUDIT-AUG15-LIVE-TOOL.html §9/§10 for the agreed risk register + 26 sign-off
   conditions). core.js + design-tool.css only; NO liquid. Every capture/production/prepress/
   proof/cart-property path is untouched by construction; the only intended output change is
   the auto-contrast DEFAULT print ink (below).
   • #1 AUTO-CONTRAST DEFAULT TEXT COLOUR — seedTextColor()/sampleMockupPALum(): new text is
     white on dark variants IN PRINT ONLY, at creation only, flagged l._autoColor (never
     serialized — serializeDesign is an allowlist). applyMethod re-seeds/parks flagged layers
     (print→engrave = #111111), restoreDesign + restoreServerDesign darken light text arriving
     under engrave, variant switch re-seeds (print), explicit picks clear the flag, telemetry
     gwarn engrave_prepress_light_text. Engrave outputs byte-identical to before for any design
     a user could previously produce.
   • W0 — mobile font-pop dt_font_select; dt_layer_add in addTextLayerAndEdit; restore
     fallback one-shot (clearTimeout — no double restoreDesign); Quick Ideas add directly via
     addTextLayer; .dt-action-tab--rc order:9; --dt-coral-ink #C4531B on the named CTA fills +
     input focus; --dt-blue removed.
   • spec §2 — syncCanvas() prepend in fitLayerToPA/fitTemplateGroup/centerTemplateGroup/
     centerLayerInPA; measured bars: syncBarHeights()/setupBarObservers() publish
     --dt-mobile-tabs-actual / --dt-cart-bar-h-actual (0 when hidden); applyEditModeZoom
     measures the real bottom chrome instead of the 56/64 constants.
   • spec §3 REVIEW MODAL v2 — in-card Add to Cart at every width (header CTA CSS-hidden while
     open, never removed); ONE exclusive two-card choice ("{Verb} as shown" / "Have an expert
     check it first +$5") driving the SAME hidden #dt-cart-proof/#dt-cart-approve checkboxes →
     doConfirm/executeCartAdd/cart properties untouched; forced-proof + no-proof-variant states;
     verbatim spell-out row, contrast + trailing-space + future-year nudges; wireModalA11y focus
     trap; dt_review_choice event.
   • #2 CANVAS-TOP ROW (desktop) — #dt-header-row2 in-flow with reserved height; setupCanvasRow()
     re-parents #dt-header-sides + #dt-method into it; .dt-props-wide retired (panel stays 320).
   • #8 LAYERS-IN-SLOT (desktop, CSS) — #dt-panel-layers shows in the closed 320px slot.
   • #3 ACTIONS BAR (desktop) — #dt-ctx-bar (Size −/+ · Fit · Center · Duplicate ┃ Delete +
     live meta); confirmDelete for pointer delete; #dt-canvas-toolbar / panel Fit-Center /
     #dt-prop-delete CSS-hidden; click-off exclusion.
   • #6 ZOOM — desktop lands at PA≈55% (cap 150%, ≥100%); zoom strip bottom-centre with −/+;
     zoomToPrintArea/panForPACenter exclude the row/bar/strip from the visible bounds.
   • spec §5 (mobile) — #dt-done-btn re-parented into the action bar's leading slot (✓ Done);
     body.dt-ctx-active hides the cart bar and drops the bar to bottom:0; tap on empty canvas
     deselects (onUp, single finger, <8px); one-time coach toast.
   • FLIP → [FRONT ✓ | BACK +] segment (#dt-side-seg, buildSideSegment) — flip button CSS-hidden.
   • M5 — font sheet reserves canvas space (applyEditModeZoom) and restores on close; picker
     34vh; preview = first word.
   Post-deploy fixes (same day, after Brandon's first live check): the wide (640px) props
   overlay is restored (panels were cramped/cropped at 320); the desktop landing zoom fits the
   WHOLE PRODUCT (mockupContentBounds alpha bbox) instead of cropping it at 150%.
   rev 3 (Brandon: "all the pills over the canvas is a huge mess"): the canvas chrome now has
   three zones with three visual languages — (1) ONE white context strip top-left holding
   [Front|Back] ┃ [Engrave|Print] (neutral toggle, forest thumb) + colour pill top-right =
   "what you're looking at"; (2) a DARK FLOATING TOOLBAR anchored to the selection
   (positionCtxBar: centred above the layer, hops above the print area or flips below when
   needed, clamped to the visible canvas, re-placed on every render/zoom/resize) with a caption
   chip = "what you can do with the selected thing"; (3) a small zoom pill bottom-right. When the
   wide props overlay opens, the view PANS right by half its overhang (syncCanvasRowInset →
   setZoom delta) so the product stays centred in the visible canvas — a transform, no reflow;
   zoomToPrintArea/panForPACenter centre on the visible region too. Live-verified by injecting
   the CSS into the deployed page; UX critique agent: PASS-WITH-NITS, nits applied.
   Backups: design-tool-core.js.bak-preUXAug15 / design-tool.css.bak-preUXAug15 (device).
   rev 4 (Brandon, Aug 17): ONE desktop panel width — --dt-panel-w: 430px (2/3 of the old
   640 drawer) for tool panels, library drawers (Templates/Graphics/Monogram/Saved) and the
   props overlay (text edit included); libraries + font picker are 2 columns; the props
   overhang/pan logic is unchanged (it measures the DOM). Toolbar Size −/+ and the zoom pill
   −/+ are inline SVG glyphs (text glyphs sat off-centre in the 28px circles). CSS-only fix
   for the FRONT/BACK group rendering as an opaque white square inside the strip (a later
   same-specificity legibility rule re-padded/re-painted .dt-header__sides).
   rev 5 (Brandon, Aug 17): (1) SOLD-OUT COLOURS HIDDEN — applySwatchAvailability() reads
   `available` from CFG.variants (false only when tracked + qty<=0 + policy deny; continue-selling
   stays true), hides every swatch (desktop popover + mobile list) whose colour has no available
   variant, retargets a swatch whose mapped variant is out but a same-colour sibling is in, hops
   off a sold-out landing variant to the first buyable colour with a toast, and never restores a
   draft onto a hidden swatch. Fail-open when data is missing or every colour is out. (2) The
   desktop colour picker is a DROP-DOWN card (5-col grid of dot + name via ::after
   attr(data-color-name), max 60vh scroll, Esc/outside/pick closes) instead of the horizontal
   slide-out that ran into the context strip on 16-colour products. CSS-only re-container of the
   same buttons; liquid untouched.
   rev 6 (Brandon, Aug 17 — "a clean, well constructed and consistent card library"): ONE
   .dt-card anatomy for Monogram / Graphics / Templates / Saved / Fonts (spec:
   claude/CARD-SYSTEM-PROPOSAL.md, UX agent). Old class names stay on the same element as
   aliases (.dt-mono-card .dt-clip-item .dt-tpl-card .dt-saved-card .dt-font-card) so every
   selector/handler/analytics hook is unchanged. Markup: check chip + optional recolour badge
   (real element, aria-label) + .dt-card__media wrapper + caption/meta slots. Monogram two-dot
   badge retired for the single wheel; div-cards get role=button/tabindex + Enter/Space. SAVED
   joins select→commit: cards are <button aria-pressed>, no per-card Load/Delete; the dock
   (#dt-saved-dock wraps #dt-save-design-btn + a quiet Delete link) flips Save Design ⇄ Load
   design (syncSavedDock; Esc deselects). loadSavedDesign / deleteSavedDesign / save flow and
   every output are untouched.
   rev 7–10 (Aug 17): see claude/HANDOFF-v2140-UX-REDESIGN.md (toolbar caption, connected
   location tabs, Format popup, custom-monogram cards, Camera button removed, Snap colour).
   rev 11 (Brandon, Aug 19 — mobile polish + iOS dock fix; css + core, no liquid):
   (1) body.dt-tool-modal-open while a tool panel is up → tab bar squares its top corners; CSS
       stacks cart bar / tab bar / panel edge-to-edge (no gaps), forest panel header, slightly
       translucent panel + sticky library bars.
   (2) LIBRARY DOCK ON iOS — "Add to Design" only appeared at the bottom of the panel. Root
       cause: rev 6 gave the div-cards tabindex=0, so tapping a card focuses it and the
       "keyboard open" rule `#dt-clipart-ui:focus-within #dt-clip-add { position:static }`
       (meant for the search input) un-stuck the dock. CSS now keys on :has(input:focus);
       nudgeStickyDock() gives the scroller a 1px kick after the dock appears (WebKit lazy
       sticky). Same for #dt-mono-add.
   (3) MOBILE REVIEW — header row becomes a floating ×, hero fills the freed height, bottom
       panel is just the two choice cards + Edit design + Add to Cart (policy line, guarantee,
       "Choose one", spell-out and side meta are CSS-hidden on ≤768px; conditional low-res /
       contrast warnings and the forced-proof reason stay); .dt-imr-check--policy class added
       so the generic line can be targeted; tap-to-zoom image has a transparent background.
   (4) Text panel "Personalizing for a group?" card: coral left accent/border removed.
   Production / prepress / proof / mockup / cart outputs: untouched.
   rev 12 (Brandon, Aug 19 — review card rewording, fit-to-PA fix, monogram UX; core + css):
   (1) REVIEW CHOICE CARDS — proof card title reworded from "Have an expert check it first"
       to "Receive an emailed proof from our experts (+$X.XX)"; as-shown subtitle updated
       to "I've verified all spelling, names & dates are correct"; proof subtitle updated to
       "Expect your proof in 1–2 business days before production begins." CSS rule that hid
       card subtitles on mobile (dt-imr2-card__sub:not(--amber) { display:none }) removed so
       both subtitles are now visible at all widths.
   (2) FIT-TO-PRINT-AREA on custom/Google fonts — fitLayerToPA now preloads the layer's
       font via document.fonts.load() before measuring text, fixing incorrect fit sizing
       when the font face hadn't been fully loaded into the canvas context yet. The text-fit
       logic is extracted into _doFitText() so fitLayerToPA stays synchronous for non-text
       layers while the text path runs after the font promise settles (instant when cached).
   (3) MONOGRAM ADD AFTER DELETE — improved empty-input feedback: when "Add Monogram to
       Design" is tapped with no initials (e.g. panel was rebuilt by exitDesignMode after a
       layer delete and the user didn't re-enter initials), the input now scrolls into view
       and a toast message appears. Also: addMonogramLayer blank-render retry now shows a
       toast instead of silently returning.
   Production / prepress / proof / mockup / cart outputs: untouched.
   rev 13 (Brandon, Aug 24 — "rotate your tablet" blocker on a 1920x1080 2-in-1 LAPTOP;
   core + css, liquid optional; see claude/HANDOFF-AUG24-ROTATE-PROMPT-2IN1.md):
   (1) ROOT CAUSE — the CSS any-pointer:fine guard was live and correct; the browser lies.
       Chromium on Windows returns ONLY a coarse pointer (any-pointer:fine = false) whenever
       Windows is in tablet/slate mode, trackpad attached or not; Firefox reports
       pointer:coarse on any touchscreen laptop without an external mouse.
   (2) FINE-POINTER DETECTOR (after isMobile): a pointermove with pointerType 'mouse' at two
       distinct positions, or a wheel event, stamps html.dt-fine-pointer (kept in
       sessionStorage dt_fine_pointer for the tab). Touch/pen never qualify. CSS hides the
       prompt under that class. isMobile() / layout gating deliberately unchanged.
   (3) "Continue in landscape" button injected into the prompt if the liquid lacks it; sets
       the same flag; receives focus whenever the prompt is showing (aria-modal, keyboard).
   (4) CSS width gate @media (min-width:1500px) hides the prompt — laptops at 100-125%.
       NOT 1440: Surface Pro 8-11 at default 200% are exactly 1440 wide (review catch).
   (5) const DEBUG hoisted to the top of the IIFE — it was declared in DOM REFS but used by
       DEBUG && guards above it (new-customer-accounts fallback), a TDZ ReferenceError that
       would have killed the tool had that path fired.
   Production / prepress / proof / mockup / cart outputs: untouched.
   rev 14 (Brandon, Aug 26 — customer got a native "Upload failed. Would you like to save
   your design locally…" confirm() over the "Making it yours" overlay; core only):
   (1) offerLocalSave() REMOVED. It was a blocking browser dialog inside a branded progress
       flow, it duplicated the overlay's own "Couldn't finish / Try Again" panel, it fired
       even on the NON-fatal design-data.json path (order still went through), and the key
       it wrote (dt_saved_design_<variantId>) was never read by anything — dead storage.
   (2) uploadWithRetry: 1.5 s pause before the single retry; on the final failure it calls
       autosave() (the REAL draft — dt_draft_<handle>, restored by checkAutosaveDraft), logs
       one unconditional console.warn naming the file + reason, sends GA4 dt_error
       s3_upload as "<file>: <reason>", and throws an Error tagged err.dtUploadFile
       (serial prefix stripped: "front-mockup.png").
   (3) Single + bulk cart catch: an upload error shows uploadFailDetail() in the overlay —
       "Couldn't upload <file>. Check your connection, turn off any VPN or ad blocker, then
       try again. Your design is saved on this device." — instead of the raw transport
       string; the raw reason still reaches GA4 (cartError) and the console. Non-upload
       errors unchanged. dtWaitFail detail cap 90 → 180 chars to fit that line.
   (4) REMOVE.BG INPUT CAPPED AT 4096 px (Vercel log the same day: "resolution_too_high —
       94 megapixels, maximum 50" on a 1.9 MB logo JPEG). callRemoveBg sent the ORIGINAL
       upload while the display frame was already downscaled to 4096 px (handleFile 4b), so
       >50 MP files were rejected outright and the "Brush the subject" roi (computed in the
       display space) landed on the wrong region of any >4096 px original. New
       prepRemoveBgFile(): same 4096 max-side rule as the display raster, JPEG→JPEG q0.92 /
       else PNG, cached per File (WeakMap) for Try-again + brush re-runs, every failure
       falls back to the original file. Source upload to Prepress unchanged. Verified in
       headless Chromium: 9000x6000 JPEG → 4096x2731 (2.1 s), 3000x5000 PNG → 2458x4096,
       ≤4096 px files pass through by identity, undecodable/non-Blob input falls back.
   Byte-identical uploads on the success path; production / prepress / proof / mockup /
   cart outputs: untouched.

   v2.13.6 — AI LINE-ART UPLOAD VECTORIZE BACK TO maxColors 2 (Brandon, Aug 14 2026;
   order CB37496/CB-20260813-XVNQ — prepress reported "tons of artifacts" riding every
   line of an AI-traced portrait engrave; artwork near-uneditable). ROOT CAUSE was NOT
   the v2.13.4 normalize-before-round pass (AI line-art SVG layers structurally skip the
   production trace where it runs — their DXF/EPS geometry flows through
   buildSingleVectorLayerGroup → flattenSvgPaths, untouched by v2.13.4). It was
   v2.13.3's upload-time maxColors 2 → 4 bump: gpt-image-1 line art is soft-gray, so at
   4 the quantizer split the ink into adjacent gray plateaus (this order: #4d4a4a +
   #63605e groups) and vectorizer.ai's gap filler emitted ~250 fill="none"/
   stroke-width=2/non-scaling-stroke seam strokes along every plateau boundary — stored
   verbatim in the layer's svgMarkup, invisible in the browser preview (strokes paint
   under the fills), then converted by the v2.11.3 stroke→ribbon pass into hundreds of
   filled sliver ribbons in the engrave DXF/EPS. The v2.13.3 A/B that blessed the bump
   used TRUE 2-color art — the one input class where the setting cannot matter. FIX:
   one token at the upload-time call — maxColors 4 → 2 (this changelog + comment aside,
   nothing else changed). The PRODUCTION engrave trace KEEPS maxColors 4 (v2.12.2): it
   is recolored to black and region-audited, and its input class (composite raster) is
   different. Adversarially reviewed by a 2nd agent (GO): the call is reachable only via
   uploaded-photo → engrave → AI filter → "Use This Design" (4 upload entry points, all
   S.method==='engrave'-gated; isLikelyPhoto-gated; restore/bulk/print/auto-prep paths
   never reach it); the 4 production callVectorize sites compute maxC independently;
   the raster fallback catch is argument-independent; the proxy's only maxColors-
   sensitive line is a both-branches-'0.5' no-op ternary. Reviewer's follow-up note
   (NOT built): gap_filler stays on in api/vectorize.js, so an unusually hazy AI result
   could still seam at 2 colors — the structural fix would drop gap-filler stroke paths
   for engrave uploads or disable gap_filler at colors<=2 in the proxy. Full finding:
   claude/FINDING-CB37496-ai-lineart-gapfiller-artifacts.md. BUILD-BASE NOTE: first cut
   of this version was mistakenly built on a STALE v2.13.3 copy found in the CB Design
   Tool folder; Brandon supplied the live file (v2.13.5, LF md5
   27579c575a36f4294a6a03d86d142d79 — byte-identical to the documented v2.13.5 build)
   and this build was redone on that base, so v2.13.4 + v2.13.5 are fully included.
   CB37496 front files need regeneration (re-add to cart) after deploy.

   v2.13.5 — SILENT PRODUCTION PDF DROP FIX (Brandon, Aug 12 2026;
   order CB37404/CB-20260811-ZG11 back — back-side PRODUCTION PDF was never generated
   or uploaded to S3; prepress designer reported "prod files are white on white"; the
   back EPS was structurally correct (ImageType 3 with 93.4% transparent MaskDict —
   white artwork on white viewer background, not a bug) but the PRODUCTION PDF was
   silently dropped). ROOT CAUSE: the PRODUCTION PDF generation block was wrapped in
   `try/catch(e){ DEBUG && console.warn(...) }` — failures were silently discarded when
   DEBUG was false, with zero telemetry. Exhaustive code tracing found no deterministic
   throw for this order's back layer (_bgRemoved:true, isRasterUpload:true, classified
   isTraceableArt); the failure was almost certainly transient (memory, jsPDF edge case,
   or network). FIX: (1) Same-input retry loop — 2 attempts with IDENTICAL vector inputs
   (sideSlug, compositePng, tracedRasterSvg); no raster degradation on retry (Brandon:
   "I don't want a nasty rastered output ever again"). (2) Separated generation/upload
   catches — a transient jsPDF issue retries generation; a transient S3 issue logs
   without re-triggering generation. (3) Always-on gwarn telemetry replaces DEBUG-only
   logging — prod_pdf_gen_failed per attempt, prod_pdf_not_generated if both attempts
   fail, prod_pdf_upload_failed on upload failure. (4) doc.output('datauristring') gwarn
   wrapper inside generateProductionPDF — the only unguarded call in the function; if
   jsPDF's internal stream is corrupt (unbalanced save/restore, dangling path operator),
   serialization throws here; the gwarn makes it diagnosable and the re-throw lets the
   caller's retry loop fire. (5) Bulk PRODUCTION PDF path gets the same retry + gwarn
   treatment (bulk_prod_pdf_gen_failed, bulk_prod_pdf_not_generated) — it had the
   identical silent-swallow pattern. (6) Comment fix at the trace decision block: the
   comment incorrectly listed _bgRemoved as a HARD EXCLUDE alongside removedColor/
   _rcTrim; corrected to match the actual isPhotoLayer code, which deliberately does NOT
   exclude _bgRemoved (it means "has transparency", not "background was removed").
   Reviewed by 3rd agent: no regressions across all code paths (front/back, single/multi
   side, transfer, engrave, bulk); all 5 complex-logo scenarios verified correct (complex
   logo with/without _bgRemoved, photo with _bgRemoved, simple logo, removedColor layers).

   v2.13.4 — NORMALIZE-BEFORE-ROUND FOR TRACED SVG + PRINT-AREA CLIP (Brandon, Aug 12 2026).
   (1) NORMALIZE-BEFORE-ROUND (order CB20260811SNU8 — Vectorizer.ai emits arc-heavy SVG
   with sub-pixel radii; _roundSvgPathData rounded ALL numbers including arc radii to
   1dp, collapsing rx=0.04 to rx=0; normalizeSvgPathD then returned [] for zero-radius
   arcs and thin features (i-stems, fine serifs) vanished from DXF/EPS while the browser-
   rasterized audit SVG rendered them perfectly). FIX: new _normalizeAndRoundSvg converts
   arcs to cubic beziers at FULL PRECISION first, then rounds the resulting (x,y) control-
   point coordinates. Rounding coordinates is safe; rounding arc parameters is destructive.
   Per-d try-catch: a malformed d-string falls back to the original (which
   _roundSvgPathData then rounds safely, matching old behavior for that path).
   (2) PRINT-AREA RECTANGLE CLIP FOR PRODUCTION PDF VECTOR CONTENT (order CB37142/
   CB-20260805-1R5U — YETI Rambler 30oz, sunburst backdrop extended beyond print area in
   PRODUCTION PDF). v2.9.8 scoped the silhouette clip to wrap ONLY the raster addImage
   (correct — the low-res traced silhouette shredded vector text glyph edges). But SVG
   layers (backdrops, clipart) painted as vector paths via paintOutlined with NO clip at
   all. EPS already had an artboard clip; SVG builder had productionClipOpen(). FIX: new
   _openPrintAreaClip() / _closePrintAreaClip() — rect(0,0,wIn,hIn,null) + clip() +
   discardPath(), wrapping ALL THREE paintOutlined call sites: (A) vectors below raster,
   (B) vectors above raster, (C) no-raster path. Silhouette clip (raster-only) and print-
   area clip (vector-only) are sequential, not nested — no graphics-state conflicts.
   PREPRESS PDF unaffected (separate function, pdf-lib, intentionally no clip). Reviewed
   by 2nd agent: caught two bugs in initial proposal (doc.rect without null style arg would
   stroke instead of clip; only 1 of 3 sites originally planned).

   v2.13.3 — URL-MODE VECTORIZE + TRACE-EXCEPTION HARD BLOCK (Brandon, Aug 11 2026;
   order CB37298/CB-20260808-7EEC back — dense AI line-art with v2.12.2 color preserved
   encoded to a 5.4MB base64 trace input; Vercel's HARD 4.5MB request-body limit rejected
   it 413 AT THE EDGE before api/vectorize ever ran; the trace catch was non-fatal, so the
   engrave side shipped PNG-only ['engrave-photo-raster'] with no DXF/EPS — live-reproduced
   from the actual source: color input 5.4MB → 413 in 2.1s; the pre-v2.12.2 black-flatten
   of the same image is 0.7MB, so v2.12.2 widened this exposure for tonal/dense art).
   The cap CANNOT be raised (Vercel platform limit; official KB says bypass). FIX:
   (1) callVectorize gains optional uploadCtx and URL MODE — payloads over 4,000,000 chars
   upload the trace-input PNG to S3 (existing presign; object doubles as a per-order audit
   artifact in the order's Prepress folder when ctx has {folder, serial}, scratch
   trace-inputs/ otherwise) and send {imageUrl} instead of {image}; memoized per payload so
   the F3 3-attempt retry loop re-uses ONE S3 object. gwarn 'vectorize_url_mode' on each
   engagement. Below threshold: byte-identical. (2) api/vectorize.js (Vercel — deploy
   separately, BEFORE this file) accepts imageUrl, validates with the URL class (https +
   exact host custom-branding-design-files.s3.us-east-2.amazonaws.com + ≤2048 chars —
   SSRF/credit-abuse hardened; the presign Lambda constructs downloadUrl as exactly this
   host, match by construction), and forwards it as multipart field image.url with the
   file part skipped; vectorizer.ai fetches the URL directly (image URL is a first-class
   input per their docs). All trace parameters unchanged. (3) Review amendments F1/F2
   (2nd-agent, on top of the F3 v2.13.0 machinery): a THROWN trace attempt (URL-mode
   presign/PUT failure, vectorizer 5xx, network) now consumes the 3-attempt retry budget
   like an audit failure instead of aborting the loop on attempt 1; if EVERY attempt
   throws and no candidate exists, the engrave cart HARD-BLOCKS via _dtTraceBlocked —
   photo sides included (Brandon Aug 11: a missing production file is unacceptable; the
   photo bypass remains only for AUDIT quality on a real trace, never for having no trace
   at all). Single + bulk mirrored. Print trace passes ctx too (URL mode for oversized
   print inputs) and keeps its non-fatal raster-fallback semantics. DEPLOY SEQUENCE:
   Vercel proxy first, live-verify image.url with mode:'test' + a real S3 URL from the
   browser (review P4), then this file. Reviewed by 2nd agent: GO conditional on F1-F4 —
   all applied (F4 closed against presign.js source: downloadUrl host matches allowlist
   by construction). ALSO (Brandon, Aug 11): upload-time AI engrave vectorize maxColors
   2 → 4 for consistency with the production trace — A/B-verified byte-identical output
   on true 2-color line art (dense fish art, 2,764 shapes, single #000000 group, same
   sizeBytes at both settings); pure AA headroom, no regression surface. AND (Brandon,
   Aug 11 — order CB37369/CB-20260810-WEOZ shipped "SURVEY NG", capital I missing from
   the traced SVG feeding BOTH DXF and EPS while the approved mockup read "SURVEYING";
   replay of the same art traces clean 3/3 and the region-audit logic catches a missing
   I when it runs — the escape vector was the audit's FAIL-OPEN catch returning ok:true
   on any crash): (a) _auditTraceRegions now FAILS CLOSED — a crash counts as an audit
   failure (crashed:true, worst:0), consumes the retry budget, and a persistently
   unauditable non-photo engrave trace hard-blocks the cart; distinct '[AUDIT CRASHED]'
   marker separates "trace bad" from "audit could not run" in GA4. (b) Always-on
   'engrave_trace_audit' receipt event per engrave trace (single + bulk) with
   comps/fails/worst — the next incident shows instantly whether the audit ran and what
   it measured. WEOZ front needs regeneration post-deploy (replay traces clean).

   v2.13.2 — FAUX-BOLD DILATION: THORNS + CROOKED SERIFS FIXED (Brandon, Aug 10 2026;
   order CB20260807W07I "hubblack", font volley bold:true engrave — EPS/DXF shipped
   ~0.3mm thorn spikes at serif corners and visibly skewed serif edges; Brandon: thorns
   survive the laser, fix mandated). ROOT CAUSE in _dilatePolysForBold's offsetRing:
   per-vertex bisector offset (a) fired up to 2.5x delta in a numerically arbitrary
   direction at duplicated/near-coincident vertices left by bezier flattening (the
   thorns), and (b) at over-limit corners emitted ONE clamped point displaced off the
   true edge lines, dragging adjacent straight edges (the crooked serifs). FIX:
   (1) _dedupe collapses consecutive vertices within 1e-3 before offsetting (closing
   duplicate too; <3-pt degenerate rings fall back to the original poly undilated).
   (2) offsetRingJoin emits the SHIPPED geometry with real joins: true miter when
   dot >= 0.5 (90-degree serif corners stay crisp single-point miters at 1.414d),
   two-point bevel beyond the limit (corner excursion capped at exactly d — spikes
   impossible), butt-cap bevel across 180-degree reversals. Straight edges offset to
   exactly parallel straight edges (verified 1e-6). (3) The original offsetRing is
   RETAINED for the guard math (grow-sign resolution + counter bbox/area/reflection
   checks — the net-dot reflection check requires index alignment with the input ring,
   which bevels break); resolved sign feeds offsetRingJoin. (4) ND1 (review amendment):
   the EMITTED shrink ring is re-screened by area sign/magnitude — a sharp-cornered
   counter near collapse could pass the aligned guards yet emit an orientation-inverted
   ring that even-odd consumers render as a phantom unengraved hole; such counters are
   now swallowed (fill solid, the established conservative semantic). Scope: engrave
   EPS/DXF only (dilation is gated off the print 'eps' type; print faux bold renders
   via the PDF embedder, untouched). _strokeToRibbonD's mirrored clamp deliberately
   deferred (hand-authored state outlines, own dedupe, no reported orders). Validated:
   13/13 function tests incl. old-bug reproduction (duplicate-vertex spike 2.5d -> clean
   1.414d miter; sharp-tip thorn 32.5 -> 30.07 bevel at tip=30/d=1), counter
   survival/orientation/swallow parity with old, ND1 inversion regression; adversarial
   review (2nd agent): GO after ND1, all other points verified. INDEPENDENT review
   (3rd agent, fresh eyes): confirmed the thorn diagnosis from the shipped EPS itself
   (35 spike triads measured), then caught a HIGH defect both prior passes missed —
   the bevel was SIDE-BLIND: at sharp INNER-side corners (M/W crotches on grow,
   pointed counter tips on shrink) the bevel points land past where the offset edges
   cross, twisting the ring into a bowtie that wrongly FILLS the crotch/counter
   (18.63u²/9.63u² in repros — larger than the thorns being fixed, and ND1-invisible
   when ring sign survives). AMENDED: bevel only when (e1 x e2)*d > 0 (outer side);
   inner-side sharp corners emit the clamped bisector point (base behavior, correct
   side). Reviewer re-verified as-applied: M-valley and A-counter diffs vs base 0.00,
   sign convention correct for negative _sd (shrink), thorn fixes retained; 18/18
   tests incl. both repro classes. Final independent verdict: GO.

   v2.13.1 — CAPTURE/PRODUCTION PARITY FOR DISPLAY RASTERS (Brandon, Aug 10 2026; inline
   code comments tag this work v2.12.3 — it was authored against v2.12.2 before the
   v2.13.0 F3 region audit landed, then rebased onto it; order
   CB202608051R5U — sunburst backdrop present in PRODUCTION PDF + PREPRESS but absent
   from the mockup/review preview the customer approved). ROOT MECHANISM: mockupPng /
   templateThumbPng / the live canvas paint image layers from l.img (paintLayer silently
   skips a null img) while production/prepress rebuild from l.svgMarkup / l.b64. KILLER
   PATH (found in adversarial review): placeBackdrop() pushes history SYNCHRONOUSLY
   while its display raster loads async with b64:null — the placement snapshot
   serializes _imgSrc:null, so ONE UNDO landing on it restores the backdrop with
   img:null forever; editor + every capture omit it, production files include it.
   Secondary: untrimmed SVG rasters snapshot a REVOKED blob: URL → undo reloads onerror
   → _imgFailed → red "Image unavailable" placeholder painted into captures. FIXES:
   (1) _rebuildSvgDisplayRaster(l) — geometry-neutral rebuild of l.img from svgMarkup
   (never mutates x/y/w/h/ar, unlike refreshSvgLayer) that resolves only after the
   final trimmed image is DECODED (trimToContent returns a not-yet-loaded data-URL
   image; drawing it pre-decode paints nothing). (2) ensureSideLayerPaintSources(side)
   — awaitable pre-capture guard: rebuilds every visible layer whose display raster is
   missing, broken, or STALE (l._imgMarkupSrc !== l.svgMarkup — the raster no longer
   matches the markup after a backdrop recolor/SVG edit whose async refresh lagged or
   failed; prevents mockup shipping OLD colors while production ships NEW), rebuilds
   dead b64 rasters, clears _imgFailed on success, gwarns 'capture_img_rebuild_failed'
   on failure, then syncGroupedBackdrops(side) (parity with renderProductionPNG).
   Wired into EVERY capture: single cart-flow mockup, bulk NON-VARIABLE side mockup,
   bulk variable-side thumbnail (placed BEFORE the longest-name text swap so the await
   window cannot serialize falsified text), immersive review previews (the customer now
   REVIEWS the truth), saveDesign/autoSaveOnCart thumbnails, saveAsTemplate thumbnail,
   quick-zoom download. (3) _imgMarkupSrc staleness tag written at every display-raster
   build site (refreshSvgLayer, addSvgLayer, restoreDesign, restoreServerDesign).
   (4) serializeLayers never persists blob: URLs as _imgSrc (revoked-URL trap);
   restoreHistory rebuilds isSvg layers from markup when the snapshot carries no
   restorable raster, and onerror for isSvg layers rebuilds from markup instead of
   flagging _imgFailed. (5) restoreDesign + restoreServerDesign onerror no longer
   SILENTLY DROP the whole layer — pushed with img:null (guard repairs at capture) +
   gwarn 'restore_svg_raster_failed'. (6) refreshSvgLayer onerror: gwarn
   'svg_display_raster_failed' + one retry (was silent-forever-null).
   (7) mockupPng telemetry net: any visible image layer with no drawable source at
   capture time fires gwarn 'mockup_missing_paint_source' naming the layers. Backdrop
   COLOR changes (reSeed → placeBackdrop regenerates svgMarkup; _bgSeed serialized)
   and PHOTO/BACKDROP REPOSITIONING (syncGroupedBackdrops) were verified correct
   upstream; with the guard, captures now always reflect them. Adversarial plan review
   (second agent) drove amendments D1–D4 + M1–M3; a second implementation review passed
   GO and its residual findings were applied (N1 refreshSvgLayer tags with BUILD-time
   markup and discards stale loads; N2 _rebuildSvgDisplayRaster calls ensureSvgInkBox
   for mask-clip parity; N3 download-button reentrancy latch; N4 undo-rebuild gwarn).
   Production/prepress builders and the engrave v2.12.2 pipeline are untouched.
   Edit, Fit, More, Delete. Move style to the MORE panel."*
   (1) ORDER — done with flex `order` on `.dt-action-tab--*` inside `.dt-action-mode`, NOT by
       moving buttons in design-tool.liquid: the snippet's DOM keeps every id, aria-label and
       handler binding exactly where it is, and the change ships in the stylesheet. Values are
       global (font 1, color 2, color2 3, edit 4, fit 5, center 6, swap 7, reframe 8, removebg 9,
       style 10, more 20, delete 30), so the other layer types show a subset and inherit the same
       relative sequence. Hidden tabs are display:none and leave no gaps. Verified by rendering
       the liquid's VERBATIM markup and sorting the visible tabs by their x position: Font ·
       Color · Edit · Fit · More · Delete, exact match.
   (2) FORMAT -> MORE — `styleBtn` is now unconditionally hidden in `updateActionBar` (the button
       stays in the DOM; its handler and the outside-click guard still reference it). A new
       `#dt-more-style-row` is surfaced in the MORE popover for text layers, built by
       `ensureMoreStyleRow()` in JS rather than added to design-tool.liquid so the removal and its
       replacement ship in ONE file — if the row is ever added to the snippet, the function finds
       it and does not duplicate. It is inserted as the popover's FIRST child so the existing
       "Layer order" title still labels only the order rows beneath it, and its button calls
       `toggleStylePop` directly (that function already closes MORE and the other popovers before
       opening, so one call is the whole handoff — same shape as the Background row).
       `gateMoreRows` shows it for `type === 'text'` only, and the MORE tab's own visibility gate
       now counts it, so a LONE text layer (no second layer, so no order rows) still gets MORE
       instead of losing Format entirely. Verified in-browser against the real functions: row
       created once across repeated gate calls, is the popover's first child, sits above the
       title, shown for text / hidden for image, click opens the Format sheet exactly once.
   ADJACENT, NOT CHANGED (flagged for Brandon): the six labels no longer share a baseline. The
   icon chips are different heights — Edit's svg has `padding: 4px` (28px chip) vs 2px for the
   rest (24px), and the colour chip is 22px — so "Edit" sits ~4px lower and "Forest" ~2px lower
   than their neighbours. Pre-existing, but it read as deliberate emphasis while Edit was the
   FIRST tab and reads as misalignment now that it is mid-row. One value
   (`.dt-action-tab--edit svg { padding: 4px }` -> 2px) puts every label on one baseline.
   (ANSWERED in v2.9.29 — Brandon: *"keep the icons and text aligned vertically ... i didn't
   notice but yes, I won't want a deliberate emphasis"*.)

   v2.9.29 — MOBILE ACTION BAR: ONE BASELINE, NO EMPHASIS (Brandon, Jul 28 2026). CSS only.
   Every glyph slot inside `.dt-action-mode` is normalised to a 24px-tall, zero-margin box —
   `> svg` (24x24, padding 2px, border-box), `> .dt-act-font-preview` and `> .dt-act-color-chip`.
   Only the vertical box is normalised; widths stay per-glyph so the "Aa" preview can still be
   wider than an icon. The two offenders were Edit's svg at `padding: 4px` (28px chip) and the
   colour dot at 23px plus `margin: 2px 0` — since each tab is a centred column, those pushed
   their labels 4px and 2px low.
   Emphasis removed with it: the Edit tab no longer takes `font-weight: 700` or the brighter
   `rgba(255,255,255,0.28)` chip fill in action mode — it now matches its peers exactly. Delete
   keeps its red button; that one is intentional and its content still centres on the same line
   despite the shorter pill (equal centres, 6px+6px margin/padding vs 12px).
   Verified by rendering five real tab sets (text, duotone monogram, raster upload, template, and
   an eleven-tab stress case) and asserting a SINGLE distinct value for icon top, icon height and
   label top across every visible tab in each: glyphTop 33, glyphH 24, labelTop 59 — all five pass.
       HONEST CAVEAT: 4.11:1 clears WCAG AA for large text (3.0) but is just under the 4.5 for
       normal text at this label size — this is the brand red Brandon asked for and the same one
       the desktop full-width Delete uses. Swapping the fill to #C92F3F would read 5.34:1 and pass;
       it is a one-value change in this rule if he wants it.

   v2.9.30 — EZCAD OVERLAP UNION: text crossing clipart no longer engraves inverted (Brandon,
   Jul 28 2026). EZCAD2's autoshader hatches an imported DXF group by EVEN-ODD PARITY across
   every contour, so a name overlapping clipart XORed to unfilled where the regions cross —
   letters engraved inverted (RICHARD / RAMIRO orders; the FRANKIE screenshot). v2.9.23's
   winding normalisation cannot address this: parity ignores orientation, and these contours
   CROSS rather than nest. New _dtEngraveOverlapUnion (worker-bundled) runs inside
   _buildDxfFromPaths after winding normalisation: contours -> nesting forests (objects),
   even-odd region per object, cross-object overlap detection (transversal crossing OR
   odd-parity containment — a letter inside an art HOLE correctly does NOT merge), then a
   boolean union of ONLY the overlapping cluster(s): split segments at intersections, keep
   sub-segments whose two sides differ in union-region membership, stitch with leftmost turns
   (exteriors CCW / holes CW — the v2.9.23 convention). GATED: no overlap -> returns null and
   the DXF is BYTE-IDENTICAL to v2.9.29 (passthrough re-emits the same arrays). Robustness:
   1 micron endpoint snap kills epsilon-slivers (a real RICHARD crossing sat 0.3 micron from a
   ring vertex and broke chain closure until snapped); y-bucketed point-in-polygon + segment
   indexes keep the RAMIRO worst case (10,788-vertex art contour, 44-ring cluster) ~1 s
   in-worker; a parity SELF-CHECK samples the rebuilt loops against the intended region and
   ANY doubt ships today's un-unioned geometry instead (fail-safe = status quo). Telemetry
   rides the __dtDxfUnionMeta global because gwarn does not exist in the worker bundle: the
   main thread gwarns 'engrave_overlap_unioned' (expected, rare) or
   'engrave_overlap_union_fallback' (prepress should eyeball that order in EZCAD). EPS is
   deliberately untouched — its two compound paths fill independently and render correctly;
   only the DXF parity consumer needed this. Within-object overlaps (script fonts whose glyphs
   overlap inside ONE outlined path) are out of scope and unchanged. VERIFIED (Node harness +
   shapely reference): RICHARD order rebuild matches to 1.7e-4 mm^2 (13 contours -> 12 loops,
   61/74 polylines byte-identical passthrough, D untouched); RAMIRO to 4e-5 mm^2; 240-scene
   fuzz (bowties, vertex-touch, shared edges, islands-in-holes, swallowed shapes, needle
   piercings) = 137 correctly unioned + 103 correctly gated, 0 failures. (Fuzz note: shapely's
   buffer(0) itself mishandles bowties — the REFERENCE had to be fixed to true parity
   semantics, keeping both lobes, before it agreed with EZCAD/SVG rendering.)

   v2.9.31 — FAUX-BOLD PREPRESS SAVED BLANK: PDFNumber-wrap operator args + blank-stream guard
   (Brandon, Jul 29 2026). Root cause of the Jul 25 RACE KING order's blank back PREPRESS PDF
   (font "sunairola", bold:true). Sunairola has no true bold face, so _drawLiveTextLayers took
   the faux-bold path (v2.8 Tr-2 approach), whose four operators (w / j / RG / Tr) passed RAW
   JS numbers to PDFLib.PDFOperator.of instead of PDFNumber.of-wrapped values (every other
   operator in the file wraps them). In pdf-lib 1.17.1 a raw-number arg makes
   PDFOperator.sizeInBytes() return NaN, and save() then SILENTLY serializes the page's entire
   content stream as ZERO bytes — no exception, so the painter-chain raster fallback never
   fired. The shipped file still embedded the subsetted font (the 9 "Race King" glyphs), the
   OCG and the guide's ExtGState, which is the diagnostic signature: resources present, content
   stream empty. Reproduced byte-mechanism in a Node harness against pdf-lib 1.17.1: identical
   build WITHOUT the faux-bold block → 306-byte content stream; WITH it → 0 bytes; raw-number
   op sizeInBytes() = NaN, wrapped = finite. Trigger = ANY text layer with bold ON whose font
   lacks a real bold face (all single-weight custom fonts) — every such prepress since the
   v2.8 faux-bold code was blank; the PRODUCTION PDF/EPS alongside were always correct (this
   order only surfaced it because its white-ink art makes correct files LOOK blank on white).
   Fix 1: the four faux-bold operator args are PDFNumber.of-wrapped (local _Nb, same convention
   as the OCG block's _N). Fix 2 (never again, any cause): _buildPdfLibPrepress now walks
   page.contentStream.operators before save() and THROWS (gwarn 'prepress_blank_stream') if the
   list is empty or any operator's sizeInBytes() is non-finite, so the existing painter chain
   ships the proof-exact raster prepress instead of a blank file. fauxItalic/rotation/flip use
   concatTransformationMatrix factories (safe, unchanged); PRODUCTION PDF and EPS untouched.

   v2.9.32 — PREPRESS EMBEDDED-FONT NAMES CLEANED (Brandon, Jul 29 2026). Prepress complaint
   (BOMBERS/Coach Mitchel order): Illustrator's missing-font prompt asked for
   "CollegiateHeavyOutline-Medium-7572" — pdf-lib appends a random numeric suffix to every
   embedded subset font's BaseFont/FontName, so the prompt can NEVER match an installed font
   ("-Medium" is the webfont file's real internal PostScript name, not a variable-font
   artifact; the random digits are pdf-lib's). Fix: _drawLiveTextLayers now passes
   customName (supported in pdf-lib 1.17.1, verified in Node: BaseFont comes out verbatim,
   glyphs still render from the embedded subset) = the loaded font file's PostScript name
   from opentype.js names.postScriptName, falling back to the design-data font name, spaces
   stripped. Editing live text in Illustrator still requires that font installed locally —
   but the prompt now names the actual font so it can be matched or sourced. Viewing and
   printing never needed the local font (subset is embedded). NOTE for prepress: the site's
   webfont file's PS name (e.g. CollegiateHeavyOutline-Medium) may differ from a static
   retail cut's name — install the site's actual font file for an exact auto-match.

   v2.9.34 — TEMPLATE FONTS CAN BE CATEGORISED FROM SHOPIFY (Brandon, Jul 29 2026).
   Brandon: five fonts show under a "standard" pill in the font picker and need to be one
   of the real categories instead. MEASURED, not guessed: the five are Dancing Script,
   Anton, Poppins, Cinzel and Bebas Neue — the only `font` values in
   design-tool-templates.liquid (Anton x27, Poppins x20, Dancing Script x20, Bebas Neue
   x18, Cinzel x8), confirmed by rendering each from its real font file against Brandon's
   screenshot.

   "standard" was never a category anyone set. `ensureGoogleFont` hardcodes
   `category: 'standard'` on every family it discovers in a template or a saved design
   (the RUNTIME_FONTS push). The picker's pill row is just the distinct set of category
   strings across getAllFonts(), rendered verbatim (2611, 13166) — so the literal leaked
   straight into the UI.

   THE TRAP THIS FIXES: the obvious repair — add the five as `custom_fonts` metaobject
   rows with proper categories — SILENTLY BROKE THEM before this version. Two gates
   conspired: `registerCustomFonts` (11374) skips any entry with no `font_file`, so no
   @font-face is written; and `ensureGoogleFont` then saw the family as "known" and
   returned WITHOUT injecting the Google stylesheet. Net: nothing loaded the family and it
   rendered as a fallback. Reproduced in Chromium against the pre-patch function — a
   fileless metaobject entry injected zero stylesheets.

   THE CHANGE (one function, ensureGoogleFont): the "known" test now distinguishes
   SELF-HOSTED entries (carry a font file — registerCustomFonts already wrote the
   @font-face, return as before) from WEBFONT-BY-NAME entries (family_css, no file — the
   Google stylesheet still has to be injected here or nothing loads it). Neither known
   case pushes a RUNTIME_FONTS twin, because a twin's hardcoded 'standard' would shadow
   the metaobject's real category. Unknown families are unchanged: inject, push, preload.

   Net effect: a template font becomes a metaobject row — display name and category owned
   by Shopify, no font file to upload, no code change for the next one. A family with no
   row still lands in "standard", which is now a useful signal (that font needs a row)
   rather than a dead end.

   VERIFIED in headless Chromium against the REAL patched function extracted from this
   file, 21/21: unknown-family path byte-for-byte unchanged; self-hosted path identical to
   pre-patch (0 injections, 0 twins); fileless path now injects exactly one stylesheet,
   pushes no twin, and keeps its metaobject category; repeat/case/whitespace calls inject
   once; 7 junk inputs throw nothing; and all five real fonts declared fileless produce a
   pill row of All/Script/Sans-Serif/Serif with **no "standard" bucket at all**.
   `getAllFonts` and `googleFontHref` proven byte-identical old vs new, so ensureGoogleFont
   is the only behavioural delta.

   ADJACENT BUG FOUND, FIXED IN DATA NOT CODE: `catMatchesRole` (4451-4452) matches
   categories by substring. The existing category string "San-Serif" lowercases to
   "san-serif", which does NOT contain "sans" but DOES contain "serif" — so the `sans`
   role in Surprise me matched NOTHING, and every San-Serif font was being picked as a
   SERIF. Brandon renames the value to "Sans-Serif" in the metaobject; that alone fixes
   both directions. "San-Serif" appears nowhere in core.js or design-tool.liquid — it is
   pure metaobject data — so no code change is needed or wanted here.

   v2.13.0 — F3: REGION-LEVEL TRACE AUDIT + RETRY + CART BLOCK (Brandon, Aug 6 2026;
   CB20260806HM1F "FRE GHTL NER" — second stem-drop order, shipped hours AFTER v2.12.2 with a
   correct mockup; second-agent review approved plan v3). The v2.12.2 audit could not see this
   class: a global percent-of-ink threshold (0.5% @640w) is blind to thin glyphs (HM1F's two
   capital I's = 0.595% — noise margin; a single KPOX-class stem ~0.2% — invisible by design).
   (1) _auditTraceRegions(): connected-component audit of the trace input at <=1024w — EVERY ink
   region above a scale-relative floor must be >=50% covered by the 1px-dilated rasterized trace;
   a dropped stem is a 0%-covered region regardless of its share of total ink. Never rejects
   (audit crash resolves ok:true) — the CALLER decides blocking. Browser-validated against KPOX:
   good trace 35/35 pass in 131-362ms; synthetic drop (one i-stem rect removed) caught as exactly
   1 region @0%, located to the pixel. (2) Engrave trace (single + bulk sites): up to 3 audited
   attempts, best-scoring kept (same-input retry recovers — KPOX went 30→34 shapes); photo sides
   never block (posterized photo traces legitimately fail region coverage — telemetry only).
   (3) Non-photo audit failure after all attempts: forensic upload of trace input + failing SVG
   to S3 (*-trace-FAILED.*), gwarn('engrave_trace_blocked'), then a thrown _dtTraceBlocked error
   that ESCAPES the trace catches (both sites) and hard-blocks add-to-cart with "We hit a snag
   preparing your engraving file — please try again." on the cart button (Brandon: no silent
   ship). New GA4 keys: engrave_trace_retry, engrave_trace_blocked. Print paths byte-identical;
   legacy _auditTraceCoverage telemetry retained. See claude/FINDING-HM1F-I-DROP-AND-FIX-PLAN.md
   (plan v3) + claude/HANDOFF-V2130-F3-REGION-AUDIT.md.

   v2.12.2 — ENGRAVE TRACES IN ORIGINAL COLORS + COVERAGE AUDIT (Brandon, Aug 6 2026;
   CB20260804KPOX "Land Spec·alists" — the shipped engrave file was missing the first-"i",
   "l", and second-"i" stems). ROOT CAUSE: the trace input was force-blackened BEFORE
   vectorization, starving Vectorizer.ai of the color boundaries its shape detector relies
   on; on alpha-edge-only input the API intermittently (nondeterministically — the identical
   PNG later returned all 34 shapes) dropped thin vertical features. FIX, engrave-gated at
   every site (print byte-identical): (1) renderProductionPNG gained opts.preserveColors
   (5th arg; all pre-existing callers pass <=4 args and keep force-to-black) — ONLY the
   trace input keeps original colors, and engrave now always renders the trace input fresh
   (never reuses the force-blackened compositePng). (2) Engrave trace maxColors 2 -> 4:
   2 true inks + headroom so anti-aliased edge pixels resolve into the nearest ink instead
   of forcing merges that eat thin features (matches the manually-verified successful trace
   of this exact artwork). (3) _recolorSvgInkBlack flattens every fill AND stroke in the
   traced SVG to #000000 immediately after tracing (gap filler emits near-source-color
   stroke hairlines — burst test observed stroke="#edeee7"), preserving fill/stroke="none";
   REQUIRED, not cosmetic: the EPS/DXF paper-white drop rules (absolute >=240 + relative
   luminance) would otherwise delete white-on-transparent ink — this very order's text is
   white. Side effect: engrave PRODUCTION/PDF vector paths are now explicitly black (the
   shipped KPOX PDF had white-on-white invisible paths). (4) _auditTraceCoverage: after
   every engrave trace (single + bulk), rasterize the traced SVG at 640w offscreen and count
   trace-input ink pixels it fails to cover; >0.5% missing fires gwarn
   'engrave_trace_coverage' with the exact percentages — telemetry only, fire-and-forget,
   never blocks generation or the cart. Watch dt_error in GA4: ANY hit = the vectorizer
   dropped geometry and that order needs manual review before the laser. VALIDATED before
   coding: 5/5 browser burst trials of the color input at the exact failing geometry
   (1943x1619) returned all 34 letter shapes with ZERO missed ink pixels (of 102,606);
   the API remains nondeterministic at the curve level (two output sizes observed) but
   never dropped geometry on color input. Bulk mirror (bulkTraceVariableRaster) identical.

   v2.12.1 — PROOF SHEET: NAMES INK-BOX SIZING + HEADER LAYOUT + SUB-CAPTION CUT (Brandon,
   Aug 4 2026; first live bulk sheet CB-20260804-6DC5, Dancing Script). (1) CRITICAL: the
   bulk names column CLIPPED script-font ink — rows were sized from measureText().width
   (ADVANCE width) and drawn at x=0, but script fonts carry ink outside the advance box
   (negative left side bearing on swashes, right tail overhang, ascent/descent past the em),
   so "Trissa"/"Josh" shipped with cut swashes and descenders. Rows are now sized from the
   TextMetrics INK box (actualBoundingBoxLeft/Right/Ascent/Descent + a 6%-of-fontSize safety
   ring), each name drawn at x = its own left-ink extent with baseline = ascent — ink inside
   the canvas BY CONSTRUCTION. Metric-missing fallbacks are over-generous on purpose
   (whitespace is cheap; a clipped name on a proof reads as "your name will be engraved
   clipped"). Same measureText-vs-ink trap v2.9.33 documented for monogram frames.
   (2) The names-column header grew with the font name and overlapped "PLACEMENT — LONGEST
   NAME SHOWN": now TWO stacked lines ("{N} NAMES — AT RELATIVE PRINT SIZE" / "FONT: {name}")
   and BOTH headers hard-clipped to their own column width (splitTextToSize first line) —
   no batch size or font name can collide. headBand 0.24 -> 0.38.
   (3) The size · method sub-caption under each product ("3\" x 2.5\" · Laser Engrave ·
   longest name shown") REMOVED on both sheets — redundant with the imprint table + legend;
   "longest name shown" survives in the placement header. capBand 0.66/0.62 -> 0.42, the
   mockups take the reclaimed height. e.sub stays on entries, unrendered. Verified in the
   Chromium harness with a real swash script font: edge-scan of the rendered names card
   (no ink in the outer pad ring) + 20-name batch single-page check.

   v2.12.0 — PROOF SHEET: SUPPLIER-TEMPLATE LAYOUT + METAFIELD DATA + NON-ALPHA PLACEMENT
   (Brandon, Aug 3 2026; spec claude/PLAN-PROOF-SHEET-YETI-LAYOUT.md rev 2, adversarially
   reviewed pre-build; reference YETIDT20000 template PDF). Both proof sheets (single
   buildProofSheetPdf + bulk buildBulkProofSheetPdf) now share a supplier-style shell:
   LEFT column = rounded ITEM box (variant SKU / product title / color label — SKU + color
   read client-side from CFG.variants; colorLabel falls back to variant.title when
   colorOptionIndex is undefined, and is NOT named colorName — that helper exists), ruled
   imprint table (Standard/Secondary/Third/Fourth Imprint Location + Maximum Imprint Size
   from the acumatica metafields via CFG.sideConfigs — rows keyed off designedSides, never
   proof entries, so a failed capture cannot drop/desync a row; null SIDE_PHYS guarded),
   red ARTWORK DIMENSIONS block with MEASURED per-side art size (rotation-aware rotBox
   union, intersected with the print area — mockup + production clip to pa — converted
   px->inches via the pa-per-inch ratio, mm = in x 25.4), red VISUAL LAYOUT REFERENCE
   disclaimer, small PROOF—serial—date identity line. RIGHT panel = light-gray backdrop,
   method-checkbox legend (available methods = WHOLE-TOKEN parse of CFG.imprintTypeRaw —
   the collapsed printMethodName hides secondary methods; X per method actually used),
   and the designed mockups placed by CONTENT BOUNDS: _proofTrimCrop does a two-stage
   scan (alpha>16 gate like trimToContent, then a corner-sampled LUMINANCE fallback for
   fully-opaque CDN photos; corners must agree or trimming is skipped), and all sides
   render at ONE COMMON PHYSICAL SCALE (px-per-product-inch from the pa fraction +
   imprint width — raw-pixel scaling is wrong across differently-shot photos) with
   per-slot aspect-fit fallback so one untrimmable side never thumbnails the rest.
   Dashed imprint rectangle (white underlay + black dash) mapped through the trim offset.
   GEOMETRY CONTRACT: mockupPng now records lastGeom {cw,ch,pa} — the EXACT canvas dims
   it drew with — and captureProofMockup derives paFrac from it; callers never
   reconstruct denominators. mockupPng fallback branch reordered to S._mockupBaseNW
   FIRST (naturalWidth-first was 2x wrong after the CRO #21 hi-res CDN swap — latent
   layer-misalignment bug, fixed). toDataURL wrapped (taint => null, not a failed order).
   HI-RES (Brandon): proof captures target ~1700px frames (captureProofMockup picks the
   scale; bulk variable side uses the existing thumbnailHi 2x), bulk names canvas 200->300
   DPI, and mockupPng gains the live-canvas pixel budget clamp (4096 edge / 1.6e7 px^2,
   folded into effective dpr) — it previously had NONE, so scale>1 on a dpr-3 phone could
   exceed iOS canvas caps and silently blank the capture. BULK fixes: variable-side artIn +
   paFrac measured INSIDE the longest-name swap window (the text is restored right after —
   measuring at proof-build time read the placeholder); per-iteration view restore removed
   in favor of one deferred restore after the loop (same race the single flow fixed —
   non-awaited loadMockup could silently drop a side sharing the same product photo).
   Builder signatures gain a trailing data arg (both call sites updated; non-fatal
   try/catch contract unchanged). Single-side artIn measured inside the cart loop while
   that side's pa/rscale caches are fresh (resize deletes non-current-side caches).

   v2.11.3 — ENGRAVE STROKE->RIBBON + FORCED BLACK + LOUD INK FILTER (Brandon, Aug 3 2026;
   order CB-20260802-5T8T, claude/FINDING-engrave-stroke-drop-and-color.md). The State
   Outline template shipped an engrave order whose EPS and DXF were both missing the state
   (all 51 hardcoded outlines are fill="none" stroke= by construction — the geoWorkerConvert
   ink filter dropped stroke-only paths for 'eps-ai'/'dxf', a Jul 17 rule the Jul 22
   cameo-ring fix only relaxed for print 'eps') and whose heart shipped as a live ORANGE
   CMYK object (forceBlack only ever reached outlined text; fillToK converted vector-layer
   fills verbatim). Zero of the 24 gwarn codes fired; the customer-approved raster proof was
   correct because every raster artifact strokes via the browser. Three changes:
   (1) _strokeToRibbonD (main thread, deliberately NOT in the worker's stringified function
       list) converts stroke-only paths to filled ribbons before the ink filter: contours
       offset ±strokeWidth/2 via _dilatePolysForBold's vertex-bisector math (miter clamp
       0.4), closed rings -> outer + reversed inner hole, open runs -> butt-capped loop,
       collapsed inners dropped (ribbon goes solid, same as a painted stroke). Gated off
       data-dt-guide, 'transparent' strokes (engrave editor "Leave blank"), zero width, and
       print 'eps' (native PostScript strokes, untouched). Ribbon fill inherits the stroke
       color so the v2.10.1/.3 paper rules still judge it. Conversion failure returns the
       original path, which now drops LOUDLY:
   (2) gwarn('engrave_paths_dropped') fires when the ink filter deletes non-guide geometry
       from an engrave output — the silent-by-construction filter was the operative defect.
       The bulk stacked master's dashed cell rect is stamped data-dt-guide="1" (rect +
       both flattenSvgPaths push sites) so every bulk order does not cry wolf.
   (3) The AI8 aiCompat EPS emits ONE forced '0 0 0 1 k' — engrave is 1-bit and the header
       already declared DocumentProcessColors: Black. fillToK/hasK/prevK deleted, not
       bypassed. White was already dropped upstream; text already forced black; the only
       behavior change is colored clipart, which the laser reads as ink regardless. DXF
       colorless by construction, needs nothing.
   KNOWN EDGES (accepted): ribbon joins are clamped miters, not the raster proof's round
   joins (deviation <= 2.5x half-width at the sharpest state notches); ribbon width rides
   resolveStrokeWidth's sqrt(|det|), approximate under non-uniform scale; a stroke-only
   photo-placeholder circle that survives to cart now ships as a ribbon (proof parity —
   the raster artifacts always showed it). VERIFIED in a Node harness against the worker's
   own function list: NH ribbon E2E through both builders renders outline-not-solid at
   true size; heart EPS emits black only; guide rect stays dropped warn-free; transparent
   stroke stays dropped; builders' source contains no reference to the new helper (worker-
   safe by construction).

   v2.11.1 — WHITE REMOVED CARD IN PRINT-MODE PICKER (Brandon, Jul 31 2026). remove.bg
   is a subject-isolation service: it fails on line-art illustrations ("Could not identify
   foreground") or returns them unchanged. These images need pixel-level white→transparent,
   not subject isolation. FIX: when the upload is line art on white paper (not a photo,
   _engraveAutoPrepEligible passes), a "White Removed" card appears in the background-
   removal picker alongside Original, Background Removed, and Logo on White. Uses the
   same stripWhiteBackground function as engrave auto-prep. AUTO-SELECT: when remove.bg
   fails AND the White Removed card is ready, it's selected instead of falling back to
   Original. When remove.bg succeeds on a non-photo AND the card is ready, it's also
   preferred (remove.bg often returns line art images unchanged). Error message updated
   to mention the White Removed option when available. No regression for photos: the
   isLikelyPhoto guard keeps the existing remove.bg-first flow for photographic content.
   DESPECKLE (Brandon, Jul 31): a hard white threshold on a JPEG leaves hundreds of
   tiny opaque islands (mosquito noise / block ringing around black↔white edges) as
   dust in the transparent areas — 607 specks <=10px on the CB-...-2F3B poster. Added
   _despeckleStrip: a 4-connected flood fill over the kept mask that drops islands
   <= minKeep px (minKeep scales with resolution, ~13px at 1254², floor 8 / cap 48).
   Per-component storage is bounded to minKeep+1 (big components stop recording once
   known-big), so memory is the O(n) seen/stack pair; skipped above 12MP for mobile.
   Applies to BOTH callers of stripWhiteBackground (White Removed card + engrave
   auto-prep). VALIDATED in Node against the real poster: 70ms, removed 2816px
   (0.41% of ink), zero islands <=13px remain, all 438 real strokes/letters intact.

   v2.11.0 — ENGRAVE INVERT OPTION (Brandon, Jul 30 2026; DESIGN-engrave-invert-option.md,
   plan adversarially verified pre-build). Edit-panel action (desktop props button +
   injected mobile tab, Remove-BG placement) on qualified engrave raster layers:
   "Inverted" engraves the ENCLOSED PAPER inside the artwork's hull so lines stay
   product-dark — corrects the negative-face read of tonal art on dark products.
   QUALIFICATION (client-side, <=400px mask analysis, cached per layer): closed hull
   (largest component >= 90%), enclosed paper >= 15% of hull, ink 5–75% of hull
   (25–75 tonal band -> Inverted pre-selected), inverted area 2–75%. Face detection is
   a BOOSTER only (FaceDetector unflagged nowhere, human-faces-only — would miss the
   antelope that motivated this): where present, a hit pre-selects Inverted + portrait
   copy. Two-card picker (Normal/Inverted) renders steel-on-product-color thumbnails.
   APPLY = full-res bitmap swap (2400px cap + iterative downscale until b64 < 290k —
   the serialize restore path drops layers at 300k), pushHistory AFTER mutation (redo
   works); original stashed in-session (_invertOrigB64, STRIPPED from undo snapshots +
   saves). UN-INVERT EXISTS ONLY WHILE THE STASH LIVES (post-review): the plate alone
   cannot reconstruct strokes connected to the hull boundary, so restored/undone
   designs keep their inverted state — no destructive recompute. _engraveInverted
   whitelisted in serializeDesign + BOTH restore paths (autosave AND server saved-
   designs — the server path also gains the previously-dropped _bgRemoved).
   _bgRemoved NOT repurposed; eligRemoveBg AND eligRemoveColor exclude inverted
   plates (Remove Color would delete the engrave field in one tap). PREVIEW PARITY:
   inverted plates composite with XOR in the renderEngrave + mockupPng mask passes,
   matching the DXF's even-odd (text ON the plate previews as dark holes — v2.9.30
   nesting passthrough renders it identically in production; the reference PNG keeps
   union — noted). Engrave->print warn + apply/auto-strip confirmations use toast()
   — showTip is globally disabled (dead-code catch; the v2.10.2 strip tip is now a
   toast too). Picker samples the product color with call-time natural/offset mapping
   (hi-res mockup-upgrade safe). Line item gains _{pos}_engrave_inverted='Yes'.
   Ships with one CSS rule (.dt-action-tab--invert order:9) in design-tool.css.
   KNOWN EDGES (accepted, documented): art CROSSING the plate's outer boundary
   dissolves in the DXF union (nested art is correct); reference PNG shows the union
   not the parity; variant switch to light products keeps the plate (preview stays
   honest via the steel mask); picker's full-res build is a synchronous main-thread
   pass (~100-300ms on mobile) behind the overlay open.

   v2.10.3 — FRAMED-ART CLASS + RELATIVE PAPER RULE (Brandon, Jul 30 2026). Root-cause
   refinement proven by comparing the incident poster against a SAME-DAY clean order
   (Stafford Land Works, frontengrave_22.dxf: 66 polylines, zero full-extent paper
   rects): the vectorizer omits EDGE-CONNECTED background — a logo on white that
   touches the canvas edges traces clean with no user action, which is why engraving
   "always worked." ENCLOSED white (art with a drawn frame/border) is NOT background
   to the tracer and ships as geometry — the failing class. Fixes: (1) engrave
   auto-prep eligibility widened from border-ring-white to ALSO accept a bright-paper
   tonal split (Otsu light-class mean >= 225, light fraction 35–98.5%) so framed art
   auto-preps; inverted art still refused; measured: incident poster now ELIGIBLE
   (light 65% @ ~250), Stafford eligible via border, dense-dark and inverted art
   refused. (2) Converter backstop gains a RELATIVE rule for dim scans: among known
   fills, when the lightest group is >= 180 luminance and >= 80 above the darkest,
   the lightest group is paper by the 1-bit engrave rule — dropped (never to empty;
   single-color art unaffected); same gwarn event, 'relative rule' message.

   v2.10.2 — ENGRAVE AUTO-PREP: WHITE PAPER STRIPPED AT UPLOAD (Brandon, Jul 30 2026).
   Direct engrave uploads of line art / logos now get the white background stripped
   AUTOMATICALLY (stripWhiteBackground — pixel threshold, client-side, no API) and are
   placed with no picker; the trace then vectorizes clean transparent art (the input
   class that has always traced well). Why this looked like a regression: the Jul 20
   vectorize.js change (deployed to Vercel ~Jul 24) switched output.shape_stacking
   'stacked'→'cutouts', so a white-background input now returns the paper as a canvas
   with art-shaped holes — duplicated boundaries that EZCAD's even-odd fill renders as
   a solid square (see v2.10.1). Direct uploads NEVER had input-side white-stripping
   (only AI photo results did); clean inputs were simply the norm until then.
   remove.bg stays for photos — it is a SUBJECT-isolation service and returns
   full-frame line art unchanged, which is why "background removed" appeared to do
   nothing on the incident poster. Guards: isLikelyPhoto keeps photo flows; dark
   border ring (white IS the art) → picker; strip that removes nothing/everything →
   picker; original file still ships to Prepress as the source upload. New helpers
   _borderIsWhite / _stripResultUsable; tip 'engrave-white-strip' on auto-prep.

   v2.10.1 — ENGRAVE SOLID-SQUARE HOTFIX (Brandon, Jul 30 2026; incident CB-20260730-2F3B).
   Root cause (reproduced by executing the deployed converters against the incident's
   image class): a white-background upload reached the vectorizer; the returned white
   "paper" group shipped as geometry — DXF discards fills so the white paths became a
   full-area contour + duplicated art boundaries (EZCAD even-odd fill = solid square);
   the AI8 EPS shipped white as real filled objects. NOT the v2.9.23 winding normalizer,
   NOT the v2.9.30 union, NOT faux-bold (all exonerated by execution), NOT the v2.10.0
   routing (converters byte-identical). Exposure came from a PRE-EXISTING restore bug:
   performRestore synced the method toggle but never swapped the upload panels, so a
   restored engrave design ran the PRINT upload flow (remove-bg picker, no engrave
   auto-prep) and let paper through. Fixes: (1) geoWorkerConvert drops white/near-white
   filled paths (RGB all >= 240) from 'eps-ai' + 'dxf' output with gwarn
   'engrave_white_bg_dropped' — engrave files are now immune regardless of upload flow;
   counters survive (black group carries its own hole subpaths); unknown fills kept;
   print EPS untouched (legit white art must ship). (2) performRestore now swaps the
   upload panels like applyMethod. Rides with a liquid v2.10.1 detection safety net:
   whole-token pass unchanged, but a non-empty imprint_type that matches ZERO tokens
   falls back to the legacy substring behavior (UV DTF before DTF Print) so no product
   can silently lose its methods to non-canonical wording.

   v2.10.0 — S3 OUTPUT ROUTING + METHOD RECIPES (Brandon, Jul 30 2026; PLAN-S3-OUTPUT-
   ROUTING rev 9.1, adversarially reviewed pre-build). Role-based subfolders inside every
   line-item folder — Radian (DXF) / Minis (engrave EPS) / ColorPrinting (print PDF +
   raster EPS) / DTF (transfer production PNGs) / Prepress (all PREPRESS PDFs, sources,
   proof sheets, reference PNGs) — via S3_ROLE_DIR + uploadProdFile(); subfolder rides the
   presign folder arg, never the filename. Design serial prepended to EVERY filename.
   Three recipe classes keyed on effectiveSideMethod() (engrave from the GLOBAL toggle —
   sideConfigs decorationMethod can never say Laser Engrave): ENGRAVE adds a PREPRESS PDF
   (Prepress-folder-only; pointer set stays DXF=1, EPS=2); FULL PRINT unchanged;
   TRANSFER-PNG (Vinyl Print / DTF Print / Direct to Film — one equivalence class; UV DTF
   Print is NOT transfer) ships ONLY the transparent 300dpi PNG (→DTF, pointer file 1) +
   PREPRESS (file 2) — PRODUCTION PDF + raster EPS suppressed, vectorizer trace skipped,
   honest type string 'print-transfer-png'. BULK: folder aligned to
   {sessionId}/{variantId}-{serial} (serial hoisted); ALL SVG uploads removed
   (_bulk_names_svg/_outlined dropped — verified writers-only); per-name sets per class
   (engrave keeps combined EPS+DXF masters AND gains per-name DXF/EPS; print = per-name
   PDF+EPS; transfer = per-name transparent PNG + PREPRESS); per-name proof PNGs dropped
   (white-fill canvas gone); print/transfer variable side is folder-referenced (no pointer
   props, Brandon Jul 30); NEW single-page two-column bulk proof sheet
   (buildBulkProofSheetPdf: names at relative print size + decorated sides w/ longest
   name). markSerialDirty() after every SUCCESSFUL cart add (single + bulk) — prevents a
   follow-on no-edit add from reusing the serial (folder overwrite + duplicate
   _design_serial breaking cart-sync); failed adds still retry into the same keys.
   presign.js / vercel.json / S3 config verified no-change. Deploys with the
   design-tool.liquid whole-token detection rewrite (DTF Print / Direct to Film added;
   flatbed exception generalized to any transfer method).

   v2.9.35 — ENGRAVE EPS BACK TO POSTSCRIPT POINTS, FOR CORELDRAW (Brandon, Jul 29 2026).
   Production reported an engrave EPS whose artwork should measure 2.025in opening in
   CorelDRAW at 0.714in, while the DXF alongside it was correct. 2.025 x 25.4/72 = 0.7144
   — the signature of a unit mismatch, not a scaling bug.

   ROOT CAUSE: v2.9.7 (order CB36475) switched the AI8 engrave dialect from points to
   MILLIMETRES because EZCAD2 is millimetre-native — it reads the AI8 operator stream's
   coordinates as mm and sizes to the geometry extents, so a points-scaled file opened
   72/25.4 = 2.8346x oversized and operators resized every engrave EPS by hand. That fixed
   EZCAD2 and broke Corel by exactly the same factor in the other direction: Corel is
   point-native and honours the PostScript unit, so every engrave EPS since Jul 23 has
   opened at 25.4/72 = 35.28% of true size.

   PROVEN, not inferred, against a shipped pair Brandon supplied (frontengrave, 3.0x2.5in
   area): the EPS coordinate stream's art extents are 42.3300 x 59.6629 units and the
   DXF's are 42.3272 x 59.6629 MILLIMETRES — identical numbers (the 0.0028 delta is beziers
   vs flattened polylines). Since the DXF is confirmed correct in EZCAD2 at mm, the EPS
   units were unambiguously mm. True size 1.6664 x 2.3489in; Corel showed 0.5879 x 0.8287in.

   THE DECISION: one coordinate stream cannot serve both readers — there is a single scale
   knob and the two consumers disagree by 2.8346x on the same numbers. Brandon confirmed
   the laser department drives EZCAD2 from the DXF and CorelDRAW is the only consumer of
   the engrave EPS, so points win. unitsPerInch is now 72 unconditionally; the AI8 operator
   dialect, CRLF line endings, CMYK fills, bounding boxes and %AI5_ArtSize are all
   unchanged in structure — only the scale factor moved. The DXF (mm) is untouched, as is
   the generic print EPS (already 72pt) and the raster print EPS (_assembleRasterEps has
   its own hardcoded x72 and never used this knob).

   IF EZCAD2 EVER NEEDS THE EPS AGAIN: emit a SECOND, separately named mm file. Do not
   rescale this one — that is what created the ping-pong.

   Applies to all three engrave call sites, which funnel through the one builder: single
   item (geoWorkerConvert 'eps-ai'), bulk per-name, and the bulk combined master. The
   worker copy is serialised from the same function source, so it inherits the change.

   v2.9.33 — GLYPH-TEMPLATE MONOGRAM STYLES (Brandon, Jul 29 2026). Adds support for
   ligature-style monogram fonts, where the left form, centre form, right form AND the
   surrounding frame are all glyphs in ONE font file — Octagone Monogram Solid/Outlined
   (2 letter, frame on '1') and Square Diamond Monogram Solid (3 letter, frame on '?').
   MEASURED, not assumed (fontTools + Chromium, Jul 29): Octagone maps A-Z to the LEFT
   half-letter (adv 417), a-z to the RIGHT half (adv 355) and 'one' to the octagon frame
   (adv 137, ink x -840..97 — a large NEGATIVE left bearing, it draws backwards over the
   pair). Square Diamond is calt-driven: lowercase -> left, uppercase -> tall centre,
   .end/.fina -> right, 'question' -> the diamond frame (adv 138, ink x -893..93); shaping
   'abc' yields a + B.medi + c.end and 'ABC' yields a.init + B + c.fina, pixel-identical.
   Both frames are hollow rings, so the isFilled/destination-out knockout never applies.

   WHY THE EXISTING PER-SLOT RENDERER CANNOT DRIVE THEM: addCustomMonogramLayer issues one
   fillText PER LETTER into a separate slot face. Per-glyph shaping never fires calt, so
   Square Diamond renders three LEFT-form letters spread apart instead of the diamond; and
   monoText is force-uppercased (8562), so an Octagone 'right' slot receives an uppercase
   char and draws a second LEFT half. Both reproduced in Chromium before writing any code.

   THE CHANGE — three new functions above the border cache, plus a gated branch in each of
   the two places a custom monogram is drawn (renderCustomMonoPreview for the library card,
   addCustomMonogramLayer for the placed layer). A style opts in with the new metaobject
   field `glyph_template` (emitted by design-tool.liquid:569 as `glyphTemplate`):
        {N} = Nth typed initial, {NU}/{NL} force case, {{ }} literal braces,
        anything else = a literal character handed straight to the font.
   Octagone: {1U}{2L}1     Square Diamond: {1}{2}{3}?
   drawTemplatedMono draws the whole thing as ONE fillText and centres it on the INK box
   (actualBoundingBoxLeft/Right/Ascent/Descent), NOT on measureText().width — advance width
   is meaningless for these fonts because the frame's negative bearing puts ink far left of
   the origin. It also CLAMPS the fitted size so textScale can never push ink past the box
   edge, because that clip would be baked into the production PNG/PDF/EPS. layout.spacing,
   centerScale and baselineOffset are deliberately NOT read on the template path — they
   exist to fake placement these fonts already do, and applying them shifts letters out of
   their own frame. defaultColor, textScale and textOffsetY still apply.

   BACKWARD COMPATIBILITY IS THE WHOLE GATE: monoGlyphTemplate() returns null for a missing
   key, '', whitespace, null, or a non-string, and every such style takes the untouched
   per-slot path. PRODUCTION needs no change — renderProductionPNG's monogram branch (14090)
   only catches monoStyle >= 0 (built-ins); a custom monogram is monoStyle -1 and rides the
   generic raster branch, so its production file is exactly the trimmed 2048 canvas this
   change produces. Colour rebuilds (rebuildMonogramColor 1864) and saved-design restores
   (findCustomMonoStyle 1852) both re-enter addCustomMonogramLayer, so they inherit it free.

   VERIFIED in headless Chromium against the REAL patched functions extracted from this
   file (not a reimplementation), 67/67 assertions: card + 2048 layer + trimToContent render
   correctly for all three faces (Octagone trims 1813x1806, Square Diamond 1817x1820, both
   centred within 0.1% and clear of every canvas edge); colour bakes in; 9 malformed
   templates return false without throwing; and 24 LEGACY comparisons — 6 style variants
   (no key / '' / '   ' / null / undefined / with borderImage) x 4 texts (A/AB/ABC/ABCD) —
   are BYTE-IDENTICAL between the pre-patch and post-patch renderCustomMonoPreview, 0 diff
   bytes. NOT verified: an actual PDF/EPS was not generated (the raster handed to them is
   unchanged in kind), and only Chromium was exercised — calt is on by default in every
   engine, but Safari/iOS was not tested.

   v2.9.21 — PRINT EPS BACK TO 300 DPI (Brandon, Jul 27 2026).
   EPS_DPI_MULT 2 -> 1. One constant; the hi-res branch in the print EPS block is a guarded
   no-op while it is 1 (`_ess > 1.01` never passes), so nothing else changed and flipping it
   back to 2 restores 600dpi. Rationale: the PRODUCTION PDF has carried outlined text as real
   vector paths with photos as 300dpi raster since v2.9.16, which is already "text at full
   resolution, uploads at 300" losslessly — so the EPS was paying ~3.5x the bytes for a gain
   production does not consume. Filename stays -PRODUCTION-RASTER (v2.9.19); it is accurate.

   v2.9.20 — PRINT EPS RASTER RAISED TO 600 DPI, BUDGETED (Brandon, Jul 27 2026).
   Single-item print EPS only. Physical size is unchanged and cannot change: _assembleRasterEps
   derives ImageMatrix from physDims and pins BoundingBox to physical inches, so pixel count is
   purely resolution. renderProductionPNG already accepted a resScale supersample argument (the
   vectorizer path uses it), so this is a second render at that scale feeding generateRasterEPS.

   Brandon asked whether text could be 600 while uploads stayed 300. It cannot — one raster has a
   single /Width and /Height. But rendering at 600 does not DEGRADE the 300dpi content: uploads
   are interpolated and carry the same information, they just gain nothing. Text, clipart and SVG
   re-render from infinite-resolution sources and are genuinely sharper.

     area          scale   eff dpi   canvas
     5.34 x 2.18   2.00      600      17MB
     8 x 8         2.00      600      92MB
     10 x 10       1.63      490      96MB
     14 x 14       1.17      350      96MB

   EPS_PX_BUDGET is 24M rather than a flat 2x because memory is double-buffered here — the hi-res
   PNG data URL is live while generateRasterEPS decodes it into another canvas. Any failure or
   null render falls back to the 300dpi composite (gwarn eps_hires_failed / eps_hires_empty), so a
   large area on a phone degrades instead of throwing.

   Measured cost ~3.5x the EPS bytes, near-constant across content: Flate here has no image
   predictor, so smooth upscaled regions compress barely better than detailed ones. The earlier
   assumption that interpolated content would compress away most of the growth was wrong.

   NOT changed: urls.png still uploads the 300dpi composite (its name says 300dpi and downstream
   keys on it); bulk stays at 300 (a hi-res render per name inside the serial confirm loop would
   multiply frozen-UI time by the name count); engrave is vector and unaffected.

   Worth knowing: the PRODUCTION PDF has been hybrid since v2.9.16 — outlined text as real vector
   paths, photos as 300dpi raster — which is already "text at full resolution, uploads at 300",
   losslessly. If production prints from the PDF, this EPS change is optional.

   v2.9.19 — PRINT EPS RENAMED TO -PRODUCTION-RASTER (Brandon, Jul 27 2026).
   Filename only. No pipeline change: the print EPS was ALREADY a full 300dpi DeviceRGB raster
   composite and has been since Jul 23. generateRasterEPS(compositePng, physDims) takes the
   composite and nothing else — it has no parameter for the traced SVG, in either the single-item
   (17778) or bulk (19871) path. Item C of PLAN-PRINT-VECTOR-HYBRID (the hybrid EPS) was scoped
   but never built, so there was nothing to revert. What v2.9.16 made hybrid was the PRODUCTION
   PDF, which still is.

     single item  ${sideSlug}-print.eps           → ${sideSlug}-print-PRODUCTION-RASTER.eps
     bulk         name-N-<name>-print.eps         → name-N-<name>-print-PRODUCTION-RASTER.eps
     engrave      ${sideSlug}-engrave.eps         UNCHANGED (AI8 vector for EZCAD2)
     bulk engrave name-N-<name>-engrave.eps       UNCHANGED

   The bulk template was shared between the two — one `bulkSfx` fed the EPS, DXF and both PDFs —
   so the EPS now takes its own `bulkEpsSfx` and engrave keeps its name. DXF and PDF still use
   bulkSfx; their vector/raster nature was never ambiguous.

   ⚠ These names are S3 object keys and they appear inside the URLs stored in
   _{pos}_production_file_N. Anything downstream matching on "-print.eps" needs updating.
   production_type strings are untouched.

   v2.9.18 — "LOGO ON WHITE" AS A THIRD PICKER CARD (Brandon, Jul 27 2026).
   remove.bg reads the specular white highlights on glossy 3-D logo renders as
   semitransparency and eats them ("Lew Crew"). Those highlights are artwork, and the Onyx
   RIP does not pick up white from a raster, so losing them loses them on press too.
   A local matte — dark-ink detection, a σ=60 normalized-convolution background model, and a
   distance ramp — keeps them: 2,131 highlight px retained, 7,082 partial-alpha edge px.

   It is NOT a background remover and is never the default. It forces every ink-enclosed
   region opaque, so on a closed-outline logo it fills the whole interior. Measured against
   known-good cutouts: CB36597 +445,550 wrongly-opaque px (87% of what it kept),
   Commonwealth Mór +203,321, FMF 142,434 light px forced opaque — and zero false negatives
   in every case, i.e. it never cuts too much, it refuses to cut interiors.

   So it is offered only as a third card in openRemoveBgPicker, next to Original and
   Background Removed, and only when LowMatte.eligible() passes (fillRatio ≤ 1.5 bounds the
   fill damage; bgStd ≥ 3 requires a gradient worth modelling). The auto-select line is
   untouched — sel(isLikelyPhoto(...)) still only ever chooses 'rbg' or 'orig' — so this can
   reach a customer's artwork only by a deliberate click on a thumbnail they are looking at.
   Blast radius on uploads that work today is zero by construction, not by tuning.

   Costs no API call; runs deferred on the main thread (~170ms analyse + ~210ms matte at
   1024px) while remove.bg is still in flight. Ported from the Python reference and validated
   against it: mean |Δalpha| 0.03/255, 99.988% binary agreement, identical highlight count.
   CSS: .dt-rbg-choices wraps and .dt-rbg-choice gains a flex-basis floor for the 3-up row.

   v2.9.17 — isLikelyPhoto INVERTED ON CUT-OUT LINE ART (Brandon, Jul 27 2026).
   v2.9.16 shipped and print still did not trace. Diagnosed live against the loaded layer:
   isPhoto was TRUE on a 7-colour black-line-art logo, so it sat in bucket C.
   isLikelyPhoto's texture test skips pixels below alpha 128. On background-removed line art
   ~85% of the ink is partial alpha, so the gate discards precisely the hard black/white
   boundaries that make it line art, leaving only soft in-stroke gradients. Measured on the
   real layer: colors 7, samples 332 of ~4032, hard 0, soft frac 0.539. With hard = 0 the
   `sf > 3 * (hard/n)` term degenerates to `sf > 0` and cannot be false, so the texture test
   overrode a colour signal that was already correct.
   FIX: require n >= 600 AND hard > 0 before trusting the texture test; otherwise fall back to
   the colour-count signal. A background-removed PHOTO still fills the frame (thousands of
   samples, hard edges intact) and grayscale photos — the case the texture test was added for —
   sample fully, so neither is affected. Verified both directions on the actual files.
   ── v2.9.16 — PRINT TRACES NON-PHOTO RASTER ART (Brandon, Jul 27 2026).
   Print stopped using the vectorizer entirely: v2.9.6 made the print EPS a raster composite,
   which removed the trace's last print consumer, and v2.9.9 then deleted the call as dead weight.
   Each step was locally correct; together they took the tracer out of print.
   MEASURED on the Fleet Marine Force order (Jul 27), same logo through both pipelines:
     ENGRAVE (Vectorizer.ai -> DXF) : 172 contours, 25,216 points = 146.6 pts/contour
     PRINT   (embedded raster)      : 177 contours,  1,314 points =   7.4 pts/contour
   The same shapes, described 20x more coarsely — and the print "outlines" were only the
   silhouette CLIP (a <=512px marching-squares polygon), never artwork. The print PDF held one
   1800x1800 uncompressed raster and no vector at all. Its embedded art was also 85.3% partially
   transparent (alpha 255 on just 14.7% of painted px), because thin linework upscaled 1.38x from
   a 1172px JPEG is nearly all edge pixels — which prints grey, not black.
   WHAT CHANGED — per-layer classification, hoisted to module scope so the "what to trace" and
   "what to embed" decisions can never disagree:
     A isNativeVector  already paths, untouched
     B isTraceableArt  NOW TRACED to paths        <- the fix
     C isPhotoLayer    stays an embedded raster
   One production file can carry all three, so a side holding a photo AND a logo gets both
   treatments — the old per-SIDE flag classified that whole side "photo" and traced nothing.
   HARD EXCLUDE (Brandon): removedColor / _rcTrim are ALWAYS photo, never traced. Remove Color
   REPLACES l.img, so isLikelyPhoto() would judge the edited bitmap and a photo with a flat
   background stripped could flip into the traceable bucket and ship posterized.
   _bgRemoved is DELIBERATELY NOT in that list. Adversarial review caught this: despite the name it
   does not mean "the user removed a background", it means "this layer has no background to remove",
   and it is set on ANY upload that already carries transparency (~6289 "already a cutout", ~6672,
   ~6829). Excluding it would have put every transparent-PNG logo — the most common customer upload
   — into bucket C and traced nothing, making this whole change a no-op for the case that motivated
   it, and REGRESSING bulk (whose old gate ignored _bgRemoved and did trace those logos, so they
   would have vanished from the per-name shipping SVGs, which have no raster embed path at all).
   A genuinely background-removed PHOTO is still caught by isPhoto / isLikelyPhoto.
   Also from that review: a truthy-but-unusable traced string is more dangerous than no trace,
   because the embed narrows the moment it is truthy and any text on the side keeps paintedCount
   above zero so the never-blank guard cannot fire. The trace is now proven to extract geometry
   (extractTracedSvgGroup) before it is trusted; otherwise it falls back to null + *_trace_empty.
   KNOWN LIMITATION, unchanged: bucket B is a single composite plane, so a vector layer sandwiched
   between two photos still paints above both.
   maxColors stays 32 for print (2 for engrave) — it is a cap, not a target.
   THREE DEFECTS FIXED ALONGSIDE, each of which would have made this ship worse than before:
   (1) DOUBLE PAINT. buildCompositeOutlinedSvg pushed tracedGroup unconditionally while
       generateProductionPDF calls it TWICE per side (below-raster slice, above-raster slice), so
       traced art painted twice and two elements shared the hard-coded id="traced-raster". Now
       gated on `pred` like vecGroups and textPaths already were.
   (2) TRANSPARENT-PNG TRAP. renderProductionPNG returns null ONLY when a side has no visible
       layers; its filter runs inside the render loop, so a predicate matching nothing yields a
       fully transparent PNG. The old `if (rasterPng)` would have embedded that blank and set
       drewRaster, silently dropping the artwork. The embed is now gated on the LAYER SET.
   (3) SILENT DEGRADATION ON TRACE FAILURE. If the vectorizer fails, the embed predicate widens
       back to ALL raster so the order degrades to exactly the pre-v2.9.16 file. Necessary because
       the never-blank guard CANNOT catch this: paintedCount is > 0 from any text on the side.
       Failures now log print_trace_failed, keyed separately from engrave_trace_failed so print
       problems are visible to dashboards filtered on print.
   BULK GETS THE SAME TREATMENT, deliberately: bulkTraceVariableRaster used the same per-side
   photo bail (a bulk print job with a photo background never traced its logo either), and the
   bulk PDF call passed only two arguments — which after (2) above would have dropped bucket B
   from every bulk PDF while the proof still showed it.
   ENGRAVE IS UNCHANGED throughout: it still traces whenever a side has any raster, photo or not.
   DXF must exist for every engraving scenario, and a traced outline beats a bare PNG for the
   laser even on a mis-flagged photo. skipTrace is deleted — it only ever meant "photo, on the
   print path", which the per-layer split now states directly.
   COST: a Vectorizer.ai round-trip (1-4s) returns to print orders containing non-photo raster
   art. That is the cost v2.9.9 removed; it now buys the output above. It also puts print orders
   on the vectorizer credit budget.
   STILL OPEN: productionType (~17347) is not yet recomputed, so a side whose logo now ships as
   vector is still stamped print-raster-300dpi on _{pos}_production_type. Label only, no consumer
   breaks — 18249/19608 branch on startsWith('engrave').
   ── v2.9.15 — SILHOUETTE CLIP ALPHA GATE 128 -> 16 (Brandon, Jul 27 2026).
   traceAlphaContours built the PRODUCTION-PDF clip from a <=512px downsample tested at alpha >= 128.
   Downsampling averages a 1px stroke over a fraction of a pixel, roughly halving its alpha, so fine
   linework fell below the gate, dropped out of the silhouette, and the clip CUT IT OUT of the
   full-resolution raster it wraps. Subtractive, not just coarse (Commonwealth Mor Wine, Jul 25).
   MEASURED before changing, then RE-MEASURED after an adversarial review found the first pass
   unsound (it rasterized the clip a half-pixel off and let antialiasing soften coverage). Method:
   this function ported verbatim to Node, the resulting compound clip rasterized 4x-supersampled,
   pixel-centre aligned, nonzero fill, no AA, compared against true ink at alpha > 0 — a ground
   truth deliberately NOT equal to the new gate. Percentage of real ink the clip deletes:
                                          bilinear         box (Chrome-like)
     CB36597 fine-line logo 1084x1084     19.62 -> 4.49     23.48 -> 7.29
     real 300dpi print composite 900x750   1.21 -> 0.64      1.08 -> 0.75
   Improves under BOTH resamplers on BOTH assets, so the result does not hinge on which filter the
   browser uses for drawImage — the review showed a filter CAN flip the sign, so this was checked
   explicitly rather than assumed. Quote "3-4x less ink deleted", not a single figure: the absolute
   number moves by ~3x with rasterization convention alone.
   COST, on-path: 145ms -> 244ms per side (CB36597). Runs once per side in generateProductionPDF,
   main thread, single-item and bulk. Bulk is the exposure: it is once PER NAME inside the serial
   render loop with the confirm button blocked, so a 100-name group order adds roughly 10-17s.
   Shrinks on its own once the per-layer print hybrid lands — the clip's remaining job is then the
   photo bucket only. Text/vector-only sides never trace at all (hasRaster gate).
   MAX_TRACE WAS MEASURED AND DELIBERATELY NOT CHANGED. 512 -> 1024 helped one asset and hurt the
   other, and full source resolution was worse for both at higher cost. No value wins on both, so
   the "raise MAX_TRACE" fix direction is rejected on evidence.
   DP_TOL unchanged: contour points are upscaled to original space at the push site BEFORE
   dpSimplify runs, so 1.5 * upscale is constant in trace-resolution terms — the correct
   compensation, not the inversion it looks like.
   TWO KNOWN DEFECTS THIS DOES NOT FIX, both pre-existing and both larger in places:
   (a) WINDING CANCELLATION. contoursToPdfClipOps' comment claims non-zero winding "handles the
       union naturally". It does not — marching squares emits contours of mixed orientation, so
       overlapping contours CANCEL under clip('non-zero') and punch holes in the clip. Measured on
       CB36597: about half the remaining ink loss at the new gate is cancellation, not threshold.
       Real fix is consistent contour orientation. Even-odd is worse, not better.
   (b) visited IS A COLLIDING HASH. It is sized (tw+1)*(th+1) but keys run to ~4x that, so
       "% visited.length" makes false collisions routine — 13-25 contour starts skipped per run,
       measured. Unchanged by this edit (25 at gate 128, 24 at gate 16) but it is why contour counts
       are not a smooth function of the threshold.
   ALSO: the safety crop gets looser — mask/ink area ratio rises about 18%. Per the v2.9.8 comment
   the clip exists only for RIPs that ignore the SMask; those RIPs now print correspondingly more
   background. Accepted: cutting real ink is the worse failure.
   SCOPE: one constant in one function with one caller (generateProductionPDF, sole call site).
   PREPRESS, EPS, DXF and the whole engrave path are untouched. Verified in the shipped bundle: the
   minified body differs from v2.9.14 by exactly "o||128" -> "o||16" and nothing else.
   ALSO IN v2.9.15 — CUSTOM MONOGRAM RENDER SIZE 512 -> 2048 (addCustomMonogramLayer).
   Custom monograms were the ONLY layer family shipping an upscaled raster to production.
   renderProductionPNG re-renders built-in MONO_STYLES at the true production size
   (monoSize = max(lw, lh), the l.monoStyle >= 0 branch), but a custom style has monoStyle === -1,
   fails that test, and falls through to the generic raster branch where the trimmed 512px canvas is
   just scaled into the layer box. That upscale is the jagged ring on the Jul 23 order
   (round_monogram_circle_500). The three other `size = 512` sites are built-in monogram paths and
   are deliberately NOT changed — they already re-render at production size, so bumping them would
   cost memory and buy nothing on the production file.
   2048 is sized from measured decoration areas, not picked round. Across the five monogram-enabled
   products the largest area is Simple Modern 24 oz at 2.5 x 6 in, so a monogram scaled to fill needs
   6 * 300 = 1800px. 2048 covers every live product with 248px spare; 1024 would be short by 776px.
   COST, measured on the live "Round" style: PNG data URL 47KB -> 283KB. serializeLayers keeps b64
   TWICE per history snapshot (b64 + _imgSrc) and MAX_UNDO is 50, so worst case per side goes
   ~4.6MB -> ~27.6MB of strings. Accepted. serializeDesign does NOT carry it — that gate requires
   isRasterUpload, false for monograms — so autosave and the saved-design payload are unchanged.
   STOPGAP: stops mattering for production once the monogram emits real svgMarkup and rides the
   vector branch. Still governs the editor preview and picker thumbnails until then.
   ── v2.9.14 — INK-BOX DENSITY THRESHOLD 2% -> 0% (Brandon, Jul 25 2026).
   Uploaded artwork was being cropped before placement. trimToContent used a density rule —
   a row/column only counted as content if its summed alpha exceeded 2% OF THE PEAK row/column.
   That assumes uniform density and fails on any artwork with a SPARSE EXTREMITY attached to a
   DENSE BODY. Commonwealth Mor Wine (front, print): a fine-line house illustration above a solid
   wordmark. Peak row density 224,480; 2% threshold 4,490; the roof apex rows measured 17 to 3,380
   (0.01%-1.5% of peak) — every one below threshold. Result: the TOP 32 ROWS of the customer's
   logo were discarded, then the remainder was scaled up to fill the vacated space, so it read as
   "the top is cropped off". Verified against the customer's actual source PNG: old bbox
   y 54..1230, new bbox y 22..1230, where 22 is the true topmost ink row.
   FIX: density fraction set to 0 at BOTH mirror sites — trimToContent and computeSvgInkBox.
   The alpha > 16 gate is now the only filter. Both sites MUST stay identical or the display
   raster and the production remap disagree and geometry ships wrong; the doc comment at
   computeSvgInkBox says so and is updated to read 0%-of-peak.
   ALSO FIXES the clip warning not firing on first non-alpha overlap (deferred punch-list item 3):
   the clip probe mirrors this same scan, so thin ink at an artwork's edge was invisible to it too.
   TRADE-OFF ACCEPTED (Brandon): the 2% rule existed to stop stray halo/speckle pixels pinning the
   bbox to the frame. With 0%, a single pixel above alpha 16 anywhere near an edge will define that
   edge. remove.bg cutouts with faint edge speckle are the likeliest regression; the lever is the
   alpha gate (currently >16), NOT the density fraction. The 0.98 both-axes no-trim guard is
   unchanged and still prevents a frame-filling bbox from trimming at all.
   ── v2.9.13 — PRESENTATION EXTRACTED TO CSS (Brandon, Jul 25 2026).
   Groundwork for re-skinning: presentation moved out of JS strings into design-tool.css so the
   stylesheet and brand skin can actually reach it. NO logic changed; uploads and the production
   file pipeline were not touched (verified: zero static inline styles exist inside any production
   function). Inline style= attributes 183 -> 152.
   (1) DIALOG WIDTH LADDER: the 11 .dt-rbg-panel inline widths became five modifiers —
       --xs/sm/md/lg/xl = 380/400/420/520/620px, plus --center for text-align. 560px stays the
       unclassed default. Declared immediately after the base rule so they win at equal
       specificity, and before the .dt-above-tabs !important block so the monogram popup is
       unaffected. Widths are pixel-identical at every viewport including tablet.
   (2) FOUR DIALOGS NORMALIZED TO THE BASE 24px PADDING (approved): PDF spinner (was 40px), PDF
       error (28px), blank-item confirm (32px 28px), copyright agreement (28px 24px).
   (3) DIALOG TYPOGRAPHY: .dt-dlg-title / --body / --note / --field added, 9 exact-duplicate sites
       converted, values moved verbatim — no visual change. The remaining dialog type is a long
       tail (36 of 48 attributes are unique, six different title sizes for one role); a
       token-backed scale is proposed but NOT applied because it would visibly change ~two dozen
       dialogs. See claude/TYPE-SCALE-PROPOSAL.md.
   (4) 11 REDUNDANT INLINE DECLARATIONS DELETED — each already set to the identical value by an
       existing rule: grid-column:1/-1 x5 (.dt-empty-state, css:363), width:100% x4
       (.dt-rbg-use-btn css:1098, .dt-input css:438), max-width:560px x2 (equals the base).
       Includes an isMobile() ternary on the monogram panel whose branches were both no-ops.
   (5) DEAD CODE REMOVED: showWizard / showFirstTimeTooltip / closeWizard and their init() call.
       showWizard's second statement was an unconditional return, so the whole chain was inert;
       a stale getElementById('dt-wizard') guard in maybeStartTour() went with it. 49 lines.
   NOT DONE, deliberately: 13 orphan functions are flagged and untouched in
   claude/FLAGGED-ORPHANS-DO-NOT-DELETE.md (incl. isUVDTF, verified superseded by
   printFinishType(), and isVectorEligible, verified superseded by the v2.9.0 per-layer
   classification). 13 dead .dt-wizard* CSS rules also left in place.
   ── v2.9.12 — CUSTOM MONOGRAM CARD THUMBNAIL SIZE + CRISPNESS (Brandon, Jul 24 2026).
   Custom (metaobject) monogram styles rendered small/floating in their picker cards while the
   built-in styles filled theirs. Root cause: renderCustomMonoPreview() drew letters at a small
   fraction of the 160px canvas (side font ~0.3h) and bordered PNGs carry their own transparent
   padding, so the art occupied ~30-45% of the card. Fix: SUPERSAMPLE — render the full composite
   (letters + border + tint) into a 4× offscreen (640px) so text is rasterized large and the cached
   CDN border image is drawn at full resolution, then trim to its non-transparent bounds (new
   _alphaBounds()) and DOWNSCALE into the card to fill ~86% — matching the built-ins, crisp (a first
   pass that rendered at card size and scaled UP was blurry; downscaling from 4× is sharp). THUMBNAIL
   ONLY: the placed monogram is rendered + trimmed independently in addCustomMonogramLayer (512px),
   so production files and on-product placement are unchanged. A tainted canvas (border image without
   CORS) falls back to fitting the whole offscreen.
   ── v2.9.11 — CORPSMAN AUDIT FIXES #2/#3/#5 (Brandon, Jul 24 2026).
   (1) #2 FAUX-BOLD IN ENGRAVE OUTPUTS: faux-bold text (fill + same-color stroke in the outlined
       SVG) engraved at regular weight — the AI8 EPS and DXF builders are fill-only and dropped
       the stroke. geoWorkerConvert now bakes the weight into fill geometry for engrave types
       ('eps-ai'/'dxf'): _fauxBoldDilateD flattens the glyph path (parseSvgPathToPolylines) and
       _dilatePolysForBold offsets every contour by strokeWidth/2 — nesting depth (even-odd)
       decides direction (outer grows, counter shrinks; direction verified empirically by area so
       winding never matters; miter-clamped bisector offset; counters a fat stroke would swallow
       are dropped). Runs main-thread before worker post — worker contract untouched. Print 'eps'
       keeps its native PostScript stroke pass.
   (2) #3 REMOVE COLOR TIGHTENS THE LAYER BOX: Remove Color shrank visible ink but left the box at
       the full frame (Corpsman badge box overstated ~10% tall) — measured logo size and Fit both
       overstated. applyRemoveColor now trims the processed image (same trimToContent as upload;
       trimToContent gained crop-offset returns x/y/fw/fh) and remaps the box to the surviving
       ink, rotation/flip-safe (new center = C + R·F·(Δsub·s)). The remap is RELATIVE via l._rcTrim
       (which frame region the current img/box shows): re-picks preserve user moves/resizes and
       never compound-shrink; undoRemoveColor un-tightens relatively too. _rcTrim is stripped from
       history snapshots together with _originalImg/_originalB64 (one consistent frame trio), and
       the trimmed image is awaited to decode before the swap so production renders can't catch a
       blank layer. Faux-bold dilation is gated on an explicit data-dt-fauxbold marker (emitText →
       flattenSvgPaths), outer-vs-counter classified by winding sign vs the dominant contour (safe
       for overlapping script glyphs), swallowed counters dropped via size + net-edge-dot guards.
   (3) #5 DOS-EPS TWIN CUT: both EPS variants loaded fine in EZCAD2; the DOS twin cost a TIFF
       preview render + an extra sequential upload at add-to-cart, so it lost. Plain AI8 EPS is
       now the only engrave EPS — single + bulk (per-name and stacked master); helpers
       (_rasterToPreviewCanvas/_canvasToTiffBytes/_wrapDosEps/uploadDosEps) removed. Engrave file
       order is now DXF (file 1), EPS (file 2).
   ── v2.9.10 — gwarn DEFINED + LOUD ENGRAVE FAILURE TELEMETRY (Brandon, Jul 24 2026; Michigan
   line-art order shipped PNG-only with zero signal). Root problem: gwarn was CALLED at 4 fallback
   sites since v2.8 but its definition was lost (likely the Jul 6 re-baseline) — any reached call
   threw ReferenceError — and every engrave failure gate was a DEBUG-gated console.warn, invisible
   on the live site. An engrave side with no text/vector layers depends ENTIRELY on the
   Vectorizer.ai trace for EPS+DXF; when that API call fails (credits, rate limit, outage), the
   order silently degrades to a lone 300dpi PNG. Now: gwarn is defined (console.warn + GA4
   dt_error via dtAnalytics, guarded, hoisted); engrave trace/EPS/DXF/outline failures and the
   bulk equivalents all gwarn with the real error message; and a PNG-only engrave result fires
   engrave_png_only. Watch dt_error in GA4 for engrave_* keys.
   ── v2.9.9 — PRINT ORDERS NO LONGER CALL VECTORIZER.AI (Brandon, Jul 23 2026). The raster
   EPS (v2.9.6) removed the single-item print flow's last consumer of tracedRasterSvg (print PDFs
   pass null; print EPS embeds the composite raster), but the trace still ran for every print
   order with a non-photo raster: a ~2.8MP supersampled render + a 1-4s Vectorizer.ai API
   round-trip whose output was discarded. The trace is now gated to ENGRAVE only (which needs
   vector geometry for DXF/EPS). Print cart-adds with logo/clipart uploads get the full trace
   time back. Bulk's own trace (bulkVarTracedSvg, feeds the per-name print shipping SVG) is
   untouched. Also saves the vectorizer API cost per print order.
   ── v2.9.8 — SILHOUETTE CLIP NO LONGER DEGRADES VECTOR CONTENT (Brandon, Jul 23 2026; ROMO
   CPA order). generateProductionPDF clipped ALL content — including opentype-outlined text and
   vector art — with the traced silhouette of the composite: a <=512px marching-squares polygon,
   Douglas-Peucker simplified. That laid a low-res copy of the art over its own true outlines and
   chewed every edge (a text-only back rendered shredded in the PRODUCTION PDF while the EPS,
   which has no such clip, was perfect; small text lost glyph interiors entirely). Vector paths
   are their own exact silhouette — the clip's only legitimate job is the safety crop around the
   embedded raster PNG for RIPs that ignore its SMask. Now: the trace runs only when a raster is
   embedded, and the clip wraps ONLY the addImage call (q/W n/Do/Q); vector overlays paint
   unclipped. Applies to single-item and bulk (same function).
   ── v2.9.7 — ENGRAVE AI8 EPS IN MILLIMETERS [SUPERSEDED BY v2.9.35 — REVERTED TO POINTS;
      Corel, not EZCAD2, is the EPS consumer. Read v2.9.35 before acting on this entry.]
      (Brandon, Jul 23 2026; order CB36475). EZCAD2 is
   mm-native and reads AI8 coordinate units as MILLIMETERS, sizing to geometry extents (artboard
   ignored) — points-scaled engrave EPS opened 72/25.4 = 2.835x oversized (145.79pt of art showed
   as 145.79mm = the 5.739331in production measured on a 2.75x3in engrave area; operators resized
   every engrave EPS by hand). _buildEpsFromPaths now emits the eps-ai dialect at 25.4 units/inch
   (coordinates + BoundingBox + AI5_ArtSize), matching the DXF (already mm, already correct).
   Generic print EPS stays at 72 pt/inch. Applies to single-item, bulk per-name, and the bulk
   combined master (all funnel through the same builder; worker copy serialized from the same
   function). AI8 remains engraver-only — it now renders small in Ghostscript, which is fine.
   ── v2.9.6 — RASTER EPS FOR ALL PRINT ORDERS (Brandon, Jul 23 2026). Print EPS is now the
   300 DPI composite embedded as a PostScript Level-3 DeviceRGB image with a stencil mask
   (ImageType 3 / InterleaveType 2, Flate+ASCII85; mask bit 1 = not painted so nothing prints
   outside the design silhouette; transparent pixels also carry white RGB as a belt-and-braces
   for mask-ignoring RIPs). WHY: the outlined vector EPS wrote device CMYK via a naive max-UCR
   formula — no ICC profile, no gamut mapping — so colors drifted on press (cream #F3F0E9 became
   a gray K tint) and photo rasters could not be represented at all (v2.9.5 skipped photo EPS
   entirely; this supersedes that skip). DeviceRGB delegates color conversion to the color-managed
   RIP — the same treatment the PDFs get. Applies to single-item AND bulk print
   (generateRasterEPS from the composite); ENGRAVE EPS (AI8 vector for EZCAD2) and DXF unchanged;
   geoWorkerConvert('eps') retained but no longer called by the print flow. Limitations: alpha
   thresholded at 50% (no soft edges in EPS), text rasterized at 300 DPI (PDFs still carry
   live/vector text). Uncompressed ASCII85 fallback when CompressionStream is unavailable.
   ── v2.9.5 — PRODUCTION ↔ DISPLAY PARITY (Brandon, Jul 23 2026). Root-caused from a shipped
   Jul 22 order (oval-cameo photo design): production files were skewed vs the approved mockup, the
   PRODUCTION PDF painted a black oval over the photo, and the EPS shipped only a cream oval.
   (1) INK-BOX MAPPING: the display raster of an SVG layer is TRIMMED to its ink (trimToContent) and
       stretched into the layer box, but production mapped the FULL viewBox uniformly — any markup
       whose ink doesn't fill its viewBox (every DTBackdrops frame) shipped letterboxed/off-aspect.
       New computeSvgInkBox/ensureSvgInkBox (cached on l._inkBox, invalidated on markup change,
       AWAITED by production paths) mirrors trimToContent's scan exactly (incl. the 0.98 no-trim
       guard); rasterizeSvgForProduction crops the viewBox to the ink rect with
       preserveAspectRatio="none"; buildSingleVectorLayerGroup maps the ink rect; buildMaskPath
       takes the backdrop art's ink box so the photo mask rides the SAME mapping.
   (2) MASK PARITY: mockupPng (the APPROVED cart preview!), templateThumbPng, and renderEngrave now
       apply maskClipFor like renderPrint and production always did — the mockup no longer shows an
       unmasked photo that every production file crops.
   (3) RESTORE PARITY: saved-design restore now display-sizes + trims the SVG raster like fresh
       placement (was: raw markup at intrinsic size, untrimmed → blurry + letterboxed after reload).
       Saved geometry untouched; paint source only. ensureSvgInkBox hooked into restore, addSvgLayer,
       refreshSvgLayer, swapStateLayer.
   (4) PRODUCTION PDF Z-ORDER: vectors whose layer sits BELOW the first raster layer now paint
       BEFORE the raster PNG embed (buildCompositeOutlinedSvg gained an optional layerPred zIdx
       filter; same rule as the CB36156 traced-raster split). A backdrop no longer paints over its
       photo. paintedCount sums across both slices so the never-blank fallback still holds.
   (5) STROKE-AWARE VECTOR OUTPUT: flattenSvgPaths now resolves stroke color + width (attr/style,
       ancestor walk, SVG default 1, width scaled by sqrt|det| of the accumulated transform);
       shared _paintPdfPathEntry paints fill-none+stroke paths as STROKES in the stroke color
       (was: black fill via fillStroke) in PRODUCTION + prepress-fallback PDFs; generic print EPS
       strokes them natively (setlinewidth/stroke) instead of dropping them. ENGRAVE AI8 EPS and
       DXF keep the Jul 17 drop (EZCAD2 stroke support unverified). Faux-bold text unchanged
       (stroke === fill degenerates to the old fillStroke).
   (6) Silhouette clip: doc.discardPath() after clip('non-zero') so the stream is `W n`, not a bare
       `W` (invalid; Illustrator rendered the raw traced polygon).
   (7) Print EPS is SKIPPED for photo designs with no trace (it would omit the photo and mislead
       production; PDFs + 300dpi PNG are authoritative — productionType already 'print-photo-300dpi').
   (8) v2.9.4 fontkit fix RE-APPLIED to this canonical source (waitForFontkit + registerFontkit in
       _buildPdfLibPrepress) — it was live in the deployed min but missing here.
   ── v2.9.3 — PRINT PREPRESS proof-exact fallback + shell refactor (Brandon, Jul 22 2026).
   generatePrepressPDF refactored so the prepress STRUCTURE (bleed page + "Print Artwork" OCG layer +
   bleed-box clip + dashed trim guide, no label) is defined ONCE in _buildPdfLibPrepress and shared by
   both attempts. Primary = non-text composite (bleed) + LIVE editable text. If live text throws, the
   new fallback stays in pdf-lib and embeds the FULL composite rendered by the SAME canvas engine as
   the on-screen proof — so the prepress matches what the customer approved pixel-for-pixel — still on
   the Print Artwork layer with bleed/clip/guide. (Running pdf-lib twice wouldn't help: live-text
   failures are deterministic and repeat; the raster path also survives a font that opentype can't
   re-fetch but canvas already loaded.) jsPDF is now the LAST resort only when pdf-lib can't load at
   all (that path can't add the OCG layer). Removed dead locals (pxW/pxH/uniformScale/offX/offY/sidePA)
   from the prepress fn. Net less code despite the added fallback.
   ── v2.9.2 — PRINT PREPRESS bleed fix (Brandon, Jul 22 2026). Corrects v2.9.1, which clipped
   artwork to the TRIM and so trimmed off the bleed. Now the prepress CARRIES the bleed: the raster
   composite is rendered at BLEED size (renderProductionPNG gains an optional bleedPx arg — defaults
   to 0, so production/engrave/trace/mockup callers are byte-identical) to capture up to 100px of
   artwork overflowing the print area, placed filling the whole page, and clipped to the BLEED box
   (page) — NOT the trim. The dashed rectangle still marks the trim; the "Print Artwork" layer and
   label removal from v2.9.1 are unchanged. Applies to the pdf-lib primary path (single + bulk) and
   the jsPDF fallback (label removal + bleed-box clip; no OCG layer — jsPDF limitation).
   ── v2.9.1 — PRINT PREPRESS PDF: designer-ready output (Brandon, Jul 22 2026).
   Applies to EVERY print prepress path (generatePrepressPDF for single + bulk; jsPDF fallback),
   regardless of upload type or design elements:
   (1) "PRINT AREA" text label removed — the dashed print-area rectangle (clip guide) is kept.
   (2) All artwork (raster composite + live text) is wrapped in a named Optional-Content layer
       "Print Artwork" (pdf-lib OCG), so the prepress opens with the design on a selectable layer.
   (3) Artwork is clipped to the print-area rectangle (pre-masked to the imprint bounds) so the
       file arrives prod-ready and the designer no longer masks by hand. Balanced BDC/q/W n … Q/EMC,
       emitted only when setup succeeds so operators never go unbalanced; guide drawn outside the
       layer+clip. The jsPDF fallback (only used if pdf-lib fails to load) applies (1) and (3);
       jsPDF has no OCG support so it cannot add the named layer. PRODUCTION PDF untouched.
   ── v2.9.0 — VECTOR-PDF PRESERVATION + ENGRAVE OUTPUT GUARANTEES (Brandon, Jul 22 2026).
   (1) True-vector PDF uploads are no longer rasterized: pdfPageIsVector() classifies the page
       (live text / >2 vector path-paints = vector; a single image covering >=90% of the page =
       raster-wrapped, e.g. a PowerPoint-exported JPEG), and vector PDFs are extracted to outlined
       SVG via MuPDF (mupdf.js WASM, lazy-loaded from CDN; text=path for font-independent glyphs)
       and placed as an isSvg layer -> production emits real DXF/EPS. Raster-wrapped PDFs fall
       through to the existing 300 DPI rasterize path; any failure also falls through.
   (2) stripSvgPageBackground(): removes ONLY a true page background (the single backmost element
       that is an axis-aligned rectangle covering ~the whole page) — structural, never color-based,
       so intended logo whites (knockouts, highlights) are preserved. MuPDF WASM objects freed after use.
   (3) Engrave ALWAYS vector-traces (skipTrace = hasPhotoRaster && !isEngrave): a photo-flagged
       raster logo no longer exports a lone PNG — DXF is produced for every engraving scenario, EPS
       as fallback, PNG only if vector conversion fails entirely. productionType is labeled by what
       actually shipped (engrave-vector when DXF/EPS exist). Same carve-out applied to bulk
       (bulkTraceVariableRaster). Print pipeline unchanged (gate is inert when !isEngrave).
   (4) Engrave production-file ORDER fixed to DXF (file 1), EPS (file 2), DOS-EPS twin (file 3) —
       single-item and bulk. Print ordering unchanged.
   (5) Fit to Print Area: text fit now constrains by height as well as width (scaleH = pa.h*0.85 / h),
       so multiline / line-broken text no longer oversizes past the print area.
   Prior v2.8.7: Source file upload fallback (l.b64 data-URL when _sourceFile ref lost). Prior v2.8.6:
   Remove Color timing fix, Mobile Remove Color. Prior v2.8.5: vectorization fidelity (trace
   supersample budget, vectorizer.ai input.max_pixels 3145828). Prior v2.8.4: font-ttf outlining,
   abort outlined build, composite z-order fix. Prior v2.8.3: jsPDF path+fill, conditional clipPath,
   engrave vector PDF, supersampled trace */